/**
 * Dashboard Campaigns Tab Controller
 * Integrates campaigns functionality into the dashboard
 */

class DashboardCampaigns {
    // Normalize phone for matching (handles Israeli local/international format differences)
    static normalizePhoneForMatching(phone) {
        if (!phone) return null;
        let cleaned = phone.replace(/[^\d+]/g, '');
        if (cleaned.startsWith('+')) cleaned = cleaned.substring(1);
        if (cleaned.startsWith('0') && (cleaned.length === 10 || cleaned.length === 9)) {
            return '972' + cleaned.substring(1);
        }
        if (cleaned.length === 9 && /^5[0-8]/.test(cleaned)) {
            return '972' + cleaned;
        }
        if (cleaned.length === 10 && !cleaned.startsWith('0')) {
            return '1' + cleaned;
        }
        return cleaned;
    }

    // cTxt for the static helpers below, which run without an instance.
    static sTxt(key, n) {
        return DashboardCampaigns.prototype.cTxt.call(
            { dashboardLanguage: window.DASHBOARD_LANGUAGE || 'en' }, key, n);
    }

    // Failure codes the dashboard words in the host's language: [label, tip]
    // keys in CAMPAIGN_I18N. 131053 showed "failed" with the tip "Unknown
    // error"; text and email failures had no code at all (the contacts
    // payload dropped it), so they read "Failed / Delivery failed" with no
    // hint whether to fix the number or just resend (dashboard review #13,
    // 2026-10-07). Text codes come from messaging.service / campaigns.service;
    // the bounce codes are derived in deliveryErrorCode.
    static get WORDED_ERRORS() {
        return {
            '131053': ['err131053Label', 'err131053Tip'],
            'SMS_BOUNCED': ['errSmsBouncedLabel', 'errSmsBouncedTip'],
            'SMS_FAILED': ['errSmsFailedLabel', 'errSmsFailedTip'],
            'SMS_EXCEPTION': ['errSmsFailedLabel', 'errSmsFailedTip'],
            'SMS_NON_GSM': ['errSmsNonGsmLabel', 'errSmsNonGsmTip'],
            'NON_US_NUMBER': ['errNonUsLabel', 'errNonUsTip'],
            'EMAIL_HARD_BOUNCE': ['errEmailHardLabel', 'errEmailHardTip'],
            'EMAIL_SOFT_BOUNCE': ['errEmailSoftLabel', 'errEmailSoftTip'],
            'EMAIL_BOUNCE': ['errEmailBounceLabel', 'errEmailBounceTip'],
            // An Add Contacts a deploy stopped before this guest (PMb).
            'SEND_INTERRUPTED': ['errInterruptedLabel', 'errInterruptedTip']
        };
    }

    /**
     * Did the host send this guest, from their own WhatsApp, every message
     * that failed them? Then the row is not a failure: like an email
     * fallback, it went out another way (task #584's rule). The server leaves
     * those messages out of failedCampaignIds and lists them in
     * hostSentCampaignIds (markHostSent, 2026-10-18).
     */
    static hostSentCoversFailures(contact) {
        const sent = contact && Array.isArray(contact.hostSentCampaignIds) ? contact.hostSentCampaignIds : [];
        if (!sent.length) return false;
        return !(Array.isArray(contact.failedCampaignIds) && contact.failedCampaignIds.length);
    }

    /**
     * A message that never reached this guest went to a number or email the
     * row no longer has: { phoneChanged, emailChanged }, else null. Never
     * reached = it failed, or it failed and the host then sent it from their
     * own WhatsApp - to the old number too, though the row stops reading as
     * failed (chat b97997a7: a digit missing, the number fixed, no Resend).
     */
    static resendOwedAfterCorrection(contact) {
        if (!contact) return null;
        const reached = contact.whatsappStatus?.delivered || contact.whatsappStatus?.read
            || contact.smsStatus?.delivered || contact.emailStatus?.delivered || contact.emailStatus?.opened;
        const handSentOnly = Array.isArray(contact.hostSentCampaignIds) && contact.hostSentCampaignIds.length > 0 && !reached;
        if (!contact.deliveryFailed && !handSentOnly) return null;
        const norm = (v) => String(v || '').trim().toLowerCase();
        const digits = (v) => String(v || '').replace(/\D/g, '');
        const emailChanged = !!contact.email && (contact.sentToEmails || []).some(e => norm(e) && norm(e) !== norm(contact.email));
        const phoneChanged = !!contact.phone && (contact.sentToPhones || []).some(ph => digits(ph) && digits(ph) !== digits(contact.phone));
        return emailChanged || phoneChanged ? { phoneChanged, emailChanged } : null;
    }

    /**
     * The code that explains one contact's failure. WhatsApp's own code wins
     * (as before); then the text leg, whose code the server now passes
     * through; then an email bounce, by its kind. A text code we have no
     * words for reads as a generic text failure rather than a bare "failed".
     */
    static deliveryErrorCode(contact) {
        const ws = (contact && contact.whatsappStatus) || {};
        const ss = (contact && contact.smsStatus) || {};
        const es = (contact && contact.emailStatus) || {};
        if (ws.errorCode) return String(ws.errorCode);
        if (ss.error || ss.bounced) {
            const code = ss.errorCode ? String(ss.errorCode) : '';
            if (code === 'BREVO_REJECTED' || DashboardCampaigns.WORDED_ERRORS[code]) return code;
            return ss.bounced ? 'SMS_BOUNCED' : 'SMS_FAILED';
        }
        if (ss.errorCode) return String(ss.errorCode);
        // Not a bounce: never sent (an interrupted Add Contacts).
        if (es.errorCode && DashboardCampaigns.WORDED_ERRORS[String(es.errorCode)]) return String(es.errorCode);
        if (es.bounced) {
            return es.bounceType === 'hard' ? 'EMAIL_HARD_BOUNCE'
                : es.bounceType === 'soft' ? 'EMAIL_SOFT_BOUNCE' : 'EMAIL_BOUNCE';
        }
        return null;
    }

    // Map WhatsApp error codes to short human-readable labels
    static getErrorLabel(code) {
        const codeStr = String(code);
        const worded = DashboardCampaigns.WORDED_ERRORS[codeStr];
        if (worded) return DashboardCampaigns.sTxt(worded[0]);
        const labels = {
            '131026': "WhatsApp couldn't deliver",
            // NOT an opt-out — 131049 is Meta's marketing policy/engagement block
            // (the user did not unsubscribe). Kept in sync with admin-dashboard's
            // getErrorLabel; true STOP opt-outs arrive via a separate webhook event.
            '131049': 'blocked by Meta policy',
            '131047': '24h window expired',
            '131048': 'spam blocked',
            '131056': 'rate limited',
            '131058': 'template issue',
            '131061': 'template paused',
            '131062': 'template disabled',
            '132001': 'template issue',
            // Meta withheld the message as part of one of its own delivery
            // experiments — NOT a carrier rejection. Was missing here entirely,
            // so hosts saw a bare "failed" for a code the admin view names.
            '130472': 'held back by Meta test',
            '130497': 'WhatsApp blocked in this country',
            '131063': 'not on WhatsApp',
            '130429': 'sending limit reached',
            'marketing_limit_reached': 'Meta marketing limit',
            'BREVO_REJECTED': 'SMS provider rejected',
            'unknown': 'failed'
        };
        return labels[codeStr] || 'failed';
    }

    // ── "not on WhatsApp" is sometimes a fact about OUR data (task #654) ──
    // Six of fourteen guests reported as unreachable on one September wedding
    // were on WhatsApp perfectly well — the stored number was mangled (a
    // doubled country code, a trunk 0 with no country code, an Israeli mobile
    // wearing a UK prefix). We told the host a fact about the GUEST, so they
    // had no reason to go and fix the number.
    //
    // Only these two codes are overridden. They are the ones that blame the
    // recipient; a paused template or a rate limit has nothing to do with how
    // the number is written, and dressing those up as a data problem would
    // send the host looking in the wrong place.
    static get RECIPIENT_BLAMING_CODES() {
        return ['131026', '131063'];
    }

    /**
     * The calling code most of this campaign's guests are in, or null when
     * there is no clear majority. Used only to catch a well-formed number
     * wearing the wrong country's prefix, which is not detectable without it.
     * Needs a real majority (over half) AND a few numbers to judge from.
     */
    static dominantCountryCode(contacts) {
        const codes = ['972', '351', '44', '33', '34', '61', '91', '52', '55', '66', '1'];
        const tally = {};
        let total = 0;
        (contacts || []).forEach((c) => {
            const d = String((c && c.phone) || '').replace(/[^\d]/g, '');
            if (!d) return;
            const cc = codes.find(x => d.startsWith(x));
            if (!cc) return;
            tally[cc] = (tally[cc] || 0) + 1;
            total++;
        });
        if (total < 4) return null;
        const best = Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0];
        return (best && tally[best] > total / 2) ? best : null;
    }

    /**
     * What to show for a failed delivery: the ordinary reason, unless the
     * number itself is provably mis-shaped and the code was blaming the guest.
     * Returns { label, tip } so both call sites stay in step.
     */
    static describeDeliveryFailure(code, phone, expectedCountry) {
        const ordinary = {
            label: code ? DashboardCampaigns.getErrorLabel(code) : 'Failed',
            tip: code ? DashboardCampaigns.formatErrorMessage(null, code) : 'Delivery failed'
        };
        if (!code || DashboardCampaigns.RECIPIENT_BLAMING_CODES.indexOf(String(code)) === -1) return ordinary;
        let problem = null;
        try {
            problem = window.PhoneShape
                ? window.PhoneShape.phoneShapeProblem(phone, expectedCountry) : null;
        } catch (e) { problem = null; }
        if (!problem) return ordinary;
        return {
            label: 'check this number',
            tip: 'WhatsApp could not deliver to this number as we have it stored, and the number does not look complete — '
                + problem.detail + '. It is worth correcting it and resending before assuming the guest is not on WhatsApp.',
            shape: problem
        };
    }

    // Get detailed error description for tooltips
    static formatErrorMessage(error, errorCode) {
        const errorDescriptions = {
            // Measured retry odds (server/scripts/audit-wa-retry-base-rates.js,
            // 9,239 tracking docs): 131049 succeeds on a later send 49.6% of the
            // time, 131026 only 4.6%. The old copy told hosts a 131026 was "Worth
            // retrying", which steered them into spending credits on numbers that
            // almost never come back. Kept deliberately non-technical — the
            // shared-number quality cost is platform internals, not a host concern.
            '131049': "Meta blocked this marketing message (policy/engagement throttle) — not a user opt-out. They are on WhatsApp and reachable; resending after about a day works roughly half the time.",
            '131026': "This number couldn't receive the message — they may not use WhatsApp, or the digits may be off. Resending rarely helps (about 1 in 20 get through), so reaching them another way is more reliable.",
            '131048': 'Phone number restricted',
            '131056': 'Rate limit exceeded',
            '131058': 'Template not approved',
            '131047': '24-hour window expired',
            '131061': 'Meta paused the message template used here, so it could not be delivered. This affects everyone on this campaign, not just this guest — the template needs recreating.',
            '131062': 'Template disabled',
            '132001': "WhatsApp rejected the message template (it didn't exist in the language it was sent in), so nothing was delivered. Recreate the reminder's template and resend.",
            'marketing_limit_reached': 'Meta paused marketing delivery to this number — not a user opt-out',
            '130472': 'Meta held this message back as part of one of its own delivery experiments. Nothing is wrong with the number, but resending hits the same hold — reach them another way.',
            '130497': "Meta isn't letting our WhatsApp account message numbers in this country right now, so a resend fails the same way. Reach them by email or their personal link.",
            '131063': "This number isn't registered on WhatsApp, so it can't receive the invitation. Try a different number for them, or reach them another way.",
            '130429': 'We hit our own sending limit for the moment — nothing is wrong with this number. Resending shortly should go through.',
            'BREVO_REJECTED': 'The SMS provider rejected this message after accepting it (usually a temporary sending-balance issue on our side). Worth retrying.',
            'NOT_FOUND': 'Contact not found or invalid'
        };
        const worded = errorCode ? DashboardCampaigns.WORDED_ERRORS[String(errorCode)] : null;
        if (worded) return DashboardCampaigns.sTxt(worded[1]);
        if (errorCode && errorDescriptions[String(errorCode)]) {
            return errorDescriptions[String(errorCode)];
        }
        // "Unknown error" told the host nothing and nothing to do.
        if (!error) return DashboardCampaigns.sTxt('errGenericTip');
        if (typeof error === 'string') return error.substring(0, 100);
        if (typeof error === 'object') {
            if (error.message) return error.message;
            if (error.error && typeof error.error === 'string') return error.error;
        }
        return 'Delivery failed';
    }

    // Check if an error code is retryable.
    //
    // 131026 stays in the retryable set, but NOT because it's "commonly
    // transient" — that claim was wrong. Meta treats it as a bucket error
    // (not-on-WhatsApp OR outdated app OR throttling OR policy block), and
    // measured platform-wide it succeeds on a later send only 4.6% of the time
    // (6 of 130 — server/scripts/audit-wa-retry-base-rates.js). It is left
    // retryable because the real control lives downstream: the suppression gate
    // (services/suppression.service.js) scrubs a number after 2 hard failures,
    // so allowing one more attempt costs at most a single send while still
    // catching the small transient minority. Do not widen this on the
    // assumption that these mostly recover — they don't.
    static isRetryableError(errorCode) {
        // 132001 = template doesn't exist in the sent language: a plain retry uses
        // the same broken template, so it needs recreate-and-resend, not auto-retry.
        const nonRetryable = ['131048', '131058', '131059', '131063', '132001'];
        return !nonRetryable.includes(String(errorCode));
    }

    // A "ghost template" failure: Meta 132001 ("template does not exist in the
    // translation") on a template YCloud reported APPROVED. YCloud wraps the code as
    // NOT_FOUND, so also sniff the message text for the real 132001. Unlike an
    // ordinary retry, a ghost needs the template recreated under a fresh name.
    static isGhostFailureContact(c) {
        if (!c) return false;
        const code = String(c.errorCode || c.whatsappStatus?.errorCode || '');
        if (code === '132001') return true;
        const msg = String(c.whatsappStatus?.errorMessage || c.error || c.descriptiveError || '');
        return /132001|does not exist in the translation|does not exist in [a-z_]{2,6}/i.test(msg);
    }

    constructor() {
        this.formId = null;
        this.formData = null;
        this.campaigns = [];
        this.isInitialized = false;
        this.autoRefreshInterval = null;
        this.dashboardLanguage = window.DASHBOARD_LANGUAGE || 'en';
    }

    /**
     * Translate a dynamic dashboard string (campaign rows, reminder rows,
     * status chips, etc.) into the current dashboard language. These strings
     * are built in JS and never went through the page i18n system, so they
     * stayed English on non-English dashboards. One inline {en,es,fr,de,pt,he,ar}
     * map (mirrors admin.js's statusMap pattern) covers ALL locale dashboards.
     * Falls back to English per-key. `n` interpolates a {n} placeholder; an
     * object instead fills each {key} it names. Values are inserted as given —
     * escape anything user-supplied before passing it.
     */
    cTxt(key, n) {
        const M = DashboardCampaigns.CAMPAIGN_I18N;
        const lang = this.dashboardLanguage || 'en';
        let s = (M[lang] && M[lang][key] != null) ? M[lang][key] : (M.en[key] != null ? M.en[key] : key);
        if (n != null && typeof n === 'object') {
            s = s.replace(/\{(\w+)\}/g, (m, k) => (n[k] != null ? String(n[k]) : m));
        } else if (n != null) {
            s = s.replace('{n}', n);
        }
        return s;
    }

    // A count and its noun. `<key>One` is the form for exactly one, which
    // most languages need even where English reads the same ("1 sent").
    cCount(key, n) {
        return this.cTxt(n === 1 ? key + 'One' : key, n);
    }

    // cTxt for a title/aria attribute. _escape leaves quotes alone when
    // window.escapeHtml is loaded, and a translated tooltip can hold one.
    cAttr(key, n) {
        return this._escape(this.cTxt(key, n)).replace(/"/g, '&quot;');
    }

    // Locale for every date on the Messages tab. They were hard-coded 'en-US',
    // so a Hebrew dashboard read "Oct 17, 4:00 PM" (code review 2026-10-07).
    // Portuguese is European; Arabic keeps the Latin digits every count
    // beside it uses.
    _dateLocale() {
        const lang = this.dashboardLanguage || 'en';
        return ({ en: 'en-US', pt: 'pt-PT', ar: 'ar-u-nu-latn' })[lang] || lang;
    }

    // A WhatsApp/SMS error code's short label and tooltip in the dashboard
    // language. The static getErrorLabel/formatErrorMessage stay English: the
    // CSV export and other callers use them. An unknown code, or a language
    // without that key, falls back to the English text, which is exactly what
    // an English dashboard shows.
    _errLabel(code) {
        const M = DashboardCampaigns.CAMPAIGN_I18N[this.dashboardLanguage] || {};
        const en = DashboardCampaigns.getErrorLabel(code);
        return M['errLabel_' + code] || (en === 'failed' ? (M.errLabel_unknown || en) : en);
    }

    _errTip(code) {
        const M = DashboardCampaigns.CAMPAIGN_I18N[this.dashboardLanguage] || {};
        const en = DashboardCampaigns.formatErrorMessage(null, code);
        return M['errTip_' + code] || (en === 'Unknown error' ? (M.errTipUnknown || en) : en);
    }

    // describeDeliveryFailure, in the dashboard language. The phone-shape
    // detail comes from PhoneShape in English only, so the translated
    // sentences leave it out; English keeps it.
    _deliveryFailureText(code, phone) {
        const d = DashboardCampaigns.describeDeliveryFailure(code, phone, this._dominantCc);
        if (d.shape) return { label: this.cTxt('checkNumber'), tip: this.cTxt('checkNumberTip', { detail: d.shape.detail }) };
        return {
            label: code ? this._errLabel(code) : this.cTxt('dlvFailed'),
            tip: code ? this._errTip(code) : this.cTxt('deliveryFailed')
        };
    }

    /**
     * Get bulk wizard translations for the current dashboard language
     * Falls back to English if translation not found
     */
    getWizardTranslations() {
        const lang = this.dashboardLanguage;

        // Map of language code to content object name
        const contentMap = {
            'es': window.SpanishDashboardContent,
            'fr': window.FrenchDashboardContent,
            'de': window.GermanDashboardContent,
            'pt': window.PortugueseDashboardContent,
            'he': window.HebrewDashboardContent,
            'ar': window.ArabicDashboardContent
        };

        const content = contentMap[lang];

        // Return translations or English defaults
        if (content?.bulkWizard) {
            return content.bulkWizard;
        }

        // English defaults
        return {
            checkingAccess: "Checking access permissions...",
            wizardFailedToLoad: "Messaging wizard failed to load. Please refresh the page.",
            step1Contacts: "1. Contacts",
            step2Channels: "2. Channels",
            step2Review: "3. Customize",
            step3FollowUp: "4. Follow-Up",
            step4Send: "5. Send",
            previous: "Previous",
            next: "Next",
            nextChannels: "Next: Delivery Channels",
            nextCustomize: "Next: Customize",
            skip: "Skip",
            saveAsDraft: "Save as Draft"
        };
    }

    /**
     * Initialize campaigns tab - called when tab is first shown or form changes
     */
    async init(formId) {
        if (!formId || formId === 'all') {
            this.showNoFormError();
            return;
        }

        this.formId = formId;
        this.showLoading();

        try {
            await this.loadFormData();
            await this.loadCampaigns();
            this.setupEventListeners();
            this.setupCampaignListener();
            this.isInitialized = true;
        } catch (error) {
            console.error('Error initializing campaigns tab:', error);
            this.showError('Failed to load campaigns');
        }
    }

    showLoading() {
        const loading = document.getElementById('campaignsLoadingState');
        const content = document.getElementById('campaignsMainContent');
        const error = document.getElementById('campaignsNoFormError');

        if (loading) loading.style.display = 'block';
        if (content) content.style.display = 'none';
        if (error) error.style.display = 'none';
    }

    showNoFormError() {
        const loading = document.getElementById('campaignsLoadingState');
        const content = document.getElementById('campaignsMainContent');
        const error = document.getElementById('campaignsNoFormError');

        if (loading) loading.style.display = 'none';
        if (content) content.style.display = 'none';
        if (error) error.style.display = 'block';
    }

    showError(message) {
        const loading = document.getElementById('campaignsLoadingState');
        if (loading) {
            loading.innerHTML = `
                <i class="bi bi-exclamation-circle text-danger" style="font-size: 2rem;"></i>
                <p class="mt-2 text-muted">${message}</p>
            `;
        }
    }

    async loadFormData() {
        try {
            const db = firebase.firestore();
            const formDoc = await db.collection('rsvp_forms').doc(this.formId).get();

            if (!formDoc.exists) {
                throw new Error('Form not found');
            }

            this.formData = { id: formDoc.id, ...formDoc.data() };
        } catch (error) {
            console.error('Error loading form data:', error);
            throw error;
        }
    }

    /**
     * Does this signed-in user manage the event the dashboard is showing?
     * Mirrors firestore.rules `isFormManager`: the owner, or a co-host whose
     * VERIFIED email is on the form's coOwnerEmails (rules match the verified
     * token email, so an unverified co-host's formId-only read would be denied
     * and must keep the userId filter).
     */
    _managesThisEvent(user) {
        if (!user || !this.formData) return false;
        if (this.formData.owner && this.formData.owner === user.uid) return true;
        const myEmail = (user.email || '').toLowerCase();
        return Array.isArray(this.formData.coOwnerEmails)
            && user.emailVerified === true
            && !!myEmail
            && window.ValidationUtils.mailboxListHas(this.formData.coOwnerEmails, myEmail);
    }

    async loadCampaigns() {
        // Callers outside this tab (the personal-links wizard) reload it even
        // when init() never ran for a form; nothing to load without one.
        if (!this.formId) return;
        try {
            const db = firebase.firestore();
            const user = firebase.auth().currentUser;

            // Need user auth for permission check
            if (!user) {
                console.log('ðŸ“‹ Not authenticated - showing empty state');
                this.campaigns = [];
                this.renderCampaigns();
                this.renderMessagingActivity();
                return;
            }

            // In admin mode, use the form owner's ID so we see their campaigns
            // Forms store owner in 'owner' field, campaigns store creator in 'userId'
            const queryUserId = window.isAdminMode && this.formData?.owner
                ? this.formData.owner
                : user.uid;

            // Anyone who MANAGES this event — the owner as well as a co-host —
            // reads the event's whole campaign set by formId, with no userId
            // filter. A campaign carries the userId of whoever created it, so on
            // a shared event the owner's own filter hid every list a co-host had
            // built (support 2026-09-22: the co-host's 86-guest invitation list
            // was invisible on the owner's dashboard). The rules allow the
            // formId-only read for either of them (`canManageLinkedForm`).
            const isManager = this._managesThisEvent(user);

            // Query with userId filter to satisfy Firestore security rules
            // (skipped for managers and admins, who read the whole form's set).
            let campaignsRef = db.collection('bulk_campaigns')
                .where('formId', '==', this.formId);
            if (!isManager && !window.isAdminMode) {
                campaignsRef = campaignsRef.where('userId', '==', queryUserId);
            }

            const snapshot = await campaignsRef.get();
            // hydrateAll: the guest list may live in chunk docs under the campaign.
            // reuseFrom: a list already held whose contactsRev is unchanged is
            // not read again (this runs after nearly every host action).
            this.campaigns = (await window.CampaignContacts.hydrateAll(db, snapshot, { reuseFrom: this.campaigns }))
                .map(x => ({ id: x.id, ...x.data }));

            // If admin mode and no results with form owner's ID, try without userId filter
            // (handles cases where formData.owner is missing or campaigns have different ownership)
            if (window.isAdminMode && this.campaigns.length === 0) {
                console.log('ðŸ“‹ Admin mode: retrying campaign load without userId filter');
                const adminRef = db.collection('bulk_campaigns')
                    .where('formId', '==', this.formId);
                const adminSnapshot = await adminRef.get();
                this.campaigns = (await window.CampaignContacts.hydrateAll(db, adminSnapshot))
                    .map(x => ({ id: x.id, ...x.data }));
            }

            // Personal-links batches carry a server-made English name.
            this.campaigns.forEach((c) => this._localizeAutoName(c));

            // Sort client-side by createdAt descending
            this.campaigns.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
                return dateB - dateA;
            });

            await this.loadMessageTrackingStats();
            this.renderCampaigns();
            this.renderMessagingActivity();
            // Handle the "Buy credits & resume" email deep link once campaigns
            // are loaded (so the target campaign exists in this.campaigns).
            this._maybeHandleResumeDeepLink();

        } catch (error) {
            console.error('Error loading campaigns:', error);
            // Handle permission errors gracefully - treat as no campaigns
            // This can happen for new users who haven't created any campaigns yet
            if (error.code === 'permission-denied' || error.message?.includes('permission')) {
                console.log('ðŸ“‹ No campaign access yet - showing empty state');
                this.campaigns = [];
                this.renderCampaigns();
                this.renderMessagingActivity();
            } else {
                throw error;
            }
        }
    }

    /**
     * A personal-links batch is named by the server when it is created
     * ("Personal links · Sep 17"; "Shared by hand · Sep 17" before task #779).
     * Only that exact auto-name is swapped for the dashboard's own language,
     * so a name the host typed is never touched. Returns the campaign.
     */
    _localizeAutoName(c) {
        const m = /^(?:Shared by hand|Personal links) · (.+)$/.exec((c && c.name) || '');
        if (m) c.name = `${this.cTxt('personalLinks')} · ${m[1]}`;
        return c;
    }

    // Authenticated fetch for the message-tracking endpoints. Delegates to
    // the shared AuthedFetch gate (attaches the Firebase token; fails fast
    // on auth loss so pollers don't hammer tokenless). Returns a Response,
    // so existing .ok/.then/.catch handling is unchanged.
    async _mtFetch(path, init) {
        if (window.AuthedFetch) return window.AuthedFetch.fetch(path, init);
        return fetch(path, init); // AuthedFetch script not loaded — degraded fallback
    }

    async loadMessageTrackingStats() {
        // Drives the per-row campaign card stats ("X responded, Y pending, Z sent").
        // Single source of truth: /api/message-tracking/:formId/contacts. We bucket
        // the canonical per-contact data by campaignId so all campaign UI surfaces
        // (card stats, status modal, reminders) agree.
        try {
            const resp = await this._mtFetch(`/api/message-tracking/${this.formId}/contacts`);
            if (!resp.ok) throw new Error(`tracking fetch ${resp.status}`);
            const data = await resp.json();
            const contacts = data.contacts || [];

            const statsByCampaign = {};
            const bump = (id, key) => {
                if (!statsByCampaign[id]) statsByCampaign[id] = { sent: 0, seen: 0, responded: 0, failed: 0, manual: 0, waSendable: 0 };
                statsByCampaign[id][key]++;
            };

            // Skipped guests who have since been looked after (see _skipSummary):
            // when each person last replied, and on which messages they opened
            // a personal link.
            const skipKey = DashboardCampaigns._skipGuestKeys;
            const replyAtByKey = new Map();
            const linkedKeysByCampaign = {};

            contacts.forEach(c => {
                const seen = !!(c.whatsappStatus?.read || c.emailStatus?.opened || c.emailStatus?.clicked);
                // Failure is counted against the SPECIFIC campaign whose message
                // hard-failed (task #370) — not the contact's aggregate. Otherwise a
                // guest reached by an earlier message masks a LATER message's failure,
                // and that later campaign's card shows "0 not delivered" (looks like
                // nothing was even attempted). The server marks a campaign failed only
                // when no attempt for it ever delivered.
                const failedCampaigns = new Set(c.failedCampaignIds || []);
                // Personal links made on a message that never sent this person
                // anything. Still counted under 'sent' as before (the other
                // cards' arithmetic leans on it); 'manual' lets the skipped-guest
                // logic tell a link from a send.
                const manualOnly = new Set(c.manualOnlyCampaignIds || []);
                const keys = skipKey(c.phone, c.email);

                (c.campaignIds || []).forEach(id => {
                    bump(id, 'sent');
                    if (seen) bump(id, 'seen');
                    if (c.responded) bump(id, 'responded');
                    if (failedCampaigns.has(id)) {
                        bump(id, 'failed');
                        // One the host can send from their own WhatsApp: another
                        // channel is the answer (Need a different channel), and
                        // there is a phone and this message's link. The card's
                        // "Send from my WhatsApp (N)" counts these.
                        if (DashboardCampaigns.CHANNEL_SWITCH_CODES.includes(String(DashboardCampaigns.deliveryErrorCode(c) || ''))
                            && String(c.phone || '').replace(/\D/g, '').length >= 8
                            && ((c.trackingIdByCampaign && c.trackingIdByCampaign[id]) || (Array.isArray(c.trackingIds) && c.trackingIds.length))) {
                            bump(id, 'waSendable');
                        }
                        // Which channel failed, so the card's "not delivered"
                        // tip names it instead of always blaming WhatsApp.
                        const ch = (c.failedChannelByCampaign || {})[id];
                        if (ch) {
                            const by = statsByCampaign[id].failedBy || (statsByCampaign[id].failedBy = {});
                            by[ch] = (by[ch] || 0) + 1;
                        }
                    }
                    if (manualOnly.has(id)) bump(id, 'manual');
                });
                // A link only counts once the guest has opened it. Opening
                // Personal links mints one for every guest, so "has a link"
                // settled a message the host had only looked at, and took
                // Retry away for good (review round 2, 2026-10-07).
                (c.openedLinkCampaignIds || []).forEach(id => {
                    const set = linkedKeysByCampaign[id] || (linkedKeysByCampaign[id] = new Set());
                    keys.forEach(k => set.add(k));
                });
                const repliedAt = c.responded && c.respondedAt ? Date.parse(c.respondedAt) : NaN;
                if (Number.isFinite(repliedAt)) {
                    keys.forEach(k => { if (!(replyAtByKey.get(k) >= repliedAt)) replyAtByKey.set(k, repliedAt); });
                }
            });
            const msOf = (v) => {
                if (!v) return NaN;
                const d = v.toDate ? v.toDate() : new Date(v);
                return d.getTime();
            };

            this.campaigns.forEach(campaign => {
                campaign.trackingStats = statsByCampaign[campaign.id]
                    || { sent: 0, seen: 0, responded: 0, failed: 0, manual: 0 };
                // One entry per skipped person on this message: replied wins
                // over linked (opened a personal link made on this message).
                // A reply only settles a message that asks for one: a reminder
                // to guests who have already replied (attending, maybe,
                // declined, everyone) carries news they still need. And only a
                // reply made after the message was created: an update sent to
                // guests who answered weeks ago still has to reach them.
                const raw = Array.isArray(campaign.suppressedRecipients) ? campaign.suppressedRecipients : [];
                if (!raw.length) { campaign.skipFollowUp = null; return; }
                const linked = linkedKeysByCampaign[campaign.id] || new Set();
                const replyEnds = !(campaign.isReminderCampaign
                    && ['attending', 'maybe', 'declined', 'all'].includes(campaign.reminderConfig?.recipients));
                const since = msOf(campaign.createdAt) || msOf(campaign.processedAt || campaign.sentAt);
                const repliedSince = (k) => k.some(x => replyAtByKey.get(x) > since);
                const people = new Map();
                raw.forEach(e => {
                    if (!e) return;
                    const k = skipKey(e.phone, e.email);
                    if (!k.length || people.has(k[0])) return;
                    people.set(k[0], (replyEnds && repliedSince(k)) ? 'replied'
                        : (k.some(x => linked.has(x)) ? 'linked' : 'open'));
                });
                const states = [...people.values()];
                campaign.skipFollowUp = {
                    total: states.length,
                    replied: states.filter(s => s === 'replied').length,
                    linked: states.filter(s => s === 'linked').length
                };
            });
        } catch (error) {
            console.error('Error loading tracking stats:', error.message || error);
        }
    }

    // ===== Messaging Activity card (moved from the former Analytics tab) =====
    // Aggregate engagement across all campaigns + reminders. Fetches
    // /api/message-tracking/:formId/stats for per-channel open/read rates and
    // /contacts for per-person headline totals (so they match the campaign cards
    // below). Renders into #messaging-activity-section and hides it when there's
    // no tracking yet. Runs once per Campaigns-tab load — deliberately no
    // background polling (that lived in the deleted analytics engine).
    async renderMessagingActivity() {
        const section = document.getElementById('messaging-activity-section');
        if (!section) return;

        // Show/hide the stats, and flip the Credits card between its compact tile
        // (stats present, sits adjacent) and its full-width bar (no campaign sent
        // yet) presentations — see .credits-card / .is-compact in user-analytics.css.
        const setStatsVisible = (on) => {
            section.style.display = on ? 'block' : 'none';
            document.getElementById('credits-card')?.classList.toggle('is-compact', on);
        };

        try {
            const isDemoForm = this.formId === 'sarah-mike-s-wedding-5zag';
            let stats;

            if (isDemoForm) {
                stats = this._demoMessagingStats();
            } else {
                const [statsResp, contactsResp] = await Promise.all([
                    this._mtFetch(`/api/message-tracking/${this.formId}/stats`),
                    this._mtFetch(`/api/message-tracking/${this.formId}/contacts`)
                ]);
                if (!statsResp.ok) { setStatsVisible(false); return; }
                stats = await statsResp.json();
                if (contactsResp.ok) {
                    const data = await contactsResp.json();
                    stats._contacts = data.contacts || [];
                }
            }

            if (!stats || !stats.hasTracking) {
                setStatsVisible(false);
                return;
            }

            setStatsVisible(true);
            this._renderMessagingMetrics(stats);
        } catch (error) {
            console.error('Error rendering messaging activity:', error.message || error);
            setStatsVisible(false);
        }
    }

    // Mock data for the marketing demo form — mirrors the old analytics engine.
    _demoMessagingStats() {
        return {
            hasTracking: true,
            uniqueContacts: 50,
            email: { sent: 45, delivered: 44, opened: 38, clicked: 19, responded: 32, bounced: 1, openRate: 86.4, clickRate: 43.2, bounceRate: 2.3 },
            whatsapp: { sent: 20, delivered: 19, read: 17, responded: 14, failed: 3, deliveryRate: 95.0, readRate: 85.0 },
            sms: { sent: 2, delivered: 2, fallbackCount: 2 }
        };
    }

    // Ported from UserAnalytics.renderMessageEngagementMetrics — populates the
    // Email + Mobile (WhatsApp+SMS, deduped per person) performance cards and
    // the per-person summary. Email: "Seen" = max(opened/clicked, responded);
    // "Not opened" = sent - seen; "Opened" (no reply) = seen - responded;
    // "Responded" = actual form submissions. Both cards headline the SAME metric
    // (% responded) so they're comparable, with the channel's funnel rate (open
    // rate / delivery rate) shown as a secondary line. Email can measure opens;
    // texts only confirm delivery (no read receipt), so the amber/blue slices are
    // parallel but channel-accurate. Mobile slices come from _computeMobileStats().
    _renderMessagingMetrics(stats) {
        // Donut slice colors are pulled from the legend dot classes so the chart
        // and the key always agree: amber=bg-warning (Not opened / Not reached),
        // blue=bg-primary (Opened / Delivered), green=bg-success (Responded).
        // ("Sent" is the whole ring/total, not a slice.)
        const slicePalette = [this._dotColor('bg-warning'), this._dotColor('bg-primary'), this._dotColor('bg-success')];

        // --- EMAIL METRICS ---
        if (stats.email) {
            const openRate = stats.email.openRate || 0;
            const sent = stats.email.sent || 0;
            const opened = stats.email.opened || 0;
            const clicked = stats.email.clicked || 0;
            const responded = stats.email.responded || 0;
            const seen = Math.max(opened, clicked, responded);
            const notSeen = Math.max(0, sent - seen);
            const seenNotResponded = Math.max(0, seen - responded);
            const respondRate = sent ? Math.round((responded / sent) * 100) : 0;

            this._msgEl('email-sent-count', sent);
            this._msgEl('email-respond-rate-display', `${respondRate}%`); // headline (parallel to Mobile)
            this._msgEl('email-open-rate-display', `${openRate}%`);       // secondary funnel rate
            this._msgEl('email-unopened-count', notSeen);
            this._msgEl('email-opened-count', seenNotResponded);
            this._msgEl('email-clicked-count', responded);
            this._msgEl('email-donut-center', responded);
            this._msgPie('emailPerformanceChart', [notSeen, seenNotResponded, responded], slicePalette, ['Not opened', 'Opened', 'Responded']);
        }

        // --- MOBILE METRICS (WhatsApp + SMS), deduped per person ---
        // The "WhatsApp" donut now reports the whole MOBILE channel: a guest
        // reached via WhatsApp OR its SMS fallback counts ONCE as a mobile
        // success — never a WhatsApp fail + a separate SMS success. We prefer
        // the per-person contact list (already deduped across channels by the
        // /contacts endpoint) and fall back to summed per-channel stats only
        // when that list is unavailable. Element IDs stay whatsapp-* to avoid
        // churn across the 7 dashboard HTMLs; they now hold COMBINED figures.
        const mobile = this._computeMobileStats(stats);
        if (mobile && mobile.sent > 0) {
            const { sent, responded, reached, viaSms } = mobile;
            const notReached = Math.max(0, sent - reached);
            const reachedNotResponded = Math.max(0, reached - responded);
            const respondRate = sent ? Math.round((responded / sent) * 100) : 0;
            const deliveryRate = sent ? Math.round((reached / sent) * 100) : 0;

            this._msgEl('whatsapp-read-rate-display', `${respondRate}%`);      // headline
            this._msgEl('whatsapp-delivery-rate-display', `${deliveryRate}%`); // secondary funnel rate
            this._msgEl('whatsapp-delivered-count', notReached);       // amber: Not reached
            this._msgEl('whatsapp-read-count', reachedNotResponded);   // blue: Delivered, no reply
            this._msgEl('whatsapp-responded-count', responded);        // green: Responded
            this._msgEl('whatsapp-sent-count', sent);
            this._msgEl('whatsapp-donut-center', responded);
            this._msgPie('whatsappPerformanceChart', [notReached, reachedNotResponded, responded], slicePalette, ['Not reached', 'Delivered', 'Responded']);

            // "of which N via SMS" caption (reuses the old sms-fallback-note element)
            const smsNote = document.getElementById('sms-fallback-note');
            const smsNoteCount = document.getElementById('sms-fallback-count');
            if (smsNote && smsNoteCount) {
                if (viaSms > 0) {
                    smsNoteCount.textContent = viaSms;
                    smsNote.style.display = 'block';
                } else {
                    smsNote.style.display = 'none';
                }
            }
        }

        // --- PER-PERSON SUMMARY (matches the campaign cards) ---
        // This "Recipients / Pending / Responded" funnel is counted per DISTINCT
        // PERSON (not per message — the channel tiles below show message counts).
        // Prefer the canonical per-contact list (deduped across campaigns); fall
        // back to the unique-contact count for the demo's pre-baked mock data.
        const contacts = stats._contacts || [];
        let totalSent, totalPending, totalResponded;
        if (contacts.length) {
            totalSent = contacts.length;
            totalResponded = contacts.filter(c => c.rsvpStatus && c.rsvpStatus !== 'pending').length;
            totalPending = Math.max(0, totalSent - totalResponded);
        } else {
            // No per-contact list: approximate the people-funnel from stats. Use the
            // deduped unique-contact count for Recipients (NOT the sum of channel
            // sends, which counts messages) so the funnel stays per-person; cap
            // responded at recipients so the funnel can't exceed itself.
            totalSent = stats.uniqueContacts || ((stats.email?.sent || 0) + (stats.whatsapp?.sent || 0));
            totalResponded = Math.min(totalSent, (stats.email?.responded || 0) + (stats.whatsapp?.responded || 0));
            totalPending = Math.max(0, totalSent - totalResponded);
        }
        this._msgEl('total-sent-count', totalSent);
        this._msgEl('total-pending-count', totalPending);
        this._msgEl('total-responded-count', totalResponded);
    }

    // Combine WhatsApp + SMS into one per-person "mobile" funnel for the donut.
    // Uses the deduped per-contact list when present (a guest reached on either
    // channel counts once); falls back to summed per-channel stats only when
    // that list is unavailable (e.g. /contacts failed, or the demo mock). SMS
    // has no read receipt, so "reached" uses delivery (WhatsApp read/delivered,
    // or SMS delivered); "responded" mirrors the per-channel funnel (any reply
    // by a person we messaged on mobile).
    _computeMobileStats(stats) {
        const contacts = (stats._contacts || []).filter(c => c.whatsappStatus || c.smsStatus);
        if (contacts.length) {
            return {
                sent: contacts.length,
                responded: contacts.filter(c => c.responded).length,
                reached: contacts.filter(c => c.responded
                    || c.whatsappStatus?.read || c.whatsappStatus?.delivered
                    || c.smsStatus?.delivered).length,
                viaSms: contacts.filter(c => c.smsStatus && !c.smsStatus.error).length
            };
        }
        // Degraded fallback: no per-contact list — approximate from per-channel
        // stats. Cannot dedup, so a WhatsApp-fail→SMS guest may count twice here.
        const wa = stats.whatsapp || {};
        const sm = stats.sms || {};
        const sent = (wa.sent || 0) + (sm.sent || 0);
        if (!sent) return null;
        const responded = Math.min(sent, (wa.responded || 0) + (sm.responded || 0));
        const reached = Math.min(sent, Math.max(responded, (wa.read || 0) + (sm.delivered || 0)));
        return { sent, responded, reached, viaSms: sm.fallbackCount || sm.sent || 0 };
    }

    _msgEl(id, value) {
        const el = document.getElementById(id);
        if (el) el.textContent = value;
    }

    // Computed background color for a Bootstrap bg-* class, so the donut slices
    // can exactly match the legend dots (and track the theme).
    _dotColor(cls) {
        const el = document.createElement('span');
        el.className = 'dot ' + cls;
        document.body.appendChild(el);
        const color = getComputedStyle(el).backgroundColor;
        el.remove();
        return color;
    }

    _msgPie(canvasId, data, colors, labels) {
        const ctx = document.getElementById(canvasId);
        if (!ctx || typeof Chart === 'undefined') return;
        const key = `${canvasId}_instance`;
        if (this[key]) this[key].destroy();
        // No data yet → draw a solid light-grey ring placeholder instead of an
        // empty (invisible) canvas, so the centered "0" sits inside a clean ring
        // rather than floating in a void.
        const total = data.reduce((sum, n) => sum + (Number(n) || 0), 0);
        const ringData = total > 0 ? data : [1];
        const ringColors = total > 0 ? colors : ['#E5E7EB'];
        const ringLabels = total > 0 ? labels : [''];
        this[key] = new Chart(ctx, {
            type: 'doughnut',
            data: { labels: ringLabels, datasets: [{ data: ringData, backgroundColor: ringColors, borderWidth: 0, hoverOffset: 0 }] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '60%',
                // Purely a visual indicator — the legend beside it lists every value,
                // so disable interaction/tooltips (they'd clip inside this 54px canvas).
                events: [],
                animation: false,
                plugins: { legend: { display: false }, tooltip: { enabled: false } }
            }
        });
    }

    // Order campaigns so each linked reminder child appears immediately beneath
    // its parent (children sorted by reminderIndex). Parents keep their existing
    // createdAt-desc order. Orphan children (parent not in the list) fall back to
    // their natural position so nothing disappears.
    _groupCampaignsForDisplay() {
        // Hand-shared "Personal links · Oct 20" cards are not in the feed
        // (task #1044, Esther: the feed is for messages we send). They are
        // listed in the Personal links window instead, and each guest's link
        // is on their row in Responses. Their documents stay: the guest-side
        // lookups and reply tracking read them. A reminder sent from one
        // still shows, as a message of its own.
        const all = (this.campaigns || []).filter(c => c.status !== 'manual_links');
        const childrenByParent = new Map();
        for (const c of all) {
            if (c.isReminderCampaign && c.parentCampaignId) {
                if (!childrenByParent.has(c.parentCampaignId)) childrenByParent.set(c.parentCampaignId, []);
                childrenByParent.get(c.parentCampaignId).push(c);
            }
        }
        for (const arr of childrenByParent.values()) {
            arr.sort((a, b) => (a.reminderIndex || 0) - (b.reminderIndex || 0));
        }
        const parentIds = new Set(all.map(c => c.id));
        const ordered = [];
        const seen = new Set();
        for (const c of all) {
            // Skip children whose parent is present — they're emitted under the parent below.
            if (c.isReminderCampaign && c.parentCampaignId && parentIds.has(c.parentCampaignId)) continue;
            if (seen.has(c.id)) continue;
            ordered.push(c); seen.add(c.id);
            const kids = childrenByParent.get(c.id);
            if (kids) for (const k of kids) { if (!seen.has(k.id)) { ordered.push(k); seen.add(k.id); } }
        }
        return ordered;
    }

    // Reminder groups start COLLAPSED by default so the campaigns list stays
    // short and easy to scan (the host clicks a campaign to reveal its reminders),
    // EXCEPT a group holding a reminder that needs the host (failed, refused,
    // out of credits, WhatsApp half lost): collapsed, its "Couldn't send" sat
    // behind a bell that showed only a count (audit 2026-10-07, review #15).
    // Manual per-session toggles (_reminderToggleOverrides) still win over this
    // default. Returns a Map<parentId, boolean expanded>.
    _computeReminderExpandStates() {
        const map = new Map();
        const attention = this._reminderAttentionByParent();
        for (const parent of this.campaigns) {
            if (!parent.hasSplitReminders || !Array.isArray(parent.childReminderCampaignIds) || !parent.childReminderCampaignIds.length) continue;
            map.set(parent.id, (attention.get(parent.id) || 0) > 0);
        }
        return map;
    }

    // Map<parentId, number of its reminders needing the host> over the loaded
    // reminder rows (the live children, like splitReminderCount).
    _reminderAttentionByParent() {
        const out = new Map();
        for (const c of (this.campaigns || [])) {
            if (!c || !c.isReminderCampaign || !c.parentCampaignId) continue;
            if (DashboardCampaigns.reminderAttention(c)) out.set(c.parentCampaignId, (out.get(c.parentCampaignId) || 0) + 1);
        }
        return out;
    }

    // The red dot on a reminders toggle, with its meaning for screen readers.
    _reminderAttentionDotHtml(n) {
        if (!(n > 0)) return '';
        const label = this._escape(n === 1 ? this.cTxt('remAttentionOne') : this.cTxt('remAttentionMany', n));
        return `<span class="reminder-attention-dot" aria-hidden="true"></span><span class="visually-hidden">${label}</span>`;
    }

    // Effective expanded state for a parent: a manual toggle (this session) wins,
    // otherwise fall back to the collapsed-by-default state. Defaults to collapsed.
    _isReminderGroupExpanded(parentId) {
        if (this._reminderToggleOverrides && this._reminderToggleOverrides.has(parentId)) {
            return this._reminderToggleOverrides.get(parentId);
        }
        if (this._reminderExpandDefaults && this._reminderExpandDefaults.has(parentId)) {
            return this._reminderExpandDefaults.get(parentId) !== false;
        }
        return false;
    }

    // Reflect a parent's expanded state onto its toggle controls (label +
    // chevron). A campaign can have TWO toggles — the desktop status-line pill
    // and the mobile "Reminders (N)" action button (task #176) — keep both in sync.
    _syncReminderToggleButton(parentId, expanded) {
        document.querySelectorAll(`[data-action="toggle-reminders"][data-campaign-id="${parentId}"]`).forEach(chip => {
            chip.setAttribute('aria-expanded', String(expanded));
            const caret = chip.querySelector('.reminder-caret');
            if (caret) caret.className = `bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'} reminder-caret`;
            const label = chip.querySelector('.reminder-toggle-label');
            if (label) {
                const n = document.querySelectorAll(`.reminder-child-row[data-reminder-parent="${parentId}"]`).length;
                // Icon-only toggle (task #75) — show just the count; the caret conveys state.
                label.textContent = String(n);
                chip.setAttribute('aria-label', this._reminderToggleAria(parentId, n, expanded));
            }
        });
    }

    // The bell pill's aria-label, for the render and for every expand or
    // collapse. The toggle wrote its own English label and dropped the
    // attention line until the next full render (wave-2 merge review); one
    // helper keeps the two from drifting apart again. The aria-label replaces
    // the pill's contents for screen readers, so it carries the attention
    // line itself (review of M3, 2026-10-07).
    _reminderToggleAria(parentId, n, expanded) {
        const attentionN = (this._reminderAttentionMap || this._reminderAttentionByParent()).get(parentId) || 0;
        const attention = attentionN > 0
            ? ` · ${attentionN === 1 ? this.cTxt('remAttentionOne') : this.cTxt('remAttentionMany', attentionN)}` : '';
        return this.cTxt(expanded ? 'hideRemindersAria' : 'showRemindersAria', n) + attention;
    }

    // Show/hide the collapsed reminder children for a parent campaign and flip
    // the chevron + label on its toggle button. Remembers the manual choice for
    // this session so a re-render doesn't snap it back to the smart default.
    toggleReminders(parentId) {
        if (!parentId) return;
        const rows = document.querySelectorAll(`.reminder-child-row[data-reminder-parent="${parentId}"]`);
        if (!rows.length) return;
        // Class-based toggle: d-none present === currently hidden. (Reading
        // r.style.display is ambiguous — '' reads as falsy/"hidden" once shown,
        // which made the chip only ever open.)
        const willShow = rows[0].classList.contains('d-none');
        rows.forEach(r => r.classList.toggle('d-none', !willShow));
        if (!this._reminderToggleOverrides) this._reminderToggleOverrides = new Map();
        this._reminderToggleOverrides.set(parentId, willShow);
        this._syncReminderToggleButton(parentId, willShow);
        this._syncExpandCollapseAllBtn();
    }

    // Expand or collapse EVERY reminder group at once. If any group is currently
    // collapsed, the action expands all; otherwise it collapses all.
    _toggleAllReminders() {
        // One-click toggle that matches the button label: if anything is open,
        // collapse everything; only expand-all when every group is collapsed.
        const show = !document.querySelector('.reminder-child-row:not(.d-none)');
        if (!this._reminderToggleOverrides) this._reminderToggleOverrides = new Map();
        const parentIds = new Set();
        document.querySelectorAll('.reminder-child-row[data-reminder-parent]').forEach(r => parentIds.add(r.getAttribute('data-reminder-parent')));
        parentIds.forEach(pid => {
            this._reminderToggleOverrides.set(pid, show);
            document.querySelectorAll(`.reminder-child-row[data-reminder-parent="${pid}"]`).forEach(r => r.classList.toggle('d-none', !show));
            this._syncReminderToggleButton(pid, show);
        });
        this._syncExpandCollapseAllBtn();
    }

    // Inject (once) the "Expand all / Collapse all" control into the Campaign
    // History header, shown only when at least one campaign has reminders. Kept
    // in JS so it covers every localized dashboard without editing 7 HTML files.
    // Drop the redundant "Campaign History" heading (we're already on the
    // Campaigns tab) and left-align the global toolbar with + New Campaign first.
    // Idempotent — safe to call on every render. Runs in JS so it applies to all
    // localized dashboards without editing 7 HTML files.
    _arrangeCampaignToolbar() {
        const header = document.getElementById('campaignsHistoryHeader');
        if (!header) return;
        const heading = header.querySelector('h6');
        if (heading) heading.remove();
        // Left-align the cluster (the header was justify-content-between for the
        // old heading↔actions split; with the heading gone, pull actions left).
        header.classList.remove('justify-content-between');
        header.classList.add('justify-content-start');
        const newBtn = document.getElementById('campaignsNewCampaignBtn');
        const actions = newBtn && newBtn.parentElement;
        if (!actions) return;
        if (actions.firstElementChild !== newBtn) actions.prepend(newBtn);

        // Single info icon for the per-campaign stats explanation. It used to be
        // repeated on EVERY row (one (i) per campaign); consolidate it into one
        // icon in the section header. Idempotent — guarded by id. Not shown in the
        // empty state (task #371) — there are no per-campaign stats to explain yet.
        if (!this.campaigns.length) {
            document.getElementById('campaignsStatsInfo')?.remove();
        } else if (!document.getElementById('campaignsStatsInfo')) {
            const info = document.createElement('span');
            info.id = 'campaignsStatsInfo';
            info.className = 'text-muted ms-2 d-inline-flex align-items-center';
            info.style.cursor = 'help';
            info.setAttribute('data-bs-toggle', 'tooltip');
            info.setAttribute('data-bs-placement', 'top');
            info.setAttribute('title', "Each campaign's responded / pending / sent counts track responses from that campaign's own links. Your total response count may be higher if guests also replied via a direct link, QR code, or another campaign.");
            info.innerHTML = '<i class="bi bi-info-circle"></i>';
            header.appendChild(info);
            try { if (window.bootstrap?.Tooltip) new bootstrap.Tooltip(info); } catch (e) {}
        }
    }

    _renderExpandCollapseAllControl() {
        const anchor = document.getElementById('campaignsNewCampaignBtn');
        if (!anchor) return;
        const hasGroups = this.campaigns.some(c => c.hasSplitReminders && Array.isArray(c.childReminderCampaignIds) && c.childReminderCampaignIds.length);
        let btn = document.getElementById('campaignsToggleAllReminders');
        if (!hasGroups) { btn?.remove(); return; }
        if (!btn) {
            btn = document.createElement('button');
            btn.id = 'campaignsToggleAllReminders';
            btn.type = 'button';
            // outline-primary so Contact Details / Expand All / Remind All
            // Contacts read as one cohesive family (task #175 follow-up).
            btn.className = 'btn btn-outline-primary btn-sm';
            btn.addEventListener('click', () => this._toggleAllReminders());
        }
        // Place Expand/Collapse-all right after + New Campaign (toolbar order:
        // New Campaign · Expand all · All Contacts · Remind All Contacts).
        if (btn.previousElementSibling !== anchor) anchor.after(btn);
        this._syncExpandCollapseAllBtn();
    }

    _syncExpandCollapseAllBtn() {
        const btn = document.getElementById('campaignsToggleAllReminders');
        if (!btn) return;
        // Label reflects what one click will do: when any group is expanded,
        // the button collapses all; when everything is already collapsed
        // (e.g. smart-collapse defaults), it expands all.
        const anyExpanded = !!document.querySelector('.reminder-child-row:not(.d-none)');
        // In the dashboard language (wave-2 merge review: English on all 7).
        btn.innerHTML = anyExpanded
            ? `<i class="bi bi-arrows-collapse me-1"></i>${this._escape(this.cTxt('collapseAllReminders'))}`
            : `<i class="bi bi-arrows-expand me-1"></i>${this._escape(this.cTxt('expandAllReminders'))}`;
        btn.title = this.cTxt(anyExpanded ? 'collapseAllRemindersTip' : 'expandAllRemindersTip');
    }

    // One-time styling so the reminders toggle reads as a clickable button (not a
    // flat status badge) and the chevron animates on open/close.
    _ensureReminderToggleStyles() {
        if (document.getElementById('reminder-toggle-styles')) return;
        const st = document.createElement('style');
        st.id = 'reminder-toggle-styles';
        st.textContent = [
            // Neutral monochrome outline pill (was gold/beige, which added color
            // clutter to every campaign row). Still shows the reminder count.
            '.reminder-toggle{display:inline-flex;align-items:center;gap:.35rem;cursor:pointer;',
            'font-size:.78rem;font-weight:600;line-height:1;padding:.28rem .6rem;border-radius:999px;',
            'border:1px solid #d1d5db;background:#fff;color:#4b5563;white-space:nowrap;',
            'transition:background .12s,color .12s,border-color .12s,box-shadow .12s}',
            '.reminder-toggle:hover{background:#f9fafb;border-color:#9ca3af;color:#1f2937;box-shadow:0 1px 3px rgba(0,0,0,.08)}',
            '.reminder-toggle:focus-visible{outline:2px solid #9ca3af;outline-offset:1px}',
            '.reminder-toggle .reminder-caret{transition:transform .15s;font-size:.9em}',
            // Red dot: a reminder in this group needs the host (review #15).
            '.reminder-attention-dot{display:inline-block;width:.5rem;height:.5rem;border-radius:50%;background:#dc3545;margin-inline-start:.3rem;vertical-align:middle;flex-shrink:0}',
            '.reminder-toggle.needs-attention{border-color:#dc3545}',
            // Mobile (task #176): the pill is replaced by a full-size "Reminders (N)"
            // button in the card's action grid — hide the pill there.
            '@media (max-width: 767.98px){.reminder-toggle{display:none}',
            // Mobile reminder child card (task #177): smaller indent so the card
            // reads as part of the list, and the icon actions become one
            // evenly-spread full-width row with larger tap targets. Grid with
            // auto equal columns (works for any button count and is immune to
            // the flex-grow overrides other card rules apply to .btn).
            // Logical side, matching the row's inline margin-inline-start, so
            // the indent sits on the right in Hebrew and Arabic.
            '.reminder-child-row{margin-inline-start:.75rem !important}',
            '.reminder-child-row .reminder-child-actions{width:100%;display:grid !important;grid-auto-flow:column;grid-auto-columns:1fr;gap:.5rem !important;margin-top:.35rem}',
            '.reminder-child-row .reminder-child-actions .btn{width:100%;padding:.5rem .25rem !important;font-size:.95rem}',
            '}',
            // Campaign name reads as editable on hover (double-click to rename).
            '.campaign-name-editable{cursor:text;border-radius:4px;padding:0 .15rem;transition:background .12s}',
            '.campaign-name-editable:hover{background:#f1f3f5}'
        ].join('');
        document.head.appendChild(st);
    }

    // Who a reminder that hasn't gone out yet would go to if it sent now
    // (audit 2026-10-07, review #16). Its Status button opened the delivery
    // list, empty until it sends, so it said "No contacts found". The server
    // runs the dispatcher's own resolver read-only (GET .../reminder-audience)
    // and the list opens in the recipient review window, read-only, with a
    // note that it is finalized when the reminder sends.
    async showReminderAudiencePreview(childId, triggerBtn) {
        const campaign = (this.campaigns || []).find(c => c.id === childId);
        if (!campaign) return;
        const toast = window.languageHelpers?.showToast || window.showToast;
        if (triggerBtn) triggerBtn.disabled = true;
        let data = null;
        try {
            const user = window.firebaseAuth?.currentUser || firebase.auth().currentUser;
            const token = await user.getIdToken();
            const resp = await fetch(`/api/campaigns/${encodeURIComponent(childId)}/reminder-audience`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            data = await resp.json().catch(() => null);
            if (!resp.ok || !data || data.success === false) throw new Error(`reminder-audience ${resp.status}`);
        } catch (err) {
            console.warn('Reminder audience preview failed:', err?.message || err);
            if (toast) toast(this.cTxt('remAudienceFailed'), 'error');
            return;
        } finally {
            if (triggerBtn) triggerBtn.disabled = false;
        }
        const contacts = Array.isArray(data.contacts) ? data.contacts : [];
        const n = contacts.length;
        // A failed reminder's fixed list is who it was MEANT to reach: it
        // goes nowhere until edited. A fixed list is not checked again at
        // send time, so an empty one says so instead of the "checked again"
        // line (review of M3, 2026-10-07).
        const failed = ['failed', 'template_rejected', 'template_timeout'].includes(campaign.status);
        // A scheduled message, not a reminder (task #993).
        const plain = !campaign.isReminderCampaign;
        let lead;
        if (plain) lead = n === 1 ? this.cTxt('msgAudienceOne') : this.cTxt('msgAudienceMany', n);
        else if (data.resolved && n === 0) lead = this.cTxt('remAudienceFixedNone');
        else if (data.resolved && failed) lead = n === 1 ? this.cTxt('remAudienceMeantOne') : this.cTxt('remAudienceMeantMany', n);
        else if (data.resolved) lead = n === 1 ? this.cTxt('remAudienceFixedOne') : this.cTxt('remAudienceFixedMany', n);
        else lead = n === 1 ? this.cTxt('remAudienceNowOne') : this.cTxt('remAudienceNowMany', n);
        // One statement per line: "Already reminded by another message: 3"
        // has no full stop and ran into the next sentence when joined.
        const note = [lead];
        if (data.duplicatesSkipped > 0) note.push(this.cTxt('reminderDupSkipped', data.duplicatesSkipped));
        if (data.waitingForInvitation) note.push(this.cTxt('remSendNowWaiting'));
        if (!window.RecipientReviewModal) {
            if (toast) toast(note.join(' · '), 'info');
            return;
        }
        // The review window's mode per audience: its empty-state title, and
        // the duplicate check, which only the pending list runs.
        const MODE = { no_response: 'remind-pending', attending: 'remind-yes', maybe: 'remind-maybe', declined: 'remind-no', all: 'remind-all' };
        await window.RecipientReviewModal.show({
            contacts: contacts.map(c => ({ name: c.name, phone: c.phone, email: c.email })),
            mode: plain ? 'remind-all' : (MODE[campaign.reminderConfig?.recipients || 'no_response'] || 'remind-all'),
            formId: this.formId,
            preview: true,
            allowRemove: false,
            allowAdd: false,
            hideCancel: true,
            title: this.cTxt(plain ? 'msgAudienceTitle' : 'remAudienceTitle'),
            confirmLabel: this.cTxt('close'),
            note,
            emptyDesc: data.resolved ? false : this.cTxt('remAudienceEmpty'),
            fixedList: !!data.resolved
        });
    }

    // Edit a linked reminder child that hasn't sent yet: its message, send
    // date/time, and audience. Writes straight to the child doc (mirrors the
    // pause/resume direct-write pattern). Changing the date pins it to that
    // absolute instant (timing → 'custom'); the dispatch cron reads
    // scheduledSendDate + reminderConfig.recipients, so both take effect.
    async showEditReminderModal(childId) {
        const campaign = this.campaigns.find(c => c.id === childId);
        if (!campaign) return;
        const cfg = campaign.reminderConfig || {};
        const pad = n => String(n).padStart(2, '0');
        const sd = campaign.scheduledSendDate ? (campaign.scheduledSendDate.toDate?.() || new Date(campaign.scheduledSendDate)) : null;
        const localVal = (sd && !isNaN(sd)) ? `${sd.getFullYear()}-${pad(sd.getMonth() + 1)}-${pad(sd.getDate())}T${pad(sd.getHours())}:00` : '';
        const recip = cfg.recipients || 'no_response';
        const t = this.getWizardTranslations();

        // Links / action buttons — prefill from the reminder, falling back to the
        // form's registry URL / map link (mirrors the wizard's per-reminder picker).
        const cfgButtons = (Array.isArray(cfg.buttons) && cfg.buttons.length) ? cfg.buttons : ['rsvp'];
        const bUrls = cfg.buttonUrls || {};
        const fd = this.formData || {};
        const formRegistry = String(fd.giftRegistryUrl || '').trim();
        const formAddr = String(fd.locationAddress || '').trim();
        const formMaps = String(fd.customGoogleMapsUrl || '').trim()
            || (formAddr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formAddr)}` : '');
        const regVal = (bUrls.registry != null ? bUrls.registry : formRegistry);
        const dirVal = (bUrls.directions != null ? bUrls.directions : formMaps);
        const wazeVal = (bUrls.waze != null ? bUrls.waze : (String(fd.customWazeUrl || '').trim() || (formAddr ? `https://waze.com/ul?q=${encodeURIComponent(formAddr)}` : '')));
        const gmapsVal = (bUrls.gmaps != null ? bUrls.gmaps : formMaps);
        const livestreamVal = (bUrls.livestream != null ? bUrls.livestream : String(fd.livestreamUrl || '').trim());

        // Default button labels follow the FORM's language. These labels are sent to
        // the form's guests, so they must match the invitation's language — NOT the
        // campaign's stored sourceLanguage, which can be the host's account language
        // and mismatch an English form (that bug showed Hebrew defaults on English
        // forms). Falls back to the dashboard UI language. Saved per-reminder
        // overrides (cfgLabels) always win over these defaults.
        const lang = this.formData?.defaultLanguage || this.dashboardLanguage || 'en';
        const defLabels = (window.QR_REMINDER_BTN_LABELS && (window.QR_REMINDER_BTN_LABELS[lang] || window.QR_REMINDER_BTN_LABELS.en)) || { rsvp: 'RSVP Here', registry: 'View Registry', directions: 'Get Directions', website: 'Wedding Website' };
        const cfgLabels = cfg.buttonLabels || {};

        // Per-reminder image. The inherited invitation photo (the parent campaign's
        // image) is the default; the child's own bulkImageUrl is what it currently
        // sends — a custom image is one that differs from the inherited photo.
        const parentCampaign = this.campaigns.find(c => c.id === campaign.parentCampaignId);
        const inheritedPhoto = parentCampaign?.bulkImageUrl || null;
        const currentImg = campaign.bulkImageUrl || null;
        const customImg = (currentImg && currentImg !== inheritedPhoto) ? currentImg : '';
        const photoIncluded = !!currentImg;

        // Wedding-website button URL: a saved per-reminder override wins, else the
        // campaign's chosen wedding-site URL (this/parent), else blank for the host
        // to paste. The server resolves the same fallback at send time.
        const webVal = (bUrls.website != null ? bUrls.website : (campaign.rsvpWebsiteUrl || parentCampaign?.rsvpWebsiteUrl || ''));

        // Standalone follow-ups ("Remind All …") were sent to an explicit,
        // already-resolved recipient list — the audience dropdown would imply
        // the list can still change, so show a fixed-recipients note instead.
        const fixedCount = campaign.reminderContactsResolved
            ? (campaign.totalContacts || (campaign.contacts || []).length || 0)
            : 0;
        const audienceNote = campaign.reminderContactsResolved
            ? (t.reminderFixedRecipientsNote || 'This reminder goes to the {count} recipients you selected.').replace('{count}', fixedCount)
            : null;

        // Language Options (task #342): the available target/modes come from the
        // ORIGINAL campaign (so a reminder previously downgraded to "Send as
        // written" can still switch back to bilingual); the default selection is
        // the reminder's CURRENT mode.
        const li = this._reminderLangInfo(parentCampaign || campaign);
        const childLangMode = this._campaignLangShape(campaign).mode;
        const childMode = childLangMode === 'bilingual' ? 'bilingual'
            : childLangMode === 'translate' ? 'translate' : 'original';
        const bodyHtml = this._reminderComposerBodyHtml({ localVal, recip, audienceNote, buttons: cfgButtons, regVal, dirVal, wazeVal, gmapsVal, webVal, livestreamVal, defLabels, cfgLabels, message: this._isDefaultReminderMessage(campaign.customMessage) ? this._defaultReminderMessage(recip) : campaign.customMessage, eventPhotoUrl: inheritedPhoto, customImageUrl: customImg, photoIncluded, remTranslateTarget: li.target, remTranslateLangName: li.name, reminderLangMode: (li.target ? childMode : 'original'), sourceLanguage: (parentCampaign || campaign)?.sourceLanguage || 'en', formLanguages: this._reminderFormLanguages(parentCampaign || campaign), translatedMessage: campaign.translatedMessage || '', eventTitle: campaign.formTitle || parentCampaign?.formTitle || '', includeQR: (campaign.includeQRCode ?? parentCampaign?.includeQRCode) === true, invitationSplash: campaign.invitationSplash !== false });
        // Does saving this edit have to put the message back to WhatsApp?
        //
        // The card offers "Edit & Resend" whenever the template is broken, but
        // this editor used to decide on THREE DEAD STATUSES instead — so a
        // reminder carrying the error without one of them got the button, and
        // then the save quietly wrote the text and said "Reminder updated"
        // (task #592 round 5: "it still says save changes and nothing happens").
        // One shared test now, so the button and the save cannot disagree.
        const remTemplateError = DashboardCampaigns.hasTemplateError(campaign);
        // A reminder the dispatcher cancelled (its event was deleted, RSVPs
        // closed, the event moved earlier past its moment) is dead the same
        // way: once the host has fixed the cause — extended the deadline,
        // picked a new time — saving the edit has to re-arm it. Only those
        // causes: the host's own Cancel stays final, and so does a cancelled
        // or deleted invitation, which cannot come back (re-arming one of
        // those just looped it into the same cancel behind a "Save &
        // Resubmit" that never sends).
        const remWasDead = ['template_rejected', 'template_timeout', 'failed'].includes(campaign.status)
            || (campaign.status === 'cancelled' && DashboardCampaigns.REMINDER_FIXABLE_CANCEL_REASONS.includes(campaign.cancelledReason));
        const needsResubmit = remTemplateError || remWasDead;
        // Only name WhatsApp when there is a WhatsApp leg to resubmit — an
        // email-only reminder has no template and never goes near Meta. Judged
        // on the guests, with the wizard's and the dispatcher's test:
        // whatsappCount also counts US guests, whose texts go SMS-direct, and
        // since 2026-10-07 a reminder with no WhatsApp guest is sent without
        // its template, so "sent back to WhatsApp for approval" promised a wait
        // that never came. Until it is resolved, a reminder's guests come from
        // its invitation's list.
        const needsWa = window.BulkSharingWizard?.contactNeedsWhatsAppTemplate;
        const remGuests = campaign.reminderContactsResolved
            ? (campaign.contacts || [])
            : [...(campaign.contacts || []), ...(parentCampaign?.contacts || [])];
        const remHasWhatsApp = remTemplateError || (needsWa
            ? remGuests.some(c => needsWa(c))
            : ((campaign.whatsappCount || 0) > 0 || !!campaign.whatsappTemplate?.name));

        this._openReminderModal({
            id: 'editReminderDrawer',
            // Templates are offered in the language this reminder is written in.
            composeLang: (parentCampaign || campaign)?.sourceLanguage,
            titleHtml: `${t.reminderEditTitle || 'Edit Reminder'} ${campaign.reminderIndex || ''}`,
            subHtml: (parentCampaign && parentCampaign.name) ? this._escape(parentCampaign.name) : '',
            bodyHtml,
            formId: campaign.formId || parentCampaign?.formId,
            primaryLabel: needsResubmit
                ? (remHasWhatsApp
                    ? (t.saveAndResubmitWhatsApp || 'Save & Resubmit to WhatsApp')
                    : (t.saveAndResend || 'Save & Resend'))
                : (t.reminderSaveChanges || 'Save changes'),
            onPrimary: async (el, oc) => {
            const v = this._collectReminderComposerValues(el);
            if (!v) return;

            try {
                const db = firebase.firestore();
                // Language Options (task #342): re-resolve using the host's drawer
                // choice (falls back to the reminder's current mode) so an edited
                // bilingual reminder never ships a stale translation, and switching
                // to "Send as written" clears the old one. Target/available modes
                // come from the ORIGINAL campaign so a downgraded child can still
                // switch back to bilingual.
                const langRes = await this._resolveReminderTranslation(
                    parentCampaign || campaign,
                    (v.message || '').trim() ? v.message : this._defaultReminderMessage(v.audience),
                    v.languageMode,
                    v
                );
                const editedMessage = langRes.customMessage;
                const editedTranslated = langRes.translatedMessage;
                const update = {
                    customMessage: editedMessage,
                    translatedMessage: editedTranslated || firebase.firestore.FieldValue.delete(),
                    campaignLanguageMode: langRes.campaignLanguageMode || campaign.campaignLanguageMode || 'single',
                    campaignLanguages: langRes.campaignLanguages || campaign.campaignLanguages || null,
                    hasCustomMessage: !!(v.message && v.message.trim()),
                    scheduledSendDate: v.iso,
                    // includeRsvpLink drives the RSVP button in both the email and
                    // the WhatsApp template (kept in sync with the RSVP checkbox).
                    includeRsvpLink: v.buttons.includes('rsvp'),
                    'reminderConfig.recipients': v.audience,
                    'reminderConfig.timing': 'custom',
                    'reminderConfig.customDate': v.datePart,
                    'reminderConfig.customSendAt': v.iso,
                    'reminderConfig.buttons': v.buttons,
                    'reminderConfig.buttonUrls': v.buttonUrls,
                    'reminderConfig.buttonLabels': v.buttonLabels
                };
                // Every edit makes the timing the host's own ('custom' above),
                // which the event-date follow never moves, so "moved with the
                // event date" no longer describes it.
                update.rescheduledForEventDateAt = firebase.firestore.FieldValue.delete();
                // Only when the host was actually shown the switch (task #631).
                if (v.includeQRCode !== null) update.includeQRCode = v.includeQRCode === true;
                // Per-reminder image — a custom upload, the inherited invitation
                // photo, or none. Changing it shifts the template content key, so
                // it recreates cleanly.
                const img = this._collectReminderImage(el, inheritedPhoto);
                update.bulkImageUrl = img.url;
                update.hasImage = img.hasImage;
                update.invitationSplash = img.splash;
                // Self-heal dead reminders: the failed-state chip tells hosts to
                // "edit it and try again", but these statuses are terminal — no
                // dispatch query revisits them, so without this the edit saves
                // and the reminder still never sends. Re-arm it, re-resolve the
                // audience at the new send time, and drop a rejected/expired
                // template (it can never send; the cron recreates it from the
                // edited content). A 'failed' child keeps its template — it may
                // be approved and reusable; the contentKey guard handles staleness.
                if (needsResubmit) {
                    // Re-arming the SCHEDULE is only right for a reminder that
                    // actually died — a broken template on one that is still
                    // scheduled (or has already gone out to its email half) must
                    // not have its status rewritten, or editing it would send it
                    // again to everyone.
                    if (remWasDead) {
                        update.status = 'scheduled';
                        update.reminderContactsResolved = false;
                    } else if (remTemplateError && ['sent', 'partially_sent'].includes(campaign.status)) {
                        // Its email/SMS guests have it and only WhatsApp was lost
                        // (refused, or never approved). No dispatch query revisits
                        // 'partially_sent', so the save resubmitted nothing.
                        // 'emails_sent_whatsapp_pending' is what the dispatcher
                        // reads as "WhatsApp only from here", as the wizard's Edit
                        // & Resend does for a campaign.
                        update.status = 'emails_sent_whatsapp_pending';
                    }
                    // Wipe the FAILURE, not just the status (task #592). Editing
                    // re-arms the reminder, but the reason it died stayed on the
                    // doc — so the red state kept being drawn from fields nothing
                    // had cleared, and a host who had just fixed the problem was
                    // still looking at the old complaint. Anything that fails
                    // again is written afresh by the server.
                    const del = firebase.firestore.FieldValue.delete();
                    update.templateCreationError = del;
                    update.templateCreationErrorAt = del;
                    update.lastError = del;
                    update.lastErrorAt = del;
                    update.failureReason = del;
                    update.failureCode = del;
                    update.failedAt = del;
                    update.timeoutReason = del;
                    update.timeoutAt = del;
                    update.noSendDetectedAt = del;
                    update.noSendAttempts = del;
                    update.cancelledReason = del;
                    update.cancelledAt = del;
                    // The host has acted, so a refusal of the edited message is
                    // worth another email (the wizard clears it the same way).
                    update.templateFailureNoticeAt = del;
                    // Display only: says "Re-submitting" on the row until the
                    // reminder reaches a sending or sent state, so saving an edit
                    // visibly does something instead of dropping silently back to
                    // "Scheduled".
                    update.resubmittedAt = firebase.firestore.FieldValue.serverTimestamp();
                    // A STANDALONE reminder ("Remind All") has no parent, so the
                    // dispatch-time resolver needs scope:'form_contacts' to know
                    // how to rebuild its audience — without it the resolver
                    // skips the doc forever and the re-armed reminder never sends.
                    if (remWasDead && !campaign.parentCampaignId) {
                        update['reminderConfig.scope'] = 'form_contacts';
                    }
                    // Drop the template so the dispatcher builds a fresh one from
                    // the edited words. A 'failed' child keeps its template — it
                    // may be approved and reusable — but a template Meta REFUSED,
                    // or one whose name is stale against a later refusal, can
                    // never send and has to go.
                    if (remTemplateError || campaign.status !== 'failed') {
                        update.whatsappTemplate = firebase.firestore.FieldValue.delete();
                    }
                }
                await db.collection('bulk_campaigns').doc(childId).update(update);
                oc.hide();
                const savedMsg = (needsResubmit && remHasWhatsApp)
                    ? (t.reminderResubmittedToast || 'Reminder updated and sent back to WhatsApp for approval.')
                    : (t.reminderUpdatedToast || 'Reminder updated');
                if (window.languageHelpers?.showToast) window.languageHelpers.showToast(savedMsg, 'success');
                await this.loadCampaigns();
            } catch (err) {
                console.error('Error updating reminder:', err);
                alert((t.reminderUpdateFailed || 'Failed to update reminder: ') + err.message);
            }
            }
        });
    }

    // ---- Shared reminder composer (Edit + new per-campaign "Remind") --------
    // Inner modal-body HTML. BOTH the edit and create modals use this, with the
    // same field IDs, so _collectReminderComposerValues can read either one.
    _reminderComposerBodyHtml(o) {
        const t = this.getWizardTranslations();
        const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const btns = Array.isArray(o.buttons) ? o.buttons : ['rsvp'];
        const def = o.defLabels || { rsvp: 'RSVP Here', registry: 'View Registry', directions: 'Get Directions', waze: 'Waze', gmaps: 'Google Maps', website: 'Wedding Website' };
        if (def.waze == null) def.waze = 'Waze';
        if (def.gmaps == null) def.gmaps = 'Google Maps';
        if (def.livestream == null) def.livestream = 'Watch Livestream';
        // A bilingual reminder sends its default captions in both languages
        // (task #833) — show them that way, so the placeholder is what goes out.
        const pairedDef = (o.reminderLangMode === 'bilingual' && o.remTranslateTarget && window.BulkSharingWizard?.pairedReminderLabels)
            ? window.BulkSharingWizard.pairedReminderLabels(def, o.remTranslateTarget)
            : def;
        const lbl = o.cfgLabels || {};
        // Most field labels reuse the wizard's already-translated bulkWizard keys
        // (sendTo, attendingGuests, reminderMessage, the button keys, …) so the
        // modal localizes with the dashboard language. Only modal-specific copy
        // (recipients-finalized note) needs its own key.
        // Audience: a real <select> for per-campaign reminders, or a static
        // "sending to N selected guests" note + a hidden #er-audience (so the
        // shared collector still reads a value) for the "Remind All" flow, where
        // the recipients were already chosen in the review list.
        const audienceBlock = o.audienceNote ? `
                <div class="mb-3">
                  <label class="form-label fw-semibold small">${t.sendTo || 'Send to'}</label>
                  <div class="d-flex align-items-center gap-2 px-3 py-2" style="background:#f1f5f9;border:1px solid #e2e8f0;border-radius:8px;font-size:0.86rem;color:#334155;">
                    <i class="bi bi-people-fill"></i><span>${o.audienceNote}</span>
                  </div>
                  <!-- Hidden select keeps the collector working; carries the reminder's
                       REAL audience value so saving never rewrites it to 'all'. -->
                  <select class="d-none" id="er-audience"><option value="${esc(o.recip || 'all')}" selected></option></select>
                </div>` : `
                <div class="mb-3">
                  <label class="form-label fw-semibold small">${t.sendTo || 'Send to'}</label>
                  <select class="form-select" id="er-audience">
                    <option value="no_response" ${o.recip === 'no_response' ? 'selected' : ''}>${t.guestsWhoHaventRSVPd || "Guests who haven't responded"}</option>
                    <option value="attending" ${o.recip === 'attending' ? 'selected' : ''}>${t.attendingGuests || 'Guests attending'}</option>
                    <option value="maybe" ${o.recip === 'maybe' ? 'selected' : ''}>${t.guestsAnsweredMaybe || 'Guests who answered Maybe'}</option>
                    <option value="declined" ${o.recip === 'declined' ? 'selected' : ''}>${t.guestsDeclined || 'Guests who declined'}</option>
                    <option value="all" ${o.recip === 'all' ? 'selected' : ''}>${t.allGuests || 'All contacts'}</option>
                  </select>
                  <div class="form-text">${t.reminderRecipientsFinalized || 'Recipients are finalized when the reminder sends.'}</div>
                  ${o.removedNote ? `<div class="form-text d-flex align-items-start gap-1"><i class="bi bi-person-dash flex-shrink-0"></i><span>${o.removedNote}</span></div>` : ''}
                </div>`;
        return `
                ${o.audienceNote ? '' : `<div class="alert alert-info d-flex align-items-start gap-2 py-2 px-3 mb-3" role="note" style="border-radius:8px; font-size:0.83rem;">
                  <i class="bi bi-people-fill flex-shrink-0" style="margin-top:2px;"></i>
                  <div>${t.reminderAudienceScopeNote || 'This follow-up will be sent <strong>only</strong> to the recipients in this specific message — not to all of your event\'s guests.'}</div>
                </div>`}
                ${(() => {
                    // Explicit "Send now" vs "Schedule for later" choice (task
                    // #191). Previously "now" was implied by leaving the
                    // prefilled time untouched — hosts couldn't tell — and the
                    // native datetime picker exposed minute options the hourly
                    // dispatcher ignores. The scheduled path now uses a date
                    // input + an hour-only dropdown, matching delivery reality.
                    const isFuture = o.forceSchedule === true
                        || (!!o.localVal && (new Date(o.localVal).getTime() > Date.now() + 60000));
                    const nowDisabled = o.forceSchedule === true ? ' disabled' : '';
                    const dateVal = (o.localVal || '').slice(0, 10);
                    const savedHour = (o.localVal || '').slice(11, 13);
                    const hourOptions = Array.from({ length: 24 }, (_, h) => {
                        const hh = String(h).padStart(2, '0');
                        return `<option value="${hh}"${savedHour === hh ? ' selected' : ''}>${hh}:00</option>`;
                    }).join('');
                    return `
                <div class="mb-3">
                  <label class="form-label fw-semibold small d-block">${t.whenToSend || 'Send date & time'}</label>
                  <div class="btn-group w-100 mb-2" role="group" aria-label="${t.whenToSend || 'Send date & time'}">
                    <input type="radio" class="btn-check" name="er-when-mode" id="er-mode-now" value="now"${isFuture ? '' : ' checked'}${nowDisabled}>
                    <label class="btn btn-outline-primary btn-sm" for="er-mode-now"><i class="bi bi-send me-1"></i>${t.reminderSendNowChoice || 'Send now'}</label>
                    <input type="radio" class="btn-check" name="er-when-mode" id="er-mode-later" value="later"${isFuture ? ' checked' : ''}>
                    <label class="btn btn-outline-primary btn-sm" for="er-mode-later"><i class="bi bi-calendar-event me-1"></i>${t.reminderScheduleChoice || 'Schedule for later'}</label>
                  </div>
                  <div id="er-now-hint" class="form-text"${isFuture ? ' style="display:none"' : ''}>${t.reminderGoesOutShortly || 'Your reminder goes out shortly (once WhatsApp approves the message).'}</div>
                  <div id="er-schedule-fields"${isFuture ? '' : ' style="display:none"'}>
                    <div class="d-flex gap-2">
                      <input type="date" class="form-control" id="er-when-date" value="${dateVal}">
                      <select class="form-select" id="er-when-hour" style="max-width: 9rem;">${hourOptions}</select>
                    </div>
                    <div class="form-text">${t.reminderHourOnlyHint || 'Reminders are sent on the hour.'}</div>
                  </div>
                </div>`;
                })()}
                ${audienceBlock}
                <!-- The message comes FIRST (task #631). What the reminder says is
                     the thing the host came here to write; the buttons decorate it.
                     Having to scroll past six link cards to reach the text box read
                     as if the links were the point. Its translations panel travels
                     with it — they are one thought. -->
                <div class="mb-3">
                  <label class="form-label fw-semibold small">${t.reminderMessage || 'Message'}</label>
                  ${this._personalizationToolbarHtml()}
                  <textarea class="form-control" id="er-message" rows="4" dir="auto">${esc(o.message)}</textarea>
                  ${window.TableSplitNotice ? window.TableSplitNotice.html('id="er-table-split-notice"') : ''}
                  <!-- A template's [bracketed] blanks, still unfilled (task #603). -->
                  <div id="er-template-blanks" class="mt-blanks-hint"></div>
                </div>
                ${this._buildReminderTranslationsPanel(o)}
                <!-- Links / buttons are managed through toggle chips (see
                     _reminderLinkChipsHtml). The chips drive hidden #er-btn-*/
                     #er-url-*/#er-label-* inputs so _collectReminderComposerValues
                     keeps reading exactly what it always has — no naked URL
                     textboxes clutter the form. -->
                ${this._reminderLinkChipsHtml({
                  active: btns,
                  urls: { registry: o.regVal, directions: o.dirVal, waze: o.wazeVal, gmaps: o.gmapsVal, website: o.webVal, livestream: o.livestreamVal },
                  labels: lbl,
                  defLabels: pairedDef,
                  bilingualLabels: pairedDef !== def,
                  showKeys: ['rsvp', 'registry', ...(btns.includes('directions') ? ['directions'] : []), 'waze', 'gmaps', 'website', 'livestream']
                })}
                ${this._reminderQrToggleHtml(o, t)}
                ${(() => {
                  // Per-reminder image picker. Defaults to the campaign's invitation
                  // photo, but the host can swap in a different uploaded image — or
                  // add one when the campaign has no photo of its own. A custom image
                  // overrides the inherited photo; the checkbox (only shown when
                  // there IS an invitation photo) suppresses the image entirely.
                  const campaignPhoto = o.eventPhotoUrl || '';
                  const customImg = o.customImageUrl || '';
                  const imgIncluded = o.photoIncluded !== false;
                  const effImg = imgIncluded ? (customImg || campaignPhoto) : '';
                  const showCheckbox = !!campaignPhoto;
                  return `
                <div class="mb-1 er-image-picker" data-inherited="${esc(campaignPhoto)}">
                  <label class="form-label fw-semibold small d-block mb-1"><i class="bi bi-image me-1"></i>${t.reminderImageHeading || 'Image'}</label>
                  <input type="hidden" id="er-custom-image-url" value="${esc(customImg)}">
                  ${showCheckbox ? `
                  <div class="form-check mb-1">
                    <input class="form-check-input" type="checkbox" id="er-include-image" ${imgIncluded ? 'checked' : ''}>
                    <label class="form-check-label small" for="er-include-image" id="er-include-image-label">${customImg ? (t.reminderIncludeThisImage || 'Include this image') : (t.reminderIncludeImage || 'Include the invitation photo')}</label>
                  </div>` : ''}
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <input type="file" class="d-none" id="er-image-input" accept="image/*">
                    <button type="button" class="btn btn-sm btn-outline-secondary rounded-pill px-3" id="er-image-browse">
                      <i class="bi ${customImg ? 'bi-arrow-repeat' : (campaignPhoto ? 'bi-images' : 'bi-image')} me-1"></i><span id="er-image-browse-text">${customImg ? (t.changeImage || 'Change image') : (campaignPhoto ? (t.reminderUseDifferentImage || 'Use a different image') : (t.addImage || 'Add an image'))}</span>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger rounded-circle p-1 px-2 ${customImg ? '' : 'd-none'}" id="er-image-remove" title="${t.removeImage || 'Remove'}">
                      <i class="bi bi-trash"></i>
                    </button>
                    <div class="d-none text-primary" id="er-image-spinner"><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></div>
                    <img id="er-image-thumb" class="${effImg ? '' : 'd-none'}" src="${effImg}" alt="" style="height:42px;width:42px;object-fit:cover;border-radius:8px;border:1px solid #dee2e6;">
                  </div>
                  <!-- Full-screen on tap (task #833), the invitation composer's
                       switch. Only while the reminder carries the INVITATION
                       photo: the full-screen view paints the form's invitation
                       image, so a reminder with its own picture would open onto
                       a different one. _syncReminderSplashOption shows/hides it. -->
                  <div id="er-splash-option" class="mt-2 p-2 px-3 border rounded ${(imgIncluded && campaignPhoto && !customImg) ? '' : 'd-none'}">
                    <div class="form-check form-switch d-flex align-items-center gap-2 mb-0">
                      <input class="form-check-input" type="checkbox" role="switch" id="er-invitation-splash" ${o.invitationSplash !== false ? 'checked' : ''}>
                      <label class="form-check-label small fw-semibold" for="er-invitation-splash">
                        <i class="bi bi-arrows-fullscreen me-1"></i>${t.invitationSplashLabel || 'Open full-screen when tapped'}
                      </label>
                    </div>
                    <small class="text-muted d-block mt-1 ms-4">${t.invitationSplashDesc || 'Guests who tap your invitation in WhatsApp see it full size, then your RSVP form.'}</small>
                  </div>
                </div>`;
                })()}`;
    }

    // ── Message & Translations for a reminder (task #453) ──────────────────
    // The reminder composer now uses the SAME panel as the invitation composer:
    // one prominent Translate button under the message box, then a "Translations
    // ready" list with a checkbox and an editable box per language. It replaces
    // the compact three-radio "Language Options" drawer from task #342 — the
    // three radios were only ever three tick patterns, and the drawer could not
    // offer a language the campaign itself did not already have.
    //
    // Only the UI changed. What is stored is still campaignLanguageMode +
    // campaignLanguages + translatedMessage, exactly the fields the send path
    // reads, so nothing downstream moved.
    // Languages to offer a reminder: the ones already switched on in the form,
    // plus whatever the parent campaign is using. A campaign that was sent in one
    // language shouldn't stop a host reminding in another the form supports.
    _reminderFormLanguages(campaign) {
        const fromForm = Array.isArray(this.formData?.enabledLanguages) ? this.formData.enabledLanguages : [];
        const fromCampaign = Array.isArray(campaign?.campaignLanguages) ? campaign.campaignLanguages : [];
        return [...new Set([...fromForm, ...fromCampaign])].filter(Boolean);
    }

    _buildReminderTranslationsPanel(o) {
        if (!window.MessageTranslationsPanel) return '';
        const id = 'rem';
        window.MessageTranslationsPanel.dispose(id);

        // Seed from the reminder's own mode, so reopening one shows the ticks it
        // was saved with. The offered languages come from the FORM plus whatever
        // the parent campaign uses, so a reminder can be sent in a language the
        // campaign itself wasn't.
        const target = o.remTranslateTarget || null;
        const mode = (o.reminderLangMode === 'translate' || o.reminderLangMode === 'bilingual') ? o.reminderLangMode : 'single';
        const source = o.sourceLanguage || 'en';
        const selection = {
            mode,
            langs: mode === 'bilingual' ? [source, target] : (mode === 'translate' ? [target] : [source]),
            translatedMessage: o.translatedMessage || ''
        };

        const panel = new window.MessageTranslationsPanel({
            id,
            getTranslations: () => this.getWizardTranslations(),
            getSourceLang: () => source,
            getFormLanguages: () => (o.formLanguages || []).concat(target ? [target] : []),
            getSourceText: () => document.getElementById('er-message')?.value || '',
            setSourceText: (text) => {
                const box = document.getElementById('er-message');
                if (box && box.value !== text) box.value = text;
            },
            getSelection: () => selection,
            setSelection: (next) => {
                selection.mode = next.mode;
                selection.langs = next.langs;
                selection.translatedMessage = next.translatedMessage;
            },
            // A reminder always goes out on whatever channels the campaign uses;
            // showing both counters is the honest default here.
            getChannels: () => ['mobile', 'email'],
            getEventTitle: () => o.eventTitle || '',
            showToast: (msg, type) => (window.showToast ? window.showToast(msg, type) : console.log(msg))
        });
        this._reminderTrPanel = panel;
        return `<div class="mb-3">${panel.html()}</div>`;
    }

    // ---- Personalization toolbar (shared) ---------------------------------
    // The merge-tag pills that sit above a reminder's message box. Both reminder
    // editors render this same block: the composer (New reminder / Edit
    // reminder / Remind all) and the Reminder Schedule drawer, which shipped
    // with NO toolbar at all -- a host editing a scheduled reminder there had no
    // way to insert {{table_number}}, which is exactly the "I used the table
    // placeholder and nothing happened" report on task #594.
    //
    // The +Table Number pill starts disabled and is enabled by
    // _wireReminderComposerInteractions once the seating check confirms the form
    // uses the built-in seating chart. A click that lands BEFORE that check
    // resolves now waits for it instead of being swallowed.
    _personalizationToolbarHtml() {
        const t = this.getWizardTranslations();
        return `
                  <div class="d-flex gap-1 flex-wrap align-items-center mb-2">
                    <span class="text-muted small me-1" style="line-height:1.8;"><i class="bi bi-person-badge me-1"></i>${t.personalize || 'Personalize:'}</span>
                    <button type="button" class="er-tag-btn" data-token="{{name}}" style="font-size:0.75rem;background:#dce8ff;color:#1a4fd6;border:1px solid #a8c0f5;border-radius:20px;padding:2px 10px;cursor:pointer;font-weight:500;">${t.fullNamePlus || '+ Full Name'}</button>
                    <button type="button" class="er-tag-btn" data-token="{{first_name}}" style="font-size:0.75rem;background:#dce8ff;color:#1a4fd6;border:1px solid #a8c0f5;border-radius:20px;padding:2px 10px;cursor:pointer;font-weight:500;">${t.firstNamePlus || '+ First Name'}</button>
                    <!-- The Email personalization token was intentionally removed from the
                         reminder composer: guests already know their own email, so inserting
                         it into a message they receive reads as robotic and adds clutter. -->
                    <button type="button" class="er-tag-btn" id="er-table-tag" data-token="{{table_number}}" data-active="0" style="font-size:0.75rem;background:#eef0f3;color:#9aa3af;border:1px solid #d6dae0;border-radius:20px;padding:2px 10px;cursor:not-allowed;font-weight:500;">${t.tableNumberPlus || '+ Table Number'}</button>
                  </div>
                  <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <!-- Pre-written reminders (task #603), offered for whichever
                         audience is chosen in the dropdown above. -->
                    ${window.MessageTemplates
                        ? window.MessageTemplates.openButtonHtml('er-templates-btn', t.messageTemplates || 'Templates')
                        : ''}
                    <span class="text-muted" style="font-size:0.7rem;">${t.clickToInsert || 'Click to insert — each guest gets their own personalized version.'}</span>
                  </div>
                  <div id="er-table-tag-hint" class="mb-2" style="display:none;font-size:0.72rem;color:#856404;background:#fff8e1;border:1px solid #ffe58a;border-radius:6px;padding:5px 9px;"><i class="bi bi-info-circle me-1"></i>${t.tableTagDisabledHint || "Set up the built-in seating chart to personalize with each guest's table number."}</div>`;
    }

    // Does this form use the built-in seating chart (>= 1 table)? Cached per
    // formId as a PROMISE, so a pill clicked while the request is still in
    // flight can await the same answer instead of reading a not-yet-set flag
    // and silently doing nothing.
    _seatingHasTables(formId) {
        if (!formId) return Promise.resolve(false);
        if (!this._seatingHasTablesCache) this._seatingHasTablesCache = new Map();
        if (this._seatingHasTablesCache.has(formId)) return this._seatingHasTablesCache.get(formId);
        const p = (async () => {
            try {
                const authToken = window.firebaseAuth?.currentUser
                    ? await window.firebaseAuth.currentUser.getIdToken()
                    : null;
                const resp = await fetch(`/api/seating/${encodeURIComponent(formId)}`, {
                    headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
                });
                if (!resp.ok) throw new Error(`seating ${resp.status}`);
                const data = await resp.json();
                const tables = data?.config?.tables || [];
                // Split parties with unnamed seats, for the notice under the
                // message box while it uses {{table_number}} (task #905).
                if (!this._splitUnnamedByForm) this._splitUnnamedByForm = new Map();
                this._splitUnnamedByForm.set(formId, Number(data?.splitUnnamedParties) || 0);
                return Array.isArray(tables) && tables.length > 0;
            } catch (e) {
                // Don't cache a failure as "no seating chart" -- the host may
                // simply have been offline for a moment, and a sticky false
                // would keep the pill dead for the rest of the session.
                console.warn('Could not load seating status for the table tag:', e.message);
                this._seatingHasTablesCache.delete(formId);
                return false;
            }
        })();
        this._seatingHasTablesCache.set(formId, p);
        return p;
    }

    // Forget the cached seating answer -- called after the host edits the
    // seating chart so the next composer sees the new tables.
    invalidateSeatingStatus() {
        this._seatingHasTablesCache = null;
    }

    // Wire the merge-tag pills inside an open reminder editor to its message
    // textarea. Handles both editors: the composer writes into #er-message, the
    // Reminder Schedule drawer into #sched-message.
    _wirePersonalizationToolbar(el, formId) {
        const msg = () => el.querySelector('#er-message') || el.querySelector('#sched-message');

        // Templates (task #603). The audience dropdown sits directly above the
        // message box, so the offered reminders follow whatever it currently
        // says — read at click time, not at render time, because the host
        // changes it after the drawer has been built.
        const tplBox = msg();
        const blanksHint = el.querySelector('#er-template-blanks');
        let syncBlanks = null;
        if (window.MessageTemplates && tplBox && blanksHint) {
            syncBlanks = window.MessageTemplates.wireBlanksHint(
                tplBox, blanksHint, this._reminderComposeLang || this.dashboardLanguage);
        }
        el.querySelector('#er-templates-btn')?.addEventListener('click', () => {
            if (!window.MessageTemplates || !tplBox) return;
            window.MessageTemplates.open({
                kind: 'reminder',
                audience: el.querySelector('#er-audience')?.value || 'all',
                // Written in the language of the message, not of the browser
                // (task #603 round 2).
                lang: this._reminderComposeLang || this.dashboardLanguage,
                currentText: tplBox.value,
                prefill: window.MessageTemplates.prefillFromEvent(
                    this.formData || {}, this._reminderComposeLang || this.dashboardLanguage),
                onPick: (body) => {
                    tplBox.value = body;
                    tplBox.dispatchEvent(new Event('input', { bubbles: true }));
                    tplBox.focus();
                    if (syncBlanks) syncBlanks();
                }
            });
        });
        const hint = () => el.querySelector('#er-table-tag-hint');
        const tableBtn = el.querySelector('#er-table-tag');
        const seatingReady = this._seatingHasTables(formId);

        // Task #905: warn while the message uses {{table_number}} and some
        // parties would print as "Dana 2, Dana 3".
        const splitNotice = () => window.TableSplitNotice?.render(
            el.querySelector('#er-table-split-notice'), msg()?.value || '',
            this._splitUnnamedByForm?.get(formId) || 0, this.getWizardTranslations());
        msg()?.addEventListener('input', splitNotice);
        seatingReady.then(splitNotice);

        // Light the pill up as soon as we know the form has tables.
        if (tableBtn) {
            seatingReady.then(ok => {
                if (!ok || !tableBtn.isConnected) return;
                tableBtn.dataset.active = '1';
                tableBtn.disabled = false;
                tableBtn.style.background = '#dce8ff';
                tableBtn.style.color = '#1a4fd6';
                tableBtn.style.border = '1px solid #a8c0f5';
                tableBtn.style.cursor = 'pointer';
                const h = hint();
                if (h) h.style.display = 'none';
            });
        }

        el.querySelectorAll('.er-tag-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                const box = msg();
                const token = btn.dataset.token;
                if (!box || !token) return;
                // Capture the caret NOW -- the seating await below is a
                // microtask at worst, but the selection must not depend on it.
                const start = box.selectionStart ?? box.value.length;
                const end = box.selectionEnd ?? box.value.length;
                if (btn.id === 'er-table-tag' && btn.dataset.active !== '1') {
                    // Wait for the seating answer rather than swallowing the
                    // click: an early click used to do nothing at all, and a
                    // slow answer showed "set up a seating chart" to a host who
                    // already had one.
                    const ok = await seatingReady;
                    if (!ok) {
                        const h = hint();
                        if (h) h.style.display = 'block';
                        return;
                    }
                }
                box.value = box.value.slice(0, start) + token + box.value.slice(end);
                const caret = start + token.length;
                box.focus();
                try { box.setSelectionRange(caret, caret); } catch (_) { /* ignore */ }
                box.dispatchEvent(new Event('input', { bubbles: true }));
            });
        });
    }

    // Read + validate the composer fields from an open reminder modal (create or
    // edit). Alerts and returns null on a validation problem.
    _collectReminderComposerValues(el) {
        const t = this.getWizardTranslations();
        const pad = n => String(n).padStart(2, '0');
        // "Send now" vs scheduled date + hour-only dropdown (task #191).
        const whenMode = el.querySelector('input[name="er-when-mode"]:checked')?.value || 'later';
        const audience = el.querySelector('#er-audience').value;
        // Meta rejects WhatsApp templates whose body has a personalization tag
        // alone on a line ("floating parameter" → INVALID_FORMAT rejection).
        // Hosts naturally write "your table number is:\n{{table_number}}" — merge
        // such lines at save so the stored message, the email, and the WhatsApp
        // template are all identical and valid.
        const message = el.querySelector('#er-message').value
            .replace(/\n[ \t]*(\{\{\s*[a-z_]+\s*\}\})[ \t]*(?=\n|$)/gi, ' $1');
        // Meta also rejects a template whose body ENDS with a tag — and
        // whitespace, punctuation and formatting marks after it are transparent
        // ("Table: {{table_number}}." still 400s, canary-verified 2026-08-26).
        // The wizard blocks this for main campaigns; block it here too so the
        // host hears about it now instead of the WhatsApp leg silently dropping.
        if (/\{\{\s*(?:name|first_name|email|table_number)\s*\}\}[\s!?.,:;`*_~]*$/i.test(message.trim())) {
            alert(t.reminderAlertTrailingTag || 'Your message can\'t end with a personalization tag like {{table_number}} — WhatsApp doesn\'t allow it. Add a few words after it (e.g. "See you there!").');
            return null;
        }
        let when;
        if (whenMode === 'now') {
            // The actual moment — NOT floored to the hour. Flooring made a
            // 9:04 PM "Send now" store 9:00 PM, so the card read "Scheduled
            // for 9:00 PM" (a past time) while the host expected an immediate
            // send (task #546). The creators mark these docs isImmediate and
            // trigger dispatch right away, so the stored minute is honest.
            when = new Date();
        } else {
            const dateVal = el.querySelector('#er-when-date')?.value;
            const hourVal = el.querySelector('#er-when-hour')?.value || '09';
            if (!dateVal) { alert(t.reminderAlertPickDateTime || 'Please choose a send date and time.'); return null; }
            when = new Date(`${dateVal}T${hourVal}:00:00`);
            if (isNaN(when.getTime())) { alert(t.reminderAlertValidDateTime || 'Please enter a valid date and time.'); return null; }
        }
        // Scheduled reminders dispatch on the hour — floor to the top of the
        // hour so the stored time never implies a minute precision we don't
        // honor. "Send now" keeps the real minute (see above).
        if (whenMode !== 'now') when.setMinutes(0, 0, 0);
        // Reject a SCHEDULED ("later") reminder whose time is already in the past:
        // otherwise it's saved as a past send date and the dispatcher fires it on
        // the very next run, silently acting like "send immediately" with no
        // warning (task #251). "Send now" mode is exempt — a due/past time is the
        // whole point there.
        if (whenMode !== 'now' && when.getTime() <= Date.now()) {
            alert(t.reminderAlertFutureDateTime || 'Please choose a date and time in the future for this reminder.');
            return null;
        }
        const buttons = [];
        if (el.querySelector('#er-btn-rsvp').checked) buttons.push('rsvp');
        if (el.querySelector('#er-btn-registry').checked) buttons.push('registry');
        // The legacy Directions row only renders for reminders that already use it.
        if (el.querySelector('#er-btn-directions')?.checked) buttons.push('directions');
        if (el.querySelector('#er-btn-waze')?.checked) buttons.push('waze');
        if (el.querySelector('#er-btn-gmaps')?.checked) buttons.push('gmaps');
        if (el.querySelector('#er-btn-website')?.checked) buttons.push('website');
        if (el.querySelector('#er-btn-livestream')?.checked) buttons.push('livestream');
        // Safety net behind the live cap: never persist >2 buttons — the
        // WhatsApp template build truncates to 2 and guests would silently
        // miss whatever the host thought they added.
        if (buttons.length > 2) { alert(t.reminderButtonsMax || 'You can include up to 2 buttons per reminder'); return null; }
        // Prepend https:// when the host typed a scheme-less link ('//host' or
        // 'host/path') — Meta's WhatsApp buttons require an absolute http(s) URL
        // or the button is silently dropped at template build.
        const normUrl = u => {
            const s = String(u || '').trim();
            if (!s) return '';
            if (/^https?:\/\//i.test(s)) return s;
            if (s.startsWith('//')) return 'https:' + s;
            if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return s;
            return 'https://' + s;
        };
        const buttonUrls = {};
        const regUrl = normUrl(el.querySelector('#er-url-registry').value);
        const dirUrl = normUrl(el.querySelector('#er-url-directions')?.value || '');
        const wazeUrl = normUrl(el.querySelector('#er-url-waze')?.value || '');
        const gmapsUrl = normUrl(el.querySelector('#er-url-gmaps')?.value || '');
        const webUrl = normUrl(el.querySelector('#er-url-website')?.value || '');
        if (regUrl) buttonUrls.registry = regUrl;
        if (dirUrl) buttonUrls.directions = dirUrl;
        if (wazeUrl) buttonUrls.waze = wazeUrl;
        if (gmapsUrl) buttonUrls.gmaps = gmapsUrl;
        if (webUrl) buttonUrls.website = webUrl;
        const livestreamUrl = normUrl(el.querySelector('#er-url-livestream')?.value || '');
        if (livestreamUrl) buttonUrls.livestream = livestreamUrl;
        const buttonLabels = {};
        const rsvpLabel = el.querySelector('#er-label-rsvp').value.trim();
        const regLabel = el.querySelector('#er-label-registry').value.trim();
        const dirLabel = el.querySelector('#er-label-directions')?.value.trim() || '';
        const wazeLabel = el.querySelector('#er-label-waze')?.value.trim() || '';
        const gmapsLabel = el.querySelector('#er-label-gmaps')?.value.trim() || '';
        const webLabel = el.querySelector('#er-label-website')?.value.trim() || '';
        if (rsvpLabel) buttonLabels.rsvp = rsvpLabel;
        if (regLabel) buttonLabels.registry = regLabel;
        if (dirLabel) buttonLabels.directions = dirLabel;
        if (wazeLabel) buttonLabels.waze = wazeLabel;
        if (gmapsLabel) buttonLabels.gmaps = gmapsLabel;
        if (webLabel) buttonLabels.website = webLabel;
        const livestreamLabel = el.querySelector('#er-label-livestream')?.value.trim() || '';
        if (livestreamLabel) buttonLabels.livestream = livestreamLabel;
        if (buttons.includes('registry') && !buttonUrls.registry) { alert(t.reminderAlertRegistryUrl || 'Add a URL for the gift registry link, or uncheck it.'); return null; }
        if (buttons.includes('directions') && !buttonUrls.directions) { alert(t.reminderAlertDirectionsUrl || 'Add a URL for the directions link, or uncheck it.'); return null; }
        if (buttons.includes('waze') && !buttonUrls.waze) { alert(t.reminderAlertDirectionsUrl || 'Add a URL for the Waze link, or uncheck it.'); return null; }
        if (buttons.includes('gmaps') && !buttonUrls.gmaps) { alert(t.reminderAlertDirectionsUrl || 'Add a URL for the Google Maps link, or uncheck it.'); return null; }
        if (buttons.includes('website') && !buttonUrls.website) { alert(t.reminderAlertWebsiteUrl || 'Add a URL for the wedding website link, or uncheck it.'); return null; }
        if (buttons.includes('livestream') && !buttonUrls.livestream) { alert(t.reminderAlertLivestreamUrl || 'Add a URL for the livestream link, or uncheck it.'); return null; }
        // Block URLs Meta's template API would reject as "not a valid URI"
        // (spaces / no real hostname — e.g. a street address typed into the
        // maps box). One bad button URL fails the WHOLE template create and
        // silently drops every WhatsApp send (Rama & Sundar, 2026-08-22).
        // Mirrors server-side isSendableButtonUrl (campaign-buttons.js).
        const urlSendable = (u) => {
            const s = String(u || '');
            if (!/^https?:\/\//i.test(s) || /\s/.test(s)) return false;
            try { return new URL(s).hostname.includes('.'); } catch (_) { return false; }
        };
        for (const k of buttons) {
            if (buttonUrls[k] && !urlSendable(buttonUrls[k])) {
                alert(t.invalidButtonUrlWarning || "This doesn't look like a valid web link. If it's an address, paste the link from Google Maps instead — invalid links are left off WhatsApp messages.");
                return null;
            }
        }
        const iso = when.toISOString();
        const datePart = `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
        // Message & Translations panel (task #453, replacing #342's radios). The
        // host has already produced and possibly hand-edited the translated text
        // here, so we carry it through rather than re-translating at save — that
        // is the whole point of letting them edit the box. 'single' is reported
        // as 'original' for the callers that still speak the old three names.
        const panel = this._reminderTrPanel;
        // A language ticked with nothing written in it (task #589) — refuse
        // before anything is created, and name the language.
        if (panel && typeof panel.missingTranslationsMessage === 'function') {
            const missing = panel.missingTranslationsMessage();
            if (missing) {
                if (typeof panel.say === 'function') panel.say(missing, 'warning');
                else alert(missing);
                return null;
            }
        }
        const sel = panel ? panel.getSelectionForSave() : null;
        const languageMode = sel ? (sel.mode === 'single' ? 'original' : sel.mode) : null;
        const translatedMessage = sel ? (sel.translatedMessage || null) : null;
        const languages = sel ? sel.langs : null;
        // null, not false, when the toggle isn't on screen (task #631) — a form
        // without per-guest QR must not silently clear a flag it never showed.
        const qrToggle = el.querySelector('#er-include-qr');
        const includeQRCode = qrToggle ? qrToggle.checked : null;
        return { when, iso, datePart, whenMode, audience, message, buttons, buttonUrls, buttonLabels, languageMode, translatedMessage, languages, includeQRCode };
    }

    /**
     * "Include QR check-in code" for a reminder (task #631).
     *
     * Copied in shape and wording from the campaign wizard's toggle so the two
     * read as one control, and gated on the same tier feature — a form without
     * per-guest QR codes has nothing to attach, and showing a dead switch is
     * worse than showing none.
     *
     * It writes `includeQRCode` on the reminder itself. Before this, a reminder
     * could only inherit the flag from the campaign that spawned it, which meant
     * the "bring your QR code" reminder — the one send that most wants it — had
     * no way to turn it on.
     */
    _reminderQrToggleHtml(o, t) {
        if (!window.FeatureGates?.canUse?.(this.formData, 'individual_qr')) return '';
        const on = o.includeQR === true;
        return `
                <div class="mb-3 p-3 border rounded">
                  <div class="form-check form-switch d-flex align-items-center gap-2 mb-0">
                    <input class="form-check-input" type="checkbox" role="switch" id="er-include-qr" ${on ? 'checked' : ''}>
                    <label class="form-check-label fw-semibold" for="er-include-qr">
                      <i class="bi bi-qr-code me-1"></i>${t.includeQrCode || 'Include QR check-in code'}
                    </label>
                  </div>
                  <small class="text-muted d-block mt-1 ms-4">${t.qrCodeDesc || "Guests who RSVP'd will receive their personal check-in QR code."} <strong>${t.qrCodeWhatsappNote || 'For WhatsApp, the QR code will replace the message image.'}</strong></small>
                </div>`;
    }

    // ---- Link / button cards (task #277: match the wizard's #240 design) ----
    // Renders the "Links / buttons" section as the same full-width tappable
    // option cards the campaign wizard uses (icon left, checkbox right, URL
    // field + label pill/edit beneath when selected) — one look across the
    // Original Message and Reminder flows. The cards drive HIDDEN
    // #er-btn-<key> / #er-url-<key> / #er-label-<key> inputs for EVERY known
    // key, so the proven collectors (_collectReminderComposerValues / the
    // schedule collector) read exactly the fields they always have — the UI
    // changed, the data shape did not.
    _reminderLinkChipsHtml(o) {
        const t = this.getWizardTranslations();
        const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const ALL_KEYS = ['rsvp', 'registry', 'directions', 'waze', 'gmaps', 'website', 'livestream'];
        const active = new Set(Array.isArray(o.active) ? o.active : ['rsvp']);
        const urls = o.urls || {};
        const def = Object.assign({ rsvp: 'RSVP Here', registry: 'View Registry', directions: 'Get Directions', waze: 'Waze', gmaps: 'Google Maps', website: 'Wedding Website', livestream: 'Watch Livestream' }, o.defLabels || {});
        const lbl = o.labels || {};
        const showKeys = (Array.isArray(o.showKeys) && o.showKeys.length) ? o.showKeys : ['rsvp', 'registry', 'waze', 'gmaps', 'website', 'livestream'];
        const META = {
            rsvp: { icon: 'bi-reply-fill', name: t.reminderButtonRsvp || 'RSVP link', hasUrl: false },
            registry: { icon: 'bi-gift', name: t.reminderButtonRegistry || 'Gift registry', hasUrl: true },
            directions: { icon: 'bi-signpost-2', name: t.reminderButtonDirections || 'Directions', hasUrl: true },
            waze: { icon: 'bi-geo-alt', name: 'Waze', hasUrl: true },
            gmaps: { icon: 'bi-geo-alt-fill', name: 'Google Maps', hasUrl: true },
            website: { icon: 'bi-globe2', name: t.reminderButtonWebsite || 'Wedding website', hasUrl: true },
            livestream: { icon: 'bi-broadcast', name: t.reminderButtonLivestream || 'Livestream', hasUrl: true }
        };
        const card = (key) => {
            const m = META[key];
            const on = active.has(key);
            const nameHtml = key === 'rsvp'
                ? `${esc(m.name)} <span class="text-muted fw-normal">${t.reminderRsvpAutoIncluded || '(link automatically included)'}</span>`
                : esc(m.name);
            // Config panel matches the campaign wizard's Buttons & Links section
            // exactly (task #277 round 2): bordered box, icon+name header, URL
            // field, always-visible "Button label (optional)" input — no more
            // pill + pencil.
            return `
              <div class="js-link-card" data-key="${key}">
                <label class="reminder-link-option d-flex align-items-center gap-2">
                    <i class="bi ${m.icon} reminder-link-icon"></i>
                    <span class="reminder-link-text flex-grow-1 small">${nameHtml}</span>
                    <input class="form-check-input reminder-button-check js-card-check m-0 flex-shrink-0" type="checkbox" data-key="${key}" ${on ? 'checked' : ''}>
                </label>
                <div class="js-card-config bw-btn-config mt-2 p-2 px-3 border rounded-3" data-key="${key}" ${on ? '' : 'style="display:none"'}>
                    ${key === 'rsvp' ? '' : `<div class="d-flex align-items-center gap-2 mb-1 mt-1"><i class="bi ${m.icon} text-primary"></i><span class="small fw-semibold">${esc(m.name)}</span></div>`}
                    ${m.hasUrl ? `
                    <input type="url" class="form-control form-control-sm js-card-url" data-key="${key}"
                           placeholder="${esc(t.reminderButtonUrlPlaceholder || 'Paste a link (https://...)')}"
                           value="${esc(urls[key] || '')}">` : ''}
                    <input type="text" class="form-control form-control-sm mt-1 mb-1 js-card-labelinput" data-key="${key}"
                           maxlength="25" placeholder="${esc(def[key] || '')} — ${esc(t.buttonLabelOptional || 'Button label (optional)')}" value="${esc(lbl[key] || '')}">
                </div>
              </div>`;
        };
        // Hidden inputs for ALL keys (not just shown ones) so collectors never hit
        // a missing element. URL is omitted for rsvp (its link is automatic).
        const hidden = ALL_KEYS.map(key => `
                <input type="checkbox" class="reminder-link-chips-hidden" id="er-btn-${key}" ${active.has(key) ? 'checked' : ''}>
                ${key !== 'rsvp' ? `<input type="text" class="reminder-link-chips-hidden" id="er-url-${key}" value="${esc(urls[key] || '')}">` : ''}
                <input type="text" class="reminder-link-chips-hidden" id="er-label-${key}" data-default="${esc(def[key] || '')}" value="${esc(lbl[key] || '')}">`).join('');
        return `
                <div class="mb-3 reminder-links-block">
                  <label class="form-label fw-semibold small d-block mb-2">${t.reminderButtonsLabel || 'Buttons'}</label>
                  <div class="d-flex flex-column gap-2">${showKeys.map(card).join('')}</div>
                  <div class="form-text">${t.reminderButtonsHint || 'Buttons are optional — add up to 2, or leave all unchecked to send a plain-text reminder.'}</div>
                  ${o.bilingualLabels ? `<div class="form-text"><i class="bi bi-translate me-1"></i>${t.reminderButtonLabelLangHint || 'Left blank, each button reads in both languages, as shown. A label you type is sent exactly as written — include both languages if you want both.'}</div>` : ''}
                  ${hidden}
                </div>`;
    }

    // Wire the option cards for an open composer/drawer (task #277): checking a
    // card reveals its URL field + label row; edits sync straight into the
    // hidden #er-* inputs as you type, and the label editor auto-saves on blur.
    // Enforces the live 2-button cap (RSVP counts).
    _wireReminderLinkChips(el) {
        const t = this.getWizardTranslations();
        const MAX = 2;
        const countActive = () => el.querySelectorAll('.js-card-check:checked').length;
        el.querySelectorAll('.js-link-card').forEach(cardEl => {
            const key = cardEl.dataset.key;
            const check = cardEl.querySelector('.js-card-check');
            const cfg = cardEl.querySelector('.js-card-config');
            const urlInput = cardEl.querySelector('.js-card-url');
            const labelInput = cardEl.querySelector('.js-card-labelinput');
            const hiddenCb = el.querySelector(`#er-btn-${key}`);
            const hiddenUrl = el.querySelector(`#er-url-${key}`);
            const hiddenLabel = el.querySelector(`#er-label-${key}`);

            check?.addEventListener('change', () => {
                if (check.checked && countActive() > MAX) {
                    check.checked = false;
                    const msg = t.reminderButtonsMax || 'You can include up to 2 buttons per reminder';
                    if (window.languageHelpers?.showToast) window.languageHelpers.showToast(msg, 'info');
                    else alert(msg);
                    return;
                }
                if (hiddenCb) hiddenCb.checked = check.checked;
                // The whole config panel expands/collapses with the selection —
                // same behavior as the campaign wizard (task #277 round 2).
                if (cfg) cfg.style.display = check.checked ? '' : 'none';
            });

            urlInput?.addEventListener('input', () => {
                if (hiddenUrl) hiddenUrl.value = urlInput.value.trim();
            });

            labelInput?.addEventListener('input', () => {
                if (hiddenLabel) hiddenLabel.value = labelInput.value.trim();
            });
        });

        // Wedding-website card: the host's own QuikRSVP sites are the primary
        // choices, with "Use a different link…" as the fallback (task #274).
        this._injectComposerWebsitePicker(el);
    }

    // Fetch (once per dashboard session) the host's wedding sites for pickers.
    async _fetchMyWeddingSites() {
        if (this._myWeddingSites) return this._myWeddingSites;
        try {
            const user = window.firebaseAuth?.currentUser || (typeof firebase !== 'undefined' ? firebase.auth().currentUser : null);
            if (!user) return (this._myWeddingSites = []);
            const token = await user.getIdToken();
            const r = await fetch('/api/wedding-websites', { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) return (this._myWeddingSites = []);
            const data = await r.json();
            this._myWeddingSites = (data.websites || []).filter(s => s && s.subdomain).map(s => {
                const selectable = s.status === 'published';
                const base = [s.partner1Name, s.partner2Name].filter(Boolean).join(' & ') || s.title || s.subdomain;
                return {
                    label: selectable ? base : `${base} (${s.status || 'unpublished'})`,
                    url: `${window.location.origin}/w/${s.subdomain}`,
                    selectable
                };
            });
        } catch (e) {
            console.warn('Could not load wedding sites for picker:', e.message);
            this._myWeddingSites = [];
        }
        return this._myWeddingSites;
    }

    async _injectComposerWebsitePicker(el) {
        const card = el.querySelector('.js-link-card[data-key="website"]');
        if (!card || card.querySelector('.js-card-site-select')) return;
        const sites = await this._fetchMyWeddingSites();
        if (!sites.length || !card.isConnected) return; // no sites — plain URL box
        const t = this.getWizardTranslations();
        const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const urlInput = card.querySelector('.js-card-url');
        const check = card.querySelector('.js-card-check');
        const hiddenUrl = el.querySelector('#er-url-website');
        if (!urlInput || !check) return;
        const current = (urlInput.value || '').trim();
        const matched = sites.some(s => s.url === current);
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm mt-1 js-card-site-select';
        sel.innerHTML = `<option value="" ${!current ? 'selected' : ''} disabled hidden>${esc(t.chooseSite || 'Choose a wedding site…')}</option>`
            + sites.map(s => `<option value="${esc(s.url)}" ${s.selectable ? '' : 'disabled'} ${current === s.url ? 'selected' : ''}>${esc(s.label)} — ${esc(s.url.replace(/^https?:\/\//, ''))}</option>`).join('')
            + `<option value="__custom__" ${current && !matched ? 'selected' : ''}>${esc(t.useDifferentLink || 'Use a different link…')}</option>`;
        urlInput.before(sel);
        const syncVis = () => {
            const on = check.checked;
            sel.style.display = on ? '' : 'none';
            urlInput.style.display = on && sel.value === '__custom__' ? '' : 'none';
        };
        syncVis();
        check.addEventListener('change', syncVis);
        sel.addEventListener('change', () => {
            if (sel.value === '__custom__') {
                urlInput.style.display = '';
                urlInput.focus();
                return;
            }
            if (!sel.value) return;
            urlInput.value = sel.value;
            if (hiddenUrl) hiddenUrl.value = sel.value;
            urlInput.style.display = 'none';
        });
    }

    // ---- Shared reminder side-drawer ---------------------------------------
    // ONE centered popup (Bootstrap modal) used for every add/edit reminder action
    // (the Reminder Schedule modal, the per-campaign Remind actions, and Remind
    // All), replacing the old inline forms + the multi-step send wizard. It's a
    // regular roomy modal (modal-lg, scrollable) that layers over whatever is open
    // (the Reminder Schedule modal or the dashboard) so the background state is
    // preserved. The caller supplies the body HTML and an onPrimary(el, modal)
    // handler; composer interactions + link chips are wired automatically.
    /**
     * @param {string} [o.composeLang] the language this reminder's words are
     *   being written in — used to pick the language of the message templates
     *   (task #603 round 2). The toolbar that opens the picker is two calls
     *   down from here and has no campaign in scope, so it is parked on the
     *   instance rather than threaded through every signature.
     */
    _openReminderModal({ id = 'reminderEditorModal', titleHtml, subHtml, bodyHtml, formId, primaryLabel, primaryIcon = 'bi-check-lg', onPrimary, afterRender, composeLang }) {
        this._reminderComposeLang = composeLang || this.dashboardLanguage;
        const t = this.getWizardTranslations();
        document.getElementById(id)?.remove();
        const html = `
        <div class="modal fade reminder-modal" tabindex="-1" id="${id}" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div class="modal-content">
              <div class="modal-header">
                <div>
                  <h5 class="modal-title"><i class="bi bi-bell-fill me-2 text-secondary"></i>${titleHtml}</h5>
                  ${subHtml ? `<div class="reminder-modal-sub small text-muted">${subHtml}</div>` : ''}
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">${bodyHtml}</div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">${t.reminderCancel || 'Cancel'}</button>
                <button type="button" class="btn btn-primary" id="reminder-drawer-save"><i class="bi ${primaryIcon} me-1"></i>${primaryLabel}</button>
              </div>
            </div>
          </div>
        </div>`;
        document.body.insertAdjacentHTML('beforeend', html);
        const el = document.getElementById(id);
        const oc = new bootstrap.Modal(el);
        // When opened over another modal (the Reminder Schedule list), raise this
        // modal + the freshly-added backdrop above it so it stacks cleanly on top.
        el.addEventListener('shown.bs.modal', () => {
            const bds = document.querySelectorAll('.modal-backdrop');
            bds[bds.length - 1]?.classList.add('reminder-modal-backdrop');
        });
        el.addEventListener('hidden.bs.modal', () => el.remove());
        this._wireReminderComposerInteractions(el, formId);
        this._wireReminderLinkChips(el);
        if (typeof afterRender === 'function') afterRender(el);
        el.querySelector('#reminder-drawer-save').addEventListener('click', () => onPrimary(el, oc));
        oc.show();
        return { el, oc };
    }

    // Wire the composer's preview-pill + Edit interactions for an open modal
    // (both modals call this after inserting the body). The label input value is
    // still read on save by _collectReminderComposerValues via its #er-label-<key> id.
    _wireReminderComposerInteractions(el, formId) {
        // "Send now" / "Schedule for later" toggle (task #191): show the
        // date + hour fields only for scheduled sends.
        el.addEventListener('change', (e) => {
            if ((e.target.name || '') !== 'er-when-mode') return;
            const later = e.target.value === 'later' && e.target.checked;
            const fields = el.querySelector('#er-schedule-fields');
            const nowHint = el.querySelector('#er-now-hint');
            if (fields) fields.style.display = later ? '' : 'none';
            if (nowHint) nowHint.style.display = later ? 'none' : '';
            // First switch to "later" with no date yet: prefill today so the
            // host only has to pick the hour.
            if (later) {
                const d = el.querySelector('#er-when-date');
                if (d && !d.value) {
                    const now = new Date();
                    const p = n => String(n).padStart(2, '0');
                    d.value = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
                }
            }
        });
        // Edit pencil → reveal the inline wording input.
        el.addEventListener('click', (e) => {
            const editBtn = e.target.closest('.er-edit');
            if (!editBtn) return;
            const input = el.querySelector(`#er-label-${editBtn.dataset.button}`);
            if (input) { input.classList.remove('d-none'); input.focus(); input.select(); }
        });
        // Typing → live-update the preview pill (falls back to the default when cleared).
        el.addEventListener('input', (e) => {
            if (!e.target.classList.contains('er-label-input')) return;
            const preview = el.querySelector(`.er-preview[data-button="${e.target.dataset.button}"]`);
            if (preview) preview.textContent = e.target.value.trim() || e.target.dataset.default || '';
        });
        // WhatsApp caps URL buttons at 2 per template (the RSVP link counts —
        // it's a URL button too). Block the 3rd checkbox live, mirroring the
        // wizard's cap; without this the template build silently truncates to
        // 2 and hosts believe all selected links went out.
        el.addEventListener('change', (e) => {
            if (!/^er-btn-/.test(e.target.id || '') || !e.target.checked) return;
            const checked = el.querySelectorAll('input[id^="er-btn-"]:checked');
            if (checked.length > 2) {
                e.target.checked = false;
                const tc = this.getWizardTranslations();
                const msg = tc.reminderButtonsMax || 'You can include up to 2 buttons per reminder';
                if (window.languageHelpers?.showToast) window.languageHelpers.showToast(msg, 'info');
                else alert(msg);
            }
        });

        // Message & Translations panel (task #453). Draw it now that its markup is
        // in the DOM, and keep the "Your message" row in step with the box above —
        // they are the same message shown twice.
        if (this._reminderTrPanel) {
            this._reminderTrPanel.render();
            el.querySelector('#er-message')?.addEventListener('input', (e) => {
                this._reminderTrPanel?.syncSourceDraft(e.target.value);
            });
        }

        // Personalization tags → insert the merge token at the caret.
        this._wirePersonalizationToolbar(el, formId);

        this._wireReminderImagePicker(el, formId);
    }

    // Wires the per-reminder image picker (browse / upload / remove) inside an
    // open reminder composer. A custom uploaded image overrides the campaign's
    // invitation photo; "Remove" reverts to that photo (or no image when the
    // campaign has none). DOM is updated in place — the modal is single-instance.
    _wireReminderImagePicker(el, formId) {
        const imgInput = el.querySelector('#er-image-input');
        const imgBrowse = el.querySelector('#er-image-browse');
        const imgRemove = el.querySelector('#er-image-remove');
        const hidden = el.querySelector('#er-custom-image-url');
        const thumb = el.querySelector('#er-image-thumb');
        const inheritedPhoto = el.querySelector('.er-image-picker')?.dataset.inherited || '';

        if (imgBrowse && imgInput) {
            imgBrowse.addEventListener('click', () => imgInput.click());
            imgInput.addEventListener('change', async () => {
                const file = imgInput.files && imgInput.files[0];
                imgInput.value = '';
                if (!file) return;
                const t = this.getWizardTranslations();
                const show = window.languageHelpers?.showToast || window.showToast;
                const valid = this._validateReminderImageFile(file);
                if (!valid.valid) { if (show) show(valid.error, 'error'); else alert(valid.error); return; }
                const spinner = el.querySelector('#er-image-spinner');
                spinner?.classList.remove('d-none');
                imgBrowse.disabled = true;
                try {
                    const url = await this._uploadReminderImage(file, formId);
                    if (hidden) hidden.value = url;
                    const inc = el.querySelector('#er-include-image'); if (inc) inc.checked = true;
                    const incLabel = el.querySelector('#er-include-image-label'); if (incLabel) incLabel.textContent = (t.reminderIncludeThisImage || 'Include this image');
                    if (thumb) { thumb.src = url; thumb.classList.remove('d-none'); }
                    const icon = imgBrowse.querySelector('i'); if (icon) icon.className = 'bi bi-arrow-repeat me-1';
                    const txt = el.querySelector('#er-image-browse-text'); if (txt) txt.textContent = (t.changeImage || 'Change image');
                    if (imgRemove) imgRemove.classList.remove('d-none');
                    this._syncReminderSplashOption(el);
                } catch (err) {
                    console.error('❌ Reminder image upload failed:', err);
                    const m = (err && err.message) ? err.message : 'Unable to upload image. Please try again.';
                    if (show) show(m, 'error'); else alert(m);
                } finally {
                    spinner?.classList.add('d-none');
                    imgBrowse.disabled = false;
                }
            });
        }

        if (imgRemove) {
            imgRemove.addEventListener('click', () => {
                const t = this.getWizardTranslations();
                if (hidden) hidden.value = '';
                const inc = el.querySelector('#er-include-image');
                const included = inc ? inc.checked : false;
                const eff = (included && inheritedPhoto) ? inheritedPhoto : '';
                if (thumb) { thumb.src = eff; thumb.classList.toggle('d-none', !eff); }
                const icon = imgBrowse?.querySelector('i'); if (icon) icon.className = `bi ${inheritedPhoto ? 'bi-images' : 'bi-image'} me-1`;
                const txt = el.querySelector('#er-image-browse-text'); if (txt) txt.textContent = inheritedPhoto ? (t.reminderUseDifferentImage || 'Use a different image') : (t.addImage || 'Add an image');
                const incLabel = el.querySelector('#er-include-image-label'); if (incLabel) incLabel.textContent = (t.reminderIncludeImage || 'Include the invitation photo');
                imgRemove.classList.add('d-none');
                this._syncReminderSplashOption(el);
            });
        }

        el.querySelector('#er-include-image')?.addEventListener('change', (e) => {
            const eff = e.target.checked ? ((hidden?.value || '').trim() || inheritedPhoto) : '';
            if (thumb) { thumb.src = eff; thumb.classList.toggle('d-none', !eff); }
            this._syncReminderSplashOption(el);
        });
    }

    // Resolve the reminder's effective image from the composer's image picker.
    // A custom uploaded image wins over the inherited invitation photo; the
    // checkbox (present only when there's an invitation photo) suppresses it.
    // `splash` is false for a custom image: the full-screen view shows the
    // invitation, never this reminder's own picture (task #833).
    _collectReminderImage(el, eventPhotoUrl) {
        const customUrl = (el.querySelector('#er-custom-image-url')?.value || '').trim();
        const incEl = el.querySelector('#er-include-image');
        const included = incEl ? incEl.checked : !!customUrl;
        const url = included ? (customUrl || eventPhotoUrl || null) : null;
        const splashEl = el.querySelector('#er-invitation-splash');
        const splash = !!url && !customUrl && (splashEl ? splashEl.checked : true);
        return { url: url || null, hasImage: !!url, splash };
    }

    // The full-screen switch follows the picker: visible only while the
    // reminder carries the inherited invitation photo.
    _syncReminderSplashOption(el) {
        const box = el.querySelector('#er-splash-option');
        if (!box) return;
        const inherited = el.querySelector('.er-image-picker')?.dataset.inherited || '';
        const custom = (el.querySelector('#er-custom-image-url')?.value || '').trim();
        const inc = el.querySelector('#er-include-image');
        box.classList.toggle('d-none', !(inherited && !custom && (!inc || inc.checked)));
    }

    // Upload a per-reminder image to Firebase Storage (reusing the wizard's
    // compression when it's loaded). Returns the download URL.
    async _uploadReminderImage(file, formId) {
        const storage = firebase.storage();
        const safeForm = formId || `reminder_${Date.now()}`;
        const fileName = `reminder_${Date.now()}_${file.name}`;
        const storageRef = storage.ref(`form-images/${safeForm}/${fileName}`);
        let toUpload = file;
        try {
            if (window.bulkSharingWizard?.compressImage) {
                toUpload = await window.bulkSharingWizard.compressImage(file);
            }
        } catch (_) { toUpload = file; }
        const snapshot = await storageRef.put(toUpload);
        return await snapshot.ref.getDownloadURL();
    }

    // Validate an uploaded reminder image (reuses the wizard's rule set when
    // available; falls back to a basic type/size check).
    _validateReminderImageFile(file) {
        if (window.bulkSharingWizard?.validateBulkImageFile) {
            return window.bulkSharingWizard.validateBulkImageFile(file);
        }
        if (file.size > 5 * 1024 * 1024) {
            return { valid: false, error: 'Image size must be less than 5MB.' };
        }
        const okExt = ['.png', '.jpg', '.jpeg', '.webp', '.heic', '.heif'].some(ext => file.name.toLowerCase().endsWith(ext));
        if (!okExt) return { valid: false, error: 'Please upload a PNG, JPG, JPEG, WebP, or HEIC image.' };
        return { valid: true };
    }

    // Built-in reminder default messages per language. Keyed by the campaign's
    // MESSAGE language (the form's language = what it's actually sent in), not the
    // dashboard UI language, so a Hebrew campaign's example text reads in Hebrew.
    // Mirror of bulk-sharing-wizard.js#_reminderDefaultMap (task #287) — keep
    // the two maps in sync.
    _reminderDefaultMap() {
        return {
            en: { no_response: "Hi! Just a friendly reminder — we'd love to hear from you! Please RSVP when you get a chance.", attending: "We're looking forward to seeing you! Here's a quick reminder about the event details.", maybe: "Hi! You let us know you might attend — have you decided yet? We'd love a final yes or no so we can plan. Please update your RSVP when you get a chance.", declined: "We're sorry you can't make it — you'll be missed! We'd still love for you to feel part of the celebration." },
            he: { no_response: "היי! רק תזכורת ידידותית — נשמח לשמוע ממך! אנא אשר הגעה כשתוכל.", attending: "מצפים לראות אותך! הנה תזכורת מהירה על פרטי האירוע.", maybe: "היי! ציינת שאולי תגיע/י — הספקת להחליט? נשמח לתשובה סופית, כן או לא, כדי שנוכל להיערך. אנא עדכנ/י את אישור ההגעה כשתוכל/י.", declined: "מצטערים שלא תוכלו להגיע — נתגעגע! נשמח שתרגישו חלק מהשמחה גם מרחוק." },
            es: { no_response: "¡Hola! Solo un recordatorio amigable — ¡nos encantaría saber de ti! Por favor confirma tu asistencia cuando puedas.", attending: "¡Esperamos verte! Aquí tienes un recordatorio rápido sobre los detalles del evento.", maybe: "¡Hola! Nos dijiste que quizás asistirías — ¿ya lo decidiste? Nos encantaría una respuesta final, sí o no, para poder organizarnos. Por favor actualiza tu confirmación cuando puedas.", declined: "Lamentamos que no puedas asistir — ¡te extrañaremos! Nos encantaría que igual te sientas parte de la celebración." },
            fr: { no_response: "Bonjour ! Juste un petit rappel amical — nous serions ravis d'avoir de vos nouvelles ! Veuillez confirmer votre présence quand vous le pourrez.", attending: "Nous avons hâte de vous voir ! Voici un petit rappel sur les détails de l'événement.", maybe: "Bonjour ! Vous nous aviez dit que vous viendriez peut-être — avez-vous pu décider ? Nous aimerions une réponse définitive, oui ou non, afin de pouvoir nous organiser. Merci de mettre à jour votre réponse dès que possible.", declined: "Nous sommes désolés que vous ne puissiez pas venir — vous nous manquerez ! Nous aimerions que vous vous sentiez malgré tout partie de la fête." },
            de: { no_response: "Hallo! Nur eine freundliche Erinnerung — wir würden uns freuen, von Ihnen zu hören! Bitte bestätigen Sie Ihre Teilnahme, wenn Sie können.", attending: "Wir freuen uns darauf, Sie zu sehen! Hier eine kurze Erinnerung zu den Veranstaltungsdetails.", maybe: "Hallo! Sie hatten uns gesagt, dass Sie vielleicht kommen — haben Sie sich schon entschieden? Über eine endgültige Zu- oder Absage würden wir uns freuen, damit wir planen können. Bitte aktualisieren Sie Ihre Antwort, wenn Sie können.", declined: "Schade, dass Sie nicht dabei sein können — Sie werden uns fehlen! Wir möchten Sie trotzdem gerne an unserer Feier teilhaben lassen." },
            pt: { no_response: "Olá! Apenas um lembrete amigável — adoraríamos ouvir de você! Por favor, confirme sua presença quando puder.", attending: "Estamos ansiosos para vê-lo! Aqui está um lembrete rápido sobre os detalhes do evento.", maybe: "Olá! Você disse que talvez pudesse vir — já decidiu? Adoraríamos uma resposta final, sim ou não, para podermos nos organizar. Por favor, atualize sua confirmação quando puder.", declined: "Sentimos muito que você não possa vir — sentiremos sua falta! Adoraríamos que você se sentisse parte da celebração mesmo assim." },
            ar: { no_response: "مرحباً! مجرد تذكير ودي — نود أن نسمع منك! يرجى تأكيد حضورك عندما تستطيع.", attending: "نتطلع لرؤيتك! إليك تذكير سريع بتفاصيل الحدث.", maybe: "مرحباً! أخبرتنا بأنك ربما تحضر — هل اتخذت قرارك؟ نود الحصول على إجابة نهائية، نعم أو لا، حتى نتمكن من التنظيم. يرجى تحديث تأكيد حضورك عندما تستطيع.", declined: "نأسف لعدم تمكنك من الحضور — سنفتقدك! يسعدنا أن تكون جزءاً من احتفالنا ولو عن بُعد." }
        };
    }

    // The reminder's message language: the form/campaign language (what guests
    // receive), falling back to the dashboard UI language, then English.
    _reminderMessageLang() {
        return this.formData?.defaultLanguage || this.dashboardLanguage || 'en';
    }

    _defaultReminderMessage(recipients, lang) {
        const map = this._reminderDefaultMap();
        const block = map[lang || this._reminderMessageLang()] || map.en;
        return block[recipients] || block.no_response;
    }

    // True when a stored reminder message is just one of the built-in defaults
    // (in ANY language) — i.e. the host never customized it — so the editor can
    // re-show it in the current message language instead of a stale English copy.
    _isDefaultReminderMessage(text) {
        const s = String(text || '').trim();
        if (!s) return true;
        const map = this._reminderDefaultMap();
        return Object.keys(map).some(l => Object.values(map[l]).some(v => v.trim() === s));
    }

    // Per-campaign "Remind → Pending/Yes" creates a NEW linked reminder nested
    // under this campaign (NOT a standalone follow-up campaign — that's the top
    // "Remind All"). Audience is preset from the menu but editable; send time
    // defaults to now. The child flows through the normal reminder dispatch.
    // reviewRemovals: guests the host took off the review list just before;
    // saved on this child only (see _remindCampaignByStatus).
    async showCreateReminderModal(parentId, presetAudience, { reviewRemovals = [] } = {}) {
        const removed = Array.isArray(reviewRemovals) ? reviewRemovals : [];
        const parent = this.campaigns.find(c => c.id === parentId);
        if (!parent) { if (window.showToast) window.showToast(this.cTxt('msgNotFound'), 'error'); return; }
        if (parent.isReminderCampaign) { if (window.showToast) window.showToast(this.cTxt('remFromMainMessage'), 'info'); return; }

        const recip = (presetAudience === 'attending' || presetAudience === 'all' || presetAudience === 'maybe' || presetAudience === 'declined') ? presetAudience : 'no_response';
        const t = this.getWizardTranslations();
        const pad = n => String(n).padStart(2, '0');
        const now = new Date();
        const localVal = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:00`;

        // Registry/Directions URLs prefill from the form (host can override).
        const fd = this.formData || {};
        const formRegistry = String(fd.giftRegistryUrl || '').trim();
        const formAddr = String(fd.locationAddress || '').trim();
        const formMaps = String(fd.customGoogleMapsUrl || '').trim()
            || (formAddr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formAddr)}` : '');
        // Button labels follow the form's language (see showEditReminderModal) so an
        // English form never shows Hebrew defaults; falls back to the dashboard UI language.
        const lang = this.formData?.defaultLanguage || this.dashboardLanguage || 'en';
        const defLabels = (window.QR_REMINDER_BTN_LABELS && (window.QR_REMINDER_BTN_LABELS[lang] || window.QR_REMINDER_BTN_LABELS.en)) || { rsvp: 'RSVP Here', registry: 'View Registry', directions: 'Get Directions', website: 'Wedding Website' };
        // Wedding-website button URL prefills from the parent campaign's chosen
        // wedding-site URL (host can override); blank when the campaign links to a form.
        const webVal = parent.rsvpWebsiteUrl || '';
        const eventPhotoUrl = parent.bulkImageUrl || null;
        const parentLabel = String(parent.name || parent.formTitle || 'this message').replace(/</g, '&lt;');
        // Language Options (task #342): offer the 3-choice drawer when the parent
        // campaign has a translation language; default to the campaign's own mode.
        const li = this._reminderLangInfo(parent);

        const bodyHtml = `
                <p class="text-muted small mb-3">${(t.reminderLinkedNote || 'Sends as a linked reminder under "{name}" — not a new message.').replace('{name}', '<strong>' + parentLabel + '</strong>')}</p>
                ${this._reminderComposerBodyHtml({ localVal, recip, removedNote: removed.length ? this.cTxt(removed.length === 1 ? 'remindRemovedOne' : 'remindRemovedMany', removed.length) : '', buttons: ['rsvp'], regVal: formRegistry, dirVal: formMaps, wazeVal: (String(fd.customWazeUrl || '').trim() || (formAddr ? `https://waze.com/ul?q=${encodeURIComponent(formAddr)}` : '')), gmapsVal: formMaps, livestreamVal: String(fd.livestreamUrl || '').trim(), webVal, defLabels, cfgLabels: {}, message: this._defaultReminderMessage(recip), eventPhotoUrl, customImageUrl: '', photoIncluded: true, remTranslateTarget: li.target, remTranslateLangName: li.name, reminderLangMode: li.mode, sourceLanguage: parent?.sourceLanguage || lang, formLanguages: this._reminderFormLanguages(parent), translatedMessage: '', eventTitle: parent?.formTitle || '', includeQR: parent?.includeQRCode === true, invitationSplash: parent?.invitationSplash !== false })}`;
        this._openReminderModal({
            id: 'createReminderDrawer',
            composeLang: parent?.sourceLanguage || lang,
            titleHtml: t.reminderNewTitle || 'New reminder',
            subHtml: parentLabel,
            bodyHtml,
            formId: parent.formId,
            primaryLabel: t.reminderCreateBtn || 'Create reminder',
            primaryIcon: 'bi-bell-fill',
            onPrimary: async (el, oc) => {
            const v = this._collectReminderComposerValues(el);
            if (!v) return;
            const img = this._collectReminderImage(el, eventPhotoUrl);
            el.querySelector('#reminder-drawer-save').disabled = true;
            // Close the drawer BEFORE the work, not after it (task #578).
            // Creating a reminder is several seconds of round trips — a
            // translation pass, the write, and for "send now" the dispatch
            // itself — and the drawer used to sit open through all of it behind
            // a dead button, with nothing to say whether the click had
            // registered. Everything from here reports on the dashboard behind
            // it, which is where the reminder's own state lives anyway.
            oc.hide();
            const toast = window.languageHelpers?.showToast || window.showToast;
            try {
                await this._createReminderChild(parent, v, img, removed);
                // No toast either way (task #610 round 2). The card the reminder
                // just created is already on screen: sending now shows its
                // spinner, and a scheduled one shows "Scheduled" with its date.
                // Both say the same thing in the place the host is looking, so
                // the toast was an interruption that added nothing.
                await this.loadCampaigns();
            } catch (err) {
                console.error('Error creating reminder:', err);
                // The drawer is gone, so a failure has to be loud here AND has
                // to say plainly that nothing was created — otherwise closing
                // the drawer would read as confirmation on its own.
                const msg = (t.reminderCreateFailed || 'Failed to create reminder: ') + (err.message || err);
                if (toast) toast(msg, 'error'); else alert(msg);
            }
            }
        });
    }

    // Build + persist one child reminder doc for an existing parent campaign,
    // Translate one reminder message via the same endpoint the campaign wizard
    // uses. Returns null on any failure so callers fall back to sending the
    // text as written (never block a save).
    async _translateReminderMessage(message, targetLang) {
        try {
            const resp = await fetch('/api/translate/invitation-message', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(await window.claudeTranslator?.authHeader()) },
                body: JSON.stringify({ message, sourceLanguage: 'auto', targetLanguages: [targetLang] })
            });
            if (!resp.ok) return null;
            const data = await resp.json();
            return data.translations?.[targetLang] || null;
        } catch (e) {
            console.warn('Reminder translation failed (sending as written):', e);
            return null;
        }
    }

    // The language setup a campaign was meant to have. A translate-only
    // invitation saved since 2026-10-07 is stored as 'single' (its message IS
    // the translation) with `translateOnly` naming the target; read as plain
    // 'single', its reminders went out in the host's original language.
    // One reading, shared with the wizard (BulkSharingWizard.campaignLanguageShape).
    _campaignLangShape(campaign) {
        if (window.BulkSharingWizard?.campaignLanguageShape) {
            return window.BulkSharingWizard.campaignLanguageShape(campaign);
        }
        return { mode: campaign?.campaignLanguageMode, langs: campaign?.campaignLanguages || [], translateOnly: null };
    }

    // Translation setup for a per-campaign reminder's compact "Language Options"
    // drawer (task #342). A bilingual campaign appends its 2nd language, a
    // translate-mode campaign uses its (single) target; single-language campaigns
    // return no target so the drawer stays hidden (nothing to translate to).
    // `mode` is the campaign's own mode mapped to the drawer's 3 choices — the
    // default, so an untouched drawer reproduces the reminder's prior behavior.
    _reminderLangInfo(campaign) {
        const shape = this._campaignLangShape(campaign);
        const langMode = shape.mode;
        const langs = shape.langs;
        const target = (langMode === 'bilingual' && langs[1]) ? langs[1]
            : (langMode === 'translate' && langs[0]) ? langs[0] : null;
        const mode = (langMode === 'bilingual' || langMode === 'translate') ? langMode : 'original';
        const name = target ? (window.languageHelpers?.getLanguageName?.(target) || String(target).toUpperCase()) : '';
        return { target, mode, name };
    }

    // Resolve a reminder's message + language fields for the host's Language
    // Options choice (task #342). `chosenMode` is 'original' | 'translate' |
    // 'bilingual' (falls back to the campaign's own mode when the drawer wasn't
    // shown). Reuses the same single-language translate call the reminder always
    // used — only the SOURCE of the mode changed (an explicit drawer choice vs.
    // silent inheritance), so an untouched drawer sends identically to before.
    // A translation failure falls back to sending the text as written. Returns
    // the exact fields the server send path reads (campaignLanguageMode +
    // campaignLanguages[1] + translatedMessage drive the bilingual append).
    async _resolveReminderTranslation(campaign, baseMessage, chosenMode, hostChoice = null) {
        const shape = this._campaignLangShape(campaign);
        const parentMode = shape.mode;
        const langs = shape.langs;
        // The host's own picks win over the campaign's, so a reminder can go out
        // in a language the campaign itself wasn't (task #453).
        const chosenLangs = hostChoice?.languages || [];
        const primaryFallback = (parentMode === 'bilingual' && langs[0]) ? langs[0]
            : (campaign?.sourceLanguage || 'en');
        const T = chosenLangs.length
            ? (chosenLangs.find(l => l !== primaryFallback) || null)
            : ((parentMode === 'bilingual' && langs[1]) ? langs[1]
                : (parentMode === 'translate' && langs[0]) ? langs[0] : null);
        const primary = primaryFallback;
        const mode = (chosenMode === 'original' || chosenMode === 'translate' || chosenMode === 'bilingual')
            ? chosenMode
            : ((parentMode === 'bilingual' || parentMode === 'translate') ? parentMode : 'original');
        const asWritten = { customMessage: baseMessage, translatedMessage: null, campaignLanguageMode: 'single', campaignLanguages: (langs.length ? langs : null) };
        if (!T || mode === 'original') return asWritten;
        // Prefer the text the host actually has in front of them — they may have
        // rewritten the machine's wording, and re-translating would discard it.
        const tr = (hostChoice?.translatedMessage || '').trim()
            ? hostChoice.translatedMessage
            : await this._translateReminderMessage(baseMessage, T);
        if (!tr) return asWritten;
        if (mode === 'bilingual') {
            return { customMessage: baseMessage, translatedMessage: tr, campaignLanguageMode: 'bilingual', campaignLanguages: [primary, T] };
        }
        return { customMessage: tr, translatedMessage: null, campaignLanguageMode: 'translate', campaignLanguages: [T] };
    }

    // mirroring the wizard's _buildReminderChildCampaigns shape so it dispatches
    // identically. Also flags the parent so the dashboard nests + counts it.
    async _createReminderChild(parent, v, img, reviewRemovals = []) {
        const db = firebase.firestore();
        const parentId = parent.campaignId || parent.id;
        // Next index among existing children — keeps the child doc id unique.
        const index = this.campaigns
            .filter(c => c.isReminderCampaign && c.parentCampaignId === parentId)
            .reduce((m, c) => Math.max(m, c.reminderIndex || 0), 0) + 1;
        const childId = `${parentId}_r${index}`;
        const parentName = parent.name || parent.formTitle || 'Campaign';
        const nowIso = new Date().toISOString();

        // Reminders follow the campaign's language setup (same as the wizard):
        // bilingual parents get the reminder text + its translation (joined at
        // send), translate-mode parents send the translated text only. A
        // translation failure falls back to the text as written.
        const baseMessage = (v.message && v.message.trim()) ? v.message : this._defaultReminderMessage(v.audience);
        // Language Options (task #342): the host's drawer choice drives the
        // translation; when the drawer wasn't shown (single-language campaign) it
        // falls back to the campaign's own mode — identical to the prior behavior.
        const langRes = await this._resolveReminderTranslation(parent, baseMessage, v.languageMode, v);
        const childMessage = langRes.customMessage;
        const childTranslated = langRes.translatedMessage;

        const child = {
            campaignId: childId,
            parentCampaignId: parentId,
            isReminderCampaign: true,
            reminderIndex: index,
            reminderConfig: {
                timing: 'custom',
                customDate: v.datePart,
                customHour: v.when.getHours(),
                customSendAt: v.iso,
                recipients: v.audience,
                buttons: v.buttons,
                buttonUrls: v.buttonUrls,
                buttonLabels: v.buttonLabels
            },
            reminderContactsResolved: false,
            // Guests the host removed in the review list. Only this reminder
            // skips them: the server drops them when it resolves the audience
            // (reminders.service _dropReviewRemovals).
            reviewRemovals: (reviewRemovals || []).map(c => ({
                name: c.name || '', email: c.email || '', phone: c.phone || ''
            })),
            // Identity / ownership (inherited from parent)
            formId: parent.formId,
            userId: parent.userId,
            userEmail: parent.userEmail || null,
            userName: parent.userName || null,
            // Scheduling — fires via the normal pending-campaign cron.
            // isImmediate mirrors the host's explicit "Send now" choice (task
            // #546): the cron's time gate is `!isImmediate && scheduledDate >
            // now`, so scheduled-for-later docs MUST stay false, while a
            // send-now doc is dispatched right away by _dispatchReminderNow
            // (with the cron as fallback) and its card shows the creation date
            // instead of a misleading "Scheduled for <a minute ago>".
            status: 'scheduled',
            priority: 'normal',
            isImmediate: v.whenMode === 'now',
            scheduledSendDate: v.iso,
            scheduledSendDateDisplay: v.when.toLocaleString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }),
            // Display + event context
            name: `[Reminder ${index}] ${parentName}`,
            eventTitle: parent.eventTitle || null,
            formTitle: parent.formTitle || null,
            eventDate: parent.eventDate || null,
            eventLocation: parent.eventLocation || null,
            // Message (translated per the parent campaign's language mode above)
            customMessage: childMessage,
            ...(childTranslated ? { translatedMessage: childTranslated } : {}),
            hasCustomMessage: !!(v.message && v.message.trim()),
            // Rendering / template context (inherited)
            themeColor: parent.themeColor || null,
            emailSenderName: parent.emailSenderName || null,
            replyToEmail: parent.replyToEmail || null,
            emailLogoUrl: parent.emailLogoUrl || null,
            hideEmailLogo: parent.hideEmailLogo === true,
            bulkImageUrl: (img && img.url) ? img.url : null,
            hasImage: !!(img && img.hasImage),
            invitationSplash: !!(img && img.splash),
            sourceLanguage: parent.sourceLanguage || null,
            // Reflect the Language Options choice (task #342) so the server send
            // path (campaignLanguageMode + campaignLanguages[1] + translatedMessage)
            // appends/replaces exactly what the host picked.
            campaignLanguageMode: langRes.campaignLanguageMode || parent.campaignLanguageMode || null,
            campaignLanguages: langRes.campaignLanguages || parent.campaignLanguages || null,
            selectedChannels: parent.selectedChannels || null,
            includeRsvpLink: v.buttons.includes('rsvp'),
            // The host's own choice when the switch was on screen, otherwise the
            // parent campaign's setting as before (task #631).
            includeQRCode: v.includeQRCode !== null && v.includeQRCode !== undefined
                ? v.includeQRCode === true
                : (parent.includeQRCode || false),
            formUrl: parent.formUrl || null,
            bulkLinkType: parent.bulkLinkType || null,
            rsvpWebsiteUrl: parent.rsvpWebsiteUrl || null,
            ...(parent.customUrl ? { customUrl: parent.customUrl } : {}),
            builderUrl: parent.builderUrl || null,
            hostName: parent.hostName || null,
            hostEmail: parent.hostEmail || null,
            hostPhone: parent.hostPhone || null,
            // A child is never itself a reminder parent
            hasReminders: false,
            reminderSettings: { enabled: false },
            createdAt: nowIso,
            requestedAt: firebase.firestore.FieldValue.serverTimestamp(),
            processed: false,
            processedAt: null,
            sentAt: null
        };
        if (typeof window.isCurrentUserAdminCached === 'function' && await window.isCurrentUserAdminCached()) {
            child.isTest = true;
        }

        const batch = db.batch();
        batch.set(db.collection('bulk_campaigns').doc(childId), child);
        // Flag the parent so its card shows the "N reminders" chip and nests the
        // child (mirrors the wizard's split-reminder parent flags).
        batch.set(db.collection('bulk_campaigns').doc(parentId), {
            hasSplitReminders: true,
            hasReminders: false,
            childReminderCampaignIds: firebase.firestore.FieldValue.arrayUnion(childId)
        }, { merge: true });
        await batch.commit();
        if (child.isImmediate) await this._dispatchReminderNow(childId);
    }

    // "Send now" reminders shouldn't wait for the next cron tick (task #546):
    // kick the owner-authorized auto-process endpoint so dispatch starts
    // immediately (it resolves the audience, creates the WhatsApp template, and
    // sends the email half right away; the WhatsApp half still waits on Meta
    // approval when the template is new). Deliberately fire-and-log: if this
    // call fails for any reason the doc's due scheduledSendDate means the
    // 5-minute cron picks it up anyway — a delay, never a lost send.
    async _dispatchReminderNow(campaignId) {
        try {
            const user = window.firebaseAuth?.currentUser || (firebase.auth && firebase.auth().currentUser);
            if (!user) return;
            const token = await user.getIdToken();
            await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/auto-process`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
        } catch (err) {
            console.warn('Immediate reminder dispatch failed — the scheduler will send it shortly:', err?.message || err);
        }
    }

    /**
     * Take down every Bootstrap tooltip inside `root` (task #684).
     *
     * The campaign list is re-rendered wholesale — on a poll, a filter, a
     * status change — with `innerHTML =`. That destroys the elements the
     * tooltips are anchored to, but NOT the tip elements themselves, which
     * Bootstrap parks on <body>. A tip that happened to be OPEN under the
     * cursor at that moment is left there with nothing to be positioned
     * against, and nothing ever removes it — the cursor leaving does not,
     * because the element that would have heard the mouseleave is gone. It
     * comes to rest in the top-left corner and stays for the rest of the
     * session: the floating black box on the report, carrying the text of the
     * row it had been over.
     *
     * dispose() removes the tip with the instance, which is the fix; the sweep
     * below is for any that got away before this existed.
     */
    _disposeTooltipsIn(root) {
        if (!root || !window.bootstrap?.Tooltip) return;
        root.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
            try { bootstrap.Tooltip.getInstance(el)?.dispose(); } catch (e) { /* non-fatal */ }
        });
    }

    /**
     * Remove any tip element on <body> that no element still points at.
     *
     * Bootstrap gives each open tip an id and puts that id in the trigger's
     * aria-describedby, so an orphan is exactly a tip no live element
     * describes. Belt and braces behind _disposeTooltipsIn: a tip left by
     * anything else that replaces markup out from under one — and by every
     * session that loaded the dashboard before this fix — is cleared on the
     * next render rather than living in the corner until a reload.
     */
    _sweepOrphanTooltips() {
        document.querySelectorAll('body > .tooltip').forEach((tip) => {
            if (!tip.id) { tip.remove(); return; }
            if (!document.querySelector(`[aria-describedby~="${tip.id}"]`)) tip.remove();
        });
    }

    renderCampaigns() {
        this._ensureReminderToggleStyles();
        const loading = document.getElementById('campaignsLoadingState');
        const content = document.getElementById('campaignsMainContent');
        const historyHeader = document.getElementById('campaignsHistoryHeader');
        const campaignsList = document.getElementById('campaignsCampaignsList');
        const noCampaigns = document.getElementById('campaignsNoCampaignsMessage');

        if (loading) loading.style.display = 'none';
        if (content) content.style.display = 'block';

        // Load wallet balance
        this.loadWalletBalance();

        const shown = this._groupCampaignsForDisplay();
        if (shown.length === 0) {
            // Keep the action toolbar visible AND anchored exactly like the
            // populated view (task #371): heading dropped, Send Messages first,
            // justify-content-start (which is direction-aware — left in LTR, right
            // in RTL). Previously the empty state left the header in its original
            // heading-vs-actions split, so the buttons jumped to the opposite edge
            // the moment history was empty. Arranging here removes that jump.
            if (historyHeader) historyHeader.style.display = 'flex';
            if (campaignsList) campaignsList.style.display = 'none';
            if (noCampaigns) noCampaigns.style.display = 'block';
            this._arrangeCampaignToolbar();
            this._renderExpandCollapseAllControl();
        } else {
            if (historyHeader) historyHeader.style.display = 'flex';
            if (campaignsList) campaignsList.style.display = 'block';
            if (noCampaigns) noCampaigns.style.display = 'none';

            // Decide which reminder groups open vs. collapsed by default BEFORE
            // rendering (smart-state rules). Manual per-session toggles are kept in
            // _reminderToggleOverrides and win over these defaults.
            this._reminderExpandDefaults = this._computeReminderExpandStates();
            // Read by every parent row's bell (one pass, not one per row).
            this._reminderAttentionMap = this._reminderAttentionByParent();

            // Every tooltip in here is about to have its element destroyed by the
            // line below, so let Bootstrap take its tip element away with it
            // (task #684).
            this._disposeTooltipsIn(campaignsList);

            // Render campaign items. Linked reminder children are ordered
            // immediately beneath their parent campaign (see _groupCampaignsForDisplay).
            campaignsList.innerHTML = shown.map(c => this.renderCampaignItem(c)).join('');

            // Left-align toolbar (drop "Campaign History", + New Campaign first).
            this._arrangeCampaignToolbar();
            // Header control to expand/collapse every reminder group at once.
            this._renderExpandCollapseAllControl();

            // Initialize Bootstrap tooltips on campaign cards. `container: body`
            // so the tip is measured against the viewport and flips instead of
            // being clipped by a scrolling card (task #684).
            campaignsList.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
                try { new bootstrap.Tooltip(el, { container: 'body' }); } catch (e) { /* non-fatal */ }
            });
            this._sweepOrphanTooltips();

            // Add event listeners for campaign action buttons
            campaignsList.querySelectorAll('[data-action="view-status"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.showContactStatusModal(campaignId);
                });
            });

            // "N not delivered" opens the contact list already narrowed to those
            // people (task #657). The number was the only thing telling a host
            // that a send had gaps, and it named nobody — finding out WHO meant
            // opening the status list and hunting for the right filter. The
            // tooltip explaining Meta's reasons stays on the same element; a
            // Bootstrap tooltip and a click listener do not fight.
            //
            // It opens the FAILED view, not "Not reached" (round 2): the number on
            // the card is the failed count, and only the Failed filter shows the
            // "What went wrong and what to do" section with its Retry / switch
            // channel actions - which is the part a host clicking "22 not
            // delivered" is actually after.
            campaignsList.querySelectorAll('[data-action="view-not-delivered"]').forEach(el => {
                const open = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    bootstrap.Tooltip.getInstance(el)?.hide();
                    this.showContactStatusModal(el.dataset.campaignId, 'failed');
                };
                el.addEventListener('click', open);
                el.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') open(e);
                });
            });

            // "Send from my WhatsApp (N)" on the card: the Failed view of this
            // message with the walk-through already open, so a host whose
            // guests our WhatsApp cannot reach (130497…) starts from the card.
            campaignsList.querySelectorAll('[data-action="wa-walk"]').forEach(el => {
                el.addEventListener('click', async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await this.showContactStatusModal(el.dataset.campaignId, 'failed');
                    this._openWaWalk();
                });
            });

            // Retry on a message where every guest was skipped.
            campaignsList.querySelectorAll('[data-action="retry-skipped"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.retrySkippedCampaign(btn.dataset.campaignId, btn);
                });
            });

            // Toggle the collapsed reminder children under a parent campaign
            campaignsList.querySelectorAll('[data-action="toggle-reminders"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.toggleReminders(e.currentTarget.dataset.campaignId);
                });
            });

            // Who a not-yet-sent reminder would go to if it sent now (review #16).
            campaignsList.querySelectorAll('[data-action="view-recipients"]').forEach(btn => {
                const open = (e) => {
                    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    this.showReminderAudiencePreview(e.currentTarget.dataset.campaignId, null);
                };
                btn.addEventListener('click', open);
                btn.addEventListener('keydown', open);
            });

            campaignsList.querySelectorAll('[data-action="preview-reminder-audience"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.showReminderAudiencePreview(e.currentTarget.dataset.campaignId, e.currentTarget);
                });
            });

            // Edit a not-yet-sent reminder child (message / timing / audience)
            campaignsList.querySelectorAll('[data-action="edit-reminder"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.showEditReminderModal(e.currentTarget.dataset.campaignId);
                });
            });

            campaignsList.querySelectorAll('[data-action="preview-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.showCampaignPreviewModal(campaignId);
                });
            });

            campaignsList.querySelectorAll('[data-action="remind-pending"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.remindPending(campaignId);
                });
            });

            campaignsList.querySelectorAll('[data-action="remind-yes"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.remindYes(campaignId);
                });
            });

            campaignsList.querySelectorAll('[data-action="remind-maybe"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.remindMaybe(campaignId);
                });
            });

            campaignsList.querySelectorAll('[data-action="remind-no"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.remindNo(campaignId);
                });
            });

            // "Send to these too" under an outage-held note (_outageHeldNoteHtml).
            campaignsList.querySelectorAll('[data-action="send-held"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.sendHeldGuests(e.currentTarget.dataset.campaignId, e.currentTarget);
                });
            });

            // Add event listeners for add-contacts buttons
            campaignsList.querySelectorAll('[data-action="add-contacts"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.showAddContactsModal(campaignId);
                });
            });

            // Add event listeners for edit-campaign buttons (opens full wizard)
            campaignsList.querySelectorAll('[data-action="edit-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.editCampaign(campaignId);
                });
            });

            // Duplicate — opens wizard fresh, pre-filled with the customize-step
            // fields (message, image, languages, link) and the source's follow-up
            // reminders (task #425). Contacts and scheduling stay empty so the
            // user picks them for the new campaign.
            campaignsList.querySelectorAll('[data-action="duplicate-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.duplicateCampaign(campaignId);
                });
            });

            // Personal links — every guest's own ?tk link for this message, to
            // re-share by hand (the wizard owns the modal; it runs here too).
            campaignsList.querySelectorAll('[data-action="personal-links"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    if (window.PersonalLinks) window.PersonalLinks.open({ formId: this.formId, campaignId });
                });
            });

            // Tag everyone on this message (task #847) — the tags decide which
            // parts of the event each guest's personalized link shows.
            campaignsList.querySelectorAll('[data-action="tag-contacts"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.tagCampaignContacts(e.currentTarget.dataset.campaignId);
                });
            });

            // Add event listeners for cancel-campaign buttons
            campaignsList.querySelectorAll('[data-action="cancel-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.confirmCancelCampaign(campaignId);
                });
            });

            // Rename: one click on the name edits it in place (task #1037,
            // Esther: the double-click opened a browser prompt). Enter also
            // starts it for keyboard users.
            campaignsList.querySelectorAll('[data-action="rename-campaign-name"]').forEach(el => {
                const trigger = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.renameCampaign(el.dataset.campaignId, el);
                };
                el.addEventListener('click', trigger);
                el.addEventListener('keydown', (e) => { if (e.key === 'Enter') trigger(e); });
            });

            // Send-test buttons (task #424)
            campaignsList.querySelectorAll('[data-action="send-test"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.sendCampaignTest(e.currentTarget.dataset.campaignId, e.currentTarget);
                });
            });

            // Add event listeners for continue-draft buttons
            campaignsList.querySelectorAll('[data-action="continue-draft"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.continueDraft(campaignId);
                });
            });

            // Add event listeners for delete-draft buttons
            campaignsList.querySelectorAll('[data-action="delete-draft"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.confirmDeleteDraft(campaignId);
                });
            });

            // Add event listeners for delete-campaign buttons (non-draft permanent delete)
            campaignsList.querySelectorAll('[data-action="delete-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.confirmDeleteCampaign(campaignId);
                });
            });

            // Add event listeners for resume-campaign buttons (paused campaigns)
            campaignsList.querySelectorAll('[data-action="resume-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.resumeCampaign(campaignId);
                });
            });

            // Add event listeners for send-now-campaign buttons (paused campaigns with passed date)
            campaignsList.querySelectorAll('[data-action="send-now-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.sendNowCampaign(campaignId);
                });
            });

            // Add event listeners for buy-credits-from-card buttons (insufficient-credits paused campaigns)
            campaignsList.querySelectorAll('[data-action="buy-credits-from-card"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (window.WalletManager) {
                        window.WalletManager.showPurchaseModal(async () => {
                            await this.loadWalletBalance();
                        }, this.formId, { resumeCampaignId: e.currentTarget.dataset.campaignId });
                    }
                });
            });

            // Add event listeners for pause-campaign buttons (scheduled campaigns)
            campaignsList.querySelectorAll('[data-action="pause-campaign"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    this.pauseCampaign(campaignId);
                });
            });

            campaignsList.querySelectorAll('[data-action="toggle-auto-retry"]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    const campaignId = e.currentTarget.dataset.campaignId;
                    const currentlyPaused = e.currentTarget.dataset.paused === 'true';
                    this.toggleAutoRetry(campaignId, !currentlyPaused);
                });
            });
        }
    }

    // Compact, collapsible card for a linked reminder child. Reminder children
    // are sub-items of their parent campaign, so they get a trimmed layout
    // (one-line label + audience + 3 icon-only actions) instead of the full
    // primary-campaign treatment. Hidden by default; the parent's "N reminders"
    // chip toggles them (see toggleReminders).
    // "Not delivered" count that also includes SEND-TIME failures (e.g. a WhatsApp
    // template rejected by Meta, error 132001) which never produce a delivery
    // webhook and so are invisible to trackingStats (built only from callbacks).
    // Without this, a batch that failed at send (0 delivered, hundreds failed)
    // would show "0 not delivered". permanentFailures + retryQueue are the
    // concrete per-contact send-time failure records; take the larger of that and
    // the webhook-tracked count so neither source can under-report.
    /**
     * How many guests each channel carries on this message: email, text (a
     * US number on 'mobile'/'both' goes SMS-direct, as the server's
     * takesSmsDirect) and WhatsApp. Read from the stored list; the sent
     * counters stand in when there is none.
     */
    _campaignLegs(campaign) {
        const list = Array.isArray(campaign.contacts) ? campaign.contacts : [];
        const isUS = (p) => (window.NanpRegion ? window.NanpRegion.isUSPhone(p) : false);
        const legs = { email: 0, sms: 0, whatsapp: 0 };
        list.forEach(c => {
            if (!c) return;
            const m = c.method;
            if ((m === 'email' || m === 'both') && c.email) legs.email++;
            if (m === 'whatsapp' && c.phone) legs.whatsapp++;
            else if ((m === 'mobile' || m === 'both') && c.phone) legs[isUS(c.phone) ? 'sms' : 'whatsapp']++;
        });
        if (!list.length) {
            legs.email = campaign.emailStats?.sentCount || 0;
            legs.sms = campaign.smsStats?.sentCount || 0;
        }
        return legs;
    }

    /**
     * One plain line per channel for a message that has not finished going
     * out, saying what is actually happening on each. One "Processing" badge
     * with an English-only tooltip used to cover three different states
     * (nothing sent and retrying, waiting for WhatsApp, email and texts sent
     * with only WhatsApp waiting); 7 hosts asked support "did it send?"
     * (campaign audit, 2026-10-07). Returns [] for any other status.
     */
    _channelStatusLines(campaign, status) {
        const lines = [];
        const line = (icon, chKey, text) => lines.push({ icon, text: chKey ? `${this.cTxt(chKey)}: ${text}` : text });
        if (status === 'pending') {
            // A pass that sent nobody, retried on the next tick (finalizeSendResult).
            line('bi-arrow-repeat', null, this.cTxt(campaign.noSendAttempts > 0 ? 'stRetrying' : 'stSendingSoon'));
            return lines;
        }
        if (status !== 'pending_template' && status !== 'emails_sent_whatsapp_pending') return lines;
        const legs = this._campaignLegs(campaign);
        const othersSent = status === 'emails_sent_whatsapp_pending';
        const sched = campaign.scheduledSendDate
            ? (campaign.scheduledSendDate.toDate?.() || new Date(campaign.scheduledSendDate)) : null;
        const notYet = sched && !isNaN(sched) && sched.getTime() > Date.now();
        const other = (n, sentCount) => {
            if (othersSent || sentCount > 0) return this.cTxt('stSentN', sentCount || n);
            return this.cTxt(notYet ? 'stAtSendTime' : 'stGoingOut');
        };
        if (legs.email > 0) line('bi-envelope', 'chEmail', other(legs.email, campaign.emailStats?.sentCount || 0));
        if (legs.sms > 0) line('bi-phone', 'chText', other(legs.sms, campaign.smsStats?.sentCount || 0));
        // The status itself says a WhatsApp leg is waiting, list or no list.
        // Over two hours is past "a few minutes": say so rather than repeat it.
        const tplAt = campaign.whatsappTemplate?.createdAt || campaign.requestedAt || campaign.createdAt;
        const tplDate = tplAt ? (tplAt.toDate?.() || new Date(tplAt)) : null;
        const slow = tplDate && !isNaN(tplDate) && (Date.now() - tplDate.getTime()) > 2 * 60 * 60 * 1000;
        // The wizard could not get it to WhatsApp yet (the blue note below
        // says so): no review is happening, so no "waiting for WhatsApp to
        // approve" (round-3 review).
        line('bi-whatsapp', 'chWhatsApp', this.cTxt(DashboardCampaigns.templateSubmitPending(campaign)
            ? 'stWaSubmitting' : (slow ? 'stWaWaitingLong' : 'stWaWaiting')));
        return lines;
    }

    _channelStatusHtml(lines, cls = 'text-body-secondary') {
        if (!lines || !lines.length) return '';
        return `<div class="campaign-channel-status small ${cls} mb-1" style="min-width:0;overflow-wrap:anywhere;">${lines.map(l =>
            `<div><i class="bi ${l.icon}" style="margin-inline-end:0.3rem;" aria-hidden="true"></i>${this._escape(l.text)}</div>`).join('')}</div>`;
    }

    /**
     * Why a 'failed' message did not go out, and what to do, in the host's
     * language. "Unable to Send" never said why: the server's reason was
     * English text the card did not show. Worded from failureCode (written
     * since 2026-10-07); older docs are read from the text they carry.
     *
     * `hasBanner`: the card shows the red template banner, which already
     * gives WhatsApp's reason. Only the main card has one; a reminder row
     * (`reminder`) never does, so it always carries the reason (task #590:
     * the trailing-tag rule is what tells the host what to fix), and its
     * advice points at the row's edit pencil, as it has no Edit & Resend.
     */
    _failureReasonText(campaign, { hasBanner = false, reminder = false } = {}) {
        const reason = String(campaign.failureReason || '');
        const last = String(campaign.lastError || '');
        let code = campaign.failureCode || null;
        if (!code) {
            if (/form no longer exists/i.test(reason)) code = 'form_missing';
            else if (/can only be reached on WhatsApp/i.test(reason)) code = 'wa_refused_wa_only';
            else if (/template was rejected/i.test(reason) || campaign.whatsappTemplate?.status === 'rejected') code = 'wa_refused';
            else if (/no delivery method/i.test(last)) code = 'no_method';
            else if (/attempted 0 of/i.test(last)) code = 'nothing_sent';
            else if (campaign.error) code = 'send_error';
        }
        // WhatsApp's words for the refusal. The dispatcher writes lastError in
        // the same update as wa_refused_wa_only; the template-status endpoint
        // writes wa_refused with no lastError of its own, so an older one
        // ("Send path attempted 0 of N...") must never be quoted as WhatsApp's.
        const raw = code === 'wa_refused_wa_only' ? (last || String(campaign.templateCreationError || ''))
            : code === 'wa_refused' ? String(campaign.whatsappTemplate?.rejectedReason || '') : '';
        const info = (raw && typeof BulkSharingWizard !== 'undefined' && BulkSharingWizard.describeTemplateError)
            ? BulkSharingWizard.describeTemplateError(raw) : null;
        // The template auto-create can throw our own crash, which is stored as
        // the refusal ('reusable is not defined'): not WhatsApp's doing, and
        // there is nothing in the message to edit.
        if (info && info.ours) code = 'send_error';
        // wa_refused is also written once the email/text leg has gone: those
        // guests have it, and "nothing was sent" beside "40 sent" led hosts to
        // resend to everyone (M2 review, 2026-10-07).
        if (code === 'wa_refused' && DashboardCampaigns.reachedAnyone(campaign)) code = 'wa_refused_partial';
        const KEYS = {
            form_missing: 'FormMissing', wa_refused_wa_only: 'WaOnly', wa_refused: 'WaRefused',
            wa_refused_partial: 'WaRefusedPartial', nothing_sent: 'NothingSent', no_method: 'NoMethod',
            send_error: 'SendError'
        };
        let text = this.cTxt((reminder ? 'remFail' : 'fail') + (KEYS[code] || 'Generic'));
        if (/^wa_refused/.test(code || '') && !hasBanner) {
            const why = this._templateReasonText(raw, true);
            if (why) text += ' ' + why;
            // WhatsApp gave nothing specific: on a WhatsApp-only reminder,
            // name the usual cause, a tag at the very end (task #590; M3's note).
            else if (reminder && code === 'wa_refused_wa_only') text = this.cTxt('remFailWaOnlyRefusedNote');
        }
        return text;
    }

    // Has this message reached anyone, on any channel?
    static reachedAnyone(campaign) {
        const c = campaign || {};
        return ((c.sentCount || 0) + (c.emailStats?.sentCount || 0) + (c.smsStats?.sentCount || 0)
            + (c.trackingStats?.sent || 0)) > 0;
    }

    /**
     * WhatsApp's reason for refusing a message, in the host's language: a
     * rule we recognise is worded from its key (describeTemplateError), and
     * anything else is WhatsApp's own sentence, quoted.
     */
    _templateReasonText(raw, skipNoReason = false) {
        if (!String(raw || '').trim()) return '';
        const info = (typeof BulkSharingWizard !== 'undefined' && BulkSharingWizard.describeTemplateError)
            ? BulkSharingWizard.describeTemplateError(raw) : { key: null, text: String(raw || '') };
        // "Not accepted, edit and resend" already says all a bare refusal can.
        if (skipNoReason && (info.key === 'noReason' || info.key === 'ours')) return '';
        if (info.key) return this.cTxt('tplErr_' + info.key);
        return info.text ? this.cTxt('tplLangsSaid', { text: info.text }) : '';
    }

    // A WhatsApp language code as the host's dashboard names it. 'fil' is the
    // slot our combined two-language template is filed under, not Filipino.
    _langName(code) {
        const c = String(code || '').split(/[_-]/)[0].toLowerCase();
        if (!c) return null;
        if (c === 'fil') return this.cTxt('langCombined');
        try {
            return new Intl.DisplayNames([this.dashboardLanguage || 'en'], { type: 'language' }).of(c) || null;
        } catch (_) {
            return null;
        }
    }

    /**
     * The refused-language banner, worded from templateRefusal (codes the
     * server writes beside the English sentence): each language with its own
     * reason. With two refused languages, the first one's reason used to be
     * attached to both. Returns escaped HTML.
     */
    _templateRefusalHtml(refusal) {
        const langs = (refusal.languages || []).map(l => ({ ...l, name: this._langName(l.language) }));
        const named = langs.filter(l => l.name);
        const why = (l) => (l.reasonKey && DashboardCampaigns.CAMPAIGN_I18N.en['tplErr_' + l.reasonKey]
            ? this.cTxt('tplErr_' + l.reasonKey)
            : (l.reasonText ? this.cTxt('tplLangsSaid', { text: l.reasonText }) : ''));
        const many = named.length > 1;
        // Our combined two-language template does not fit the "{langs}
        // version" slot in every language (German, Arabic), so it has its
        // own sentence.
        const combinedOnly = !many && named.length === 1 && /^fil/i.test(String(named[0].language || ''));
        const head = named.length === 0 ? this.cTxt('tplLangsHeadUnknown')
            : combinedOnly ? this.cTxt('tplLangsHeadCombined')
            : this.cTxt(many ? 'tplLangsHeadMany' : 'tplLangsHeadOne', { langs: named.map(l => l.name).join(', ') });
        const parts = [`<div>${this._escape(head)}</div>`];
        if (many) {
            named.forEach(l => { const w = why(l); if (w) parts.push(`<div>${this._escape(`${l.name}: ${w}`)}</div>`); });
        } else {
            // The head names one language (or none): only that language's
            // reason may follow it, never one from a code we cannot name.
            const pool = named.length === 1 ? named : (langs.length === 1 ? langs : []);
            const l = pool.find(x => why(x));
            if (l) parts.push(`<div>${this._escape(why(l))}</div>`);
        }
        parts.push(`<div>${this._escape(this.cTxt(many ? 'tplLangsEditMany' : 'tplLangsEditOne'))}</div>`);
        return parts.join('');
    }

    /**
     * The card's "N not delivered" tip, naming the channel that failed. It
     * always said WhatsApp, also for a bounced email or text.
     */
    _notDeliveredTip(campaign, n, held = 0) {
        const tracked = campaign.trackingStats?.failedBy || {};
        // Send-time failures with no tracking doc fill in only the channels
        // tracking has nothing on: an SMS-direct failure has both a tracking
        // doc and a ledger entry, and taking the larger of each per channel,
        // after misreading the entry as WhatsApp, read "WhatsApp 1 · Text 1"
        // for one failed text (M2 review, 2026-10-07).
        const sendTime = {};
        [...(campaign.permanentFailures || []), ...(campaign.retryQueue?.failedContacts || [])].forEach(f => {
            const ch = DashboardCampaigns.failureEntryChannel(f);
            if (ch) sendTime[ch] = (sendTime[ch] || 0) + 1;
        });
        const by = {};
        ['whatsapp', 'sms', 'email'].forEach(ch => {
            const v = tracked[ch] > 0 ? tracked[ch] : (sendTime[ch] || 0);
            if (v > 0) by[ch] = v;
        });
        const chans = Object.keys(by);
        // Guests held back on purpose are in the card's count too (#1039).
        if (held > 0) {
            if (!n) return this.cTxt('skippedTip');
            const label = { whatsapp: 'chWhatsApp', sms: 'chText', email: 'chEmail' };
            const parts = chans.length ? chans.map(ch => `${this.cTxt(label[ch])} ${by[ch]}`) : [`${this.cTxt('deliveryFailed')} ${n}`];
            parts.push(held === 1 ? this.cTxt('skippedOne') : this.cTxt('skippedMany', held));
            return this.cTxt('ndTipMixed', { n: n + held, parts: parts.join(' · ') });
        }
        // cCount: one guest reads "One of your texts wasn't delivered", not
        // "1 of your texts weren't" (M1 kept a singular form; wave-2 merge).
        if (chans.length === 1) {
            return this.cCount({ whatsapp: 'ndTipWa', sms: 'ndTipSms', email: 'ndTipEmail' }[chans[0]], n);
        }
        if (chans.length > 1) {
            const label = { whatsapp: 'chWhatsApp', sms: 'chText', email: 'chEmail' };
            return this.cTxt('ndTipMixed', { n, parts: chans.map(ch => `${this.cTxt(label[ch])} ${by[ch]}`).join(' · ') });
        }
        return this.cCount('ndTipGeneric', n);
    }

    /**
     * Which channel a send-time failure entry (permanentFailures, retryQueue)
     * is about, or null when it cannot be told. Entries written before
     * 2026-10-07 name no channel for a failed text or email, and the guest's
     * method alone read a US 'mobile'/'both' text failure and a 'both'
     * guest's email failure as WhatsApp. So: the entry's channel; else the
     * code's shape (our text codes, Meta's numeric ones); else the routing
     * rule (a US number on 'mobile'/'both' goes by text, as the server's
     * takesSmsDirect).
     */
    static failureEntryChannel(f) {
        if (!f) return null;
        if (f.channel === 'email' || f.channel === 'sms' || f.channel === 'whatsapp') return f.channel;
        const code = String(f.errorCode || '');
        if (/^(SMS_|NON_US_NUMBER$|BREVO_REJECTED$)/.test(code)) return 'sms';
        if (/^EMAIL_/.test(code)) return 'email';
        if (/^\d+$/.test(code) || code === 'marketing_limit_reached') return 'whatsapp';
        const c = f.contact || f;
        const m = c.method;
        const isUS = (p) => !!p && !!window.NanpRegion && window.NanpRegion.isUSPhone(p);
        if (m === 'email') return 'email';
        if (m === 'sms') return 'sms';
        if ((m === 'mobile' || m === 'both') && isUS(c.phone)) return 'sms';
        if (m === 'whatsapp' || m === 'mobile') return 'whatsapp';
        // 'both' with no code: the email or the WhatsApp leg; we cannot say.
        return null;
    }

    _failedDeliveryCount(campaign) {
        const tracked = campaign.trackingStats?.failed || 0;
        const sendTime = (campaign.permanentFailures?.length || 0)
            + (campaign.retryQueue?.failedContacts?.length || 0);
        return Math.max(tracked, sendTime);
    }

    /**
     * Guests this message deliberately did not send to: Meta's 24-hour
     * cooldown (131049), a number not on WhatsApp, a STOP reply, an email
     * unsubscribe. They sit in no headline count, so a message where EVERY
     * guest was skipped read "Sent" with 0 reached — Mesh' Bachelorette
     * (2026-10-06): one guest in cooldown, the message re-created 4 times in
     * 35 minutes. 5 such 'sent' campaigns in 60 days.
     *
     * The server writes skipSummary (counted against tracking truth, so a
     * guest a later pass did reach drops out) and allSkipped, and its flag
     * stands unless something has really gone out since. Older docs only
     * have the raw list: count it one row per person, and treat it as
     * all-skipped when nothing was sent or failed, for a few days only.
     *
     * `quiet`: nothing left for the host to do. Every skipped guest has
     * replied since the message was created or has opened a personal link
     * made on it, or the only reason is a STOP reply. The card then says
     * "Not sent" without the amber call to action or Retry, which would
     * WhatsApp a guest who already has it (review of 2026-10-07: the card's
     * own advice left it stuck). Merely making the links is not enough
     * (round 2): the host may only have looked.
     */
    _skipSummary(campaign) {
        const raw = Array.isArray(campaign.suppressedRecipients) ? campaign.suppressedRecipients : [];
        const s = campaign.skipSummary;
        const hasSummary = !!(s && typeof s.count === 'number');
        const toDate = (v) => {
            if (!v) return null;
            const d = v.toDate ? v.toDate() : new Date(v);
            return isNaN(d) ? null : d;
        };
        let count = 0, cooldown = 0, notOnWhatsApp = 0, optedOut = 0, unsubscribed = 0, unreachable = 0, liftsAt = null;
        if (hasSummary) {
            count = s.count;
            cooldown = s.cooldown || 0;
            notOnWhatsApp = s.notOnWhatsApp || 0;
            optedOut = s.optedOut || 0;
            unsubscribed = s.unsubscribed || 0;
            unreachable = s.unreachable || 0;
            liftsAt = toDate(s.liftsAt);
        } else {
            // One row per guest; a guest skipped on two passes is blocked
            // until their LATER lift (a fresh 131049 extends it), and the
            // earliest of those is when a retry can first reach anyone.
            const seen = new Set();
            const liftByGuest = new Map();
            raw.forEach(e => {
                if (!e) return;
                const k = DashboardCampaigns._skipEntryKey(e);
                if (!k) return;
                const u = e.reason === 'meta_throttle_cooldown' ? toDate(e.until) : null;
                if (u && (!liftByGuest.has(k) || u > liftByGuest.get(k))) liftByGuest.set(k, u);
                if (seen.has(k)) return;
                seen.add(k);
                count++;
                if (e.reason === 'meta_throttle_cooldown') cooldown++;
                else if (e.reason === 'user_opted_out') optedOut++;
                else if (e.reason === 'email_unsubscribed') unsubscribed++;
                else if (DashboardCampaigns.UNREACHABLE_REASONS.has(e.reason)) unreachable++;
                else notOnWhatsApp++;
            });
            liftByGuest.forEach(u => { if (!liftsAt || u < liftsAt) liftsAt = u; });
        }
        const sentAt = toDate(campaign.processedAt || campaign.sentAt);
        if (!liftsAt && cooldown > 0 && sentAt) {
            // Not recorded before 2026-10-07. Our cooldown runs 24 hours from
            // Meta's refusal, which came before this send, so 24 hours after
            // the send is the latest it can lift.
            liftsAt = new Date(sentAt.getTime() + 24 * 60 * 60 * 1000);
        }
        // What this message actually SENT. trackingStats.sent also counts
        // personal links (see loadMessageTrackingStats), and making the links
        // is what the note tells the host to do, so they are taken back out.
        const ts = campaign.trackingStats || {};
        const realSent = Math.max(0, (ts.sent || 0) - (ts.manual || 0));
        const reached = realSent > 0 || (campaign.sentCount || 0) > 0
            || this._failedDeliveryCount(campaign) > 0 || (campaign.failedCount || 0) > 0;
        let allSkipped;
        if (typeof campaign.allSkipped === 'boolean') {
            // The server's verdict, written with its summary (or without it
            // when that read failed); it stands unless something went out since.
            allSkipped = campaign.allSkipped && !reached;
        } else {
            // Before skipSummary existed. An amber call to action on a message
            // weeks old helps nobody, so after 3 days only the count shows.
            const at = sentAt || toDate(campaign.createdAt);
            const recent = !!at && (Date.now() - at.getTime()) < 3 * 24 * 60 * 60 * 1000;
            allSkipped = recent && !reached;
        }
        allSkipped = allSkipped && count > 0 && ['sent', 'partially_sent'].includes(campaign.status);

        // Looked after since the skip: replied since the message was created,
        // or opened a personal link made on it (counted per person in
        // loadMessageTrackingStats).
        const fu = campaign.skipFollowUp || null;
        const replied = fu ? fu.replied : 0;
        const followed = fu ? fu.replied + fu.linked : 0;
        const handled = !!fu && fu.total > 0 && followed >= fu.total;
        const allReplied = !!fu && fu.total > 0 && replied >= fu.total;
        const quiet = handled || (count > 0 && optedOut === count);
        return {
            count, cooldown, notOnWhatsApp, optedOut, unsubscribed, unreachable, liftsAt, allSkipped,
            handled, allReplied, anyHandled: followed > 0, quiet
        };
    }

    // Match keys for one skipped person, phone first. The last 9 digits, so
    // "050-123 4567" on the guest list and "+972 50 123 4567" on a tracking
    // record are one person; under 6 digits is no phone key at all.
    // One skipped person's key: their number or address, as below, and their
    // name where the number is not theirs alone (shared_number: the
    // housemate's message reached it) or they have neither (as the server's
    // campaign-skips skipGuestId).
    static _skipEntryKey(e) {
        if (!e) return null;
        const keys = DashboardCampaigns._skipGuestKeys(e.phone, e.email);
        if (e.reason === 'shared_number' || !keys.length) {
            const n = String(e.name || '').trim().toLowerCase();
            return n ? 'n:' + n + '|' + (keys[0] || '') : null;
        }
        return keys[0];
    }

    static _skipGuestKeys(phone, email) {
        const keys = [];
        const d = String(phone || '').replace(/\D/g, '');
        if (d.length >= 6) keys.push('p:' + (d.length > 9 ? d.slice(-9) : d));
        const e = String(email || '').trim().toLowerCase();
        if (e) keys.push('e:' + e);
        return keys;
    }

    // "21:36" today, "Thu 21:36" on another day, in the dashboard's language.
    _skipTimeStr(date) {
        const lang = this._dateLocale();
        const sameDay = date.toDateString() === new Date().toDateString();
        try {
            return sameDay
                ? date.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
                : date.toLocaleString(lang, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        } catch (_) {
            return date.toLocaleString();
        }
    }

    // The "N not delivered" stat of a card or reminder row: the failures
    // AND the guests the message held back on purpose (task #1039, Esther:
    // "for all sense and purposes for the host it wasn't delivered"). They
    // had an amber "N skipped" of their own. It opens the message's Failed
    // view, which lists both, each row saying why; the held-back ones sit
    // in no Retry or resend group, so a STOP or an unsubscribe is never
    // messaged again from there.
    _notDeliveredStatHtml(campaign, failed, skip) {
        const held = (skip && skip.count) || 0;
        const n = failed + held;
        const tip = n > 0 ? this._notDeliveredTip(campaign, failed, held) : '';
        return `<span class="cs-stat cs-stat-nd text-muted${n > 0 ? ` cs-stat-nd-link" data-action="view-not-delivered" data-campaign-id="${campaign.id}" role="button" tabindex="0` : ''}"${tip ? ` data-bs-toggle="tooltip" data-bs-placement="top" title="${DashboardCampaigns.escapeAttr(tip)}"` : ''}><i class="bi bi-slash-circle me-1"></i>${this.cCount('statNotDelivered', n)}</span>`;
    }

    // Why nobody got it: one sentence per reason, each with its own remedy.
    // The server records the reasons apart, and a host whose guest simply
    // isn't on WhatsApp must not read that the guest may have asked us to
    // stop (review of 2026-10-07). The time is OUR pause after Meta's 131049
    // refusal (suppression.service cooldownHours), not one Meta gives, so the
    // copy says so. The pause is per NUMBER across every host, so a refusal of
    // another event's message holds this one too; the copy says that as well,
    // or a host who never messaged the guest reads it as our mistake.
    _skipReasonLines(skip) {
        const lines = [];
        if (skip.cooldown > 0) {
            const future = skip.liftsAt && skip.liftsAt.getTime() > Date.now();
            const time = future ? this._skipTimeStr(skip.liftsAt) : null;
            if (skip.cooldown === 1) {
                lines.push(future ? this.cTxt('skipCoolOne', { time }) : this.cTxt('skipCoolLiftedOne'));
            } else {
                lines.push(future
                    ? this.cTxt('skipCoolMany', { n: skip.cooldown, time })
                    : this.cTxt('skipCoolLiftedMany', { n: skip.cooldown }));
            }
        }
        const add = (n, one, many) => { if (n > 0) lines.push(n === 1 ? this.cTxt(one) : this.cTxt(many, n)); };
        add(skip.notOnWhatsApp, 'skipNoWaOne', 'skipNoWaMany');
        add(skip.optedOut, 'skipOptOutOne', 'skipOptOutMany');
        add(skip.unsubscribed, 'skipUnsubOne', 'skipUnsubMany');
        add(skip.unreachable, 'skipUnreachOne', 'skipUnreachMany');
        return lines;
    }

    // Badge for a message the server paused because a messaging provider went
    // down (pausedReason PROVIDER_OUTAGE: YCloud or Brevo stopped accepting
    // sends mid-send) or because Brevo's SMS credits could not cover it
    // (BREVO_SMS_CREDITS_INSUFFICIENT). A bare "Paused" read as if the host
    // had paused it. null for any other pause.
    //
    // The why is not on the badge (a tooltip never shows on a phone): see
    // _providerPauseNoteHtml, a tappable <details> under it.
    _providerPauseBadgeHtml(campaign) {
        if (!this._providerPauseState(campaign)) return null;
        return `<span class="badge bg-warning text-dark"><i class="bi bi-exclamation-octagon" style="margin-inline-end:0.25rem;"></i>${this._escape(this.cTxt('providerPaused'))}</span>`;
    }

    // Which provider pause this is, for the copy and the Resume button:
    //   'resuming' - the host or an admin pressed Resume; the server restarts
    //                it within minutes (outageResumeRequestedBy);
    //   'auto'     - a temporary outage: the server tries again by itself
    //                (outageAutoResumeAt, set only then);
    //   'team'     - waits for our team: the provider refused our account,
    //                SMS credits ran out, or the automatic tries are used up.
    //                A Resume before the fix stops again.
    // null for any other status or pause.
    _providerPauseState(campaign) {
        if (!campaign || campaign.status !== 'paused') return null;
        if (!['PROVIDER_OUTAGE', 'BREVO_SMS_CREDITS_INSUFFICIENT'].includes(campaign.pausedReason)) return null;
        if (campaign.outageResumeRequestedBy) return 'resuming';
        return typeof campaign.outageAutoResumeAt === 'string' ? 'auto' : 'team';
    }

    // The reason under a provider-paused card or reminder row. A <details>,
    // not a title: it has to open on a tap (see _reminderDupNote).
    _providerPauseNoteHtml(campaign) {
        const state = this._providerPauseState(campaign);
        if (!state) return '';
        if (state === 'resuming') {
            return `<div class="small text-muted lh-sm provider-pause-note" dir="auto"><i class="bi bi-arrow-repeat" style="margin-inline-end:0.25rem;"></i>${this._escape(this.cTxt('providerResuming'))}</div>`;
        }
        const phrase = this.cTxt(state === 'auto' ? 'providerPausedPhraseAuto' : 'providerPausedPhrase');
        const tip = state === 'auto'
            ? this.cTxt('providerPausedTipAuto', { resume: this.cTxt('resume') })
            : this.cTxt('providerPausedTip');
        return `<details class="small text-muted lh-sm provider-pause-note" dir="auto"><summary>${this._escape(phrase)}<i class="bi bi-info-circle" style="margin-inline-start:0.3rem;opacity:0.7;" aria-hidden="true"></i></summary><div class="provider-pause-why">${this._escape(tip)}</div></details>`;
    }

    // Guests a provider outage held back (campaign.outageHeld, written by the
    // server): their WhatsApp or text got no answer, so they may already have
    // it, and no resume sends to them twice. A <details> like the pause note;
    // inside, who they are and one button that sends to them too
    // (sendHeldGuests). Product decision PMa, 2026-10-07.
    _outageHeldNoteHtml(campaign) {
        const guests = Array.isArray(campaign?.outageHeld?.guests)
            ? campaign.outageHeld.guests.filter(g => g && g.key) : [];
        const n = guests.length;
        if (!n || ['processing', 'resending', 'retrying', 'processing_additions'].includes(campaign.status)) return '';
        const SHOWN = 6;
        const names = guests.slice(0, SHOWN).map(g => this._escape(g.name || g.phone || ''));
        if (n > SHOWN) names.push(this._escape(this.cTxt('heldMore', n - SHOWN)));
        const btnLabel = n === 1 ? this.cTxt('heldSendOne') : this.cTxt('heldSend', n);
        return `<details class="small text-muted lh-sm provider-pause-note outage-held-note" dir="auto"><summary><i class="bi bi-hourglass-split" style="margin-inline-end:0.25rem;" aria-hidden="true"></i>${this._escape(this.cCount('heldPhrase', n))}<i class="bi bi-info-circle" style="margin-inline-start:0.3rem;opacity:0.7;" aria-hidden="true"></i></summary>`
            + `<div class="provider-pause-why">${this._escape(this.cTxt('heldWhy'))}</div>`
            + `<div class="provider-pause-why outage-held-names" dir="auto">${names.join(', ')}</div>`
            + `<button type="button" class="btn btn-outline-primary btn-sm mt-2 outage-held-send" data-action="send-held" data-campaign-id="${this._escape(campaign.id)}"><i class="bi bi-send" style="margin-inline-end:0.3rem;" aria-hidden="true"></i>${this._escape(btnLabel)}</button></details>`;
    }

    // The host's "send to these too" (POST /send-held). While the outage
    // pause stands they join its resume; otherwise the server sends to them
    // now, charged like any send.
    async sendHeldGuests(campaignId, btn) {
        const toast = (msg, type) => {
            if (window.languageHelpers?.showToast) window.languageHelpers.showToast(msg, type);
            else alert(msg);
        };
        if (btn) btn.disabled = true;
        try {
            const authHeaders = await window.AuthedFetch.authHeaders();
            const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/send-held`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders },
                body: '{}'
            });
            const data = await resp.json().catch(() => ({}));
            if (data.success && data.withResume) toast(this.cTxt('heldSendWithResume'), 'success');
            else if (data.success && data.stopped) {
                const restarted = data.code === 'server_restarting';
                toast(this.cTxt(restarted ? 'sendRestartRetry' : 'heldSendFailed'), 'warning');
            }
            else if (data.success) toast(this.cTxt('heldSendDone'), 'success');
            else if (data.code === 'nothing_held') { /* sent from another window: the reload shows it */ }
            else if (data.code === 'send_in_progress') toast(this.cTxt('heldSendBusy'), 'warning');
            else if (data.code === 'not_sendable') toast(this.cTxt('heldSendPaused'), 'warning');
            else if (data.code === 'server_restarting') toast(this.cTxt('sendRestartRetry'), 'warning');
            else toast(this.cTxt('heldSendFailed'), 'error');
        } catch (error) {
            console.error('Error sending to held guests:', error);
            toast(this.cTxt('heldSendFailed'), 'error');
        } finally {
            if (btn) btn.disabled = false;
            await this.loadCampaigns();
        }
    }

    // "Not sent" badge: amber while there is something to do, grey once
    // there is not (see `quiet` in _skipSummary).
    _skipBadgeHtml(skip) {
        const tip = skip.quiet ? this._skipQuietText(skip) : this.cTxt('skipNotSentTip');
        const cls = skip.quiet ? 'bg-light text-secondary border' : 'bg-warning text-dark';
        return `<span class="badge ${cls}" data-bs-toggle="tooltip" data-bs-placement="top" title="${this._escape(tip)}"><i class="bi bi-pause-circle me-1"></i>${this._escape(this.cTxt('skipNotSent'))}</span>`;
    }

    _skipQuietText(skip) {
        if (skip.allReplied) return skip.count === 1 ? this.cTxt('skipHandledRepliedOne') : this.cTxt('skipHandledRepliedMany');
        if (skip.handled) return skip.count === 1 ? this.cTxt('skipHandledLinkOne') : this.cTxt('skipHandledLinkMany');
        return this._skipReasonLines(skip).join(' ');
    }

    // Under a card or reminder row where every guest was skipped.
    //
    // Amber note while the host can act: why (one line per reason), what to
    // do — try again once our pause lifts (same message, same guests, no new
    // template) or send the personal link by hand. Retry rides /auto-process,
    // which takes a 'partially_sent' message but not a legacy 'sent' one, and
    // is withheld once any skipped guest has opened their link or replied:
    // the resend would WhatsApp them too (manual links are not "already
    // sent"). A link that was only made does not withhold it.
    // The reminder version leaves out the long why.
    //
    // Quiet line once there is nothing left to do (see `quiet`).
    _skipNoteHtml(campaign, skip, compact = false) {
        const linksBtn = (cls) => `
                            <button type="button" class="btn ${cls} btn-sm" data-action="personal-links" data-campaign-id="${campaign.id}">
                                <i class="bi bi-link-45deg me-1"></i>${this.cTxt('personalLinks')}
                            </button>`;
        if (skip.quiet) {
            const offerLinks = skip.handled && !skip.allReplied;
            return `
                <div class="small text-muted mt-2 d-flex align-items-start flex-wrap campaign-skip-quiet" style="gap:0.25rem 0.5rem;" dir="auto">
                    <i class="bi bi-info-circle flex-shrink-0" style="margin-top:2px;"></i>
                    <span style="min-width:0; flex:1 1 12rem;">${this._escape(this._skipQuietText(skip))}</span>
                    ${offerLinks ? linksBtn('btn-link p-0 align-baseline') : ''}
                </div>`;
        }
        const canRetry = skip.cooldown > 0 && campaign.status === 'partially_sent' && !skip.anyHandled;
        const future = skip.liftsAt && skip.liftsAt.getTime() > Date.now();
        let doLine = '';
        if (skip.cooldown > 0) {
            if (canRetry && future) doLine = this.cTxt('skipDoRetryAt', { time: this._skipTimeStr(skip.liftsAt) });
            else if (canRetry) doLine = this.cTxt('skipDoRetryNow');
            else doLine = this.cTxt('skipDoLink');
        }
        // A STOP reply or an email unsubscribe is no reason to hand the link
        // over: Personal links only for the cooldown and not-on-WhatsApp guests.
        const offerLinks = (skip.cooldown + skip.notOnWhatsApp) > 0;
        const why = (skip.cooldown > 0 && !compact) ? `<div class="mb-1">${this._escape(this.cTxt('skipCoolWhy'))}</div>` : '';
        const reasons = this._skipReasonLines(skip).map(l => `<div class="fw-semibold mb-1">${this._escape(l)}</div>`).join('');
        const buttons = (canRetry || offerLinks) ? `
                        <div class="d-flex flex-wrap mt-2" style="gap:0.5rem;">
                            ${canRetry ? `
                            <button type="button" class="btn btn-warning btn-sm fw-semibold" data-action="retry-skipped" data-campaign-id="${campaign.id}">
                                <i class="bi bi-arrow-repeat me-1"></i>${this.cTxt('retry')}
                            </button>` : ''}
                            ${offerLinks ? linksBtn('btn-outline-dark') : ''}
                        </div>` : '';
        return `
                <div class="alert alert-warning ${compact ? 'py-1 px-2' : 'py-2 px-3'} small mt-2 mb-0 d-flex align-items-start campaign-skip-note" role="status" style="border-radius:8px; gap:0.5rem;" dir="auto">
                    <i class="bi bi-pause-circle-fill flex-shrink-0" style="margin-top:2px;"></i>
                    <div style="min-width:0;">
                        ${reasons}
                        ${why}
                        ${doLine ? `<div>${this._escape(doLine)}</div>` : ''}
                        ${buttons}
                    </div>
                </div>`;
    }

    /**
     * Try an all-skipped message again: the same message to the same guests,
     * through /auto-process (owner-checked; it takes a 'partially_sent'
     * message). Before our pause lifts it says so instead of calling the
     * server — a resend then would only be skipped again.
     *
     * /auto-process answers success without sending in several cases (held
     * for review, paused, waiting on template approval, scheduled, already
     * handled by another tab), so the toast follows what came back rather
     * than assuming something is on its way, and the card is reloaded so it
     * shows the real state.
     */
    async retrySkippedCampaign(campaignId, btn) {
        const campaign = (this.campaigns || []).find(c => c.id === campaignId);
        if (!campaign) return;
        const toast = (msg, type) => { if (window.showToast) window.showToast(msg, type); };
        const skip = this._skipSummary(campaign);
        if (skip.liftsAt && skip.liftsAt.getTime() > Date.now()) {
            const time = this._skipTimeStr(skip.liftsAt);
            toast(skip.cooldown > 1 ? this.cTxt('skipStillBlockedMany', { time }) : this.cTxt('skipStillBlockedOne', { time }), 'warning');
            return;
        }
        const restore = btn ? btn.innerHTML : null;
        if (btn) { btn.disabled = true; btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>${this.cTxt('retry')}`; }
        let reload = false;
        try {
            const authHeaders = await window.AuthedFetch.authHeaders();
            const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/auto-process`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders }
            });
            const data = await resp.json().catch(() => ({}));
            reload = resp.status < 500 || data.error === 'INSUFFICIENT_CREDITS';
            if (data.error === 'INSUFFICIENT_CREDITS') {
                // processAllMessages has paused it; the card turns into Buy credits.
                toast(this.cTxt('skipRetryNoCredits'), 'warning');
                return;
            }
            if (!resp.ok || data.success === false) {
                toast(this.cTxt('skipRetryError'), 'error');
                console.warn('Retry of a skipped message failed:', resp.status, data.message || data.error || '');
                return;
            }
            const r = data.results || null;
            if (r && r.held) toast(this.cTxt('skipRetryHeld'), 'info');
            else if (r && r.paused) toast(this.cTxt('skipRetryPaused'), 'warning');
            else if (r && (r.sent || 0) > 0) toast(this.cTxt('skipRetrySent'), 'success');
            else if (r && (r.failed || 0) > 0) toast(this.cTxt('skipRetryFailed'), 'warning');
            else if (r && (r.skippedOnly || r.allSkipped)) toast(this.cTxt('skipRetryStill'), 'warning');
            else toast(this.cTxt('skipRetryRequested'), 'info');
        } catch (err) {
            console.warn('Retry of a skipped message failed:', err?.message || err);
            toast(this.cTxt('skipRetryError'), 'error');
        } finally {
            if (btn && btn.isConnected) { btn.disabled = false; if (restore != null) btn.innerHTML = restore; }
            if (reload) this.loadCampaigns().catch(e => console.warn('Reload after retry failed:', e?.message || e));
        }
    }

    // The Contact Status modal is fed by /api/message-tracking, which is built
    // purely from delivery webhooks. SEND-TIME failures (e.g. a WhatsApp template
    // Meta rejected, error 132001) never produce a webhook, so they have NO
    // tracking doc: the failed guest is absent from the list entirely AND the
    // per-campaign scope filter (campaignIds) can't see them. Inject them here from
    // each campaign's own failure ledger (permanentFailures / retryQueue /
    // failedRecipients) so the status list shows the full recipient set — not
    // delivered included — matching the campaign card's "N not delivered".
    _mergeSendTimeFailures(list) {
        const merged = Array.isArray(list) ? list.slice() : [];
        const digits = (p) => (p || '').replace(/\D/g, '');
        const norm = (e) => (e || '').toLowerCase().trim();
        // The ledger keeps the address a message was SENT to. A host who then
        // corrects it on the row (pencil) moves the row to the new address, so
        // also match the row's at-send addresses: otherwise the entry no longer
        // finds its row and is added again as a second, pencil-less "Failed"
        // row with no tracking id (chat 6582f145, a gnail.com typo fixed).
        const samePerson = (entry, phone, email) =>
            (phone && ((digits(entry.phone) && digits(entry.phone) === digits(phone))
                || (entry.sentToPhones || []).some(p => digits(p) && digits(p) === digits(phone)))) ||
            (email && ((norm(entry.email) && norm(entry.email) === norm(email))
                || (entry.sentToEmails || []).some(e => norm(e) === norm(email))));
        // The leg a ledger entry failed on. Every injected failure used to land
        // on whatsappStatus, so an email bounce read as a WhatsApp failure, and
        // Retry then judged an accepted WhatsApp leg as failed and sent it a
        // second time (review of audit 2026-10-07 #6). Webhook and SMS entries
        // name their channel; send-time WhatsApp and email entries carry only
        // the guest's method, so for 'both' the leg that got out is the one
        // that did not fail.
        const failedLeg = (f, c, row) => {
            const named = f.channel || c.method;
            if (named === 'email' || named === 'sms' || named === 'whatsapp') return named;
            const ws = (row && row.whatsappStatus) || {}, ss = (row && row.smsStatus) || {};
            const phoneLeg = (row && row.smsStatus && !row.whatsappStatus) ? 'sms' : 'whatsapp';
            if (named === 'mobile') return phoneLeg;
            const phoneOut = (ws.sent && !ws.failed) || (ss.sent && !ss.error && !ss.bounced);
            return (phoneOut && (c.email || (row && row.email))) ? 'email' : phoneLeg;
        };

        (this.campaigns || []).forEach(campaign => {
            const fails = [
                ...(campaign.permanentFailures || []),
                ...(campaign.retryQueue?.failedContacts || []),
                ...(campaign.failedRecipients || [])
            ];
            if (!fails.length) return;

            fails.forEach(f => {
                const c = f.contact || f;
                const phone = c.phone || null;
                const email = c.email || null;
                if (!phone && !email) return;

                const rawMsg = (typeof f.error === 'string' ? f.error
                    : (f.error?.error?.message || f.error?.message)) || f.descriptiveError || 'Message could not be sent';
                // Prefer the real Meta code embedded in the message (e.g. "(#132001)")
                // over YCloud's generic wrapper code ("NOT_FOUND"), so the badge label
                // and tooltip describe the actual failure (a template issue, not a bad number).
                const metaMatch = typeof rawMsg === 'string' ? rawMsg.match(/\(#(\d+)\)/) : null;
                const code = (metaMatch && metaMatch[1]) || f.errorCode || null;

                // Already represented for THIS campaign (a tracking doc exists)?
                // Flag it failed unless the message actually got through. The
                // guest's RSVP status deliberately does NOT suppress the flag
                // (task #154) — a responded guest with a hard-failed send shows
                // in their RSVP tab AND the Failed tab.
                const existing = merged.find(e =>
                    Array.isArray(e.campaignIds) && e.campaignIds.includes(campaign.id) && samePerson(e, phone, email));
                if (existing) {
                    const ws = existing.whatsappStatus || {};
                    const ss = existing.smsStatus || {};
                    const es = existing.emailStatus || {};
                    // Delivered on ANY channel counts. This checked WhatsApp
                    // only, so a US guest texted directly (no whatsappStatus at
                    // all) or one reached by email stayed Failed here while the
                    // card said 0 not delivered (audit 2026-10-07 #6).
                    const delivered = ws.delivered || ws.read || ss.delivered
                        || es.delivered || es.opened || es.clicked;
                    if (!delivered) {
                        const leg = failedLeg(f, c, existing);
                        if (leg === 'email') {
                            // Never sent is not a bounce (SEND_INTERRUPTED).
                            existing.emailStatus = DashboardCampaigns.WORDED_ERRORS[String(code)]
                                ? { ...es, errorCode: code, errorMessage: es.errorMessage || rawMsg }
                                : { ...es, bounced: true, errorMessage: es.errorMessage || rawMsg };
                        } else if (leg === 'sms') {
                            existing.smsStatus = { ...ss, error: true, errorCode: ss.errorCode || code, errorMessage: ss.errorMessage || rawMsg };
                        } else {
                            existing.whatsappStatus = { ...ws, failed: true, errorCode: ws.errorCode || code, errorMessage: ws.errorMessage || rawMsg };
                        }
                        existing.deliveryFailed = true;
                        existing.hasAnyFailure = true;
                        existing.errorCode = existing.errorCode || code;
                        // Which message this send-time failure belongs to. These
                        // rows never produced a tracking doc, so the server's
                        // per-campaign failedCampaignIds cannot see them and a
                        // single-message view would count them as fine (#584).
                        existing._sendTimeFailedCampaigns =
                            [...(existing._sendTimeFailedCampaigns || []), campaign.id];
                    }
                    return;
                }

                // Carry over response info if this person replied via another campaign,
                // so a guest who already RSVP'd isn't shown as a stark hard-failure.
                const elsewhere = merged.find(e => e.responded && samePerson(e, phone, email));
                const leg = failedLeg(f, c, null);

                merged.push({
                    name: c.name || c.recipientName || 'Guest',
                    email: email,
                    phone: phone,
                    trackingId: null,
                    trackingIds: [],
                    campaignIds: [campaign.id],
                    emailStatus: leg === 'email'
                        ? (DashboardCampaigns.WORDED_ERRORS[String(code)]
                            ? { sent: false, delivered: false, opened: false, bounced: false, errorCode: code, errorMessage: rawMsg }
                            : { sent: false, delivered: false, opened: false, bounced: true, errorMessage: rawMsg }) : null,
                    whatsappStatus: leg === 'whatsapp'
                        ? { sent: false, delivered: false, read: false, failed: true, errorCode: code, errorMessage: rawMsg, failedAt: f.failedAt || null } : null,
                    smsStatus: leg === 'sms'
                        ? { sent: false, delivered: false, error: true, errorCode: code, errorMessage: rawMsg } : null,
                    channel: leg,
                    fallbackFrom: null,
                    responded: !!elsewhere,
                    respondedVia: elsewhere ? elsewhere.respondedVia : null,
                    rsvpStatus: elsewhere ? elsewhere.rsvpStatus : 'pending',
                    attending: elsewhere ? elsewhere.attending : null,
                    responseId: null,
                    manuallyEntered: false,
                    isSMSFallback: false,
                    smsFallbackAttempted: false,
                    // A send-time failure is a hard failure by definition — flagged
                    // regardless of whether the guest responded via another campaign
                    // (task #154). Their carried-over rsvpStatus still places them in
                    // their RSVP tab, so they occupy both tabs.
                    deliveryFailed: true,
                    hasAnyFailure: true,
                    _sendTimeFailedCampaigns: [campaign.id],
                    errorCode: code,
                    lastUpdated: null
                });
            });
        });

        // Contacts the scrub deliberately skipped (known not on WhatsApp, or
        // inside Meta's throttle cooldown). They were never attempted, so they
        // have no tracking doc AND no failure record — meaning they vanished
        // from this list entirely. A host who re-sent to their failed guests
        // saw 5 of 7 simply disappear with no explanation (2026-08-02).
        //
        // Injected as un-delivered but NOT `deliveryFailed`: they show up under
        // "Not reached" with a reason, and every headline count (Failed chip,
        // card "N not delivered", donut) is left exactly as it was.
        //
        // The reason is the card's own wording (review of 2026-10-07: the card
        // said "WhatsApp" and a time, this list said "Meta" and "tomorrow", in
        // English on a Hebrew dashboard), with the guest's own lift time.
        const skipReasonFor = (reason, liftsAt, canEmail, channel, sameAs) => {
            if (reason === 'meta_throttle_cooldown') {
                return (liftsAt && liftsAt.getTime() > Date.now())
                    ? this.cTxt('skipRowCool', { time: this._skipTimeStr(liftsAt) })
                    : this.cTxt('skipRowCoolLifted');
            }
            // "or email" only when email is a real way to reach them. A
            // 'mobile' guest with a usable email is emailed instead of being
            // skipped (PMf), so most rows left here have none, or asked this
            // host to stop emailing them (emailUnsubscribed on the entry).
            if (reason === 'previously_not_on_whatsapp') return this.cTxt(canEmail ? 'skipRowNoWa' : 'skipRowNoWaNoEmail');
            if (reason === 'user_opted_out') return this.cTxt(channel === 'sms' ? 'skipRowOptOutSms' : 'skipRowOptOut');
            if (reason === 'email_unsubscribed') return this.cTxt('skipRowUnsub');
            // Could not be tried at all: what is wrong with their details.
            if (reason === 'no_contact_details') return this.cTxt('skipRowNoDetails');
            if (reason === 'invalid_phone') return this.cTxt('skipRowBadNumber');
            if (reason === 'no_email') return this.cTxt('skipRowNoEmail');
            if (reason === 'no_phone') return this.cTxt('skipRowNoPhone');
            if (reason === 'no_channel') return this.cTxt('skipRowNoChannel');
            if (reason === 'shared_number') return sameAs ? this.cTxt('skipRowShared', { name: sameAs }) : this.cTxt('skipRowSharedNoName');
            return this.cTxt('skipRowOther');
        };
        const toDate = (v) => {
            if (!v) return null;
            const d = v.toDate ? v.toDate() : new Date(v);
            return isNaN(d) ? null : d;
        };
        (this.campaigns || []).forEach(campaign => {
            const entries = campaign.suppressedRecipients || [];
            if (!entries.length) return;
            // A guest skipped on two passes has two entries; their LATER lift
            // is the real one (a fresh 131049 extends it). Entries from before
            // 2026-10-07 carry none: 24 hours after the send is the latest.
            const sentAt = toDate(campaign.processedAt || campaign.sentAt);
            const liftByGuest = new Map();
            entries.forEach(e => {
                if (!e || e.reason !== 'meta_throttle_cooldown') return;
                const k = DashboardCampaigns._skipGuestKeys(e.phone, e.email)[0];
                const u = toDate(e.until) || (sentAt ? new Date(sentAt.getTime() + 24 * 60 * 60 * 1000) : null);
                if (k && u && (!liftByGuest.has(k) || u > liftByGuest.get(k))) liftByGuest.set(k, u);
            });
            const nameOf = (v) => String(v || '').trim().toLowerCase();
            entries.forEach(s => {
                if (!s) return;
                const phone = s.phone || null;
                const email = s.email || null;
                const byName = s.reason === 'shared_number' || (!phone && !email);
                if (byName ? !nameOf(s.name) : (!phone && !email)) return;
                // A guest left out of a family number is not the housemate
                // whose message reached it, nor is a guest with no details
                // anyone else: those are found by name.
                const isThem = (e) => (byName ? nameOf(e.name) === nameOf(s.name) && (!phone || samePerson(e, phone, null)) : samePerson(e, phone, email));
                if (byName && merged.some(e => Array.isArray(e.campaignIds) && e.campaignIds.includes(campaign.id) && e.wasSkipped && isThem(e))) return;
                const onThis = merged.filter(e => Array.isArray(e.campaignIds)
                    && e.campaignIds.includes(campaign.id) && isThem(e));
                if (onThis.length) {
                    // Their row already exists. If all it holds for this
                    // message is a personal link, nothing was sent: they are
                    // still one of its "N skipped" (skipSummary ignores
                    // links), so the scoped Not reached view must keep them.
                    onThis.forEach(e => {
                        if ((e.manualOnlyCampaignIds || []).includes(campaign.id)
                            && !(e._skippedCampaigns || []).includes(campaign.id)) {
                            e._skippedCampaigns = [...(e._skippedCampaigns || []), campaign.id];
                        }
                    });
                    return;
                }

                const elsewhere = merged.find(e => e.responded && isThem(e));
                const isEmailSkip = s.reason === 'email_unsubscribed' || s.reason === 'no_email';
                const why = skipReasonFor(s.reason,
                    liftByGuest.get(DashboardCampaigns._skipGuestKeys(phone, email)[0]) || null,
                    !!email && s.emailUnsubscribed !== true, s.channel, s.sameAs || null);
                merged.push({
                    name: s.name || 'Guest',
                    email, phone,
                    trackingId: null,
                    trackingIds: [],
                    campaignIds: [campaign.id],
                    // An email opt-out is an EMAIL skip; a STOP to a text an
                    // SMS one (#1039); everything else the scrub records is a
                    // WhatsApp one.
                    emailStatus: isEmailSkip ? {
                        sent: false, delivered: false, failed: false, bounced: false,
                        errorCode: s.reason, errorMessage: why
                    } : null,
                    whatsappStatus: (isEmailSkip || s.channel === 'sms') ? null : {
                        sent: false, delivered: false, read: false, failed: false,
                        errorCode: s.reason || 'skipped',
                        errorMessage: why
                    },
                    smsStatus: s.channel === 'sms' ? {
                        sent: false, delivered: false, failed: false,
                        errorCode: s.reason, errorMessage: why
                    } : null,
                    channel: isEmailSkip ? 'email' : (s.channel === 'sms' ? 'sms' : 'whatsapp'),
                    fallbackFrom: null,
                    responded: !!elsewhere,
                    respondedVia: elsewhere ? elsewhere.respondedVia : null,
                    rsvpStatus: elsewhere ? elsewhere.rsvpStatus : 'pending',
                    attending: elsewhere ? elsewhere.attending : null,
                    responseId: null,
                    manuallyEntered: false,
                    isSMSFallback: false,
                    smsFallbackAttempted: false,
                    deliveryFailed: false,   // never attempted — keeps the Failed chip honest
                    wasSkipped: true,
                    hasAnyFailure: false,
                    errorCode: s.reason || 'skipped',
                    lastUpdated: null
                });
            });
        });
        return merged;
    }

    /**
     * "Already reminded by “Rosalina V2”: N" under a reminder that skipped
     * guests because the same reminder from another message on this event
     * reached them in the last 20 hours (server/utils/reminder-dedupe).
     * Without it the reminder simply shows fewer recipients than its audience,
     * unexplained. A <details>, not a title: the reason has to open on a tap,
     * and a tooltip never shows on a phone. Empty string when nothing was
     * skipped.
     */
    _reminderDupNote(campaign) {
        const n = Number(campaign && campaign.reminderDuplicatesSkipped) || 0;
        if (n <= 0) return '';
        const names = this._reminderDupSourceNames(campaign);
        // First-strong isolate around the name: an English message name inside
        // a Hebrew/Arabic line otherwise drags the quotes and ": N" out of place.
        const isolate = (t) => String.fromCharCode(0x2068) + t + String.fromCharCode(0x2069);
        const label = names.length
            ? this.cTxt('reminderDupSkippedBy', { name: isolate(names[0]), n }) + (names.length > 1 ? ` (+${names.length - 1})` : '')
            : this.cTxt('reminderDupSkipped', n);
        return `<details class="small text-muted lh-sm reminder-dup-note"><summary><i class="bi bi-person-dash" style="margin-inline-end:0.25rem;"></i>${this._escape(label)}<i class="bi bi-info-circle" style="margin-inline-start:0.3rem;opacity:0.7;" aria-hidden="true"></i></summary><div class="reminder-dup-why">${this._escape(this.cTxt('reminderDupSkippedTip'))}</div></details>`;
    }

    // The messages whose reminder reached those guests first, as the host
    // knows them: a reminder child by the message it hangs under (the six
    // Rosalina copies are told apart by their own names), a Remind All by its
    // own name. Sources not loaded here (deleted since) are left out.
    _reminderDupSourceNames(campaign) {
        const ids = Array.isArray(campaign && campaign.reminderDuplicateSources) ? campaign.reminderDuplicateSources : [];
        const all = this.campaigns || [];
        const names = [];
        for (const id of ids) {
            const src = all.find(c => c.id === id);
            if (!src) continue;
            const parent = src.parentCampaignId ? all.find(c => c.id === src.parentCampaignId) : null;
            const name = String((parent && parent.name) || src.name || '').trim();
            if (name && !names.includes(name)) names.push(name);
        }
        return names;
    }

    // Why a reminder was cancelled, in the host's language, from the code the
    // dispatcher writes as cancelledReason (scheduled-campaigns
    // resolveReminderCampaignContacts / followTheEvent). The Send now answer
    // carries the same code as cancelReason. No code = the host's own Cancel.
    _reminderCancelReasonText(code) {
        const key = {
            form_deleted: 'remCancelFormDeleted',
            rsvp_deadline_passed: 'remCancelDeadline',
            event_date_moved: 'remCancelDateMoved',
            parent_campaign_deleted: 'remCancelInvitation',
            parent_campaign_cancelled: 'remCancelInvitation',
            no_parent_campaign: 'remCancelInvitation',
            invitation_resumed_late: 'remCancelLate',
            event_cancelled: 'remCancelEventCancelled',
            postponed_too_late: 'remCancelPostponedLate'
        }[code];
        return this.cTxt(key || 'remCancelNotSent');
    }

    // What the host can do about it, on the row only. Editing re-arms these
    // (REMINDER_FIXABLE_CANCEL_REASONS), but nothing said so, and re-arming a
    // deadline cancel without moving the deadline only cancels it again.
    _reminderCancelHintText(code) {
        const key = { rsvp_deadline_passed: 'remCancelDeadlineHint', event_date_moved: 'remCancelDateMovedHint' }[code];
        return key ? this.cTxt(key) : '';
    }

    // Where a scheduled reminder timed from the event is headed once the host
    // has moved the event, until the hourly server pass
    // (followEventDatesForScheduledReminders) has moved it: the row used to
    // show the old day, before the new event, which reads as broken. Same
    // rule as server/utils/reminder-event-follow followEventDate: the same
    // number of whole days, keeping the hour. null when nothing moved.
    //   { at: Date }        it will send then
    //   { soon: true }      its new time has passed but the event has not
    //   { skipped: true }   its new time has passed for good
    //
    // A catch-up ("soon") that the server sends only one of (reminder-event-
    // follow supersededCatchUps, PMd re-review) reads as skipped: of one
    // invitation's reminders to one audience, only the latest catch-up goes,
    // and none while a sibling before the event day is still to come.
    _reminderEventMove(campaign, nowMs = Date.now()) {
        const OFFSET_DAYS = { day_of: 0, '1_day_before': 1, '3_days_before': 3, '1_week_before': 7, '2_weeks_before': 14, '1_day_after': -1, '3_days_after': -3 };
        const DAY = 24 * 60 * 60 * 1000;
        const dayMs = (v) => {
            const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '').trim());
            return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
        };
        const sendMsOf = (c) => {
            const raw = c.scheduledSendDate;
            return raw ? (raw.toDate?.() || new Date(raw)).getTime() : NaN;
        };
        const newDay = dayMs(this.formData?.eventDate);
        // This reminder's own move, ignoring its siblings. `atMs` rides along
        // for the comparison below.
        const own = (c) => {
            const cfg = c.reminderConfig || {};
            if (!(cfg.timing in OFFSET_DAYS) || c.isImmediate || !c.parentCampaignId) return null;
            const oldDay = dayMs(c.eventDate);
            if (isNaN(oldDay) || isNaN(newDay) || oldDay === newDay) return null;
            const sendMs = sendMsOf(c);
            if (isNaN(sendMs)) return null;
            const atMs = sendMs + (newDay - oldDay);
            if (atMs > nowMs) return { at: new Date(atMs), atMs };
            const offsetMs = OFFSET_DAYS[cfg.timing] * DAY;
            const worth = offsetMs > 0 ? nowMs < atMs + offsetMs : nowMs - atMs < DAY;
            return worth ? { soon: true, atMs } : { skipped: true };
        };
        const mine = own(campaign);
        if (!mine) return null;
        const offsetOf = (c) => OFFSET_DAYS[(c.reminderConfig || {}).timing];
        if (mine.soon && offsetOf(campaign) >= 0) {
            const audience = (c) => (c.reminderConfig || {}).recipients || 'no_response';
            const siblings = (this.campaigns || []).filter(c => c && c !== campaign && c.id !== campaign.id
                && c.isReminderCampaign && c.parentCampaignId === campaign.parentCampaignId
                && (c.formId || '') === (campaign.formId || '') && audience(c) === audience(campaign)
                && (c.status || 'scheduled') === 'scheduled' && !c.isImmediate && offsetOf(c) !== undefined);
            const superseded = siblings.some((c) => {
                const m = own(c);
                if (offsetOf(c) > 0 && ((m && m.at) || (!m && sendMsOf(c) > nowMs))) return true; // still to come before the event
                return offsetOf(c) >= 0 && !!(m && m.soon) && m.atMs > mine.atMs; // a later catch-up goes instead
            });
            if (superseded) return { skipped: true };
        }
        return mine.at ? { at: mine.at } : (mine.soon ? { soon: true } : mine);
    }

    _renderReminderChildItem(campaign) {
        const status = campaign.status || 'scheduled';
        const cfg = campaign.reminderConfig || {};
        const isPaused = status === 'paused';
        const isInsufficientCredits = status === 'paused_insufficient_credits';
        // The dispatcher sends a reminder's email/SMS guests while its WhatsApp
        // template is still in review, which sets sentCount; only the WhatsApp
        // guests are still to come, so it is sending, not sent (the parent card
        // has the same rule in preSendStatuses).
        const waOnlyLeft = status === 'emails_sent_whatsapp_pending';
        const isSent = !waOnlyLeft && (['sent', 'partially_sent', 'completed', 'processed'].includes(status) || campaign.sentCount > 0);
        // Its email and text guests have it, but WhatsApp refused the message or
        // never approved it (the server settles that to partially_sent with a
        // templateCreationError). A plain "Sent" with no Edit left the host no
        // way to reach the WhatsApp guests; Edit's save resubmits to WhatsApp.
        // The rule lives in reminderWaHalfLost, shared with the bell's red dot.
        const waHalfLost = DashboardCampaigns.reminderWaHalfLost(campaign);
        // Due-but-not-finished states — so the host isn't left staring at a stale
        // "Scheduled" after the send time has passed. pending_template = waiting on
        // Meta to approve THIS reminder's WhatsApp template before it can send.
        const isSending = ['pending', 'pending_template', 'emails_sent_whatsapp_pending', 'processing'].includes(status);
        const isFailedState = ['failed', 'template_rejected', 'template_timeout'].includes(status);
        // Cancelled, by the host or by the dispatcher (its event was deleted,
        // RSVPs closed, the event moved earlier past its moment). This had no
        // branch, so a cancelled reminder fell through to "Scheduled · Sends …"
        // and kept a Pause button for a send that was never coming.
        const isCancelled = status === 'cancelled';
        // Every label on this row reads in the dashboard's language (code
        // review 2026-10-07: they were English on all 7 dashboards).
        const TIMING = { day_of: 'timingDayOf', '1_day_before': 'timing1DayBefore', '3_days_before': 'timing3DaysBefore', '1_week_before': 'timing1WeekBefore', '2_weeks_before': 'timing2WeeksBefore' };
        const audience = this._reminderAudienceLabel(cfg) || (cfg.recipients || '');
        // Relative timings add useful context beside the date ("1 week before");
        // for a custom date the absolute date already says it, so no descriptor.
        const relativeLabel = (cfg.timing && cfg.timing !== 'custom' && TIMING[cfg.timing]) ? this.cTxt(TIMING[cfg.timing]) : '';

        const sendDate = campaign.scheduledSendDate ? (campaign.scheduledSendDate.toDate?.() || new Date(campaign.scheduledSendDate)) : null;
        const sendStr = sendDate && !isNaN(sendDate) ? sendDate.toLocaleString(this._dateLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

        // A "Send now" reminder is written as status:'scheduled' + isImmediate:true
        // (the doc keeps the scheduled shape so the cron stays a fallback if the
        // immediate hand-off hiccups) and only flips to a sending status once the
        // dispatch has resolved the audience and created the WhatsApp template —
        // which can take a couple of minutes. In that window the generic branch
        // below labelled it "Scheduled · Sends <the moment you clicked>", i.e. a
        // time already in the past, so the host had no sign anything was
        // happening (task #546 round 2). Show the same Processing badge an
        // immediate CAMPAIGN shows instead. Display-only — the doc is untouched.
        // A FUTURE scheduledSendDate wins over the flag, mirroring the parent-row
        // rule below: legacy docs can carry a stale isImmediate:true.
        const sendDateIsFuture = sendDate && !isNaN(sendDate) && sendDate.getTime() > Date.now();
        const isImmediatePending = campaign.isImmediate === true && !sendDateIsFuture
            && !isSent && !isPaused && !isInsufficientCredits && !isFailedState && !isSending;

        // Re-armed by an edit and not yet moved on (task #592). Display only —
        // the stamp is left on the doc and simply stops mattering the moment the
        // reminder starts sending or lands in a terminal state.
        const isResubmitting = !!campaign.resubmittedAt
            && !isSent && !isSending && !isFailedState && !isPaused && !isInsufficientCredits
            && !campaign.noRecipients;

        // The host moved the event since this reminder was written (display
        // only; the server moves the doc within the hour).
        const eventMove = (status === 'scheduled' && !isSent) ? this._reminderEventMove(campaign) : null;

        // Line 1 = status chip; line 2 = a "when / outcome" phrase + audience.
        // skipNote: the "Not sent" note under a reminder that skipped everyone.
        // failNote: why a failed reminder failed, as text under the row; it was
        // only a hover title on the chip, which a phone never shows (review #15).
        let chip, whenPhrase, skipNote = '', failNote = '';
        const providerPauseChip = isPaused ? this._providerPauseBadgeHtml(campaign) : null;
        if (providerPauseChip) {
            chip = providerPauseChip;
            // The phrase and its why are the tappable note below the chip line.
            whenPhrase = '';
        } else if (isPaused) {
            chip = `<span class="badge bg-secondary"><i class="bi bi-pause-circle me-1"></i>${this.cTxt('badgePaused')}</span>`;
            whenPhrase = sendStr ? this.cTxt('remWasSetFor', { when: sendStr }) : this.cTxt('badgePaused');
        } else if (isInsufficientCredits) {
            // Reminder paused because the account ran out of credits — show the
            // real reason (not a generic "Couldn't send") with a tooltip + the
            // Buy Credits / Resume actions below, so the host can fix it inline.
            chip = `<span class="badge bg-warning text-dark" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.cAttr('remNoCreditsTip')}"><i class="bi bi-wallet2 me-1"></i>${this.cTxt('badgeNoCredits')}</span>`;
            whenPhrase = this.cTxt('remAddCredits');
        } else if (isCancelled) {
            chip = `<span class="badge bg-secondary"><i class="bi bi-x-circle me-1"></i>${this._escape(this.cTxt('remCancelled'))}</span>`;
            const hint = this._reminderCancelHintText(campaign.cancelledReason);
            whenPhrase = this._escape(this._reminderCancelReasonText(campaign.cancelledReason)) + (hint ? ` ${this._escape(hint)}` : '');
        } else if (campaign.noRecipients) {
            chip = `<span class="badge bg-light text-muted border">${this.cTxt('remNoRecipients')}</span>`;
            // Everyone on it had already had the same reminder from another
            // message on this event (server/utils/reminder-dedupe). "No matching
            // recipients" would read as a mistake in the audience. The note
            // below names that message and opens the reason.
            whenPhrase = campaign.reminderDuplicatesSkipped > 0 ? this._escape(this.cTxt('reminderDupAll')) : this.cTxt('remNoMatching');
        } else if ((campaign.status || 'scheduled') === 'scheduled' && !campaign.isImmediate && this.formData?.eventStatus === 'postponed'
            && ['day_of', '1_day_before', '3_days_before', '1_week_before', '2_weeks_before', '1_day_after', '3_days_after'].includes(campaign.reminderConfig?.timing)) {
            // Timed from an event that has no date (task #955): the server holds
            // it (scheduled-campaigns followTheEvent) and moves it with the new
            // date. Its old time would read as overdue.
            chip = `<span class="badge bg-warning text-dark"><i class="bi bi-hourglass-split me-1"></i>${this._escape(this.cTxt('remOnHold'))}</span>`;
            whenPhrase = this._escape(this.cTxt('remHeldPostponed'));
        } else if (isResubmitting) {
            // Editing a reminder that had died re-arms it (task #592). Until it
            // reaches a sending or sent state, say so — dropping straight back to
            // "Scheduled" made saving the fix look like it had done nothing.
            chip = `<span class="badge bg-info text-dark" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.cAttr('remResubmitTip')}"><i class="bi bi-arrow-repeat me-1"></i>${this.cTxt('remResubmitting')}</span>`;
            whenPhrase = sendStr ? this.cTxt('remSendsOnceApproved', { when: sendStr }) : this.cTxt('remWaitingApproval');
        } else if (waHalfLost) {
            const lostTip = this._escape(this.cTxt(campaign.whatsappTemplate?.status === 'timeout' ? 'remWaLostTimeoutTip' : 'remWaLostTip')).replace(/"/g, '&quot;');
            chip = `<span class="badge bg-danger" data-bs-toggle="tooltip" data-bs-placement="top" title="${lostTip}"><i class="bi bi-exclamation-triangle me-1"></i>${this._escape(this.cTxt('remWaLost'))}</span>`;
            whenPhrase = this._escape(this.cTxt('remWaLostPhrase'));
        } else if (isSent) {
            const sent = campaign.trackingStats?.sent || campaign.sentCount || 0;
            const responded = campaign.trackingStats?.responded ?? 0;
            const notDelivered = this._failedDeliveryCount(campaign);
            // Actual send date (mirrors the primary campaign showing its date) —
            // sentAt/processedAt, falling back to the scheduled time.
            const sentOnRaw = campaign.sentAt || campaign.processedAt || campaign.scheduledSendDate;
            const sentOn = sentOnRaw ? (sentOnRaw.toDate?.() || new Date(sentOnRaw)) : null;
            const sentOnStr = sentOn && !isNaN(sentOn)
                ? sentOn.toLocaleString(this._dateLocale(), { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                : null;
            chip = `<span class="badge bg-light text-secondary border"><i class="bi bi-check2 me-1"></i>${this.cTxt('badgeSent')}</span>`;
            // Every guest skipped (Meta's 24h cooldown, not on WhatsApp): not a
            // send, whatever the status says — same rule as the parent card,
            // and the same note under the row. Reminders are the sends most
            // likely to meet 131049 (a second marketing template to the same
            // guest within a day), and a hover tooltip on the chip was the only
            // place that said why, which a phone never shows.
            const skip = this._skipSummary(campaign);
            if (skip.allSkipped) {
                chip = this._skipBadgeHtml(skip);
                skipNote = this._skipNoteHtml(campaign, skip, true);
            }
            // Reminder stats use the SAME fixed-column grid as the main campaign
            // row — same order (Date, Responded, Pending, Sent, Not delivered),
            // no dot separators, each metric locked to its own column so they line
            // up vertically across reminders. "Not delivered" always shows (0 if
            // none) in a neutral grey (tasks #75 / #76).
            // "Pending" means somebody still owes us an RSVP. On a reminder
            // aimed at guests who have ALREADY replied — attending, maybe or
            // declined — nobody does: having replied is the entry ticket to that
            // audience. The generic sent-minus-responded arithmetic counted them
            // as pending anyway, because the reply predates this reminder's own
            // tracking record and so isn't attributed to it, and a reminder to
            // one attending guest reported "0 responded · 1 pending" (task #574).
            // For those audiences the honest numbers are: they have all replied,
            // and none is outstanding.
            const audienceHasReplied = ['attending', 'maybe', 'declined'].includes(cfg.recipients);
            const reachedCount = Math.max(0, sent - notDelivered);
            const remResponded = audienceHasReplied ? Math.max(responded, reachedCount) : responded;
            const remPending = audienceHasReplied ? 0 : Math.max(0, sent - responded - notDelivered);
            const respTitle = audienceHasReplied
                ? ` title="${this.cAttr('remAllRepliedTip')}"`
                : '';
            whenPhrase = skip.allSkipped ? `
                <span class="campaign-stats-grid reminder-stats-grid d-inline-flex align-items-center flex-wrap">
                    ${sentOnStr ? `<span class="cs-stat cs-stat-date">${sentOnStr}</span>` : ''}
                    ${this._notDeliveredStatHtml(campaign, 0, skip)}
                </span>` : `
                <span class="campaign-stats-grid reminder-stats-grid d-inline-flex align-items-center flex-wrap">
                    ${sentOnStr ? `<span class="cs-stat cs-stat-date">${sentOnStr}</span>` : ''}
                    <span class="cs-stat cs-stat-resp text-success"${respTitle}><i class="bi bi-check-circle-fill me-1"></i>${this.cCount('statResponded', remResponded)}</span>
                    <span class="cs-stat cs-stat-pend text-warning"><i class="bi bi-hourglass-split me-1"></i>${this.cCount('statPending', remPending)}</span>
                    <span class="cs-stat cs-stat-sent text-muted">${this.cCount('statSent', sent)}</span>
                    ${this._notDeliveredStatHtml(campaign, notDelivered, skip)}
                </span>`;
        } else if (isFailedState) {
            const expired = status === 'template_timeout';
            // template_rejected happens BEFORE any send attempt (Meta refused the
            // message content) — "Couldn't send · Send failed" reads like a send
            // was tried and confuses hosts whose reminder is still future-dated.
            const rejected = status === 'template_rejected';
            // A reason written by the dispatcher wins over the generic line — it
            // says WHICH thing went wrong, which is the difference between "try
            // again" and knowing to take the table-number tag off the end of the
            // message (task #590).
            // A plain failure says why ON the row (a tooltip is all it had,
            // and a phone never shows one), in the host's language. The row
            // has no template banner, so WhatsApp's reason is always on it,
            // and its advice names the edit pencil (there is no Edit & Resend).
            // The server's English failureReason (it can carry a raw provider
            // error) is the chip's hover title only (review of M3, 2026-10-07).
            const failedWhy = (!expired && !rejected) ? this._failureReasonText(campaign, { reminder: true }) : '';
            const failTitle = campaign.failureReason ? DashboardCampaigns.escapeAttr(String(campaign.failureReason))
                : this.cAttr(expired ? 'remExpiredTip' : rejected ? 'remRejectedTip' : 'remFailedTip');
            const failLabel = this._escape(this.cTxt(expired ? 'badgeExpired' : rejected ? 'remRejected' : 'remCouldntSend'));
            chip = `<span class="badge bg-danger" title="${failTitle}"><i class="bi bi-exclamation-triangle me-1"></i>${failLabel}</span>`;
            // The reason sits under the row in the host's language (M3's note
            // block, M2's wording: _failureReasonText names the code and adds
            // WhatsApp's own reason); the short label stays on the detail line.
            const failText = this._escape(failedWhy || this.cTxt(expired ? 'remFailExpiredNote' : 'remFailRejectedNote'));
            failNote = `<div class="small text-danger mt-1 d-flex align-items-start reminder-fail-note" style="gap:0.35rem;"><i class="bi bi-exclamation-circle flex-shrink-0" style="margin-top:0.1rem;"></i><span>${failText}</span></div>`;
            whenPhrase = this._escape(this.cTxt(expired ? 'remTimedOut' : rejected ? 'remEditToResubmit' : 'remSendFailed'));
        } else if (isSending) {
            // Due (or past due) but still finishing. Mention WhatsApp approval ONLY
            // when this campaign actually has a WhatsApp leg — an email-only send
            // was wrongly telling hosts it "sends once WhatsApp approves" (task
            // #253 round 2).
            const involvesWhatsApp = !!(campaign.whatsappTemplate
                || String(status).includes('template')
                || status === 'emails_sent_whatsapp_pending'
                || (campaign.selectedChannels || []).some(ch => ch === 'whatsapp' || ch === 'mobile')
                || (campaign.contacts || []).some(c => ['whatsapp', 'mobile', 'both'].includes(c.method)));
            // Email and text guests already have it in this state (waOnlyLeft);
            // only the WhatsApp guests wait (the first leg sends US texts too).
            const waitingTip = waOnlyLeft
                ? this.cTxt('remWaOnlyWaitingTip').replace(/"/g, '&quot;')
                : this.cAttr('remWaitingTip');
            chip = involvesWhatsApp
                ? `<span class="badge bg-info text-dark" data-bs-toggle="tooltip" data-bs-placement="top" title="${waitingTip}"><i class="bi bi-hourglass-split me-1"></i>${this.cTxt('remSendingSoon')}</span>`
                : `<span class="badge bg-info text-dark" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.cAttr('remBeingSentTip')}"><i class="bi bi-hourglass-split me-1"></i>${this.cTxt('badgeSending')}</span>`;
            // Only show a "Due <time>" when that time is genuinely in the FUTURE.
            // For "Send Immediately" campaigns the scheduled time is now (rounded to
            // the hour), so "Due 3:00 PM" at 3:30 PM read as a stale past time while
            // it was really just waiting on WhatsApp approval (task #253).
            const dueInFuture = sendDate && !isNaN(sendDate) && sendDate.getTime() > Date.now();
            if (waOnlyLeft) {
                whenPhrase = this._escape(this.cTxt('remWaOnlyWaiting'));
            } else if (involvesWhatsApp) {
                whenPhrase = dueInFuture
                    ? this.cTxt('remDueOnceApproved', { when: sendStr })
                    : this.cTxt('remSendsWhenApproved');
            } else {
                whenPhrase = dueInFuture ? this.cTxt('remDue', { when: sendStr }) : this.cTxt('remSendingNow');
            }
        } else if (isImmediatePending) {
            chip = `<span class="badge bg-info" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.cAttr('remProcessingTip')}"><i class="bi bi-hourglass-split me-1"></i>${this.cTxt('badgeProcessing')} <i class="bi bi-info-circle ms-1" style="opacity:0.7;"></i></span>`;
            whenPhrase = this.cTxt('remSendingMinute');
        } else {
            // Moved to the guests' morning because its preset time fell in their
            // night (server/utils/quiet-hours.js systemTimedHoldFor). Without a
            // word, "1 day before" now sending at 4 PM reads as a mistake.
            const heldUntil = campaign.quietHoursHold?.reason === 'system_timed' && campaign.quietHoursHold.until
                ? (campaign.quietHoursHold.until.toDate?.() || new Date(campaign.quietHoursHold.until)) : null;
            const quietHeld = heldUntil && sendDateIsFuture && heldUntil.getTime() === sendDate.getTime();
            chip = quietHeld
                ? `<span class="badge bg-light text-info border" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.cAttr('quietHoldSystemTip')}"><i class="bi bi-moon-stars me-1"></i>${this.cTxt('badgeScheduled')} <i class="bi bi-info-circle ms-1" style="opacity:0.7;"></i></span>`
                : `<span class="badge bg-light text-info border"><i class="bi bi-clock me-1"></i>${this.cTxt('badgeScheduled')}</span>`;
            whenPhrase = sendStr ? this.cTxt('remSends', { when: sendStr }) : this.cTxt('badgeScheduled');
            const movedNote = ` · ${this._escape(this.cTxt('remMovedWithEvent'))}`;
            if (eventMove && eventMove.at) {
                // Same words and date format as the line above (M1's remSends,
                // the dashboard's locale), at the moved time.
                const atStr = eventMove.at.toLocaleString(this._dateLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                whenPhrase = this.cTxt('remSends', { when: atStr }) + movedNote;
            } else if (eventMove && eventMove.soon) {
                whenPhrase = this._escape(this.cTxt('remSendsSoon')) + movedNote;
            } else if (eventMove && eventMove.skipped) {
                // Said while the host can still act: edit it to a new time.
                whenPhrase = `<span class="text-danger">${this._escape(this.cTxt('remDateMovedPast'))}</span>`;
            } else if (campaign.rescheduledForEventDateAt && cfg.timing !== 'custom') {
                // The server moved it when the host moved the event
                // (scheduled-campaigns followTheEvent / the hourly pass) — say
                // so, or the new send time looks like it changed by itself.
                whenPhrase += movedNote;
            }
        }
        if (relativeLabel && !isSent && !campaign.noRecipients && !isSending && !isImmediatePending && !isFailedState && !isInsufficientCredits && !isResubmitting && !isCancelled && !(eventMove && eventMove.skipped)) whenPhrase += ` · ${relativeLabel}`;

        // Audience now lives in the reminder TITLE ("Reminder · Maybe guests"),
        // so the subtext shows only the timing/outcome phrase — no duplicate
        // "· Maybe guests" line beneath the title.
        const detailLine = whenPhrase;
        const dupNote = this._reminderDupNote(campaign) + this._providerPauseNoteHtml(campaign) + this._outageHeldNoteHtml(campaign);

        // `tip` is a CAMPAIGN_I18N key.
        const btn = (action, icon, tip, cls = 'btn-outline-secondary') =>
            `<button class="btn ${cls} btn-sm px-2 py-1" data-action="${action}" data-campaign-id="${campaign.id}" title="${this.cAttr(tip)}"><i class="bi ${icon}"></i></button>`;
        let actions = btn('preview-campaign', 'bi-eye', 'remPreviewTip');
        if (!isSent || waHalfLost) actions += btn('edit-reminder', 'bi-pencil', 'remEditTip');
        // Status (the delivery grid, scoped to this reminder) once it has sent.
        // Before that the grid is empty, and a scheduled reminder only ever
        // said "No contacts found", so the same slot previews who it WOULD go
        // to if it sent now (audit 2026-10-07, review #16). 'processing' has
        // started writing deliveries, so it keeps Status.
        const previewsAudience = !isSent && !waOnlyLeft && !waHalfLost && status !== 'processing';
        const audienceTip = this.cAttr('remAudienceBtn');
        actions += previewsAudience
            ? `<button class="btn btn-outline-secondary btn-sm px-2 py-1" data-action="preview-reminder-audience" data-campaign-id="${campaign.id}" title="${audienceTip}" aria-label="${audienceTip}"><i class="bi bi-people"></i></button>`
            : btn('view-status', 'bi-bar-chart-line', 'remStatusTip');
        if (isInsufficientCredits) {
            actions += btn('buy-credits-from-card', 'bi-wallet2', 'remBuyCreditsTip', 'btn-primary');
            actions += btn('resume-campaign', 'bi-play-fill', 'remResumeCreditsTip', 'btn-outline-secondary');
        } else if (isPaused && this._providerPauseState(campaign) === 'resuming') {
            // Restarting already: a second Resume would do nothing.
        } else if (isPaused) actions += btn('resume-campaign', 'bi-play-fill', 'remResumeTip', 'btn-primary');
        else if (!isSent && !isFailedState && !waHalfLost && !isCancelled) actions += btn('pause-campaign', 'bi-pause-fill', 'remPauseTip', 'btn-outline-warning');
        actions += btn('delete-campaign', 'bi-trash', 'remDeleteTip', 'btn-outline-danger');

        const collapsed = !this._isReminderGroupExpanded(campaign.parentCampaignId);
        // Name each reminder by its AUDIENCE type ("Reminder · Maybe guests")
        // instead of a generic number — reminders are already nested under their
        // parent campaign, so the audience is the distinguishing detail. Falls
        // back to the index only when no audience is set yet.
        const reminderTitle = audience
            ? this.cTxt('remTitleAudience', { audience: this._escape(audience) })
            : this.cTxt('remTitleIndex', { n: campaign.reminderIndex || '' });
        // Logical margin/border: the indent and gold rail belong on the
        // reading-start side, which is the right in Hebrew and Arabic.
        return `
            <div class="campaign-item reminder-child-row border-bottom px-3 py-2${collapsed ? ' d-none' : ''}" data-campaign-id="${campaign.id}" data-reminder-parent="${campaign.parentCampaignId}" style="margin-inline-start:1.5rem; border-inline-start:3px solid #CBAA6A; background:#FAF9F8;">
                <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div class="d-flex flex-column" style="min-width:0; gap:4px;">
                        <!-- Icon sits beside the left-aligned title. Inline gap, NOT
                             the .gap-2 class: the global card rule turns
                             .d-flex.gap-2.flex-wrap into a 2-col grid on mobile,
                             which shoved the title into the second column (task #177). -->
                        <div class="d-flex align-items-center" style="gap:0.5rem;">
                            <i class="bi bi-bell-fill text-secondary"></i>
                            <span class="fw-semibold text-dark">${reminderTitle}</span>
                        </div>
                        <!-- Status chip + schedule share the muted line under the title. -->
                        <div class="small text-muted lh-sm d-flex align-items-center flex-wrap" style="gap:0.4rem;">
                            ${chip}
                            <span>${detailLine}</span>
                        </div>
                        ${dupNote}
                    </div>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0 reminder-child-actions">
                        ${actions}
                    </div>
                </div>
                ${skipNote}${failNote}
            </div>`;
    }

    renderCampaignItem(campaign) {
        // Linked reminder children (nested under a parent) render as a compact,
        // collapsible sub-row. A standalone reminder (e.g. "Remind All", no parent)
        // renders as a normal top-level campaign row instead of an orphaned child.
        if (campaign.isReminderCampaign && campaign.parentCampaignId) return this._renderReminderChildItem(campaign);

        const createdAt = campaign.createdAt?.toDate?.() || new Date(campaign.createdAt);
        const dateStr = createdAt.toLocaleDateString(this._dateLocale(), {
            month: 'short', day: 'numeric', year: 'numeric'
        });

        // Determine campaign status
        const status = campaign.status || 'scheduled';
        const isDraft = status === 'draft';
        // Tests REPEAT (task #424, round 2). The one-per-campaign lock greyed
        // the button out after a single use, which blocked the one thing a test
        // is for — change the wording and check it again. What limits tests is
        // their cost, and the paid channels already charge per send.
        const alreadyTested = !!(campaign.testSend && campaign.testSend.sentAt);
        // The server 400s a test on a campaign with no message on any channel.
        // Mirrors campaigns.controller sendTest so the button never offers a
        // click that can only fail — hosts were pressing it on empty drafts,
        // getting the error, and coming back an hour later to try again.
        // The server also refuses an empty customMessage (task #577), whatever
        // channels are picked; this check came first and never learned that,
        // so empty drafts with a channel still offered the click (4x on 09-30).
        const hasTestableMessage = (() => {
            if (!String(campaign.customMessage || '').trim()) return false;
            const contacts = Array.isArray(campaign.contacts) ? campaign.contacts : [];
            const sel = Array.isArray(campaign.selectedChannels) ? campaign.selectedChannels : [];
            return sel.includes('email') || !!campaign.emailSubject || !!campaign.emailMessage
                || contacts.some(c => c.method === 'email' || c.method === 'both')
                || sel.includes('whatsapp') || !!(campaign.whatsappTemplate && campaign.whatsappTemplate.name)
                || contacts.some(c => c.method === 'whatsapp' || c.method === 'mobile' || c.method === 'both')
                || sel.includes('sms') || sel.includes('mobile');
        })();
        // 'isProcessed' means the campaign has finished sending — show Status/Remind/Add buttons.
        // Never treat pre-send statuses as processed even if sentCount > 0 (e.g. emails sent, WhatsApp still pending).
        const preSendStatuses = ['scheduled', 'pending', 'pending_template', 'emails_sent_whatsapp_pending', 'processing', 'paused', 'paused_insufficient_credits', 'draft', 'cancelled'];
        // 'manual_links' = personal links the host shared by hand: nothing was
        // sent by us, but replies land on it like any other message.
        const isProcessed = !preSendStatuses.includes(status) && (status === 'completed' || status === 'processed' || status === 'sent' || status === 'partially_sent' || status === 'manual_links' || campaign.sentCount > 0);
        // Task #834: a hand-shared card has no message of ours to preview or
        // copy - the host sent each link themselves - so it shows neither.
        const isHandShared = status === 'manual_links';

        // Date line beneath the title: for a not-yet-sent scheduled campaign, show
        // the planned DELIVERY date+time ("Scheduled for …") instead of just the
        // creation date, so the host can see WHEN it will actually send. Falls back
        // to the creation date when there's no scheduled time (immediate/draft/sent).
        let dateLineHtml = dateStr;
        {
            const schedRaw = campaign.scheduledSendDate
                ? (campaign.scheduledSendDate.toDate?.() || new Date(campaign.scheduledSendDate)) : null;
            const isPreSend = ['scheduled', 'pending', 'pending_template', 'emails_sent_whatsapp_pending'].includes(status);
            // A future send date wins over the isImmediate flag: standalone
            // remind-alls created before the #133 fix carry isImmediate:true
            // even though they're scheduled, and their rows showed only the
            // creation date with no dispatch time (task #155).
            const futureSched = schedRaw && !isNaN(schedRaw) && schedRaw.getTime() > Date.now() + 60 * 1000;
            if (isPreSend && schedRaw && !isNaN(schedRaw) && (!campaign.isImmediate || futureSched)) {
                const schedStr = schedRaw.toLocaleString(this._dateLocale(), { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                dateLineHtml = `<i class="bi bi-clock me-1"></i>${this.cTxt('scheduledFor', { when: schedStr })}`;
            }
        }

        const stats = campaign.trackingStats || { sent: 0, seen: 0, responded: 0, failed: 0 };
        // Prefer live tracking count over the stored campaign.sentCount so the card
        // and the Status modal (both fed from /api/message-tracking) show the same
        // numbers. Fall back to sentCount only if tracking data isn't available yet.
        const actualSent = stats.sent || campaign.sentCount || 0;
        // "Not delivered" — includes send-time failures invisible to trackingStats
        // (see _failedDeliveryCount), so a silent batch failure surfaces here too.
        const notDelivered = this._failedDeliveryCount(campaign);
        // Guests held back on purpose (Meta's 24h cooldown, not on WhatsApp…),
        // and whether that was everyone — see _skipSummary.
        const skip = isProcessed ? this._skipSummary(campaign) : null;
        // Exclude undelivered contacts from "pending" — they can't respond on this channel,
        // so lumping them in misleads the host into thinking those guests still might reply.
        //
        // And a standalone reminder aimed at guests who have ALREADY replied has
        // nobody outstanding by definition — same rule as the nested reminder
        // rows below, kept here so the two never disagree about the same send
        // (task #574).
        const parentAudienceHasReplied =
            ['attending', 'maybe', 'declined'].includes(campaign.reminderConfig?.recipients);
        const respondedCount = parentAudienceHasReplied
            ? Math.max(stats.responded || 0, Math.max(0, actualSent - notDelivered))
            : (stats.responded || 0);
        const pending = parentAudienceHasReplied
            ? 0
            : Math.max(0, actualSent - stats.responded - notDelivered);

        const standalone = this._standaloneReminderTitle(campaign);
        const nameHtml = standalone.autoName
            ? this.cTxt('remTitleAudience', { audience: this._escape(standalone.audience) })
            : this._escape(campaign.name || this.cTxt('untitledMessage'));
        const channelIcon = campaign.channels?.includes('whatsapp') || campaign.channel === 'whatsapp'
            ? '<i class="bi bi-whatsapp text-success"></i>'
            : '<i class="bi bi-envelope text-primary"></i>';

        // Status badge based on campaign status. Only pre-send states badge here;
        // a finished send (sent / partially_sent) shows none — see the note after
        // the switch below.

        // Detect upcoming (unprocessed) reminders
        const hasUpcomingReminders = (
            (campaign.reminderSettings?.enabled || campaign.hasReminders) &&
            !campaign.remindersPaused &&
            !campaign.allRemindersCompleted &&
            (
                (Array.isArray(campaign.reminders) && campaign.reminders.length > 0 &&
                    (campaign.processedReminderIds?.length || 0) < campaign.reminders.length) ||
                (Array.isArray(campaign.reminderSettings?.timing) && campaign.reminderSettings.timing.length > 0 &&
                    (campaign.processedReminders?.length || 0) < campaign.reminderSettings.timing.length)
            )
        );
        // Count linked (split) reminders from the children that ACTUALLY still
        // exist in the loaded campaign list — NOT from campaign.childReminderCampaignIds,
        // which is a pointer array that is NOT pruned when a reminder is deleted.
        // Reading the stale array left "ghost" UI: a "View Reminders (N)" pill
        // lingering on campaigns whose reminders were all deleted. The live
        // count self-heals even for already-polluted parent docs.
        const splitReminderCount = (this.campaigns || []).filter(
            c => c.isReminderCampaign && c.parentCampaignId === campaign.id
        ).length;
        // NOTE (task #301): the campaign row deliberately shows NO "Reminders
        // paused" note. `campaign.remindersPaused` is a LEGACY-path flag (it
        // only gates the inline reminders[] cron in reminders.service) —
        // split-reminder children are separate scheduled campaigns that pause
        // via their own status, so surfacing the stale parent flag next to
        // them claimed "paused" while a child was scheduled and WOULD send.
        // Granular Scheduled/Paused state lives on each reminder row; the
        // bell pill count is the only campaign-level reminder signal.

        // Auto-retry queue: only relevant for non-US WhatsApp marketing-limit failures.
        // The cron retries those at 24/72/168 hour intervals (max 3 attempts) — users
        // can pause it if they don't want further attempts.
        const retryQueue = campaign.retryQueue;
        const pendingRetries = retryQueue?.failedContacts?.length || 0;
        const hasRetryQueue = !!(retryQueue?.enabled && pendingRetries > 0);
        const autoRetryPaused = !!campaign.autoRetryPaused;

        // Shared Remind audience menu — used by the Remind dropdown and by the
        // mobile "Reminders (0)" zero-state button (task #197), so both always
        // offer the identical options.
        const remindMenuItemsHtml = `
                                <li>
                                    <button class="dropdown-item" data-action="remind-pending" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-hourglass-split me-2 text-warning"></i>${this.cTxt('remindPending')}
                                        <small class="d-block text-muted ms-4">${this.cTxt('remindPendingSub')}</small>
                                    </button>
                                </li>
                                <li>
                                    <button class="dropdown-item" data-action="remind-maybe" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-question-circle me-2 text-warning"></i>${this.cTxt('remindMaybe')}
                                        <small class="d-block text-muted ms-4">${this.cTxt('remindMaybeSub')}</small>
                                    </button>
                                </li>
                                <li>
                                    <button class="dropdown-item" data-action="remind-yes" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-check-circle me-2 text-success"></i>${this.cTxt('remindYes')}
                                        <small class="d-block text-muted ms-4">${this.cTxt('remindYesSub')}</small>
                                    </button>
                                </li>
                                <li>
                                    <button class="dropdown-item" data-action="remind-no" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-x-circle me-2 text-danger"></i>${this.cTxt('remindNo')}
                                        <small class="d-block text-muted ms-4">${this.cTxt('remindNoSub')}</small>
                                    </button>
                                </li>`;

        let statusBadge = '';
        // Plain per-channel lines under the badge (see _channelStatusLines).
        let channelStatusHtml = '';
        // Approved during the guests' night and parked for their morning
        // (server/utils/quiet-hours.js). Without its own badge this read as
        // "Processing" with a tooltip about waiting on Meta — which is over.
        const quietUntil = campaign.quietHoursHold?.until
            ? (campaign.quietHoursHold.until.toDate?.() || new Date(campaign.quietHoursHold.until)) : null;
        const quietHeld = !isProcessed && quietUntil && !isNaN(quietUntil) && quietUntil.getTime() > Date.now()
            && ['scheduled', 'pending', 'pending_template', 'emails_sent_whatsapp_pending'].includes(status);
        if (quietHeld) {
            const when = quietUntil.toLocaleString(this._dateLocale(), { weekday: 'short', hour: 'numeric', minute: '2-digit' });
            // A reminder whose preset time fell in the guests' night is held on
            // time (reason 'system_timed'): no WhatsApp approval is involved.
            // Neither tip names an hour: a mixed list is released once it is
            // daytime in every zone holding a quarter of the guests (09:00 PT
            // is 19:00 in Israel), so the old fixed 9 AM promise was untrue for some.
            const quietTip = this.cAttr(campaign.quietHoursHold.reason === 'system_timed'
                ? 'quietHoldSystemTip' : 'quietHoldLateTip');
            statusBadge = `<span class="badge bg-info text-dark" data-bs-toggle="tooltip" data-bs-placement="top" title="${quietTip}"><i class="bi bi-moon-stars me-1"></i>${this.cTxt('sendingAt', { when })} <i class="bi bi-info-circle ms-1" style="opacity:0.7;"></i></span>`;
        } else if (!isProcessed && campaign.interruptedAt
            && ['scheduled', 'pending', 'pending_template', 'emails_sent_whatsapp_pending'].includes(status)
            && (() => {
                // A deploy stopped this send a moment ago and handed it back
                // for the dispatcher's next tick (server/utils/send-shutdown).
                // Guests are mid-send: not "Processing" with a tooltip about
                // Meta's approval, nor "Scheduled" for a time already past.
                const at = campaign.interruptedAt.toDate?.() || new Date(campaign.interruptedAt);
                return !isNaN(at) && Date.now() - at.getTime() < 15 * 60 * 1000;
            })()) {
            statusBadge = `<span class="badge bg-warning text-dark"><i class="bi bi-arrow-repeat spin me-1"></i>${this.cTxt('badgeSending')}</span>`;
        } else if (!isProcessed) {
            switch (status) {
                case 'processing':
                    statusBadge = `<span class="badge bg-warning text-dark"><i class="bi bi-arrow-repeat spin me-1"></i>${this.cTxt('badgeSending')}</span>`;
                    break;
                case 'pending':
                case 'pending_template':
                case 'emails_sent_whatsapp_pending':
                    // No badge: one line per channel says what is happening on
                    // it. A single "Processing" with an English tooltip covered
                    // three different states (2026-10-07 audit).
                    channelStatusHtml = this._channelStatusHtml(this._channelStatusLines(campaign, status));
                    break;
                case 'template_timeout':
                    // Expired: WhatsApp never approved it in time. Edit & Resend
                    // (below) submits it again; the wizard re-arms a dead status.
                    statusBadge = `<span class="badge bg-warning text-dark" title="${this.cAttr('expiredTip')}"><i class="bi bi-clock-history me-1"></i>${this.cTxt('badgeExpired')}</span>`;
                    break;
                case 'template_rejected':
                    // Meta refused the wording before anything went: the fix is
                    // to edit and resubmit (the Edit & Resend button), not to
                    // contact support.
                    statusBadge = `<span class="badge bg-danger" title="${DashboardCampaigns.escapeAttr(this.cTxt('waRejectedBadgeTip'))}"><i class="bi bi-exclamation-triangle me-1"></i>${this._escape(this.cTxt('badgeUnableToSend'))}</span>`;
                    // The reason is in the red banner below when the card has
                    // one; otherwise it goes on this line.
                    channelStatusHtml = this._channelStatusHtml([{
                        icon: 'bi-whatsapp',
                        text: `${this.cTxt('chWhatsApp')}: ${DashboardCampaigns.hasTemplateError(campaign)
                            ? this.cTxt('stWaRefusedBelow')
                            : [this.cTxt('stWaRefused'), this._templateReasonText(campaign.whatsappTemplate?.rejectedReason || '', true)].filter(Boolean).join(' ')}`
                    }], 'text-danger');
                    break;
                case 'failed':
                    // "Unable to Send" always says why, on the card itself: a
                    // tooltip with "contact support or try again" was all it
                    // had, and a phone never shows a tooltip.
                    statusBadge = `<span class="badge bg-danger"><i class="bi bi-exclamation-triangle me-1"></i>${this._escape(this.cTxt('badgeUnableToSend'))}</span>`;
                    channelStatusHtml = this._channelStatusHtml([{
                        icon: 'bi-info-circle',
                        text: this._failureReasonText(campaign, { hasBanner: DashboardCampaigns.hasTemplateError(campaign) })
                    }], 'text-danger');
                    break;
                case 'cancelled':
                    statusBadge = `<span class="badge bg-secondary"><i class="bi bi-x-circle me-1"></i>${this.cTxt('badgeCancelled')}</span>`;
                    break;
                case 'paused':
                    statusBadge = this._providerPauseBadgeHtml(campaign)
                        || `<span class="badge bg-secondary"><i class="bi bi-pause-circle me-1"></i>${this.cTxt('badgePaused')}</span>`;
                    break;
                case 'paused_insufficient_credits':
                    statusBadge = `<span class="badge bg-warning text-dark" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.cAttr('noCreditsTip')}"><i class="bi bi-wallet2 me-1"></i>${this.cTxt('badgeNoCredits')} <i class="bi bi-info-circle ms-1" style="opacity:0.7;"></i></span>`;
                    break;
                case 'held_for_review':
                    // A send from an account that has never purchased waits for a
                    // person to approve it (shouldHoldFirstSend, campaigns.service.js).
                    // This had NO case here, so it fell to the `default` arm below and
                    // rendered as "Scheduled" — telling a host their message was queued
                    // to go out when it was never going to leave until someone approved
                    // it. The server writes lastError for exactly this reason, but the
                    // host card has never rendered lastError, so the badge is the only
                    // place the stall can be explained.
                    //
                    // bg-info (like 'Processing'), not amber: the wait is on US, and
                    // there is nothing for the host to do — an alarm colour would imply
                    // otherwise. Deliberately vague about WHY it was held; the gate's
                    // discriminator is a signal an abuser would route around, and a real
                    // host doesn't need it to understand the wait.
                    statusBadge = `<span class="badge bg-info text-dark" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.cAttr('inReviewTip')}"><i class="bi bi-shield-check me-1"></i>${this.cTxt('badgeInReview')} <i class="bi bi-info-circle ms-1" style="opacity:0.7;"></i></span>`;
                    break;
                case 'draft':
                    statusBadge = `<span class="badge bg-warning text-dark"><i class="bi bi-pencil-square me-1"></i>${this.cTxt('badgeDraft')}</span>`;
                    break;
                case 'manual_links':
                    statusBadge = `<span class="badge bg-secondary"><i class="bi bi-link-45deg me-1"></i>${this.cTxt('sharedByHand')}</span>`;
                    break;
                default: {
                    // A due "Send now" doc (task #546) is between creation and
                    // the dispatcher picking it up — "Scheduled" reads like the
                    // host's immediate send was ignored. Show it as sending.
                    const sched = campaign.scheduledSendDate
                        ? (campaign.scheduledSendDate.toDate?.() || new Date(campaign.scheduledSendDate)) : null;
                    const dueNow = campaign.isImmediate
                        && !(sched && !isNaN(sched) && sched.getTime() > Date.now() + 60 * 1000);
                    statusBadge = (status === 'scheduled' && dueNow)
                        ? `<span class="badge bg-warning text-dark"><i class="bi bi-arrow-repeat spin me-1"></i>${this.cTxt('badgeSending')}</span>`
                        : `<span class="badge bg-secondary"><i class="bi bi-clock me-1"></i>${this.cTxt('badgeScheduled')}</span>`;
                }
            }
        }
        // Deliberately NO badge for 'partially_sent'.
        //
        // It used to render a grey "Partial" pill, which reads as a verdict on the
        // whole send — but a partially_sent campaign is usually a near-complete
        // one. The case that prompted this: 294 sent, 12 not delivered (~96%),
        // labelled "Partial". The badge told the host their campaign was
        // second-rate while the stats row was already saying exactly how many
        // missed, which is the more useful and less alarming framing.
        //
        // Nothing is hidden: the stats row unconditionally renders
        // "N not delivered" (with a tooltip explaining Meta's reasons), and a
        // completed 'sent' campaign has never shown a badge here either — so
        // partially_sent now renders consistently with it rather than being
        // singled out. A wholesale failure is a different status ('failed' →
        // red "Unable to Send") and still badges loudly.
        //
        // The one finished state that does badge: nothing went out because
        // every guest was skipped. With no badge it read as a send (Mesh'
        // Bachelorette, 2026-10-06). Amber, not red: nothing is broken, and
        // the note under the card says what to do; grey once nothing is left
        // to do (the guests replied or have their personal links).
        if (skip && skip.allSkipped) {
            statusBadge = this._skipBadgeHtml(skip);
        }

        // Compact, icon-only reminder toggle: bell + count + caret (task #75).
        // The count alone keeps the component narrow; the title/aria-label
        // carry the meaning. Rendered INSIDE the stats grid (task #301) so the
        // bell always sits on the same flow line as the stats it follows,
        // instead of wrapping to a lonely row of its own.
        // A red dot when one of them needs the host (review #15): the count
        // alone hid a "Couldn't send" or "Insufficient credits" behind it.
        const reminderAttentionN = splitReminderCount > 0
            ? ((this._reminderAttentionMap || this._reminderAttentionByParent()).get(campaign.id) || 0) : 0;
        const reminderAttentionLine = reminderAttentionN > 0
            ? (reminderAttentionN === 1 ? this.cTxt('remAttentionOne') : this.cTxt('remAttentionMany', reminderAttentionN)) : '';
        const reminderToggleTitle = reminderAttentionLine
            ? this._escape(reminderAttentionLine).replace(/"/g, '&quot;')
            : this.cAttr('toggleRemindersTip');
        const reminderPillHtml = splitReminderCount > 0 ? (() => {
            const n = splitReminderCount;
            const expanded = this._isReminderGroupExpanded(campaign.id);
            const caret = expanded ? 'bi-chevron-up' : 'bi-chevron-down';
            const aria = this._reminderToggleAria(campaign.id, n, expanded);
            return `<button type="button" class="reminder-toggle${reminderAttentionN > 0 ? ' needs-attention' : ''}" data-action="toggle-reminders" data-campaign-id="${campaign.id}" aria-expanded="${expanded}" aria-label="${this._escape(aria).replace(/"/g, '&quot;')}" title="${reminderToggleTitle}"><i class="bi bi-bell-fill"></i><span class="reminder-toggle-label">${n}</span>${this._reminderAttentionDotHtml(reminderAttentionN)}<i class="bi ${caret} reminder-caret"></i></button>`;
        })() : '';

        // The reminder controls (bell pill, or the plain "Reminders on" note when a
        // campaign's reminders haven't been split into their own rows yet) are ONE
        // unbreakable group that lives outside the stats box entirely.
        //
        // Task #583 round 5: it used to sit inside .campaign-stats-grid, centred
        // against the STATS. But the stats are only part of this column — the
        // status badge sits above them and the "N guests added after launch" note
        // below — so its centre line was higher than the action buttons', which are
        // centred against the whole row. Now it is a direct child of
        // .campaign-col-meta, which the row grid centres exactly like the actions
        // column, so the bell and the buttons share one centre line at every width.
        //
        // "Reminders on" is dropped whenever the bell pill is there: a bell saying
        // "Reminders on" next to a second bell carrying the reminder count said the
        // same thing twice and cost ~110px of the line it was competing for.
        const trailInnerHtml = (isProcessed && hasUpcomingReminders && !reminderPillHtml)
            ? `<span class="text-info"><i class="bi bi-bell-fill me-1"></i>${this.cTxt('remindersOn')}</span>` + reminderPillHtml
            : reminderPillHtml;
        const trailHtml = trailInnerHtml ? `<span class="cs-trail">${trailInnerHtml}</span>` : '';

        // Stats display only if processed, otherwise show scheduled info
        // Stopped by a provider outage after part of it went: the host still
        // needs to see who got it (the counts and Status), which the
        // partially_sent card this used to settle as showed (PMa review).
        const providerPauseState = this._providerPauseState(campaign);
        const providerPausedWithSends = !!providerPauseState && actualSent > 0;
        let statsHtml;
        if (isProcessed || providerPausedWithSends) {
            // Fixed-width columns (.cs-stat-*) keep responded/pending/sent
            // vertically aligned across every row. The three core stats come
            // first (always present) so their columns line up; the optional
            // "not delivered" + reminder pill trail after.
            //
            // Task #583 round 3: the four metrics live in their OWN wrapping box
            // (.cs-metrics), so when they need a second line it starts hard-left
            // under "responded", on the same column rhythm rather than trailing
            // off wherever the line above ended.
            //
            // Round 5: the reminder controls are no longer part of this box at
            // all — see trailHtml above; they are a sibling of the whole meta
            // block so they centre with the action buttons.
            //
            // Guests held back on purpose count as not delivered (#1039).
            // When that is everyone, it is the only count shown: four zeros
            // beside "Not sent" say nothing.
            statsHtml = (skip && skip.allSkipped) ? `
                <div class="campaign-stats-grid d-flex align-items-start small">
                    <span class="cs-metrics">${this._notDeliveredStatHtml(campaign, 0, skip)}</span>
                </div>
            ` : `
                <div class="campaign-stats-grid d-flex align-items-start small">
                    <span class="cs-metrics">
                    <span class="cs-stat cs-stat-resp text-success"${parentAudienceHasReplied ? ` title="${this.cAttr('allRepliedTip')}"` : ''}><i class="bi bi-check-circle-fill me-1"></i>${this.cCount('statResponded', respondedCount)}</span>
                    <span class="cs-stat cs-stat-pend text-warning"><i class="bi bi-hourglass-split me-1"></i>${this.cCount('statPending', pending)}</span>
                    ${isHandShared ? `
                    <span class="cs-stat cs-stat-sent text-muted">${this.cCount('statContacts', campaign.totalContacts || actualSent)}</span>` : `
                    <span class="cs-stat cs-stat-sent text-muted">${this.cCount('statSent', actualSent)}</span>`}
                    ${isHandShared ? '' : this._notDeliveredStatHtml(campaign, notDelivered, skip)}
                    ${(stats.waSendable || 0) > 0 ? `<button type="button" class="btn btn-sm btn-whatsapp py-0 px-2 cs-wa-walk" data-action="wa-walk" data-campaign-id="${campaign.id}" title="${this.cAttr('waWalkCardTip')}"><i class="bi bi-whatsapp me-1"></i>${this._escape(this.cTxt('waWalkCardBtn', stats.waSendable))}</button>` : ''}
                    </span>
                </div>
            `;
        } else {
            // The scheduled delivery time now leads the date line beneath the title
            // (see dateLineHtml above), so the stats line just shows the recipient
            // count — no duplicate "Scheduled: …" string here.
            const recipientCount = campaign.totalContacts || campaign.contacts?.length || 0;
            // Task #993: the count opens the list it will go to, so a host can
            // check who is on a scheduled message before it sends.
            const canList = recipientCount > 0 && !campaign.isReminderCampaign;
            statsHtml = `
                <div class="small text-muted">
                    <span${canList ? ` class="cs-stat-nd-link" data-action="view-recipients" data-campaign-id="${campaign.id}" role="button" tabindex="0" title="${this.cAttr('msgAudienceTitle')}"` : ''}><i class="bi bi-people me-1"></i>${this.cCount('statRecipients', recipientCount)}</span>
                </div>
            `;
        }

        // Determine if campaign is cancellable (scheduled but not yet sent)
        const isCancellable = !isProcessed && (status === 'scheduled' || status === 'pending' || status === 'pending_template' || status === 'emails_sent_whatsapp_pending');
        const isPauseable = isCancellable || status === 'processing';
        const isPaused = status === 'paused';
        const isInsufficientCreditsPaused = status === 'paused_insufficient_credits';
        const isFailed = status === 'failed' || status === 'template_rejected';
        // Allow permanent deletion of campaigns the host no longer wants in
        // their history (failed sends, finished sends, paused/cancelled).
        // Drafts already have their own Delete button. Skip while actively
        // processing — deleting mid-send would race the cron and trigger
        // mid-batch errors. Skip 'held_for_review' too: that row is the one an
        // admin is about to rule on, and deleting it takes the evidence with
        // it (2026-09-18 — a phishing account's held campaign was gone by the
        // time it was adjudicated). The rules enforce this independently, keyed
        // on the account's server-written sendHold rather than on this status,
        // since the status is owner-writable; this guard only stops the button
        // from offering an action that will now be refused.
        const canDelete = !isDraft && status !== 'processing' &&
            status !== 'processing_additions' && status !== 'held_for_review';

        // Template creation silently failed (e.g. Meta rejected the header_url
        // because the image wasn't .jpg/.jpeg/.png). Without surfacing this,
        // hosts see "0/N reached" with no explanation and assume the product
        // is broken. Show a red alert + a recovery "Edit & Resend" button
        // even if campaign.status is 'sent' (emails to email-only contacts
        // may have gone out; only the WhatsApp side died).
        // Show it when the template never made it to Meta AT ALL, and also when a
        // LATER re-submission was refused while an older template name is still
        // stored — that case used to be invisible, so an Edit & Resend that
        // WhatsApp rejected looked like a save that worked (task #592).
        const hasTemplateError = DashboardCampaigns.hasTemplateError(campaign);
        // Not a refusal: WhatsApp has not seen the message yet, and the
        // dispatcher keeps trying (see hasTemplateError). A neutral note, no
        // Edit & Resend.
        const templateSubmitPending = DashboardCampaigns.templateSubmitPending(campaign);
        const templateErrorRaw = String(campaign.templateCreationError || '');
        // One shared reader turns the raw answer into a sentence a host can act
        // on, and tells us whether the failure was WhatsApp's rule or our own
        // crash (task #592 round 3 — hosts were being shown
        // `500:{"success":false,"error":"reusable is not defined"}`).
        const templateErrorInfo = (typeof BulkSharingWizard !== 'undefined'
            && BulkSharingWizard.describeTemplateError)
            ? BulkSharingWizard.describeTemplateError(templateErrorRaw)
            : { ours: false, text: this._escape(this.cTxt('waCouldNotAccept')), technical: templateErrorRaw };
        // A template still in review 48 hours after the send time is our own
        // verdict, not WhatsApp's words, so it reads in the dashboard language.
        // A refused language set is worded from its codes (templateRefusal),
        // a recognised rule from its key, and only WhatsApp's own sentence is
        // shown as it came: the banner was English for every host, and
        // WhatsApp's words went into the page unescaped.
        const refusal = campaign.templateRefusal;
        const refusalFits = !!(refusal && Array.isArray(refusal.languages) && refusal.languages.length
            && refusal.forError === campaign.templateCreationError);
        const templateErrorFriendly = (campaign.whatsappTemplate?.status === 'timeout')
            ? this._escape(this.cTxt('waTimeoutBody'))
            : refusalFits
                ? this._templateRefusalHtml(refusal)
                : this._escape(templateErrorInfo.key ? this.cTxt('tplErr_' + templateErrorInfo.key) : templateErrorInfo.text);
        // The email/SMS leg went before WhatsApp failed (the dispatcher sends
        // it at the send time while the template is in review): say who has
        // it already, and that the resend reaches the WhatsApp guests.
        const waHalfLostAfterOthers = hasTemplateError
            && (status === 'partially_sent' || status === 'sent') && actualSent > 0;
        const escapeAttr = (s = '') => String(s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

        // Post-launch guest additions. Adding contacts to a finished campaign
        // appends to THIS campaign (and fires a real message) instead of creating
        // a new campaign row — so without this the host just sees the "sent" count
        // silently tick up with no hint that anything went out later. Surface it.
        const additions = Array.isArray(campaign.additionsHistory) ? campaign.additionsHistory : [];
        // A processedFromQueue entry is the RELEASE of guests the queued entry
        // already counted; summing both showed one added guest as two.
        const addedLaterTotal = additions.reduce((n, a) => n + (a.processedFromQueue ? 0 : (a.contactCount || 0)), 0);
        let addedLaterHtml = '';
        if (isProcessed && addedLaterTotal > 0) {
            const parseAddDate = (a) => {
                const ts = a?.addedAt;
                const d = ts?.toDate?.() || (ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
                return isNaN(d) ? null : d;
            };
            const fmtAdd = (d) => d ? d.toLocaleDateString(this._dateLocale(), { month: 'short', day: 'numeric' }) : '';
            const lastAdded = fmtAdd(additions.filter(a => !a.processedFromQueue).map(parseAddDate).filter(Boolean).sort((x, y) => y - x)[0]);
            const pendingCount = campaign.hasPendingAdditions
                ? (campaign.contacts || []).filter(c => c.pendingTemplate).length
                : 0;
            // One line per calendar day, newest first (task #912): a big
            // upload lands as dozens of micro-batches, and a line per batch
            // ("Oct 4: 1 added · 1 sent" ×40) ran the tooltip off the screen.
            // Only "N added": who was reached is already on the card's
            // delivery stats, and a guest who was not shows up there as not
            // delivered (#912 round 2). Releases of queued guests are not
            // additions, so they get no line.
            const days = new Map();
            additions.forEach(a => {
                if (a.processedFromQueue || !(a.contactCount > 0)) return;
                const d = parseAddDate(a);
                const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '';
                if (!days.has(key)) days.set(key, { d, added: 0 });
                days.get(key).added += a.contactCount;
            });
            const tip = [...days.values()]
                .sort((x, y) => (y.d || 0) - (x.d || 0))
                .map(day => this._escape(this.cTxt('acTipAdded', { when: fmtAdd(day.d) || '—', n: day.added })))
                .join('<br>');
            const lineParts = [addedLaterTotal === 1 ? this.cTxt('acLineOne') : this.cTxt('acLineMany', addedLaterTotal)];
            if (lastAdded) lineParts.push(this.cTxt('acLineLast', lastAdded));
            const lineText = this._escape(lineParts.join(' · '))
                + (pendingCount > 0 ? ` · <span class="text-warning">${this._escape(this.cTxt('acLineAwaiting', pendingCount))}</span>` : '');
            addedLaterHtml = `
                        <div class="small text-muted campaign-additions-line mt-1" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-html="true" title="${escapeAttr(tip)}">
                            <i class="bi bi-person-plus me-1"></i>${lineText}
                        </div>`;
        }

        // Guests the host added in a Remind All review whom the dispatch skipped
        // (the server writes addedSkipped when it resolves the audience). The
        // review had listed them, so the card says why they got nothing rather
        // than leaving it to a server log.
        const addedSkipped = campaign.addedSkipped || {};
        const addedSkippedParts = [];
        if (addedSkipped.optedOut > 0) addedSkippedParts.push(this.cTxt('addedSkippedOptedOut', addedSkipped.optedOut));
        if (addedSkipped.responded > 0) addedSkippedParts.push(this.cTxt('addedSkippedResponded', addedSkipped.responded));
        const addedSkippedHtml = addedSkippedParts.length ? `
                        <div class="small text-muted mt-1">
                            <i class="bi bi-person-dash" style="margin-inline-end:0.25rem;"></i>${addedSkippedParts.join(' · ')}
                        </div>` : '';

        // For paused campaigns, check if scheduled date has passed
        let isPausedDatePassed = false;
        // A provider pause always offers Resume: resumeCampaign sends it to
        // the guests not reached yet, with its own wording (see there).
        if (isPaused && !campaign.isImmediate && campaign.scheduledSendDate && !this._providerPauseBadgeHtml(campaign)) {
            const schedDate = campaign.scheduledSendDate?.toDate?.() || new Date(campaign.scheduledSendDate);
            isPausedDatePassed = !isNaN(schedDate.getTime()) && schedDate < new Date();
        }

        return `
            <div class="campaign-item border-bottom p-3" data-campaign-id="${campaign.id}">
                <div class="campaign-row-grid">
                    <!-- Col 1: campaign name (truncates past the column width, full name on
                         hover) with the creation date beneath it. -->
                    <div class="campaign-col-name">
                        <div class="d-flex align-items-center gap-1" style="min-width:0;">
                            ${channelIcon}
                            <span class="fw-semibold text-dark campaign-name-editable campaign-name-text" data-action="rename-campaign-name" data-campaign-id="${campaign.id}" title="${escapeAttr(campaign.name || this.cTxt('untitledMessage'))}" role="button" tabindex="0">${nameHtml}</span>
                            ${standalone.audience && !standalone.autoName ? `<span class="fw-semibold text-dark campaign-name-audience">· ${this._escape(standalone.audience)}</span>` : ''}
                        </div>
                        <div class="text-muted small campaign-date-line">${dateLineHtml}</div>
                    </div>
                    <!-- Col 2: status badge + delivery metrics + the View Reminders toggle.
                         NOTE: inline gap (not the gap-2 class) — a global ".card .d-flex.gap-2.flex-wrap"
                         mobile rule would otherwise force this label row into a 2-column button grid. -->
                    <div class="campaign-col-meta">
                        <div class="campaign-meta-main">
                            <div class="d-flex align-items-center flex-wrap mb-1" style="gap:0.5rem;">
                                ${statusBadge}
                            </div>
                            ${channelStatusHtml}
                            <div class="campaign-statusbar d-flex align-items-center flex-wrap" style="gap:0.5rem;">
                                ${statsHtml}
                            </div>
                            ${addedLaterHtml}
                            ${this._providerPauseNoteHtml(campaign)}
                            ${this._outageHeldNoteHtml(campaign)}
                            ${campaign.isReminderCampaign ? this._reminderDupNote(campaign) : ''}
                            ${addedSkippedHtml}
                        </div>
                        <!-- The reminder bell sits beside the whole meta block, not inside
                             the stats line, so the row grid centres it on the same line as
                             the action buttons (task #583 round 5). -->
                        ${trailHtml}
                    </div>
                    <!-- Col 3: action buttons, pinned to the right edge. -->
                    <div class="campaign-col-actions campaign-card-actions d-flex align-items-center gap-2">
        ${hasTemplateError && !isFailed && !isCancellable && !isPaused ? `
                        <button class="btn btn-warning btn-sm fw-semibold" data-action="edit-campaign" data-campaign-id="${campaign.id}" title="${this.cAttr('editResendTip')}">
                            <i class="bi bi-pencil-square me-1"></i>${this.cTxt('editResend')}
                        </button>
        ` : ''}
        ${!isHandShared ? `
                        <button class="btn btn-outline-secondary btn-sm" data-action="preview-campaign" data-campaign-id="${campaign.id}" title="${this.cTxt('previewTip')}" aria-label="${this.cAttr('preview')}">
                            <i class="bi bi-eye me-1"></i><span class="cca-label">${this.cTxt('preview')}</span>
                        </button>
        ` : ''}
        ${isProcessed ? `
                        ${hasRetryQueue ? `
                        <button class="btn ${autoRetryPaused ? 'btn-outline-success' : 'btn-outline-secondary'} btn-sm" data-action="toggle-auto-retry" data-campaign-id="${campaign.id}" data-paused="${autoRetryPaused}" title="${autoRetryPaused ? this.cTxt('resumeAutoRetryTip', pendingRetries) : this.cTxt('pauseAutoRetryTip', pendingRetries)}">
                            <i class="bi ${autoRetryPaused ? 'bi-play-fill' : 'bi-pause-fill'}"></i>
                        </button>` : ''}
                        <button class="btn btn-outline-secondary btn-sm" data-action="view-status" data-campaign-id="${campaign.id}" title="${this.cTxt('statusTip')}" aria-label="${this.cAttr('status')}">
                            <i class="bi bi-bar-chart-line me-1"></i><span class="cca-label">${this.cTxt('status')}</span>
                        </button>
                        <div class="dropdown d-inline-block">
                            <button class="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false" title="${this.cTxt('remindTip')}">
                                <i class="bi bi-arrow-repeat me-1"></i>${this.cTxt('remind')}
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end">${remindMenuItemsHtml}
                            </ul>
                        </div>
                        <button class="btn btn-success btn-sm fw-semibold" data-action="add-contacts" data-campaign-id="${campaign.id}" ${(hasTemplateError || templateSubmitPending) ? 'disabled' : ''} title="${hasTemplateError ? this.cTxt('addDisabledTip') : templateSubmitPending ? this.cTxt('addWaitWaTip') : this.cTxt('addTip')}">
                            <i class="bi bi-person-plus-fill me-1"></i>${this.cTxt('addContacts')}
                        </button>
                        ` : ''}
                        ${isDraft ? `
                        <button class="btn btn-outline-primary btn-sm" data-action="send-test" data-campaign-id="${campaign.id}"
                                ${!hasTestableMessage ? 'disabled' : ''}
                                title="${!hasTestableMessage ? this.cTxt('sendTestNoMessage') : (alreadyTested ? this.cTxt('sendTestAgainTip') : this.cTxt('sendTestTip')) + ' — ' + this.cTxt('sendTestCost')}">
                            <i class="bi bi-send-check me-1"></i>${alreadyTested ? this.cTxt('sendTestAgain') : this.cTxt('sendTest')}
                        </button>
                        <button class="btn btn-primary btn-sm" data-action="continue-draft" data-campaign-id="${campaign.id}">
                            <i class="bi bi-arrow-right-circle me-1"></i>${this.cTxt('continueBtn')}
                        </button>
                        <button class="btn btn-outline-danger btn-sm" data-action="delete-draft" data-campaign-id="${campaign.id}">
                            <i class="bi bi-trash me-1"></i>${this.cTxt('delete')}
                        </button>
                        ` : ''}
                        ${(isFailed || status === 'template_timeout') ? `
                        <button class="btn btn-outline-secondary btn-sm" data-action="edit-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-pencil me-1"></i>${this.cTxt('editResend')}
                        </button>
                        ` : ''}
                        ${isCancellable ? `
                        <button class="btn btn-outline-secondary btn-sm" data-action="edit-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-pencil me-1"></i>${this.cTxt('edit')}
                        </button>
                        ${status === 'scheduled' ? `
                        <button class="btn btn-success btn-sm fw-semibold" data-action="add-contacts" data-campaign-id="${campaign.id}" title="${this.cTxt('addScheduledTip')}">
                            <i class="bi bi-person-plus-fill me-1"></i>${this.cTxt('addContacts')}
                        </button>
                        ` : ''}
                        ` : ''}
                        ${isPauseable ? `
                        <button class="btn btn-outline-warning btn-sm" data-action="pause-campaign" data-campaign-id="${campaign.id}" title="${this.cTxt('pauseTip')}">
                            <i class="bi bi-pause-fill me-1"></i>${this.cTxt('pause')}
                        </button>
                        ` : ''}
                        ${isCancellable ? `
                        <button class="btn btn-outline-danger btn-sm" data-action="cancel-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-x-circle me-1"></i>${this.cTxt('cancel')}
                        </button>
                        ` : ''}
                        ${isPaused ? `
                        ${providerPausedWithSends ? `
                        <button class="btn btn-outline-secondary btn-sm" data-action="view-status" data-campaign-id="${campaign.id}" title="${this.cTxt('statusTip')}" aria-label="${this.cAttr('status')}">
                            <i class="bi bi-bar-chart-line me-1"></i><span class="cca-label">${this.cTxt('status')}</span>
                        </button>
                        ` : ''}
                        <button class="btn btn-outline-secondary btn-sm" data-action="edit-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-pencil me-1"></i>${this.cTxt('edit')}
                        </button>
                        ${providerPauseState === 'resuming' ? '' : isPausedDatePassed ? `
                        <button class="btn btn-primary btn-sm" data-action="send-now-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-send me-1"></i>${this.cTxt('sendNow')}
                        </button>
                        ` : `
                        <button class="btn btn-primary btn-sm" data-action="resume-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-play-fill me-1"></i>${this.cTxt('resume')}
                        </button>
                        `}
                        <button class="btn btn-outline-danger btn-sm" data-action="cancel-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-x-circle me-1"></i>${this.cTxt('cancel')}
                        </button>
                        ` : ''}
                        ${isInsufficientCreditsPaused ? `
                        <button class="btn btn-primary btn-sm" data-action="buy-credits-from-card" data-campaign-id="${campaign.id}" title="${this.cTxt('buyCreditsTip')}">
                            <i class="bi bi-wallet2 me-1"></i>${this.cTxt('buyCredits')}
                        </button>
                        <button class="btn btn-outline-secondary btn-sm" data-action="resume-campaign" data-campaign-id="${campaign.id}" title="${this.cTxt('retryTip')}">
                            <i class="bi bi-arrow-repeat me-1"></i>${this.cTxt('retry')}
                        </button>
                        <button class="btn btn-outline-danger btn-sm" data-action="cancel-campaign" data-campaign-id="${campaign.id}">
                            <i class="bi bi-x-circle me-1"></i>${this.cTxt('cancel')}
                        </button>
                        ` : ''}
                        ${splitReminderCount > 0 ? `
                        <button class="btn btn-outline-secondary btn-sm d-md-none" data-action="toggle-reminders" data-campaign-id="${campaign.id}" aria-expanded="${this._isReminderGroupExpanded(campaign.id)}" title="${reminderToggleTitle}">
                            <i class="bi bi-bell-fill me-1"></i>${this.cTxt('reminders')} (${splitReminderCount})${this._reminderAttentionDotHtml(reminderAttentionN)}
                        </button>
                        ` : (isProcessed ? `
                        <div class="dropdown d-inline-block d-md-none">
                            <button class="btn btn-outline-secondary btn-sm" data-bs-toggle="dropdown" aria-expanded="false" title="${this.cTxt('remindersNoneTip')}">
                                <i class="bi bi-bell me-1"></i>${this.cTxt('reminders')} (0)
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end">${remindMenuItemsHtml}
                            </ul>
                        </div>
                        ` : '')}
                        ${(!isDraft || canDelete) ? `
                        <div class="dropdown d-inline-block">
                            <button class="btn btn-outline-secondary btn-sm" data-bs-toggle="dropdown" aria-expanded="false" title="${this.cTxt('moreActions')}" aria-label="${this.cTxt('moreActions')}">
                                <i class="bi bi-three-dots"></i>
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end">
                                ${isProcessed ? `
                                <li>
                                    <button class="dropdown-item" data-action="personal-links" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-link-45deg me-2"></i>${this.cTxt('personalLinks')}
                                    </button>
                                </li>
                                <li>
                                    <button class="dropdown-item" data-action="tag-contacts" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-tag me-2"></i>${this.cTxt('tagContacts')}
                                    </button>
                                </li>` : ''}
                                ${!isDraft && !isHandShared ? `
                                <li>
                                    <button class="dropdown-item" data-action="duplicate-campaign" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-files me-2"></i>${this.cTxt('duplicate')}
                                    </button>
                                </li>` : ''}
                                ${canDelete ? `
                                <li>
                                    <button class="dropdown-item text-danger" data-action="delete-campaign" data-campaign-id="${campaign.id}">
                                        <i class="bi bi-trash me-2"></i>${this.cTxt('delete')}
                                    </button>
                                </li>` : ''}
                            </ul>
                        </div>
                        ` : ''}

                    </div>
                </div>
                ${hasTemplateError ? `
                <!-- Inline gap, not gap-2: admin.css stacks every .d-flex.gap-2
                     into a column on phones, the icon alone above the text. -->
                <div class="alert alert-danger py-2 px-3 small mt-2 mb-0 d-flex align-items-start" role="alert" style="border-radius:8px;gap:.5rem;">
                    <i class="bi bi-exclamation-triangle-fill flex-shrink-0" style="margin-top:2px;"></i>
                    <div style="min-width:0;">
                        <div class="fw-semibold mb-1">${this._escape(this.cTxt(campaign.whatsappTemplate?.status === 'timeout' ? 'waTimeoutTitle' : (templateErrorInfo.ours ? 'waOursTitle' : 'waRejectedTitle')))}</div>
                        <!-- The raw answer stays on the title attribute for support
                             and the console; it is never the visible line (task #592
                             round 3). -->
                        <div title="${escapeAttr(templateErrorInfo.technical || '')}">${templateErrorFriendly}</div>
                        ${waHalfLostAfterOthers ? `<div class="mt-1">${this._escape(this.cTxt('waOthersHaveIt', { editResend: this.cTxt('editResend') }))}</div>` : ''}
                    </div>
                </div>
                ` : ''}
                ${templateSubmitPending ? `
                <div class="alert alert-info py-2 px-3 small mt-2 mb-0 d-flex align-items-start campaign-wa-submit-pending" role="status" style="border-radius:8px;gap:.5rem;">
                    <i class="bi bi-hourglass-split flex-shrink-0" style="margin-top:2px;"></i>
                    <div style="min-width:0;">
                        <div class="fw-semibold mb-1">${this._escape(this.cTxt('waSubmitPendingTitle'))}</div>
                        <div>${this._escape(this.cTxt('waSubmitPendingBody'))}</div>
                    </div>
                </div>
                ` : ''}
                ${(skip && skip.allSkipped) ? this._skipNoteHtml(campaign, skip) : ''}
            </div>
        `;
    }

    async loadWalletBalance() {
        const balanceEl = document.getElementById('campaignsWalletBalance');
        if (!balanceEl) return;

        // In admin mode, load the form owner's wallet, not the admin's
        const ownerUid = window.isAdminMode && this.formData?.owner
            ? this.formData.owner
            : null;

        if (ownerUid) {
            // Admin viewing a user's campaigns - load that user's wallet directly from Firebase
            try {
                const db = firebase.firestore();
                const walletDoc = await db.collection('users').doc(ownerUid)
                    .collection('messaging_wallet').doc('balance').get();

                if (walletDoc.exists) {
                    const balance = walletDoc.data().balance || 0;
                    balanceEl.textContent = balance.toLocaleString();
                } else {
                    balanceEl.textContent = '0';
                }
            } catch (error) {
                console.warn('Could not load user wallet balance:', error.message);
                balanceEl.textContent = '—';
            }
            return;
        }

        // Normal user mode - use WalletManager (loads current user's balance via API)
        try {
            if (window.WalletManager) {
                const result = await window.WalletManager.loadBalance();
                const balance = result?.balance ?? result ?? 0;
                balanceEl.textContent = window.WalletManager.formatBalance(balance);
            } else {
                balanceEl.textContent = '0';
            }
        } catch (error) {
            console.warn('Wallet API unavailable, trying Firebase direct access:', error.message);

            // Try to load directly from Firebase as fallback
            try {
                const user = firebase.auth().currentUser;
                if (user) {
                    const db = firebase.firestore();
                    const walletDoc = await db.collection('users').doc(user.uid)
                        .collection('messaging_wallet').doc('balance').get();

                    if (walletDoc.exists) {
                        const balance = walletDoc.data().balance || 0;
                        balanceEl.textContent = balance.toLocaleString();
                    } else {
                        balanceEl.textContent = '0';
                    }
                } else {
                    balanceEl.textContent = '0';
                }
            } catch (fbError) {
                console.error('Firebase wallet fallback failed:', fbError);
                balanceEl.textContent = '0';
            }
        }
    }

    setupEventListeners() {
        // init(formId) is called every time the campaigns tab is opened or the
        // active form changes. Without this guard each re-init would attach
        // another listener to the same buttons, so a single click on Remind /
        // Send Reminder fired the handler N times — opening the wizard modal
        // and showing the loading toast repeatedly ("rebooting").
        if (this._listenersAttached) return;
        this._listenersAttached = true;

        // One ledger, every module (task #479): credits are a single account-wide
        // balance shared by RSVP campaigns and ticketed events, so this card must
        // follow the ledger — not just its own sends. WalletManager watches the
        // wallet doc and fires `walletUpdated` on every change (a ticketed-event
        // reminder, a purchase in another tab, an admin top-up).
        // Skipped in admin mode, where the card deliberately shows the FORM
        // OWNER's wallet rather than the signed-in admin's.
        document.addEventListener('walletUpdated', (event) => {
            if (window.isAdminMode && this.formData?.owner) return;
            const balanceEl = document.getElementById('campaignsWalletBalance');
            const balance = event?.detail?.balance;
            if (!balanceEl || typeof balance !== 'number') return;
            balanceEl.textContent = window.WalletManager
                ? window.WalletManager.formatBalance(balance)
                : balance.toLocaleString();
        });

        // Create first campaign
        document.getElementById('campaignsCreateFirstBtn')?.addEventListener('click', () => {
            this.openCreateCampaignModal();
        });

        // New Campaign button
        document.getElementById('campaignsNewCampaignBtn')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.openCreateCampaignModal();
        });

        // Buy Credits button — pass this.formId so the wallet warns the user
        // if they're buying credits from a still-free form's dashboard.
        document.getElementById('campaignsBuyCreditsBtn')?.addEventListener('click', () => {
            if (window.WalletManager) {
                window.WalletManager.showPurchaseModal(async () => {
                    await this.loadWalletBalance();
                }, this.formId);
            }
        });

        // "All Contacts" button - opens global contact status modal
        document.getElementById('campaignsAllContactsBtn')?.addEventListener('click', () => {
            this.showContactStatusModal(null);
        });

        // "Personal links" (task #873): a link per guest for hosts who send from
        // their own WhatsApp or email - the same window as the RSVP builder's
        // Share tab, offered right where messages are sent. Next to "Send
        // Messages" and on the empty state's "Send Invitations", injected so the
        // seven dashboard.html files need no edit.
        this._injectPersonalLinksButtons();

        // "Address Book" button — the personal saved-contacts manager (task
        // #510). Injected from JS so all 7 locale dashboard.html files get it
        // without per-file HTML edits. The manager itself lives on the wizard
        // class; the auto-init instance is the live one per the #165 invariant.
        const allContactsBtn = document.getElementById('campaignsAllContactsBtn');
        if (allContactsBtn && !document.getElementById('campaignsAddressBookBtn')) {
            const t = this.getWizardTranslations();
            const abBtn = document.createElement('button');
            abBtn.className = 'btn btn-outline-primary btn-sm';
            abBtn.id = 'campaignsAddressBookBtn';
            abBtn.title = t.addressBook || 'Address Book';
            abBtn.innerHTML = `<i class="bi bi-journal-text me-1"></i>${t.addressBook || 'Address Book'}`;
            allContactsBtn.insertAdjacentElement('beforebegin', abBtn);
            abBtn.addEventListener('click', () => {
                window.bulkSharingWizard?.openAddressBookManager?.();
            });
        }

        // Make the Messaging-Activity "Not reached" donut figure a deep-link into
        // the contact list, pre-filtered to exactly those guests. Hosts previously
        // saw the number but had no way to see WHO — the donut said "Not reached"
        // while the only matching list filter was worded differently and buried in
        // a dropdown. Resolve the legend wrapper from the (unique) count span so
        // this works on every localized dashboard with no per-file HTML hook.
        const notReachedCount = document.getElementById('whatsapp-delivered-count');
        const notReachedLegend = notReachedCount?.closest('.legend-item');
        if (notReachedLegend) {
            notReachedLegend.style.cursor = 'pointer';
            notReachedLegend.setAttribute('role', 'button');
            notReachedLegend.setAttribute('tabindex', '0');
            // Clickability cue is a subtle hover fade on the whole legend item —
            // no underline (it read as a broken link beside the bold number).
            notReachedLegend.style.transition = 'opacity .12s';
            notReachedLegend.addEventListener('mouseenter', () => { notReachedLegend.style.opacity = '0.6'; });
            notReachedLegend.addEventListener('mouseleave', () => { notReachedLegend.style.opacity = ''; });
            const openNotReached = () => this.showContactStatusModal(null, 'not-delivered');
            notReachedLegend.addEventListener('click', openNotReached);
            notReachedLegend.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openNotReached(); }
            });
        }

        // "Remind All > All contacts" - everyone across all campaigns, deduped
        document.getElementById('campaignsRemindAllEveryoneBtn')?.addEventListener('click', () => {
            this.remindAllEveryone();
        });

        // "Remind All > Pending" - go directly to wizard with deduped pending contacts
        document.getElementById('campaignsRemindAllPendingBtn')?.addEventListener('click', () => {
            this.remindAllPending();
        });

        // "Remind All > Yes responders" - deduped Yes responders across all campaigns
        document.getElementById('campaignsRemindAllYesBtn')?.addEventListener('click', () => {
            this.remindAllYes();
        });

        // "Remind All > Maybe responders" - deduped Maybe responders across all campaigns
        document.getElementById('campaignsRemindAllMaybeBtn')?.addEventListener('click', () => {
            this.remindAllMaybe();
        });

        // "Remind All > No responders" - deduped declined guests across all campaigns
        document.getElementById('campaignsRemindAllNoBtn')?.addEventListener('click', () => {
            this.remindAllNo();
        });

        // Contact Status search
        document.getElementById('contactStatusSearch')?.addEventListener('input', (e) => {
            this.filterContactStatus();
        });

        // Contact Status response/delivery filter dropdown
        document.getElementById('contactStatusFilter')?.addEventListener('change', () => {
            this.filterContactStatus();
        });

        // Contact Status campaign scope selector — re-fetches data from correct endpoint
        document.getElementById('contactStatusCampaignFilter')?.addEventListener('change', () => {
            this.loadContactsForScope();
        });

        // Contact Status CSV export — downloads the current view (task #505)
        document.getElementById('contactStatusExportBtn')?.addEventListener('click', () => {
            this.exportContactStatusCsv();
        });

        // Stat card clicks — set filter and refresh table
        document.querySelectorAll('.contact-stat-card').forEach(card => {
            card.addEventListener('click', () => {
                const filter = card.dataset.filter;
                const filterEl = document.getElementById('contactStatusFilter');
                if (filterEl) {
                    if (filter === 'all') filterEl.value = 'all';
                    else if (filter === 'responded-yes') filterEl.value = 'responded-yes';
                    else if (filter === 'responded-no') filterEl.value = 'responded-no';
                    else if (filter === 'responded-maybe') filterEl.value = 'responded-maybe';
                    else filterEl.value = filter;
                }
                this.filterContactStatus();

                // Highlight active card
                document.querySelectorAll('.contact-stat-card').forEach(c => c.classList.remove('border-primary', 'shadow-sm'));
                card.classList.add('border-primary', 'shadow-sm');
            });
        });

        // Retry failed button
        document.getElementById('retryFailedBtn')?.addEventListener('click', () => {
            this.retryFailedContacts();
        });

        // Switch channel button
        document.getElementById('switchChannelBtn')?.addEventListener('click', () => {
            this.switchChannelForFailedContacts();
        });
    }

    // Contact Status Modal - stores loaded contacts for filtering
    contactStatusData = [];
    // The exact (possibly search/filter-narrowed) slice currently rendered in
    // the table. data-row-idx on each row indexes into THIS, not the full
    // contactStatusData — see renderContactStatusTable / the edit handlers.
    _renderedContacts = [];
    contactStatusModalInstance = null;

    /**
     * Show a read-only modal with the exact text we sent for this campaign:
     * email subject + body, WhatsApp template body, all with sample variable
     * substitution so the user can see what the recipient actually receives.
     */
    showCampaignPreviewModal(campaignId) {
        const campaign = (this.campaigns || []).find(c => c.id === campaignId);
        if (!campaign) {
            this.showToast?.('Campaign not found', 'error');
            return;
        }

        // Strip existing instance so reopens don't stack up
        document.getElementById('campaignPreviewModal')?.remove();

        const escapeHtml = (str = '') => String(str)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

        // Sample contact used to render variable substitutions so the user
        // sees something concrete (e.g. "Hi Sarah,") instead of "Hi {{first_name}},".
        const sample = { name: 'Sarah Cohen', email: 'sarah@example.com' };
        const substitute = (text) => {
            if (!text) return '';
            const first = (sample.name || '').split(' ')[0];
            return String(text)
                .replace(/\{\{name\}\}/gi, sample.name)
                .replace(/\{\{first_name\}\}/gi, first)
                .replace(/\{\{email\}\}/gi, sample.email);
        };
        const renderBody = (text) => {
            const rendered = escapeHtml(substitute(text));
            // Preserve user-entered line breaks.
            return rendered.replace(/\n/g, '<br>');
        };

        // Channel detection. `campaign.channels` is a recent addition — older
        // campaigns (and many current ones) leave it unset, so we infer from
        // the per-channel template fields the same way the rest of the
        // dashboard does (see line ~3518). Fall through to legacy `channel`.
        const wa = campaign.whatsappTemplate || {};
        const explicitChannels = Array.isArray(campaign.channels)
            ? campaign.channels
            : (campaign.channel ? [campaign.channel] : []);
        // Current campaigns store the chosen channels in `selectedChannels`, where
        // 'mobile' means the phone channel (SMS-direct for US numbers, WhatsApp for
        // international). The old detection only read `channels`/`channel`, so a
        // normal mobile-only campaign rendered "No channels". Also fall back to the
        // per-channel counts and the post-send stats so already-sent campaigns are
        // detected correctly. 'mobile' maps to SMS for the preview (WhatsApp, when
        // actually used, is shown from its template/stats below).
        const selChannels = Array.isArray(campaign.selectedChannels) ? campaign.selectedChannels : [];
        const hasWhatsapp = explicitChannels.includes('whatsapp') || selChannels.includes('whatsapp')
            || !!wa.name || (campaign.whatsappStats?.sentCount > 0);
        const hasEmail = explicitChannels.includes('email') || selChannels.includes('email')
            || !!campaign.emailTemplate
            || !!campaign.emailSubject
            || !!campaign.emailMessage
            || (campaign.emailCount > 0) || (campaign.emailStats?.sentCount > 0);
        const hasSms = explicitChannels.includes('sms') || selChannels.includes('sms') || selChannels.includes('mobile')
            || !!campaign.smsTemplate || (campaign.mobileCount > 0) || (campaign.smsStats?.sentCount > 0);

        const subject = substitute(campaign.emailSubject || campaign.formTitle || campaign.name || '');
        // customMessage is the source-language body. For bilingual campaigns
        // the rendered text lives in translatedMessage (single string with a
        // language separator) or translatedMessages (per-language map). Fall
        // back across all three so older / multilingual campaigns still show
        // something instead of "No message content recorded".
        const message =
            campaign.customMessage
            || campaign.emailMessage
            || campaign.translatedMessage
            || (campaign.translatedMessages ? Object.values(campaign.translatedMessages).filter(Boolean).join('\n\n') : '')
            || '';
        const renderedBody = renderBody(message);
        const rawBody = escapeHtml(message);

        // The WhatsApp body actually sent isn't stored — it's rebuilt from
        // customMessage + event title at template-creation time (mirror of
        // server/services/campaign-template.service.js). Reconstruct here so
        // the preview shows what guests received, not an empty box.
        const waLanguageMode = campaign.campaignLanguageMode || 'single';
        const waLanguages = (campaign.campaignLanguages && campaign.campaignLanguages.length)
            ? campaign.campaignLanguages
            : [campaign.sourceLanguage || 'en'];
        const waCustomMessage = (campaign.customMessage && campaign.customMessage.trim())
            ? campaign.customMessage
            : 'You are invited to our event!';
        // headerText tri-state: null/undefined → use eventTitle/formTitle default; '' → hide; string → custom
        const waDisplayTitle = (campaign.headerText !== undefined && campaign.headerText !== null)
            ? campaign.headerText
            : (campaign.eventTitle || campaign.formTitle || '');
        const waTitleHeader = waDisplayTitle ? `*${waDisplayTitle}*\n\n` : '';
        let waRawTemplate = `${waTitleHeader}${waCustomMessage}`;
        if (waLanguageMode === 'bilingual' && waLanguages[1] && campaign.translatedMessage) {
            waRawTemplate = `${waTitleHeader}${waCustomMessage}\n\n─────────\n\n${campaign.translatedMessage}`;
        }
        // Render WhatsApp's *bold* markdown so the preview matches what the
        // recipient actually sees in the chat (asterisks become bold spans).
        const waBodyHtml = escapeHtml(substitute(waRawTemplate))
            .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');

        // ── Unified message preview ──────────────────────────────────────
        // One clean chat-style window instead of stacked Email/WhatsApp/SMS
        // blocks. The body is the same content across channels, so it's shown
        // ONCE (bilingual campaigns append the translated copy). Internal Meta
        // template names and the "View raw template" drawer are deliberately
        // not rendered. The RSVP link is mocked BOTH ways: a tappable button
        // (how WhatsApp shows it) and a plain text link (SMS / email).
        let unifiedSource = (message && message.trim()) ? message : waCustomMessage;
        if (waLanguageMode === 'bilingual' && waLanguages[1] && campaign.translatedMessage) {
            unifiedSource = `${unifiedSource}\n\n─────────\n\n${campaign.translatedMessage}`;
        }
        const unifiedBodyHtml = escapeHtml(substitute(unifiedSource))
            .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br>');
        const unifiedTitle = waDisplayTitle ? escapeHtml(substitute(waDisplayTitle)) : '';

        const smsLinkUrl = campaign.customUrl || campaign.rsvpWebsiteUrl || campaign.formUrl || '';
        const smsIncludeLink = campaign.includeRsvpLink !== false && !!smsLinkUrl;
        const smsLinkLabel = (campaign.bulkLinkType === 'website' && campaign.rsvpWebsiteUrl) ? 'Wedding Website' : 'RSVP';
        const showLink = smsIncludeLink && !!smsLinkUrl;

        // CTA buttons (task #157). Reminders keep theirs under reminderConfig;
        // regular campaigns composed with the Buttons & Links section carry them
        // at the root. Mirror the send pipeline: RSVP resolves to the tracked
        // link, the rest to their configured URLs, capped at 2 like WhatsApp/
        // email. Each renders as a CLICKABLE link so the host can test targets.
        const previewButtons = (() => {
            const cfg = campaign.isReminderCampaign ? (campaign.reminderConfig || {}) : campaign;
            const keys = Array.isArray(cfg.buttons)
                ? cfg.buttons
                : (campaign.includeRsvpLink !== false && smsLinkUrl ? ['rsvp'] : []);
            const urls = cfg.buttonUrls || {};
            const labels = cfg.buttonLabels || {};
            // Task #719: these were hard-coded English, so a Spanish campaign
            // previewed "RSVP Here" under Spanish copy while the message that
            // actually went out carried "Confirmar aquí". The caption belongs to
            // the MESSAGE's language, not the dashboard's, and it comes from the
            // same dictionary the wizard preview and the send path read.
            // Bilingual campaigns pair the two through the same 25-character
            // rule Meta enforces, so the preview shows the caption that will
            // survive rather than a pair the send would drop.
            const btnUI = (lang) => (window.BULK_UI_TRANSLATIONS
                && (window.BULK_UI_TRANSLATIONS[lang] || window.BULK_UI_TRANSLATIONS.en)) || {};
            const pUI = btnUI(waLanguages[0] || 'en');
            const sUI = (waLanguageMode === 'bilingual' && waLanguages[1]) ? btnUI(waLanguages[1]) : null;
            const cap = (key, fallback) => (sUI && window.BulkSharingWizard?.pairedCaption)
                ? window.BulkSharingWizard.pairedCaption(key, pUI, sUI, fallback)
                : (pUI[key] || fallback);
            const defLabels = {
                rsvp: smsLinkLabel === 'Wedding Website'
                    ? cap('websiteButton', 'Wedding Website') : cap('rsvpButton', 'RSVP Here'),
                registry: cap('registryButton', 'View Registry'),
                directions: cap('directionsButton', 'Get Directions'),
                // Product names, not words — nobody translates these, and the
                // server hard-codes them for the same reason.
                waze: 'Waze', gmaps: 'Google Maps',
                website: cap('websiteButton', 'Wedding Website'),
                album: cap('albumButton', 'Add Photos'),
                livestream: cap('livestreamButton', 'Watch Livestream')
            };
            return keys.map(k => ({
                key: k,
                label: String(labels[k] || '').trim() || defLabels[k] || k,
                url: k === 'rsvp' ? smsLinkUrl : (urls[k] || (k === 'website' ? (campaign.rsvpWebsiteUrl || '') : ''))
            })).filter(b => b.url).slice(0, 2);
        })();
        const previewHasRsvp = previewButtons.some(b => b.key === 'rsvp');

        const channelChips = [
            hasEmail ? '<span class="chatprev-chip"><i class="bi bi-envelope"></i>Email</span>' : '',
            hasWhatsapp ? '<span class="chatprev-chip"><i class="bi bi-whatsapp"></i>WhatsApp</span>' : '',
            hasSms ? '<span class="chatprev-chip"><i class="bi bi-chat-text"></i>SMS</span>' : ''
        ].filter(Boolean).join('');
        const hasAnyChannel = hasEmail || hasWhatsapp || hasSms;
        const hasBody = !!(unifiedBodyHtml && unifiedBodyHtml.trim());

        // The attached image (campaign or reminder — child reminders inherit
        // the parent's) sends as the media header on WhatsApp and at the top
        // of the email, but the preview never showed it (task #548). Render it
        // the way WhatsApp does: image atop the bubble, text underneath.
        const previewImageUrl = String(campaign.bulkImageUrl || '').trim();

        const html = `
            <div class="modal fade" id="campaignPreviewModal" tabindex="-1">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header">
                            <div>
                                <h5 class="modal-title mb-1">${escapeHtml(this.cTxt('previewTitle'))}</h5>
                                <div class="small text-muted">${escapeHtml(campaign.name || this.cTxt('untitledMessage'))}</div>
                            </div>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body" style="background:#eae6df;">
                            <style>
                                #campaignPreviewModal .chatprev-channels { display:flex; flex-wrap:wrap; gap:6px; justify-content:center; margin-bottom:14px; }
                                #campaignPreviewModal .chatprev-chip { display:inline-flex; align-items:center; gap:5px; font-size:.72rem; font-weight:600; color:#3b4a54; background:#fff; border:1px solid #d7dde0; border-radius:999px; padding:3px 10px; }
                                #campaignPreviewModal .chatprev-chip .bi { font-size:.9em; }
                                #campaignPreviewModal .chatprev-stage { max-width:430px; margin:0 auto; }
                                #campaignPreviewModal .chatprev-bubble { position:relative; background:#fff; border-radius:10px; border-top-left-radius:2px; padding:11px 13px 8px; box-shadow:0 1px 1px rgba(0,0,0,.13); }
                                #campaignPreviewModal .chatprev-bubble::after { content:''; position:absolute; top:0; left:-8px; border-width:0 9px 9px 0; border-style:solid; border-color:transparent #fff transparent transparent; }
                                #campaignPreviewModal .chatprev-img { display:block; width:100%; max-height:260px; object-fit:cover; border-radius:8px; margin-bottom:9px; }
                                #campaignPreviewModal .chatprev-msg-title { font-weight:700; color:#111; margin-bottom:6px; }
                                #campaignPreviewModal .chatprev-msg-body { color:#1f2937; line-height:1.5; white-space:normal; word-break:break-word; }
                                #campaignPreviewModal .chatprev-cta { margin-top:10px; padding-top:9px; border-top:1px solid #eef0f2; text-align:center; color:#0a7cff; font-weight:600; cursor:pointer; }
                                #campaignPreviewModal .chatprev-cta:hover { color:#0868d8; text-decoration:underline !important; }
                                #campaignPreviewModal .chatprev-cta .bi { margin-right:6px; }
                                #campaignPreviewModal .chatprev-linknote { margin-top:8px; font-size:.72rem; color:#6b7280; line-height:1.45; }
                                #campaignPreviewModal .chatprev-textlink { display:inline-block; word-break:break-all; color:#0a7cff; }
                                #campaignPreviewModal .chatprev-meta { text-align:right; margin-top:2px; color:#9aa7b0; font-size:.72rem; }
                                #campaignPreviewModal .chatprev-meta .bi { color:#53bdeb; }
                                #campaignPreviewModal .chatprev-foot { max-width:430px; margin:13px auto 0; font-size:.72rem; color:#5b6770; text-align:center; }
                            </style>
                            ${hasAnyChannel ? `<div class="chatprev-channels">${channelChips}</div>` : ''}
                            <div class="chatprev-stage">
                                <div class="chatprev-bubble">
                                    ${previewImageUrl ? `<img class="chatprev-img" src="${escapeHtml(previewImageUrl)}" alt="" loading="lazy" onerror="this.remove()">` : ''}
                                    ${unifiedTitle ? `<div class="chatprev-msg-title">${unifiedTitle}</div>` : ''}
                                    <div class="chatprev-msg-body">${hasBody ? unifiedBodyHtml : `<span class="text-muted">${escapeHtml(this.cTxt('previewNoBody'))}</span>`}</div>
                                    ${previewButtons.map(b => `
                                        <a class="chatprev-cta d-block text-decoration-none" href="${escapeHtml(b.url)}" target="_blank" rel="noopener" title="${escapeHtml(b.url)}"><i class="bi bi-box-arrow-up-right"></i>${escapeHtml(b.label)}</a>`).join('')}
                                    ${previewButtons.length ? `
                                        <div class="chatprev-linknote"><i class="bi bi-info-circle me-1"></i>${escapeHtml(this.cTxt(previewButtons.length > 1 ? 'previewNoteMany' : 'previewNoteOne'))}${previewHasRsvp && showLink ? ` ${escapeHtml(this.cTxt('previewSmsNote'))}<br><a class="chatprev-textlink" href="${escapeHtml(smsLinkUrl)}" target="_blank" rel="noopener">${escapeHtml(smsLinkUrl)}</a>` : ''}</div>
                                    ` : ''}
                                    <div class="chatprev-meta"><i class="bi bi-check2-all"></i></div>
                                </div>
                            </div>
                            <div class="chatprev-foot"><i class="bi bi-person-circle me-1"></i>${(() => {
                                // {name} is bolded in place, so each language can
                                // put the guest wherever its grammar wants them.
                                const parts = String(this.cTxt('previewSampleFor')).split('{name}');
                                return escapeHtml(parts[0])
                                    + '<strong>' + escapeHtml(sample.name) + '</strong>'
                                    + escapeHtml(parts.slice(1).join(''));
                            })()}</div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${escapeHtml(this.cTxt('close'))}</button>
                            <button type="button" class="btn btn-outline-primary" id="campaignPreviewAddContactsBtn"><i class="bi bi-person-plus me-1"></i>${escapeHtml(this.cTxt('addContacts'))}</button>
                            <button type="button" class="btn btn-primary" id="campaignPreviewCloneBtn"><i class="bi bi-files me-1"></i>${escapeHtml(this.cTxt('previewClone'))}</button>
                        </div>
                    </div>
                </div>
            </div>`;

        document.body.insertAdjacentHTML('beforeend', html);
        const modalEl = document.getElementById('campaignPreviewModal');
        const m = new bootstrap.Modal(modalEl);
        m.show();
        // "Add contacts" is the action that follows this preview "decision":
        // close the preview and open the Add Contacts wizard for THIS campaign.
        modalEl.querySelector('#campaignPreviewAddContactsBtn')?.addEventListener('click', () => {
            m.hide();
            this.showAddContactsModal(campaignId);
        });
        // "Clone message as new draft" opens the campaign wizard pre-filled with
        // this campaign's message / title / link so the host can tweak it and
        // send a fresh one.
        modalEl.querySelector('#campaignPreviewCloneBtn')?.addEventListener('click', () => {
            m.hide();
            this.openCreateCampaignModal({
                customMessage: (campaign.customMessage || message || ''),
                headerText: campaign.headerText,
                emailSenderName: campaign.emailSenderName,
                replyToEmail: campaign.replyToEmail,
                emailLogoUrl: campaign.emailLogoUrl,
                hideEmailLogo: campaign.hideEmailLogo,
                themeColor: campaign.themeColor,
                bulkLinkType: campaign.bulkLinkType,
                customUrl: campaign.customUrl,
                rsvpWebsiteUrl: campaign.rsvpWebsiteUrl
            });
        });
        modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true });
    }

    async showContactStatusModal(campaignId = null, presetFilter = null) {
        const modalEl = document.getElementById('campaignsContactStatusModal');
        if (!modalEl) return;

        // Single modal instance to prevent backdrop issues
        this.contactStatusModalInstance = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);

        if (!modalEl.hasAttribute('data-cleanup-listener')) {
            modalEl.setAttribute('data-cleanup-listener', 'true');
            modalEl.addEventListener('hidden.bs.modal', () => {
                document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
                document.body.classList.remove('modal-open');
                document.body.style.removeProperty('overflow');
                document.body.style.removeProperty('padding-right');
            });
        }

        // Reset UI
        const tableBody = document.getElementById('contactStatusTableBody');
        const loading = document.getElementById('contactStatusLoading');
        const empty = document.getElementById('contactStatusEmpty');
        const tableWrapper = document.getElementById('contactStatusTableWrapper');
        const failedBuckets = document.getElementById('contactStatusFailedBuckets');

        if (tableBody) tableBody.innerHTML = '';
        if (loading) loading.style.display = 'block';
        if (empty) empty.style.display = 'none';
        if (tableWrapper) tableWrapper.style.display = 'none';
        if (failedBuckets) failedBuckets.style.display = 'none';

        // Apply a caller-supplied response/delivery filter (e.g. the "Not reached"
        // donut deep-link) so the list opens already narrowed to the right people.
        // Set it before the data loads — filterContactStatus() reads this element
        // at the end of the load, so the preset is picked up with no re-render race.
        if (presetFilter) {
            const presetEl = document.getElementById('contactStatusFilter');
            if (presetEl) presetEl.value = presetFilter;
        }
        // A tag picked on an earlier opening must not narrow this one unseen.
        const tagSel = document.getElementById('contactStatusTagFilter');
        if (tagSel) tagSel.value = '';

        // Populate campaign dropdown
        const campaignDropdown = document.getElementById('contactStatusCampaignFilter');
        if (campaignDropdown) {
            // The "all messages" option keeps the page's own, translated text
            // (it read "All Campaigns" over every language; review 2026-10-07).
            const allOpt = document.createElement('option');
            allOpt.value = 'all';
            allOpt.textContent = this._statusHtmlText(campaignDropdown.querySelector('option[value="all"]'), 'statusAllMessages');
            campaignDropdown.innerHTML = '';
            campaignDropdown.appendChild(allOpt);
            // Task #877: a "Personal links" card is never SENT (sentCount 0,
            // status manual_links), so it was missing here - and Status on that
            // card then set the dropdown to a value it did not have: the box
            // went blank and the window fell back to every message. Those cards
            // are listed now, and so, always, is the message Status came from.
            const sentCampaigns = (this.campaigns || []).filter(c => {
                const s = c.status || '';
                return ['sent', 'completed', 'processed', 'partially_sent', 'manual_links'].includes(s)
                    || c.manualLinks === true || c.sentCount > 0 || c.id === campaignId;
            });
            sentCampaigns.forEach(c => {
                const opt = document.createElement('option');
                opt.value = c.id;
                const d = c.createdAt?.toDate?.() || new Date(c.createdAt);
                // "Personal links · Oct 6" already carries its day.
                const rem = this._standaloneReminderTitle(c);
                const label = rem.autoName ? this.cTxt('remTitleAudience', { audience: rem.audience })
                    : `${c.name || this.cTxt('untitledMessage')}${rem.audience ? ` · ${rem.audience}` : ''}`;
                opt.textContent = c.manualAutoName
                    ? (c.name || this.cTxt('personalLinks'))
                    : `${label} (${d.toLocaleDateString(this._dateLocale(), { month: 'short', day: 'numeric' })})`;
                campaignDropdown.appendChild(opt);
            });
            // Pre-select if a specific campaignId was passed
            if (campaignId) campaignDropdown.value = campaignId;
        }

        // Update modal title and subtitle
        const titleEl = document.getElementById('contactStatusModalTitleText');
        const subtitleEl = document.getElementById('contactStatusModalSubtitle');
        if (campaignId) {
            const campaign = (this.campaigns || []).find(c => c.id === campaignId);
            // Before the page text is replaced, keep it for the all-messages view.
            this._statusHtmlText(titleEl);
            this._statusHtmlText(subtitleEl);
            if (titleEl) titleEl.textContent = campaign?.name || this.cTxt('statusTitleFallback');
            if (subtitleEl) {
                // Note post-launch additions so the drill-down list doesn't look
                // like the original send was simply bigger than the host remembers.
                const adds = Array.isArray(campaign?.additionsHistory) ? campaign.additionsHistory : [];
                const addedTotal = adds.reduce((n, a) => n + (a.processedFromQueue ? 0 : (a.contactCount || 0)), 0);
                subtitleEl.textContent = this.cTxt('statusThisMessageOnly')
                    + (addedTotal > 0 ? ` · ${this.cCount('statusAddedAfterLaunch', addedTotal)}` : '');
            }
        } else {
            if (titleEl) titleEl.textContent = this._statusHtmlText(titleEl, 'statusAllTitle');
            if (subtitleEl) subtitleEl.textContent = this._statusHtmlText(subtitleEl, 'statusAllSubtitle');
        }

        this.contactStatusModalInstance.show();

        this._hideContactStatusLoadError();

        try {
            // Single source of truth: the message-tracking endpoint.
            // It now returns `responded` and `rsvpStatus` ('yes'|'no'|'maybe'|'responded'|'pending')
            // computed against the rsvp_forms responses collection — no client-side enrichment needed
            // for response status. We only derive delivery-failure flags here.
            //
            // Only a real answer may draw the list. A 401/429/500 (and a dropped
            // connection) used to become an empty array, so the window said "No
            // contacts found" about an event with hundreds of guests (campaign
            // audit 2026-10-07).
            // Taken before the fetch, so the list can only be newer than it.
            const retryMarks = this._retryWatermarks();
            const resp = await this._mtFetch(`/api/message-tracking/${this.formId}/contacts`);
            if (!resp.ok) {
                const httpErr = new Error(`contacts ${resp.status}`);
                httpErr.httpStatus = resp.status;
                throw httpErr;
            }
            const trackingData = ((await resp.json()) || {}).contacts || [];
            this._contactStatusLoadFailed = false;
            this._statusRetryMarks = retryMarks;

            this.contactStatusData = trackingData.map(contact => {
                // SMS fallback = WA failed AND an SMS was attempted (success or failure).
                // We track both so the UI can show "SMS Sent" or "SMS Failed" honestly,
                // instead of leaving the row labelled with the underlying WA error
                // (e.g. "marketing limit") when SMS actually went out or hard-failed.
                const fallbackFromWhatsApp = typeof contact.fallbackFrom === 'string'
                    && contact.fallbackFrom.startsWith('whatsapp');
                const smsSent = !!contact.smsStatus?.sent;
                const smsFailed = !!(contact.smsStatus?.error || contact.smsStatus?.bounced);
                const smsFallbackAttempted = (smsSent || smsFailed) && (
                    fallbackFromWhatsApp
                    || (contact.whatsappStatus?.failed)
                );
                const isSMSFallback = smsSent && smsFallbackAttempted;
                // SMS in flight: API accepted, no failure (yet). Treated as a success
                // for badge purposes — Brevo delivery webhooks will update later.
                const smsInFlight = smsSent && !smsFailed;

                // "Failed" = a hard delivery error with no successful delivery on
                // any channel. Deliberately independent of RSVP status (task #154):
                // a guest whose reminder hard-failed must surface under Failed even
                // if they responded earlier — they appear in BOTH their RSVP tab
                // and the Failed tab.
                const whatsappFailed = contact.whatsappStatus?.failed &&
                    !contact.whatsappStatus?.delivered && !contact.whatsappStatus?.read;
                const emailBounced = !!(contact.emailStatus?.bounced || contact.emailStatus?.failed);
                // Email in flight: accepted by the provider, no bounce (yet).
                // Counts as a success for badge purposes exactly as SMS does —
                // a WhatsApp failure that was re-sent by email did reach the
                // guest's inbox, and the host's own call is that a message which
                // went out on ANY channel is not "not delivered" (task #584).
                const emailInFlight = !!contact.emailStatus?.sent && !emailBounced;
                const deliveryFailed = (whatsappFailed || emailBounced || smsFailed) &&
                    !contact.emailStatus?.delivered && !contact.smsStatus?.delivered &&
                    !smsInFlight && !emailInFlight &&
                    !DashboardCampaigns.hostSentCoversFailures(contact);

                const hasAnyFailure = !!(contact.whatsappStatus?.failed || contact.emailStatus?.bounced || contact.emailStatus?.failed ||
                    contact.smsStatus?.error || contact.smsStatus?.bounced);

                const errorCode = DashboardCampaigns.deliveryErrorCode(contact);

                return { ...contact, isSMSFallback, smsFallbackAttempted, deliveryFailed, hasAnyFailure, errorCode };
            });

            // Inject send-time failures (template rejected, etc.) — these have no
            // tracking doc so /api/message-tracking can't see them. Without this the
            // Status list silently omits every guest a batch failed to reach.
            this.contactStatusData = this._mergeSendTimeFailures(this.contactStatusData);
            this._attachRowTags(this.contactStatusData);

            // Clear cached scope filter so a fresh modal open re-stashes the full list
            this._allContactStatusData = null;

            if (loading) loading.style.display = 'none';

            // If the modal was opened for a specific campaign, apply the campaign-scope
            // filter immediately — otherwise the dropdown shows the campaign name but
            // the stats and table show all-campaigns data until the user toggles.
            if (campaignId) {
                this.loadContactsForScope();
            } else {
                this.updateStatCards(this.contactStatusData);
                this.filterContactStatus();
            }

        } catch (error) {
            console.error('Error loading contact status:', error);
            // The spinner element is left intact (it used to be overwritten with
            // an English line, so every later open showed that instead of loading).
            this._contactStatusLoadFailed = true;
            // Drop the previous opening's list too. It may be another message's,
            // and the stat cards, search, filter and CSV export would otherwise
            // keep showing it under this message's title, beside the error.
            this.contactStatusData = [];
            this._allContactStatusData = null;
            this.failedBuckets = null;
            ['csStat-all', 'csStat-yes', 'csStat-no', 'csStat-maybe', 'csStat-pending', 'csStat-failed']
                .forEach((id) => { const el = document.getElementById(id); if (el) el.textContent = '—'; });
            const countBadge = document.getElementById('contactStatusCount');
            if (countBadge) countBadge.textContent = '';
            if (loading) loading.style.display = 'none';
            this._showContactStatusLoadError(error && error.httpStatus,
                () => this.showContactStatusModal(campaignId, presetFilter));
        }
    }

    /**
     * The Status window's all-messages heading, subtitle and dropdown option
     * are translated in each dashboard's own HTML. This code used to write
     * English over them on every open ("Contact Details", "All Campaigns"),
     * so the Hebrew heading was never seen (code review 2026-10-07). The
     * page's text is remembered the first time an element is seen, before
     * anything replaces it, and handed back for the all-messages view;
     * `fallbackKey` covers a page without that element.
     */
    _statusHtmlText(el, fallbackKey) {
        if (!this._statusDefaults) this._statusDefaults = new WeakMap();
        if (el && typeof el === 'object') {
            // Remembered even when empty, so text this code writes later is
            // never mistaken for the page's own.
            if (!this._statusDefaults.has(el)) {
                this._statusDefaults.set(el, String(el.textContent || '').replace(/\s+/g, ' ').trim());
            }
            const text = this._statusDefaults.get(el);
            if (text) return text;
        }
        return fallbackKey ? this.cTxt(fallbackKey) : '';
    }

    /**
     * Status window load failure: say so, in the dashboard's language, with a
     * way forward. A 401 means the session is gone (AuthedFetch fails every
     * later call without trying), so its button reloads the page instead.
     */
    _showContactStatusLoadError(httpStatus, onRetry) {
        const loading = document.getElementById('contactStatusLoading');
        if (!loading || !loading.parentNode) return;
        let box = document.getElementById('contactStatusLoadError');
        if (!box) {
            box = document.createElement('div');
            box.id = 'contactStatusLoadError';
            box.className = 'text-center py-4';
            box.setAttribute('role', 'alert');
            loading.parentNode.insertBefore(box, loading.nextSibling);
        }
        const expired = httpStatus === 401;
        box.innerHTML = `
            <i class="bi bi-exclamation-triangle text-warning" style="font-size: 2rem;"></i>
            <p class="mt-2 mb-3 text-muted">${this.cTxt(expired ? 'statusLoadExpired' : 'statusLoadFailed')}</p>
            <button type="button" class="btn btn-outline-primary btn-sm">
                <i class="bi bi-arrow-clockwise" style="margin-inline-end: .25rem;"></i>${this.cTxt(expired ? 'statusLoadReload' : 'statusLoadRetry')}
            </button>`;
        box.querySelector('button').addEventListener('click', () => {
            if (expired) { window.location.reload(); return; }
            onRetry();
        });
        box.style.display = 'block';
    }

    _hideContactStatusLoadError() {
        const box = document.getElementById('contactStatusLoadError');
        if (box) box.style.display = 'none';
    }


    // Re-fetch contacts when campaign scope dropdown changes
    async loadContactsForScope() {
        const campaignScope = document.getElementById('contactStatusCampaignFilter')?.value || 'all';
        // Nothing loaded to filter — the list in memory is from an earlier
        // opening, if any. Load again for the scope just picked.
        if (this._contactStatusLoadFailed) {
            return this.showContactStatusModal(campaignScope === 'all' ? null : campaignScope);
        }
        const loading = document.getElementById('contactStatusLoading');
        const tableWrapper = document.getElementById('contactStatusTableWrapper');
        const empty = document.getElementById('contactStatusEmpty');
        const failedBuckets = document.getElementById('contactStatusFailedBuckets');

        if (loading) loading.style.display = 'block';
        if (tableWrapper) tableWrapper.style.display = 'none';
        if (empty) empty.style.display = 'none';
        if (failedBuckets) failedBuckets.style.display = 'none';

        const titleEl = document.getElementById('contactStatusModalTitleText');
        const subtitleEl = document.getElementById('contactStatusModalSubtitle');

        // For 'all' scope, just reuse the already Firestore-enriched data — no re-fetch needed
        // Stash the unfiltered list once so we can switch scopes without re-fetching
        if (!this._allContactStatusData && Array.isArray(this.contactStatusData)) {
            this._allContactStatusData = this.contactStatusData;
        }
        const allData = this._allContactStatusData || [];

        if (campaignScope === 'all') {
            if (titleEl) titleEl.textContent = this._statusHtmlText(titleEl, 'statusAllTitle');
            if (subtitleEl) subtitleEl.textContent = this._statusHtmlText(subtitleEl, 'statusAllSubtitle');
            this.contactStatusData = allData;
            if (loading) loading.style.display = 'none';
            this.updateStatCards(this.contactStatusData);
            this.filterContactStatus();
            return;
        }

        // Per-campaign view: filter the already-loaded global data by campaignIds.
        // Single source of truth, no second API call, no duplicated enrichment logic.
        const campaign = (this.campaigns || []).find(c => c.id === campaignScope);
        this._statusHtmlText(titleEl);
        this._statusHtmlText(subtitleEl);
        if (titleEl) titleEl.textContent = campaign?.name || this.cTxt('statusTitleFallback');
        if (subtitleEl) subtitleEl.textContent = this.cTxt('statusThisMessageOnly');

        this.contactStatusData = allData
            .filter(c => Array.isArray(c.campaignIds) && c.campaignIds.includes(campaignScope))
            .map(c => ({
                ...c,
                // Scoped to ONE message, "failed" has to mean "THIS message did
                // not reach them" — the same per-message judgement the campaign
                // card counts (failedCampaignIds, task #370). The flag these rows
                // otherwise carry is the contact's LATEST state across every
                // message, so the two surfaces drifted apart in both directions:
                // a later reminder getting through cleared a failure the card
                // still counted, and a message that hard-failed showed under
                // Failed while its own card said 0 not delivered (task #584).
                deliveryFailed: (this._fallbackIsAlive(c, campaignScope)
                    || (c.hostSentCampaignIds || []).includes(campaignScope))
                    ? false
                    : (Array.isArray(c.failedCampaignIds)
                        ? (c.failedCampaignIds.includes(campaignScope)
                            || (c._sendTimeFailedCampaigns || []).includes(campaignScope))
                        : !!c.deliveryFailed)
            }));

        this.updateStatCards(this.contactStatusData);
        if (loading) loading.style.display = 'none';
        this.filterContactStatus();
    }


    // In the Failed view: a delivery failure, or, scoped to one message, a
    // guest it held back on purpose. The card's "N not delivered" counts
    // both and opens this view (task #1039).
    _inFailedView(c, scope) {
        if (c.deliveryFailed) return true;
        return !!scope && scope !== 'all' && !!(c.wasSkipped || (c._skippedCampaigns || []).includes(scope));
    }

    updateStatCards(contacts) {
        const yes = contacts.filter(c => c.rsvpStatus === 'yes').length;
        const no = contacts.filter(c => c.rsvpStatus === 'no').length;
        const maybe = contacts.filter(c => c.rsvpStatus === 'maybe').length;
        const pending = contacts.filter(c => !c.responded).length;
        const scope = document.getElementById('contactStatusCampaignFilter')?.value || 'all';
        const failed = contacts.filter(c => this._inFailedView(c, scope)).length;

        const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
        set('csStat-all', contacts.length);
        set('csStat-yes', yes);
        set('csStat-no', no);
        set('csStat-maybe', maybe);
        set('csStat-pending', pending);
        set('csStat-failed', failed);
        this._renderTagFilter(contacts);
    }

    // The modal's active view — search + status filter applied to the current
    // scope — computed in ONE place so the table and the CSV export (task #505)
    // can never disagree about which rows are "showing".
    _computeFilteredContactStatus() {
        const search = (document.getElementById('contactStatusSearch')?.value || '').trim().toLowerCase();
        const filterValue = document.getElementById('contactStatusFilter')?.value || 'all';

        let filtered = this.contactStatusData || [];

        // Apply search
        if (search) {
            filtered = filtered.filter(c =>
                (c.name || '').toLowerCase().includes(search) ||
                (c.email || '').toLowerCase().includes(search) ||
                (c.phone || '').includes(search) ||
                (c.tags || []).some(t => String(t).toLowerCase().includes(search))
            );
        }

        // Tag filter: one tag's guests, or the guests with none.
        const tagValue = document.getElementById('contactStatusTagFilter')?.value || '';
        if (tagValue === '__none__') {
            filtered = filtered.filter(c => !(c.tags || []).length);
        } else if (tagValue) {
            filtered = filtered.filter(c => (c.tags || []).some(t => DashboardCampaigns._tagKey(t) === tagValue));
        }

        // Apply status filter
        switch (filterValue) {
            case 'responded':
                filtered = filtered.filter(c => c.responded);
                break;
            case 'responded-yes':
                filtered = filtered.filter(c => c.rsvpStatus === 'yes');
                break;
            case 'responded-no':
                filtered = filtered.filter(c => c.rsvpStatus === 'no');
                break;
            case 'responded-maybe':
                filtered = filtered.filter(c => c.rsvpStatus === 'maybe');
                break;
            case 'pending':
                filtered = filtered.filter(c => !c.responded);
                break;
            case 'failed': {
                const scope = document.getElementById('contactStatusCampaignFilter')?.value || 'all';
                filtered = filtered.filter(c => this._inFailedView(c, scope));
                break;
            }
            case 'not-delivered': {
                // "Who never got it": no delivery confirmation on ANY channel and
                // no response. Superset of Failed — also catches messages stuck at
                // "sent" with no carrier receipt (the jmkewedding "88 not reached"
                // ask, which previously had no matching filter).
                //
                // Scoped to one message, every guest it skipped stays in, even
                // one who replied through another message: the card's "N
                // skipped" opens this view and must list N rows (a skipped row
                // carries the reply from elsewhere). Across all messages the
                // rule is unchanged, so the donut's "Not reached" still matches.
                const scope = document.getElementById('contactStatusCampaignFilter')?.value || 'all';
                const keepSkipped = scope !== 'all';
                filtered = filtered.filter(c =>
                    (keepSkipped && (c.wasSkipped || (c._skippedCampaigns || []).includes(scope))) || (
                        !c.responded &&
                        !(c.whatsappStatus?.delivered || c.whatsappStatus?.read ||
                            c.smsStatus?.delivered ||
                            c.emailStatus?.delivered || c.emailStatus?.opened || c.emailStatus?.clicked))
                );
                break;
            }
            case 'delivered':
                filtered = filtered.filter(c =>
                    (c.whatsappStatus?.delivered || c.smsStatus?.delivered || c.emailStatus?.delivered) &&
                    !c.responded
                );
                break;
            case 'read':
                filtered = filtered.filter(c =>
                    (c.whatsappStatus?.read || c.emailStatus?.opened || c.linkClicked) &&
                    !c.responded
                );
                break;
            case 'double':
                filtered = filtered.filter(c => c.doubleResponse);
                break;
        }

        return { filtered, filterValue, tagValue };
    }

    /**
     * The tag filter beside the status filter: every tag the guests in this
     * scope carry, with how many have it, plus "No tag". Picking one lists
     * that group, which is how a host checks who will see a tagged event on
     * the wedding site or a tagged part of the form. Hidden while no guest in
     * the scope has a tag. Injected so the seven dashboard.html files need no
     * edit.
     */
    _renderTagFilter(contacts) {
        const statusSel = document.getElementById('contactStatusFilter');
        if (!statusSel) return;
        let sel = document.getElementById('contactStatusTagFilter');
        if (!sel) {
            sel = document.createElement('select');
            sel.id = 'contactStatusTagFilter';
            sel.className = 'form-select form-select-sm';
            sel.style.width = 'auto';
            sel.setAttribute('aria-label', this.cTxt('statusTagsLine'));
            statusSel.insertAdjacentElement('afterend', sel);
            sel.addEventListener('change', () => this.filterContactStatus());
        }
        const counts = new Map();
        let untagged = 0;
        (contacts || []).forEach((c) => {
            const tags = c.tags || [];
            if (!tags.length) { untagged++; return; }
            tags.forEach((t) => {
                const k = DashboardCampaigns._tagKey(t);
                const e = counts.get(k) || { label: t, n: 0 };
                e.n++;
                counts.set(k, e);
            });
        });
        const table = document.getElementById('contactStatusTable');
        if (table) table.classList.toggle('cs-has-tags', counts.size > 0);
        const prev = sel.value;
        sel.innerHTML = '';
        const add = (value, text) => {
            const o = document.createElement('option');
            o.value = value;
            o.textContent = text;
            sel.appendChild(o);
        };
        add('', this.cTxt('statusAllTags'));
        [...counts.entries()]
            .sort((a, b) => (b[1].n - a[1].n) || a[1].label.localeCompare(b[1].label))
            .forEach(([k, e]) => add(k, `${e.label} (${e.n})`));
        if (counts.size && untagged) add('__none__', `${this.cTxt('statusNoTag')} (${untagged})`);
        sel.value = [...sel.options].some(o => o.value === prev) ? prev : '';
        sel.classList.toggle('d-none', counts.size === 0);
    }

    filterContactStatus() {
        // Nothing loaded: a search, filter or stat-card click must not draw an
        // empty "No contacts found" table under the load error.
        if (this._contactStatusLoadFailed) return;
        const { filtered, filterValue } = this._computeFilteredContactStatus();

        // Show/hide failed buckets panel
        const failedBuckets = document.getElementById('contactStatusFailedBuckets');
        if (filterValue === 'failed' && filtered.length > 0 && failedBuckets) {
            failedBuckets.style.display = 'block';
            this.renderFailedBuckets(filtered);
        } else if (failedBuckets) {
            failedBuckets.style.display = 'none';
        }

        // Update count badge
        const countBadge = document.getElementById('contactStatusCount');
        if (countBadge) countBadge.textContent = this.cCount('statusContactCount', filtered.length);

        const empty = document.getElementById('contactStatusEmpty');
        const tableWrapper = document.getElementById('contactStatusTableWrapper');

        if (filtered.length === 0) {
            if (empty) empty.style.display = 'block';
            if (tableWrapper) tableWrapper.style.display = 'none';
        } else {
            if (empty) empty.style.display = 'none';
            if (tableWrapper) tableWrapper.style.display = 'block';
            this.renderContactStatusTable(filtered);
        }
    }

    // Did THIS message's fallback attempt itself get out cleanly? A WhatsApp
    // failure re-sent by email only stops being a failure if the email was
    // accepted and hasn't bounced — if the fallback failed too, the message
    // really did not arrive and the row stays red.
    _fallbackIsAlive(contact, campaignId) {
        const channel = (contact && contact.fallbackByCampaign || {})[campaignId];
        if (!channel) return false;
        const st = channel === 'sms' ? contact.smsStatus : contact.emailStatus;
        return !!st?.sent && !st?.bounced && !st?.error;
    }

    // Which channel a message to this contact FELL BACK to, for the view the
    // host is currently looking at (task #584).
    //
    // A WhatsApp send that hard-fails is immediately retried by email or SMS.
    // The row said a bare "Failed" for a message that actually arrived — the
    // host's words: "it just says Failed even though it went out by email".
    // Scoped to one message when the modal is, so a fallback on a DIFFERENT
    // message never colours this row; across all messages it only speaks up
    // when every fallback used the same channel, since there is no one answer
    // otherwise.
    _fallbackChannelFor(contact) {
        const map = contact && contact.fallbackByCampaign;
        if (!map) return null;
        const scope = document.getElementById('contactStatusCampaignFilter')?.value || 'all';
        if (scope && scope !== 'all') {
            return this._fallbackIsAlive(contact, scope) ? map[scope] : null;
        }
        const channels = [...new Set(
            Object.keys(map).filter(id => this._fallbackIsAlive(contact, id)).map(id => map[id])
        )];
        return channels.length === 1 ? channels[0] : null;
    }

    // Why the fallback went out, when WhatsApp was skipped rather than tried
    // ('previously_not_on_whatsapp' | 'meta_throttle_cooldown'), or null.
    // Same scope rule as _fallbackChannelFor: across all messages it answers
    // only when every live fallback agrees.
    _fallbackReasonFor(contact) {
        const map = (contact && contact.fallbackByCampaign) || {};
        const reasons = (contact && contact.fallbackReasonByCampaign) || {};
        const scope = document.getElementById('contactStatusCampaignFilter')?.value || 'all';
        const ids = (scope && scope !== 'all') ? [scope] : Object.keys(map);
        const live = ids.filter(id => this._fallbackIsAlive(contact, id));
        const found = new Set(live.map(id => reasons[id] || null));
        return found.size === 1 ? [...found][0] : null;
    }

    // The CAMPAIGN_I18N key the CSV export words that channel with: the same
    // pair (fallbackEmail/fallbackSms) the Status badge shows.
    static fallbackLabel(channel) {
        return channel === 'sms' ? 'fallbackSms' : 'fallbackEmail';
    }

    // What a delivery badge means, on hover and on TAP: tabindex lets a phone
    // focus it, and .js-hint-tip upgrades the title to a real tooltip (the
    // delegation at the end of this file). Hosts read "Delivered" as "in their
    // inbox" and an email open as proof it was read (chat 4d5262e0), so each
    // badge says how sure it is. No role: it does nothing when pressed, and its
    // visible label is already its name.
    _badgeTip(key) {
        return this._tipAttrs(this.cTxt(key));
    }

    // The same for a badge whose note is built text (a failure reason, why a
    // guest was skipped): those were hover-only, so on a phone the reason a
    // host most needed was the one they could not reach. Escaped once here;
    // pair with class js-hint-tip.
    _tipAttrs(text, style = '') {
        if (!text) return style ? ` style="${style}"` : '';
        return ` title="${DashboardCampaigns.escapeAttr(text)}" tabindex="0" style="cursor:help;${style}"`;
    }

    // Delivery status of one contact, as the CAMPAIGN_I18N key of its label —
    // the SAME priority order the table badge uses, so the exported file never
    // disagrees with the screen. The export sorts on these keys and words them
    // in the dashboard language only when it writes the cell; its sort map was
    // keyed on English words. The one vocabulary bridge: an accepted send with
    // no carrier receipt yet (the badge's "Sent") exports as "Pending" — task
    // #505's word for in-flight.
    _contactDeliveryStatus(c) {
        if (c.deliveryFailed) return 'dlvFailed';
        const fb = this._fallbackChannelFor(c);
        if (fb) return DashboardCampaigns.fallbackLabel(fb);
        // An email open is "Opened", never "Read": Apple Mail loads it on
        // arrival and Outlook often never reports one, so it is weaker than a
        // WhatsApp read receipt. A clicked link outranks both (chat 4d5262e0).
        if (c.linkClicked || c.emailStatus?.clicked) return 'dlvClicked';
        if (c.whatsappStatus?.read) return 'dlvRead';
        if (c.emailStatus?.opened) return 'dlvOpened';
        if (c.whatsappStatus?.delivered || c.smsStatus?.delivered || c.emailStatus?.delivered) return 'dlvDelivered';
        if (c.whatsappStatus?.sent || c.smsStatus?.sent || c.emailStatus?.sent) return 'csvPending';
        return 'csvNotSent';
    }

    /**
     * Task #505 — download the contact list the host is looking at as a CSV.
     *
     * Exports the modal's CURRENT view: campaign scope, status filter and
     * search all apply, so the Failed view exports exactly the failed rows.
     * On the unfiltered view the rows are grouped by delivery status
     * (Pending → Failed → Read → Delivered) so the statuses arrive separated,
     * ready to scan or pivot. Phones are exported raw (with their +country
     * code) so the file re-imports cleanly as a follow-up campaign list.
     */
    exportContactStatusCsv() {
        if (this._contactStatusLoadFailed) {
            if (typeof showToast === 'function') showToast(this.cTxt('statusLoadFailed'), 'warning');
            return;
        }
        const { filtered, filterValue, tagValue } = this._computeFilteredContactStatus();
        if (!filtered.length) {
            if (typeof showToast === 'function') showToast(this.cTxt('exportNone'), 'warning');
            return;
        }

        const rows = filtered.map(c => ({ c, status: this._contactDeliveryStatus(c) }));
        if (filterValue === 'all') {
            // A fallback sorts with the delivered rows it was (it was reached).
            const ORDER = { csvPending: 0, dlvFailed: 1, dlvClicked: 2, dlvRead: 2, dlvOpened: 2, dlvDelivered: 3, fallbackEmail: 3, fallbackSms: 3, csvNotSent: 4 };
            rows.sort((a, b) => (ORDER[a.status] - ORDER[b.status])
                || String(a.c.name || '').localeCompare(String(b.c.name || '')));
        }

        const esc = (v) => {
            const s = String(v == null ? '' : v);
            return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
        };
        const rsvpKey = { yes: 'csvAttending', no: 'csvDeclined', maybe: 'csvMaybe' };
        // Tags column only when someone has a tag, so untagged events export as before.
        const withTags = filtered.some(c => (c.tags || []).length);
        const fmtTags = (tags) => (window.FileHelpers ? window.FileHelpers.formatContactTags(tags) : (tags || []).join(', '));
        // Guest name(s) | Group name, as every guest list now reads (task #1042
        // r3): everyone on the invitation in one cell, then who it is addressed to.
        const nameCols = (c) => {
            const H = window.HouseholdNames, F = window.FileHelpers;
            const contact = { name: c.name, groupName: c.groupName, members: (c.members || []).map(m => (typeof m === 'string' ? { name: m } : m)) };
            const cols = H && H.guestListColumns ? H.guestListColumns(contact) : null;
            return [cols && F && F.formatGuestNames ? F.formatGuestNames(cols.people) : (c.name || ''), (cols && cols.groupName) || c.groupName || ''];
        };
        const lines = [['acGuestNames', 'acGroupName', 'csvPhone', 'csvEmail', 'csvDeliveryStatus', 'csvDeliveryIssue', 'csvRsvp', 'csvGuests']
            .concat(withTags ? ['statusTagsLine'] : [])
            .map(k => esc(this.cTxt(k))).join(',')];
        for (const { c, status } of rows) {
            // The issue column names the failure (e.g. "Not on WhatsApp") for
            // failed rows, and also for reached rows that had an earlier send
            // hard-fail — same honesty rule as the table's warning flag.
            const issue = (c.deliveryFailed || c.hadDeliveryFailure) && c.errorCode
                ? this._errLabel(c.errorCode)
                : (c.deliveryFailed ? this.cTxt('deliveryFailed') : '');
            lines.push([
                ...nameCols(c).map(esc), esc(c.phone), esc(c.email),
                esc(this.cTxt(status)),
                esc(issue),
                esc(this.cTxt(c.responded ? (rsvpKey[c.rsvpStatus] || 'csvResponded') : 'csvNoResponse')),
                // Party size of the reply; blank for a decline or no reply.
                esc(c.guestCount || '')
            ].concat(withTags ? [esc(fmtTags(c.tags))] : []).join(','));
        }

        // BOM so Excel opens Hebrew/Arabic names correctly; CRLF per RFC 4180.
        const csv = '﻿' + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

        const scopeEl = document.getElementById('contactStatusCampaignFilter');
        const scopeName = scopeEl && scopeEl.value !== 'all'
            ? ((this.campaigns || []).find(x => x.id === scopeEl.value)?.name || 'campaign')
            : 'all-campaigns';
        const datePart = new Date().toISOString().slice(0, 10);
        const tagPart = tagValue === '__none__' ? '-' + this.cTxt('statusNoTag') : (tagValue ? '-' + tagValue : '');
        const fname = `message-status-${scopeName}${filterValue !== 'all' ? '-' + filterValue : ''}${tagPart}-${datePart}.csv`
            .replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '-');

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        if (typeof showToast === 'function') {
            showToast(this.cTxt(rows.length === 1 ? 'exportedOne' : 'exportedMany', rows.length), 'success');
        }
    }

    renderFailedBuckets(failedContacts) {
        // Classify failed contacts into buckets using existing isRetryableError logic.
        //
        // "Needs a different channel" used to match only 470, which our send
        // path never produces, so it never showed (dashboard review #12,
        // 2026-10-07). 131026 (resending gets through about 1 in 20 —
        // audit-wa-retry-base-rates.js, 6 of 130) and 130472 (Meta's own
        // experiment hold; a resend meets the same hold) sat under "may work
        // if you try again tomorrow" with a Retry button. 131049 stays under
        // Retry: it lifts after 24 hours and about half then get through.
        //
        // Text and email failures have codes of their own since M2
        // (deliveryErrorCode) and fell under Retry too, beside row tips saying
        // "this address doesn't exist" (M2 review). A text that bounced, went
        // to a non-US number or had no English wording gets nowhere by text
        // again (the last two are refusals that repeat,
        // SMS_REFUSALS_THAT_REPEAT): reach them by email. An email address
        // that bounced needs fixing, so it is not reachable by email either.
        // A soft bounce, a provider hiccup (SMS_FAILED / SMS_EXCEPTION /
        // BREVO_REJECTED) stays under Retry.
        const CHANNEL_SWITCH_CODES = DashboardCampaigns.CHANNEL_SWITCH_CODES;
        const EMAIL_DEAD_CODES = ['EMAIL_HARD_BOUNCE', 'EMAIL_BOUNCE'];
        const PERMANENT_CODES = ['131048', '131058', '131059', '131063', ...EMAIL_DEAD_CODES]; // Spam blocked, template issues, permanent

        const retryable = [];
        const needsChannel = [];
        const permanent = [];
        // Held back on purpose (a STOP, an unsubscribe, not on WhatsApp,
        // WhatsApp's pause): in this view since #1039, but never in a group
        // with a Retry or resend action.
        const isHeld = (c) => !c.deliveryFailed && !!(c.wasSkipped || (c._skippedCampaigns || []).length);
        const heldBack = failedContacts.filter(isHeld);
        failedContacts = failedContacts.filter(c => !isHeld(c));

        failedContacts.forEach(c => {
            const code = String(c.errorCode || '');
            if (CHANNEL_SWITCH_CODES.includes(code)) {
                needsChannel.push(c);
            } else if (PERMANENT_CODES.includes(code)) {
                permanent.push(c);
            } else {
                retryable.push(c);
            }
        });

        this.failedBuckets = { retryable, needsChannel, permanent };

        const show = (id, countId, count) => {
            const el = document.getElementById(id);
            const countEl = document.getElementById(countId);
            if (el) el.style.display = count > 0 ? 'flex' : 'none';
            if (countEl) countEl.textContent = count;
        };

        show('failedBucketRetry', 'failedBucketRetryCount', retryable.length);
        this._styleRetryBucket();
        show('failedBucketChannel', 'failedBucketChannelCount', needsChannel.length);
        // The pages' fixed lines under each group, worded for what is in it.
        // Retry: a 131049 guest can only be reached once WhatsApp's 24-hour
        // hold lifts, so say when. Channel: 130472 guests DO use WhatsApp, so
        // "doesn't use WhatsApp" was wrong for them.
        const sub = (id) => document.querySelector(`#${id} .small.text-muted`);
        const setSub = (el, text) => {
            if (!el) return;
            if (el.dataset.pageText == null) el.dataset.pageText = el.textContent;
            el.textContent = text != null ? text : el.dataset.pageText;
        };
        const liftsAt = DashboardCampaigns.throttleLiftsAt(retryable);
        const held = retryable.filter(c => String(c.errorCode || '') === '131049').length;
        setSub(sub('failedBucketRetry'), (liftsAt && liftsAt.getTime() > Date.now())
            ? this.cTxt(held === retryable.length ? 'fbRetryUntilAll' : 'fbRetryUntilSome', { time: this._skipTimeStr(liftsAt) })
            : null);
        setSub(sub('failedBucketChannel'), this.cTxt('fbChannelSub'));
        // The page's line says "number"; a dead email address is not one.
        setSub(sub('failedBucketPermanent'), permanent.some(c => EMAIL_DEAD_CODES.includes(String(c.errorCode || '')))
            ? this.cTxt('fbPermanentSubEmail') : null);
        const permEl = document.getElementById('failedBucketPermanent');
        if (permEl) permEl.style.display = permanent.length > 0 ? 'block' : 'none';
        const permCountEl = document.getElementById('failedBucketPermanentCount');
        if (permCountEl) permCountEl.textContent = permanent.length;

        this._renderHeldBackGroup(heldBack);
        this._renderWaWalkButton(needsChannel);

        // Ghost-template (Meta 132001) recovery CTA — recreate & resend, not a plain retry.
        this._renderGhostRecoveryCTA(failedContacts);
    }

    /**
     * The guests this message held back on purpose, as a group of their own
     * under "What went wrong" (task #1039): how many, and that each row says
     * why. No button: a STOP or an unsubscribe is never re-sent, and the
     * rows' own reasons say what is left to do for the others. Added here
     * rather than in the seven dashboard pages' markup.
     */
    _renderHeldBackGroup(heldBack) {
        let el = document.getElementById('failedBucketHeld');
        const anchor = document.getElementById('failedBucketPermanent');
        if (!el && anchor && anchor.parentNode) {
            el = document.createElement('div');
            el.id = 'failedBucketHeld';
            el.className = 'p-2 mt-1 bg-white rounded border';
            anchor.insertAdjacentElement('afterend', el);
        }
        if (!el) return;
        const n = (heldBack || []).length;
        el.style.display = n ? 'block' : 'none';
        el.innerHTML = n ? `<span class="text-secondary fw-medium"><i class="bi bi-pause-circle me-1"></i>${this._escape(this.cTxt('fbHeldBack', { n }))}</span>
            <div class="small text-muted">${this._escape(this.cTxt('fbHeldBackSub'))}</div>` : '';
    }

    /**
     * "N may work if you try again tomorrow" stands out (task #1039, Esther:
     * the pale yellow line and outline Retry "blend into the background"): a
     * soft amber box, dark amber words and a solid Retry. Styled here rather
     * than in the seven dashboard pages' markup.
     */
    _styleRetryBucket() {
        const row = document.getElementById('failedBucketRetry');
        if (!row || row.dataset.styled) return;
        row.dataset.styled = '1';
        row.classList.remove('bg-white');
        row.style.background = '#fff4d6';
        row.style.borderColor = '#f0c040';
        const title = row.querySelector('.text-warning');
        if (title) { title.classList.remove('text-warning'); title.style.color = '#7a4b00'; }
        const btn = document.getElementById('retryFailedBtn');
        if (btn) { btn.classList.remove('btn-outline-warning'); btn.classList.add('btn-warning', 'fw-semibold'); btn.style.color = '#3d2a00'; }
    }

    /**
     * "Send from my WhatsApp" beside "Remind via Email" in the Need a
     * different channel group: the guests our WhatsApp could not reach
     * (131026, 130472, 130497 Meta's country block…), one at a time from the
     * host's OWN WhatsApp (_waHrefFor). Added here rather than in the seven
     * dashboard pages' markup. Shown only when one of them has a phone and a
     * personal link.
     */
    _renderWaWalkButton(needsChannel) {
        // Not a guest the host already sent this message from their WhatsApp:
        // stamped on the server (_recordHostSent), so a reload, or the phone
        // dropping the page while WhatsApp was open, never restarts the list
        // at guest 1 and sends them a second copy.
        const guests = (needsChannel || []).filter(c => this._waHrefFor(c) && !this._hostSentFor(c));
        this._waWalk = { guests, i: 0, opened: 0 };
        document.getElementById('waWalkPanel')?.remove?.();
        const emailBtn = document.getElementById('switchChannelBtn');
        let btn = document.getElementById('waWalkBtn');
        if (!btn && emailBtn && emailBtn.parentNode) {
            // Both buttons in one wrapping group, so a phone-width row stacks
            // them instead of pushing the text out of the box.
            const wrap = document.createElement('div');
            wrap.className = 'd-flex flex-wrap gap-2 justify-content-end ms-3';
            emailBtn.parentNode.insertBefore(wrap, emailBtn);
            emailBtn.classList.remove('ms-3');
            wrap.appendChild(emailBtn);
            btn = document.createElement('button');
            btn.type = 'button';
            btn.id = 'waWalkBtn';
            // Solid WhatsApp green, like the card's "Send from my WhatsApp (N)" (task #1076):
            // as an outline it read as secondary to "Remind via Email".
            btn.className = 'btn btn-sm btn-whatsapp';
            btn.addEventListener('click', () => this._openWaWalk());
            wrap.appendChild(btn);
        }
        if (!btn) return;
        btn.innerHTML = `<i class="bi bi-whatsapp me-1"></i>${this._escape(this.cTxt('waWalkBtn'))}`;
        btn.style.display = guests.length ? '' : 'none';
    }

    _openWaWalk() {
        if (!this._waWalk || !this._waWalk.guests.length) return;
        this._waWalk.i = 0;
        this._waWalk.opened = 0;
        this._renderWaWalkStep();
    }

    /**
     * One guest at a time, under the group: who, then "Open WhatsApp" (a
     * plain link, so the browser never treats it as a pop-up), "Skip" and
     * "Close". Opening moves on to the next guest; the host sends in WhatsApp
     * and comes back.
     */
    _renderWaWalkStep() {
        const bucket = document.getElementById('failedBucketChannel');
        const w = this._waWalk;
        if (!bucket || !w) return;
        let panel = document.getElementById('waWalkPanel');
        if (!panel) {
            panel = document.createElement('div');
            panel.id = 'waWalkPanel';
            panel.className = 'p-2 mb-1 bg-white rounded border border-success';
            bucket.insertAdjacentElement('afterend', panel);
            if (typeof panel.scrollIntoView === 'function') panel.scrollIntoView({ block: 'nearest' });
        }
        // While the walk is open its own line says what to do, so the group's
        // "N need a different channel" box above it is the same thing twice
        // (task #1039, Esther). Hidden until the walk is closed.
        bucket.dataset.walkHidden = '1';
        bucket.style.setProperty('display', 'none', 'important');
        const n = w.guests.length;
        const btnClose = `<button type="button" class="btn btn-sm btn-link text-muted" data-wa-walk="close">${this._escape(this.cTxt('waWalkClose'))}</button>`;
        if (w.i >= n) {
            panel.innerHTML = `<div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div class="small">${this._escape(this.cTxt('waWalkDone', { k: w.opened, n }))}</div>${btnClose}</div>`;
        } else {
            const g = w.guests[w.i];
            const name = g.name && g.name !== 'Unknown' ? g.name : '';
            // A WhatsApp link carries text only. When the message has a
            // picture, say so and offer it, to save once and attach in
            // WhatsApp (Donny, 2026-10-18). The share sheet could carry it,
            // but makes the host find each guest in WhatsApp, and a number
            // not saved on their phone is not there at all.
            const img = this._waCampaignFor(g)?.bulkImageUrl;
            const imgNote = (typeof img === 'string' && /^https:\/\//.test(img))
                ? `<div class="small mb-2"><i class="bi bi-image me-1"></i>${this._escape(this.cTxt('waWalkImageNote'))}
                    <a href="${DashboardCampaigns.escapeAttr(img)}" target="_blank" rel="noopener">${this._escape(this.cTxt('waWalkImageOpen'))}</a></div>`
                : '';
            panel.innerHTML = `
                <div class="small text-muted mb-2">${this._escape(this.cTxt('waWalkIntro'))}</div>
                ${imgNote}
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                        <div class="small text-muted">${this._escape(this.cTxt('waWalkCounter', { i: w.i + 1, n }))}</div>
                        ${name ? `<div class="fw-medium">${this._escape(name)}</div>` : ''}
                        <div class="small text-muted" dir="ltr">${this._escape(g.phone || '')}</div>
                    </div>
                    <div class="d-flex flex-wrap align-items-center gap-2">
                        <a class="btn btn-sm btn-whatsapp" data-wa-walk="open" target="_blank" rel="noopener"
                           href="${DashboardCampaigns.escapeAttr(this._waHrefFor(g))}"><i class="bi bi-whatsapp me-1"></i>${this._escape(this.cTxt('waWalkOpen'))}</a>
                        <button type="button" class="btn btn-sm btn-outline-secondary" data-wa-walk="skip">${this._escape(this.cTxt('waWalkSkip'))}</button>
                        ${btnClose}
                    </div>
                </div>`;
        }
        panel.onclick = (e) => {
            const el = e.target.closest('[data-wa-walk]');
            if (!el) return;
            const act = el.dataset.waWalk;
            if (act === 'close') {
                panel.remove();
                if (bucket.dataset.walkHidden) { delete bucket.dataset.walkHidden; bucket.style.display = 'flex'; }
                // The guests sent this way leave the Failed list and the card's count.
                if (w.opened > 0) this._refreshAfterHostSend();
                return;
            }
            if (act === 'open') {
                w.opened++;
                this._recordHostSent(w.guests[w.i]);
            }
            w.i++;
            // After the link's own default action, so WhatsApp still opens.
            setTimeout(() => this._renderWaWalkStep(), 0);
        };
    }

    /** Has the host already sent this guest the message we would resend, from their own WhatsApp? */
    _hostSentFor(contact) {
        const campaign = this._waCampaignFor(contact);
        return !!(campaign && Array.isArray(contact.hostSentCampaignIds) && contact.hostSentCampaignIds.includes(campaign.id));
    }

    /**
     * The host just opened their own WhatsApp for this guest: stamp it on the
     * guest's tracking doc for that message (markHostSent), and locally at
     * once. keepalive, because opening WhatsApp on a phone can put this page
     * in the background mid-request. Only when there is a message to tie it
     * to; a link shared from the all-messages view records nothing.
     */
    _recordHostSent(contact) {
        const campaign = contact && this._waCampaignFor(contact);
        const tk = campaign && contact.trackingIdByCampaign && contact.trackingIdByCampaign[campaign.id];
        if (!campaign || !tk) return false;
        const list = Array.isArray(contact.hostSentCampaignIds) ? contact.hostSentCampaignIds : (contact.hostSentCampaignIds = []);
        if (!list.includes(campaign.id)) list.push(campaign.id);
        if (Array.isArray(contact.failedCampaignIds)) contact.failedCampaignIds = contact.failedCampaignIds.filter(id => id !== campaign.id);
        contact.deliveryFailed = false;
        Promise.resolve(this._mtFetch(`/api/message-tracking/${encodeURIComponent(this.formId)}/host-sent`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ trackingId: tk, campaignId: campaign.id })
        })).catch(() => {});
        return true;
    }

    /** Redraw the list and the cards after host sends (the server already has them). */
    _refreshAfterHostSend() {
        try { this.filterContactStatus(); } catch (_) { /* the window may be closed */ }
        Promise.resolve(this.loadMessageTrackingStats && this.loadMessageTrackingStats())
            .then(() => this.renderCampaigns && this.renderCampaigns())
            .catch(() => {});
    }

    renderContactStatusTable(contacts) {
        const tableBody = document.getElementById('contactStatusTableBody');
        if (!tableBody) return;

        // Task #755: name the link column, but only on the Failed tab.
        //
        // Everywhere else the copy icon needs no header — it sits beside the
        // pencil and both self-explain. On Failed it is doing a different job:
        // these messages did not arrive, and the personal link is how the host
        // reaches those guests by hand. Naming the column there is what tells
        // them that is what the icon is for.
        this._syncLinkColumnHeader();

        // Tags column (support 2026-10-19), after Email. Injected like the tag
        // filter; shown only while the scope has a tagged guest (cs-has-tags,
        // set by _renderTagFilter) and folded into the row detail on mobile.
        const emailTh = document.querySelector('#contactStatusTable thead th.cs-col-email');
        if (emailTh && !document.querySelector('#contactStatusTable thead th.cs-col-tags')) {
            const th = document.createElement('th');
            th.className = 'cs-col-tags';
            th.textContent = this.cTxt('statusTagsLine');
            emailTh.insertAdjacentElement('afterend', th);
        }
        const tagChips = (tags) => (tags && tags.length)
            ? tags.map(t => `<span class="badge rounded-pill cs-tag-chip">${this._escape(t)}</span>`).join(' ')
            : '<span class="text-muted">&mdash;</span>';

        // Rows carry data-row-idx that indexes into THIS array — keep a
        // reference so the name/response edit handlers resolve the right
        // contact even when a search or status filter has narrowed the list.
        this._renderedContacts = contacts;

        // #654: computed ONCE per render, not per row — it walks the whole
        // contact list, and doing that inside the map would make a 400-guest
        // table quadratic for a string nearly every row never uses.
        this._dominantCc = DashboardCampaigns.dominantCountryCode(contacts);

        tableBody.innerHTML = contacts.map((contact, idx) => {
            // Single delivery badge — using Bootstrap Icons to avoid encoding issues
            let deliveryBadge = '';

            // A message that hard-failed on WhatsApp but went back out on another
            // channel is NOT a failure to the host — it arrived, just not the way
            // they picked. Named rather than hidden, and short enough not to push
            // the column into the next one (task #584).
            const fallbackChannel = contact.deliveryFailed ? null : this._fallbackChannelFor(contact);
            // The host sent the failed message from their own WhatsApp
            // (_recordHostSent): said instead of the red failure, like a fallback.
            const scopeId = this._currentCampaignScope();
            const hostSentHere = !contact.deliveryFailed
                && (contact.hadDeliveryFailure || contact.whatsappStatus?.failed)
                && (scopeId ? (contact.hostSentCampaignIds || []).includes(scopeId)
                    : DashboardCampaigns.hostSentCoversFailures(contact));

            if (contact.deliveryFailed) {
                const code = contact.errorCode;
                // #654: a mis-shaped number is reported as a data problem, not
                // as a fact about the guest.
                // In the dashboard language (code review 2026-10-07); the CSV
                // export keeps the English labels.
                const described = this._deliveryFailureText(code, contact.phone);
                const label = this._escape(described.label);
                const isRetryable = code ? DashboardCampaigns.isRetryableError(code) : true;
                const icon = isRetryable
                    ? '<i class="bi bi-arrow-repeat"></i>'
                    : '<i class="bi bi-x-circle"></i>';
                // Escaped once: a pre-escaped tip through escapeAttr showed a
                // literal "&amp;" or "&quot;" in the tooltip (wave-2 merge review).
                deliveryBadge = `<span class="badge bg-danger js-hint-tip"${this._tipAttrs(described.tip)}>${icon} ${label}</span>`;
            } else if (hostSentHere) {
                deliveryBadge = `<span class="badge bg-success-subtle text-success-emphasis border border-success-subtle js-hint-tip"${this._tipAttrs(this.cTxt('hostSentTip'), 'white-space:nowrap;')}><i class="bi bi-whatsapp"></i> ${this._escape(this.cTxt('hostSentBadge'))}</span>`;
            } else if (fallbackChannel) {
                const sentByEmail = fallbackChannel === 'email';
                const st = sentByEmail ? contact.emailStatus : contact.smsStatus;
                // Two whole sentences, each worded per language: why it went
                // another way, then how far it got. SMS has no open tracking,
                // so an SMS never reads "opened" (M1 review #6).
                const reached = this.cTxt((sentByEmail && st?.opened) ? 'fbStateOpened'
                    : (st?.delivered || st?.opened) ? 'fbStateDelivered'
                        : 'fbStateOnWay');
                // An email sent INSTEAD of a skipped WhatsApp (PMf): WhatsApp was
                // never tried, so "couldn't deliver" would hide the reason the
                // host needs (a typo'd number, or a paused one).
                const why = sentByEmail ? this._fallbackReasonFor(contact) : null;
                const lead = why === 'previously_not_on_whatsapp' ? 'fbTipNoWa'
                    : why === 'meta_throttle_cooldown' ? 'fbTipCool'
                        : (sentByEmail ? 'fbTipFailedEmail' : 'fbTipFailedSms');
                const tip = `${this.cTxt(lead)} ${reached}`;
                const icon = sentByEmail ? '<i class="bi bi-envelope-fill"></i>' : '<i class="bi bi-phone-fill"></i>';
                deliveryBadge = `<span class="badge bg-warning text-dark js-hint-tip"${this._tipAttrs(tip, 'white-space:nowrap;')}>${icon} ${this.cTxt(sentByEmail ? 'fallbackEmail' : 'fallbackSms')}</span>`;
            } else if (contact.linkClicked || contact.emailStatus?.clicked) {
                deliveryBadge = `<span class="badge bg-success js-hint-tip"${this._badgeTip('dlvClickedTip')}><i class="bi bi-hand-index-thumb-fill"></i> ${this.cTxt('dlvClicked')}</span>`;
            } else if (contact.whatsappStatus?.read) {
                deliveryBadge = `<span class="badge bg-primary js-hint-tip"${this._badgeTip('dlvReadTip')}><i class="bi bi-eye-fill"></i> ${this.cTxt('dlvRead')}</span>`;
            } else if (contact.emailStatus?.opened) {
                deliveryBadge = `<span class="badge bg-primary js-hint-tip"${this._badgeTip('dlvOpenedTip')}><i class="bi bi-envelope-open-fill"></i> ${this.cTxt('dlvOpened')}</span>`;
            } else if (contact.whatsappStatus?.delivered || contact.smsStatus?.delivered || contact.emailStatus?.delivered) {
                // By email, Delivered is the provider accepting it; on a phone it is
                // arrival, and only WhatsApp can later say it was read.
                deliveryBadge = `<span class="badge bg-info text-dark js-hint-tip"${this._badgeTip(contact.whatsappStatus?.delivered ? 'dlvDeliveredWaTip' : contact.smsStatus?.delivered ? 'dlvDeliveredSmsTip' : 'dlvDeliveredEmailTip')}><i class="bi bi-check2-all"></i> ${this.cTxt('dlvDelivered')}</span>`;
            } else if (contact.whatsappStatus?.sent || contact.smsStatus?.sent || contact.emailStatus?.sent) {
                deliveryBadge = `<span class="badge bg-secondary js-hint-tip"${this._badgeTip('dlvSentTip')}><i class="bi bi-send"></i> ${this.cTxt('dlvSent')}</span>`;
            } else if (contact.wasSkipped) {
                // Never attempted on purpose (see the suppressedRecipients merge):
                // name the reason instead of a bare dash.
                const why = (contact.emailStatus && contact.emailStatus.errorMessage)
                    || (contact.whatsappStatus && contact.whatsappStatus.errorMessage) || '';
                const unsub = contact.errorCode === 'email_unsubscribed';
                deliveryBadge = `<span class="badge bg-light text-dark border${why ? ' js-hint-tip' : ''}"${this._tipAttrs(why, 'white-space:nowrap;')}><i class="bi ${unsub ? 'bi-envelope-slash' : 'bi-slash-circle'}"></i> ${this._escape(this.cTxt(unsub ? 'unsubBadge' : 'skipBadge'))}</span>`;
            } else if (contact.manualLink) {
                // A personal link the host shared themselves — we sent nothing,
                // so there is no delivery to report, only that a link exists.
                deliveryBadge = `<span class="badge bg-light text-dark border"><i class="bi bi-link-45deg"></i> ${this.cTxt('sharedByHand')}</span>`;
            } else {
                deliveryBadge = '<span class="text-muted">&mdash;</span>';
            }

            // Subtle "message failed" flag on an otherwise-reached contact
            // (task #370, option b). A send to them hard-failed even though they
            // were reached (by another channel, a resend, or a later message) — so
            // the host sees the problem without the contact being counted under
            // "Failed". Uses the server's `hadDeliveryFailure`, which survives a
            // later success (the aggregate status is latest-wins and would erase
            // the earlier failure). deliveryFailed already owns the red badge above;
            // this only decorates the reached rows (Read / Delivered / Sent).
            // …but not when the fallback badge is already saying exactly this,
            // which would put two failure notes on one row and overflow the cell.
            if (!contact.deliveryFailed && !fallbackChannel && !hostSentHere && contact.hadDeliveryFailure) {
                const code = contact.errorCode || contact.whatsappStatus?.errorCode;
                const tip = code ? this._errTip(code) : this.cTxt('aMessageFailedTip');
                deliveryBadge += ` <span class="text-warning small ms-1 js-hint-tip"${this._tipAttrs(tip, 'white-space:nowrap;')}><i class="bi bi-exclamation-triangle-fill"></i> ${this.cTxt('aMessageFailed')}</span>`;
            }

            // Response badge wrapped in an edit cell so the host can pair
            // the contact with an existing response (or record a manual
            // entry as a last resort) — see _renderResponseEditCell.
            const responseBadge = this._renderResponseEditCell(contact, idx);

            const contactInfo = contact.email && contact.phone
                ? `${contact.email}<br><small class="text-muted">${contact.phone}</small>`
                : (contact.email || contact.phone || '&mdash;');

            // Build full detail chain for expand-on-click
            const detailLines = [];
            if (contact.wasSkipped) {
                const why = (contact.emailStatus && contact.emailStatus.errorMessage)
                    || (contact.whatsappStatus && contact.whatsappStatus.errorMessage);
                if (why) detailLines.push('<i class="bi bi-info-circle me-1"></i>' + this._escape(why));
            }
            // Step words in the dashboard language; the arrows follow the
            // reading direction (&rarr; pointed backwards in Hebrew/Arabic).
            const arrow = (document.documentElement && document.documentElement.dir === 'rtl') ? ' &larr; ' : ' &rarr; ';
            if (contact.emailStatus) {
                const steps = [];
                if (contact.emailStatus.sent) steps.push(this.cTxt('dlvSent'));
                if (contact.emailStatus.delivered) steps.push(`<span class="text-info">${this.cTxt('dlvDelivered')}</span>`);
                if (contact.emailStatus.opened) steps.push(`<span class="text-primary">${this.cTxt('dlvOpened')}</span>`);
                if (contact.emailStatus.clicked) steps.push(`<span class="text-success">${this.cTxt('dlvClicked')}</span>`);
                if (contact.emailStatus.bounced) steps.push(`<span class="text-danger">${this.cTxt('dlvBounced')}</span>`);
                else if (contact.emailStatus.failed) steps.push(`<span class="text-danger">${this.cTxt('dlvFailed')}${contact.emailStatus.errorMessage ? ': ' + this._escape(contact.emailStatus.errorMessage) : ''}</span>`);
                if (steps.length) detailLines.push(`<i class="bi bi-envelope me-1"></i>${this.cTxt('detailEmail')}: ` + steps.join(arrow));
            }
            if (contact.whatsappStatus) {
                const steps = [];
                if (contact.whatsappStatus.sent) steps.push(this.cTxt('dlvSent'));
                if (contact.whatsappStatus.delivered) steps.push(`<span class="text-info">${this.cTxt('dlvDelivered')}</span>`);
                if (contact.whatsappStatus.read) steps.push(`<span class="text-primary">${this.cTxt('dlvRead')}</span>`);
                if (contact.whatsappStatus.failed) steps.push(`<span class="text-danger">${this.cTxt('dlvFailed')}${contact.errorCode ? ': ' + this._escape(this._errLabel(contact.errorCode)) : ''}</span>`);
                if (steps.length) detailLines.push('<i class="bi bi-whatsapp me-1"></i>WhatsApp: ' + steps.join(arrow));
            }
            if (contact.smsFallbackAttempted && contact.smsStatus) {
                const steps = [];
                if (contact.smsStatus.sent) steps.push(this.cTxt('dlvSmsSent'));
                if (contact.smsStatus.delivered) steps.push(`<span class="text-info">${this.cTxt('dlvDelivered')}</span>`);
                if (contact.smsStatus.error || contact.smsStatus.bounced) {
                    const errMsg = contact.smsStatus.errorMessage
                        ? `: ${contact.smsStatus.errorMessage}`
                        : '';
                    steps.push(`<span class="text-danger">${this.cTxt('dlvSmsFailed')}${errMsg}</span>`);
                }
                if (steps.length) detailLines.push(`<i class="bi bi-phone me-1"></i>${this.cTxt('detailSmsFallback')}: ` + steps.join(arrow));
            } else if (contact.smsStatus && contact.channel === 'sms' && !contact.fallbackFrom) {
                // Direct SMS send (US 'mobile' contacts — no WA attempted, SMS is primary)
                const steps = [];
                if (contact.smsStatus.sent) steps.push(this.cTxt('dlvSent'));
                if (contact.smsStatus.delivered) steps.push(`<span class="text-info">${this.cTxt('dlvDelivered')}</span>`);
                if (contact.smsStatus.error || contact.smsStatus.bounced) {
                    const errMsg = contact.smsStatus.errorMessage
                        ? `: ${contact.smsStatus.errorMessage}`
                        : '';
                    steps.push(`<span class="text-danger">${this.cTxt('dlvFailed')}${errMsg}</span>`);
                }
                if (steps.length) detailLines.push('<i class="bi bi-phone me-1"></i>SMS: ' + steps.join(arrow));
            }
            // The chain above can end in a failure with nothing after it. A
            // failed WhatsApp or bounced text goes out by email by itself when
            // the guest has one; say why it did not here, or the host reads
            // the bare failure as us skipping an email (chat 6582f145).
            {
                const ws = contact.whatsappStatus, ss = contact.smsStatus;
                const smsDead = !!(ss && (ss.error || ss.bounced) && !ss.delivered);
                const waDeadNoText = !!(ws && ws.failed && !ss);
                if (!contact.email && contact.phone && (smsDead || waDeadNoText)
                    && !(ws && ws.delivered) && !contact.responded
                    && !DashboardCampaigns.hostSentCoversFailures(contact)) {
                    detailLines.push('<i class="bi bi-envelope-slash me-1"></i>' + this._escape(this.cTxt('detailNoEmailFallback')));
                }
            }
            if (contact.responded) {
                const label = this.cTxt(contact.rsvpStatus === 'yes' ? 'reAttending'
                    : contact.rsvpStatus === 'no' ? 'reDeclined'
                    : contact.rsvpStatus === 'maybe' ? 'reMaybe'
                    : 'reResponded');
                detailLines.push(`<i class="bi bi-card-checklist me-1"></i>${this.cTxt('detailRsvp')}: ${label}`);
            }

            // On the mobile status board the email/phone is dropped from the
            // primary row, so surface it here at the top of the expanded detail.
            const mobileContactLine = (contact.email || contact.phone)
                ? `<div class="d-md-none small mb-1"><i class="bi bi-person-vcard me-1"></i>${contactInfo}</div>`
                : '';
            const mobileTagsLine = (contact.tags && contact.tags.length)
                ? `<div class="d-md-none small mb-1"><i class="bi bi-tags me-1"></i>${this.cTxt('statusTagsLine')}: ${tagChips(contact.tags)}</div>`
                : '';
            const detailHtml = mobileContactLine + mobileTagsLine + (detailLines.length
                ? detailLines.map(l => `<div class="small text-muted">${l}</div>`).join('')
                : `<div class="small text-muted">${this.cTxt('noDetail')}</div>`);

            const nameCell = this._renderEditableNameCell(contact, idx);

            return `
                <tr class="contact-main-row" data-row-idx="${idx}" style="cursor:pointer;" title="${this.cAttr('rowDetailsTip')}">
                    <td>${nameCell}</td>
                    <td class="small cs-col-phone">${contact.phone ? contact.phone : '<span class="text-muted">&mdash;</span>'}</td>
                    <td class="small cs-col-email">${contact.email ? contact.email : '<span class="text-muted">&mdash;</span>'}</td>
                    <td class="small cs-col-tags">${tagChips(contact.tags)}</td>
                    <td class="cs-col-delivery">${deliveryBadge}</td>
                    <td class="text-center cs-col-response">${responseBadge}</td>
                    <td class="text-center cs-col-reminder">${this._renderReminderCell(contact, idx)}</td>
                    <td class="text-center cs-col-edit">${this._renderEditCell(contact, idx)}</td>
                </tr>
                <tr class="contact-detail-row d-none" id="contact-detail-${idx}">
                    <td colspan="8" class="bg-light px-4 py-2">
                        ${detailHtml}
                    </td>
                </tr>
            `;
        }).join('');

        // Expand/collapse on row click — but ignore clicks inside the editable
        // name cell, the edit pencil, or the reminder bell so inline controls
        // don't toggle row expansion.
        tableBody.querySelectorAll('.contact-main-row').forEach(row => {
            row.addEventListener('click', (e) => {
                if (e.target.closest('.editable-name-cell')) return;
                if (e.target.closest('.response-edit-cell')) return;
                if (e.target.closest('.reminder-cell-btn')) return;
                // A tap on a delivery badge shows what it means, not the details.
                if (e.target.closest('.js-hint-tip')) return;
                const idx = row.dataset.rowIdx;
                const detail = document.getElementById(`contact-detail-${idx}`);
                if (detail) detail.classList.toggle('d-none');
            });
        });

        this._bindNameEditHandlers(tableBody);
        this._bindResponseEditHandlers(tableBody);
        this._bindReminderCellHandlers(tableBody);
    }

    // ---- Reminder exclusions ("Stop reminders for this guest") ----
    // Excluded contacts are dropped from the campaign's reminder audience
    // server-side (campaign.reminderExclusions). State is read straight from
    // this.campaigns (already loaded as full campaign docs), so toggling needs
    // no extra fetch — we just keep that in-memory list in sync.

    // The campaign currently scoped in the status modal, or null for "All Campaigns".
    _currentCampaignScope() {
        const v = document.getElementById('contactStatusCampaignFilter')?.value;
        return (v && v !== 'all') ? v : null;
    }

    // Does a stored exclusion entry refer to the same person as `contact`?
    // Phone match tolerates country-code prefixing via a 9-digit suffix compare;
    // email is matched exactly (lowercased). Mirrors the server's phone/email keying.
    _exclusionMatches(entry, contact) {
        const digits = s => (s || '').replace(/\D/g, '');
        const ep = digits(entry.phone), cp = digits(contact.phone);
        if (ep && cp && (ep === cp || (ep.length >= 9 && cp.length >= 9 && ep.slice(-9) === cp.slice(-9)))) {
            return true;
        }
        const ee = (entry.email || '').toLowerCase().trim();
        const ce = (contact.email || '').toLowerCase().trim();
        return !!ee && ee === ce;
    }

    // Is this contact currently excluded from reminders, given the active scope?
    // Specific-campaign scope → excluded in that campaign. "All" scope → excluded
    // in every campaign the contact belongs to (i.e. they truly won't be nudged).
    // The persistent, form-level guest-reminder status (task #372): stored on the
    // form independent of any campaign, so the bell reflects it even when there's
    // no messaging history. This is the source of truth; a paused guest is paused
    // regardless of scope.
    _isContactOptedOutFormLevel(contact) {
        const list = Array.isArray(this.formData?.reminderOptOuts) ? this.formData.reminderOptOuts : [];
        return list.some(e => this._exclusionMatches(e, contact));
    }

    /**
     * Stable identity for a contact row, used to pin an in-flight pause/resume
     * across re-renders (task #372 round 2).
     */
    _contactKey(contact) {
        if (!contact) return '';
        return String(contact.phone || contact.email || contact.name || '').trim().toLowerCase();
    }

    /**
     * Task #372 round 2 — the bell "removes the slash, adds it back, then
     * removes it again" on a single click.
     *
     * Clicking paints the new state immediately, but several other things
     * repaint the same row on their own schedule: the exclusion POST finishes
     * and calls filterContactStatus(), the campaign write lands and the
     * bulk_campaigns onSnapshot listener re-renders, and the debounced stats
     * refresh re-reads contacts ~2s later. Each of those derives the bell from
     * whichever copy of the data it happens to hold, so a repaint that arrives
     * before its own source has caught up flips the icon back, and the next one
     * flips it forward again — a visible flicker on what should be one change.
     *
     * Rather than chase every repaint path, the intent is pinned here: once the
     * host clicks, THIS is the state the bell shows, until the underlying data
     * genuinely agrees (then the pin is dropped) or the request fails (then it
     * is reverted explicitly). Renders in between simply read the pin.
     */
    _pinReminderState(contact, off) {
        if (!this._reminderPending) this._reminderPending = new Map();
        const key = this._contactKey(contact);
        if (key) this._reminderPending.set(key, off);
    }

    _unpinReminderState(contact) {
        const key = this._contactKey(contact);
        if (key && this._reminderPending) this._reminderPending.delete(key);
    }

    _isContactExcluded(contact) {
        // A click in flight (or one whose data hasn't propagated yet) wins over
        // every derived source, so the icon changes exactly once per click.
        const pending = this._reminderPending?.get(this._contactKey(contact));
        if (pending !== undefined) {
            // Drop the pin as soon as the real data says the same thing — from
            // then on the row is driven normally again.
            if (this._derivedContactExcluded(contact) === pending) {
                this._unpinReminderState(contact);
            }
            return pending;
        }
        return this._derivedContactExcluded(contact);
    }

    _derivedContactExcluded(contact) {
        // Form-level opt-out wins outright — it's the guest's own reminder status.
        if (this._isContactOptedOutFormLevel(contact)) return true;
        const scope = this._currentCampaignScope();
        const ids = scope ? [scope] : (Array.isArray(contact.campaignIds) ? contact.campaignIds : []);
        if (ids.length === 0) return false;
        const excludedIn = (cid) => {
            const c = (this.campaigns || []).find(x => x.id === cid);
            const list = (c && Array.isArray(c.reminderExclusions)) ? c.reminderExclusions : [];
            return list.some(e => this._exclusionMatches(e, contact));
        };
        return scope ? excludedIn(scope) : ids.every(excludedIn);
    }

    // Excluded from reminders in ANY campaign the contact belongs to (or paused
    // form-level). Used by the form-level manual "Remind pending/yes" blast (which
    // spans all campaigns) so a guest the host silenced isn't re-surfaced.
    _isContactExcludedForAnyCampaign(contact) {
        if (this._isContactOptedOutFormLevel(contact)) return true;
        const ids = Array.isArray(contact.campaignIds) ? contact.campaignIds : [];
        return ids.some(cid => {
            const c = (this.campaigns || []).find(x => x.id === cid);
            const list = (c && Array.isArray(c.reminderExclusions)) ? c.reminderExclusions : [];
            return list.some(e => this._exclusionMatches(e, contact));
        });
    }

    _renderResponseEditCell(contact, idx) {
        // Labels are wrapped in .resp-label so the mobile status board can
        // collapse the response to its colour-coded icon (see campaigns.css).
        let badgeHtml;
        if (contact.rsvpStatus === 'yes') {
            badgeHtml = `<span class="badge bg-success"><i class="bi bi-check-circle-fill"></i> <span class="resp-label">${this.cTxt('reAttending')}</span></span>`;
        } else if (contact.rsvpStatus === 'no') {
            badgeHtml = `<span class="badge bg-danger"><i class="bi bi-x-circle-fill"></i> <span class="resp-label">${this.cTxt('reDeclined')}</span></span>`;
        } else if (contact.rsvpStatus === 'maybe') {
            badgeHtml = `<span class="badge bg-warning text-dark"><i class="bi bi-question-circle-fill"></i> <span class="resp-label">${this.cTxt('reMaybe')}</span></span>`;
        } else if (contact.responded) {
            badgeHtml = `<span class="badge bg-success"><i class="bi bi-check-lg"></i> <span class="resp-label">${this.cTxt('reResponded')}</span></span>`;
        } else {
            badgeHtml = `<span class="badge bg-light text-secondary border"><i class="bi bi-hourglass-split"></i> <span class="resp-label">${this.cTxt('rePending')}</span></span>`;
        }

        // Response column shows ONLY the status badge (task #185) — the edit
        // pencil moved to its own trailing column (_renderEditCell) and the
        // reminder state to a dedicated column (_renderReminderCell), so the
        // cell never crams three controls together.
        return badgeHtml;
    }

    // Dedicated Edit column: the pencil that opens the response-edit modal.
    // Wrapped in .response-edit-cell so the existing binder + the row-click
    // ignore keep working unchanged.
    _renderEditCell(contact, idx) {
        const canEdit = Array.isArray(contact.trackingIds) && contact.trackingIds.length > 0;
        if (!canEdit) return '<span class="text-muted">&mdash;</span>';
        // The guest's own personal link (their ?tk token) — for re-sharing by
        // hand. Same token their message carried, so name prefill, party cap,
        // envelope addressee and reply tracking all still work.
        return `
            <span class="response-edit-cell" data-row-idx="${idx}">
                <button type="button" class="btn btn-link btn-sm p-0 response-edit-trigger"
                        title="${this.cAttr('updateResponseTip')}">
                    <i class="bi bi-pencil text-muted small"></i>
                </button>
                <button type="button" class="btn btn-link btn-sm p-0 ms-2 personal-link-copy"
                        title="${this.cTxt('copyPersonalLink')}" aria-label="${this.cTxt('copyPersonalLink')}">
                    <i class="bi bi-link-45deg text-muted"></i>
                </button>
                ${this._waHrefFor(contact) ? `<button type="button" class="btn btn-link btn-sm p-0 ms-2 personal-link-wa"
                        title="${this.cAttr('sendOnWhatsApp')}" aria-label="${this.cAttr('sendOnWhatsApp')}">
                    <i class="bi bi-whatsapp text-success"></i>
                </button>` : ''}
            </span>
        `;
    }

    /**
     * wa.me address that opens the HOST's own WhatsApp with this guest's
     * personal link written out, or null (no phone, no link). Every guest a
     * message went to has a row here, unlike the Responses tab, which lists
     * only guests who replied or were uploaded as a guest list: a host whose
     * guests were typed into the wizard had no "Send on WhatsApp" for any of
     * them (wedding-57pb, 2026-10-18). Sent from the host's own account, so
     * it works where ours cannot (Meta's 130497 country block) and uses no
     * credits. The host presses send in WhatsApp: nothing is automated.
     *
     * The text is the MESSAGE's own (_campaignWaText) when there is one to
     * resend: the message the Status window is scoped to, else the one that
     * failed for this guest. Otherwise admin.js's guestLinkWaText (loaded on
     * the same page), a short note with the link, in the guest's language.
     */
    _waHrefFor(contact) {
        const digits = String(contact?.phone || '').replace(/\D/g, '');
        if (digits.length < 8) return null;
        const campaign = this._waCampaignFor(contact);
        const url = this._personalLinkFor(contact, campaign ? campaign.id : null);
        if (!url) return null;
        let text = campaign ? this._campaignWaText(campaign, contact, url) : null;
        if (!text) {
            const title = this.formData?.title || window.currentFormData?.title || '';
            const name = contact.name && contact.name !== 'Unknown' ? contact.name : '';
            text = (typeof window.guestLinkWaText === 'function')
                ? window.guestLinkWaText(contact.language, name, title, url)
                : `${name ? `Hi ${name}! ` : ''}Here's your personal link to RSVP for ${title}: ${url}`;
        }
        return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    }

    /** The message a "Send on WhatsApp" resends: the one in scope, else the one that failed for this guest. */
    _waCampaignFor(contact) {
        const id = this._currentCampaignScope()
            || (Array.isArray(contact?.failedCampaignIds) && contact.failedCampaignIds[0]) || null;
        return id ? ((this.campaigns || []).find(c => c && c.id === id) || null) : null;
    }

    /**
     * A message's own text for this guest, as the send path writes it, with
     * their personal link on its own line after it; null when the message has
     * no text. Language as sent: an old translate-only doc sends its
     * translation (server utils/translate-only), a bilingual one both texts.
     * Tags as server substituteVariables fills them, through the same
     * HouseholdNames rules (loaded on this page), spellings like
     * {{first name}} included; a table number we do not have reads empty,
     * as on the server.
     */
    _campaignWaText(campaign, contact, url) {
        const translateOnly = campaign.campaignLanguageMode === 'translate'
            && typeof campaign.translatedMessage === 'string' && campaign.translatedMessage.trim();
        const main = translateOnly ? campaign.translatedMessage : campaign.customMessage;
        if (typeof main !== 'string' || !main.trim()) return null;
        const second = (!translateOnly && campaign.campaignLanguageMode === 'bilingual'
            && typeof campaign.translatedMessage === 'string' && campaign.translatedMessage.trim())
            ? campaign.translatedMessage : null;
        const guest = { ...contact, name: contact.name && contact.name !== 'Unknown' ? contact.name : '' };
        const HN = window.HouseholdNames;
        const fullName = HN && HN.invitationMessageName ? HN.invitationMessageName(guest) : guest.name;
        const greeting = HN && HN.invitationGreeting ? HN.invitationGreeting(guest) : String(guest.name || '').split(' ')[0];
        const fill = (t) => String(t)
            .replace(/\{\{\s*first[\s_-]*name\s*\}\}/gi, greeting || '')
            .replace(/\{\{\s*(?:guest[\s_-]*)?name\s*\}\}/gi, fullName || '')
            .replace(/\{\{\s*email\s*\}\}/gi, guest.email || '')
            .replace(/\{\{\s*table[\s_-]*number\s*\}\}/gi, guest.tableNumber || guest._tableNumber || '');
        return [fill(main).trim(), second ? fill(second).trim() : null, url].filter(Boolean).join('\n\n');
    }

    /**
     * Show "LINK" over the copy column on the Failed tab, and nothing anywhere
     * else (task #755).
     *
     * The header keeps a screen-reader label in every case — a column of
     * unlabelled buttons is not improved by the label being invisible to the
     * people who most need it.
     */
    _syncLinkColumnHeader() {
        const th = document.querySelector('#contactStatusTable thead th.cs-col-edit');
        if (!th) return;
        const onFailed = (document.getElementById('contactStatusFilter')?.value || 'all') === 'failed';
        th.classList.toggle('cs-col-link-named', onFailed);
        th.innerHTML = onFailed
            ? this._escape(this.cTxt('linkColumn'))
            : `<span class="visually-hidden">${this._escape(this.cTxt('edit'))}</span>`;
    }

    // The personal link for a contact row: where that guest's message sent
    // them, plus the token it carried. `/<formId>` is the same address Copy
    // Link in the header uses (the slug router forwards ?tk through).
    _personalLinkFor(contact, campaignId = null) {
        // The link of THAT message when one is named (trackingIdByCampaign),
        // so a resend carries the token its own message did.
        const byCampaign = campaignId && contact.trackingIdByCampaign && contact.trackingIdByCampaign[campaignId];
        const tid = byCampaign || (Array.isArray(contact.trackingIds) && contact.trackingIds[0]) || contact.trackingId;
        if (!tid) return null;
        const token = `tk=${encodeURIComponent(tid)}&ch=manual`;
        // A message whose only link is the host's QuikRSVP website sent its
        // guests THERE (campaigns.service withWeddingSiteTracking appends the
        // same tk), so a hand-sent copy must too. It used to open the form,
        // with its own envelope and look, next to the site the app's message
        // showed (chat b97997a7: "no queda lo mismo que manda la app").
        const campaign = (campaignId && (this.campaigns || []).find(c => c && c.id === campaignId)) || this._waCampaignFor(contact);
        const site = campaign && this._siteOnlyLinkOf(campaign);
        if (site) return `${site}${site.includes('?') ? '&' : '?'}${token}`;
        return `${window.location.origin}/${this.formId}?${token}`;
    }

    /** The QuikRSVP website a message's only button opened (no RSVP button), else null. */
    _siteOnlyLinkOf(campaign) {
        const buttons = Array.isArray(campaign.buttons) ? campaign.buttons : [];
        if (!buttons.includes('website') || buttons.includes('rsvp')) return null;
        const raw = String((campaign.buttonUrls && campaign.buttonUrls.website) || campaign.rsvpWebsiteUrl || '').trim();
        if (!raw) return null;
        try {
            const u = new URL(raw, window.location.origin);
            const ours = /(^|\.)quikrsvp\.com$/i.test(u.hostname) || u.origin === window.location.origin;
            if (!ours) return null;
            // quikrsvp.com/w/<site>, or the site on its own subdomain.
            if (/^\/w\/[^/]+/.test(u.pathname)) return u.origin + u.pathname.replace(/\/+$/, '');
            const sub = u.hostname.toLowerCase().replace(/\.quikrsvp\.com$/, '');
            if (sub !== u.hostname.toLowerCase() && sub && sub !== 'www' && (u.pathname === '/' || u.pathname === '')) return u.origin;
            return null;
        } catch (_) {
            return null;
        }
    }

    // Dedicated Reminder-status column (task #185): a small bell (reminders on)
    // or slashed bell (paused). Hover shows what a click does; a click opens a
    // compact popover with Resume/Pause + a dismiss option — no modal needed.
    _renderReminderCell(contact, idx) {
        const canManage = !!(contact.phone || contact.email);
        if (!canManage) return '<span class="text-muted">&mdash;</span>';
        const off = this._isContactExcluded(contact);
        const label = this.cAttr(off ? 'remCellOffAria' : 'remCellOnAria');
        // No title attribute: native tooltips take 1-2s to appear; the hover
        // hint is a custom instant tip driven by data-tip (task #185).
        return `
            <button type="button" class="reminder-cell-btn${off ? ' is-off' : ''}" data-row-idx="${idx}"
                    data-tip="${this.cAttr(off ? 'resume' : 'pause')}" aria-label="${label}">
                <i class="bi ${off ? 'bi-bell-slash' : 'bi-bell'}"></i>
            </button>
        `;
    }

    // Paint a bell button for a given paused/active state. Used to flip the icon
    // the instant it's clicked, before the server has answered (task #372).
    _setReminderCellVisual(btn, off) {
        btn.classList.toggle('is-off', off);
        btn.dataset.tip = this.cTxt(off ? 'resume' : 'pause');
        btn.setAttribute('aria-label', this.cTxt(off ? 'remCellOffAria' : 'remCellOnAria'));
        const icon = btn.querySelector('i');
        if (icon) icon.className = `bi ${off ? 'bi-bell-slash' : 'bi-bell'}`;
    }

    _bindReminderCellHandlers(tableBody) {
        tableBody.querySelectorAll('.reminder-cell-btn').forEach(btn => {
            // One click = pause or resume, with the icon flipping immediately
            // (task #372). There is no confirm step: the action is one click to
            // undo, so a popover asking "are you sure" was pure friction — and
            // waiting for the server before redrawing made it feel broken.
            btn.addEventListener('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                this._hideReminderTip();
                if (btn.dataset.busy === '1') return;
                const idx = parseInt(btn.dataset.rowIdx, 10);
                const contact = this._renderedContacts?.[idx];
                if (!contact) return;

                const off = this._isContactExcluded(contact);
                btn.dataset.busy = '1';
                // Pin BEFORE painting, so any re-render triggered while the
                // request is in flight reads the intended state rather than
                // whichever copy of the data it holds (task #372 round 2).
                this._pinReminderState(contact, !off);
                this._setReminderCellVisual(btn, !off);   // optimistic
                const ok = await this._toggleReminderExclusion(contact, { hide() { } }, !off);
                if (!ok) {
                    // Server said no — drop the pin and put the icon back.
                    this._unpinReminderState(contact);
                    this._setReminderCellVisual(btn, off);
                }
                btn.dataset.busy = '';
            });
            // Instant "Pause"/"Resume" hint on hover/focus (fixed-positioned so
            // the table's scroll container can't clip it on the first row).
            btn.addEventListener('mouseenter', () => this._showReminderTip(btn));
            btn.addEventListener('mouseleave', () => this._hideReminderTip());
            btn.addEventListener('focus', () => this._showReminderTip(btn));
            btn.addEventListener('blur', () => this._hideReminderTip());
        });
    }

    _showReminderTip(btn) {
        this._hideReminderTip();
        const text = btn.dataset.tip;
        if (!text) return;
        const tip = document.createElement('div');
        tip.className = 'reminder-cell-tip';
        tip.textContent = text;
        document.body.appendChild(tip);
        const r = btn.getBoundingClientRect();
        const w = tip.offsetWidth || 50;
        const left = Math.max(6, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 6));
        tip.style.left = `${Math.round(left)}px`;
        tip.style.top = `${Math.round(r.top - tip.offsetHeight - 6)}px`;
        requestAnimationFrame(() => tip.classList.add('show'));
        this._reminderTip = tip;
    }

    _hideReminderTip() {
        if (this._reminderTip) { this._reminderTip.remove(); this._reminderTip = null; }
    }


    _bindResponseEditHandlers(tableBody) {
        tableBody.querySelectorAll('.response-edit-cell').forEach(cell => {
            const trigger = cell.querySelector('.response-edit-trigger');
            if (trigger) trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const idx = parseInt(cell.dataset.rowIdx, 10);
                const contact = this._renderedContacts?.[idx];
                if (!contact) return;
                this._openResponseEditModal(contact);
            });
            const copyBtn = cell.querySelector('.personal-link-copy');
            if (copyBtn) copyBtn.addEventListener('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const idx = parseInt(cell.dataset.rowIdx, 10);
                const contact = this._renderedContacts?.[idx];
                const url = contact && this._personalLinkFor(contact);
                if (!url) return;
                let ok = false;
                try { await navigator.clipboard.writeText(url); ok = true; } catch (_) {
                    try {
                        const ta = document.createElement('textarea');
                        ta.value = url; ta.style.position = 'fixed'; ta.style.left = '-9999px';
                        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
                        ok = true;
                    } catch (_2) { ok = false; }
                }
                if (ok && window.showToast) window.showToast(this.cTxt('linkCopied'), 'success');
            });
            const waBtn = cell.querySelector('.personal-link-wa');
            if (waBtn) waBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const idx = parseInt(cell.dataset.rowIdx, 10);
                const contact = this._renderedContacts?.[idx];
                const href = this._waHrefFor(contact);
                if (!href) return;
                // Opened in the click itself: a window opened later is a blocked pop-up.
                window.open(href, '_blank', 'noopener');
                // A failed row now reads "Sent from your WhatsApp".
                if (this._recordHostSent(contact)) setTimeout(() => this._refreshAfterHostSend(), 0);
            });
        });
    }

    /**
     * Who a reminder goes to ("Attending guests"), in the dashboard's
     * language; '' for an audience this list doesn't know.
     */
    _reminderAudienceLabel(cfg) {
        const AUDIENCE = { no_response: 'audNoResponse', attending: 'audAttending', maybe: 'audMaybe', declined: 'audDeclined', all: 'audAll' };
        const key = cfg && AUDIENCE[cfg.recipients];
        return key ? this.cTxt(key) : '';
    }

    /**
     * A standalone reminder (Remind All Contacts) is named "[Reminder] <event>",
     * so every one of them read the same and none said who it went to (task
     * #907). Its card now reads like a campaign's own reminder rows: an
     * untouched name shows as "Reminder · Attending guests" (the event is the
     * one the whole dashboard is about); a name the host changed is kept, with
     * "· Attending guests" after it. Read from the reminder's settings at render
     * time, so an edited audience shows at once and older reminders get it too;
     * the stored name is left alone.
     *
     * @returns {{ audience: string, autoName: boolean }} audience '' = not one
     */
    _standaloneReminderTitle(campaign) {
        if (!campaign || !campaign.isReminderCampaign || campaign.parentCampaignId) return { audience: '', autoName: false };
        const audience = this._reminderAudienceLabel(campaign.reminderConfig);
        const name = String(campaign.name || '').trim();
        return { audience, autoName: !!audience && (!name || name === `[Reminder] ${campaign.eventTitle || ''}`.trim()) };
    }

    /**
     * A contact's Guest name(s) and Group name cells (task #935), through the
     * shared reading of the stored model (HouseholdNames.guestListColumns), so
     * "The Smith Family" + Party members or a #908 couple saved before the
     * columns existed shows everyone, under the right column.
     */
    _guestListCells(contact) {
        const H = window.HouseholdNames;
        const F = window.FileHelpers;
        if (!H || !F || !F.formatGuestNames) return { names: String((contact && contact.name) || ''), group: '', lastFirst: false };
        const cols = H.guestListColumns(contact || {});
        const names = F.formatGuestNames(cols.people);
        return { names, group: cols.groupName || '', lastFirst: !!(F.looksLikeLastFirst && F.looksLikeLastFirst(names, { group: cols.groupName })) };
    }

    _escape(s) {
        return window.escapeHtml ? window.escapeHtml(String(s ?? '')) : String(s ?? '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    // For a value inside a quoted attribute. window.escapeHtml (toast.js)
    // leaves quotes alone, so a tip holding one (WhatsApp said: "...", the
    // Hebrew abbreviation for the USA) ended the title there and spilled the
    // rest into stray attributes (M2 review, 2026-10-07).
    static escapeAttr(s) {
        return String(s ?? '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    _formatResponseDate(iso) {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString(this.dashboardLanguage || undefined, { month: 'short', day: 'numeric' });
        } catch (_) { return ''; }
    }

    _statusBadgeSmall(rsvpStatus) {
        if (rsvpStatus === 'yes') return `<span class="badge bg-success">${this.cTxt('reAttending')}</span>`;
        if (rsvpStatus === 'no') return `<span class="badge bg-danger">${this.cTxt('reDeclined')}</span>`;
        if (rsvpStatus === 'maybe') return `<span class="badge bg-warning text-dark">${this.cTxt('reMaybe')}</span>`;
        if (rsvpStatus === 'responded') return `<span class="badge bg-secondary">${this.cTxt('reResponded')}</span>`;
        return `<span class="badge bg-light text-secondary border">${this.cTxt('rePending')}</span>`;
    }

    _openResponseEditModal(contact) {
        const modalId = 'responseEditModal';
        // Kill any prior instance so reopening reflects the latest state.
        const existing = document.getElementById(modalId);
        if (existing) {
            const inst = bootstrap.Modal.getInstance(existing);
            if (inst) inst.dispose();
            existing.remove();
        }

        const contactName = (contact.name && contact.name !== 'Unknown') ? contact.name : this.cTxt('reThisGuest');
        const t = (k, v) => this.cTxt(k, v);
        const nameVar = { name: this._escape(contactName) };
        const isPaired = !!contact.responded;
        const isManual = contact.manuallyEntered === true;

        let currentBlock = '';
        if (isPaired) {
            const badge = this._statusBadgeSmall(contact.rsvpStatus);
            if (isManual) {
                currentBlock = `
                    <div class="alert alert-warning d-flex justify-content-between align-items-center py-2 mb-3">
                        <div class="small">
                            <strong>${t('reManualEntry')}</strong> ${badge}
                            <div class="text-muted">${t('reManualEntryNote')}</div>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-danger response-remove-manual">
                            <i class="bi bi-trash"></i> ${t('reRemove')}
                        </button>
                    </div>`;
            } else {
                currentBlock = `
                    <div class="alert alert-info d-flex justify-content-between align-items-center py-2 mb-3">
                        <div class="small">
                            <strong>${t('reCurrentlyPaired')}</strong> ${badge}
                        </div>
                        <button type="button" class="btn btn-sm btn-outline-secondary response-unpair">
                            <i class="bi bi-x-lg"></i> ${t('reUnpair')}
                        </button>
                    </div>`;
            }
        }

        const html = `
            <div class="modal fade" id="${modalId}" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-lg modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">${t('reTitle', nameVar)}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${t('close')}"></button>
                        </div>
                        <div class="modal-body">
                            ${currentBlock}

                            <!-- Task #964 phase 2: one guest, one set of details, one Save -
                                 the guest-list columns in their order (Guest name(s), Group
                                 name, Phone, Email, Max guests, Tags). The save reaches the
                                 guest's row in Responses, every link and message of theirs
                                 (the guest's personalized link reads them live, task #507),
                                 and their seats. It replaces a Name / Phone / Email box with
                                 its own Save above a separate invitation box. -->
                            <h6 class="text-uppercase small text-muted mb-2">
                                <i class="bi bi-person-vcard me-1"></i>${t('reEditDetails')}
                            </h6>
                            <p class="small text-muted mb-2">
                                ${t('reInvitationHelp', {
                                    name: this._escape(contactName),
                                    sep: '<code>;</code>',
                                    child: `<code>${this._escape(t('reChildMarker'))}</code>`,
                                    example: `<code dir="auto">${this._escape(t('reExample'))}</code>`
                                })}
                            </p>
                            <div class="input-group input-group-sm mb-2">
                                <span class="input-group-text">${t('reGuestNames')}</span>
                                <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="text" class="form-control form-control-sm" id="cdMembers" dir="auto"
                                       maxlength="600" placeholder="${t('reLoading')}" value="${this._escape((contact.name && contact.name !== 'Unknown') ? contact.name : '')}">
                            </div>
                            <div class="input-group input-group-sm mb-2">
                                <span class="input-group-text" title="${this._escape(t('reGroupNameHint'))}">${t('reGroupName')}</span>
                                <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="text" class="form-control form-control-sm" id="cdGroupName" dir="auto"
                                       maxlength="120" placeholder="${t('reGroupNamePh')}" title="${this._escape(t('reGroupNameHint'))}" disabled>
                            </div>
                            <div class="row g-2 mb-2">
                                <div class="col-sm-6">
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text"><i class="bi bi-telephone" aria-hidden="true"></i></span>
                                        <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="tel" class="form-control form-control-sm" id="cdPhone" maxlength="40" placeholder="${t('rePhone')}" aria-label="${this._escape(t('rePhone'))}" value="${this._escape(contact.phone || '')}" dir="ltr">
                                    </div>
                                </div>
                                <div class="col-sm-6">
                                    <div class="input-group input-group-sm">
                                        <span class="input-group-text"><i class="bi bi-envelope" aria-hidden="true"></i></span>
                                        <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="email" class="form-control form-control-sm" id="cdEmail" maxlength="200" placeholder="${t('reEmail')}" aria-label="${this._escape(t('reEmail'))}" value="${this._escape(contact.email || '')}" dir="ltr">
                                    </div>
                                </div>
                            </div>
                            <div class="input-group input-group-sm mb-2" style="max-width: 240px;">
                                <span class="input-group-text" title="${this._escape(t('reMaxGuestsHint'))}">${t('reMaxGuests')}</span>
                                <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="number" class="form-control form-control-sm" id="cdMaxGuests"
                                       min="1" max="50" step="1" placeholder="—" title="${this._escape(t('reMaxGuestsHint'))}" disabled>
                            </div>
                            <!-- Guest tags (task #847): which parts of the event this guest's
                                 link shows, when a part is limited to certain tags. -->
                            <div class="input-group input-group-sm mb-2">
                                <span class="input-group-text"><i class="bi bi-tag me-1"></i>${t('reTags')}</span>
                                <input type="text" class="form-control form-control-sm" id="cdTags"
                                       data-tag-picker data-tag-form="${this._escape(this.formId || '')}" readonly
                                       maxlength="400" placeholder="${t('reTagsPh')}" title="${this._escape(t('reTagsHelp'))}" disabled>
                            </div>
                            <div class="d-flex align-items-center gap-2 mb-1">
                                <button type="button" class="btn btn-sm btn-primary" id="cdSaveBtn"><i class="bi bi-check-lg me-1"></i>${t('reSave')}</button>
                                <span class="small" id="cdStatus"></span>
                            </div>
                            <div class="small mb-2" id="cdMembersStatus"></div>
                            <!-- Where the "you corrected the number — resend?" prompt
                                 appears after a save that changed the phone or email
                                 (task #572). Empty until then. -->
                            <div id="cdResendSlot" class="mb-3"></div>

                            <hr class="my-4">

                            <h6 class="text-uppercase small text-muted mb-2">
                                <i class="bi bi-link-45deg me-1"></i>${t('rePairHeading')}
                            </h6>
                            <p class="small text-muted mb-3">${t('rePairHelp', nameVar)}</p>
                            <div id="responseCandidatesLoading" class="text-center py-3">
                                <div class="spinner-border spinner-border-sm text-secondary" role="status">
                                    <span class="visually-hidden">${t('reLoading')}</span>
                                </div>
                                <span class="ms-2 small text-muted">${t('reLooking')}</span>
                            </div>
                            <div id="responseCandidatesContent" class="d-none"></div>

                            <hr class="my-4">

                            <h6 class="text-uppercase small text-muted mb-2">
                                <i class="bi bi-pencil-square me-1"></i>${t('reManualHeading')}
                            </h6>
                            <p class="small text-muted mb-2">${t('reManualHelp', nameVar)}</p>
                            <div class="btn-group" role="group">
                                <button type="button" class="btn btn-outline-success response-manual" data-status="yes">
                                    <i class="bi bi-check-circle-fill me-1"></i>${t('reAttending')}
                                </button>
                                <button type="button" class="btn btn-outline-danger response-manual" data-status="no">
                                    <i class="bi bi-x-circle-fill me-1"></i>${t('reDeclined')}
                                </button>
                                <button type="button" class="btn btn-outline-warning response-manual" data-status="maybe">
                                    <i class="bi bi-question-circle-fill me-1"></i>${t('reMaybe')}
                                </button>
                            </div>
                            <!-- The Reminders pause/resume control moved to the
                                 contact table's dedicated Reminders column
                                 (bell popover, task #185). -->
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${t('close')}</button>
                        </div>
                    </div>
                </div>
            </div>`;

        document.body.insertAdjacentHTML('beforeend', html);
        const modalEl = document.getElementById(modalId);
        const modal = new bootstrap.Modal(modalEl);

        modalEl.addEventListener('hidden.bs.modal', () => {
            document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
            modal.dispose();
            modalEl.remove();
        }, { once: true });

        // Corrected earlier but never sent again: the offer used to appear only
        // in the window where the correction was typed, so closing it lost the
        // way to resend (chat 6582f145: gnail.com fixed, window closed).
        const owed = DashboardCampaigns.resendOwedAfterCorrection(contact);
        if (owed) {
            this._offerResendAfterCorrection(modalEl, contact, {
                ...owed, prevPhone: contact.phone || '', prevEmail: contact.email || ''
            });
        }

        // "Edit contact details" — save Name/Phone/Email directly from the pencil
        // modal (previously this modal only paired responses; editing details was
        // hidden behind clicking the name in the table).
        modalEl.querySelector('#cdSaveBtn')?.addEventListener('click', async () => {
            // The guest the message went to: the first person under Guest
            // name(s), else who it is addressed to (task #964 phase 2: the
            // Name box above it said the same thing twice).
            const H = window.HouseholdNames, F = window.FileHelpers;
            const groupText = modalEl.querySelector('#cdGroupName')?.value || '';
            const typedNames = modalEl.querySelector('#cdMembers')?.value || '';
            const people = F ? ((F.parseGuestNames ? F.parseGuestNames(typedNames, { group: groupText }) : F.parseHouseholdMembers(typedNames)) || []) : [];
            const name = ((H && people.length ? H.guestListContact(people, groupText).name : '') || typedNames.split(';')[0] || '').trim()
                || String(contact.name || '').trim();
            const typedPhone = (modalEl.querySelector('#cdPhone')?.value || '').trim();
            // As we will dial it — the table, the resend offer and the address
            // book then all show the same number the server stores.
            const phone = typedPhone && window.ValidationUtils?.normalizeInternationalPhone
                ? window.ValidationUtils.normalizeInternationalPhone(typedPhone) : typedPhone;
            const email = (modalEl.querySelector('#cdEmail')?.value || '').trim();
            const statusEl = modalEl.querySelector('#cdStatus');
            const btn = modalEl.querySelector('#cdSaveBtn');
            btn.disabled = true;
            if (statusEl) { statusEl.textContent = this.cTxt('reSaving'); statusEl.className = 'small text-muted'; }
            try {
                // What the details WERE, so a corrected phone/email can offer a
                // resend to the new destination (task #572).
                const prevPhone = String(contact.phone || '').trim();
                const prevEmail = String(contact.email || '').trim();

                await this._saveContactDetails(contact, { name, phone, email });
                // …and the invitation (names, group, party, tags), when there is one to edit.
                if (typeof modalEl._saveInvitation === 'function') await modalEl._saveInvitation();
                contact.name = name || 'Unknown';
                contact.phone = phone;
                contact.email = email;
                if (statusEl) { statusEl.textContent = this.cTxt('reSaved'); statusEl.className = 'small text-success'; }
                // Reflect the edit in the table behind the modal.
                if (Array.isArray(this._renderedContacts)) this.renderContactStatusTable(this._renderedContacts);

                // Correcting a wrong number/address is almost always followed by
                // "…so send it to them properly this time", and there was no way
                // to do that without rebuilding a campaign by hand (task #572).
                const phoneChanged = phone && phone !== prevPhone;
                const emailChanged = email && email.toLowerCase() !== prevEmail.toLowerCase();
                if (phoneChanged || emailChanged) {
                    this._offerResendAfterCorrection(modalEl, contact, {
                        phoneChanged, emailChanged, prevPhone, prevEmail
                    });
                }
                // Keep the host's address book in step with the correction, so the
                // same wrong number doesn't come back on the next campaign.
                this._syncCorrectedContactToAddressBook(contact, { prevPhone, prevEmail });
                // No toast: the field already says "Saved ✓" and the table behind the
                // modal has just been redrawn with the new details — a third
                // confirmation of the same edit is noise (task #449).
            } catch (err) {
                console.error('Save contact details failed:', err);
                if (statusEl) { statusEl.textContent = err?.message || this.cTxt('reCouldNotSave'); statusEl.className = 'small text-danger'; }
            } finally {
                btn.disabled = false;
            }
        });

        // Wire current-state buttons (only present when applicable).
        modalEl.querySelector('.response-remove-manual')?.addEventListener('click', async () => {
            await this._removeManualResponse(contact, modal);
        });
        modalEl.querySelector('.response-unpair')?.addEventListener('click', async () => {
            await this._unpairContact(contact, modal);
        });

        // Manual-entry buttons.
        modalEl.querySelectorAll('.response-manual').forEach(btn => {
            btn.addEventListener('click', async () => {
                const status = btn.dataset.status;
                await this._createManualEntry(contact, status, modal);
            });
        });

        // Party members (task #507) — load the current roster, then wire Save.
        this._wireHouseholdEditor(contact, modalEl);

        modal.show();

        // Kick off candidate fetch.
        this._loadResponseCandidates(contact, modalEl, modal);
    }

    /**
     * "Tag everyone on this message" (task #847): one prompt, one request,
     * every contact on the campaign gains the tags. A host who sent the
     * reception-only guests as their own message tags that whole group here
     * instead of editing each contact.
     */
    async tagCampaignContacts(campaignId) {
        if (!campaignId) return;
        // Task #870: picked from the tags the event uses, not typed — a
        // misspelt tag tagged everyone with something no part matches.
        let tags = null;
        if (window.TagPicker && window.TagPicker.ask) {
            tags = await window.TagPicker.ask({ formId: this.formId, title: this.cTxt('tagContacts') });
        } else {
            const raw = window.prompt(this.cTxt('tagContactsPrompt'), '');
            tags = raw == null ? null : (window.FileHelpers ? window.FileHelpers.parseContactTags(raw) : null);
        }
        if (!tags || !tags.length) return;
        const toast = (msg, type) => (window.languageHelpers?.showToast ? window.languageHelpers.showToast(msg, type)
            : (window.showToast ? window.showToast(msg, type) : alert(msg)));
        try {
            const res = await this._mtFetch('/api/campaigns/' + encodeURIComponent(campaignId) + '/tag-contacts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tags })
            });
            if (!res.ok) {
                let msg = this.cTxt('tagContactsFailed');
                try { const d = await res.json(); if (d?.error) msg = d.error; } catch (_) {}
                throw new Error(msg);
            }
            const data = await res.json();
            // Same merge as the server's, on the guest list held here, so an
            // open Contact Details window shows the new tags without a reload.
            const campaign = (this.campaigns || []).find(x => x.id === campaignId);
            const added = Array.isArray(data.tags) ? data.tags : tags;
            (Array.isArray(campaign?.contacts) ? campaign.contacts : []).forEach((c) => {
                if (!c) return;
                const have = new Set((c.tags || []).map(DashboardCampaigns._tagKey));
                const next = (c.tags || []).concat(added.filter(t => !have.has(DashboardCampaigns._tagKey(t))));
                if (next.length) c.tags = next;
            });
            this._refreshRowTags();
            toast(this.cTxt('tagContactsDone', data.tagged || 0), 'success');
        } catch (err) {
            console.error('Tag contacts failed:', err);
            toast(err?.message || this.cTxt('tagContactsFailed'), 'error');
        }
    }

    /**
     * "Party members" on the contact modal (task #507 item 1): show who the host
     * put on this invitation and let them change it after the message has gone
     * out. Worth having only because the guest's personalized link re-reads the
     * roster every time it is opened — so an edit lands on a link that was sent
     * days ago, with no re-send.
     *
     * Uses the same "A; B (child)" text the contact tables and the CSV column
     * use, parsed by the shared FileHelpers so all three stay in one format.
     */
    async _wireHouseholdEditor(contact, modalEl) {
        const input = modalEl.querySelector('#cdMembers');
        const groupInput = modalEl.querySelector('#cdGroupName');
        const maxInput = modalEl.querySelector('#cdMaxGuests');
        const tagsInput = modalEl.querySelector('#cdTags');
        const statusEl = modalEl.querySelector('#cdMembersStatus');
        if (!input) return;

        const ids = Array.isArray(contact.trackingIds) ? contact.trackingIds.filter(Boolean) : [];
        const setStatus = (text, cls) => {
            if (statusEl) { statusEl.textContent = text || ''; statusEl.className = `small mb-3 ${cls || 'text-muted'}`; }
        };
        // partySize is what the guest's link will ALLOW — the server applies the
        // same rule as the link itself (an explicit Max guests wins, else the
        // names listed). It used to be the roster alone, so "Max guests 1" with
        // one companion listed read "for 2 people" here while the link capped
        // at 1 (chat 6c30e8cb).
        const partyLine = (n) => n > 1
            ? this.cTxt('rePartyMany', n)
            : this.cTxt('rePartyOne');
        const setEnabled = (on) => {
            input.disabled = !on;
            if (groupInput) groupInput.disabled = !on;
            if (maxInput) maxInput.disabled = !on;
            if (tagsInput) tagsInput.disabled = !on;
        };
        const fmtTags = (tags) => (window.FileHelpers ? window.FileHelpers.formatContactTags(tags) : '');

        if (!this.formId || !ids.length) {
            input.placeholder = '—';
            return;
        }

        // The host's own language's marker: the parser reads all of them
        // (FileHelpers.HOUSEHOLD_CHILD_MARKERS), so the round trip is safe.
        const fmt = (m) => (window.FileHelpers ? window.FileHelpers.formatHouseholdMembers(m, this.cTxt('reChildMarker')) : '');
        // The two columns of the invitation (task #935), read through the
        // shared model: everyone on it under Guest name(s) - the guest the
        // message went to included - and who it is addressed to.
        const H = window.HouseholdNames;
        let mainName = '';
        const showHousehold = (data) => {
            mainName = String(data.name || '').trim();
            const cols = H ? H.guestListColumns({ name: mainName, groupName: data.groupName, members: data.members }) : null;
            input.value = cols ? fmt(cols.people) : fmt(data.members);
            if (groupInput) groupInput.value = cols ? cols.groupName : (data.groupName || '');
        };
        try {
            const res = await this._mtFetch(
                `/api/forms/${encodeURIComponent(this.formId)}/invitation-household?trackingIds=${encodeURIComponent(ids.join(','))}`
            );
            if (!res.ok) throw new Error('Could not load the invitation');
            const data = await res.json();
            showHousehold(data);
            input.placeholder = this.cTxt('reMembersPh');
            if (maxInput) maxInput.value = data.maxGuests ? String(data.maxGuests) : '';
            if (tagsInput) tagsInput.value = fmtTags(data.tags);
            if (data.editable === false) {
                setEnabled(false);
                // The name still saves on the guest's messages.
                input.disabled = false;
                setStatus(this.cTxt('reNotOnMessage'));
                return;
            }
            setEnabled(true);
            setStatus(partyLine(data.partySize || 1));
        } catch (err) {
            console.error('Load household failed:', err);
            input.placeholder = '—';
            setStatus(this.cTxt('reLoadFailed'), 'text-danger');
            return;
        }

        // Called by the window's one Save (task #964 phase 2).
        modalEl._saveInvitation = async () => {
            // Guest name(s) + Group name → the stored shape (task #935). The
            // guest the message went to stays on the invitation whatever is
            // typed (the link puts them first), so one other person typed
            // alone is still sent as a member; the main guest alone is "nobody
            // else" ([]).
            const F = window.FileHelpers;
            const groupText = groupInput ? groupInput.value : '';
            const people = F ? (F.parseGuestNames ? F.parseGuestNames(input.value, { group: groupText }) : F.parseHouseholdMembers(input.value)) || [] : [];
            const who = H ? H.guestListContact(people, groupText) : { members: people };
            const isMain = (p) => H && mainName && (H.cleanName(p.name).toLowerCase() === H.cleanName(mainName).toLowerCase() || H.repeatsMainGuest(p, mainName));
            const members = who.members || (people.length === 1 && !isMain(people[0]) ? people : null);
            // Blank = "no explicit cap" (sent as null so the server clears one
            // that was there); a number is validated server-side too.
            const rawMax = maxInput ? maxInput.value.trim() : '';
            const maxGuests = rawMax === '' ? null : parseInt(rawMax, 10);
            if (rawMax !== '' && !(Number.isFinite(maxGuests) && maxGuests >= 1 && maxGuests <= 50)) {
                setStatus(this.cTxt('reMaxInvalid'), 'text-danger');
                return;
            }
            setEnabled(false);
            setStatus(this.cTxt('reSaving'));
            try {
                const res = await this._mtFetch(
                    `/api/forms/${encodeURIComponent(this.formId)}/invitation-household`,
                    {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        // null (an emptied box) is sent as [] — "nobody else is on
                        // this invitation" is a real instruction, not a no-op.
                        // tags: an emptied box clears them (sent as []); the key is
                        // left out entirely when this build has no tags box.
                        body: JSON.stringify({
                            trackingIds: ids, members: members || [], maxGuests,
                            ...(groupInput ? { groupName: (who.groupName || '') } : {}),
                            ...(tagsInput ? { tags: (window.FileHelpers ? window.FileHelpers.parseContactTags(tagsInput.value) : null) || [] } : {})
                        })
                    }
                );
                if (!res.ok) {
                    let msg = this.cTxt('reCouldNotSave');
                    try { const d = await res.json(); if (d?.error) msg = d.error; } catch (_) {}
                    throw new Error(msg);
                }
                const data = await res.json();
                showHousehold(data);
                if (data.groupName) contact.groupName = data.groupName; else delete contact.groupName;
                if (maxInput) maxInput.value = data.maxGuests ? String(data.maxGuests) : '';
                if (tagsInput) tagsInput.value = fmtTags(data.tags);
                // The server replaced this guest's tags on every message they
                // are on; the copy held here is what the Tags column and the
                // tag filter read, so it changes the same way.
                (Array.isArray(contact.campaignIds) ? contact.campaignIds : []).forEach((cid) => {
                    const stored = this._storedCampaignContact(cid, contact);
                    if (!stored) return;
                    if (Array.isArray(data.tags) && data.tags.length) stored.tags = data.tags.slice(); else delete stored.tags;
                });
                this._refreshRowTags();
                // Keep the table behind the modal in step (its Max guests column).
                if (data.maxGuests) contact.maxGuests = data.maxGuests; else delete contact.maxGuests;
                setStatus(partyLine(data.partySize || 1));
            } catch (err) {
                console.error('Save household failed:', err);
                setStatus(err?.message || this.cTxt('reCouldNotSave'), 'text-danger');
                throw err;
            } finally {
                setEnabled(true);
            }
        };
    }

    async _loadResponseCandidates(contact, modalEl, modal) {
        const loadingEl = modalEl.querySelector('#responseCandidatesLoading');
        const contentEl = modalEl.querySelector('#responseCandidatesContent');

        try {
            const params = new URLSearchParams();
            if (contact.phone) params.set('phone', contact.phone);
            if (contact.email) params.set('email', contact.email);
            if (contact.name) params.set('name', contact.name);
            if (Array.isArray(contact.trackingIds) && contact.trackingIds.length) {
                params.set('excludeTrackingIds', contact.trackingIds.join(','));
            }
            const url = `/api/message-tracking/${this.formId}/response-candidates?${params.toString()}`;
            const res = await this._mtFetch(url);
            if (!res.ok) throw new Error('Failed to load candidates');
            const data = await res.json();

            loadingEl.classList.add('d-none');
            contentEl.classList.remove('d-none');
            contentEl.innerHTML = this._renderCandidatesPanel(data.likely || [], data.all || [], data.totalUnpaired || 0);

            // Wire pair buttons + browse-all toggle + search filter
            contentEl.querySelectorAll('.response-pair-btn').forEach(btn => {
                btn.addEventListener('click', async () => {
                    const responseId = btn.dataset.responseId;
                    await this._pairContact(contact, responseId, modal);
                });
            });
            const toggle = contentEl.querySelector('#browseAllToggle');
            const allWrap = contentEl.querySelector('#browseAllWrap');
            toggle?.addEventListener('click', (e) => {
                e.preventDefault();
                const isHidden = allWrap.classList.toggle('d-none');
                toggle.innerHTML = isHidden
                    ? `<i class="bi bi-chevron-down"></i> ${data.totalUnpaired === 1 ? this.cTxt('reBrowseOne') : this.cTxt('reBrowseAll', data.totalUnpaired)}`
                    : `<i class="bi bi-chevron-up"></i> ${this.cTxt('reHideList')}`;
            });
            const searchInput = contentEl.querySelector('#browseAllSearch');
            searchInput?.addEventListener('input', () => {
                const q = searchInput.value.toLowerCase().trim();
                contentEl.querySelectorAll('#browseAllList .candidate-row').forEach(row => {
                    const hay = (row.dataset.search || '').toLowerCase();
                    row.classList.toggle('d-none', q && !hay.includes(q));
                });
            });
        } catch (err) {
            console.error('Candidate fetch failed:', err);
            loadingEl.classList.add('d-none');
            contentEl.classList.remove('d-none');
            contentEl.innerHTML = `<div class="alert alert-warning small">${this.cTxt('reCandidatesFailed')}</div>`;
        }
    }

    _renderCandidatesPanel(likely, all, totalUnpaired) {
        const renderRow = (r, ctx) => {
            const subtitle = [r.phone, r.email].filter(Boolean).join(' · ');
            const date = this._formatResponseDate(r.submittedAt);
            const search = `${r.name} ${r.phone} ${r.email}`;
            const manualTag = r.manuallyEntered
                ? `<span class="badge bg-secondary ms-1" title="${this.cTxt('reManualTagTip')}">${this.cTxt('reManualTag')}</span>`
                : '';
            return `
                <div class="candidate-row d-flex align-items-center justify-content-between py-2 border-bottom"
                     data-search="${this._escape(search)}">
                    <div class="small me-2 flex-grow-1">
                        <div>
                            <strong>${this._escape(r.name || this.cTxt('reNoName'))}</strong>
                            ${this._statusBadgeSmall(r.rsvpStatus)}
                            ${manualTag}
                        </div>
                        <div class="text-muted">${subtitle ? `<bdi dir="ltr">${this._escape(subtitle)}</bdi>` : '&mdash;'}${date ? ` · ${date}` : ''}</div>
                    </div>
                    <button type="button" class="btn btn-sm btn-primary response-pair-btn" data-response-id="${this._escape(r.id)}">
                        ${this.cTxt('rePair')}
                    </button>
                </div>`;
        };

        let html = '';

        if (likely.length > 0) {
            html += `
                <div class="mb-3">
                    <div class="small fw-bold text-success mb-1">
                        <i class="bi bi-stars"></i> ${this.cTxt('reLikely')}
                    </div>
                    ${likely.map(r => renderRow(r, 'likely')).join('')}
                </div>`;
        }

        if (totalUnpaired === 0) {
            html += `
                <div class="alert alert-light border small mb-0">${this.cTxt('reNoUnpaired')}</div>`;
            return html;
        }

        if (likely.length === 0) {
            html += `
                <div class="alert alert-light border small mb-3">${this.cTxt('reNoLikely')}</div>`;
        }

        // Full list ALWAYS starts collapsed (task #187) — even with no likely
        // matches the wall of unpaired responses overwhelmed the modal. The
        // toggle stays the single, consistent way to browse it.
        const browseHidden = true;
        const toggleLabel = `<i class="bi bi-chevron-down"></i> ${totalUnpaired === 1 ? this.cTxt('reBrowseOne') : this.cTxt('reBrowseAll', totalUnpaired)}`;

        html += `
            <div>
                <a href="#" id="browseAllToggle" class="small text-decoration-none">${toggleLabel}</a>
                <div id="browseAllWrap" class="${browseHidden ? 'd-none' : ''} mt-2">
                    <input type="text" id="browseAllSearch" class="form-control form-control-sm mb-2"
                           placeholder="${this.cTxt('reSearchPh')}">
                    <div id="browseAllList" style="max-height:280px;overflow-y:auto;">
                        ${all.map(r => renderRow(r, 'all')).join('')}
                    </div>
                </div>
            </div>`;

        return html;
    }

    async _pairContact(contact, responseId, modal) {
        try {
            const res = await this._mtFetch(`/api/message-tracking/${this.formId}/link-response`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ trackingIds: contact.trackingIds, responseId })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || this.cTxt('reFailedPair'));
            }
            if (window.showToast) window.showToast(this.cTxt('rePaired', { name: contact.name || this.cTxt('reContact') }), 'success');
            modal.hide();
            await this._reloadAfterResponseEdit();
        } catch (err) {
            console.error('Pair failed:', err);
            if (window.showToast) window.showToast(err.message || this.cTxt('reFailedPair'), 'error');
        }
    }

    async _unpairContact(contact, modal) {
        try {
            const res = await this._mtFetch(`/api/message-tracking/${this.formId}/unlink-response`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ trackingIds: contact.trackingIds })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || this.cTxt('reFailedUnpair'));
            }
            if (window.showToast) window.showToast(this.cTxt('reUnpaired', { name: contact.name || this.cTxt('reContact') }), 'success');
            modal.hide();
            await this._reloadAfterResponseEdit();
        } catch (err) {
            console.error('Unpair failed:', err);
            if (window.showToast) window.showToast(err.message || this.cTxt('reFailedUnpair'), 'error');
        }
    }

    async _createManualEntry(contact, rsvpStatus, modal) {
        try {
            const res = await this._mtFetch(`/api/message-tracking/${this.formId}/manual-response`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    trackingIds: contact.trackingIds,
                    phone: contact.phone || null,
                    email: contact.email || null,
                    name: contact.name || null,
                    rsvpStatus
                })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || this.cTxt('reFailedRecord'));
            }
            if (window.showToast) window.showToast(this.cTxt('reRecorded', {
                status: this.cTxt({ yes: 'reAttending', no: 'reDeclined', maybe: 'reMaybe' }[rsvpStatus] || 'reResponded'),
                name: contact.name || this.cTxt('reContact')
            }), 'success');
            modal.hide();
            await this._reloadAfterResponseEdit();
        } catch (err) {
            console.error('Manual entry failed:', err);
            if (window.showToast) window.showToast(err.message || this.cTxt('reFailedRecord'), 'error');
        }
    }

    async _removeManualResponse(contact, modal) {
        // The unlink endpoint auto-deletes the response when it was a
        // manual entry — so this is just a relabeled unpair call.
        try {
            const res = await this._mtFetch(`/api/message-tracking/${this.formId}/unlink-response`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ trackingIds: contact.trackingIds })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || this.cTxt('reFailedRemove'));
            }
            if (window.showToast) window.showToast(this.cTxt('reManualRemoved'), 'success');
            modal.hide();
            await this._reloadAfterResponseEdit();
        } catch (err) {
            console.error('Remove manual failed:', err);
            if (window.showToast) window.showToast(err.message || this.cTxt('reFailedRemove'), 'error');
        }
    }

    // Add/remove this contact on the scoped campaign(s)' reminder opt-out list.
    // Specific-campaign scope updates that one campaign; "All" scope updates every
    // campaign the contact belongs to.
    async _toggleReminderExclusion(contact, modal, exclude) {
        const scope = this._currentCampaignScope();
        // campaignIds may be empty (task #372): the pause/resume is stored form-
        // level, so it works even when this guest has no messaging history. Any
        // campaigns are still updated too, for the legacy embedded-reminder path.
        const campaignIds = scope
            ? [scope]
            : (Array.isArray(contact.campaignIds) ? contact.campaignIds.slice() : []);
        try {
            const res = await this._mtFetch(`/api/message-tracking/${this.formId}/reminder-exclusion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    campaignIds,
                    phone: contact.phone || null,
                    email: contact.email || null,
                    name: contact.name || null,
                    exclude
                })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || 'Failed to update reminders');
            }
            // Keep this.campaigns in sync so the row badge updates without a reload.
            this._applyExclusionLocally(contact, campaignIds, exclude);
            // No success toast (task #372, option a): the bell icon flipping to its
            // paused/active state is the confirmation — the extra corner toast was
            // redundant. Errors below still surface.
            modal.hide();
            this.filterContactStatus();
            return true;
        } catch (err) {
            console.error('Reminder exclusion failed:', err);
            if (window.showToast) window.showToast(err.message || 'Failed to update reminders', 'error');
            return false;
        }
    }

    // Mirror the server write into the in-memory campaign list (the source the
    // exclusion-state helpers read), so the UI reflects the change immediately.
    _applyExclusionLocally(contact, campaignIds, exclude) {
        // Form-level opt-out (task #372) — keep this.formData.reminderOptOuts in
        // step so the bell reflects the paused/active state instantly, even with
        // no campaigns to mirror into below.
        if (!this.formData) this.formData = {};
        if (!Array.isArray(this.formData.reminderOptOuts)) this.formData.reminderOptOuts = [];
        const matchesForm = e => this._exclusionMatches(e, contact);
        if (exclude) {
            if (!this.formData.reminderOptOuts.some(matchesForm)) {
                this.formData.reminderOptOuts.push({
                    phone: contact.phone || null,
                    email: contact.email || null,
                    name: contact.name || null
                });
            }
        } else {
            this.formData.reminderOptOuts = this.formData.reminderOptOuts.filter(e => !matchesForm(e));
        }
        campaignIds.forEach(cid => {
            const c = (this.campaigns || []).find(x => x.id === cid);
            if (!c) return;
            if (!Array.isArray(c.reminderExclusions)) c.reminderExclusions = [];
            const isMatch = e => this._exclusionMatches(e, contact);
            if (exclude) {
                if (!c.reminderExclusions.some(isMatch)) {
                    c.reminderExclusions.push({
                        phone: contact.phone || null,
                        email: contact.email || null,
                        name: contact.name || null
                    });
                }
            } else {
                c.reminderExclusions = c.reminderExclusions.filter(e => !isMatch(e));
            }
        });
    }

    async _reloadAfterResponseEdit() {
        // Re-fetch /contacts (cache was invalidated server-side), reset the
        // campaign-scope cache, and re-render the table. An error answer keeps
        // the list on screen — read as data it emptied the window.
        let httpStatus = null;
        const retryMarks = this._retryWatermarks(); // before the fetch, as in showContactStatusModal
        const trackingData = await this._mtFetch(`/api/message-tracking/${this.formId}/contacts`)
            .then(r => { httpStatus = r.status; return r.ok ? r.json() : null; })
            .then(d => (d ? d.contacts || [] : null))
            .catch(() => null);

        if (!trackingData) {
            // Shown right after a resend or an edit, so in the host's language
            // like the rest of the window (this line used to be English-only).
            if (window.showToast) {
                window.showToast(this.cTxt(httpStatus === 401 ? 'statusLoadExpired' : 'statusRefreshFailed'), 'info');
            }
            return;
        }
        this._contactStatusLoadFailed = false;
        this._statusRetryMarks = retryMarks;
        this._hideContactStatusLoadError();

        // Re-apply the same per-contact derived flags as showContactStatusModal.
        this.contactStatusData = trackingData.map(contact => {
            const fallbackFromWhatsApp = typeof contact.fallbackFrom === 'string'
                && contact.fallbackFrom.startsWith('whatsapp');
            const smsSent = !!contact.smsStatus?.sent;
            const smsFailed = !!(contact.smsStatus?.error || contact.smsStatus?.bounced);
            const smsFallbackAttempted = (smsSent || smsFailed) && (
                fallbackFromWhatsApp || (contact.whatsappStatus?.failed)
            );
            const isSMSFallback = smsSent && smsFallbackAttempted;
            const smsInFlight = smsSent && !smsFailed;
            const whatsappFailed = contact.whatsappStatus?.failed &&
                !contact.whatsappStatus?.delivered && !contact.whatsappStatus?.read;
            const emailBounced = !!(contact.emailStatus?.bounced || contact.emailStatus?.failed);
            const emailInFlight = !!contact.emailStatus?.sent && !emailBounced;
            // Hard failure regardless of RSVP status (task #154) — mirrors
            // showContactStatusModal's derivation exactly.
            const deliveryFailed = (whatsappFailed || emailBounced || smsFailed) &&
                !contact.emailStatus?.delivered && !contact.smsStatus?.delivered &&
                !smsInFlight && !emailInFlight &&
                !DashboardCampaigns.hostSentCoversFailures(contact);
            const hasAnyFailure = !!(contact.whatsappStatus?.failed || contact.emailStatus?.bounced || contact.emailStatus?.failed ||
                contact.smsStatus?.error || contact.smsStatus?.bounced);
            const errorCode = DashboardCampaigns.deliveryErrorCode(contact);
            return { ...contact, isSMSFallback, smsFallbackAttempted, deliveryFailed, hasAnyFailure, errorCode };
        });

        // Same send-time-failure injection as showContactStatusModal, so a refresh
        // doesn't silently drop the failed guests that have no tracking doc.
        this.contactStatusData = this._mergeSendTimeFailures(this.contactStatusData);
        this._attachRowTags(this.contactStatusData);

        // Preserve the active campaign scope when refreshing.
        const scope = document.getElementById('contactStatusCampaignFilter')?.value;
        if (scope && scope !== 'all') {
            this._allContactStatusData = this.contactStatusData;
            this.contactStatusData = this.contactStatusData.filter(c =>
                Array.isArray(c.campaignIds) && c.campaignIds.includes(scope)
            );
        } else {
            this._allContactStatusData = this.contactStatusData;
        }

        this.updateStatCards(this.contactStatusData);
        this.filterContactStatus();
    }

    _renderEditableNameCell(contact, idx) {
        const canEdit = Array.isArray(contact.trackingIds) && contact.trackingIds.length > 0;
        const name = (contact.name && contact.name !== 'Unknown') ? contact.name : '';
        const escaped = window.escapeHtml ? window.escapeHtml(name) : name;
        if (!canEdit) {
            return name ? escaped : `<span class="text-muted">${this.cTxt('guestFallback')}</span>`;
        }
        const display = name
            ? `<span class="name-text">${escaped}</span>`
            : `<span class="name-text text-muted fst-italic">${this.cTxt('addName')}</span>`;
        // The guest name itself is the edit trigger (no separate pencil) — the
        // row-click handler skips .editable-name-cell, so clicking the name opens
        // the inline editor instead of expanding the row.
        return `
            <span class="editable-name-cell" data-row-idx="${idx}" role="button" tabindex="0" title="${this.cAttr('editNameTip')}" style="display:inline-flex;align-items:center;gap:6px;cursor:pointer;">
                ${display}
            </span>
        `;
    }

    _bindNameEditHandlers(tableBody) {
        tableBody.querySelectorAll('.editable-name-cell').forEach(cell => {
            const enter = (e) => {
                if (cell.querySelector('.name-edit-input')) return; // already editing
                e.stopPropagation();
                this._enterNameEditMode(cell);
            };
            cell.addEventListener('click', enter);
            cell.addEventListener('keydown', (e) => {
                // While the inline input is active, let keystrokes (especially the
                // SPACE bar — needed for multi-word names) reach the input. Without
                // this guard the cell's own Space-to-edit shortcut preventDefault'd
                // the space before it could be typed.
                if (cell.querySelector('.name-edit-input')) return;
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); enter(e); }
            });
        });
    }

    _enterNameEditMode(cell) {
        const idx = parseInt(cell.dataset.rowIdx, 10);
        const contact = this._renderedContacts?.[idx];
        if (!contact) return;
        const current = (contact.name && contact.name !== 'Unknown') ? contact.name : '';

        const safeCurrent = window.escapeHtml ? window.escapeHtml(current) : current;
        cell.innerHTML = `
            <span class="input-group input-group-sm" style="max-width:240px;" onclick="event.stopPropagation()">
                <input type="text" class="form-control form-control-sm name-edit-input" value="${safeCurrent}" maxlength="200" placeholder="${this.cAttr('nameEditPlaceholder')}">
                <button type="button" class="btn btn-success name-edit-save" title="${this.cAttr('nameEditSave')}" aria-label="${this.cAttr('nameEditSave')}"><i class="bi bi-check-lg"></i></button>
                <button type="button" class="btn btn-outline-secondary name-edit-cancel" title="${this.cAttr('nameEditCancel')}" aria-label="${this.cAttr('nameEditCancel')}"><i class="bi bi-x-lg"></i></button>
            </span>
        `;

        const input = cell.querySelector('.name-edit-input');
        const saveBtn = cell.querySelector('.name-edit-save');
        const cancelBtn = cell.querySelector('.name-edit-cancel');
        input.focus();
        input.select();

        const cancel = (e) => {
            if (e) e.stopPropagation();
            cell.outerHTML = this._renderEditableNameCell(contact, idx);
            const refreshed = document.querySelector(`.editable-name-cell[data-row-idx="${idx}"]`);
            if (refreshed) this._bindNameEditHandlers(refreshed.parentElement);
        };
        const save = async (e) => {
            if (e) e.stopPropagation();
            const newName = input.value.trim();
            if (newName === current) { cancel(); return; }
            saveBtn.disabled = true;
            cancelBtn.disabled = true;
            input.disabled = true;
            try {
                await this._saveContactName(contact, newName);
                contact.name = newName || 'Unknown';
                cancel(); // re-render the cell in display mode with the new value
            } catch (err) {
                console.error('Failed to save contact name:', err);
                // The thrown messages (no form context, the server's 400/404
                // text) are English developer detail, so the host gets the
                // translated sentence and the detail stays in the console.
                if (window.showToast) window.showToast(this.cTxt('nameSaveFailed'), 'error');
                saveBtn.disabled = false;
                cancelBtn.disabled = false;
                input.disabled = false;
                input.focus();
            }
        };

        saveBtn.addEventListener('click', save);
        cancelBtn.addEventListener('click', cancel);
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        });
    }

    async _saveContactName(contact, newName) {
        if (!this.formId) throw new Error('No form context');
        if (!Array.isArray(contact.trackingIds) || contact.trackingIds.length === 0) {
            throw new Error('Contact has no editable tracking records');
        }
        const res = await this._mtFetch(`/api/message-tracking/${this.formId}/contact-name`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                trackingIds: contact.trackingIds,
                recipientName: newName
            })
        });
        if (!res.ok) {
            let msg = 'Server rejected the update';
            try { const data = await res.json(); if (data?.error) msg = data.error; } catch (_) {}
            throw new Error(msg);
        }
        return res.json();
    }

    // Save Name + Phone + Email together (the pencil modal's "Edit contact
    // details" section). Reuses the contact-name endpoint, which now accepts
    // optional phone/email alongside the name.
    async _saveContactDetails(contact, { name, phone, email }) {
        if (!this.formId) throw new Error('No form context');
        if (!Array.isArray(contact.trackingIds) || contact.trackingIds.length === 0) {
            throw new Error('Contact has no editable tracking records');
        }
        // The corrected number is stored as we will dial it (phone-resolve.js),
        // not as typed: a "06…" correction must not become an Israeli number
        // at the next reminder.
        const V = window.ValidationUtils;
        const dialPhone = phone && V && V.normalizeInternationalPhone ? V.normalizeInternationalPhone(phone) : phone;
        const res = await this._mtFetch(`/api/message-tracking/${this.formId}/contact-name`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                trackingIds: contact.trackingIds,
                recipientName: name,
                recipientPhone: dialPhone,
                recipientEmail: email
            })
        });
        if (!res.ok) {
            let msg = 'Server rejected the update';
            try { const data = await res.json(); if (data?.error) msg = data.error; } catch (_) {}
            throw new Error(msg);
        }
        return res.json();
    }

    /**
     * After a host corrects a contact's phone or email, offer to send the
     * message again to the corrected destination (task #572).
     *
     * Reuses the campaign's own "add contacts" dispatch — the same path used
     * when a guest is added after launch — so the resend inherits the campaign's
     * channel mix, message, tracking and billing rather than inventing a second
     * send route. The new tracking record joins this contact's history, so the
     * activity log shows the retry alongside the original attempt.
     */
    _offerResendAfterCorrection(modalEl, contact, { phoneChanged, emailChanged, prevPhone, prevEmail }) {
        const host = modalEl.querySelector('#cdResendSlot')
            || modalEl.querySelector('#cdStatus')?.parentElement;
        if (!host) return;
        modalEl.querySelector('#cdResendPrompt')?.remove();

        const campaignId = this._resendCampaignIdFor(contact);
        const dest = phoneChanged ? contact.phone : contact.email;
        const updated = this.cTxt(phoneChanged && emailChanged ? 'reUpdatedDetails' : (phoneChanged ? 'reUpdatedNumber' : 'reUpdatedEmail'));
        const esc = (s) => this._escape(String(s == null ? '' : s));
        const resendMethod = phoneChanged && emailChanged ? 'both' : (phoneChanged ? 'mobile' : 'email');
        // A corrected number can turn out to be a US one, texted on resend; the
        // add-contacts consent rule applies (_needsSmsConsentFor).
        const resendCampaign = campaignId ? (this.campaigns || []).find(c => c && c.id === campaignId) : null;
        const needsSms = !!campaignId && phoneChanged
            && this._needsSmsConsentFor(resendCampaign, [{ phone: contact.phone, email: contact.email }], resendMethod);

        const box = document.createElement('div');
        box.id = 'cdResendPrompt';
        box.className = 'alert alert-info d-flex align-items-start gap-2 mt-2 mb-0 py-2 px-3 small';
        box.setAttribute('role', 'status');
        box.innerHTML = campaignId
            ? `<i class="bi bi-arrow-repeat flex-shrink-0" style="margin-top:0.15rem;"></i>
               <div class="flex-grow-1">
                 <div>${esc(updated)} ${this.cTxt('reResendAsk', { dest: `<strong dir="ltr">${esc(dest)}</strong>` })}</div>
                 ${needsSms ? `
                 <div class="form-check mt-2 mb-0">
                   <input class="form-check-input" type="checkbox" id="cdResendSmsConsent">
                   <label class="form-check-label" for="cdResendSmsConsent">${this.cTxt('acSmsConsent')}</label>
                 </div>
                 <div class="text-muted" style="font-size:0.75rem;">${this.cTxt('acSmsDisclosure')}</div>` : ''}
                 <button type="button" class="btn btn-sm btn-primary mt-2" id="cdResendBtn"${needsSms ? ' disabled' : ''}>
                   <i class="bi bi-send me-1"></i>${esc(this.cTxt('reResend'))}
                 </button>
                 <span class="ms-2 align-middle" id="cdResendStatus"></span>
               </div>`
            // No single campaign to resend: the contact's history spans several,
            // so picking one for them would be a guess.
            : `<i class="bi bi-info-circle flex-shrink-0" style="margin-top:0.15rem;"></i>
               <div>${esc(updated)} ${this.cTxt('reMultiMessage', { addContacts: `<strong>${esc(this.cTxt('addContacts'))}</strong>` })}</div>`;
        host.appendChild(box);
        if (!campaignId) return;

        const smsBox = box.querySelector('#cdResendSmsConsent');
        smsBox?.addEventListener('change', () => {
            const b = box.querySelector('#cdResendBtn');
            if (b) b.disabled = !smsBox.checked;
        });

        box.querySelector('#cdResendBtn')?.addEventListener('click', async (e) => {
            const btn = e.currentTarget;
            const st = box.querySelector('#cdResendStatus');
            if (needsSms && !(smsBox && smsBox.checked)) return;
            btn.disabled = true;
            btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${esc(this.cTxt('reSending'))}`;
            try {
                const token = await (window.firebaseAuth?.currentUser || firebase.auth().currentUser).getIdToken();
                const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/add-contacts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        contacts: [{
                            name: contact.name || '',
                            phone: contact.phone || '',
                            email: contact.email || '',
                            // The campaign contact still holds the details it
                            // was sent to; the receipts let the server find it
                            // and keep the guest's household on the resend.
                            trackingIds: Array.isArray(contact.trackingIds) ? contact.trackingIds : []
                        }],
                        // This person is ALREADY on the campaign — that is the
                        // whole premise of correcting their details. Without this
                        // the route recognised them as a duplicate, updated the
                        // row, sent nothing, and still answered success (task
                        // #572 round 2: "it said it resent the message but I
                        // never received it").
                        resend: true,
                        // …and send it on the channel the host actually fixed. A
                        // corrected WhatsApp number means WhatsApp; without this
                        // the channel came from the campaign as a whole, so a
                        // fixed number could go out as an email.
                        sendingMethod: resendMethod,
                        ...(needsSms ? { smsConsentConfirmed: true } : {})
                    })
                });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok || data.success === false) {
                    throw new Error(this._resendFailureText(data) || data.error || data.message || `Resend failed (${resp.status})`);
                }
                // The server recorded it; the next add on this campaign need not ask.
                if (needsSms && resendCampaign && !resendCampaign.smsConsentConfirmedAt) resendCampaign.smsConsentConfirmedAt = new Date();
                btn.remove();
                // No approved WhatsApp template yet: the server submitted one and
                // queued this guest, who gets it on approval. "Sent" would be false.
                const sentKey = data.queued ? 'reQueued'
                    : (phoneChanged && emailChanged ? 'reSentBoth' : (phoneChanged ? 'reSentNumber' : 'reSentEmail'));
                if (st) { st.textContent = this.cTxt(sentKey); st.className = 'text-success'; }
                // The history behind the modal now has a new attempt on it —
                // refresh the numbers AND redraw the cards holding them, or the
                // resend lands in the data and nowhere on screen (task #584).
                // Isolated from the send's try/catch: the message has already
                // gone out, so a refresh that throws must never repaint a
                // successful resend as a red failure.
                try {
                    if (typeof this.loadMessageTrackingStats === 'function') {
                        Promise.resolve(this.loadMessageTrackingStats())
                            .then(() => this.renderCampaigns())
                            .catch(() => {});
                    }
                } catch (refreshErr) {
                    console.warn('Post-resend refresh skipped:', refreshErr?.message);
                }
            } catch (err) {
                console.error('Resend after correction failed:', err);
                btn.disabled = false;
                btn.innerHTML = `<i class="bi bi-send me-1"></i>${esc(this.cTxt('reResend'))}`;
                if (st) { st.textContent = err?.message || this.cTxt('reResendFailed'); st.className = 'text-danger'; }
            }
        });
    }

    // "2 of 3 sent · 1 paused by WhatsApp until 14:10, try again then" for an
    // Add Contacts pass that reached fewer guests than it was given, from the
    // server's guest counts (results.guests); '' when everyone was reached,
    // when the pass was held by a gate (review, verification, credits), which
    // the card shows, or when the server could not count guests.
    _additionShortfallText(g) {
        if (!g || g.held || g.known === false || !(g.total > 0) || g.sent >= g.total) return '';
        const parts = [this.cTxt('acSentOf', { sent: g.sent || 0, total: g.total })];
        if (g.skipped > 0) {
            const lifts = g.skipLiftsAt ? new Date(g.skipLiftsAt) : null;
            // Only the cooldown skips are "paused by WhatsApp until"; an
            // opted-out or unsubscribed guest in the same pass is plainly
            // skipped (M3 re-review). An older server sends no split.
            const cool = typeof g.skippedCooldown === 'number' ? Math.min(g.skippedCooldown, g.skipped) : g.skipped;
            if (cool > 0 && lifts && !isNaN(lifts) && lifts.getTime() > Date.now()) {
                parts.push(this.cTxt('acSkippedUntil', { n: cool, time: this._skipTimeStr(lifts) }));
                if (g.skipped - cool > 0) parts.push(this.cTxt('acSkipped', g.skipped - cool));
            } else {
                parts.push(this.cTxt('acSkipped', g.skipped));
            }
        }
        if (g.failed > 0) parts.push(this.cTxt('acFailed', g.failed));
        if (g.deferred > 0) parts.push(this.cTxt('acDeferred', g.deferred));
        if (g.notSent > 0) parts.push(this.cTxt('acNotSent', g.notSent));
        // Named, not just counted: who could not be tried, the details to fix
        // are on their row in the message's Not delivered list.
        if (g.unreachable > 0) {
            const names = (g.unreachableGuests || []).map(u => u && u.name).filter(Boolean);
            parts.push(this.cTxt('acUnreachable', g.unreachable) + (names.length ? ': ' + names.slice(0, 3).join(', ') + (names.length > 3 || g.unreachable > 3 ? '…' : '') : ''));
        }
        return parts.join(' · ');
    }

    // A resend that reached nobody, in the host's language, from the code the
    // add-contacts route returns. Every such answer used to say "check the
    // number", which is wrong advice when WhatsApp is only pausing messages
    // to a number that is fine (audit 2026-10-07, review #14).
    _resendFailureText(data) {
        const code = data && data.code;
        if (code === 'resend_cooldown') {
            const lifts = data.liftsAt ? new Date(data.liftsAt) : null;
            return lifts && !isNaN(lifts) && lifts.getTime() > Date.now()
                ? this.cTxt('reResendCooldown', { time: this._skipTimeStr(lifts) })
                : this.cTxt('reResendCooldownNoTime');
        }
        if (code === 'resend_not_on_whatsapp') return this.cTxt('skipRowNoWa');
        if (code === 'resend_opted_out') return this.cTxt('skipRowOptOut');
        if (code === 'resend_unsubscribed') return this.cTxt('skipRowUnsub');
        if (code === 'resend_failed') return this.cTxt('reResendNotSent');
        return '';
    }

    /**
     * The ONE campaign a corrected contact can be resent from, or null.
     *
     * A contact's history can span several messages; resending "the previous
     * message" is only unambiguous when there is exactly one. With more, the
     * prompt points at Add Contacts instead of guessing which the host meant.
     */
    _resendCampaignIdFor(contact) {
        const ids = Array.isArray(contact.campaignIds)
            ? [...new Set(contact.campaignIds.filter(Boolean))] : [];
        // Which message to send again: the one the Status window is open on,
        // else the one message that failed for them, else their only message.
        // A guest with an invitation, a later message and a personal link had
        // three ids and got no Resend at all, though only one had failed
        // (chat 6582f145).
        const scope = this._currentCampaignScope();
        // A failure the host then sent from their own WhatsApp is still the
        // message that failed them (resendOwedAfterCorrection).
        const failed = [...new Set([...(contact.failedCampaignIds || []), ...(contact._sendTimeFailedCampaigns || []),
            ...(contact.hostSentCampaignIds || [])])]
            .filter(x => ids.includes(x));
        const id = (scope && ids.includes(scope)) ? scope
            : failed.length === 1 ? failed[0]
            : ids.length === 1 ? ids[0] : null;
        if (!id) return null;
        // Only a finished campaign can take an immediate add-contacts send; a
        // scheduled one would merge them in silently and send later, which is
        // not what "Resend" promises.
        const campaign = (this.campaigns || []).find(c => c && c.id === id);
        const status = campaign?.status || '';
        // A paused message has sentCount > 0 once part of it went (a provider
        // outage stops it mid-send), but add-contacts refuses 'paused', so the
        // Resend could only fail; its guests go out when it is resumed.
        if (status === 'paused' || status === 'processing') return null;
        const finished = ['sent', 'completed', 'processed', 'partially_sent', 'archived'].includes(status)
            || (campaign?.sentCount || 0) > 0;
        return finished ? id : null;
    }

    /**
     * The channel add-contacts gives new guests when the caller names none.
     * Mirrors the server's inferDefaultSendingMethod (campaigns.controller.js,
     * addContactsToCampaign).
     */
    _addContactsSendingMethod(campaign) {
        const list = Array.isArray(campaign?.contacts) ? campaign.contacts : [];
        const hadEmail = (campaign?.emailCount || 0) > 0
            || list.some(c => c && (c.method === 'email' || c.method === 'both'));
        const hadWa = (campaign?.whatsappCount || 0) > 0
            || list.some(c => c && (c.method === 'whatsapp' || c.method === 'mobile' || c.method === 'both'));
        if (hadEmail && hadWa) return 'both';
        return hadWa ? 'mobile' : 'email';
    }

    /**
     * Could add-contacts text this guest? The wizard's _contactMayGetSms rule:
     * a US number goes SMS-direct (server takesSmsDirect) and any other number
     * we text (NanpRegion.mayBeTexted: +1, and Brazil since 2026-10-17) may be
     * texted after a WhatsApp failure (isSMSFallbackAllowed).
     * The phone is used unless the campaign is email-only and the guest has an
     * email (the server's resolveContactMethod), and is read the way the
     * server will dial it, in the campaign's country.
     */
    _addedContactMayGetSms(contact, sendingMethod, region) {
        const V = window.ValidationUtils;
        const raw = String(contact?.phone || '').trim();
        if (!raw) return false;
        const email = String(contact?.email || '').trim();
        if (sendingMethod === 'email' && email && (!V || V.isValidEmail(email))) return false;
        const phone = (V && V.normalizeInternationalPhone) ? V.normalizeInternationalPhone(raw, region || undefined) : raw;
        if (window.NanpRegion && window.NanpRegion.mayBeTexted) return window.NanpRegion.mayBeTexted(phone).allowed;
        const d = String(phone || '').replace(/\D/g, '');
        return d.length === 11 && d.startsWith('1');
    }

    /**
     * Add-contacts (and the resend after a corrected number) skip the wizard's
     * Send step, where SMS consent is asked. Since that box shows only when a
     * guest could be texted, a campaign sent to nobody textable (an
     * Ecuador-only list) never had it, and adding a US guest later texted them
     * with no consent on record (2026-10-07 audit). Ask when a guest being sent
     * to could be texted and the campaign has no smsConsentConfirmedAt.
     */
    _needsSmsConsentFor(campaign, contacts, sendingMethod) {
        if (campaign && campaign.smsConsentConfirmedAt) return false;
        const method = sendingMethod || (campaign ? this._addContactsSendingMethod(campaign) : 'both');
        return (contacts || []).some(c => this._addedContactMayGetSms(c, method, campaign && campaign.phoneDefaultCountry));
    }

    /**
     * Push a corrected phone/email into the host's address book too (task #572)
     * — otherwise the same wrong number returns on the next campaign, because
     * the correction only ever touched this campaign's tracking record.
     *
     * Best-effort and silent: the correction itself has already been saved and
     * confirmed, so a failure here must not present as a failed edit.
     */
    async _syncCorrectedContactToAddressBook(contact, { prevPhone, prevEmail }) {
        try {
            const user = window.firebaseAuth?.currentUser || (firebase.auth && firebase.auth().currentUser);
            if (!user) return;
            const token = await user.getIdToken();
            const res = await fetch('/api/users/me/contacts', { headers: { 'Authorization': `Bearer ${token}` } });
            if (!res.ok) return;
            const saved = (await res.json()).contacts || [];
            const norm = (s) => String(s || '').replace(/\D/g, '');
            const lower = (s) => String(s || '').trim().toLowerCase();
            // Match on what the contact USED to be — that is the stale record.
            const match = saved.find(c =>
                (prevPhone && norm(c.phone) && norm(c.phone) === norm(prevPhone))
                || (prevEmail && lower(c.email) && lower(c.email) === lower(prevEmail)));
            if (!match) return;
            await fetch('/api/users/me/contacts/' + encodeURIComponent(match.id), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    name: contact.name || match.name,
                    email: contact.email || '',
                    phone: contact.phone || '',
                    tags: match.tags || []
                })
            });
        } catch (err) {
            console.warn('Could not sync the corrected contact to the address book:', err?.message || err);
        }
    }

    // Guest tags (task #847) live on each message's stored guest list, not on
    // the tracking docs this window is built from, so the window had no way to
    // show them (support 2026-10-19: a host asked to see her guests grouped by
    // tag). A row takes the tags of its entry on every message it was on.
    _attachRowTags(rows) {
        const norm = DashboardCampaigns._tagKey;
        (rows || []).forEach((row) => {
            const seen = new Set();
            const tags = [];
            (Array.isArray(row.campaignIds) ? row.campaignIds : []).forEach((cid) => {
                const stored = this._storedCampaignContact(cid, row);
                (stored && Array.isArray(stored.tags) ? stored.tags : []).forEach((t) => {
                    const k = norm(t);
                    if (k && !seen.has(k)) { seen.add(k); tags.push(t); }
                });
            });
            row.tags = tags;
        });
        return rows;
    }

    // Re-reads every loaded row's tags after an edit and redraws the window,
    // keeping the picked filters.
    _refreshRowTags() {
        if (!Array.isArray(this.contactStatusData)) return;
        this._attachRowTags(this._allContactStatusData || []);
        this._attachRowTags(this.contactStatusData);
        if (this._contactStatusLoadFailed) return;
        if (!document.getElementById('campaignsContactStatusModal')?.classList.contains('show')) return;
        this._renderTagFilter(this.contactStatusData);
        this.filterContactStatus();
    }

    static _tagKey(tag) {
        return window.EventParts && window.EventParts.normalizeTag
            ? window.EventParts.normalizeTag(tag)
            : String(tag || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    // A status row's own entry in a campaign's STORED guest list (hydrated by
    // CampaignContacts.hydrateAll on load). Phone first, on the last 8 digits
    // the server matches with (normalization.js phoneMatchTail), then email.
    //
    // Only an entry that is certainly this guest: the same rule as the
    // server's applyStoredRetryMethods. Two guests can share a number, and the
    // first one on it is not necessarily this one. Several candidates with no
    // email or distinct name to tell them apart is no match (null), and Retry
    // then goes by the leg that failed.
    _storedCampaignContact(campaignId, row) {
        const campaign = (this.campaigns || []).find(x => x.id === campaignId);
        const list = Array.isArray(campaign?.contacts) ? campaign.contacts : [];
        if (!list.length || !row) return null;
        const tail = (p) => {
            const d = String(p || '').replace(/\D/g, '');
            return d.length >= 8 ? d.slice(-8) : d;
        };
        const mail = (e) => String(e || '').toLowerCase().trim();
        // 'Unknown' and 'Guest' are what a row says when it has no name.
        const nameKey = (n) => {
            const s = String(n || '').toLowerCase().replace(/\s+/g, ' ').trim();
            return (s === 'unknown' || s === 'guest') ? '' : s;
        };
        const t = tail(row.phone), e = mail(row.email), n = nameKey(row.name);
        // Only a value present on both sides can disagree. The name is just a
        // tie-break: a rename here changes the tracking docs, not this list.
        const clash = (s) => (e && mail(s.email) && mail(s.email) !== e)
            || (t && tail(s.phone) && tail(s.phone) !== t);
        const pick = (candidates) => {
            const fits = candidates.filter(s => !clash(s));
            if (fits.length === 1) return fits[0];
            const named = n ? fits.filter(s => nameKey(s.name) === n) : [];
            return named.length === 1 ? named[0] : null;
        };
        const byPhone = t ? list.filter(s => s && tail(s.phone) === t) : [];
        return (e && byPhone.find(s => mail(s.email) === e))
            || (byPhone.length ? pick(byPhone) : null)
            || (e ? pick(list.filter(s => s && mail(s.email) === e)) : null)
            || null;
    }

    /**
     * The method a Retry resends one guest on (audit 2026-10-07 #6).
     *
     * This used to read only whatsappStatus, which is null for a US guest
     * texted directly, so the server was told 'whatsapp' or 'email' and fired
     * a WhatsApp template at a US number. The stored contact's method is how
     * the message first went out; the server applies the same rule
     * (applyStoredRetryMethods) and this is the hint it narrows 'both' by.
     */
    _retryMethodFor(row, campaignId) {
        const stored = this._storedCampaignContact(campaignId, row);
        const method = stored && stored.method;
        const ws = row.whatsappStatus || {}, ss = row.smsStatus || {}, es = row.emailStatus || {};
        if (method === 'both') {
            // One leg already out: resend only the other, so nobody gets the
            // message twice. 'mobile', because the server routes the phone
            // half by country (US to SMS, everyone else to WhatsApp).
            const phoneOut = ws.delivered || ws.read || ss.delivered
                || (ws.sent && !ws.failed) || (ss.sent && !ss.error && !ss.bounced);
            const emailOut = es.delivered || es.opened || (es.sent && !es.bounced);
            if (phoneOut && !emailOut) return 'email';
            if (emailOut && !phoneOut) return 'mobile';
            return 'both';
        }
        if (method) return method;
        // Not on the stored list (edited since), or not certainly one entry on
        // it: go by the leg that failed.
        const phoneFailed = ws.failed || ss.error || ss.bounced;
        if (es.bounced && phoneFailed && row.email && row.phone) return 'both';
        if (es.bounced && !phoneFailed && row.email) return 'email';
        return row.phone ? 'mobile' : 'email';
    }

    /**
     * Retry failed contacts (Bucket 1 — "may work if you try again tomorrow").
     *
     * Task #668. This used to open the message wizard pre-filled with the
     * contacts — a whole new message the host had to walk through and pay for
     * again, opened BEHIND the status window they were already looking at
     * (createFollowupCampaign closes the campaign-contacts dialog, which is a
     * different one), so it read as a blank window appearing from nowhere.
     *
     * The real thing has existed on the server the whole time: it resends the
     * ORIGINAL message, to those recipients only, with nothing to re-enter.
     * The status window stays open and refreshes in place when it finishes.
     */
    async retryFailedContacts() {
        const retryable = this.failedBuckets?.retryable || [];
        if (!retryable.length) return;

        // The retry resends one specific message, so the contacts have to be
        // grouped by the message that failed them. Scoped to a single message
        // that is simply the scope; across all messages, each contact carries
        // the ids of the ones that failed (failedCampaignIds, plus the
        // send-time failures that never got a tracking doc).
        const scope = this._currentCampaignScope();
        const byCampaign = new Map();
        retryable.forEach((c) => {
            const ids = scope
                ? [scope]
                : [...new Set([
                    ...(Array.isArray(c.failedCampaignIds) ? c.failedCampaignIds : []),
                    ...(Array.isArray(c._sendTimeFailedCampaigns) ? c._sendTimeFailedCampaigns : [])
                ])];
            ids.filter(Boolean).forEach((cid) => {
                if (!byCampaign.has(cid)) byCampaign.set(cid, []);
                byCampaign.get(cid).push(c);
            });
        });

        if (byCampaign.size === 0) {
            // Nothing tells us which message to resend — the contact's failure
            // predates per-message tracking. Say so rather than guessing and
            // billing the host for the wrong send.
            if (window.showToast) {
                window.showToast(this.cTxt('retryWhichMessage'), 'warning');
            }
            return;
        }

        const total = [...byCampaign.values()].reduce((n, list) => n + list.length, 0);
        const nameOf = (cid) => ((this.campaigns || []).find((c) => c.id === cid) || {}).name || this.cTxt('retryAMessage');
        const scopeLine = byCampaign.size === 1
            ? this.cTxt('retryScopeOne', nameOf([...byCampaign.keys()][0]))
            : this.cTxt('retryScopeMany', byCampaign.size);
        const ok = confirm(
            `${this.cTxt(total === 1 ? 'retryConfirmOne' : 'retryConfirmMany', total)}\n\n`
            + `${scopeLine}\n`
            + `${this.cTxt('retryConfirmNobody')}\n`
            // The server sends a manual retry unbilled: each failed original was
            // refunded when its failure was reported (wallet.refundFailedSendCredit;
            // retry tracking docs carry billed:false). This line used to say the
            // resend costs credits, which it has not since 2026-09-15.
            + this.cTxt('retryConfirmFree')
        );
        if (!ok) return;

        const btn = document.getElementById('retryFailedBtn');
        const restore = btn ? btn.innerHTML : null;
        if (btn) { btn.disabled = true; btn.textContent = this.cTxt('retryResending'); }

        let sent = 0, stillFailed = 0;
        const problems = [];
        // Messages the server refused because a send of them is still running.
        // Kept apart from `problems`: when another message did go out, the
        // success toast has to say these did not, or the host reads "everyone
        // was retried", sees them still under Failed and presses again.
        const busyNames = [];
        // …and because a resend of them finished after this list loaded, so the
        // list may still show guests that resend reached (409 stale_view).
        const staleNames = [];
        // WHY nothing went out, when nothing goes out. The server skips a
        // number WhatsApp declined in the last 24 hours (Meta 131049) and one
        // that is not on WhatsApp at all, and it says which — but round 1 threw
        // that away and showed "Nobody could be reached this time", which sent a
        // host round the same button twice in thirty seconds (task #668 r2).
        const skipped = [];
        try {
            const authHeaders = await window.AuthedFetch.authHeaders();
            for (const [campaignId, list] of byCampaign) {
                const contacts = list.map((c) => ({
                    contact: {
                        phone: c.phone || '',
                        email: c.email || '',
                        name: c.name || 'Unknown',
                        // Send it back the way it went out the first time.
                        method: this._retryMethodFor(c, campaignId)
                    },
                    error: c.whatsappStatus?.errorMessage || c.smsStatus?.errorMessage
                        || c.emailStatus?.errorMessage || 'Delivery failed',
                    errorCode: c.errorCode || 'unknown'
                }));
                // How many resends of this message the list on screen had seen;
                // the server refuses the press if one has finished since.
                const seen = this._statusRetryMarks ? this._statusRetryMarks[campaignId] : null;
                try {
                    const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/retry-failed`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...authHeaders },
                        body: JSON.stringify(Number.isInteger(seen) ? { contacts, seenRetries: seen } : { contacts })
                    });
                    const data = await resp.json().catch(() => ({}));
                    if (data && data.success) {
                        sent += data.successCount || 0;
                        stillFailed += data.remainingFailed || 0;
                        (data.results?.skippedContacts || []).forEach(sk => skipped.push(sk));
                    } else if (resp.status === 409 && data && data.code === 'send_in_progress') {
                        // A send of this message is still running — the host's
                        // own, a scheduled one, added guests, or another retry.
                        busyNames.push(nameOf(campaignId));
                    } else if (resp.status === 409 && data && data.code === 'stale_view') {
                        // Nothing sent; the reload below shows who still needs it.
                        staleNames.push(nameOf(campaignId));
                    } else {
                        problems.push(`${nameOf(campaignId)}: ${data.error || data.message || `failed (${resp.status})`}`);
                    }
                } catch (e) {
                    problems.push(`${nameOf(campaignId)}: ${e?.message || e}`);
                }
            }
        } finally {
            if (btn) { btn.disabled = false; if (restore != null) btn.innerHTML = restore; }
        }

        if (window.showToast) {
            // One toast (a second replaces the first), so the notes ride along.
            const notes = this._retryNotes(staleNames, busyNames, byCampaign.size > 1);
            if (sent > 0) {
                window.showToast(this._retrySentLine(sent, stillFailed, notes), 'success');
            } else if (problems.length) {
                window.showToast([problems[0], ...notes].join(' '), 'error');
            } else if (notes.length) {
                window.showToast(notes.join(' '), busyNames.length ? 'warning' : 'info');
            } else {
                window.showToast(this._nothingResentMessage(skipped), 'warning');
            }
        }
        if (problems.length) console.error('Retry to failed contacts:', problems);
        if (busyNames.length) console.warn('Retry to failed contacts, still sending:', busyNames);

        // Refresh the window in place — it never closed. The messages pressed are
        // re-read first: the list's send-time failures and its resend count come
        // from them, and the live listener may not have caught up (see
        // _refreshCampaignDocs), which would refuse the next press as stale.
        await this._refreshCampaignDocs([...byCampaign.keys()]);
        await this._reloadAfterResponseEdit();
        this.renderCampaigns();
    }

    /**
     * How many manual resends of a message a list loaded now reflects: the
     * count on its campaign doc, less one still running (that resend's guests
     * are still in the list as failed). Sent with a Resend press as
     * seenRetries; a resend that finished after the list loaded makes the
     * server refuse the press (409 stale_view) instead of sending its guests
     * the message twice. null when the message isn't loaded here: the server
     * then skips the check.
     */
    _retryWatermark(campaignId) {
        const c = (this.campaigns || []).find((x) => x && x.id === campaignId);
        if (!c) return null;
        const n = Number(c.totalManualRetries) || 0;
        return Math.max(0, c.status === 'retrying' ? n - 1 : n);
    }

    /** _retryWatermark for every message loaded, keyed by campaign id. */
    _retryWatermarks() {
        const marks = {};
        (this.campaigns || []).forEach((c) => { if (c && c.id) marks[c.id] = this._retryWatermark(c.id); });
        return marks;
    }

    /**
     * Re-read these campaigns from Firestore into this.campaigns, best effort.
     * After a resend the live listener cannot be relied on to have the result:
     * it skips messages beyond its limit, a co-host on the narrow fallback
     * hears nothing of the owner's, and its own update can land after the
     * resend's answer. A stale copy would keep resent guests under Failed and
     * an old resend count, so every later press would come back stale_view.
     */
    async _refreshCampaignDocs(ids) {
        if (!window.firebase || !window.CampaignContacts || !(ids || []).length) return;
        const db = firebase.firestore();
        await Promise.all(ids.map(async (id) => {
            try {
                const data = await window.CampaignContacts.hydrate(db, await db.collection('bulk_campaigns').doc(id).get());
                const i = (this.campaigns || []).findIndex((c) => c && c.id === id);
                if (!data || i < 0) return;
                // trackingStats is the dashboard's own (loadMessageTrackingStats);
                // the card would lose its numbers until the next stats refresh.
                const { trackingStats } = this.campaigns[i];
                this.campaigns[i] = this._localizeAutoName({ id, ...data, ...(trackingStats ? { trackingStats } : {}) });
            } catch (e) {
                console.warn('Could not re-read a message after the resend:', e.message);
            }
        }));
    }

    /** "Sent again to N guests. M still couldn't be reached." plus any notes, translated. */
    _retrySentLine(sent, stillFailed, notes = []) {
        const parts = [sent === 1 ? this.cTxt('retrySentOne') : this.cTxt('retrySentMany', sent)];
        if (stillFailed > 0) {
            parts.push(stillFailed === 1 ? this.cTxt('retryStillFailedOne') : this.cTxt('retryStillFailedMany', stillFailed));
        }
        return [...parts, ...notes].join(' ');
    }

    /**
     * What the server held back from a resend, as sentences for the toast:
     * messages a resend finished after this list loaded (409 stale_view), and
     * messages still being sent (409 send_in_progress) — named when the resend
     * covered more than one message, so the host knows which to try again.
     */
    _retryNotes(staleNames, busyNames, multi) {
        const notes = [];
        (staleNames || []).forEach((name) => {
            notes.push(multi ? `${name}: ${this.cTxt('retryStaleView')}` : this.cTxt('retryStaleView'));
        });
        (busyNames || []).forEach((name) => {
            notes.push(multi ? `${name}: ${this.cTxt('retryBusy')}` : this.cTxt('retryBusy'));
        });
        return notes;
    }

    /**
     * Why a resend sent nothing, in the host's words (task #668 round 2).
     *
     * Two reasons the server skips a number, and they need opposite advice:
     * a Meta 131049 throttle lifts by itself and is worth waiting out, while a
     * number that is not on WhatsApp will never take one however many times the
     * button is pressed. "Try again later" was the same sentence for both, and
     * for neither did it say when later is.
     */
    _nothingResentMessage(skipped) {
        const list = Array.isArray(skipped) ? skipped : [];
        const cooled = list.filter(s => s && s.reason === 'cooldown');
        // Already has the message (the fallback email after their WhatsApp
        // failed, or their own email leg): the server leaves them out of a
        // retry, and they are not "not on WhatsApp".
        const reached = list.filter(s => s && s.reason === 'already_reached');
        const dead = list.filter(s => s && s.reason && s.reason !== 'cooldown' && s.reason !== 'already_reached');
        const parts = [];
        if (reached.length) {
            parts.push(this.cTxt(reached.length === 1 ? 'nothingReachedOne' : 'nothingReachedMany', reached.length));
        }
        if (cooled.length) {
            // The soonest a retry can do anything. Shown as a time today or a
            // day and time, in the host's own locale.
            const untils = cooled.map(x => Number(x.until)).filter(v => Number.isFinite(v) && v > Date.now());
            let when = this.cTxt('nothingTryLater');
            if (untils.length) {
                const at = new Date(Math.min(...untils));
                const sameDay = at.toDateString() === new Date().toDateString();
                // Optional call: tests drive this method on a bare stub.
                const loc = this._dateLocale?.();
                when = this.cTxt('nothingTryAfter', sameDay
                    ? at.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })
                    : at.toLocaleString(loc, { weekday: 'short', hour: '2-digit', minute: '2-digit' }));
            }
            parts.push(`${this.cTxt(cooled.length === 1 ? 'nothingCooledOne' : 'nothingCooledMany', cooled.length)} ${when}`);
        }
        if (dead.length) {
            parts.push(this.cTxt(dead.length === 1 ? 'nothingDeadOne' : 'nothingDeadMany', dead.length));
        }
        if (!parts.length) return this.cTxt('nothingNobody');
        return parts.join(' ');
    }

    // Self-serve recovery for a ghost WhatsApp template (Meta 132001). Kicks off the
    // server recreate→approve→canary→resend flow (runs for minutes server-side) and
    // surfaces progress. No extra credits are charged for the re-send.
    async recoverGhostTemplate(campaignId, btnEl) {
        if (!campaignId) return;
        const proceed = window.confirm(this.cTxt('ghostConfirm'));
        if (!proceed) return;
        const setBtn = (key, disabled) => { if (btnEl) { btnEl.disabled = disabled; btnEl.textContent = this.cTxt(key); } };
        try {
            setBtn('ghostStarting', true);
            const authHeaders = await window.AuthedFetch.authHeaders();
            const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/recover-ghost-template`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders },
                body: JSON.stringify({})
            });
            const data = await resp.json().catch(() => ({}));
            if (resp.status === 202 && data.started) {
                setBtn('ghostRecovering', true);
                if (window.showToast) window.showToast(this.cTxt('ghostStarted'), 'success');
            } else if (resp.status === 409 && data.code === 'send_in_progress') {
                // A send or resend of this message is running; nothing started.
                setBtn('ghostButton', false);
                if (window.showToast) window.showToast(this.cTxt('retryBusy'), 'warning');
            } else if (resp.status === 409 && data.detection) {
                // No longer the ghost signature: nothing to rebuild. This
                // branch used to say "a recovery is already in progress" and
                // lock the button.
                setBtn('ghostButton', false);
                if (window.showToast) window.showToast(this.cTxt('ghostNotNeeded'), 'info');
            } else if (resp.status === 409) {
                // A recovery is mid-flight. The server's reason is English;
                // the host's own words instead.
                setBtn('ghostRecovering', true);
                if (window.showToast) window.showToast(this.cTxt('ghostAlready'), 'info');
            } else {
                setBtn('ghostButton', false);
                if (window.showToast) window.showToast(this.cTxt('ghostStartFailed'), 'error');
                console.warn('Ghost recovery refused:', resp.status, data.reason || data.error || '');
            }
        } catch (e) {
            setBtn('ghostButton', false);
            if (window.showToast) window.showToast(this.cTxt('ghostStartFailed'), 'error');
            console.warn('Ghost recovery failed to start:', e?.message || e);
        }
    }

    // Best-effort lookup of the campaign the Contact Status modal is showing, across
    // the property names this class has used for the current campaign / list.
    _currentCampaignForModal() {
        const id = this._contactStatusCampaignId || this.currentCampaign?.id || null;
        const list = this.campaigns || this.allCampaigns || this._campaigns || [];
        const fromList = Array.isArray(list) ? list.find(c => c && c.id === id) : null;
        return fromList || this.currentCampaign || null;
    }

    // Inject/refresh the ghost-recovery CTA inside the Contact Status modal's failed
    // area when the ghost signature is present. Fully defensive — never throws into
    // the modal render. Reflects ghostRecovery.status (in-progress / resolved / escalated).
    _renderGhostRecoveryCTA(failedContacts) {
        try {
            const ghostCount = (failedContacts || []).filter(c => DashboardCampaigns.isGhostFailureContact(c)).length;
            const campaign = this._currentCampaignForModal();
            const campaignId = campaign?.id || null;
            const waSent = campaign?.whatsappStats?.sentCount || 0;
            const isGhost = ghostCount >= 3 && waSent === 0;

            let banner = document.getElementById('ghostRecoveryCTA');
            if (!isGhost || !campaignId) { if (banner) banner.style.display = 'none'; return; }

            if (!banner) {
                const anchor = document.getElementById('failedBucketRetry')
                    || document.getElementById('failedBucketPermanent')
                    || document.getElementById('failedBucketChannel');
                if (!anchor || !anchor.parentElement) return;   // no safe place to mount
                banner = document.createElement('div');
                banner.id = 'ghostRecoveryCTA';
                banner.style.marginBottom = '12px';
                anchor.parentElement.insertBefore(banner, anchor);
            }
            banner.style.display = 'block';

            const status = campaign?.ghostRecovery?.status;
            if (status === 'resolved') {
                banner.className = 'alert alert-success';
                const n = campaign.ghostRecovery.recoveredCount;
                const resent = n != null ? this.cTxt(n === 1 ? 'ghostResentToOne' : 'ghostResentTo', n) : this.cTxt('ghostResent');
                banner.innerHTML = `<i class="bi bi-check-circle me-1"></i><strong>${this._escape(this.cTxt('ghostFixed'))}</strong> ${this._escape(resent)}`;
                return;
            }
            if (status === 'escalated') {
                banner.className = 'alert alert-danger';
                banner.innerHTML = `<i class="bi bi-exclamation-octagon me-1"></i>${this._escape(this.cTxt('ghostEscalated'))}`;
                return;
            }
            const inFlight = ['recreating', 'awaiting_approval', 'canary', 'resending'].includes(status);
            banner.className = 'alert alert-warning';
            banner.innerHTML = `
                <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap:8px">
                  <div>
                    <strong>${this._escape(this.cTxt('ghostIssue'))}</strong>
                    ${this._escape(this.cTxt('ghostIssueBody', ghostCount))}
                  </div>
                  <button type="button" id="ghostRecoveryBtn" class="btn btn-sm btn-dark" ${inFlight ? 'disabled' : ''}>${this._escape(this.cTxt(inFlight ? 'ghostRecovering' : 'ghostButton'))}</button>
                </div>`;
            const btn = banner.querySelector('#ghostRecoveryBtn');
            if (btn && !inFlight) btn.onclick = () => this.recoverGhostTemplate(campaignId, btn);
        } catch (e) {
            console.warn('[ghost-recovery CTA] render skipped:', e?.message || e);
        }
    }

    // Switch channel for contacts who need a different delivery method (Bucket 2).
    // "Remind via Email" goes by email only: these guests' WhatsApp or text is
    // what failed (131026 / 130472, a bounced or refused text), and handing
    // the wizard their phone too sent the same message again. Guests with no
    // email are pointed at their personal links, which the Failed list offers
    // on every row. That list closes when the email opens, so the host is
    // asked first, while it is still on screen; a toast saying "copy them
    // from the list below" vanished with the list (M2 review, 2026-10-07).
    switchChannelForFailedContacts() {
        const needsChannel = this.failedBuckets?.needsChannel || [];
        if (!needsChannel.length) return;
        const withEmail = needsChannel.filter(c => String(c.email || '').trim());
        const noEmail = needsChannel.length - withEmail.length;
        if (!withEmail.length) {
            if (typeof showToast === 'function') showToast(this.cTxt('fbChannelNoEmail'), 'info');
            return;
        }
        if (noEmail > 0 && typeof confirm === 'function'
            && !confirm(this.cTxt('fbChannelSomeNoEmail', { n: noEmail, m: withEmail.length }))) return;
        this.createFollowupCampaign(withEmail.map(c => ({ name: c.name, email: c.email })));
    }

    /**
     * When Meta's 131049 hold on these contacts lifts: 24 hours after the
     * latest such failure (WA_COOLDOWN_HOURS, server/constants/messaging.js;
     * the send gate skips the number until then). Null when none is a 131049
     * or no failure time is known.
     */
    static throttleLiftsAt(contacts) {
        let latest = 0;
        (contacts || []).forEach(c => {
            if (String((c && c.errorCode) || '') !== '131049') return;
            const raw = (c.whatsappStatus && c.whatsappStatus.failedAt) || c.lastUpdated || null;
            const ms = raw ? (raw.toDate ? raw.toDate() : new Date(raw)).getTime() : NaN;
            if (Number.isFinite(ms) && ms > latest) latest = ms;
        });
        return latest ? new Date(latest + 24 * 60 * 60 * 1000) : null;
    }

    // Single canonical contact loader for this campaign. The server's
    // /api/message-tracking endpoint computes rsvpStatus globally (across all
    // campaigns) so the same person can't appear as 'pending' here just because
    // they replied via a different campaign's link.
    async _getCampaignContactsWithStatus(campaignId) {
        const resp = await this._mtFetch(`/api/message-tracking/${this.formId}/contacts`);
        if (!resp.ok) throw new Error('Failed to load campaign contacts');
        const data = await resp.json();
        const allContacts = data.contacts || [];
        const inCampaign = allContacts.filter(c =>
            Array.isArray(c.campaignIds) && c.campaignIds.includes(campaignId)
        );
        const yesCount = inCampaign.filter(c => c.rsvpStatus === 'yes').length;
        const pendingCount = inCampaign.filter(c => c.rsvpStatus === 'pending').length;
        console.log(`[REMIND] formId=${this.formId} campaignId=${campaignId} | inCampaign=${inCampaign.length} | yes=${yesCount} | pending=${pendingCount}`);
        return inCampaign;
    }

    // Show the recipient review modal (the dynamic breakdown list), then open
    // the clean reminder drawer directly over the dashboard — NOT the multi-step
    // send wizard. The drawer composes the message and hands the finished
    // follow-up to the existing send pipeline. Returns null if the user cancels.
    async _reviewAndHandoff(contacts, mode) {
        const simpleContacts = contacts.map(c => ({ name: c.name, phone: c.phone, email: c.email }));
        if (window.RecipientReviewModal) {
            const result = await window.RecipientReviewModal.show({
                contacts: simpleContacts,
                mode,
                formId: this.formId
            });
            if (!result) return null;
            if (result.contacts.length === 0 && !result.startedEmpty) {
                if (window.showToast) window.showToast(this.cTxt('raNoneSelected'), 'info');
                return null;
            }
            // Contacts the host removed in the review — persisted on the reminder
            // doc so the dispatch-time audience re-resolution still skips them
            // (removals are scoped to THIS send, not global reminder exclusions).
            const removed = this._reviewRemovedContacts(simpleContacts, result.contacts);
            // Guests the host ADDED in the review ("Add guests" / CSV) have no
            // tracking, so the dispatch-time rebuild can't find them and they
            // were dropped while the drawer said "Sending to N". They ride along
            // as addedContacts and are merged in at dispatch.
            const added = DashboardCampaigns.remindAllAdditions(result.contacts, result.added);
            // "Sending to N" counts who will actually get it. A row that only
            // repeats a guest still on the list isn't a second message. A host
            // opt-out still binds an added guest at dispatch, so one with
            // reminders turned off isn't counted either, and the drawer says
            // why. They stay in addedContacts: turning their reminders back on
            // before the send still reaches them.
            const optedOut = added.filter(c => this._isOptedOutOfFormReminders(c));
            const uncounted = new Set([...(result.added || []).filter(c => !added.includes(c)), ...optedOut]);
            const toSend = uncounted.size ? result.contacts.filter(c => !uncounted.has(c)) : result.contacts;
            this._openRemindAllDrawer(toSend, mode, removed, added, { addedOptedOut: optedOut.length });
            return toSend;
        }
        // Fallback if the review modal didn't load — still use the drawer.
        this._openRemindAllDrawer(simpleContacts, mode);
        return simpleContacts;
    }

    // Who the host took off a recipient review list, as the {name, email,
    // phone} entries a reminder doc stores in reviewRemovals. Keyed on
    // phone|email, the same key both review lists dedupe on.
    _reviewRemovedContacts(original, kept) {
        const key = c => (c.phone || '').replace(/\D/g, '') + '|' + (c.email || '').toLowerCase().trim();
        const keptKeys = new Set((kept || []).map(key));
        return (original || [])
            .filter(c => !keptKeys.has(key(c)))
            .map(c => ({ name: c.name || '', email: c.email || '', phone: c.phone || '' }));
    }

    // Which guests added in Remind All's review window are real additions
    // (kept = the modal's result.contacts, added = its result.added, the same
    // objects). Compared only with the listed guests the host KEPT: the modal
    // has no inline edit, so removing a guest and adding them back is how a
    // wrong number gets fixed, and comparing with the removed row as well
    // dropped the corrected guest (review of 2026-10-07). Same identity rule
    // as the dispatch merge (server/utils/remind-all-added-contacts.js): a
    // guest with a phone is the same guest only if the phone matches, so
    // household members sharing one email each count; email decides only for
    // a guest with no phone. Phones also match on their last 8 digits (the
    // server's phoneMatchTail), since a re-typed number is normalised to E.164
    // while the tracked one may be stored as 0501234567, and each accepted
    // addition is checked against the ones before it, so "Sending to N"
    // counts a guest typed twice once, as the dispatch merge does.
    static remindAllAdditions(kept, added) {
        const digits = (p) => String(p || '').replace(/\D/g, '');
        const tail = (d) => (d.length >= 8 ? d.slice(-8) : '');
        const mail = (e) => String(e || '').toLowerCase().trim();
        const list = Array.isArray(added) ? added.filter(Boolean) : [];
        const addedSet = new Set(list);
        const listed = (Array.isArray(kept) ? kept : []).filter(c => c && !addedSet.has(c));
        const phones = new Set();
        const tails = new Set();
        const emails = new Set();
        const remember = (c) => {
            const p = digits(c.phone);
            if (p) { phones.add(p); if (tail(p)) tails.add(tail(p)); }
            const e = mail(c.email);
            if (e) emails.add(e);
        };
        listed.forEach(remember);
        return list.filter(c => {
            const p = digits(c.phone);
            const e = mail(c.email);
            const seen = p ? (phones.has(p) || (!!tail(p) && tails.has(tail(p)))) : (!e || emails.has(e));
            if (seen) return false;
            remember(c);
            return true;
        });
    }

    // Would the dispatch-time resolver drop this guest as a host opt-out? It
    // reads reminderExclusions from EVERY campaign of the form plus the form's
    // reminderOptOuts (resolveFormWideReminderContacts), so a guest with no
    // campaigns of their own, one added in the review, is checked against all
    // of them (_isContactExcludedForAnyCampaign only checks their own).
    _isOptedOutOfFormReminders(contact) {
        if (this._isContactOptedOutFormLevel(contact)) return true;
        return (this.campaigns || []).some(c => Array.isArray(c.reminderExclusions)
            && c.reminderExclusions.some(e => this._exclusionMatches(e, contact)));
    }

    // The "Remind All" popup — fully SELF-CONTAINED (no wizard hand-off). Uses the
    // same composer as a per-campaign reminder (message + personalization + link
    // chips + invitation image + send time), then writes a standalone reminder
    // campaign to the reviewed contacts. The existing scheduled-send dispatch
    // delivers it and charges credits server-side, exactly like a per-campaign
    // reminder — so nothing here re-implements billing.
    _openRemindAllDrawer(contacts, mode, removedContacts = [], addedContacts = [], { addedOptedOut = 0 } = {}) {
        const t = this.getWizardTranslations();
        const audienceByMode = { 'remind-pending': 'no_response', 'remind-yes': 'attending', 'remind-maybe': 'maybe', 'remind-no': 'declined', 'remind-all': 'all' };
        const audience = audienceByMode[mode] || 'no_response';
        const count = contacts.length;
        const pad = n => String(n).padStart(2, '0');
        const now = new Date();
        const localVal = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:00`;

        // Registry / directions URLs prefill from the form (host can override via
        // the chips). The wedding-website URL prefills from the form if it has one.
        const fd = this.formData || {};
        const formRegistry = String(fd.giftRegistryUrl || '').trim();
        const formAddr = String(fd.locationAddress || '').trim();
        const formMaps = String(fd.customGoogleMapsUrl || '').trim()
            || (formAddr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formAddr)}` : '');
        const lang = fd.defaultLanguage || this.dashboardLanguage || 'en';
        const defLabels = (window.QR_REMINDER_BTN_LABELS && (window.QR_REMINDER_BTN_LABELS[lang] || window.QR_REMINDER_BTN_LABELS.en)) || { rsvp: 'RSVP Here', registry: 'View Registry', directions: 'Get Directions', website: 'Wedding Website' };
        // Form invitation photo (if any) is the default image; host can swap/remove.
        const eventPhotoUrl = fd.bulkImageUrl || fd.eventImage || fd.imageUrl || null;

        const bodyHtml = this._reminderComposerBodyHtml({
            localVal,
            recip: audience,
            buttons: ['rsvp'],
            regVal: formRegistry,
            dirVal: formMaps,
            wazeVal: (String(fd.customWazeUrl || '').trim() || (formAddr ? `https://waze.com/ul?q=${encodeURIComponent(formAddr)}` : '')),
            gmapsVal: formMaps,
            webVal: String(fd.rsvpWebsiteUrl || ''),
            livestreamVal: String(fd.livestreamUrl || '').trim(),
            defLabels,
            cfgLabels: {},
            message: this._defaultReminderMessage(audience),
            eventPhotoUrl,
            customImageUrl: '',
            photoIncluded: !!eventPhotoUrl,
            audienceNote: (count === 0
                ? (t.remindAllPreConfigure || 'No matching guests right now — this reminder will automatically go to whoever matches when it sends.')
                : (t.remindAllSendingTo || 'Sending to <strong>{n}</strong> selected guest(s).').replace('{n}', count))
                + (addedOptedOut > 0
                    ? `<span class="d-block small text-muted mt-1">${(t.remindAllAddedOptedOut || "{n} guest(s) you added have reminders turned off for this event, so they won't get this one.").replace('{n}', addedOptedOut)}</span>`
                    : ''),
            forceSchedule: count === 0,
            // Message & Translations (task #453) — a standalone reminder has no
            // parent campaign, so the languages come straight off the form.
            sourceLanguage: lang,
            formLanguages: this._reminderFormLanguages(null),
            translatedMessage: '',
            eventTitle: fd.title || fd.formTitle || '',
            includeQR: false
        });

        this._openReminderModal({
            id: 'remindAllDrawer',
            composeLang: this.campaigns?.find(c => c.formId === this.formId)?.sourceLanguage,
            titleHtml: t.remindAllTitle || 'Send a reminder',
            subHtml: (t.remindAllSubtitle || '{n} recipients').replace('{n}', count),
            bodyHtml,
            formId: this.formId,
            primaryLabel: t.reminderCreateBtn || 'Send reminder',
            primaryIcon: 'bi-send',
            onPrimary: async (el, oc) => {
                const v = this._collectReminderComposerValues(el);
                if (!v) return;
                const img = this._collectReminderImage(el, eventPhotoUrl);
                const saveBtn = el.querySelector('#reminder-drawer-save');
                // The one failure that is the host's INPUT rather than ours has
                // to be caught while the drawer is still open — a fixable
                // problem belongs next to the thing that needs fixing, not in a
                // toast over a closed drawer (task #578).
                const sendable = (contacts || []).some((c) =>
                    (c.email && String(c.email).trim()) || (c.phone && String(c.phone).trim()));
                if (contacts.length && !sendable) {
                    alert((t.reminderCreateFailed || 'Failed to create reminder: ') + this.cTxt('raNoValidRecipients'));
                    return;
                }
                saveBtn.disabled = true;
                // Everything else runs with the drawer already closed — see the
                // matching note on the per-campaign reminder drawer.
                oc.hide();
                const toast = window.languageHelpers?.showToast || window.showToast;
                try {
                    await this._createRemindAllCampaign(contacts, v, img, removedContacts, { allowEmpty: contacts.length === 0, addedContacts });
                    // Same as the per-campaign drawer above (task #610).
                    await this.loadCampaigns();
                } catch (err) {
                    console.error('Error sending Remind All:', err);
                    const msg = (t.reminderCreateFailed || 'Failed to create reminder: ') + (err.message || err);
                    if (toast) toast(msg, 'error'); else alert(msg);
                }
            }
        });
    }

    // Who pays for a standalone reminder — {userId, userEmail, userName}.
    //
    // Normally the signed-in host, and for a co-owner or an org member still
    // themselves: charging the sender is deliberate, and the per-form shared
    // credit pool is what lets co-hosts spend each other's credits.
    //
    // The exception is PLATFORM STAFF setting a reminder up on a host's behalf.
    // The send path charges campaign.userId, so a staff-stamped uid billed
    // support's wallet, left the host's balance untouched, and — once support
    // ran dry — failed the send outright with "insufficient credits" while the
    // host had hundreds of credits sitting there (task #553). Every other flow
    // already resolves the payer as the form owner: the wizard's
    // getCurrentUserId(), loadCampaigns' queryUserId, loadWalletBalance.
    //
    // The admin claim is read from the ID token rather than window.isAdminMode
    // because that flag is entry-path dependent — viewEventResponses() and the
    // ?formId= direct-link branch of dashboard.html both clear it — and it is
    // also the exact condition firestore.rules requires to write a campaign
    // whose userId isn't its author (bulk_campaigns create). Anything else
    // falls back to the signed-in user, which is what the rules will accept.
    async _isPlatformAdmin(user) {
        try {
            const u = user && typeof user.getIdTokenResult === 'function' ? user : null;
            if (!u) return false;
            const r = await u.getIdTokenResult();
            return !!(r && r.claims && r.claims.admin === true);
        } catch (e) {
            return false;
        }
    }

    async _remindAllPayer(fd, user) {
        const self = {
            userId: user.uid || fd.owner || null,
            userEmail: user.email || null,
            userName: user.displayName || null
        };
        if (!fd.owner || fd.owner === user.uid) return self;
        if (!(await this._isPlatformAdmin(user))) return self;
        console.log('👑 Admin mode: charging the reminder to the form owner:', fd.owner);
        return { userId: fd.owner, userEmail: fd.ownerEmail || null, userName: null };
    }

    // Build + persist a STANDALONE reminder campaign for the "Remind All" flow.
    // Modeled on _createReminderChild, but with no parent: the audience is
    // re-resolved server-side AT DISPATCH (scope:'form_contacts'), deriving the
    // RSVP link from the form (server builds form.html?id=<slug> for reminder
    // campaigns) and charging credits server-side at send.
    async _createRemindAllCampaign(contacts, v, img, removedContacts = [], { allowEmpty = false, addedContacts = [] } = {}) {
        const db = firebase.firestore();
        const fd = this.formData || {};
        const user = (firebase.auth && firebase.auth().currentUser) || {};
        const formId = this.formId;
        const campaignId = `remindall_${formId}_${Date.now()}`;
        const nowIso = new Date().toISOString();

        // Explicit recipients + a per-contact delivery method — reach everyone on
        // whatever channel they have (mirrors the wizard's both-channels mapping).
        const toRecipient = (c) => {
            const hasEmail = !!(c.email && String(c.email).trim());
            const hasPhone = !!(c.phone && String(c.phone).trim());
            const method = (hasEmail && hasPhone) ? 'both' : (hasEmail ? 'email' : 'mobile');
            return { name: c.name || '', email: c.email || '', phone: c.phone || '', method };
        };
        const recipients = (contacts || []).map(toRecipient).filter(c => c.email || c.phone);
        // Guests added in the review window, stamped the same way.
        const added = (addedContacts || []).map(toRecipient).filter(c => c.email || c.phone);
        // Pre-configured empty reminders (task #300) are legitimate: the
        // audience re-resolves server-side at dispatch, so the list fills
        // itself with whoever matches at send time.
        // Shown to the host after "Failed to create reminder: ".
        if (!recipients.length && !allowEmpty) throw new Error(this.cTxt('raNoValidRecipients'));

        const baseMessage = (v.message && v.message.trim()) ? v.message : this._defaultReminderMessage(v.audience);
        const eventTitle = fd.eventTitle || fd.title || fd.formTitle || 'Your event';

        // Message & Translations (task #545): this flow used to hardcode
        // campaignLanguageMode:'single' and drop the panel's selection entirely —
        // the host ticked a second language, saw "Translations ready", and the
        // saved doc (and therefore the preview, the reopened editor, and the
        // actual send) carried English only. Resolve exactly like the
        // per-campaign reminder path; a standalone reminder has no parent, so
        // the source language comes from the form.
        const langRes = await this._resolveReminderTranslation(
            { sourceLanguage: fd.defaultLanguage || this.dashboardLanguage || 'en' },
            baseMessage, v.languageMode, v
        );

        // The audience is ALWAYS re-resolved server-side AT DISPATCH
        // (reminderContactsResolved:false + scope:'form_contacts' → the server
        // recomputes "all the form's contacts with this RSVP status" the moment
        // it sends), so a guest who answers between scheduling and send time is
        // correctly dropped from a "hasn't responded" nudge (and vice versa) —
        // exactly like a per-campaign row reminder (task #133; a previous 15-min
        // "keep the reviewed list" carve-out froze near-future sends and missed
        // status changes). The reviewed list is kept on the doc as a preview of
        // expected reach; guests the host removed in the review are persisted as
        // reviewRemovals so the dispatch-time resolution still skips them, and
        // guests they added (never messaged, so not in tracking) as
        // addedContacts, which the resolution merges in.

        const campaign = {
            campaignId,
            // Standalone follow-up reminder — NO parent.
            isReminderCampaign: true,
            reminderContactsResolved: false,
            parentCampaignId: null,
            contacts: recipients,
            totalContacts: recipients.length,
            reviewRemovals: (removedContacts || []).map(c => ({
                name: c.name || '', email: c.email || '', phone: c.phone || ''
            })),
            addedContacts: added,
            reminderConfig: {
                timing: 'custom',
                customDate: v.datePart,
                customHour: v.when.getHours(),
                customSendAt: v.iso,
                recipients: v.audience,
                scope: 'form_contacts',
                buttons: v.buttons,
                buttonUrls: v.buttonUrls,
                buttonLabels: v.buttonLabels
            },
            // Identity / ownership (from the form + signed-in host).
            // `userId` is the PAYER: the send path charges it
            // (campaigns.service → resolveCreditPool/debitCredits) and the
            // cron's credit gate + the insufficient-credits email both resolve
            // from it. See _remindAllPayer for why it isn't always the
            // signed-in user (task #553).
            formId,
            ...(await this._remindAllPayer(fd, user)),
            // Scheduling — fires via the normal pending-campaign cron.
            // isImmediate mirrors the host's "Send now" choice (task #546):
            // scheduled-for-later docs MUST stay false (the cron's time gate is
            // `!isImmediate && scheduledDate > now`, so a true here would make
            // them fire on the next tick), while a send-now doc is dispatched
            // right away by _dispatchReminderNow (cron as fallback) and its
            // card skips the misleading "Scheduled for <a minute ago>" line.
            status: 'scheduled',
            priority: 'normal',
            isImmediate: v.whenMode === 'now',
            scheduledSendDate: v.iso,
            scheduledSendDateDisplay: v.when.toLocaleString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }),
            // Display + event context
            name: `[Reminder] ${eventTitle}`,
            eventTitle,
            formTitle: eventTitle,
            eventDate: fd.eventDate || null,
            eventLocation: fd.locationAddress || null,
            // Message — in whatever languages the host picked in the panel
            customMessage: langRes.customMessage,
            ...(langRes.translatedMessage ? { translatedMessage: langRes.translatedMessage } : {}),
            hasCustomMessage: !!(v.message && v.message.trim()),
            sourceLanguage: fd.defaultLanguage || null,
            campaignLanguageMode: langRes.campaignLanguageMode || 'single',
            campaignLanguages: langRes.campaignLanguages || null,
            // Reach everyone on whatever channel they have
            selectedChannels: ['email', 'mobile'],
            // Rendering / link context. Form docs store their theme color under
            // `color` (fd is the raw rsvp_forms doc) — fd.themeColor never exists,
            // and null meant these reminders sent with the default gray header.
            themeColor: fd.themeColor || fd.color || null,
            bulkImageUrl: (img && img.url) ? img.url : null,
            hasImage: !!(img && img.hasImage),
            invitationSplash: !!(img && img.splash),
            includeRsvpLink: v.buttons.includes('rsvp'),
            includeQRCode: v.includeQRCode === true,
            // RSVP link: reminder sends always use form.html?id=<slug> server-side;
            // customUrl (if the form has one) gives a nicer slug URL in email.
            ...(fd.customUrl ? { customUrl: fd.customUrl } : {}),
            rsvpWebsiteUrl: fd.rsvpWebsiteUrl || null,
            bulkLinkType: null,
            // Never itself a reminder parent
            hasReminders: false,
            reminderSettings: { enabled: false },
            createdAt: nowIso,
            requestedAt: firebase.firestore.FieldValue.serverTimestamp(),
            processed: false,
            processedAt: null,
            sentAt: null
        };
        if (typeof window.isCurrentUserAdminCached === 'function' && await window.isCurrentUserAdminCached()) {
            campaign.isTest = true;
        }

        {
            const { contacts: reminderContacts, ...reminderFields } = campaign;
            await window.CampaignContacts.save(db, db.collection('bulk_campaigns').doc(campaignId), reminderContacts, { also: reminderFields, create: true });
        }
        if (campaign.isImmediate) await this._dispatchReminderNow(campaignId);
        return campaignId;
    }

    // Per-campaign "Remind → Pending guests": open the composer to create a
    // reminder nested UNDER this campaign (audience = guests who haven't
    // responded, finalized at send) rather than spinning up a separate
    // standalone follow-up campaign. (The top "Remind All" still creates a new
    // campaign via _remindAllByStatus → createFollowupCampaign.)
    async remindPending(campaignId) {
        return this._remindCampaignByStatus(campaignId, 'no_response', 'remind-pending');
    }

    // Per-campaign "Remind → Yes responders": linked reminder to attending guests.
    async remindYes(campaignId) {
        return this._remindCampaignByStatus(campaignId, 'attending', 'remind-yes');
    }

    // Per-campaign "Remind → Maybe responders": linked reminder to guests who
    // RSVP'd "Maybe" — the confirm-or-decline nudge.
    async remindMaybe(campaignId) {
        return this._remindCampaignByStatus(campaignId, 'maybe', 'remind-maybe');
    }

    // Per-campaign "Remind → No responders": linked reminder to guests who
    // declined — e.g. a live-stream link or a thank-you for letting us know.
    async remindNo(campaignId) {
        return this._remindCampaignByStatus(campaignId, 'declined', 'remind-no');
    }

    // Shared path for a campaign's "Remind → <status>": first show the SAME
    // recipient review the top "Remind All" uses (so the two flows feel
    // consistent — you see who you're nudging before composing), then open the
    // reminder composer. Because a campaign reminder's audience is recalculated
    // when it actually sends, this review is a read-only PREVIEW with a note that
    // the list can still change (no per-row remove here — see RecipientReviewModal
    // preview mode). On cancel we stop. The review opens even when nobody
    // currently matches (task #300 round 2): the modal's localized "No X guests
    // found" empty state + explanation shows first, and Continue proceeds to
    // the composer — jumping straight to the composer read as a bug.
    async _remindCampaignByStatus(campaignId, audience, mode) {
        const statusByAudience = { no_response: 'pending', attending: 'yes', maybe: 'maybe', declined: 'no' };
        const status = statusByAudience[audience] || 'pending';
        let contacts = [];
        let removed = [];
        try {
            const inCampaign = await this._getCampaignContactsWithStatus(campaignId);
            const matching = (inCampaign || []).filter(c => c.rsvpStatus === status && !this._isContactExcludedForAnyCampaign(c));
            // Dedupe by phone|email so the same guest reached on two channels
            // shows once (mirrors _remindAllByStatus).
            const seen = new Set();
            for (const c of matching) {
                const key = (c.phone || '').replace(/\D/g, '') + '|' + (c.email || '').toLowerCase().trim();
                if (key === '|' || seen.has(key)) continue;
                seen.add(key);
                contacts.push({ name: c.name, phone: c.phone, email: c.email });
            }
        } catch (e) {
            // If the preview fetch fails, fall through to the composer rather than
            // blocking the host from setting up a reminder.
            console.warn('Reminder recipient preview unavailable:', e?.message || e);
        }

        if (window.RecipientReviewModal) {
            const campaign = (this.campaigns || []).find(c => c.id === campaignId);
            const t = this.getWizardTranslations();
            const result = await window.RecipientReviewModal.show({
                contacts,
                mode,
                formId: this.formId,
                campaignName: campaign?.name,
                preview: true,
                allowRemove: true, // removals apply to this one reminder (reviewRemovals)
                confirmLabel: t.reminderReviewContinue || 'Continue',
                note: t.reminderReviewPreviewNote || 'Anyone you remove here is left out of this reminder only; your other reminders aren\'t affected. The rest of the list is finalized at send time — for example, guests who respond before then are skipped automatically.'
            });
            if (!result) return; // host cancelled the review
            // Guests removed here ride into the composer and are saved on the
            // new reminder as reviewRemovals, the way Remind All stores them, so
            // only THIS reminder skips them. Until 2026-10-07 they went to the
            // parent's reminderExclusions and the form-wide opt-out list: every
            // later reminder and every Remind All skipped them too, and closing
            // the composer without creating anything still left them excluded.
            // (New guests are added via the "Add contacts" flow, not this modal.)
            removed = this._reviewRemovedContacts(contacts, result.contacts || []);
        }
        return this.showCreateReminderModal(campaignId, audience, { reviewRemovals: removed });
    }

    /**
     * Show campaign contacts modal with filtering and export
     */    // Remind all pending across all campaigns — deduplicates by phone/email
    async remindAllPending() {
        return this._remindAllByStatus('pending');
    }

    // The v2 next step's "Send a reminder" (deep link open=remind). Neither
    // the line nor the home row can tell whether this event's invitations
    // went out through us: the "awaiting" tile counts uploaded guest-list
    // rows, not guests we messaged. It chose the blank invitation wizard
    // whenever that tile was 0, so a host who had sent through QuikRSVP
    // pressed "Send a reminder" and got the Contacts upload step, and closed
    // it. Decided here instead, from the guests this event has messaged.
    async remindPendingOrInvite() {
        return this._remindAllByStatus('pending', { inviteIfNoneMessaged: true });
    }

    // Remind all Yes responders across all campaigns — deduplicates by phone/email
    async remindAllYes() {
        return this._remindAllByStatus('yes');
    }

    // Remind all Maybe responders across all campaigns — deduplicates by phone/email
    async remindAllMaybe() {
        return this._remindAllByStatus('maybe');
    }

    // Remind all No responders (declined guests) across all campaigns —
    // deduplicates by phone/email. Typical use: live-stream link, thank-you note.
    async remindAllNo() {
        return this._remindAllByStatus('no');
    }

    // Remind EVERY contact across all campaigns, regardless of response status —
    // deduplicates by phone/email like the status-specific buckets.
    async remindAllEveryone() {
        return this._remindAllByStatus('all');
    }

    async _remindAllByStatus(audience, opts = {}) {
        try {
            // Per-bucket copy keys + the canonical rsvpStatus to match. rsvpStatus
            // is server-computed: 'yes' | 'no' | 'maybe' | 'responded' | 'pending'.
            // status:null means "everyone" (no status filter). The toasts were
            // English on every dashboard (code review 2026-10-07). The unused
            // empty-state copy went too: since task #300 the review window shows
            // its own, translated empty state.
            const COPY = {
                all:     { status: null,      loading: 'raLoadingAll',     excluded: 'raExcludedAll',     mode: 'remind-all' },
                pending: { status: 'pending', loading: 'raLoadingPending', excluded: 'raExcludedPending', mode: 'remind-pending' },
                yes:     { status: 'yes',     loading: 'raLoadingYes',     excluded: 'raExcludedYes',     mode: 'remind-yes' },
                maybe:   { status: 'maybe',   loading: 'raLoadingMaybe',   excluded: 'raExcludedMaybe',   mode: 'remind-maybe' },
                no:      { status: 'no',      loading: 'raLoadingNo',      excluded: 'raExcludedNo',      mode: 'remind-no' }
            };
            const cfg = COPY[audience] || COPY.pending;
            if (!opts.inviteIfNoneMessaged && window.showToast) window.showToast(this.cTxt(cfg.loading), 'info');

            // Always fetch fresh global data — DON'T reuse this.contactStatusData
            // because the Status modal's per-campaign view mutates it. If the user
            // opened a campaign-scoped Status modal then clicked Remind All, the
            // cached data would already be filtered to one campaign.
            // includeGuestList (task #964): the event's whole guest list, so an
            // uploaded guest no message has reached yet, or a guest who
            // answered on the shared link, is in the audience too - the same
            // list the scheduled send resolves at send time.
            const resp = await this._mtFetch(`/api/message-tracking/${this.formId}/contacts?includeGuestList=1`);
            if (!resp.ok) throw new Error('Failed to load contacts');
            const data = await resp.json();
            const source = data.contacts || [];
            // Nobody messaged yet: there is no one to remind, and the
            // invitation is the step (remindPendingOrInvite).
            if (opts.inviteIfNoneMessaged && !source.some(c => c.source !== 'guest_list')) return this.openCreateCampaignModal();

            const filteredByStatus = cfg.status ? source.filter(c => c.rsvpStatus === cfg.status) : source.slice();

            // Drop guests the host explicitly excluded from reminders so they
            // aren't re-surfaced (pre-selected) in this manual blast. Scheduled
            // reminders already skip them server-side; this keeps the manual
            // path consistent.
            const filtered = filteredByStatus.filter(c => !this._isContactExcludedForAnyCampaign(c));
            const optedOutSkipped = filteredByStatus.length - filtered.length;

            const seen = new Set();
            const deduped = [];
            for (const c of filtered) {
                const phoneKey = (c.phone || '').replace(/\D/g, '');
                const emailKey = (c.email || '').toLowerCase().trim();
                const key = phoneKey + '|' + emailKey;
                if (key === '|') continue;
                if (!seen.has(key)) {
                    seen.add(key);
                    deduped.push({ name: c.name, phone: c.phone, email: c.email,
                        // A guest-list entry's row (task #964 phase 2).
                        ...(c.source === 'guest_list' && c.responseId ? { invitationId: c.responseId } : {}) });
                }
            }

            console.log(`[REMIND ALL ${audience.toUpperCase()}] Source:`, source.length, '| Filtered:', filtered.length, '| Deduped:', deduped.length);

            // Task #300: the review modal ALWAYS opens — with zero matching
            // guests it shows a "No X guests found" empty state and still lets
            // the host continue to pre-configure a reminder that resolves its
            // audience automatically at send time.
            if (!deduped.length && optedOutSkipped > 0 && window.showToast) {
                window.showToast(this.cTxt(cfg.excluded), 'info');
            }

            if (optedOutSkipped > 0 && window.showToast) {
                window.showToast(this.cTxt(optedOutSkipped === 1 ? 'raSkippedExcludedOne' : 'raSkippedExcludedMany', optedOutSkipped), 'info');
            }

            await this._reviewAndHandoff(deduped, cfg.mode);
        } catch (err) {
            console.error(`remindAll ${audience} error:`, err);
            if (window.showToast) window.showToast(this.cTxt('raLoadFailed'), 'error');
        }
    }

    createFollowupCampaign(explicitContacts) {
        // The contacts the caller passes ("Remind via Email" on the failed-delivery panel).
        const contacts = explicitContacts;

        if (!contacts || contacts.length === 0) {
            if (typeof showToast === 'function') {
                showToast('No contacts to create a message for', 'warning');
            }
            return;
        }

        // Convert contacts to wizard format
        const wizardContacts = contacts.map(c => ({
            name: c.name || '',
            email: c.email || '',
            phone: c.phone || '',
            method: c.email && c.phone ? 'both' : (c.email ? 'email' : 'whatsapp')
        }));

        // Close whatever dialog we were called from first — otherwise the
        // wizard opens BEHIND it and reads as a blank window appearing from
        // nowhere (task #668). Two callers, two different dialogs: the
        // campaign-contacts list and the contact-status window ("Remind via
        // Email" on the failed-delivery panel), and only the first was closed.
        ['campaignContactsModal', 'campaignsContactStatusModal'].forEach((id) => {
            const el = document.getElementById(id);
            if (!el) return;
            const inst = bootstrap.Modal.getInstance(el);
            if (inst) inst.hide();
        });

        // Open the create campaign modal with wizard
        const modal = new bootstrap.Modal(document.getElementById('campaignsCreateModal'));
        const openSeq = this._beginWizardOpen(document.getElementById('campaignsCreateModal'));
        const container = document.getElementById('campaignsWizardContainer');

        // Initialize wizard HTML in the container (same as openCreateCampaignModal)
        container.innerHTML = `
            <div id="bulk-sharing-wizard">
                <div id="bulk-access-check" class="text-center py-5">
                    <div class="spinner-border text-primary mb-3" role="status"></div>
                    <p>Loading follow-up message...</p>
                </div>
                <div id="bulk-get-started" class="d-none"></div>
                <div id="bulk-wizard-progress" class="d-none mb-4">
                    <div class="progress mb-4 rounded-pill shadow-sm border" style="height: 8px;">
                        <div class="progress-bar bg-primary" role="progressbar" style="width: 20%"></div>
                    </div>
                    <div class="d-flex justify-content-between">
                        <div class="step-label text-center" style="flex: 1;"><small class="text-muted">1. Contacts</small></div>
                        <div class="step-label text-center" style="flex: 1;"><small class="text-muted">2. Channels</small></div>
                        <div class="step-label text-center" style="flex: 1;"><small class="text-muted">3. Customize</small></div>
                        <div class="step-label text-center" style="flex: 1;"><small class="text-muted">4. Follow-Up</small></div>
                        <div class="step-label text-center" style="flex: 1;"><small class="text-muted">5. Send</small></div>
                    </div>
                </div>
                <div id="bulk-step-contacts" class="wizard-step d-none"></div>
                <div id="bulk-step-channels" class="wizard-step d-none"></div>
                <div id="bulk-step-review" class="wizard-step d-none"></div>
                <div id="bulk-step-reminders" class="wizard-step d-none"></div>
                <div id="bulk-step-send" class="wizard-step d-none"></div>
                <div id="bulk-wizard-nav" class="d-none">
                    <div class="d-flex justify-content-between mt-4">
                        <button type="button" class="btn btn-outline-secondary rounded-pill px-4" id="wizard-prev" style="display: none;">
                            <i class="bi bi-arrow-left me-1"></i>Previous
                        </button>
                        <button type="button" class="btn btn-primary" id="wizard-next" class="btn btn-primary rounded-pill shadow-sm px-4 fw-bold"> Next: Customize <i class="bi bi-arrow-right ms-1"></i>
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Show the modal
        modal.show();

        // Initialize the bulk sharing wizard with pre-filled contacts
        setTimeout(() => {
            if (!this._wizardOpenIsCurrent(openSeq)) return;
            if (window.BulkSharingWizard) {
                // Deactivate any existing wizard instance to prevent duplicate event handling
                if (window.bulkSharingWizard) {
                    window.bulkSharingWizard.isActive = false;
                    window.bulkSharingWizard.autoSaveEnabled = false;
                }

                window.bulkSharingWizard = new window.BulkSharingWizard();
                window.bulkSharingWizard.formId = this.formId;
                window.bulkSharingWizard.formData = this.formData;
                window.bulkSharingWizard.skipStateRestore = true;

                // Reset state
                window.bulkSharingWizard.paymentCompleted = false;
                window.bulkSharingWizard.paymentData = null;
                window.bulkSharingWizard.isSendingCampaign = false;
                window.bulkSharingWizard.currentStep = 0;
                window.bulkSharingWizard.bulkImageUrl = null;

                // PRE-FILL with the filtered contacts
                window.bulkSharingWizard.contacts = wizardContacts;
                window.bulkSharingWizard.validContacts = wizardContacts;
                window.bulkSharingWizard.invalidContacts = [];

                // Mark as follow-up campaign with source info
                window.bulkSharingWizard.isFollowUp = true;
                window.bulkSharingWizard.followUpSourceContacts = wizardContacts.length;
                window.bulkSharingWizard.followUpFilter = '';

                if (!window.builderCore) {
                    window.builderCore = { builderOptions: {} };
                }
                window.builderCore.builderOptions.formId = this.formId;

                // Initialize and start the wizard
                window.bulkSharingWizard.initializeForTab();

                if (typeof showToast === 'function') {
                    showToast(this.cTxt(wizardContacts.length === 1 ? 'followUpLoadedOne' : 'followUpLoaded', wizardContacts.length), 'info');
                }
            } else {
                container.innerHTML = `
                    <div class="alert alert-danger">
                        <i class="bi bi-exclamation-triangle me-2"></i>
                        Campaign wizard failed to load. Please refresh the page.
                    </div>
                `;
            }
        }, 100);

        // Handle modal close - refresh campaigns list
        document.getElementById('campaignsCreateModal').addEventListener('hidden.bs.modal', async () => {
            await this.loadCampaigns();
        }, { once: true });
    }

    async showNonRespondersModal(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) return;

        // Load non-responders for this campaign
        const nonResponders = await this.loadCampaignNonResponders(campaignId);

        const modalHtml = `
        <div class="modal fade" id="nonRespondersModal" tabindex="-1">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header bg-warning bg-opacity-25">
                        <h5 class="modal-title">
                            <i class="bi bi-people me-2"></i>Non-responders
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="alert alert-info small py-2 mb-3">
                            <i class="bi bi-info-circle me-1"></i>
                            These guests haven't responded yet. Select who to send reminders to.
                        </div>

                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="small text-muted">${nonResponders.length} pending</span>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="selectAllNonResponders" checked>
                                <label class="form-check-label small" for="selectAllNonResponders">Select All</label>
                            </div>
                        </div>

                        <div id="nonRespondersList" class="border rounded" style="max-height: 300px; overflow-y: auto;">
                            ${nonResponders.length === 0 ? `
                                <div class="text-center text-muted py-4">
                                    <i class="bi bi-check-circle text-success fs-3"></i>
                                    <p class="mb-0 mt-2">Everyone has responded!</p>
                                </div>
                            ` : nonResponders.map(r => `
                                <div class="form-check border-bottom p-2">
                                    <input class="form-check-input non-responder-check" type="checkbox"
                                           value="${r.trackingId}" id="nr-${r.trackingId}" checked>
                                    <label class="form-check-label d-block" for="nr-${r.trackingId}">
                                        <span class="fw-medium">${r.recipientName || 'Guest'}</span>
                                        <br>
                                        <small class="text-muted">${r.recipientEmail || r.recipientPhone}</small>
                                    </label>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                        ${nonResponders.length > 0 ? `
                        <button type="button" class="btn btn-warning" id="sendReminderBtn">
                            <i class="bi bi-send me-1"></i>Send Reminder
                        </button>
                        ` : ''}
                    </div>
                </div>
            </div>
        </div>`;

        // Remove existing modal and backdrop if any
        const existingModal = document.getElementById('nonRespondersModal');
        if (existingModal) {
            const existingInstance = bootstrap.Modal.getInstance(existingModal);
            if (existingInstance) existingInstance.dispose();
            existingModal.remove();
        }
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        const modalEl = document.getElementById('nonRespondersModal');
        const modal = new bootstrap.Modal(modalEl);

        // Add cleanup listener for when modal is hidden
        modalEl.addEventListener('hidden.bs.modal', () => {
            // Ensure backdrop is removed
            document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
            // Restore body scroll
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
            // Dispose and remove the modal element
            modal.dispose();
            modalEl.remove();
        }, { once: true });

        // Select All checkbox handler
        document.getElementById('selectAllNonResponders')?.addEventListener('change', (e) => {
            document.querySelectorAll('.non-responder-check').forEach(cb => {
                cb.checked = e.target.checked;
            });
        });

        // Send Reminder button
        document.getElementById('sendReminderBtn')?.addEventListener('click', async () => {
            const selectedIds = [];
            document.querySelectorAll('.non-responder-check:checked').forEach(cb => {
                selectedIds.push(cb.value);
            });

            if (selectedIds.length === 0) {
                alert('Please select at least one recipient');
                return;
            }

            const btn = document.getElementById('sendReminderBtn');
            btn.disabled = true;
            btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Sending...';

            try {
                // TODO: Implement actual reminder sending via API
                console.log('Sending reminders to:', selectedIds);
                await new Promise(r => setTimeout(r, 1000)); // Simulated delay

                modal.hide();
                // Show success message
                alert(`Reminder sent to ${selectedIds.length} recipient(s)`);
                await this.loadCampaigns();
            } catch (error) {
                console.error('Error sending reminders:', error);
                btn.disabled = false;
                btn.innerHTML = '<i class="bi bi-send me-1"></i>Send Reminder';
            }
        });

        modal.show();
    }

    async loadCampaignNonResponders(campaignId) {
        // Server first: the matching runs there and only the non-responder
        // list comes back, so the replies the plan hides never reach the
        // browser. The client path below stays as the fallback for an
        // unreachable API; it downloads every response to do the matching.
        try {
            const resp = await this._mtFetch(`/api/message-tracking/${encodeURIComponent(this.formId)}/campaigns/${encodeURIComponent(campaignId)}/non-responders`);
            if (resp.ok) {
                const data = await resp.json();
                if (Array.isArray(data.nonResponders)) return data.nonResponders;
            }
        } catch (e) { /* fall through */ }
        console.warn('[QuikRSVP/messaging] server non-responder lookup failed, using the client path');
        return this._loadCampaignNonRespondersClient(campaignId);
    }

    async _loadCampaignNonRespondersClient(campaignId) {
        try {
            const db = firebase.firestore();

            // Get tracking records from correct subcollection (same path as
            // analytics tab), filtered server-side — an unbounded read here
            // paid for every tracking doc on the form per modal open.
            const trackingSnapshot = await db.collection('forms').doc(this.formId)
                .collection('messageTracking')
                .where('campaignId', '==', campaignId)
                .get();

            // Get all responses to check who responded
            const responsesSnapshot = await db.collection('rsvp_forms')
                .doc(this.formId)
                .collection('responses')
                .get();

            // Phone formats differ between what the host imported
            // (+972501234567) and what a guest types (050-1234567) — compare
            // on the last 9 digits so the variants collide.
            const phoneKey = (p) => {
                const digits = String(p || '').replace(/\D/g, '');
                return digits.length >= 9 ? digits.slice(-9) : digits;
            };

            // Build lookup sets. trackingId is the strongest signal — most
            // link RSVPs carry no typed phone/email, only the tk of the
            // message the guest acted on (mirrors server-side guest-matching).
            const responderTids = new Set();
            const responderEmails = new Set();
            const responderPhones = new Set();

            responsesSnapshot.docs.forEach(doc => {
                const response = doc.data();
                if (response.trackingId) responderTids.add(response.trackingId);
                if (response.email) responderEmails.add(response.email.toLowerCase().trim());
                const respPhone = response.phoneNumber || response.phone;
                if (respPhone) responderPhones.add(phoneKey(respPhone));
            });

            // Filter to non-responders (with deduplication)
            const nonResponders = [];
            const seenEmails = new Set();
            const seenPhones = new Set();

            trackingSnapshot.docs.forEach(doc => {
                const data = doc.data();

                // Skip if already responded via tracking
                if (data.responded || data.respondedAt) return;

                // A response submitted through this message's link — covers the
                // case where the /api/tracking/response ping never landed.
                if (data.trackingId && responderTids.has(data.trackingId)) return;

                // Check against responses
                if (data.recipientEmail) {
                    const email = data.recipientEmail.toLowerCase().trim();
                    if (responderEmails.has(email)) return;
                }
                if (data.recipientPhone) {
                    if (responderPhones.has(phoneKey(data.recipientPhone))) return;
                }

                // âœ… DEDUPE: Skip if we've already seen this email/phone
                const email = (data.recipientEmail || '').toLowerCase().trim();
                const phone = (data.recipientPhone || '').replace(/[\s\-\(\)]/g, '');

                if (email && seenEmails.has(email)) return;
                if (phone && seenPhones.has(phone)) return;

                // Track this contact
                if (email) seenEmails.add(email);
                if (phone) seenPhones.add(phone);

                nonResponders.push({
                    trackingId: doc.id,
                    recipientName: data.recipientName,
                    recipientEmail: data.recipientEmail,
                    recipientPhone: data.recipientPhone
                });
            });

            return nonResponders;
        } catch (error) {
            console.error('Error loading non-responders:', error);
            return [];
        }
    }

    // The event's hand-shared link cards, newest first: the Personal links
    // window lists their links, now that the feed does not (task #1044).
    _handSharedCampaignIds() {
        const at = (c) => { const v = c.createdAt; const d = v && v.toDate ? v.toDate() : new Date(v || 0); return isNaN(d) ? 0 : d.getTime(); };
        return (this.campaigns || []).filter(c => c.status === 'manual_links' && (!c.formId || c.formId === this.formId))
            .sort((a, b) => at(b) - at(a)).map(c => c.id);
    }

    _injectPersonalLinksButtons() {
        const L = {
            en: ['Personal links', 'A link per guest to send from your own WhatsApp or email. Nothing is sent by us, no credits.'],
            es: ['Enlaces personales', 'Un enlace por invitado para enviar desde tu propio WhatsApp o correo. No enviamos nada, sin créditos.'],
            fr: ['Liens personnels', 'Un lien par invité à envoyer depuis votre propre WhatsApp ou e-mail. Nous n’envoyons rien, sans crédits.'],
            de: ['Persönliche Links', 'Ein Link pro Gast zum Versenden über Ihr eigenes WhatsApp oder E-Mail. Wir senden nichts, keine Credits.'],
            pt: ['Links pessoais', 'Um link por convidado para enviar do seu próprio WhatsApp ou e-mail. Não enviamos nada, sem créditos.'],
            he: ['קישורים אישיים', 'קישור לכל אורח לשליחה מהוואטסאפ או האימייל שלכם. אנחנו לא שולחים כלום, בלי קרדיטים.'],
            ar: ['روابط شخصية', 'رابط لكل ضيف لترسله من واتساب أو بريدك الخاص. لا نرسل شيئًا، بلا أرصدة.']
        };
        const lang = String(window.DASHBOARD_LANGUAGE || document.documentElement.lang || 'en').slice(0, 2);
        const [label, hint] = L[lang] || L.en;
        const open = () => {
            if (!window.PersonalLinks) return;
            window.PersonalLinks.open({ formId: this.formId, title: this.formData?.title || window.currentFormData?.title || '', hubCampaignIds: this._handSharedCampaignIds() });
        };
        const add = (anchorId, id, cls) => {
            const anchor = document.getElementById(anchorId);
            if (!anchor || document.getElementById(id)) return;
            const b = document.createElement('button');
            b.type = 'button';
            b.id = id;
            b.className = cls;
            b.title = hint;
            b.innerHTML = `<i class="bi bi-link-45deg me-1"></i>${label}`;
            b.addEventListener('click', (e) => { e.preventDefault(); open(); });
            anchor.insertAdjacentElement('beforebegin', b);
        };
        add('campaignsNewCampaignBtn', 'campaignsPersonalLinksBtn', 'btn btn-outline-primary btn-sm');
        add('campaignsCreateFirstBtn', 'campaignsPersonalLinksFirstBtn', 'btn btn-outline-primary me-2');
    }

    // Every path that opens the wizard modal takes a ticket. An init that
    // runs after a later open, or after the host closed this one, must not
    // replace the live wizard: Edit and Continue draft first wait on a chunk
    // read, which can outlast closing that modal and opening another, and
    // the late init would then draw the first message into the second one's
    // modal, where Save Changes writes to the wrong message (PMh re-review).
    _beginWizardOpen(modalEl) {
        const seq = this._wizardOpenSeq = (this._wizardOpenSeq || 0) + 1;
        if (modalEl) {
            // hidden, not hide: a close the wizard refuses mid-send (its
            // hide.bs.modal handler calls preventDefault) is not a close.
            modalEl.addEventListener('hidden.bs.modal', () => {
                if (this._wizardOpenSeq === seq) this._wizardOpenSeq = seq + 1;
            }, { once: true });
        }
        return seq;
    }

    _wizardOpenIsCurrent(seq) {
        return seq === this._wizardOpenSeq;
    }

    openCreateCampaignModal(prefill = null) {
        const modal = new bootstrap.Modal(document.getElementById('campaignsCreateModal'));
        const openSeq = this._beginWizardOpen(document.getElementById('campaignsCreateModal'));
        const container = document.getElementById('campaignsWizardContainer');
        const t = this.getWizardTranslations();

        // Initialize wizard HTML in the container with translations
        // Shell markup is shared with the builder mount — see
        // BulkSharingWizard.renderShell (step-driven, one source of truth).
        BulkSharingWizard.renderShell(container, t, ['contacts', 'channels', 'review', 'reminders', 'send'], {});

        // Show modal
        modal.show();

        // Initialize the bulk sharing wizard with the current form ID
        setTimeout(() => {
            if (!this._wizardOpenIsCurrent(openSeq)) return;
            if (window.BulkSharingWizard) {
                // Deactivate any existing wizard instance to prevent duplicate event handling
                if (window.bulkSharingWizard) {
                    window.bulkSharingWizard.isActive = false;
                    window.bulkSharingWizard.autoSaveEnabled = false;
                }

                // Create new wizard instance
                window.bulkSharingWizard = new window.BulkSharingWizard();

                // Set the form ID before initialization
                window.bulkSharingWizard.formId = this.formId;
                window.bulkSharingWizard.formData = this.formData;

                // Pass the dashboard language to the wizard
                window.bulkSharingWizard.dashboardLanguage = this.dashboardLanguage;

                // Skip state restoration for fresh wizard session (faster load)
                window.bulkSharingWizard.skipStateRestore = true;

                // Reset all state flags to ensure fresh campaign
                window.bulkSharingWizard.paymentCompleted = false;
                window.bulkSharingWizard.paymentData = null;
                window.bulkSharingWizard.isSendingCampaign = false;
                window.bulkSharingWizard.currentStep = 0;
                window.bulkSharingWizard.contacts = [];
                window.bulkSharingWizard.validContacts = [];
                window.bulkSharingWizard.invalidContacts = [];
                window.bulkSharingWizard.bulkImageUrl = null;

                // Also set it in builderCore if available for wizard compatibility
                if (!window.builderCore) {
                    window.builderCore = { builderOptions: {} };
                }
                window.builderCore.builderOptions.formId = this.formId;

                // Initialize the wizard (will skip state restore due to flag)
                window.bulkSharingWizard.initializeForTab();

                // Clone prefill — when "Clone message as new draft" launches the
                // wizard, seed the message/title/link from the source campaign.
                // (customMessage is read by the customize step's textarea, which
                // initializes from this.customMessage, so setting it here sticks.)
                if (prefill) {
                    const w = window.bulkSharingWizard;
                    if (prefill.customMessage) w.customMessage = prefill.customMessage;
                    if (prefill.headerText !== undefined && prefill.headerText !== null) w.messageHeader = prefill.headerText;
                    if (prefill.emailSenderName) w.emailSenderName = prefill.emailSenderName;
                    if (prefill.replyToEmail) w.replyToEmail = prefill.replyToEmail;
                    if (prefill.emailLogoUrl) w.emailLogoUrl = prefill.emailLogoUrl;
                    if (prefill.hideEmailLogo === true) w.hideEmailLogo = true;
                    if (prefill.themeColor) w.themeColor = prefill.themeColor;
                    if (prefill.bulkLinkType) w.bulkLinkType = prefill.bulkLinkType;
                    if (prefill.customUrl) w.customUrl = prefill.customUrl;
                    if (prefill.rsvpWebsiteUrl) w.rsvpWebsiteUrl = prefill.rsvpWebsiteUrl;
                }
            } else {
                container.innerHTML = `
                    <div class="alert alert-danger">
                        <i class="bi bi-exclamation-triangle me-2"></i>
                        ${t.wizardFailedToLoad}
                    </div>
                `;
            }
        }, 100);

        // Handle modal close - refresh campaigns list
        document.getElementById('campaignsCreateModal').addEventListener('hidden.bs.modal', async () => {
            await this.loadCampaigns();
        }, { once: true });
    }

    /**
     * Setup Firestore onSnapshot listener for real-time campaign updates.
     * Replaces polling — only triggers on actual data changes.
     */
    setupCampaignListener(opts = {}) {
        // A fresh attempt at the whole-event listener may narrow again: the flag
        // only stops ONE failure from looping, it must not outlive it (the
        // dashboard re-subscribes when it switches event).
        if (!opts.narrow) this._campaignListenerNarrowed = false;

        // Clean up previous listener
        if (this._campaignUnsubscribe) {
            this._campaignUnsubscribe();
            this._campaignUnsubscribe = null;
        }

        const db = firebase.firestore();
        const user = firebase.auth().currentUser;
        if (!user || !this.formId) return;

        const queryUserId = window.isAdminMode && this.formData?.owner
            ? this.formData.owner
            : user.uid;

        // Bound the listener: order by recency + limit. The formId filter already
        // narrows reads to a single event, but we add an explicit cap so a
        // runaway is impossible even if a user has hundreds of campaigns on one
        // form.
        //
        // The listener watches the same set loadCampaigns() just rendered: for a
        // manager (owner or co-host) that is the event's whole set, so a co-host
        // sending right now updates the owner's open dashboard instead of only
        // appearing after the next reload. Needs the (formId, createdAt desc)
        // composite index — see firestore.indexes.json.
        const CAMPAIGN_LISTENER_LIMIT = 100;
        const wholeEvent = !opts.narrow && (this._managesThisEvent(user) || !!window.isAdminMode);
        let query = db.collection('bulk_campaigns')
            .where('formId', '==', this.formId);
        if (!wholeEvent) {
            query = query.where('userId', '==', queryUserId);
        }
        query = query
            .orderBy('createdAt', 'desc')
            .limit(CAMPAIGN_LISTENER_LIMIT);

        this._campaignUnsubscribe = query.onSnapshot((snapshot) => {
            // Skip the initial snapshot (we already loaded via loadCampaigns)
            if (this._campaignListenerInitial) {
                this._campaignListenerInitial = false;
                return;
            }

            // The snapshot carries the campaign doc only; a guest list stored
            // in chunk docs is read alongside (hydrate) before the row is
            // swapped in, so the card never sees a campaign with no contacts.
            // 'removed' deliberately ignored. With limit(N), Firestore fires
            // 'removed' on window-exit (a new campaign bumps the oldest of
            // the recent-N out) — not only on real deletes. Treating it as
            // a delete would silently shrink this.campaigns. Real deletions
            // are reconciled by the next loadCampaigns() call (e.g. after a
            // user action) or on next page load.
            const changed = snapshot.docChanges().filter(ch => ch.type === 'added' || ch.type === 'modified');
            // A campaign the wizard has just created arrives here from the LOCAL
            // write, before the batch that carries it (parent + contact_chunks)
            // has landed on the server. Reading the chunks at that moment is
            // denied: their read rule resolves the parent with get(), which on
            // the server still finds nothing (2026-09-22: 186ms between the
            // batch and the denial, list shown without its contacts until the
            // next loadCampaigns). Let the write be acknowledged first; changes
            // that came from the server carry no pending write and read at once.
            const pending = changed.some(ch => ch.doc.metadata && ch.doc.metadata.hasPendingWrites);
            // reuse: most changes during a send are counters and message ids,
            // not the guest list. Re-reading every chunk on each one cost about
            // 1 MB per event per open tab at 3k+ guests (pre-mortem 2026-10-07);
            // hydrate keeps the list we hold unless contactsRev moved.
            (pending ? db.waitForPendingWrites() : Promise.resolve())
                .then(() => Promise.all(changed.map(ch => window.CampaignContacts.hydrate(db, ch.doc, { reuse: this.campaigns.find(c => c.id === ch.doc.id) })
                    .then(data => ({ id: ch.doc.id, ...data })))))
                .then((rows) => {
                    let hasChanges = false;
                    for (const campaignData of rows) {
                        const index = this.campaigns.findIndex(c => c.id === campaignData.id);
                        if (index >= 0) {
                            this.campaigns[index] = campaignData;
                        } else {
                            this.campaigns.push(campaignData);
                        }
                        hasChanges = true;
                    }
                    if (!hasChanges) return;
                    this._applyListenerChanges();
                })
                .catch((e) => console.warn('Campaign listener hydrate failed:', e.message));
        }, (error) => {
            console.error('Campaign listener error:', error);
            // The whole-event listener needs the (formId, createdAt desc)
            // composite index. While that index is still building — the window
            // right after a deploy — Firestore answers failed-precondition, and
            // silently losing live updates would be a regression for the very
            // person who used to have them. Fall back to the narrow, always
            // indexed (formId, userId, createdAt desc) listener; the full set is
            // still correct on load and on every refresh.
            if (wholeEvent && !this._campaignListenerNarrowed
                && (error?.code === 'failed-precondition' || error?.code === 'permission-denied')) {
                this._campaignListenerNarrowed = true;
                this.setupCampaignListener({ narrow: true });
            }
        });
        this._finishCampaignListenerSetup();
    }

    /**
     * What the listener does once changed rows are in this.campaigns.
     * A send writes the campaign doc many times a minute, and each write used
     * to redraw every card at once. The first change draws immediately; the
     * rest of a burst is folded into one trailing redraw, at most one per
     * LISTENER_RENDER_GAP_MS. The rows are already in this.campaigns, so the
     * trailing redraw shows the latest of them.
     */
    _applyListenerChanges() {
        // The stats fetch has its own debounce, which needs every change to
        // restart it, so it stays outside the throttle.
        this._scheduleStatsRefresh();
        if (this._listenerRenderTimer) return;
        const LISTENER_RENDER_GAP_MS = 1500;
        const wait = (this._lastListenerRenderAt || 0) + LISTENER_RENDER_GAP_MS - Date.now();
        if (wait <= 0) {
            this._renderListenerChanges();
            return;
        }
        this._listenerRenderTimer = setTimeout(() => {
            this._listenerRenderTimer = null;
            this._renderListenerChanges();
        }, wait);
    }

    _renderListenerChanges() {
        this._lastListenerRenderAt = Date.now();
        // Sort client-side by createdAt descending
        this.campaigns.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return dateB - dateA;
        });
        this.renderCampaigns();
    }

    /** The rest of the listener setup, split out so the callback above stays flat. */
    _finishCampaignListenerSetup() {
        this._campaignListenerInitial = true;

        // Detach on page unload as a safety net so a tab left open over a
        // weekend doesn't keep billing reads against every campaign mutation.
        if (!this._campaignTeardownBound) {
            this._campaignTeardownBound = true;
            window.addEventListener('pagehide', () => {
                if (this._campaignUnsubscribe) {
                    try { this._campaignUnsubscribe(); } catch (e) { /* ignore */ }
                    this._campaignUnsubscribe = null;
                }
            });
            // ...but 'pagehide' also fires when the page enters the back/forward
            // cache, which is a pause: the heap survives and init() never runs
            // again, so after Back on a phone the cards came back painted with
            // no listener behind them and never moved again. The same hole
            // admin-dashboard closed in b78e455c (2026-09-08).
            window.addEventListener('pageshow', (event) => {
                if (!event.persisted) return; // a normal load runs init()
                if (this._campaignUnsubscribe || !this.isInitialized || !this.formId) return;
                this.setupCampaignListener();
                // The listener skips its first snapshot (loadCampaigns drew it),
                // so whatever changed while the page was cached only arrives
                // through a full reload.
                this.loadCampaigns().catch((e) => console.warn('Campaign reload after back-navigation failed:', e.message));
            });
        }
    }

    // Debounce /contacts fetch. During an active campaign send the server
    // writes to bulk_campaigns repeatedly (messageIds/smsFallbackPhones array
    // unions per WA failure), so onSnapshot can fire dozens of times in
    // seconds. Without debounce that produces a /contacts request flood and
    // exhausts the API rate limiter — debounce collapses each burst into
    // one fetch after the writes settle.
    _scheduleStatsRefresh() {
        if (this._statsRefreshTimer) clearTimeout(this._statsRefreshTimer);
        this._statsRefreshTimer = setTimeout(async () => {
            this._statsRefreshTimer = null;
            await this.loadMessageTrackingStats();
            // …and DRAW them (task #584). loadMessageTrackingStats only writes
            // campaign.trackingStats; without a re-render the fresh numbers sat
            // in memory while the card kept the ones it was built with. That is
            // why a message whose only recipient hard-failed showed "0 sent · 0
            // not delivered" on its card at the same moment its contact list —
            // which fetches live when you open it — showed "Failed 1, WhatsApp
            // couldn't deliver". The card was two seconds stale and stayed that
            // way until the page was reloaded.
            this.renderCampaigns();
        }, 2000);
    }

    destroy() {
        if (this._campaignUnsubscribe) {
            this._campaignUnsubscribe();
            this._campaignUnsubscribe = null;
        }
        if (this._statsRefreshTimer) {
            clearTimeout(this._statsRefreshTimer);
            this._statsRefreshTimer = null;
        }
        if (this._listenerRenderTimer) {
            clearTimeout(this._listenerRenderTimer);
            this._listenerRenderTimer = null;
        }
        this.isInitialized = false;
    }

    /**
     * Rename a campaign in place (task #1037): the name becomes a text box;
     * Enter or clicking away saves, Escape puts the old name back. Saves
     * `campaign.name` in Firestore and redraws the list. Without the row's
     * name element (an older caller) it falls back to the prompt.
     */
    async renameCampaign(campaignId, nameEl = null) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) return;
        if (nameEl && nameEl.isConnected) {
            if (nameEl.dataset.editing === '1') return;
            nameEl.dataset.editing = '1';
            const input = document.createElement('input');
            input.type = 'text';
            input.className = 'form-control form-control-sm campaign-name-input';
            input.value = campaign.name || '';
            input.placeholder = this.cTxt('untitledMessage');
            input.maxLength = 120;
            input.setAttribute('aria-label', this.cTxt('renamePrompt'));
            input.style.maxWidth = '22rem';
            input.dir = 'auto';
            nameEl.style.display = 'none';
            nameEl.after(input);
            input.focus();
            input.select();
            let done = false;
            const finish = (save) => {
                if (done) return;
                done = true;
                const value = input.value;
                input.remove();
                nameEl.style.display = '';
                delete nameEl.dataset.editing;
                if (save) this._saveCampaignName(campaign, value);
            };
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') { e.preventDefault(); finish(true); }
                else if (e.key === 'Escape') { e.preventDefault(); finish(false); }
            });
            input.addEventListener('blur', () => finish(true));
            input.addEventListener('click', (e) => e.stopPropagation());
            return;
        }
        const newName = window.prompt(this.cTxt('renamePrompt'), campaign.name || this.cTxt('untitledMessage'));
        if (newName === null) return;
        return this._saveCampaignName(campaign, newName);
    }

    async _saveCampaignName(campaign, newName) {
        const campaignId = campaign.id;
        const trimmed = String(newName || '').trim();
        if (!trimmed) {
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('renameEmpty'), 'error');
            }
            return;
        }
        if (trimmed === (campaign.name || '').trim()) return;
        try {
            const db = firebase.firestore();
            await db.collection('bulk_campaigns').doc(campaignId).update({
                name: trimmed,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            });
            // No toast: loadCampaigns() below redraws the row under its new name.
            await this.loadCampaigns();
        } catch (err) {
            console.error('Rename campaign failed', err);
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('renameFailed') + err.message, 'error');
            } else {
                alert(this.cTxt('renameFailed') + err.message);
            }
        }
    }

    /**
     * Confirm and cancel a scheduled campaign
     */
    async confirmCancelCampaign(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Campaign not found:', campaignId);
            return;
        }

        // Show confirmation dialog
        const confirmed = confirm(this.cTxt('cancelConfirm', { name: campaign.name || this.cTxt('untitledMessage') }));

        if (!confirmed) return;

        try {
            const db = firebase.firestore();
            await db.collection('bulk_campaigns').doc(campaignId).update({
                status: 'cancelled',
                cancelledAt: firebase.firestore.FieldValue.serverTimestamp(),
                cancelledBy: firebase.auth().currentUser?.uid || 'unknown'
            });

            // Show success message
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('cancelDone'), 'success');
            } else {
                alert(this.cTxt('cancelDone'));
            }

            // Refresh campaigns list
            await this.loadCampaigns();
        } catch (error) {
            console.error('Error cancelling campaign:', error);
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('cancelFailed') + error.message, 'error');
            } else {
                alert(this.cTxt('cancelFailed') + error.message);
            }
        }
    }

    /**
     * Resume a paused campaign (scheduled date still in the future)
     */
    /**
     * Email "Buy credits & resume" deep link handler. When the dashboard is
     * opened with ?resumeCampaign=<id> (from the insufficient-credits email),
     * open the Buy Credits modal for that campaign, then auto-resume it once
     * credits are added — the buy + resume the host asked for, in one flow.
     */
    _maybeHandleResumeDeepLink() {
        if (this._resumeDeepLinkHandled) return;
        let params;
        try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
        const id = params.get('resumeCampaign');
        if (!id) return;
        this._resumeDeepLinkHandled = true;
        // Strip the param so a later refresh doesn't re-trigger the flow.
        try {
            params.delete('resumeCampaign');
            const qs = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
        } catch (e) {}
        const campaign = (this.campaigns || []).find(c => c.id === id);
        if (!campaign) return;
        // If it's no longer paused for credits (already resumed/sent), do nothing.
        if (campaign.status !== 'paused_insufficient_credits') return;
        if (!window.WalletManager || typeof window.WalletManager.showPurchaseModal !== 'function') return;
        window.WalletManager.showPurchaseModal(async () => {
            if (typeof this.loadWalletBalance === 'function') { try { await this.loadWalletBalance(); } catch (e) {} }
            // Credits added — resume this campaign without a second confirm
            // (the host already chose "Buy credits & resume" from the email).
            await this.resumeCampaign(id, { skipConfirm: true });
        }, this.formId, { resumeCampaignId: id });
    }

    async resumeCampaign(campaignId, opts = {}) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Campaign not found:', campaignId);
            return;
        }

        // Paused by the server mid-send (provider outage) or before it (SMS
        // credits): the send time has passed, and an immediate message has
        // none, so the generic 'scheduled' below would never be picked up.
        // The server restarts it (the outage sweep, every 5 minutes), and only
        // the guests not reached yet get it: asking is setting its due time
        // to now. Writing a status here handed a Resend or an add-contacts
        // pass to the dispatcher as 'pending', which re-ran WhatsApp steps it
        // had skipped on purpose, and skipped the check for emails that timed
        // out but went (PMa review, 2026-10-07).
        const providerState = this._providerPauseState(campaign);
        if (providerState) {
            if (providerState === 'resuming') return;
            // Guests whose send timed out unanswered are held apart (see
            // _outageHeldNoteHtml), so this resume never reaches them twice.
            if (!opts.skipConfirm && !confirm(this.cTxt('providerResumeConfirm'))) return;
            try {
                const uid = firebase.auth().currentUser?.uid || 'unknown';
                await firebase.firestore().collection('bulk_campaigns').doc(campaignId).update({
                    // A minute back, so a fast clock here cannot push it past the next sweep.
                    outageAutoResumeAt: new Date(Date.now() - 60 * 1000).toISOString(),
                    outageResumeRequestedBy: uid,
                    resumedAt: firebase.firestore.FieldValue.serverTimestamp(),
                    resumedBy: uid
                });
                if (window.languageHelpers?.showToast) {
                    // A provider that refused our account (or SMS credits) is not
                    // back until our team fixes it: no promise of minutes there.
                    window.languageHelpers.showToast(this.cTxt(providerState === 'team' ? 'providerResumedTeam' : 'providerResumed'), 'success');
                }
                await this.loadCampaigns();
            } catch (error) {
                console.error('Error resuming campaign:', error);
                if (window.languageHelpers?.showToast) {
                    window.languageHelpers.showToast(this.cTxt('providerResumeFailed'), 'error');
                }
            }
            return;
        }

        const scheduledDate = campaign.scheduledSendDate
            ? (campaign.scheduledSendDate?.toDate?.() || new Date(campaign.scheduledSendDate))
            : null;
        const scheduledStr = scheduledDate
            ? scheduledDate.toLocaleString(this._dateLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
            : null;

        // Retry on a message paused for credits: it stopped while sending, so
        // its time has passed — send it now, as Send now does. Written back to
        // 'scheduled' and left to the 5-minute dispatcher, a host who had just
        // topped up watched "Scheduled" for minutes (2026-10-13).
        if (campaign.status === 'paused_insufficient_credits' && !(scheduledDate > new Date())) {
            if (!opts.skipConfirm && !confirm(this.cTxt('sendNowConfirm', { name: campaign.name || this.cTxt('untitledMessage') }))) return;
            return this._sendNow(campaignId);
        }

        if (!opts.skipConfirm) {
            // Two whole sentences: "sent on <date>" and "sent on the original
            // schedule" take different words in other languages.
            const name = campaign.name || this.cTxt('untitledMessage');
            const confirmed = confirm(scheduledStr
                ? this.cTxt('resumeConfirm', { name, when: scheduledStr })
                : this.cTxt('resumeConfirmNoDate', { name }));
            if (!confirmed) return;
        }

        try {
            const db = firebase.firestore();
            await db.collection('bulk_campaigns').doc(campaignId).update({
                status: 'scheduled',
                resumedAt: firebase.firestore.FieldValue.serverTimestamp(),
                resumedBy: firebase.auth().currentUser?.uid || 'unknown'
            });

            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('resumeDone'), 'success');
            } else {
                alert(this.cTxt('resumeDone'));
            }

            await this.loadCampaigns();
        } catch (error) {
            console.error('Error resuming campaign:', error);
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('resumeFailed') + error.message, 'error');
            } else {
                alert(this.cTxt('resumeFailed') + error.message);
            }
        }
    }

    /**
     * Pause a scheduled campaign (prevents it from being sent at scheduled time)
     */
    async pauseCampaign(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) return;

        const confirmed = confirm(this.cTxt('pauseConfirm', { name: campaign.name || this.cTxt('untitledMessage') }));
        if (!confirmed) return;

        try {
            const db = firebase.firestore();
            const ref = db.collection('bulk_campaigns').doc(campaignId);
            // Pause against the LIVE status, not the card's. A pass can finish
            // between render and click: Shira & Ilya's 19-guest message was
            // paused 3s after its addition pass wrote 'sent' (2026-10-17), and
            // the card then read "Paused" with Send now on a finished message.
            const pausable = ['scheduled', 'pending', 'pending_template', 'emails_sent_whatsapp_pending', 'processing'];
            const paused = await db.runTransaction(async (tx) => {
                const live = await tx.get(ref);
                const liveStatus = live.exists ? live.data().status : null;
                if (!pausable.includes(liveStatus)) return false;
                tx.update(ref, {
                    status: 'paused',
                    pausedAt: firebase.firestore.FieldValue.serverTimestamp(),
                    pausedBy: firebase.auth().currentUser?.uid || 'unknown',
                    previousStatus: liveStatus
                });
                return true;
            });
            if (!paused) {
                if (window.languageHelpers?.showToast) {
                    window.languageHelpers.showToast(this.cTxt('pauseTooLate'), 'info');
                } else {
                    alert(this.cTxt('pauseTooLate'));
                }
                await this.loadCampaigns();
                return;
            }

            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('pauseDone'), 'success');
            }
            await this.loadCampaigns();
        } catch (error) {
            console.error('Error pausing campaign:', error);
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('pauseFailed') + error.message, 'error');
            } else {
                alert(this.cTxt('pauseFailed') + error.message);
            }
        }
    }

    /**
     * Pause or resume the auto-retry queue for a campaign.
     * The cron at server/jobs/scheduled-campaigns.js skips campaigns where
     * autoRetryPaused === true. Only relevant for non-US WhatsApp marketing-limit
     * failures, since US contacts already had an SMS fallback attempt and aren't
     * placed in the retry queue.
     */
    async toggleAutoRetry(campaignId, paused) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) return;

        const pendingRetries = campaign.retryQueue?.failedContacts?.length || 0;
        const one = pendingRetries === 1;
        const confirmed = confirm(
            this.cTxt(paused ? 'autoRetryPauseConfirm' : 'autoRetryResumeConfirm', { name: campaign.name || this.cTxt('untitledMessage') }) + '\n\n' +
            (paused
                ? this.cTxt(one ? 'autoRetryNotRetriedOne' : 'autoRetryNotRetriedMany', pendingRetries)
                : this.cTxt(one ? 'autoRetryRetriedOne' : 'autoRetryRetriedMany', pendingRetries))
        );
        if (!confirmed) return;

        try {
            const db = firebase.firestore();
            const uid = firebase.auth().currentUser?.uid || 'unknown';
            await db.collection('bulk_campaigns').doc(campaignId).update({
                autoRetryPaused: paused,
                autoRetryPausedAt: paused ? firebase.firestore.FieldValue.serverTimestamp() : null,
                autoRetryPausedBy: paused ? uid : null
            });

            campaign.autoRetryPaused = paused;
            const toast = window.showToast || window.languageHelpers?.showToast;
            if (toast) toast(this.cTxt(paused ? 'autoRetryPaused' : 'autoRetryResumed'), 'success');
            this.renderCampaigns();
        } catch (error) {
            console.error('Toggle auto-retry failed:', error);
            const toast = window.showToast || window.languageHelpers?.showToast;
            if (toast) {
                toast(this.cTxt(paused ? 'autoRetryPauseFailed' : 'autoRetryResumeFailed') + error.message, 'error');
            } else {
                alert(this.cTxt(paused ? 'autoRetryPauseFailed' : 'autoRetryResumeFailed') + error.message);
            }
        }
    }

    /**
     * Send a paused campaign immediately (scheduled date has passed)
     */
    async sendNowCampaign(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Campaign not found:', campaignId);
            return;
        }

        const confirmed = confirm(this.cTxt('sendNowConfirm', { name: campaign.name || this.cTxt('untitledMessage') }));
        if (confirmed) await this._sendNow(campaignId);
    }

    /**
     * Send a paused message now, after the host has confirmed: Send now, and
     * Retry on a message paused for credits.
     */
    async _sendNow(campaignId) {
        try {
            console.log('Manually triggering immediate send of past campaign:', campaignId);

            const user = window.firebaseAuth.currentUser;
            if (!user) {
                throw new Error('You must be logged in');
            }

            const token = await user.getIdToken();
            // A host's own message goes through the host dispatch route.
            // /api/admin/send-campaign looked like THE send-now endpoint,
            // but it is admin-only, so for every host this button failed
            // with "Failed to send message". The host path is Resume's
            // write (out of 'paused'; the time has passed, so the 5-minute
            // dispatcher would send it anyway) plus an immediate dispatch
            // through auto-process, which checks ownership and messaging
            // rights. An admin acting on someone else's message keeps the
            // admin route.
            let response;
            if (window.isAdminMode) {
                response = await fetch(`/api/admin/send-campaign`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ campaignId })
                });
            } else {
                await firebase.firestore().collection('bulk_campaigns').doc(campaignId).update({
                    status: 'scheduled',
                    resumedAt: firebase.firestore.FieldValue.serverTimestamp(),
                    resumedBy: user.uid
                });
                response = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/auto-process`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ sendNow: true })
                });
            }

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                // A reminder whose audience could not be read: nothing
                // went, and the dispatcher tries again by itself. Not a
                // "Failed to send", and in the host's language.
                if (error.code === 'reminder_audience_unavailable' || error.reason === 'error') {
                    const note = this.cTxt('remSendNowError');
                    if (typeof window.languageHelpers?.showToast === 'function') {
                        window.languageHelpers.showToast(note, 'warning');
                    } else {
                        alert(note);
                    }
                    return;
                }
                // Still short of credits: the server paused it again (and
                // emailed the host). Say so in their language, not the code.
                if (error.error === 'INSUFFICIENT_CREDITS') {
                    await this.loadCampaigns();
                    const note = this.cTxt('skipRetryNoCredits');
                    if (typeof window.languageHelpers?.showToast === 'function') {
                        window.languageHelpers.showToast(note, 'warning');
                    } else {
                        alert(note);
                    }
                    return;
                }
                throw new Error(error.error || 'Failed to send message');
            }
            const result = await response.json().catch(() => ({}));

            await this.loadCampaigns(); // Changed from refreshDashboard to loadCampaigns for consistency

            // A reminder the server did not send (its audience is resolved
            // at send time): say why instead of "sent". One still waiting
            // on its invitation stays scheduled and goes out later.
            const reminderNote = {
                waiting_for_invitation: ['remSendNowWaiting', 'info'],
                no_recipients: ['remSendNowNoRecipients', 'info'],
                taken_elsewhere: ['remSendNowTaken', 'info'],
                cancelled: ['remSendNowCancelled', 'warning']
            }[result && result.reason];
            // A deploy stopped the send (server code 'server_restarting'):
            // say whether the rest goes out by itself or needs a resend.
            const restartNote = result && result.code === 'server_restarting'
                ? (result.resumes ? ['sendRestartResumes', 'info'] : ['sendRestartRetry', 'warning'])
                : null;
            const note = reminderNote || restartNote;
            // A cancellation names its own reason (the event was deleted,
            // RSVPs closed) rather than always blaming the invitation.
            const [toastText, toastType] = note
                ? [(reminderNote && result.reason === 'cancelled' && result.cancelReason)
                    ? this._reminderCancelReasonText(result.cancelReason)
                    : this.cTxt(note[0]), note[1]]
                : [this.cTxt('sendNowDone'), 'success'];
            if (typeof window.languageHelpers?.showToast === 'function') {
                window.languageHelpers.showToast(toastText, toastType);
            } else {
                alert(toastText);
            }
        } catch (error) {
            console.error('Error sending campaign:', error);
            if (typeof window.languageHelpers?.showToast === 'function') {
                window.languageHelpers.showToast(this.cTxt('sendNowFailed') + error.message, 'error');
            } else {
                alert(this.cTxt('sendNowFailed') + error.message);
            }
        }
    }

    /**
     * Continue editing a draft campaign
     */
    /**
     * "Send test" on a draft row (task #424).
     *
     * Sends ONE copy of this campaign to the host's own email / phone, on
     * whichever channels the campaign uses. The message is built on the SERVER
     * from the saved draft by the same code that builds the real send, so what
     * arrives is what guests would get — that is the whole reason this exists
     * rather than reusing the wizard's browser-side preview.
     *
     * One test per campaign; the server is the authority on that, this just
     * disables the button afterwards.
     */
    /**
     * Ask for the one number a phone test should go to (task #424, round 2).
     *
     * Signup never collects a phone number, so a host testing a text or a
     * WhatsApp message has nowhere for it to land. Resolves to the number, or
     * null if they backed out.
     */
    askForTestPhone(prefill) {
        const t = this.cTxt.bind(this);
        // This module has no shared escaper; the strings here are our own
        // translations plus a number the host typed, but neither goes into
        // markup unescaped.
        const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g,
            (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        return new Promise((resolve) => {
            document.getElementById('campaignTestPhoneModal')?.remove();
            document.body.insertAdjacentHTML('beforeend', `
              <div class="modal fade" id="campaignTestPhoneModal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered modal-sm">
                  <div class="modal-content">
                    <div class="modal-header py-2">
                      <h6 class="modal-title">${esc(t('sendTestPhoneTitle'))}</h6>
                      <button type="button" class="btn-close" data-test-phone-cancel aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                      <p class="small text-muted mb-2">${esc(t('sendTestPhoneHint'))}</p>
                      <input type="tel" class="form-control" id="campaignTestPhoneInput" dir="ltr"
                             inputmode="tel" autocomplete="tel" placeholder="+14155550123"
                             value="${esc(prefill || '')}">
                      <div class="small text-danger mt-2" id="campaignTestPhoneError" style="display:none;"></div>
                    </div>
                    <div class="modal-footer py-2">
                      <button type="button" class="btn btn-outline-secondary btn-sm" data-test-phone-cancel>${esc(t('cancel') || 'Cancel')}</button>
                      <button type="button" class="btn btn-primary btn-sm" id="campaignTestPhoneOk">${esc(t('sendTest'))}</button>
                    </div>
                  </div>
                </div>
              </div>`);
            const el = document.getElementById('campaignTestPhoneModal');
            const modal = bootstrap.Modal.getOrCreateInstance(el);
            let settled = false;
            const finish = (value) => {
                if (settled) return;
                settled = true;
                modal.hide();
                // Tear the node down explicitly rather than trusting the
                // hidden event, which does not fire reliably on these pages.
                setTimeout(() => {
                    el.remove();
                    document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
                    document.body.classList.remove('modal-open');
                    document.body.style.removeProperty('padding-right');
                }, 250);
                resolve(value);
            };
            el.querySelectorAll('[data-test-phone-cancel]').forEach(b =>
                b.addEventListener('click', () => finish(null)));
            el.addEventListener('click', (e) => { if (e.target === el) finish(null); });
            el.addEventListener('keydown', (e) => { if (e.key === 'Escape') finish(null); });
            const submit = () => {
                const input = document.getElementById('campaignTestPhoneInput');
                const err = document.getElementById('campaignTestPhoneError');
                const raw = String(input.value || '').trim();
                // Same shape the server accepts, so a number it would refuse is
                // caught here instead of costing a round trip.
                const digits = raw.replace(/[\s()\-.]/g, '');
                if (/[,;]/.test(raw) || !/^\+?\d{7,15}$/.test(digits)) {
                    err.textContent = t('sendTestPhoneInvalid');
                    err.style.display = 'block';
                    input.focus();
                    return;
                }
                finish(window.ValidationUtils
                    ? window.ValidationUtils.normalizeInternationalPhone(digits)   // +1 for a US/CA host's 10 digits
                    : (digits.startsWith('+') ? digits : '+' + digits));
            };
            document.getElementById('campaignTestPhoneOk').addEventListener('click', submit);
            document.getElementById('campaignTestPhoneInput').addEventListener('keydown', (e) => {
                if (e.key === 'Enter') { e.preventDefault(); submit(); }
            });
            modal.show();
            setTimeout(() => document.getElementById('campaignTestPhoneInput')?.focus(), 300);
        });
    }

    async sendCampaignTest(campaignId, btn, phone) {
        const t = this.cTxt.bind(this);
        const toast = (msg, kind) => {
            if (window.languageHelpers?.showToast) window.languageHelpers.showToast(msg, kind);
            else if (window.uiUtils?.showToast) window.uiUtils.showToast(msg, kind);
            else alert(msg);
        };
        const original = btn ? btn.innerHTML : null;
        try {
            if (btn) {
                btn.disabled = true;
                btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${t('sendTestSending')}`;
            }
            const authHeaders = await window.AuthedFetch.authHeaders();
            if (!authHeaders.Authorization) throw new Error('Please sign in again.');

            const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/send-test`, {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify(phone ? { phone } : {})
            });
            const data = await resp.json().catch(() => ({}));

            if (!resp.ok) throw new Error(data.error || t('sendTestFailed'));

            // A phone channel had nowhere to go. Ask once for a number and send
            // again with it — the account has none because signup never asks.
            if (data.needsPhone && !phone) {
                if (btn) { btn.disabled = false; btn.innerHTML = original; }
                const typed = await this.askForTestPhone(this.lastTestPhone || '');
                if (!typed) return;
                this.lastTestPhone = typed;
                return this.sendCampaignTest(campaignId, btn, typed);
            }

            const results = Array.isArray(data.results) ? data.results : [];
            const sent = results.filter(r => r.ok).map(r => r.channel);
            // Report per channel rather than one verdict: a campaign can very
            // reasonably land its email while WhatsApp is still with Meta, and
            // collapsing that into "failed" would be a lie in both directions.
            const problems = results.filter(r => !r.ok);
            if (sent.length) {
                const where = sent.includes('email') ? (data.sentTo?.email || '') : (data.sentTo?.phone || '');
                const emailCopyNote = (sent.includes('email') && Array.isArray(data.notes)
                    && data.notes.includes('email_copy_not_sent_to_guests')) ? ` · ${t('sendTestEmailCopyOnly')}` : '';
                toast(`${t('sendTestDone')}${where ? ` (${where})` : ''} — ${sent.join(', ')}${emailCopyNote}`, 'success');
            }
            // A paid text/WhatsApp leg refused on the spot was refunded — say
            // so, after " · ": provider errors carry no full stop, and on a
            // Hebrew/Arabic dashboard a bare space ran the note into them.
            problems.forEach(p => toast(`${p.channel}: ${p.error}${p.creditReturned ? ` · ${t('sendTestCreditReturned')}` : ''}`, p.pending ? 'info' : 'warning'));
            if (!sent.length && !problems.length) toast(t('sendTestFailed'), 'warning');

            if (data.testUsed) {
                // Reflect it locally so the row reads "Send test again" without
                // a full reload (the next loadCampaigns() picks up the real doc).
                const local = this.campaigns.find(c => c.id === campaignId);
                if (local) local.testSend = { sentAt: new Date() };
                if (btn) {
                    btn.innerHTML = `<i class="bi bi-send-check me-1"></i>${t('sendTestAgain')}`;
                    btn.title = t('sendTestAgainTip') + ' — ' + t('sendTestCost');
                    btn.disabled = false;
                    return;
                }
            }
            if (btn) { btn.disabled = false; btn.innerHTML = original; }
        } catch (error) {
            console.error('Send test failed:', error);
            toast(`${t('sendTestFailed')} — ${error.message}`, 'error');
            if (btn) { btn.disabled = false; btn.innerHTML = original; }
        }
    }

    /**
     * The guest list to open in the wizard, as a copy it may edit freely.
     * The list in this.campaigns is reused until contactsRev moves, and a tab
     * still running JS from before contactsRev existed can rewrite a list
     * without moving it. A save from the wizard writes the whole list back, so
     * it must start from the server's list, not the held one: one chunk read
     * per edit. Falls back to the held list if that read fails.
     */
    async _wizardContactsFor(campaign) {
        const CC = window.CampaignContacts;
        try {
            if (CC && window.firebase && CC.hasChunked(campaign)) {
                const db = firebase.firestore();
                const fresh = await CC.hydrate(db, await db.collection('bulk_campaigns').doc(campaign.id).get());
                if (fresh && Array.isArray(fresh.contacts)) {
                    // Same version as the held row: the held list was the stale
                    // one, so the cards should show the server's too.
                    if (CC.contactsKey(fresh) === CC.contactsKey(campaign)) campaign.contacts = fresh.contacts;
                    return CC.copyList(fresh.contacts);
                }
            }
        } catch (e) {
            console.warn('Could not re-read the guest list, using the one on screen:', e.message);
        }
        return CC ? CC.copyList(campaign.contacts) : (campaign.contacts || []).map(c => ({ ...c }));
    }

    async continueDraft(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Draft not found:', campaignId);
            return;
        }
        // Started now so the read overlaps the modal opening.
        const wizardContacts = this._wizardContactsFor(campaign);

        // Open the create campaign modal
        const modal = new bootstrap.Modal(document.getElementById('campaignsCreateModal'));
        const openSeq = this._beginWizardOpen(document.getElementById('campaignsCreateModal'));
        const container = document.getElementById('campaignsWizardContainer');

        // Initialize wizard HTML in the container (same as openCreateCampaignModal)
        const t = this.getWizardTranslations();
        // Shell markup is shared with the builder mount — see
        // BulkSharingWizard.renderShell (step-driven, one source of truth).
        BulkSharingWizard.renderShell(container, t, ['contacts', 'channels', 'review', 'reminders', 'send'], { loadingText: t.loadingDraft || 'Loading draft...' });

        // Show modal
        modal.show();

        // Initialize wizard and load draft data
        setTimeout(async () => {
            const contactsForWizard = await wizardContacts;
            if (!this._wizardOpenIsCurrent(openSeq)) return;
            if (window.BulkSharingWizard) {
                // Deactivate any existing wizard instance to prevent duplicate event handling
                if (window.bulkSharingWizard) {
                    window.bulkSharingWizard.isActive = false;
                    window.bulkSharingWizard.autoSaveEnabled = false;
                }

                // Create new wizard instance
                window.bulkSharingWizard = new window.BulkSharingWizard();

                // Set the form ID before initialization
                window.bulkSharingWizard.formId = this.formId;
                window.bulkSharingWizard.formData = this.formData;

                // Skip state restoration - we'll load from draft
                window.bulkSharingWizard.skipStateRestore = true;

                // Load draft data into wizard - restore ALL saved fields
                const w = window.bulkSharingWizard;

                // Core contacts & wizard state
                // Re-validate contacts so valid/invalid split is accurate.
                // A fresh copy (_wizardContactsFor): this.campaigns' list is
                // reused until contactsRev moves, so edits the host then
                // abandons in the wizard must not land in it.
                w.contacts = contactsForWizard;
                if (w.contacts.length > 0) {
                    w.validateContacts(w.contacts);
                } else {
                    w.validContacts = [];
                    w.invalidContacts = [];
                }
                w.currentStep = campaign.currentStep || 0;

                // Campaign identity
                w.campaignName = campaign.name || '';
                w.eventTitle = campaign.eventTitle || '';

                // Sending method & channels
                w.sendingMethod = campaign.sendingMethod || 'both';
                w.selectedChannels = campaign.selectedChannels || ['mobile'];
                if (typeof w.deriveDeliveryModeFromChannels === 'function') {
                    w.deriveDeliveryModeFromChannels();
                }

                // Image
                w.bulkImageUrl = campaign.bulkImageUrl || null;

                // Reminders (both new array format and legacy settings)
                w.reminders = campaign.reminders || [];
                w.reminderSettings = campaign.reminderSettings || { enabled: false, timing: [], recipients: [] };

                // The country its national numbers were read in (contacts step picker).
                if (campaign.phoneDefaultCountry) window.__qrPhoneDefaultCountry = campaign.phoneDefaultCountry;

                // Language settings. A translate-only message is stored with
                // the translation as its text; this puts the host's original
                // back in the box (BulkSharingWizard.restoreMessageLanguage).
                const restoredMessage = window.BulkSharingWizard.restoreMessageLanguage(w, campaign);

                // Email subject, invitation header, tagline.
                // Use ?? so null (= untouched, fall back to default at render time)
                // is preserved distinct from '' (= user cleared the field to hide it).
                w.emailSubject = campaign.emailSubject ?? null;
                w.messageHeader = campaign.headerText ?? null;
                w.invitationTagline = campaign.invitationTagline ?? null;
                w.emailSenderName = campaign.emailSenderName ?? null;
                w.replyToEmail = campaign.replyToEmail ?? null;
                w.emailLogoUrl = campaign.emailLogoUrl || null;
                w.hideEmailLogo = campaign.hideEmailLogo === true;

                // Scheduling (restore so Step 4 reflects saved state)
                w.isImmediate = campaign.isImmediate !== undefined ? campaign.isImmediate : true;
                w.scheduledSendDate = campaign.scheduledSendDate || null;

                // Theme & link type
                w.themeColor = campaign.themeColor || null;
                w.bulkLinkType = campaign.bulkLinkType || 'form';
                w.rsvpWebsiteUrl = campaign.rsvpWebsiteUrl || null;
                w.includeRsvpLink = campaign.includeRsvpLink !== false;

                // Links / buttons (≤2). Older campaigns have no buttons array —
                // derive from includeRsvpLink so the chips reflect reality.
                w.campaignButtons = Array.isArray(campaign.buttons)
                    ? campaign.buttons
                    : (campaign.includeRsvpLink === false ? [] : ['rsvp']);
                w.campaignButtonUrls = campaign.buttonUrls || {};
                w.campaignButtonLabels = campaign.buttonLabels || {};

                // Custom message (set on wizard object so buildReviewStep() picks it up)
                w.customMessage = restoredMessage;

                // Store draft ID so saveDraft updates instead of creating.
                // Also set editingCampaignId so that *Send* (not just Save Draft)
                // reuses the same doc — without this, sendBulkMessages falls back
                // to `bulk_${formId}_${Date.now()}` and the draft survives the
                // send, leaving two cards (the new "sent" one + the original
                // draft). Both IDs intentionally point to the same doc.
                w.draftId = campaignId;
                w.editingCampaignId = campaignId;
                // Same guard as Edit (pre-mortem #13): a draft sent or refilled
                // from another tab is not overwritten by this one.
                w._editLoadedStatus = campaign.status || null;
                w._editContactsVersion = Number(campaign.contactsVersion) || 0;

                // Set builderCore if available
                if (!window.builderCore) {
                    window.builderCore = { builderOptions: {} };
                }
                window.builderCore.builderOptions.formId = this.formId;

                // Initialize the wizard
                window.bulkSharingWizard.initializeForTab();

                // Set custom message and email subject in UI after initialization
                setTimeout(() => {
                    if (restoredMessage) {
                        window.bulkSharingWizard.setCustomMessage(restoredMessage);
                    }
                    // Set email subject input if it exists in the DOM
                    const emailSubjectInput = document.getElementById('emailSubject');
                    if (emailSubjectInput && campaign.emailSubject) {
                        emailSubjectInput.value = campaign.emailSubject;
                    }
                    // Set bulk image preview if image URL exists
                    if (campaign.bulkImageUrl) {
                        const imagePreview = document.getElementById('bulk-image-preview');
                        if (imagePreview) {
                            imagePreview.innerHTML = `<img src="${campaign.bulkImageUrl}" class="img-fluid rounded" style="max-height: 200px;" alt="Campaign image">`;
                        }
                        if (window.bulkSharingWizard?.checkBulkImageAspectRatio) {
                            window.bulkSharingWizard.checkBulkImageAspectRatio(campaign.bulkImageUrl);
                        }
                    }
                }, 300);
            } else {
                container.innerHTML = `
                    <div class="alert alert-danger">
                        <i class="bi bi-exclamation-triangle me-2"></i>
                        Campaign wizard failed to load. Please refresh the page.
                    </div>
                `;
            }
        }, 100);

        // Handle modal close - refresh campaigns list and optionally delete draft if sent
        document.getElementById('campaignsCreateModal').addEventListener('hidden.bs.modal', async () => {
            await this.loadCampaigns();
        }, { once: true });
    }

    /**
     * Confirm and delete a draft campaign
     */
    async confirmDeleteDraft(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Draft not found:', campaignId);
            return;
        }

        // Show confirmation dialog
        const confirmed = confirm(this.cTxt('deleteDraftConfirm', { name: campaign.name || this.cTxt('untitledMessage') }));

        if (!confirmed) return;

        try {
            const db = firebase.firestore();
            // Chunk docs first: the rules judge a chunk write by its parent, so
            // once the parent is gone the chunks could never be removed.
            await window.CampaignContacts.remove(db, campaignId);
            await db.collection('bulk_campaigns').doc(campaignId).delete();

            // No success message (task #525). The host confirmed the deletion a
            // moment ago and the row disappears from the list below — that IS
            // the confirmation. Failures still speak up, in the catch.

            // Refresh campaigns list
            await this.loadCampaigns();
        } catch (error) {
            console.error('Error deleting draft:', error);
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(this.cTxt('deleteDraftFailed') + error.message, 'error');
            } else {
                alert(this.cTxt('deleteDraftFailed') + error.message);
            }
        }
    }

    /**
     * Permanently delete a non-draft campaign. Used to clear stuck failed
     * sends, finished sends the host no longer wants in their history, and
     * cancelled/paused campaigns. Confirmation is heavier than the draft
     * version because deletion takes the campaign's tracking stats and
     * scheduled reminders with it (guest responses live in a separate
     * subcollection and are kept).
     */
    async confirmDeleteCampaign(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Campaign not found:', campaignId);
            return;
        }

        const stats = campaign.trackingStats || { sent: 0, responded: 0 };
        const hasActivity = (stats.sent || 0) > 0 || (stats.responded || 0) > 0 || (campaign.sentCount || 0) > 0;
        const name = campaign.name || this.cTxt('untitledMessage');
        const confirmed = confirm(this.cTxt(hasActivity ? 'deleteConfirmActive' : 'deleteConfirmUnsent', { name }));
        if (!confirmed) return;

        // Guests this message put on the guest list who never answered and are
        // on no other message (task #1002): asked, never removed silently - a
        // host tidying an old message keeps its guests unless they say so.
        const formId = campaign.formId || this.formId;
        if (hasActivity && formId && window.AuthedFetch && !campaign.isReminderCampaign) {
            try {
                const url = `/api/forms/${encodeURIComponent(formId)}/messages/${encodeURIComponent(campaignId)}/added-guests`;
                const ask = await window.AuthedFetch.fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                const found = ask.ok ? await ask.json() : null;
                if (found && found.count > 0 && confirm(this.cTxt('deleteAlsoGuests', { n: found.count }))) {
                    await window.AuthedFetch.fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ remove: true }) });
                }
            } catch (e) {
                // The message is still deleted; its guests stay on the list.
                console.warn('Could not check the guests this message added:', e);
            }
        }

        try {
            const db = firebase.firestore();
            // The dialog promises the message's reminders go with it, but only
            // this doc was deleted: its scheduled reminder children were left
            // 'scheduled' with no parent, forever (the dispatcher now cancels
            // such orphans, but they should not be made). Remove them first.
            if (!campaign.isReminderCampaign) {
                const childIds = new Set([
                    ...(Array.isArray(campaign.childReminderCampaignIds) ? campaign.childReminderCampaignIds : []),
                    ...(this.campaigns || []).filter(c => c.parentCampaignId === campaignId).map(c => c.id)
                ]);
                for (const childId of childIds) {
                    try {
                        await window.CampaignContacts.remove(db, childId);
                        await db.collection('bulk_campaigns').doc(childId).delete();
                    } catch (e) {
                        // Non-fatal: the server cancels a reminder whose parent is gone.
                        console.warn(`Could not delete reminder ${childId} with its message:`, e);
                    }
                }
            }
            // Chunk docs first: the rules judge a chunk write by its parent, so
            // once the parent is gone the chunks could never be removed.
            await window.CampaignContacts.remove(db, campaignId);
            await db.collection('bulk_campaigns').doc(campaignId).delete();

            // If this was a linked reminder (child) campaign, prune its id from the
            // parent's childReminderCampaignIds so the parent row stops advertising a
            // reminder that no longer exists. When no reminders remain, clear the
            // split-reminder/paused flags too, so server-side consumers and any code
            // reading these flags don't act on a phantom reminder.
            if (campaign.isReminderCampaign && campaign.parentCampaignId) {
                try {
                    const remaining = (this.campaigns || []).filter(c =>
                        c.isReminderCampaign &&
                        c.parentCampaignId === campaign.parentCampaignId &&
                        c.id !== campaignId
                    ).length;
                    const parentUpdate = {
                        childReminderCampaignIds: firebase.firestore.FieldValue.arrayRemove(campaignId)
                    };
                    if (remaining === 0) {
                        parentUpdate.hasSplitReminders = false;
                        parentUpdate.remindersPaused = false;
                    }
                    await db.collection('bulk_campaigns').doc(campaign.parentCampaignId).update(parentUpdate);
                } catch (e) {
                    // Non-fatal: the render now counts live children, so the UI is
                    // already correct even if this cleanup write fails.
                    console.warn('Could not prune parent campaign after reminder deletion:', e);
                }
            }

            // No toast: loadCampaigns() below redraws the list without the row.
            await this.loadCampaigns();
        } catch (error) {
            console.error('Error deleting campaign:', error);
            const msg = this.cTxt('deleteFailed') + error.message;
            if (window.languageHelpers?.showToast) {
                window.languageHelpers.showToast(msg, 'error');
            } else {
                alert(msg);
            }
        }
    }

    // A reminder whose email/text guests have it but whose WhatsApp half was
    // refused or never approved (the row's "WhatsApp not sent"). While WhatsApp
    // is still to come, an error alone is not a verdict: the dispatcher
    // records every template-creation error, the transient Meta ones it
    // retries on the next tick included. Only a refusal or the 48-hour
    // timeout is final there.
    static reminderWaHalfLost(campaign) {
        const status = campaign?.status || 'scheduled';
        const waOnlyLeft = status === 'emails_sent_whatsapp_pending';
        const waVerdictFinal = ['rejected', 'timeout'].includes(campaign?.whatsappTemplate?.status);
        return DashboardCampaigns.hasTemplateError(campaign)
            && ((waOnlyLeft && waVerdictFinal) || (['sent', 'partially_sent'].includes(status) && (campaign.sentCount || 0) > 0));
    }

    // What on a reminder row needs the host to act, or null: 'credits',
    // 'expired', 'rejected', 'failed' or 'waLost' (audit 2026-10-07, review
    // #15). A reminder the host paused is their own choice, not a problem.
    static reminderAttention(campaign) {
        if (!campaign) return null;
        const status = campaign.status || 'scheduled';
        if (status === 'paused_insufficient_credits') return 'credits';
        if (status === 'template_timeout') return 'expired';
        if (status === 'template_rejected') return 'rejected';
        if (status === 'failed') return 'failed';
        if (DashboardCampaigns.reminderWaHalfLost(campaign)) return 'waLost';
        return null;
    }

    /**
     * Edit a scheduled campaign - opens full wizard with campaign data
     */
    /**
     * "This message's WhatsApp template is broken and an edit has to resubmit it."
     *
     * True when the template never reached Meta at all, and also when a LATER
     * re-submission was refused while an older template name is still stored —
     * that second case used to be invisible, so an Edit & Resend that WhatsApp
     * rejected looked like a save that worked (task #592).
     *
     * A STATIC, because the card and the reminder editor both have to agree:
     * the card offers "Edit & Resend" on this test, and round 5 found the
     * reminder editor deciding whether to resubmit on a DIFFERENT one (three
     * dead statuses). A reminder that carried the error without one of those
     * statuses got the button, and then the save just wrote the text and said
     * "Reminder updated" — "it still says save changes and nothing happens".
     */
    static hasTemplateError(campaign) {
        const _ts = (v) => {
            if (!v) return 0;
            const d = v?.toDate?.() || (v?.seconds ? new Date(v.seconds * 1000) : new Date(v));
            return isNaN(d) ? 0 : d.getTime();
        };
        // Our own failure to get the message to WhatsApp (the wizard's
        // submit_failed: no answer, 401, 408, 429 or 5xx, which it marks with
        // templateSubmitFailedFor = the error it wrote) is no refusal: the
        // dispatcher submits it again by itself, and the wizard told the host
        // they need not do anything. The card says so (templateSubmitPending)
        // instead of "WhatsApp didn't accept this message" (round-2 review).
        // A later error of any other kind no longer matches the mark.
        const submitFailed = !!campaign?.templateCreationError
            && typeof campaign.templateSubmitFailedFor === 'string'
            && campaign.templateSubmitFailedFor === String(campaign.templateCreationError);
        // Whatever the status: a cancelled or paused card with the mark still
        // shows no refusal banner, only its status line (templateSubmitPending).
        return !!campaign?.templateCreationError && !submitFailed && (
            !campaign.whatsappTemplate?.name
            || _ts(campaign.templateCreationErrorAt) > _ts(campaign.whatsappTemplate?.createdAt)
        );
    }

    /**
     * The wizard could not get this message to WhatsApp for approval (our side
     * or the connection, not a refusal), no template has been made since, and
     * the message is in a state where it is still tried again by itself. See
     * hasTemplateError.
     *
     * The note this gates says "We'll keep trying automatically... You don't
     * need to do anything", so it needs the status (round-3 review): the
     * dispatcher retries only scheduled, pending, pending_template and
     * emails_sent_whatsapp_pending (DISPATCH_STATUSES), plus a pass running
     * now, a review hold an admin releases, and a provider pause that resumes
     * by itself. Cancelled, failed, paused by the host, out of credits or
     * finished: nothing retries, and the card's status line and buttons say
     * what to do. The mark still keeps the refusal banner away there.
     */
    static templateSubmitPending(campaign) {
        if (!DashboardCampaigns.templateSubmitMarked(campaign)) return false;
        const status = campaign.status || 'scheduled';
        if (DashboardCampaigns.SUBMIT_RETRY_STATUSES.includes(status)) return true;
        return status === 'paused'
            && ['PROVIDER_OUTAGE', 'BREVO_SMS_CREDITS_INSUFFICIENT'].includes(campaign.pausedReason)
            && (!!campaign.outageResumeRequestedBy || typeof campaign.outageAutoResumeAt === 'string');
    }

    /** The wizard's own submit failure is the latest word on the template. */
    static templateSubmitMarked(campaign) {
        const _ts = (v) => {
            if (!v) return 0;
            const d = v?.toDate?.() || (v?.seconds ? new Date(v.seconds * 1000) : new Date(v));
            return isNaN(d) ? 0 : d.getTime();
        };
        return !!campaign?.templateCreationError
            && typeof campaign.templateSubmitFailedFor === 'string'
            && campaign.templateSubmitFailedFor === String(campaign.templateCreationError)
            && (!campaign.whatsappTemplate?.name
                || _ts(campaign.templateCreationErrorAt) > _ts(campaign.whatsappTemplate?.createdAt));
    }

    async editCampaign(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Campaign not found:', campaignId);
            return;
        }

        // Reminders are NOT wizard material: the full Send-Invitations wizard
        // can't represent a reminder's multi-button config (its RSVP radios are
        // single-choice) and re-saving through it would clobber reminderConfig.
        // Standalone reminders ("Remind All" follow-ups, no parent) render as
        // top-level rows whose Edit lands here — route them to the dedicated
        // reminder composer, same as nested reminder children.
        if (campaign.isReminderCampaign) {
            return this.showEditReminderModal(campaignId);
        }
        // Started now so the read overlaps the modal opening.
        const wizardContacts = this._wizardContactsFor(campaign);

        // Open the create campaign modal
        const modal = new bootstrap.Modal(document.getElementById('campaignsCreateModal'));
        const openSeq = this._beginWizardOpen(document.getElementById('campaignsCreateModal'));
        const container = document.getElementById('campaignsWizardContainer');

        // Initialize wizard HTML in the container
        container.innerHTML = `
            <div id="bulk-sharing-wizard">
                <!-- Access Control Check -->
                <div id="bulk-access-check" class="text-center py-5">
                    <div class="spinner-border text-primary mb-3" role="status"></div>
                    <p>Loading message...</p>
                </div>

                <!-- Get Started Section -->
                <div id="bulk-get-started" class="d-none"></div>

                <!-- Wizard Progress -->
                <div id="bulk-wizard-progress" class="d-none mb-4">
                    <div class="progress mb-4 rounded-pill shadow-sm border" style="height: 8px;">
                        <div class="progress-bar bg-primary" role="progressbar" style="width: 20%"></div>
                    </div>
                    <div class="d-flex justify-content-between">
                        <div class="step-label text-center" style="flex: 1;">
                            <small class="text-muted">1. Contacts</small>
                        </div>
                        <div class="step-label text-center" style="flex: 1;">
                            <small class="text-muted">2. Channels</small>
                        </div>
                        <div class="step-label text-center" style="flex: 1;">
                            <small class="text-muted">3. Customize</small>
                        </div>
                        <div class="step-label text-center" style="flex: 1;">
                            <small class="text-muted">4. Follow-Up</small>
                        </div>
                        <div class="step-label text-center" style="flex: 1;">
                            <small class="text-muted">5. Send</small>
                        </div>
                    </div>
                </div>

                <!-- Wizard Steps Container -->
                <div id="bulk-step-contacts" class="wizard-step d-none"></div>
                <div id="bulk-step-channels" class="wizard-step d-none"></div>
                <div id="bulk-step-review" class="wizard-step d-none"></div>
                <div id="bulk-step-reminders" class="wizard-step d-none"></div>
                <div id="bulk-step-send" class="wizard-step d-none"></div>

                <!-- Wizard Navigation -->
                <div id="bulk-wizard-nav" class="d-none">
                    <div class="d-flex justify-content-between mt-4">
                        <button type="button" class="btn btn-outline-secondary rounded-pill px-4" id="wizard-prev" style="display: none;">
                            <i class="bi bi-arrow-left me-1"></i>Previous
                        </button>
                        <div class="d-flex gap-2">
                            <!-- Edit flow: this is "Save Changes", not "Save as Draft" — the campaign
                                 is already scheduled/paused/failed, not a draft. The wizard's
                                 saveDraft() branches on editingCampaignId to call _saveCampaignEdits()
                                 which updates the existing doc in place. Same button id so the
                                 existing click handler still fires. -->
                            <button type="button" class="btn btn-outline-primary rounded-pill px-3" id="wizard-save-draft">
                                <i class="bi bi-check2 me-1"></i>Save Changes
                            </button>
                            <button type="button" class="btn btn-primary" id="wizard-next" class="btn btn-primary rounded-pill shadow-sm px-4 fw-bold"> Next: Customize <i class="bi bi-arrow-right ms-1"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Show modal
        modal.show();

        // Initialize wizard and load campaign data
        setTimeout(async () => {
            const contactsForWizard = await wizardContacts;
            if (!this._wizardOpenIsCurrent(openSeq)) return;
            if (window.BulkSharingWizard) {
                // Deactivate any existing wizard instance to prevent duplicate event handling
                if (window.bulkSharingWizard) {
                    window.bulkSharingWizard.isActive = false;
                    window.bulkSharingWizard.autoSaveEnabled = false;
                }

                // Create new wizard instance
                window.bulkSharingWizard = new window.BulkSharingWizard();

                // Set the form ID before initialization
                window.bulkSharingWizard.formId = this.formId;
                window.bulkSharingWizard.formData = this.formData;

                // Skip state restoration - we'll load from campaign
                window.bulkSharingWizard.skipStateRestore = true;

                // Load campaign data into wizard - restore ALL saved fields
                const w = window.bulkSharingWizard;

                // Core contacts & wizard state. One copy for both, as before
                // they were one array: this.campaigns' list is reused until
                // contactsRev moves, so abandoned wizard edits must not land in
                // it, and a save must start from the server's list
                // (_wizardContactsFor).
                w.contacts = contactsForWizard;
                w.validContacts = w.contacts;
                // A campaign that has already gone out opens on Customize, and
                // cannot go back to Contacts or Channels (task #592 round 3).
                // Edit & Resend exists to fix the MESSAGE; changing who it went
                // to, or which channels it used, after the fact is not an edit
                // of this send — it re-delivers and re-bills, and the host had
                // no way of knowing that from the wizard. A campaign that has
                // not sent yet still opens at the beginning: everything about
                // it is still safely editable.
                const alreadyWentOut = ['sent', 'partially_sent', 'emails_sent_whatsapp_pending', 'processing']
                    .includes(String(campaign.status || ''));
                w.minStep = alreadyWentOut ? w.steps.indexOf('review') : 0;
                // …and cannot go FORWARD past it either (task #592 round 4).
                // Follow-Up re-asks for reminders that are already scheduled and
                // Finalize & Send re-asks for terms already accepted, so walking
                // on reads as "do the whole thing again" and invites changes to
                // a campaign that has already shipped. One step, one button.
                w.maxStep = alreadyWentOut ? w.steps.indexOf('review') : null;
                w.messageOnlyEdit = alreadyWentOut;
                w.currentStep = w.minStep;

                // Campaign identity
                w.campaignName = campaign.name || '';
                w.eventTitle = campaign.eventTitle || '';

                // Sending method & channels
                w.sendingMethod = campaign.sendingMethod || 'both';
                w.selectedChannels = campaign.selectedChannels || ['mobile'];
                if (typeof w.deriveDeliveryModeFromChannels === 'function') {
                    w.deriveDeliveryModeFromChannels();
                }

                // Image
                w.bulkImageUrl = campaign.bulkImageUrl || null;

                // Reminders (both new array format and legacy settings)
                w.reminders = campaign.reminders || [];
                w.reminderSettings = campaign.reminderSettings || { enabled: false, timing: [], recipients: [] };

                // The country its national numbers were read in (contacts step picker).
                if (campaign.phoneDefaultCountry) window.__qrPhoneDefaultCountry = campaign.phoneDefaultCountry;

                // Language settings. A translate-only message is stored with
                // the translation as its text; this puts the host's original
                // back in the box (BulkSharingWizard.restoreMessageLanguage).
                const restoredMessage = window.BulkSharingWizard.restoreMessageLanguage(w, campaign);

                // Email subject, invitation header, tagline.
                // Use ?? so null (= untouched, fall back to default at render time)
                // is preserved distinct from '' (= user cleared the field to hide it).
                w.emailSubject = campaign.emailSubject ?? null;
                w.messageHeader = campaign.headerText ?? null;
                w.invitationTagline = campaign.invitationTagline ?? null;
                w.emailSenderName = campaign.emailSenderName ?? null;
                w.replyToEmail = campaign.replyToEmail ?? null;
                w.emailLogoUrl = campaign.emailLogoUrl || null;
                w.hideEmailLogo = campaign.hideEmailLogo === true;

                // Scheduling (restore so Step 4 reflects saved state)
                w.isImmediate = campaign.isImmediate !== undefined ? campaign.isImmediate : true;
                w.scheduledSendDate = campaign.scheduledSendDate || null;

                // Theme & link type
                w.themeColor = campaign.themeColor || null;
                w.bulkLinkType = campaign.bulkLinkType || 'form';
                w.rsvpWebsiteUrl = campaign.rsvpWebsiteUrl || null;
                w.includeRsvpLink = campaign.includeRsvpLink !== false;

                // Links / buttons (≤2). Older campaigns have no buttons array —
                // derive from includeRsvpLink so the chips reflect reality.
                w.campaignButtons = Array.isArray(campaign.buttons)
                    ? campaign.buttons
                    : (campaign.includeRsvpLink === false ? [] : ['rsvp']);
                w.campaignButtonUrls = campaign.buttonUrls || {};
                w.campaignButtonLabels = campaign.buttonLabels || {};

                // Custom message (set on wizard object so buildReviewStep() picks it up)
                w.customMessage = restoredMessage;

                // Store campaign ID for updating instead of creating new
                w.editingCampaignId = campaignId;
                // What this edit loaded from: the save refuses to write over a
                // campaign that has moved on since (pre-mortem #13).
                w._editLoadedStatus = campaign.status || null;
                w._editContactsVersion = Number(campaign.contactsVersion) || 0;

                // Capture pre-edit snapshot of template-affecting fields so the
                // wizard's save path can decide whether the WhatsApp template
                // actually needs to be re-submitted to Meta. Re-submission means
                // Meta re-approval (minutes-to-hours) which can miss a scheduled
                // send time — so we want to avoid it unless something that affects
                // the template body actually changed.
                w._preEditSnapshot = {
                    customMessage: campaign.customMessage || '',
                    translatedMessage: campaign.translatedMessage || '',
                    translatedMessages: campaign.translatedMessages || {},
                    eventTitle: campaign.eventTitle || '',
                    headerText: campaign.headerText ?? null,
                    invitationTagline: campaign.invitationTagline ?? null,
                    bulkImageUrl: campaign.bulkImageUrl || null,
                    campaignLanguageMode: campaign.campaignLanguageMode || 'single',
                    campaignLanguages: campaign.campaignLanguages || ['en'],
                    includeRsvpLink: campaign.includeRsvpLink !== false,
                    // Buttons are baked into the WhatsApp template — part of the
                    // "does the template need re-submission?" comparison.
                    buttons: Array.isArray(campaign.buttons)
                        ? campaign.buttons
                        : (campaign.includeRsvpLink === false ? [] : ['rsvp']),
                    buttonUrls: campaign.buttonUrls || {},
                    buttonLabels: campaign.buttonLabels || {},
                    existingTemplateName: campaign.whatsappTemplate?.name || null,
                    existingTemplateStatus: (campaign.whatsappTemplate?.status || '').toLowerCase(),
                };

                // Set builderCore if available
                if (!window.builderCore) {
                    window.builderCore = { builderOptions: {} };
                }
                window.builderCore.builderOptions.formId = this.formId;

                // Initialize the wizard
                window.bulkSharingWizard.initializeForTab();

                // Set custom message and email subject in UI after initialization
                setTimeout(() => {
                    if (restoredMessage) {
                        window.bulkSharingWizard.setCustomMessage(restoredMessage);
                    }
                    // Set email subject input if it exists in the DOM
                    const emailSubjectInput = document.getElementById('emailSubject');
                    if (emailSubjectInput && campaign.emailSubject) {
                        emailSubjectInput.value = campaign.emailSubject;
                    }
                    // Set bulk image preview if image URL exists
                    if (campaign.bulkImageUrl) {
                        const imagePreview = document.getElementById('bulk-image-preview');
                        if (imagePreview) {
                            imagePreview.innerHTML = `<img src="${campaign.bulkImageUrl}" class="img-fluid rounded" style="max-height: 200px;" alt="Campaign image">`;
                        }
                        if (window.bulkSharingWizard?.checkBulkImageAspectRatio) {
                            window.bulkSharingWizard.checkBulkImageAspectRatio(campaign.bulkImageUrl);
                        }
                    }
                }, 300);
            } else {
                container.innerHTML = `
                    <div class="alert alert-danger">
                        <i class="bi bi-exclamation-triangle me-2"></i>
                        Campaign wizard failed to load. Please refresh the page.
                    </div>
                `;
            }
        }, 100);

        // Handle modal close - refresh campaigns list
        document.getElementById('campaignsCreateModal').addEventListener('hidden.bs.modal', async () => {
            await this.loadCampaigns();
        }, { once: true });
    }

    /**
     * Duplicate a campaign — opens the wizard as a brand-new campaign
     * pre-filled with the customize-step fields from the source (message,
     * header, tagline, email subject, image, RSVP link config,
     * languages/translations, theme) AND its follow-up reminders (task
     * #425; stale custom-date reminders dropped). Contacts, scheduling,
     * channels, name, and payment are intentionally left fresh so the
     * user fills them in for the new send. No editingCampaignId means
     * the save path creates a new bulk_campaigns doc.
     */
    /**
     * The wording a campaign sends when the host left the message box empty
     * (task #425). Mirrors the server's getUIText(lang).defaultInvitationMessage
     * — the WhatsApp template paths use the same string — so what a duplicate
     * pre-fills is exactly what the source actually sent.
     */
    _defaultInvitationMessage(source) {
        const lang = (source && (source.sourceLanguage || (source.campaignLanguages || [])[0]))
            || this.formData?.defaultLanguage || this.dashboardLanguage || 'en';
        const dict = window.BULK_UI_TRANSLATIONS || null;
        const block = dict && (dict[lang] || dict.en);
        return (block && block.defaultInvitationMessage) || 'You are invited to our event!';
    }

    async duplicateCampaign(campaignId) {
        const source = this.campaigns.find(c => c.id === campaignId);
        if (!source) {
            console.error('Campaign to duplicate not found:', campaignId);
            return;
        }

        const modal = new bootstrap.Modal(document.getElementById('campaignsCreateModal'));
        const openSeq = this._beginWizardOpen(document.getElementById('campaignsCreateModal'));
        const container = document.getElementById('campaignsWizardContainer');
        const t = this.getWizardTranslations();

        // Same scaffolding as openCreateCampaignModal — this is a brand-new
        // campaign, just with the message pre-filled. The save-draft button
        // keeps its "Save as Draft" label (not "Save Changes") so the user
        // understands they're creating a new campaign, not editing the source.
        // Shell markup is shared with the builder mount — see
        // BulkSharingWizard.renderShell (step-driven, one source of truth).
        BulkSharingWizard.renderShell(container, t, ['contacts', 'channels', 'review', 'reminders', 'send'], { loadingText: t.duplicatingMessage || 'Duplicating message…' });

        modal.show();

        setTimeout(() => {
            if (!this._wizardOpenIsCurrent(openSeq)) return;
            if (!window.BulkSharingWizard) {
                container.innerHTML = `
                    <div class="alert alert-danger">
                        <i class="bi bi-exclamation-triangle me-2"></i>
                        ${t.wizardFailedToLoad}
                    </div>
                `;
                return;
            }

            if (window.bulkSharingWizard) {
                window.bulkSharingWizard.isActive = false;
                window.bulkSharingWizard.autoSaveEnabled = false;
            }

            window.bulkSharingWizard = new window.BulkSharingWizard();
            const w = window.bulkSharingWizard;

            w.formId = this.formId;
            w.formData = this.formData;
            w.dashboardLanguage = this.dashboardLanguage;

            // Skip state restore so any prior draft state doesn't overwrite
            // the fields we're about to pre-fill from the source campaign.
            w.skipStateRestore = true;

            // Fresh-campaign flags (NOT setting editingCampaignId — this is new)
            w.paymentCompleted = false;
            w.paymentData = null;
            w.isSendingCampaign = false;
            w.currentStep = 0;
            w.contacts = [];
            w.validContacts = [];
            w.invalidContacts = [];

            // === Pre-fill customize-step fields ===
            // ?? preserves the null/'' distinction (null = use default at render
            // time, '' = user explicitly cleared) — same convention as editCampaign.
            //
            // Task #425 round 2: a source that never had a written message used
            // to duplicate into an EMPTY message box, which reads as "the
            // duplicate lost my message". It hadn't — that campaign genuinely
            // had none and went out with our standard invitation wording, which
            // is invisible to the host. Pre-fill that wording so what they see
            // is what their guests will get, and so it can be edited.
            w.customMessage = source.customMessage || this._defaultInvitationMessage(source);
            w.emailSubject = source.emailSubject ?? null;
            w.messageHeader = source.headerText ?? null;
            w.invitationTagline = source.invitationTagline ?? null;
            w.emailSenderName = source.emailSenderName ?? null;
            w.replyToEmail = source.replyToEmail ?? null;
            w.emailLogoUrl = source.emailLogoUrl || null;
            w.hideEmailLogo = source.hideEmailLogo === true;

            // Image
            w.bulkImageUrl = source.bulkImageUrl || null;

            // RSVP link config (all three toggles from the customize step)
            w.includeRsvpLink = source.includeRsvpLink !== false;
            w.includeQRCode = source.includeQRCode === true;
            w.bulkLinkType = source.bulkLinkType || 'form';
            w.rsvpWebsiteUrl = source.rsvpWebsiteUrl || null;
            w.customUrl = source.customUrl || null;

            // Links / buttons (≤2) — carried over so a duplicated campaign keeps
            // its registry/maps/website buttons and label overrides.
            w.campaignButtons = Array.isArray(source.buttons)
                ? source.buttons
                : (source.includeRsvpLink === false ? [] : ['rsvp']);
            w.campaignButtonUrls = source.buttonUrls || {};
            w.campaignButtonLabels = source.buttonLabels || {};

            // Language settings (single / translate / bilingual mode + translations).
            // Sets w.customMessage too: a translate-only source keeps the
            // host's original in `translateOnly` (restoreMessageLanguage).
            const restoredMessage = window.BulkSharingWizard.restoreMessageLanguage(w, source);
            w.customMessage = restoredMessage || this._defaultInvitationMessage(source);

            // Theme color travels with the message so the duplicate's preview
            // looks identical to the source's preview.
            w.themeColor = source.themeColor || null;

            // Follow-up reminders (task #425) — carried over so a duplicated
            // campaign keeps its whole reminder cadence instead of forcing the
            // host to rebuild each tier. Given FRESH ids (ids key the new
            // campaign's split children + processed tracking, so they must not
            // correlate with the source's). Custom-date reminders whose fire
            // instant has already passed are dropped — on a new send they would
            // either fire immediately or sit dead; relative timings ("1 week
            // before") recompute from the event date on their own.
            //
            // Reminders live in ONE of two places, and the split path is the
            // default for every campaign created since task #429 — so reading
            // only the embedded array (as the first cut of this did) found
            // nothing on essentially every real campaign:
            //   1. embedded  — source.reminders[] (legacy / un-splittable)
            //   2. split     — one child bulk_campaigns doc per reminder, with
            //                  the parent's reminders[] deliberately EMPTIED so
            //                  the legacy cron can't double-fire it.
            // Children are already in the loaded campaign list, so recover them
            // from there and turn each back into a wizard reminder.
            // Task #425 round 2 — a duplicate copies the MESSAGES, never the
            // send dates. Previously a custom-dated reminder whose date had
            // passed was dropped entirely, which silently lost the host's
            // wording. Now every reminder comes across and only its absolute
            // date/time is stripped: relative timings ("1 week before")
            // recompute from the new event date on their own, and a custom one
            // comes back with a blank date, which the wizard already refuses to
            // move past until the host picks a new one.
            const dupStamp = Date.now();
            const withoutSendDate = (r) => ({
                ...r, customDate: null, customHour: null, customSendAt: null
            });

            // Children link by the parent's `campaignId` field, which normally
            // equals its doc id — accept either so an older doc can't slip past.
            const sourceIds = new Set([source.campaignId, source.id].filter(Boolean));
            const splitChildren = this.campaigns
                .filter(c => c.isReminderCampaign && sourceIds.has(c.parentCampaignId))
                .sort((a, b) => (a.reminderIndex || 0) - (b.reminderIndex || 0));

            if (splitChildren.length > 0) {
                w.reminders = splitChildren
                    .map((c, i) => {
                        const cfg = c.reminderConfig || {};
                        // A child stores the RESOLVED body, not the host's raw text.
                        // For a bilingual reminder that body is still the original
                        // (its translation rides alongside in translatedMessage), so
                        // bilingual mode can be restored intact. For a translate-only
                        // reminder the body has ALREADY been translated — restoring it
                        // as "translate me" would translate a translation, so it comes
                        // back as send-as-written.
                        const wasBilingual = !!c.translatedMessage;
                        return {
                            id: `r-${dupStamp}-${i}`,
                            timing: cfg.timing || '1_week_before',
                            // Send dates never travel with a duplicate (#425).
                            customDate: null,
                            customHour: null,
                            customSendAt: null,
                            recipients: cfg.recipients || 'no_response',
                            buttons: Array.isArray(cfg.buttons) ? [...cfg.buttons] : undefined,
                            buttonUrls: { ...(cfg.buttonUrls || {}) },
                            buttonLabels: { ...(cfg.buttonLabels || {}) },
                            customMessage: c.hasCustomMessage ? (c.customMessage || '') : '',
                            languageMode: wasBilingual ? 'bilingual' : 'original',
                            // The invitation photo is opt-out per reminder: treat a
                            // child with no image as opted out only when the campaign
                            // itself had one to drop.
                            includeImage: !(source.bulkImageUrl && !c.bulkImageUrl),
                            customImageUrl: (c.bulkImageUrl && c.bulkImageUrl !== source.bulkImageUrl)
                                ? c.bulkImageUrl : null
                        };
                    });
            } else {
                w.reminders = (Array.isArray(source.reminders) ? source.reminders : [])
                    .map((r, i) => withoutSendDate({
                        ...JSON.parse(JSON.stringify(r)), id: `r-${dupStamp}-${i}`
                    }));
            }
            // Legacy campaigns that predate the reminders array: migrate their
            // reminderSettings into one reminder, same as the wizard's restore.
            if (w.reminders.length === 0 && source.reminderSettings?.enabled) {
                const old = source.reminderSettings;
                w.reminders = [{
                    id: `r-${dupStamp}-legacy`,
                    timing: old.timing?.[0] || '1_week_before',
                    recipients: old.recipients?.[0] || 'no_response',
                    customMessage: old.customReminderMessage || ''
                }];
            }

            // Drives the success banner at the top of step 1 (contacts) so the
            // user understands why they landed on contacts instead of seeing
            // the duplicated message — that lives one step over.
            w.duplicatedFromName = source.name || 'previous campaign';

            if (!window.builderCore) {
                window.builderCore = { builderOptions: {} };
            }
            window.builderCore.builderOptions.formId = this.formId;

            w.initializeForTab();

            // Populate DOM elements that the wizard only reads from the DOM
            // (not from instance state) when the customize step renders.
            setTimeout(() => {
                if (source.customMessage && w.setCustomMessage) {
                    w.setCustomMessage(w.customMessage);
                }
                const emailSubjectInput = document.getElementById('emailSubject');
                if (emailSubjectInput && source.emailSubject) {
                    emailSubjectInput.value = source.emailSubject;
                }
                if (source.bulkImageUrl) {
                    const imagePreview = document.getElementById('bulk-image-preview');
                    if (imagePreview) {
                        imagePreview.innerHTML = `<img src="${source.bulkImageUrl}" class="img-fluid rounded" style="max-height: 200px;" alt="Campaign image">`;
                    }
                    if (w.checkBulkImageAspectRatio) {
                        w.checkBulkImageAspectRatio(source.bulkImageUrl);
                    }
                }
            }, 300);
        }, 100);

        document.getElementById('campaignsCreateModal').addEventListener('hidden.bs.modal', async () => {
            await this.loadCampaigns();
        }, { once: true });
    }

    /**
     * Show modal to add more contacts to an existing campaign
     */
    async showAddContactsModal(campaignId, prefillContacts) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Campaign not found:', campaignId);
            return;
        }

        // A hand-shared card has nothing for us to send, so this modal (which
        // sends and charges credits) would only end in a refusal. Adding guests
        // there means making their personal links on that same card.
        if (campaign.status === 'manual_links' && window.PersonalLinks) {
            window.PersonalLinks.open({ formId: this.formId, campaignId, addGuests: true, title: campaign.formTitle });
            return;
        }

        // Remove existing modal and backdrop if any
        const existingModal = document.getElementById('addContactsModal');
        if (existingModal) {
            const existingInstance = bootstrap.Modal.getInstance(existingModal);
            if (existingInstance) existingInstance.dispose();
            existingModal.remove();
        }

        // Initialize state for new contacts. `prefillContacts` is only passed by
        // the "Try again" button on a failed send (task #607 round 2) — the window
        // now closes the moment the host presses send, so a failure has to be able
        // to give them back what they typed rather than losing it.
        this.newContacts = Array.isArray(prefillContacts) ? prefillContacts.map(c => ({ ...c })) : [];

        // The Sending Method dropdown was removed — we now mirror the
        // original campaign's channel mix on the server (see
        // inferDefaultSendingMethod in campaigns.controller.js). The host
        // doesn't need to re-pick it for added contacts.

        // Scheduled campaigns merge additions into the upcoming send instead of
        // sending right away — say so up front.
        const isScheduledCampaign = campaign.status === 'scheduled' && !campaign.processed;
        const sendDateLabel = (() => {
            if (!isScheduledCampaign) return '';
            const sd = campaign.scheduledSendDate?.toDate?.() || (campaign.scheduledSendDate ? new Date(campaign.scheduledSendDate) : null);
            return (sd && !isNaN(sd)) ? sd.toLocaleString(this._dateLocale(), { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
        })();

        // Redesigned (task #82): the modal opens compact with a guided empty
        // state — "Add Contact Manually" is the primary CTA, importing a file
        // the secondary. The table only appears once contacts exist, the send
        // timing note lives quietly at the bottom, and Add & Send stays
        // disabled until at least one valid contact is in the list.
        const timingNote = isScheduledCampaign
            ? `This message hasn't sent yet. New contacts join the list and get the message <strong>together with everyone else at the scheduled send time</strong>${sendDateLabel ? ` (${sendDateLabel})` : ''}.`
            : `Messages are sent <strong>immediately</strong> to the contacts you add here, using the original campaign's message. To send at a different time, create a new campaign instead.`;
        const modalHtml = `
        <div class="modal fade" id="addContactsModal" tabindex="-1">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header py-2">
                        <h5 class="modal-title fs-6">
                            <i class="bi bi-person-plus me-2 text-primary"></i>Add Contacts to "${campaign.name || 'Campaign'}"
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body pt-3 pb-2">
                        <input type="file" class="d-none" id="addContactsFileInput" accept=".csv, .vcf">

                        <!-- Empty state: the modal's whole first screen until a contact exists -->
                        <div id="acEmptyState" class="text-center py-3">
                            <div class="mx-auto mb-2 d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 text-primary" style="width:52px;height:52px;">
                                <i class="bi bi-person-plus fs-4"></i>
                            </div>
                            <div class="fw-semibold mb-1">Who should get this message?</div>
                            <div class="text-muted small mb-3">Type contacts in one by one, or import your whole list at once.</div>
                            <div class="d-flex justify-content-center gap-2 flex-wrap">
                                <button type="button" class="btn btn-primary btn-sm px-3 ac-add-row-btn">
                                    <i class="bi bi-person-plus me-1"></i>Add Contact Manually
                                </button>
                                <button type="button" class="btn btn-outline-secondary btn-sm px-3 ac-upload-btn">
                                    <i class="bi bi-upload me-1"></i>Import CSV / vCard
                                </button>
                            </div>
                            <div class="form-text small mt-2">${this.cTxt('acCsvColumns')}</div>
                        </div>

                        <!-- Contacts area: appears once the first contact exists -->
                        <div id="acContactsArea" class="d-none">
                            <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <span class="fw-semibold small">Contacts List <span class="badge bg-light text-dark border ms-1" id="validContactsBadge"></span></span>
                                <div class="d-flex gap-2">
                                    <button type="button" class="btn btn-sm btn-primary py-0 px-2 ac-add-row-btn">
                                        <i class="bi bi-plus-lg me-1"></i>Add Contact
                                    </button>
                                    <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2 ac-upload-btn">
                                        <i class="bi bi-upload me-1"></i>Import
                                    </button>
                                </div>
                            </div>
                            <div class="table-responsive border rounded" style="max-height: 260px; overflow-y: auto;">
                                <table class="table table-sm table-hover mb-0 small" id="addContactsTable">
                                    <thead class="table-light sticky-top">
                                        <tr>
                                            <!-- Task #935: Guest name(s) | Group name | Phone | Email | Max guests | Tags -->
                                            <th class="text-nowrap" style="width: 22%">${this.cTxt('acGuestNames')}
                                                <i class="bi bi-info-circle small js-hint-tip" tabindex="0" role="button" style="cursor:help;opacity:0.7;"
                                                   title="${this.cAttr('acGuestNamesHint')}"></i>
                                            </th>
                                            <th class="text-nowrap" style="width: 16%">${this.cTxt('acGroupName')}
                                                <i class="bi bi-info-circle small js-hint-tip" tabindex="0" role="button" style="cursor:help;opacity:0.7;"
                                                   title="${this.cAttr('acGroupNameHint')}"></i>
                                            </th>
                                            <th style="width: 16%">Phone</th>
                                            <th style="width: 18%">Email</th>
                                            <th class="text-nowrap" style="width: 10%">Max guests
                                                <i class="bi bi-info-circle small js-hint-tip" tabindex="0" role="button" style="cursor:help;opacity:0.7;"
                                                   title="Max guests this invitation may bring (leave blank to use the form's default limit)"></i>
                                            </th>
                                            <th class="text-nowrap" style="width: 12%">Tags
                                                <i class="bi bi-info-circle small js-hint-tip" tabindex="0" role="button" style="cursor:help;opacity:0.7;"
                                                   title="Which group this guest is in, e.g. family, reception only. Comma-separated.&#10;A part of the event limited to certain tags (builder → Parts of the event) shows only on the links of guests whose tags match."></i>
                                            </th>
                                            <th style="width: 8%"></th>
                                        </tr>
                                    </thead>
                                    <tbody id="addContactsTableBody"></tbody>
                                </table>
                            </div>
                            <!-- Spec §4 (task #935): the same name twice with nothing to tell them apart. Never blocks. -->
                            <div class="alert alert-warning small py-2 px-3 mt-2 mb-0 d-none" id="acSameNameWarning" role="status"></div>
                            <div class="mt-1 text-end text-muted small" id="validCountLine" style="display:none;">
                                <span id="validContactsCount">0</span> valid contact(s) ready to add
                            </div>
                        </div>

                        <!-- SMS consent: shown (and required) only while a guest being
                             added could be texted and the campaign has none on record
                             (_needsSmsConsentFor) -->
                        <div id="acSmsConsentRow" class="d-none mt-3 p-2 px-3 border rounded bg-light small">
                            <div class="text-muted mb-1">${this.cTxt('acSmsConsentWhy')}</div>
                            <div class="form-check mb-1">
                                <input class="form-check-input" type="checkbox" id="acSmsConsent">
                                <label class="form-check-label" for="acSmsConsent">${this.cTxt('acSmsConsent')}</label>
                            </div>
                            <div class="text-muted" style="font-size:0.75rem;"><i class="bi bi-info-circle me-1"></i>${this.cTxt('acSmsDisclosure')}</div>
                        </div>

                        <!-- Send-timing note: quiet, at the bottom (was a loud blue banner up top) -->
                        <div class="text-muted small mt-3 pt-2 border-top" style="font-size:0.78rem;">
                            <i class="bi bi-info-circle me-1"></i>${timingNote}
                        </div>
                    </div>
                    <div class="modal-footer py-2">
                        <button type="button" class="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-success btn-sm" id="addContactsSendBtn" disabled>
                            <i class="bi bi-send me-1"></i>${isScheduledCampaign ? 'Add to Campaign' : 'Add & Send Now'}
                        </button>
                    </div>
                </div>
            </div>
        </div>`;

        document.body.insertAdjacentHTML('beforeend', modalHtml);
        const modalEl = document.getElementById('addContactsModal');
        const modal = new bootstrap.Modal(modalEl);

        // Helper to validate a contact
        const validateContact = (contact) => {
            const hasName = !!contact.name?.trim();
            const hasEmail = contact.email?.trim() && window.ValidationUtils.isValidEmail(contact.email);
            const hasPhone = contact.phone?.trim() && window.ValidationUtils.isValidPhone(contact.phone);
            return hasName && (hasEmail || hasPhone);
        };

        // The send button: needs one valid contact, and the SMS-consent tick
        // whenever that box is showing.
        const syncSend = () => {
            const valid = this.newContacts.filter(validateContact);
            const needsSms = this._needsSmsConsentFor(campaign, valid);
            const row = document.getElementById('acSmsConsentRow');
            const box = document.getElementById('acSmsConsent');
            const sendBtn = document.getElementById('addContactsSendBtn');
            if (row) row.classList.toggle('d-none', !needsSms);
            if (sendBtn) sendBtn.disabled = valid.length === 0 || (needsSms && !(box && box.checked));
        };
        document.getElementById('acSmsConsent')?.addEventListener('change', syncSend);

        // Spec §4 (task #935): two invitations a guest could not tell apart by
        // name - on this message already, or being added - with nothing to
        // confirm with. A note, never a block.
        const syncSameName = () => {
            const box = document.getElementById('acSameNameWarning');
            if (!box || !window.FileHelpers || !window.FileHelpers.sameNameInvitations) return;
            const onMessage = Array.isArray(campaign.contacts) ? campaign.contacts : [];
            const dupes = window.FileHelpers.sameNameInvitations(onMessage.concat(this.newContacts));
            box.classList.toggle('d-none', !dupes.length);
            box.innerHTML = dupes.length
                ? `<i class="bi bi-people me-1"></i>${this._escape(this.cTxt('acSameName'))}<div class="mt-1">${dupes.map(d => `<span dir="auto">“${this._escape(d.name)}”</span> ×${d.count}`).join(' · ')}</div>`
                : '';
        };

        // Render table. Also drives the empty-state ↔ table swap, the valid
        // count badge, and the send button's disabled state (task #82).
        const renderTable = () => {
            const tbody = document.getElementById('addContactsTableBody');
            const validCountSpan = document.getElementById('validContactsCount');
            const emptyState = document.getElementById('acEmptyState');
            const contactsArea = document.getElementById('acContactsArea');
            const countLine = document.getElementById('validCountLine');
            const badge = document.getElementById('validContactsBadge');
            if (!tbody) return;

            const syncChrome = (validCount) => {
                const hasAny = this.newContacts.length > 0;
                if (emptyState) emptyState.classList.toggle('d-none', hasAny);
                if (contactsArea) contactsArea.classList.toggle('d-none', !hasAny);
                if (badge) badge.textContent = String(this.newContacts.length);
                if (countLine) countLine.style.display = validCount > 0 ? '' : 'none';
                if (validCountSpan) validCountSpan.textContent = String(validCount);
                syncSend();
            };

            if (this.newContacts.length === 0) {
                tbody.innerHTML = '';
                syncChrome(0);
                return;
            }

            tbody.innerHTML = this.newContacts.map((contact, index) => {
                const isEmailValid = !contact.email || window.ValidationUtils.isValidEmail(contact.email);
                const isPhoneValid = !contact.phone || window.ValidationUtils.isValidPhone(contact.phone);
                // Guest name(s) | Group name (task #935), read through the shared model.
                const cells = this._guestListCells(contact);

                return `
                    <tr>
                        <td>
                            <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="text" class="form-control form-control-sm contact-edit-input"
                                data-index="${index}" data-field="name" value="${this._escape(cells.names).replace(/"/g, '&quot;')}" placeholder="${this.cAttr('acGuestNames')}" dir="auto"
                                title="${this.cAttr('acGuestNamesHint')}" style="min-width: 150px;">
                            <div class="small text-warning-emphasis mt-1${cells.lastFirst ? '' : ' d-none'}" data-ac-lastfirst style="font-size:0.72rem; line-height:1.2;">${this.cTxt('acLastFirst')}</div>
                        </td>
                        <td>
                            <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="text" class="form-control form-control-sm contact-edit-input"
                                data-index="${index}" data-field="groupName" value="${this._escape(cells.group).replace(/"/g, '&quot;')}" placeholder="${this.cAttr('acGroupName')}" dir="auto"
                                title="${this.cAttr('acGroupNameHint')}" style="min-width: 120px;">
                        </td>
                        <td>
                            <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="tel" class="form-control form-control-sm contact-edit-input ${!isPhoneValid && contact.phone ? 'is-invalid' : ''}"
                                data-index="${index}" data-field="phone" value="${contact.phone || ''}" placeholder="Phone">
                        </td>
                        <td>
                            <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="email" class="form-control form-control-sm contact-edit-input ${!isEmailValid && contact.email ? 'is-invalid' : ''}"
                                data-index="${index}" data-field="email" value="${contact.email || ''}" placeholder="Email">
                        </td>
                        <td>
                            <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="number" min="1" step="1" class="form-control form-control-sm contact-edit-input"
                                data-index="${index}" data-field="maxGuests" value="${contact.maxGuests || ''}" placeholder="—"
                                title="Max guests this invitation may bring (leave blank to use the form's default limit)">
                        </td>
                        <td>
                            <input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="text" class="form-control form-control-sm contact-edit-input"
                                data-tag-picker data-tag-form="${this._escape(this.formId || '')}" readonly
                                data-index="${index}" data-field="tags"
                                value="${(window.FileHelpers ? window.FileHelpers.formatContactTags(contact.tags) : '').replace(/"/g, '&quot;')}" placeholder="—"
                                title="Which group this guest is in, e.g. family, reception only. Decides which parts of the event their link shows.">
                        </td>
                        <td class="text-center">
                            <button class="btn btn-sm btn-outline-danger delete-contact-row" data-index="${index}">
                                <i class="bi bi-trash"></i>
                            </button>
                        </td>
                    </tr>
                 `;
            }).join('');

            // Update validation count + empty-state/table swap + send gating
            const validCount = this.newContacts.filter(validateContact).length;
            syncChrome(validCount);
            syncSameName();

            // Re-attach listeners
            attachRowListeners();
        };

        const attachRowListeners = () => {
            // Input changes
            document.querySelectorAll('.contact-edit-input').forEach(input => {
                input.addEventListener('input', (e) => {
                    const index = parseInt(e.target.dataset.index);
                    const field = e.target.dataset.field;
                    if (field === 'maxGuests') {
                        // Store a real number (or drop the key entirely when blank) —
                        // the invitation link builder does parseInt(...) > 0, and a
                        // leftover '' or 0 would silently mean "no cap" anyway.
                        const n = parseInt(e.target.value, 10);
                        if (Number.isFinite(n) && n > 0) this.newContacts[index].maxGuests = n;
                        else delete this.newContacts[index].maxGuests;
                        return;
                    }
                    if (field === 'name' || field === 'groupName') {
                        // Guest name(s) + Group name (task #935): the row's two
                        // boxes make the invitation together, stored the way an
                        // upload stores it ({ name, groupName?, members? }).
                        const row = e.target.closest('tr');
                        const text = (f) => (row ? (row.querySelector(`[data-field="${f}"]`)?.value || '') : '').trim();
                        const c = this.newContacts[index];
                        const H = window.HouseholdNames, FH = window.FileHelpers;
                        const who = H && FH && FH.parseGuestNames
                            ? H.guestListContact(FH.parseGuestNames(text('name'), { group: text('groupName') }) || [], text('groupName'))
                            : { name: text('name') };
                        delete c.groupName;
                        delete c.members;
                        Object.assign(c, who);
                        const flag = row && row.querySelector('[data-ac-lastfirst]');
                        if (flag) flag.classList.toggle('d-none', !(FH && FH.looksLikeLastFirst && FH.looksLikeLastFirst(text('name'), { group: text('groupName') })));
                        syncSameName();
                        return;
                    }
                    if (field === 'tags') {
                        // Guest tags (task #847): same convention.
                        const tags = window.FileHelpers ? window.FileHelpers.parseContactTags(e.target.value) : null;
                        if (tags) this.newContacts[index].tags = tags;
                        else delete this.newContacts[index].tags;
                        return;
                    }
                    this.newContacts[index][field] = e.target.value;
                    if (field === 'email' || field === 'phone') syncSameName();
                });

                // Re-render on blur to show validation states; auto-prefix `+` on phone
                input.addEventListener('blur', (e) => {
                    if (e.target.dataset.field === 'phone') {
                        const index = parseInt(e.target.dataset.index);
                        if (Number.isFinite(index) && this.newContacts[index]) {
                            this.newContacts[index].phone = window.ValidationUtils.normalizeInternationalPhone(this.newContacts[index].phone || '');
                        }
                    }
                    // Tab fires blur, and renderTable() rebuilds every row — so the
                    // box the browser was about to focus is destroyed a moment
                    // before it gets there, and focus falls back to the page. That
                    // is why Tab looked broken: the DOM order was always right
                    // (task #606). Note where focus was heading and put it back on
                    // the rebuilt element.
                    const next = e.relatedTarget;
                    const toInput = next && next.dataset && next.dataset.field
                        ? { index: next.dataset.index, field: next.dataset.field } : null;
                    const toDelete = next && next.classList && next.classList.contains('delete-contact-row')
                        ? next.dataset.index : null;
                    renderTable();
                    if (toInput) {
                        const el = document.querySelector(
                            `.contact-edit-input[data-index="${toInput.index}"][data-field="${toInput.field}"]`);
                        if (el) {
                            el.focus();
                            // Land at the end of any existing text rather than
                            // selecting it, so typing appends as it would have.
                            // number/email inputs refuse this — harmless.
                            try { el.setSelectionRange(el.value.length, el.value.length); } catch (_) { /* ignore */ }
                        }
                    } else if (toDelete != null) {
                        document.querySelector(`.delete-contact-row[data-index="${toDelete}"]`)?.focus();
                    }
                });
            });

            // Delete buttons
            document.querySelectorAll('.delete-contact-row').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const index = parseInt(e.currentTarget.dataset.index);
                    this.newContacts.splice(index, 1);
                    renderTable();
                });
            });
        };

        // CSV Parser Logic
        // File Handler Logic
        const handleFileUpload = async (file) => {
            if (!window.FileHelpers) {
                console.error('FileHelpers utility not found');
                alert('File processing utility missing. Please refresh the page.');
                return;
            }

            try {
                const fileContent = await window.FileHelpers.readFile(file);

                // File may have been mangled by the app that exported it; the
                // original characters are unrecoverable here (see detectEncodingDamage).
                if (window.FileHelpers.detectEncodingDamage(fileContent)) {
                    if (!confirm(window.FileHelpers.ENCODING_DAMAGE_WARNING.replace('{filename}', file.name))) return;
                }

                let contactsToAdd = [];
                const fileName = file.name.toLowerCase();

                if (fileName.endsWith('.csv')) {
                    contactsToAdd = window.FileHelpers.parseCsv(fileContent, file.name);
                } else if (fileName.endsWith('.vcf')) {
                    contactsToAdd = window.FileHelpers.parseVCard(fileContent, file.name);
                } else {
                    alert('Unsupported file format. Please upload a CSV or vCard (.vcf) file.');
                    return;
                }

                // Mark all parsed contacts with source: 'file' and auto-prefix `+` on phone
                contactsToAdd = contactsToAdd.map(contact => ({
                    ...contact,
                    phone: window.ValidationUtils.normalizeInternationalPhone(contact.phone || ''),
                    source: 'file'
                }));

                // Name the columns we saw, as the bulk wizard does - the bare
                // "No valid contacts" left the host nothing to fix (task #1071).
                if (contactsToAdd.length === 0) {
                    const found = fileName.endsWith('.csv') ? window.FileHelpers.peekHeaders(fileContent) : [];
                    alert(`We read "${file.name}" but couldn't find any contacts in it.\n\n`
                        + (fileName.endsWith('.csv') ? `Columns found: ${found.length ? found.join(', ') : '—'}\n\n` : '')
                        + 'Each contact needs an email address or a phone number, under a heading we recognise - for example "name", "email", "phone". Rename the headings in your spreadsheet and upload again.');
                }

                this.newContacts = [...this.newContacts, ...contactsToAdd];
                renderTable();

            } catch (error) {
                console.error('Error processing file:', error);
                alert('Failed to process file. Please check the format.');
            } finally {
                // Reset file input
                document.getElementById('addContactsFileInput').value = '';
            }
        };

        // Event: import buttons (one in the empty state, one above the table)
        modalEl.querySelectorAll('.ac-upload-btn').forEach(btn => btn.addEventListener('click', () => {
            document.getElementById('addContactsFileInput').click();
        }));

        // Event: Upload File
        document.getElementById('addContactsFileInput').addEventListener('change', (e) => {
            if (e.target.files && e.target.files[0]) {
                handleFileUpload(e.target.files[0]);
            }
        });

        // Event: Add Contact Manually (primary CTA in the empty state + table header)
        modalEl.querySelectorAll('.ac-add-row-btn').forEach(btn => btn.addEventListener('click', () => {
            this.newContacts.push({ name: '', email: '', phone: '', source: 'manual' });
            renderTable();
            // Focus on the new row's name input (last one)
            setTimeout(() => {
                const inputs = document.querySelectorAll('.contact-edit-input[data-field="name"]');
                if (inputs.length) inputs[inputs.length - 1].focus();
            }, 50);
        }));

        // (Schedule type toggle removed — see modal markup above.)

        // Event: Add & Send
        document.getElementById('addContactsSendBtn').addEventListener('click', async () => {
            const validContacts = this.newContacts.filter(validateContact);

            if (validContacts.length === 0) {
                alert('Please add at least one valid contact (Name + Email/Phone).');
                return;
            }

            // A textable guest with no consent on record: the box must be
            // ticked (syncSend keeps the button disabled until it is; this
            // covers a phone typed and sent without leaving the field).
            const smsConsentNeeded = this._needsSmsConsentFor(campaign, validContacts);
            if (smsConsentNeeded && !document.getElementById('acSmsConsent')?.checked) {
                syncSend();
                return;
            }

            // Sending method is no longer selected here — the server picks
            // the right channel mix based on the original campaign.
            // The "When to Send" toggle was removed (task_1779610415846_l2s3tu74l):
            // additions to a completed campaign always send immediately.

            // Pre-flight: warn when the campaign's message references {{name}},
            // {{first_name}}, or {{email}} but some new contacts are missing
            // that field. The send won't fail (server substitutes '—') but the
            // user should know before paying credits.
            const haystack = `${campaign.customMessage || ''}\n${campaign.translatedMessage || ''}`.toLowerCase();
            const tokensInMessage = ['first_name', 'name', 'email'].filter(t => haystack.includes(`{{${t}}}`));
            if (tokensInMessage.length > 0) {
                const missingByContact = validContacts
                    .map(c => {
                        const missing = [];
                        if (tokensInMessage.includes('email') && !c.email) missing.push('email');
                        if ((tokensInMessage.includes('name') || tokensInMessage.includes('first_name')) && !c.name) missing.push('name');
                        return missing.length > 0 ? { contact: c, missing } : null;
                    })
                    .filter(Boolean);

                if (missingByContact.length > 0) {
                    const lines = missingByContact.slice(0, 5).map(({ contact, missing }) => {
                        const who = contact.name || contact.phone || contact.email || '(unnamed)';
                        return `• ${who} — missing ${missing.join(' & ')}`;
                    });
                    const more = missingByContact.length > 5 ? `\n…and ${missingByContact.length - 5} more` : '';
                    const tokenList = tokensInMessage.map(t => `{{${t}}}`).join(', ');
                    const proceed = confirm(
                        `Your campaign message uses ${tokenList}, but ${missingByContact.length} contact(s) don't have all of those fields:\n\n${lines.join('\n')}${more}\n\n` +
                        `They'll still receive the message — a "—" placeholder will appear where the personalization would go.\n\nContinue anyway?`
                    );
                    if (!proceed) return;
                }
            }

            const btn = document.getElementById('addContactsSendBtn');
            btn.disabled = true;
            btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Sending...';

            // Close NOW — before the request is even answered (task #607 round
            // 2). Holding the host in this window while the send runs is the
            // thing they asked to be rid of; the message card behind it starts
            // its own sending spinner, which is the progress indicator. What
            // came back is reported afterwards, and a failure hands them back
            // exactly what they typed.
            const typedContacts = validContacts.map(c => ({ ...c }));
            const keysBefore = this._campaignContactKeys(campaignId);
            modal.hide();

            try {
                const response = await fetch(`/api/campaigns/${campaignId}/add-contacts`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${await firebase.auth().currentUser.getIdToken()}`
                    },
                    body: JSON.stringify({
                        contacts: validContacts,
                        // The server records it on the campaign, so it is asked once.
                        ...(smsConsentNeeded ? { smsConsentConfirmed: true } : {})
                    })
                });

                const result = await response.json();

                if (result.success) {
                    // Task #267: when any contact was merged into one already in the
                    // campaign, a fleeting native alert was too easy to miss — the
                    // outcome NAMES each updated contact and what changed, and stays
                    // on screen until acknowledged. Round 2 of #607 keeps all of
                    // that but shows it in its own panel AFTER the send, rather than
                    // holding the add-contacts window open through the wait.
                    const mergedCount = result.updatedExisting || result.results?.updatedExisting || 0;
                    if (mergedCount > 0) {
                        this._showAddContactsMergeConfirmation(result);
                        await this.loadCampaigns();
                        return;
                    }
                    const toast = window.languageHelpers?.showToast || window.showToast;
                    // Rows the server refused while adding the rest (e.g. a phone
                    // with no country code). The host has to hear about these on
                    // every success path, or they think those guests were invited.
                    const invNote = Array.isArray(result.invalid) && result.invalid.length
                        ? ` ${this._describeInvalidContacts(result.invalid)}` : '';
                    // The outcomes the spinner does NOT convey still get a line
                    // — as a toast, which needs no click and blocks nothing.
                    if (result.scheduledMerge) {
                        // Scheduled campaign: contacts merged into the upcoming send.
                        const msg = result.message || `${result.added} contact(s) added — they'll get the message at the scheduled send time.`;
                        if (toast) toast(msg + invNote, invNote ? 'warning' : 'info');
                    } else if (result.queued) {
                        // Lazy template creation path: server submitted a template and queued contacts.
                        const msg = result.message || `Queued ${result.results.queued} contact(s) — they’ll be sent automatically once Meta approves the WhatsApp template.`;
                        if (toast) toast(msg + invNote, invNote ? 'warning' : 'info');
                    } else if (result.updatedExisting && !result.results) {
                        // Merge-only outcome (task #267): matching contacts had their
                        // newly-typed details saved; nothing new was sent. Worth
                        // saying, because nothing was sent and the spinner will
                        // not appear.
                        const msg = result.message || `${result.updatedExisting} existing contact(s) had their details updated — no new messages sent.`;
                        if (toast) toast(msg + invNote, invNote ? 'warning' : 'info');
                    } else if (this._additionShortfallText(result.results?.guests)) {
                        // Fewer guests reached than added: "2 of 3 sent · 1
                        // paused by WhatsApp until 14:10". An ordinary add said nothing,
                        // so a guest Meta's cooldown held back looked invited
                        // (audit 2026-10-07, review #14). A full send stays quiet.
                        if (toast) toast(this._additionShortfallText(result.results.guests) + invNote, 'warning');
                    } else if (invNote) {
                        if (toast) toast(`${result.results.sent} contact(s) added and sending.${invNote}`, 'warning');
                    } else {
                        // The ordinary "added and sending" case: no popup at all.
                        // Anything worth knowing beyond that — skipped duplicates,
                        // details merged into an existing contact — is a quiet
                        // note rather than an interruption.
                        const upd = result.results.updatedExisting || 0;
                        const skipped = result.results.duplicatesSkipped || 0;
                        if (toast && (upd > 0 || skipped > 0)) {
                            toast(`${result.results.sent} contact(s) added and sending`
                                + (upd > 0 ? ` · ${upd} existing updated` : '')
                                + (skipped > 0 ? ` · ${skipped} duplicate(s) skipped` : ''), 'info');
                        }
                    }
                    await this.loadCampaigns();
                } else {
                    const dupCount = Array.isArray(result.duplicates) ? result.duplicates.length : 0;
                    const invCount = Array.isArray(result.invalid) ? result.invalid.length : 0;
                    // Thrown AppErrors come back as { success:false, error } (see
                    // server/utils/errors.js); validation returns { success:false, message }.
                    // Read both so the host sees the REAL reason, not a generic failure.
                    let detail = result.error || result.message || 'Failed to add contacts';
                    // Nothing was added or sent: the server was going down.
                    if (result.code === 'server_restarting') detail = this.cTxt('addRestartRetry');
                    if (dupCount > 0) {
                        const dupList = result.duplicates
                            .slice(0, 5)
                            .map(d => d.email || d.phone || `row ${d.index + 1}`)
                            .join(', ');
                        detail += `\n\nAlready in campaign: ${dupList}${dupCount > 5 ? ` (+${dupCount - 5} more)` : ''}`;
                    }
                    if (invCount > 0) {
                        detail += `\n\n${this._describeInvalidContacts(result.invalid)}`;
                    }
                    // The window has already closed, so a native alert would
                    // leave the host with an OK button and no way back to the
                    // contacts they typed. Offer them back instead.
                    this._showAddContactsFailure(campaignId, typedContacts, detail);
                }
            } catch (error) {
                // A TypeError here ('Load failed' on Safari, 'Failed to fetch'
                // on Chrome) means the ANSWER never arrived, not that the send
                // failed: a phone that drops its connection during the ~9s
                // send loses the response while the server finishes and
                // messages everyone. Reporting that as "weren't added" sent
                // one host to Try again five times, each retry coming back
                // "already in this campaign" (autopilot, 2026-10-14). Look
                // at the campaign before calling it a failure.
                if (error instanceof TypeError
                    && await this._contactsLandedAnyway(campaignId, typedContacts, keysBefore)) {
                    console.warn('Add contacts: response lost, contacts are in the campaign', error);
                    return;
                }
                console.error('Error adding contacts:', error);
                this._showAddContactsFailure(campaignId, typedContacts, error.message);
            }
        });

        // Cleanup listener
        modalEl.addEventListener('hidden.bs.modal', () => {
            document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
            modal.dispose();
            modalEl.remove();
            this.newContacts = []; // clear state
        }, { once: true });

        // Initial render (empty or default info)
        renderTable();
        modal.show();
    }

    /**
     * Task #267: prominent, persistent confirmation shown INSIDE the add-contacts
     * modal whenever a contact was merged into one already in the campaign (rather
     * than a fleeting native alert that "can be easily missed"). Names each updated
     * contact and the exact fields that changed, and swaps the footer to a single
     * "Done" button so the outcome stays on screen until the host acknowledges it.
     */
    /**
     * A small standalone panel for whatever the add-contacts send turned out to
     * be. It exists because the add-contacts window now closes on the click
     * (task #607 round 2) — the outcome arrives seconds later, with nothing left
     * to render into. Self-contained: builds its own shell, cleans itself up.
     */
    _showAddContactsOutcome({ bodyHtml, buttons }) {
        document.getElementById('addContactsOutcomeModal')?.remove();
        const el = document.createElement('div');
        el.className = 'modal fade';
        el.id = 'addContactsOutcomeModal';
        el.tabIndex = -1;
        el.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-body">${bodyHtml}</div>
                    <div class="modal-footer py-2"></div>
                </div>
            </div>`;
        document.body.appendChild(el);
        const modal = new bootstrap.Modal(el);
        const footer = el.querySelector('.modal-footer');
        (buttons || []).forEach(({ label, cls, onClick }) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = `btn btn-sm ${cls || 'btn-secondary'}`;
            b.innerHTML = label;
            b.addEventListener('click', () => { modal.hide(); if (onClick) onClick(); });
            footer.appendChild(b);
        });
        el.addEventListener('hidden.bs.modal', () => {
            document.querySelectorAll('.modal-backdrop').forEach(bd => bd.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
            modal.dispose();
            el.remove();
        }, { once: true });
        modal.show();
    }

    /**
     * One plain line about the rows add-contacts refused. A phone the server
     * could not send to is named, so the host knows which number to fix.
     */
    _describeInvalidContacts(invalid) {
        const badPhones = invalid.filter(i => i.reason === 'Invalid phone number');
        const longPhones = invalid.filter(i => i.reason === 'Phone number too long');
        const missing = invalid.length - badPhones.length - longPhones.length;
        const parts = [];
        const listOf = (rows) => rows.slice(0, 5).map(i => i.phone).join(', ') + (rows.length > 5 ? ` (+${rows.length - 5} more)` : '');
        if (badPhones.length) {
            parts.push(`Not added, phone number needs the country code (like +1 or +44): ${listOf(badPhones)}.`);
        }
        if (longPhones.length) {
            parts.push(`Not added, this looks like two phone numbers in one cell. Put one number per contact: ${listOf(longPhones)}.`);
        }
        if (missing) parts.push(`${missing} contact(s) missing email/phone.`);
        return parts.join(' ');
    }

    /** Phone/email match keys for one contact (phones in matching form). */
    static contactMatchKeys(contact) {
        const keys = [];
        const phone = DashboardCampaigns.normalizePhoneForMatching(contact?.phone);
        if (phone) keys.push('p:' + phone);
        const email = String(contact?.email || '').trim().toLowerCase();
        if (email) keys.push('e:' + email);
        return keys;
    }

    /** Every match key of the contacts this campaign holds, as last loaded. */
    _campaignContactKeys(campaignId) {
        const campaign = (this.campaigns || []).find(c => c.id === campaignId);
        const keys = new Set();
        (campaign?.contacts || []).forEach(c => DashboardCampaigns.contactMatchKeys(c).forEach(k => keys.add(k)));
        return keys;
    }

    /**
     * After the add-contacts response was lost: are the typed contacts in the
     * campaign now? Only contacts that were NOT there before the send prove
     * anything (a re-typed existing one would match either way), so if there
     * are none this says no and the host sees the ordinary failure.
     *
     * The server puts new contacts on the campaign BEFORE it sends and takes
     * them off again if the send fails, so their presence alone proves
     * nothing while `addingContactsPending` is still set. Re-reads a few times
     * to let a send that is still running settle.
     */
    async _contactsLandedAnyway(campaignId, typedContacts, keysBefore) {
        const fresh = typedContacts
            .map(c => DashboardCampaigns.contactMatchKeys(c))
            .filter(ks => ks.length && !ks.some(k => keysBefore.has(k)));
        if (!fresh.length) return false;
        for (let attempt = 0; attempt < 4; attempt++) {
            if (attempt) await new Promise(r => setTimeout(r, 3000));
            try { await this.loadCampaigns(); } catch (_) { return false; }
            const campaign = (this.campaigns || []).find(c => c.id === campaignId);
            if (!campaign || campaign.addingContactsPending) continue;
            // Not there yet may be a read from the offline cache; try again.
            const now = this._campaignContactKeys(campaignId);
            if (fresh.every(ks => ks.some(k => now.has(k)))) return true;
        }
        return false;
    }

    /**
     * The send failed after the window had already closed. Say why, and hand
     * back the contacts the host typed so "Try again" is a real option rather
     * than "type all that in again" (task #607 round 2).
     */
    _showAddContactsFailure(campaignId, typedContacts, detail) {
        const lines = String(detail || 'Failed to add contacts').split('\n').filter(Boolean);
        this._showAddContactsOutcome({
            bodyHtml: `
                <div class="text-center py-2">
                    <div class="mx-auto mb-2 d-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10 text-danger" style="width:52px;height:52px;">
                        <i class="bi bi-exclamation-triangle fs-4"></i>
                    </div>
                    <div class="fw-semibold mb-1">Those contacts weren't added</div>
                </div>
                <div class="small text-muted">${lines.map(l => `<div class="mb-1">${this._escape(l)}</div>`).join('')}</div>`,
            buttons: [
                { label: 'Close', cls: 'btn-outline-secondary' },
                {
                    label: '<i class="bi bi-arrow-clockwise me-1"></i>Try again',
                    cls: 'btn-primary',
                    onClick: () => this.showAddContactsModal(campaignId, typedContacts)
                }
            ]
        });
    }

    _showAddContactsMergeConfirmation(result) {
        const merged = Array.isArray(result.mergedUpdates) ? result.mergedUpdates : [];
        const mergedCount = result.updatedExisting || result.results?.updatedExisting || merged.length || 0;
        const sent = result.results?.sent || 0;
        const added = result.added || 0; // scheduled path: brand-new contacts queued into the send
        const scheduled = !!result.scheduledMerge;

        const fieldLabels = { name: 'name', email: 'email', phone: 'phone' };
        const listHtml = merged.map(m => {
            const who = m.name || m.email || m.phone || 'Contact';
            const changed = (Array.isArray(m.changed) ? m.changed : []).map(f => fieldLabels[f] || f);
            const detail = changed.length ? `saved new ${changed.join(' & ')}` : 'already up to date';
            return `<li class="d-flex align-items-start gap-2 mb-1">
                        <i class="bi bi-check-circle-fill text-success mt-1 flex-shrink-0"></i>
                        <span><strong>${this._escape(who)}</strong> — ${this._escape(detail)}</span>
                    </li>`;
        }).join('');

        let headline;
        if (sent > 0) {
            headline = `Messaged ${sent} new contact${sent === 1 ? '' : 's'} and updated ${mergedCount} that ${mergedCount === 1 ? 'was' : 'were'} already in this campaign.`;
        } else if (scheduled && added > 0) {
            headline = `Added ${added} new contact${added === 1 ? '' : 's'} to the scheduled send and updated ${mergedCount} that ${mergedCount === 1 ? 'was' : 'were'} already in it.`;
        } else {
            headline = mergedCount === 1
                ? `This contact was already in the campaign, so we saved your new details onto them instead of adding a duplicate.`
                : `${mergedCount} of these contacts were already in the campaign, so we saved your new details onto them instead of adding duplicates.`;
        }

        this._showAddContactsOutcome({
            bodyHtml: `
                <div class="text-center py-2">
                    <div class="mx-auto mb-2 d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 text-success" style="width:52px;height:52px;">
                        <i class="bi bi-check-lg fs-3"></i>
                    </div>
                    <div class="fw-semibold mb-1">Contact details updated</div>
                    <div class="text-muted small mb-1">${this._escape(headline)}</div>
                    <div class="text-muted small mb-3"><i class="bi bi-shield-check me-1"></i>No duplicate messages were sent.</div>
                </div>
                ${listHtml ? `<div class="border rounded p-2" style="background:#F6FBF7; max-height:220px; overflow-y:auto;">
                    <ul class="list-unstyled mb-0 small">${listHtml}</ul>
                </div>` : ''}`,
            buttons: [{ label: '<i class="bi bi-check-lg me-1"></i>Done', cls: 'btn-success' }]
        });
    }

    /**
     * Show modal to edit campaign message before sending
     */
    async showEditMessageModal(campaignId) {
        const campaign = this.campaigns.find(c => c.id === campaignId);
        if (!campaign) {
            console.error('Campaign not found:', campaignId);
            return;
        }

        // Remove existing modal and backdrop if any
        const existingModal = document.getElementById('editMessageModal');
        if (existingModal) {
            const existingInstance = bootstrap.Modal.getInstance(existingModal);
            if (existingInstance) existingInstance.dispose();
            existingModal.remove();
        }

        const currentMessage = campaign.customMessage || '';
        const hasWhatsApp = campaign.whatsappCount > 0 ||
            campaign.contacts?.some(c => c.method === 'whatsapp' || c.method === 'both');

        const modalHtml = `
        <div class="modal fade" id="editMessageModal" tabindex="-1">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header bg-primary text-white">
                        <h5 class="modal-title">
                            <i class="bi bi-pencil me-2"></i>Edit Message - "${campaign.name || 'Campaign'}"
                        </h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        ${hasWhatsApp ? `
                        <div class="alert alert-info small py-2 mb-3">
                            <i class="bi bi-whatsapp me-1"></i>
                            This message includes WhatsApp contacts. A new template will be created with the updated message.
                        </div>
                        ` : ''}

                        <div class="mb-3">
                            <label class="form-label fw-semibold">Message</label>
                            <textarea id="editMessageTextarea" class="form-control" rows="5" dir="auto"
                                placeholder="Enter your invitation message...">${currentMessage}</textarea>
                            <div class="form-text">This message will appear in emails and WhatsApp messages.</div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="saveMessageBtn">
                            <i class="bi bi-check-lg me-1"></i>Save Message
                        </button>
                    </div>
                </div>
            </div>
        </div>`;

        document.body.insertAdjacentHTML('beforeend', modalHtml);
        const modalEl = document.getElementById('editMessageModal');
        const modal = new bootstrap.Modal(modalEl);

        // Add cleanup listener for when modal is hidden
        modalEl.addEventListener('hidden.bs.modal', () => {
            // Ensure backdrop is removed
            document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
            // Restore body scroll
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
            // Dispose and remove the modal element
            modal.dispose();
            modalEl.remove();
        }, { once: true });

        // Save button handler
        document.getElementById('saveMessageBtn').addEventListener('click', async () => {
            const newMessage = document.getElementById('editMessageTextarea').value.trim();

            if (!newMessage) {
                alert('Please enter a message');
                return;
            }

            const btn = document.getElementById('saveMessageBtn');
            btn.disabled = true;
            btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Saving...';

            try {
                const response = await fetch(`/api/campaigns/${campaignId}/message`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${await firebase.auth().currentUser.getIdToken()}`
                    },
                    body: JSON.stringify({
                        customMessage: newMessage,
                        translatedMessages: {} // Could be extended to support translations
                    })
                });

                const result = await response.json();

                if (result.success) {
                    modal.hide();
                    alert(result.message || 'Message updated successfully!');
                    await this.loadCampaigns();
                } else {
                    alert(`Error: ${result.message || 'Failed to update message'}`);
                    btn.disabled = false;
                    btn.innerHTML = '<i class="bi bi-check-lg me-1"></i>Save Message';
                }
            } catch (error) {
                console.error('Error updating message:', error);
                alert(`Error: ${error.message}`);
                btn.disabled = false;
                btn.innerHTML = '<i class="bi bi-check-lg me-1"></i>Save Message';
            }
        });

        modal.show();
    }
}

// Reminder cancellations the host can undo by fixing the cause and editing
// the reminder (the edit re-arms it). The invitation-gone codes stay final,
// like the host's own Cancel.
DashboardCampaigns.REMINDER_FIXABLE_CANCEL_REASONS = ['rsvp_deadline_passed', 'event_date_moved', 'form_deleted'];

// Statuses in which a message whose template never reached WhatsApp is still
// tried again by itself (see templateSubmitPending): the dispatcher's
// DISPATCH_STATUSES (server/jobs/scheduled-campaigns.js), a pass running now,
// and a review hold that sends once an admin approves it.
DashboardCampaigns.SUBMIT_RETRY_STATUSES = ['scheduled', 'pending', 'pending_template', 'emails_sent_whatsapp_pending', 'processing', 'held_for_review'];

// Dynamic-string translations for campaign/reminder rows (see cTxt()).
// English is the source; other languages fall back to English per-key.
// Failure codes another channel is the answer to (renderFailedBuckets'
// "Need a different channel"); also the guests the card's "Send from my
// WhatsApp" counts (loadMessageTrackingStats).
DashboardCampaigns.CHANNEL_SWITCH_CODES = ['131026', '130472', '130497', 'SMS_BOUNCED', 'NON_US_NUMBER', 'SMS_NON_GSM'];

DashboardCampaigns.CAMPAIGN_I18N = {
    en: {
        remWaOnlyWaitingTip: "Your email and text guests already have this reminder. Your WhatsApp guests get it automatically once WhatsApp (Meta) approves the message.",
        remWaOnlyWaiting: "WhatsApp guests get it once approved",
        quietHoldSystemTip: "This reminder was due during the night for some of your guests, so it waits until it's daytime for all of them. Nothing else to do.",
        quietHoldLateTip: "WhatsApp approved your message during the night for some of your guests. So we don't wake them, it goes out once it's daytime for all of them. Nothing else to do.",
        remWaLost: "WhatsApp not sent",
        remWaLostTip: "WhatsApp didn't accept this reminder's message, so your WhatsApp guests haven't received it. Your email and text guests already have it. Edit the reminder and save to send it to your WhatsApp guests.",
        remWaLostTimeoutTip: "WhatsApp did not approve this reminder's message within 48 hours of its send time, so your WhatsApp guests haven't received it. Your email and text guests already have it. Edit the reminder and save to resubmit it; you don't need to change anything.",
        remWaLostPhrase: "Edit and save to send to WhatsApp guests",
        waTimeoutBody: "WhatsApp did not approve this message within 48 hours of its send time, so your WhatsApp guests have not received it.",
        waOthersHaveIt: "Your email and text guests already have it. {editResend} sends it to your WhatsApp guests.",
        waRejectedBadgeTip: "WhatsApp didn't accept this message. Edit the message and resubmit it to WhatsApp.",
        previewTitle: "Message preview",
        previewNoBody: "(no message body recorded)",
        previewNoteOne: "On WhatsApp this appears as the tappable button above (click it to test the link).",
        previewNoteMany: "These appear as tappable buttons on WhatsApp and email (click here to test each link).",
        previewSmsNote: "Over SMS the link arrives as text:",
        previewSampleFor: "Sample shown for {name} — variables like {{first_name}} are filled in per guest.",
        previewClone: "Clone message as new draft",
        close: "Close",
        preview: "Preview", status: "Status", remind: "Remind", add: "Add", addContacts: "Add Contacts", reminders: "Reminders", remindersNoneTip: "No reminders yet — send one now",
        duplicate: "Duplicate", delete: "Delete", edit: "Edit", editResend: "Edit & Resend",
        personalLinks: "Personal links", sharedByHand: "Personal link", copyPersonalLink: "Copy personal link", linkCopied: "Personal link copied",
        sendOnWhatsApp: "Send on WhatsApp from your phone", waWalkBtn: "Send from my WhatsApp", waWalkIntro: "Our WhatsApp couldn't reach these guests. Send each one the message from your own WhatsApp: open it, press send, then come back for the next.", waWalkCounter: "Guest {i} of {n}", waWalkOpen: "Open WhatsApp", waWalkSkip: "Skip", waWalkClose: "Close", waWalkDone: "Done. You opened WhatsApp for {k} of {n} guests.",
        hostSentBadge: "Sent from your WhatsApp", hostSentTip: "You opened your own WhatsApp to send this message. We can't see inside WhatsApp, so it isn't confirmed as delivered.", waWalkCardBtn: "Send from my WhatsApp ({n})", waWalkCardTip: "Our WhatsApp couldn't reach these guests. Send them this message from your own WhatsApp, one at a time.",
        waWalkImageNote: "WhatsApp opens with the text only. To include your invitation picture, attach it in WhatsApp before you press send.", waWalkImageOpen: "Open the picture to save it",
        addedSkippedOptedOut: "{n} added guest(s) skipped: reminders are off for them", addedSkippedResponded: "{n} added guest(s) skipped: they had already replied",
        tagContacts: "Tag everyone on this message", tagContactsPrompt: "Tags to add to every guest on this message, separated by commas (e.g. family, reception only):", tagContactsDone: "Tagged {n} guests", tagContactsFailed: "Could not tag the guests",
        moreActions: "More actions",
        continueBtn: "Continue", pause: "Pause", cancel: "Cancel", sendNow: "Send Now",
        sendTest: "Send test",
        sendTestTip: "Send one copy of this message to yourself, so you can see exactly what guests will get",
        sendTestSending: "Sending your test…",
        sendTestDone: "Test sent to you",
        sendTestEmailCopyOnly: "The email is a copy for you — your guests won't be emailed.",
        sendTestNoMessage: "Add a message and choose how it should be sent, then you can send yourself a test",
        sendTestFailed: "Could not send the test",
        sendTestCreditReturned: "The credit for this test has been returned to your balance.",
        sendTestCost: "Your first test on this message is free. After that, each text or WhatsApp test uses 1 credit — email tests are always free.",
        sendTestAgain: "Send test again",
        sendTestAgainTip: "Send yourself another copy — useful after you have changed the wording",
        sendTestPhoneTitle: "Where should the test go?",
        sendTestPhoneHint: "Your account has no mobile number, so enter one to send the test to. We will remember it.",
        sendTestPhoneInvalid: "That doesn't look like a mobile number. Use the international form, for example +14155550123.",
        resume: "Resume", buyCredits: "Buy Credits", retry: "Retry",
        previewTip: "Preview the message text",
        statusTip: "See who got the message and their response",
        remindTip: "Send a follow-up to guests in this message",
        addTip: "Add more contacts to this message",
        addScheduledTip: "Add more contacts — they'll get the message at the scheduled send time",
        addDisabledTip: "Can't add contacts — fix this message first via Edit & Resend (the WhatsApp template never made it to Meta).",
        duplicateTip: "Reuse this message (image, text, languages, link) in a brand-new message",
        deleteTip: "Permanently delete this message from your history",
        pauseTip: "Pause this message",
        buyCreditsTip: "Top up your message credits, then resume this message",
        retryTip: "Try sending again (succeeds once you have enough credits)",
        manageRemindersTip: "Manage scheduled reminders",
        scheduleRemindersTip: "Schedule reminders",
        resumeAutoRetryTip: "Resume auto-retry ({n} pending)",
        pauseAutoRetryTip: "Pause auto-retry ({n} pending)",
        remindPending: "Pending guests", remindPendingSub: "Haven't responded yet",
        remindMaybe: "Maybe responders", remindMaybeSub: "Said maybe — nudge them to confirm",
        remindYes: "Yes responders", remindYesSub: "Day-of reminder, venue update, etc.",
        remindNo: "No responders", remindNoSub: "Unable to attend — send a live-stream link, thank-you, etc.",
        remindRemovedOne: "The guest you removed won't get this reminder.", remindRemovedMany: "The {n} guests you removed won't get this reminder.",
        // Response-edit (pencil) window: _openResponseEditModal and what it opens.
        reTitle: "Update response for {name}",
        reThisGuest: "this guest",
        reContact: "contact",
        reManualEntry: "Manual entry:",
        reManualEntryNote: "Created here because no form response existed.",
        reRemove: "Remove",
        reCurrentlyPaired: "Currently paired:",
        reUnpair: "Unpair",
        reEditDetails: "Edit contact details",
        rePhone: "Phone",
        reEmail: "Email",
        reInvitationHelp: "List everyone, separated by {sep} (e.g. {example}). Edits update their live link automatically.",
        reChildMarker: "(child)",
        reExample: "Michael Brown; Emily (child)",
        reMaxGuests: "Max guests",
        reMembersPh: "e.g. Michael Brown; Emily (child)",
        // Task #935: the guest-list columns (Add contacts, the contact window).
        acGuestNames: "Guest name(s)",
        acGuestNamesHint: "How to add guest names\n• Separate names with semicolons (;). Commas work too.\n• Add (child) after a child’s name, e.g. Emily Smith (child).\n• Example: Sarah Smith; Michael Smith; Emily Smith (child)\n• The phone and email on the row belong to the first guest listed.",
        acGroupName: "Group name",
        acGroupNameHint: "Who the invitation is addressed to, e.g. The Smith Family: on the envelope and the form’s welcome; messages greet the guest names. With no guest names, guests find it by this name. Leave it blank for a guest invited alone.",
        acCsvColumns: "CSV columns: Guest name(s), Group name, Phone, Email · optional: Max guests, Tags",
        acSameName: "Warning: You have multiple guests or groups with the same name. Please add an email address or phone number for each so they can verify their identity during lookup.",
        acLastFirst: "One person, last name first? Put \";\" between different people.",
        reGroupName: "Group name",
        reGroupNameHint: "Who the invitation is addressed to: the envelope and the greeting. With nobody listed, guests find the invitation by this name.",
        reMaxGuestsHint: "Leave blank to size the party by the names listed (or the form's own limit).",
        reGroupNamePh: "e.g. The Smith Family",
        reGuestNames: "Guest name(s)",
        reTags: "Tags",
        reTagsPh: "e.g. family, reception only",
        reTagsHelp: "Tags decide which parts of the event this guest's link shows, when a part is limited to certain tags.",
        reLoading: "Loading…",
        reSave: "Save",
        rePairHeading: "Pair with an existing response",
        rePairHelp: "If {name} responded via the form but the response didn't auto-match (e.g. shared link, different phone), pair it here.",
        reLooking: "Looking for matching responses…",
        reManualHeading: "Or record manually",
        reManualHelp: "Only use this if {name} responded outside the form (text, phone, in person).",
        reAttending: "Attending",
        reDeclined: "Declined",
        reMaybe: "Maybe",
        reResponded: "Responded",
        rePending: "Pending",
        reSaving: "Saving…",
        reSaved: "Saved ✓",
        reCouldNotSave: "Could not save",
        reNotOnMessage: "This guest is no longer on the message, so their invitation can't be edited here.",
        rePartyMany: "Their invitation is for {n} people.",
        rePartyOne: "Their invitation is for one person.",
        reLoadFailed: "Could not load the invitation.",
        reMaxInvalid: "Max guests must be a whole number from 1 to 50.",
        reBrowseAll: "Browse all {n} unpaired responses",
        reBrowseOne: "Browse the 1 unpaired response",
        reHideList: "Hide full list",
        reCandidatesFailed: "Couldn't load possible matches.",
        reManualTag: "Manual",
        reManualTagTip: "Created as a manual entry on another contact",
        reNoName: "(no name)",
        rePair: "Pair",
        reLikely: "Likely matches",
        reNoUnpaired: "No unpaired responses on this form. If the guest responded outside the form, use the buttons under \"Or record manually\" below.",
        reNoLikely: "No likely matches by name, phone or email. Browse the full list below if you know which response is theirs.",
        reSearchPh: "Search by name, phone or email…",
        rePaired: "Response paired to {name}",
        reUnpaired: "{name} unpaired",
        reRecorded: "{status} recorded for {name}",
        reManualRemoved: "Manual entry removed",
        reFailedPair: "Could not pair the response",
        reFailedUnpair: "Could not unpair",
        reFailedRecord: "Could not record the response",
        reFailedRemove: "Could not remove",
        reUpdatedNumber: "Contact number updated.",
        reUpdatedEmail: "Email address updated.",
        reUpdatedDetails: "Contact details updated.",
        reResendAsk: "Send this message again to {dest}?",
        reMultiMessage: "This contact appears in more than one message, so use {addContacts} on the message you want to resend.",
        reResend: "Resend",
        reSending: "Sending…",
        reSentNumber: "Sent to their new number — it will appear in this contact's history shortly.",
        reQueued: "Queued: it sends on its own once WhatsApp approves this message, usually within minutes.",
        reSentEmail: "Sent to their new email address — it will appear in this contact's history shortly.",
        reSentBoth: "Sent to their new number and address — it will appear in this contact's history shortly.",
        reResendFailed: "Could not resend — please try again.",
        reminderDupSkipped: "Already reminded by another message: {n}",
        reminderDupSkippedBy: "Already reminded by “{name}”: {n}",
        reminderDupSkippedTip: "These guests got the same reminder from another message on this event in the last 20 hours, so this one skipped them instead of sending a duplicate.",
        reminderDupAll: "Everyone on this reminder had already been reminded by another message",
        // Reminders needing the host, the reminder audience preview, and what Add Contacts reached (audit 2026-10-07, M3).
        remAttentionOne: "1 reminder needs your attention",
        remAttentionMany: "{n} reminders need your attention",
        remFailExpiredNote: "WhatsApp didn't approve this reminder's message in time. Edit it and save to try again.",
        remFailRejectedNote: "WhatsApp didn't accept this reminder's message. Edit the message and save to resubmit it.",
        remAudienceBtn: "See who this reminder will go to",
        remAudienceTitle: "Who this reminder goes to",
        msgAudienceTitle: "Who this message goes to",
        msgAudienceOne: "This message goes to this guest.",
        msgAudienceMany: "This message goes to these {n} guests.",
        remAudienceNowOne: "If it sent now, it would go to 1 guest. The list is finalized when it sends, so it can still change.",
        remAudienceNowMany: "If it sent now, it would go to {n} guests. The list is finalized when it sends, so it can still change.",
        remAudienceFixedOne: "This reminder goes to this guest.",
        remAudienceFixedMany: "This reminder goes to these {n} guests.",
        remAudienceEmpty: "Nobody matches right now. The list is checked again when the reminder sends.",
        remAudienceFailed: "We couldn't load who this reminder goes to. Please try again.",
        remAudienceFixedNone: "Nobody is on this reminder's list, so it won't reach anyone.",
        remAudienceMeantOne: "This reminder was meant to go to this guest. It won't go out until you edit it and save.",
        remAudienceMeantMany: "This reminder was meant to go to these {n} guests. It won't go out until you edit it and save.",
        remFailWaOnlyRefusedNote: "WhatsApp didn't accept this reminder's message, and its guests can only be reached on WhatsApp, so nothing was sent. A personalization tag at the very end of the message is a common cause: add text after it, then save to resubmit.",
        acSentOf: "{sent} of {total} sent",
        acSkippedUntil: "{n} paused by WhatsApp until {time}, try again then",
        acSkipped: "{n} skipped",
        acUnreachable: "{n} couldn't be sent (contact details)",
        acFailed: "{n} not reached",
        acDeferred: "{n} will go out in the next sending window",
        acLineOne: "1 guest added after launch",
        acLineMany: "{n} guests added after launch",
        acLineLast: "last {n}",
        acLineAwaiting: "{n} awaiting WhatsApp approval",
        acTipAdded: "{when}: {n} added",
        reResendCooldown: "WhatsApp is pausing messages to this number until {time}. Nothing was charged. Try again after that, or send the personal link yourself.",
        reResendCooldownNoTime: "WhatsApp is pausing messages to this number for now. Nothing was charged. Try again later, or send the personal link yourself.",
        reResendNotSent: "The message couldn't be sent to those details. Nothing was charged. Check the number or address and try again.",
        // Every guest skipped (Meta 24h cooldown, not on WhatsApp...): see _skipSummary and _mergeSendTimeFailures.
        skipNotSent: "Not sent",
        skipNotSentTip: "Nothing went out: every guest on this message was held back. The note below says why and what you can do.",
        skippedOne: "1 skipped",
        skippedMany: "{n} skipped",
        skippedTip: "Held back on purpose and not sent. Click to see who and why.",
        skipCoolOne: "WhatsApp refused a recent message to 1 guest, so we're pausing messages to them until {time}.",
        skipCoolMany: "WhatsApp refused recent messages to {n} guests, so we're pausing messages to them. The first of them can be tried again after {time}.",
        skipCoolLiftedOne: "WhatsApp refused a recent message to 1 guest. Our pause on messages to them has ended.",
        skipCoolLiftedMany: "WhatsApp refused recent messages to {n} guests. Our pause has ended for the first of them.",
        skipCoolWhy: "WhatsApp turns away marketing messages to people who have had many lately, and doesn't say for how long. After a refusal we pause our messages to that number instead of sending into it again. The pause covers every event on QuikRSVP, so the refused message may not have been yours. Nothing is charged for a held message. A retry after the pause can still be refused.",
        skipNoWaOne: "1 guest isn't on WhatsApp. Reach them by SMS or email, or send the personal link yourself.",
        skipNoWaMany: "{n} guests aren't on WhatsApp. Reach them by SMS or email, or send the personal links yourself.",
        skipUnreachOne: "1 guest couldn't be sent to: their phone or email needs fixing. Open the list to see who and what to fix.",
        skipUnreachMany: "{n} guests couldn't be sent to: their phone or email needs fixing. Open the list to see who and what to fix.",
        skipOptOutOne: "1 guest replied STOP to our WhatsApp messages, so we don't message them there.",
        skipOptOutMany: "{n} guests replied STOP to our WhatsApp messages, so we don't message them there.",
        skipUnsubOne: "1 guest unsubscribed from your emails. Reach them by WhatsApp or text instead.",
        skipUnsubMany: "{n} guests unsubscribed from your emails. Reach them by WhatsApp or text instead.",
        skipDoRetryAt: "Try again after {time}, or open Personal links and send the link yourself.",
        skipDoRetryNow: "Try again now, or open Personal links and send the link yourself.",
        skipDoLink: "Open Personal links and send the link yourself.",
        skipHandledRepliedOne: "Not sent by us, but this guest has already replied.",
        skipHandledRepliedMany: "Not sent by us, but every guest it was for has already replied.",
        skipHandledLinkOne: "Not sent by us, but this guest has opened their personal link.",
        skipHandledLinkMany: "Not sent by us, but every guest it was for has opened their personal link or replied.",
        skipStillBlockedOne: "We're still pausing messages to this guest until {time}. Try again after that, or send the personal link yourself.",
        skipStillBlockedMany: "We're still pausing messages to these guests. The first of them can be tried again after {time}; until then, send the personal links yourself.",
        skipRowCool: "WhatsApp refused a recent message to this number (it may have been for another event), so we're pausing all messages to it until {time}. Try again after that, or send the personal link yourself.",
        skipRowCoolLifted: "WhatsApp refused a recent message to this number (it may have been for another event). Our pause has ended, so you can try again, or send the personal link yourself.",
        skipRowNoWa: "This number isn't on WhatsApp. Reach them by SMS or email, or send the personal link yourself.",
        skipRowNoWaNoEmail: "This number isn't on WhatsApp. Reach them by SMS, or send the personal link yourself.",
        fbTipFailedEmail: "WhatsApp couldn't deliver, so this went out by email instead.",
        fbTipFailedSms: "WhatsApp couldn't deliver, so this went out by SMS instead.",
        fbTipNoWa: "This number isn't on WhatsApp, so we emailed them instead.",
        fbTipCool: "WhatsApp messages to this number are paused for now, so we emailed them instead.",
        fbStateOpened: "It has been opened.",
        fbStateDelivered: "It was delivered.",
        fbStateOnWay: "It's on its way.",
        skipRowOptOut: "This guest replied STOP to our WhatsApp messages, so we don't message them there.",
        skipRowOptOutSms: "This guest replied STOP to our texts, so we don't text them again.",
        skipRowUnsub: "This guest unsubscribed from your emails. Reach them by WhatsApp or text instead.",
        skipRowOther: "Skipped: this number can't get WhatsApp messages right now.",
        skipRowNoDetails: "Not sent: no phone number or email. Add one and resend, or send the personal link yourself.",
        skipRowBadNumber: "Not sent: this number can't be dialled. Check the digits and the country code, then resend.",
        skipRowNoEmail: "Not sent: set to email, but there's no email address. Add one, or send by WhatsApp instead.",
        skipRowNoPhone: "Not sent: set to text, but there's no phone number. Add one and resend.",
        skipRowShared: "Not sent: same number as {name}, and each number gets one message. Give them their own number or email, or add them to {name}'s invitation.",
        skipRowSharedNoName: "Not sent: another guest has the same number, and each number gets one message. Give them their own number or email.",
        skipRowNoChannel: "Not sent: no way to send was set for this guest. Edit their details and resend.",
        skipBadge: "Skipped",
        unsubBadge: "Unsubscribed",
        skipRetrySent: "Sent. The card will update in a moment.",
        skipRetryFailed: "WhatsApp didn't take it this time. Open Status to see why.",
        skipRetryStill: "Nothing went out: the pause on messages to these guests still applies. Try again later, or send the personal link yourself.",
        skipRetryHeld: "Held for a quick review before it goes out. It sends once approved.",
        skipRetryPaused: "Not sent: it's paused. The card shows why.",
        skipRetryNoCredits: "Paused: add credits to send it.",
        skipRetryRequested: "Retry requested. The card shows where it stands.",
        skipRetryError: "Couldn't try again just now. Please try once more in a minute.",
        // Status window load error and the retry lock (campaign audit 2026-10-07).
        retryBusy: "This message is still being sent. When it finishes, check who still needs it and try again.",
        statusLoadFailed: "We couldn't load the guest list just now.",
        statusLoadExpired: "Your session has expired. Reload the page to see the guest list.",
        statusLoadRetry: "Try again",
        statusLoadReload: "Reload page",
        statusRefreshFailed: "Couldn't refresh the guest list. Reload the page to see the latest status.",
        retrySentOne: "Message sent again to 1 guest.",
        retrySentMany: "Message sent again to {n} guests.",
        retryStillFailedOne: "1 guest still couldn't be reached.",
        retryStillFailedMany: "{n} guests still couldn't be reached.",
        retryStaleView: "A resend of this message finished after this list loaded, so nothing was sent this time. The list has been refreshed: check who still needs it and try again.",
        // SMS consent in add-contacts / resend (_needsSmsConsentFor)
        acSmsConsentWhy: "Guests on US numbers get this message as a text, and other +1 or Brazilian numbers may get one if WhatsApp fails.",
        acSmsConsent: "I confirm that all recipients have consented to receive SMS event invitation messages for my event via QuikRSVP.",
        acSmsDisclosure: "Recipients can reply STOP to opt out or HELP for assistance. Msg & data rates may apply. Msg frequency varies.",
        waRejectedTitle: "WhatsApp didn't accept this message",
        waOursTitle: "WhatsApp messages couldn't be sent",
        waTimeoutTitle: "WhatsApp didn't approve this message in time",
        waSubmitPendingTitle: "Still sending your message to WhatsApp for approval",
        waSubmitPendingBody: "We couldn't reach WhatsApp yet. We'll keep trying automatically and email you if WhatsApp turns it down. You don't need to do anything.",
        addWaitWaTip: "You can add contacts once your message has reached WhatsApp for approval. We're still trying, so check back in a few minutes.",
        // Reminder rows: why the dispatcher cancelled one (cancelledReason) or moved it
        remCancelled: "Cancelled",
        remCancelFormDeleted: "Not sent: the event was deleted.",
        remCancelDeadline: "Not sent: RSVPs closed before it was due.",
        remCancelDateMoved: "Not sent: the event moved earlier and its time had passed.",
        remOnHold: "On hold",
        remHeldPostponed: "On hold: the event is postponed. It moves to the new date once you set one.",
        remCancelEventCancelled: "Not sent: the event was cancelled.",
        remCancelPostponedLate: "Not sent: the event was postponed until more than a day after this reminder's time.",
        remCancelInvitation: "Not sent: its invitation was deleted or cancelled.",
        remCancelLate: "Not sent: its invitation was stopped until more than a day after this reminder's time.",
        remCancelNotSent: "Not sent",
        remMovedWithEvent: "moved with the event date",
        remCancelDeadlineHint: "Move the RSVP deadline or remove the RSVP link, then edit it to send.",
        remCancelDateMovedHint: "Edit it to pick a new time.",
        remSendsSoon: "Sends within the hour",
        remDateMovedPast: "The event moved earlier and this reminder's new time has passed, so it won't be sent. Edit it to pick a new time.",
        remSendNowWaiting: "This reminder will go out once its invitation has reached every guest.",
        remSendNowNoRecipients: "No guests matched this reminder, so nothing was sent.",
        remSendNowTaken: "This reminder is already being sent.",
        remSendNowCancelled: "This reminder was cancelled because its invitation was deleted or cancelled.",
        remSendNowError: "We couldn't work out who this reminder goes to just now. Nothing was sent, and it will be tried again automatically.",
        // Card badges and stats, reminder rows, the Status window, Remind All
        // and the card actions' confirms/toasts (code review 2026-10-07: all
        // English on every dashboard). See cCount for the ...One forms.
        badgeSent: "Sent",
        badgeSending: "Sending",
        badgeProcessing: "Processing",
        badgeScheduled: "Scheduled",
        badgePaused: "Paused",
        badgeCancelled: "Cancelled",
        badgeDraft: "Draft",
        badgeExpired: "Expired",
        badgeUnableToSend: "Unable to Send",
        badgeNoCredits: "Insufficient credits",
        badgeInReview: "In review",
        scheduledFor: "Scheduled for {when}",
        sendingAt: "Sending {when}",
        expiredTip: "WhatsApp didn't approve this message in time. Use Edit & Resend to submit it again.",
        noCreditsTip: "This message was paused because your account ran out of credits. Buy more credits and resend to deliver the remaining messages.",
        inReviewTip: "We review messages from new accounts before they go out — someone is checking yours now. Nothing has been charged, and your message and contacts are saved. It sends automatically once approved; message us in the chat if you need it sooner.",
        toggleRemindersTip: "Show or hide this message's reminders",
        showRemindersAria: "Show reminders ({n})",
        hideRemindersAria: "Hide reminders ({n})",
        expandAllReminders: "Expand All",
        collapseAllReminders: "Collapse All",
        expandAllRemindersTip: "Show the reminders of every message",
        collapseAllRemindersTip: "Hide the reminders of every message",
        remindersOn: "Reminders on",
        statResponded: "{n} responded",
        statRespondedOne: "{n} responded",
        statPending: "{n} pending",
        statPendingOne: "{n} pending",
        statSent: "{n} sent",
        statSentOne: "{n} sent",
        statContacts: "{n} contacts",
        statContactsOne: "{n} contact",
        statNotDelivered: "{n} not delivered",
        statNotDeliveredOne: "{n} not delivered",
        statRecipients: "{n} recipients",
        statRecipientsOne: "{n} recipient",
        allRepliedTip: "Everyone this went to had already replied — that is who it was sent to.",
        waCouldNotAccept: "WhatsApp couldn't accept your message. Edit the message and resend.",
        untitledMessage: "Message",
        editResendTip: "Fix the issue and resend WhatsApp messages",
        timingDayOf: "Day of",
        timing1DayBefore: "1 day before",
        timing3DaysBefore: "3 days before",
        timing1WeekBefore: "1 week before",
        timing2WeeksBefore: "2 weeks before",
        audNoResponse: "No-response guests",
        audAttending: "Attending guests",
        audMaybe: "Maybe guests",
        audDeclined: "Declined guests",
        audAll: "All contacts",
        remTitleAudience: "Reminder · {audience}",
        remTitleIndex: "Reminder {n}",
        remWasSetFor: "Was set for {when}",
        remNoCreditsTip: "This reminder couldn't be sent because you don't have enough credits. Buy more credits and resume to deliver it.",
        remAddCredits: "Add credits to send this reminder",
        remNoRecipients: "No recipients",
        remNoMatching: "No matching recipients",
        remResubmitTip: "Your edited message has gone back to WhatsApp for approval. It sends once approved.",
        remResubmitting: "Re-submitting",
        remSendsOnceApproved: "Sends {when}, once approved",
        remWaitingApproval: "Waiting on WhatsApp approval",
        remAllRepliedTip: "Everyone this reminder went to had already replied — that is who it was sent to.",
        remExpiredTip: "WhatsApp didn't approve this reminder's message in time — edit it and try again.",
        remRejectedTip: "WhatsApp rejected this reminder's message content — edit the reminder and save to resubmit it.",
        remFailedTip: "There was a problem sending this reminder — edit it and try again.",
        remRejected: "Message rejected",
        remCouldntSend: "Couldn't send",
        remTimedOut: "WhatsApp approval timed out",
        remEditToResubmit: "Edit and save to resubmit",
        remSendFailed: "Send failed",
        remWaitingTip: "Waiting for WhatsApp (Meta) to approve this reminder's message — usually a few minutes, occasionally longer. It sends automatically once approved.",
        remSendingSoon: "Sending soon",
        remBeingSentTip: "This reminder is being sent now.",
        remDueOnceApproved: "Due {when} · sends once WhatsApp approves",
        remSendsWhenApproved: "Sends as soon as WhatsApp approves",
        remDue: "Due {when}",
        remSendingNow: "Sending now…",
        remProcessingTip: "This reminder is going out now. Email goes immediately; if it also goes by WhatsApp, that half sends as soon as Meta approves the message — usually a few minutes.",
        remSendingMinute: "Sending now — this usually takes a minute or two",
        remSends: "Sends {when}",
        remPreviewTip: "Preview the reminder message",
        remEditTip: "Edit message, timing, or audience",
        remStatusTip: "See delivery status — who got this reminder and their response",
        remBuyCreditsTip: "Buy credits to send this reminder",
        remResumeCreditsTip: "Resume this reminder once you have credits",
        remResumeTip: "Resume this reminder",
        remPauseTip: "Pause this reminder",
        remDeleteTip: "Delete this reminder",
        msgNotFound: "Message not found",
        remFromMainMessage: "Add reminders from the main message, not another reminder.",
        renamePrompt: "Rename this message:",
        renameEmpty: "Message name cannot be empty",
        renameFailed: "Could not rename message: ",
        cancelConfirm: "Are you sure you want to cancel the message \"{name}\"?\n\nThis action cannot be undone. Anything that hasn't been sent yet will not be sent.",
        cancelDone: "Message cancelled successfully",
        cancelFailed: "Failed to cancel message: ",
        resumeConfirm: "Resume message \"{name}\"?\n\nIt will be sent on {when}.",
        resumeConfirmNoDate: "Resume message \"{name}\"?\n\nIt will be sent on the original schedule.",
        resumeDone: "Message resumed successfully",
        resumeFailed: "Failed to resume message: ",
        pauseConfirm: "Pause message \"{name}\"?\n\nIt will not be sent at the scheduled time. You can resume or cancel it later.",
        pauseDone: "Message paused successfully",
        pauseFailed: "Failed to pause message: ",
        pauseTooLate: "This message finished sending before it could be paused.",
        autoRetryPauseConfirm: "Pause auto-retry for \"{name}\"?",
        autoRetryResumeConfirm: "Resume auto-retry for \"{name}\"?",
        autoRetryNotRetriedOne: "{n} contact will not be retried automatically.",
        autoRetryNotRetriedMany: "{n} contacts will not be retried automatically.",
        autoRetryRetriedOne: "{n} contact will be retried on their next scheduled attempt.",
        autoRetryRetriedMany: "{n} contacts will be retried on their next scheduled attempt.",
        autoRetryPaused: "Auto-retry paused",
        autoRetryResumed: "Auto-retry resumed",
        autoRetryPauseFailed: "Failed to pause auto-retry: ",
        autoRetryResumeFailed: "Failed to resume auto-retry: ",
        sendNowConfirm: "Send message \"{name}\" now?\n\nThe scheduled date has passed. This will send the message immediately.",
        sendNowDone: "Message sent successfully!",
        sendNowFailed: "Failed to send message: ",
        deleteDraftConfirm: "Are you sure you want to delete the draft \"{name}\"?\n\nThis action cannot be undone.",
        deleteDraftFailed: "Failed to delete draft: ",
        deleteConfirmActive: "Permanently delete \"{name}\"?\n\nThis message has delivery stats and may have scheduled reminders. Both will be removed.\n\nGuest responses are kept (they live separately).\n\nThis cannot be undone.",
        deleteAlsoGuests: "This message added {n} guests to your guest list who haven't replied and aren't on any other message.\n\nRemove them from the guest list too?\n\nOK removes them. Cancel keeps them.",
        deleteConfirmUnsent: "Permanently delete \"{name}\"?\n\nNothing was sent on this message, so deleting it is safe.\n\nThis cannot be undone.",
        deleteFailed: "Failed to delete message: ",
        statusAllTitle: "All Contacts",
        statusAllSubtitle: "Across all messages",
        statusAllMessages: "📊 All Messages",
        statusAllTags: "All tags", statusNoTag: "No tag", statusTagsLine: "Tags",
        statusTitleFallback: "Message status",
        statusThisMessageOnly: "Showing contacts from this message only",
        statusAddedAfterLaunch: "{n} added after launch",
        statusAddedAfterLaunchOne: "{n} added after launch",
        statusContactCount: "{n} contacts",
        statusContactCountOne: "{n} contact",
        exportNone: "No contacts to export",
        exportedOne: "Exported {n} contact",
        exportedMany: "Exported {n} contacts",
        dlvSent: "Sent",
        dlvDelivered: "Delivered",
        dlvRead: "Read",
        dlvOpened: "Opened",
        dlvClicked: "Clicked",
        dlvClickedTip: "Their RSVP link (or your website) was opened from one of your messages. Short of a reply, that's the surest sign they have it.",
        dlvReadTip: "WhatsApp's own read receipt (blue ticks), so they have read it.",
        dlvOpenedTip: "The email's pictures loaded, so they probably saw it. Apple Mail and some other apps load pictures automatically when it arrives, so it's a good sign, not proof.",
        dlvDeliveredEmailTip: "Their email provider accepted it. We can't see which folder it landed in, and many apps (Outlook, Hotmail, work email) never report opens, so they may already have read it.",
        dlvDeliveredWaTip: "It reached their phone (WhatsApp double tick). No read receipt yet, but some guests turn read receipts off, so they may have read it.",
        dlvDeliveredSmsTip: "It reached their phone as a text. Texts never report whether they were read.",
        dlvSentTip: "It has been handed to the network. No confirmation that it reached them yet.",
        dlvBounced: "Bounced",
        dlvFailed: "Failed",
        dlvSmsSent: "SMS Sent",
        dlvSmsFailed: "SMS Failed",
        detailNoEmailFallback: "No email on file, so it could not go by email instead. Add one with the pencil to send it.",
        deliveryFailed: "Delivery failed",
        detailEmail: "Email",
        detailSmsFallback: "SMS Fallback",
        detailRsvp: "RSVP",
        noDetail: "No detail available",
        fallbackEmail: "Email fallback",
        fallbackSms: "SMS fallback",
        aMessageFailed: "a message failed",
        aMessageFailedTip: "One of the messages to this contact could not be delivered, though they were reached by another message.",
        checkNumber: "check this number",
        checkNumberTip: "WhatsApp could not deliver to this number as we have it stored, and the number does not look complete — {detail}. It is worth correcting it and resending before assuming the guest is not on WhatsApp.",
        rowDetailsTip: "Click to see full delivery details",
        updateResponseTip: "Update response",
        linkColumn: "LINK",
        remCellOnAria: "Reminders are on — pause",
        remCellOffAria: "Reminders are off — resume",
        guestFallback: "Guest",
        addName: "Add name",
        editNameTip: "Click to edit name",
        nameEditPlaceholder: "Guest name",
        nameEditSave: "Save",
        nameEditCancel: "Cancel",
        nameSaveFailed: "Failed to save name",
        raLoadingAll: "Loading all contacts...",
        raLoadingPending: "Loading pending contacts...",
        raLoadingYes: "Loading Yes responders...",
        raLoadingMaybe: "Loading Maybe responders...",
        raLoadingNo: "Loading No responders...",
        raExcludedAll: "Every contact is excluded from reminders",
        raExcludedPending: "Every pending guest here is excluded from reminders",
        raExcludedYes: "Every Yes guest here is excluded from reminders",
        raExcludedMaybe: "Every Maybe guest here is excluded from reminders",
        raExcludedNo: "Every No guest here is excluded from reminders",
        raSkippedExcludedOne: "Skipped {n} guest you excluded from reminders",
        raSkippedExcludedMany: "Skipped {n} guests you excluded from reminders",
        raLoadFailed: "Could not load contacts",
        raNoneSelected: "No recipients selected — cancelled.",
        raNoValidRecipients: "No valid recipients to send to.",
        // Card status lines, failure reasons and WhatsApp refusals (item M2, 2026-10-07).
        chEmail: "Email",
        chText: "Text",
        chWhatsApp: "WhatsApp",
        stSentN: "{n} sent",
        stAtSendTime: "goes out at the scheduled time",
        stGoingOut: "going out in the next few minutes",
        stWaWaiting: "waiting for WhatsApp to approve the message (usually a few minutes)",
        stWaWaitingLong: "still waiting for WhatsApp to approve the message. This one is taking longer than usual; it goes out by itself once approved",
        stWaSubmitting: "still sending it for approval",
        stRetrying: "Nothing has gone out yet. We'll try again in a few minutes.",
        stSendingSoon: "Going out in the next few minutes.",
        stWaRefused: "not accepted. Edit and resend.",
        stWaRefusedBelow: "not accepted. Edit and resend (the reason is below).",
        failFormMissing: "This message's form no longer exists. Save the form again, then create a new message.",
        failWaOnly: "WhatsApp didn't accept the message, and everyone on it can only be reached on WhatsApp, so nothing was sent. Edit and resend.",
        failWaRefused: "WhatsApp didn't accept the message, so nothing was sent. Edit and resend.",
        failNothingSent: "Nothing could be sent after several tries. Press Edit & Resend to try again. If it happens again, message us in the chat.",
        failNoMethod: "Your guests' details didn't reach our sending step. This was a problem on our side, not your guest list. Press Edit & Resend to try again, and message us in the chat if it happens again.",
        failSendError: "Something went wrong on our side while sending. It was not anything you did. Press Edit & Resend to try again, and message us in the chat if it happens again.",
        failGeneric: "We couldn't send this message. Press Edit & Resend to try again, or message us in the chat.",
        err131053Label: "image couldn't be loaded",
        err131053Tip: "WhatsApp couldn't fetch the invitation image, so the message wasn't delivered. Resending usually works.",
        errSmsBouncedLabel: "text not delivered",
        errSmsBouncedTip: "The phone network didn't deliver the text. The number may be wrong, a landline or switched off. Check the number, email them, or copy their personal link and send it yourself.",
        errSmsFailedLabel: "text not sent",
        errSmsFailedTip: "Our text provider didn't send this message. Resending usually works.",
        errSmsNonGsmLabel: "can't go by text",
        errSmsNonGsmTip: "This message has no English wording, and texts can only carry that. Add an English version with Edit & Resend, or reach this guest by email.",
        errNonUsLabel: "texts go to US numbers only",
        errNonUsTip: "We only send texts to US numbers. Reach this guest by WhatsApp or email.",
        errEmailHardLabel: "email address doesn't work",
        errEmailHardTip: "This email address doesn't exist or refuses mail. Check the spelling, or reach them by text or WhatsApp.",
        errEmailSoftLabel: "email not accepted for now",
        errEmailSoftTip: "The guest's mailbox didn't accept the email this time (often because it is full or briefly unavailable). Resending later usually works.",
        errEmailBounceLabel: "email bounced",
        errEmailBounceTip: "The email couldn't be delivered to this address. Check the spelling, or reach them by text or WhatsApp.",
        errGenericTip: "This message couldn't be delivered. Reach this guest another way, or resend later.",
        fbRetryUntilAll: "WhatsApp is holding back marketing messages to them until {time}. Retry after that.",
        fbRetryUntilSome: "WhatsApp is holding back marketing messages to some of them until {time}. Retry after that.",
        fbChannelSub: "This message can't get through to them the way it was sent. Remind them by email, or copy each guest's personal link from the list below.",
        fbChannelNoEmail: "None of these guests has an email address. Copy each one's personal link from the list below and send it yourself.",
        fbChannelSomeNoEmail: "{n} of these guests have no email address, so the email can't reach them. This list closes when the email opens, so copy their personal links first. Continue with the {m} who have one?",
        ndTipWa: "WhatsApp didn't deliver to {n} of your guests. Meta sometimes holds messages back to protect against spam, or the number isn't on WhatsApp. Open the list to see why, and reach them by email or text.",
        ndTipSms: "{n} of your texts weren't delivered, usually because of a wrong or landline number. Open the list to see why, then check the number, email them, or copy their personal link.",
        ndTipEmail: "{n} of your emails bounced, usually because of a mistyped address. Open the list to see why, and reach them by text or WhatsApp.",
        ndTipMixed: "Not delivered: {parts}. Open the list to see why for each guest.",
        ndTipGeneric: "{n} of your guests didn't get this message. Open the list to see why.",
        ndTipWaOne: "WhatsApp didn't deliver to one of your guests. Meta sometimes holds messages back to protect against spam, or the number isn't on WhatsApp. Open the list to see why, and reach them by email or text.",
        ndTipSmsOne: "One of your texts wasn't delivered, usually because of a wrong or landline number. Open the list to see why, then check the number, email the guest, or copy their personal link.",
        ndTipEmailOne: "One of your emails bounced, usually because of a mistyped address. Open the list to see why, and reach the guest by text or WhatsApp.",
        ndTipGenericOne: "One of your guests didn't get this message. Open the list to see why.",
        tplErr_ours: "Something went wrong on our side, and it was not anything you did. Open Edit & Resend and send it again from there (no changes needed). If it happens a second time, tell us and we will fix it.",
        tplErr_trailingParam: "Your message can't END with a personalization tag like {{name}} or {{table_number}}. WhatsApp doesn't allow it. Put a word or a full stop after the tag and resend.",
        tplErr_waLink: "WhatsApp doesn't allow a button that links back to WhatsApp. Point that button somewhere else and resend.",
        tplErr_imageFormat: "Your invitation image needs to be a JPG or PNG for WhatsApp. Re-upload it in that format and resend.",
        tplErr_imageProcess: "WhatsApp couldn't process your invitation image. Try a different one (JPG or PNG, under 5MB) and resend.",
        tplErr_category: "WhatsApp reads your message as a different kind of message than we filed it under. Resend and it should go through.",
        tplErr_layout: "WhatsApp couldn't read the layout of your message. It is usually a {{tag}} sitting alone on its own line. Put it inside a sentence and resend.",
        tplErr_duplicateName: "WhatsApp already has a message saved under this name. Rename the message and resend.",
        tplErr_rateLimit: "You have reached WhatsApp's limit on new messages for now. Wait a little and resend. Nothing is lost.",
        tplErr_noReason: "WhatsApp didn't approve the wording of this message. Edit it and resubmit.",
        tplLangsHeadOne: "WhatsApp refused the {langs} version of your message.",
        tplLangsHeadMany: "WhatsApp refused these versions of your message: {langs}.",
        tplLangsHeadUnknown: "WhatsApp refused one of the languages of your message.",
        tplLangsSaid: "WhatsApp said: \"{text}\".",
        tplLangsEditOne: "Edit that translation and resubmit.",
        tplLangsEditMany: "Edit those translations and resubmit.",
        langCombined: "combined two-language",
        failWaRefusedPartial: "WhatsApp didn't accept the message, so your WhatsApp guests didn't get it. Your email and text guests already have it, so don't send it to them again. Edit the wording and resend it to your WhatsApp guests.",
        fbPermanentSubEmail: "The number or email address doesn't work. Correct it in the guest list, or copy the guest's personal link and send it yourself.",
        fbHeldBack: "{n} weren't sent on purpose",
        fbHeldBackSub: "We held these back: the guest asked us to stop, unsubscribed, isn't on WhatsApp, or WhatsApp paused the number. Each row below says which. Guests who asked us to stop are never messaged again.",
        tplLangsHeadCombined: "WhatsApp refused the combined two-language version of your message.",
        remFailFormMissing: "This reminder's form no longer exists. Save the form again, then edit the reminder with the pencil and save it.",
        remFailWaOnly: "WhatsApp didn't accept the reminder, and everyone on it can only be reached on WhatsApp, so nothing was sent. Edit the reminder with the pencil and save it to resubmit.",
        remFailWaRefused: "WhatsApp didn't accept the reminder, so nothing was sent. Edit the reminder with the pencil and save it to resubmit.",
        remFailWaRefusedPartial: "WhatsApp didn't accept the reminder, so your WhatsApp guests didn't get it. Your email and text guests already have it. Edit the reminder with the pencil and save it to resubmit.",
        remFailNothingSent: "Nothing could be sent after several tries. Edit the reminder with the pencil and save it to try again. If it happens again, message us in the chat.",
        remFailNoMethod: "Your guests' details didn't reach our sending step. This was a problem on our side, not your guest list. Edit the reminder with the pencil and save it to try again, and message us in the chat if it happens again.",
        remFailSendError: "Something went wrong on our side while sending. It was not anything you did. Edit the reminder with the pencil and save it to try again, and message us in the chat if it happens again.",
        remFailGeneric: "We couldn't send this reminder. Edit it with the pencil and save it to try again, or message us in the chat.",
        // A provider outage (or the SMS credit gate) paused this message: see _providerPauseBadgeHtml.
        providerPaused: "Sending stopped",
        providerPausedTip: "A problem with our messaging provider stopped this message before everyone got it. You weren't charged for guests who haven't received it. Our team has been alerted, and it will go out to them once the problem is fixed. There's nothing you need to do.",
        providerPausedPhrase: "Waiting on our messaging provider",
        providerResumeConfirm: "Try sending this message again now? Only guests who haven't received it yet will get it.",
        providerResumed: "Restarting. Guests who haven't received it yet will get it in the next few minutes.",
        providerResumeFailed: "We couldn't restart this message. Please try again.",
        providerPausedPhraseAuto: "We'll try again automatically",
        providerPausedTipAuto: "A temporary problem with our messaging provider stopped this message before everyone got it. You weren't charged for guests who haven't received it. We'll try again automatically in a little while, and only they will get it. You can also try now with {resume}.",
        providerResuming: "Restarting. This usually takes a few minutes.",
        providerResumedTeam: "We'll try again now. If our provider still isn't working, it will stop again and go out once our team has fixed it. There's nothing else you need to do.",
        sendRestartResumes: "The server restarted while this message was sending. Guests who already have it won’t get it twice, and the rest will get it automatically within a few minutes.",
        sendRestartRetry: "The server restarted while this message was sending. Guests who already have it won’t get it twice. Send it again in a minute to reach the rest.",
        // Guests a provider outage held back because their WhatsApp/SMS timed out unanswered: see _outageHeldNoteHtml.
        heldPhrase: "{n} guests may already have it",
        heldPhraseOne: "1 guest may already have it",
        heldWhy: "While our messaging provider was down, their WhatsApp or text got no answer, so it may have arrived anyway. So that nobody gets it twice, we haven't sent it to them again. Everyone else gets it as usual.",
        heldMore: "+{n} more",
        heldSend: "Send to these {n} too",
        heldSendOne: "Send to this guest too",
        heldSendDone: "Done. We've sent it to them.",
        heldSendWithResume: "Done. They'll get it with everyone else when sending restarts.",
        heldSendBusy: "This message is sending right now. Try again when it finishes.",
        heldSendPaused: "Resume this message first, then send to them.",
        heldSendFailed: "We couldn't send to them right now. Please try again in a few minutes.",
        // An Add Contacts a deploy cut off (PMb): see WORDED_ERRORS SEND_INTERRUPTED and _additionShortfallText.
        errInterruptedLabel: "not sent (interrupted)",
        errInterruptedTip: "Sending stopped before this guest was reached because our server restarted. Nothing was sent to them and nothing was charged; resending is safe and is charged like any send.",
        acNotSent: "{n} not sent: sending was interrupted. Resend to them from Status",
        addRestartRetry: "Our server was restarting, so nothing was added or sent. Please try again in a minute.",
        // Retry, resend explainer, ghost-template recovery, follow-up, CSV export.
        retryWhichMessage: "We can't tell which message these failed on. Pick a message at the top of this window and try again.",
        retryAMessage: "a message",
        retryScopeOne: "We'll resend \"{n}\" to them.",
        retryScopeMany: "We'll resend each of the {n} messages they failed on, to those guests only.",
        retryConfirmOne: "Send the same message again to 1 guest?",
        retryConfirmMany: "Send the same message again to {n} guests?",
        retryConfirmNobody: "Nobody who already received it is contacted again.",
        retryConfirmFree: "Resending is free: the credit for each failed message was already returned.",
        retryResending: "Resending…",
        nothingTryLater: "Try again in a few hours.",
        nothingTryAfter: "Try again after {n}.",
        nothingCooledOne: "WhatsApp turned this message down for 1 guest in the last 24 hours, so they were left out rather than pushed at again.",
        nothingCooledMany: "WhatsApp turned this message down for {n} guests in the last 24 hours, so they were left out rather than pushed at again.",
        nothingDeadOne: "1 guest is not on WhatsApp — a resend cannot reach them. Try email or SMS instead.",
        nothingDeadMany: "{n} guests are not on WhatsApp — a resend cannot reach them. Try email or SMS instead.",
        nothingReachedOne: "1 guest already has this message — it reached them another way — so it was not sent again.",
        nothingReachedMany: "{n} guests already have this message — it reached them another way — so it was not sent again.",
        nothingNobody: "Nobody could be reached this time. It's worth trying again later.",
        ghostConfirm: "Rebuild this message's WhatsApp template and re-send to the guests who weren't reached?\n\nThis can take a few minutes. No extra credits are charged.",
        ghostStarting: "Starting…",
        ghostRecovering: "Recovering…",
        ghostButton: "Recreate & resend",
        ghostStarted: "Rebuilding the WhatsApp template and re-sending — this takes a few minutes. We'll email you when it's done.",
        ghostAlready: "A recovery is already in progress.",
        ghostNotNeeded: "This message no longer shows the WhatsApp template problem, so there is nothing to rebuild.",
        ghostStartFailed: "Could not start recovery. Please try again in a few minutes.",
        ghostFixed: "WhatsApp template fixed.",
        ghostResent: "Re-sent.",
        ghostResentToOne: "Re-sent to 1 guest.",
        ghostResentTo: "Re-sent to {n} guests.",
        ghostEscalated: "The rebuilt template still failed WhatsApp's check — our team has been notified. Please contact support.",
        ghostIssue: "WhatsApp template issue.",
        ghostIssueBody: "{n} messages couldn't be delivered because WhatsApp rejected the template. A normal retry won't help — rebuild the template and re-send.",
        followUpLoadedOne: "Loaded 1 contact for follow-up",
        followUpLoaded: "Loaded {n} contacts for follow-up",
        csvPhone: "Phone",
        csvEmail: "Email",
        csvDeliveryStatus: "Delivery Status",
        csvDeliveryIssue: "Delivery Issue",
        csvRsvp: "RSVP Response",
        csvGuests: "Guest Count",
        csvPending: "Pending",
        csvNotSent: "Not sent",
        csvAttending: "Attending",
        csvDeclined: "Declined",
        csvMaybe: "Maybe",
        csvResponded: "Responded",
        csvNoResponse: "No response yet",
    },
    es: {
        remWaOnlyWaitingTip: "Tus invitados por correo y por SMS ya tienen este recordatorio. Tus invitados de WhatsApp lo recibirán automáticamente en cuanto WhatsApp (Meta) apruebe el mensaje.",
        remWaOnlyWaiting: "Los invitados de WhatsApp lo recibirán cuando se apruebe",
        quietHoldSystemTip: "Este recordatorio tocaba de noche para algunos de tus invitados, así que espera a que sea de día para todos. No tienes que hacer nada.",
        quietHoldLateTip: "WhatsApp aprobó tu mensaje cuando era de noche para algunos de tus invitados. Para no despertarlos, saldrá cuando sea de día para todos. No tienes que hacer nada.",
        remWaLost: "WhatsApp no enviado",
        remWaLostTip: "WhatsApp no aceptó el mensaje de este recordatorio, así que tus invitados de WhatsApp no lo han recibido. Tus invitados por correo y por SMS ya lo tienen. Edita el recordatorio y guárdalo para enviarlo a tus invitados de WhatsApp.",
        remWaLostTimeoutTip: "WhatsApp no aprobó el mensaje de este recordatorio en las 48 horas siguientes a la hora de envío, así que tus invitados de WhatsApp no lo han recibido. Tus invitados por correo y por SMS ya lo tienen. Edita el recordatorio y guárdalo para volver a enviarlo; no hace falta cambiar nada.",
        remWaLostPhrase: "Edita y guarda para enviarlo a los invitados de WhatsApp",
        waTimeoutBody: "WhatsApp no aprobó este mensaje en las 48 horas siguientes a la hora de envío, así que tus invitados de WhatsApp no lo han recibido.",
        waOthersHaveIt: "Tus invitados por correo y por SMS ya lo tienen. «{editResend}» lo envía a tus invitados de WhatsApp.",
        waRejectedBadgeTip: "WhatsApp no aceptó este mensaje. Edita el mensaje y vuelve a enviarlo a WhatsApp para su aprobación.",
        previewTitle: "Vista previa del mensaje",
        previewNoBody: "(no hay texto del mensaje guardado)",
        previewNoteOne: "En WhatsApp esto aparece como el botón de arriba (haz clic para probar el enlace).",
        previewNoteMany: "En WhatsApp y en el correo aparecen como botones (haz clic en cada uno para probar el enlace).",
        previewSmsNote: "Por SMS el enlace llega como texto:",
        previewSampleFor: "Ejemplo mostrado para {name}: las variables como {{first_name}} se completan para cada invitado.",
        previewClone: "Duplicar como borrador nuevo",
        close: "Cerrar",
        addContacts: "Añadir contactos",
        preview: "Vista previa", status: "Estado", remind: "Recordar", add: "Añadir", reminders: "Recordatorios", remindersNoneTip: "Aún no hay recordatorios — envía uno ahora",
        duplicate: "Duplicar", delete: "Eliminar", edit: "Editar", editResend: "Editar y reenviar",
        personalLinks: "Enlaces personales", sharedByHand: "Enlace personal", copyPersonalLink: "Copiar enlace personal", linkCopied: "Enlace personal copiado",
        sendOnWhatsApp: "Enviar por WhatsApp desde tu teléfono", waWalkBtn: "Enviar desde mi WhatsApp", waWalkIntro: "Nuestro WhatsApp no pudo llegar a estos invitados. Envía el mensaje a cada uno desde tu propio WhatsApp: ábrelo, pulsa enviar y vuelve para el siguiente.", waWalkCounter: "Invitado {i} de {n}", waWalkOpen: "Abrir WhatsApp", waWalkSkip: "Omitir", waWalkClose: "Cerrar", waWalkDone: "Listo. Abriste WhatsApp para {k} de {n} invitados.",
        hostSentBadge: "Enviado desde tu WhatsApp", hostSentTip: "Abriste tu propio WhatsApp para enviar este mensaje. No podemos ver dentro de WhatsApp, así que no está confirmado como entregado.", waWalkCardBtn: "Enviar desde mi WhatsApp ({n})", waWalkCardTip: "Nuestro WhatsApp no pudo llegar a estos invitados. Envíales este mensaje desde tu propio WhatsApp, uno por uno.",
        waWalkImageNote: "WhatsApp se abre solo con el texto. Para incluir la imagen de tu invitación, adjúntala en WhatsApp antes de pulsar enviar.", waWalkImageOpen: "Abrir la imagen para guardarla",
        addedSkippedOptedOut: "{n} invitado(s) agregado(s) sin enviar: tienen los recordatorios desactivados", addedSkippedResponded: "{n} invitado(s) agregado(s) sin enviar: ya habían respondido",
        tagContacts: "Etiquetar a todos en este mensaje", tagContactsPrompt: "Etiquetas para añadir a todos los invitados de este mensaje, separadas por comas (p. ej. familia, solo recepción):", tagContactsDone: "{n} invitados etiquetados", tagContactsFailed: "No se pudo etiquetar a los invitados",
        continueBtn: "Continuar", pause: "Pausar", cancel: "Cancelar", sendNow: "Enviar ahora",
        sendTest: "Enviar prueba",
        sendTestTip: "Envíate una copia de este mensaje para ver exactamente lo que recibirán tus invitados",
        sendTestSending: "Enviando tu prueba…",
        sendTestDone: "Prueba enviada",
        sendTestEmailCopyOnly: "El correo es una copia solo para ti — tus invitados no recibirán correo.",
        sendTestNoMessage: "Añade un mensaje y elige cómo enviarlo; después podrás enviarte una prueba",
        sendTestFailed: "No se pudo enviar la prueba",
        sendTestCreditReturned: "Te hemos devuelto a tu saldo el crédito de esta prueba.",
        sendTestCost: "La primera prueba de este mensaje es gratis. Después, cada prueba por SMS o WhatsApp usa 1 crédito; las pruebas por correo siempre son gratis.",
        sendTestAgain: "Enviar otra prueba",
        sendTestAgainTip: "Envíate otra copia — útil después de cambiar el texto",
        sendTestPhoneTitle: "¿A dónde enviamos la prueba?",
        sendTestPhoneHint: "Tu cuenta no tiene número de móvil, así que escribe uno para recibir la prueba. Lo recordaremos.",
        sendTestPhoneInvalid: "Eso no parece un número de móvil. Usa el formato internacional, por ejemplo +14155550123.",
        resume: "Reanudar", buyCredits: "Comprar créditos", retry: "Reintentar",
        previewTip: "Vista previa del texto del mensaje",
        statusTip: "Ver quién recibió el mensaje y su respuesta",
        remindTip: "Enviar un seguimiento a los invitados de este mensaje",
        addTip: "Añadir más contactos a este mensaje",
        addScheduledTip: "Añade más contactos: recibirán el mensaje a la hora de envío programada",
        addDisabledTip: "No se pueden añadir contactos: corrige primero este mensaje con Editar y reenviar (la plantilla de WhatsApp nunca llegó a Meta).",
        duplicateTip: "Reutiliza este mensaje (imagen, texto, idiomas, enlace) en un mensaje nuevo",
        deleteTip: "Eliminar permanentemente este mensaje de tu historial",
        pauseTip: "Pausar este mensaje",
        buyCreditsTip: "Recarga tus créditos de mensajes y luego reanuda este mensaje",
        retryTip: "Intentar enviar de nuevo (funciona cuando tengas créditos suficientes)",
        manageRemindersTip: "Gestionar recordatorios programados",
        scheduleRemindersTip: "Programar recordatorios",
        resumeAutoRetryTip: "Reanudar reintento automático ({n} pendientes)",
        pauseAutoRetryTip: "Pausar reintento automático ({n} pendientes)",
        remindPending: "Invitados pendientes", remindPendingSub: "Aún no han respondido",
        remindMaybe: "Respondieron quizás", remindMaybeSub: "Dijeron quizás: anímalos a confirmar",
        remindYes: "Respondieron sí", remindYesSub: "Recordatorio del día, actualización del lugar, etc.",
        remindNo: "Respondieron no", remindNoSub: "No pueden asistir: envía un enlace de transmisión en vivo, un agradecimiento, etc.",
        remindRemovedOne: "El invitado que quitaste no recibirá este recordatorio.", remindRemovedMany: "Los {n} invitados que quitaste no recibirán este recordatorio.",
        // Response-edit (pencil) window: _openResponseEditModal and what it opens.
        reTitle: "Actualizar la respuesta de {name}",
        reThisGuest: "este invitado",
        reContact: "contacto",
        reManualEntry: "Entrada manual:",
        reManualEntryNote: "Creada aquí porque no había respuesta en el formulario.",
        reRemove: "Eliminar",
        reCurrentlyPaired: "Vinculada ahora:",
        reUnpair: "Desvincular",
        reEditDetails: "Editar datos de contacto",
        rePhone: "Teléfono",
        reEmail: "Email",
        reInvitationHelp: "Escribe a todos, separados por {sep} (p. ej. {example}). Los cambios se aplican a su enlace al instante.",
        reChildMarker: "(niño)",
        reExample: "Miguel López; Emilia (niño)",
        reMaxGuests: "Máx. invitados",
        reMembersPh: "p. ej. Miguel López; Emilia (niño)",
        // Task #935: the guest-list columns (Add contacts, the contact window).
        acGuestNames: "Nombre(s) de invitados",
        acGuestNamesHint: "Cómo añadir nombres de invitados\n• Separa los nombres con punto y coma (;). Las comas también funcionan.\n• Añade (niño) después del nombre de un niño, p. ej. Lucas López (niño).\n• Ejemplo: Sara López; Miguel López; Lucas López (niño)\n• El teléfono y el correo de la fila son de la primera persona de la lista.",
        acGroupName: "Nombre del grupo",
        acGroupNameHint: "A quién va dirigida la invitación, p. ej. Familia López: en el sobre y en la bienvenida del formulario; los mensajes saludan a los nombres de los invitados. Sin nombres de invitados, la encuentran con este nombre. Déjalo en blanco para un invitado que viene solo.",
        acCsvColumns: "Columnas del CSV: Nombre(s) de invitados, Nombre del grupo, Teléfono, Correo · opcionales: Máx. invitados, Etiquetas",
        acSameName: "Atención: tienes varios invitados o grupos con el mismo nombre. Añade un correo electrónico o un teléfono a cada uno para que puedan confirmar su identidad al buscar su invitación.",
        acLastFirst: "¿Una persona con el apellido primero? Separa a personas distintas con \";\".",
        reGroupName: "Nombre del grupo",
        reGroupNameHint: "A quién va dirigida: el sobre y el saludo. Sin nadie en la lista, la encuentran por este nombre.",
        reMaxGuestsHint: "Déjalo en blanco para calcular el grupo según los nombres (o el límite del formulario).",
        reGroupNamePh: "p. ej. Familia López",
        reGuestNames: "Nombre(s) de invitados",
        reTags: "Etiquetas",
        reTagsPh: "p. ej. familia, solo recepción",
        reTagsHelp: "Las etiquetas deciden qué partes del evento muestra el enlace de este invitado, cuando una parte está limitada a ciertas etiquetas.",
        reLoading: "Cargando…",
        reSave: "Guardar",
        rePairHeading: "Vincular con una respuesta existente",
        rePairHelp: "Si {name} respondió en el formulario pero la respuesta no se vinculó sola (por ejemplo, enlace compartido u otro teléfono), vincúlala aquí.",
        reLooking: "Buscando respuestas que coincidan…",
        reManualHeading: "O registrar manualmente",
        reManualHelp: "Úsalo solo si {name} respondió fuera del formulario (mensaje, llamada, en persona).",
        reAttending: "Asistirá",
        reDeclined: "No asistirá",
        reMaybe: "Quizás",
        reResponded: "Respondió",
        rePending: "Pendiente",
        reSaving: "Guardando…",
        reSaved: "Guardado ✓",
        reCouldNotSave: "No se pudo guardar",
        reNotOnMessage: "Este invitado ya no está en el mensaje, así que su invitación no se puede editar aquí.",
        rePartyMany: "Su invitación es para {n} personas.",
        rePartyOne: "Su invitación es para una persona.",
        reLoadFailed: "No se pudo cargar la invitación.",
        reMaxInvalid: "Máx. invitados debe ser un número entero entre 1 y 50.",
        reBrowseAll: "Ver las {n} respuestas sin vincular",
        reBrowseOne: "Ver la respuesta sin vincular",
        reHideList: "Ocultar la lista",
        reCandidatesFailed: "No se pudieron cargar las posibles coincidencias.",
        reManualTag: "Manual",
        reManualTagTip: "Creada como entrada manual en otro contacto",
        reNoName: "(sin nombre)",
        rePair: "Vincular",
        reLikely: "Coincidencias probables",
        reNoUnpaired: "No hay respuestas sin vincular en este formulario. Si el invitado respondió fuera del formulario, usa los botones de \"O registrar manualmente\".",
        reNoLikely: "No hay coincidencias probables por nombre, teléfono o email. Revisa la lista completa si sabes cuál es su respuesta.",
        reSearchPh: "Buscar por nombre, teléfono o email…",
        rePaired: "Respuesta vinculada a {name}",
        reUnpaired: "{name} desvinculado",
        reRecorded: "{status} registrado para {name}",
        reManualRemoved: "Entrada manual eliminada",
        reFailedPair: "No se pudo vincular la respuesta",
        reFailedUnpair: "No se pudo desvincular",
        reFailedRecord: "No se pudo registrar la respuesta",
        reFailedRemove: "No se pudo eliminar",
        reUpdatedNumber: "Número actualizado.",
        reUpdatedEmail: "Email actualizado.",
        reUpdatedDetails: "Datos de contacto actualizados.",
        reResendAsk: "¿Enviar este mensaje de nuevo a {dest}?",
        reMultiMessage: "Este contacto aparece en más de un mensaje, así que usa {addContacts} en el mensaje que quieras reenviar.",
        reResend: "Reenviar",
        reSending: "Enviando…",
        reSentNumber: "Enviado a su nuevo número; aparecerá en el historial de este contacto en breve.",
        reQueued: "En cola: se enviará solo cuando WhatsApp apruebe este mensaje, normalmente en pocos minutos.",
        reSentEmail: "Enviado a su nuevo email; aparecerá en el historial de este contacto en breve.",
        reSentBoth: "Enviado a su nuevo número y email; aparecerá en el historial de este contacto en breve.",
        reResendFailed: "No se pudo reenviar. Inténtalo de nuevo.",
        reminderDupSkipped: "Ya recibieron un recordatorio de otro mensaje: {n}",
        reminderDupSkippedBy: "Ya recibieron el recordatorio de «{name}»: {n}",
        reminderDupSkippedTip: "Estos invitados ya recibieron el mismo recordatorio desde otro mensaje de este evento en las últimas 20 horas, así que este no se les envió para no repetirlo.",
        reminderDupAll: "Todos ya habían recibido un recordatorio de otro mensaje",
        remAttentionOne: "1 recordatorio necesita tu atención",
        remAttentionMany: "{n} recordatorios necesitan tu atención",
        remFailExpiredNote: "WhatsApp no aprobó a tiempo el mensaje de este recordatorio. Edítalo y guárdalo para intentarlo de nuevo.",
        remFailRejectedNote: "WhatsApp no aceptó el mensaje de este recordatorio. Edita el mensaje y guárdalo para volver a enviarlo a revisión.",
        remAudienceBtn: "Ver a quién se enviará este recordatorio",
        remAudienceTitle: "A quién se envía este recordatorio",
        msgAudienceTitle: "A quién se envía este mensaje",
        msgAudienceOne: "Este mensaje se envía a este invitado.",
        msgAudienceMany: "Este mensaje se envía a estos {n} invitados.",
        remAudienceNowOne: "Si se enviara ahora, le llegaría a 1 invitado. La lista se cierra en el momento del envío, así que todavía puede cambiar.",
        remAudienceNowMany: "Si se enviara ahora, les llegaría a {n} invitados. La lista se cierra en el momento del envío, así que todavía puede cambiar.",
        remAudienceFixedOne: "Este recordatorio se envía a este invitado.",
        remAudienceFixedMany: "Este recordatorio se envía a estos {n} invitados.",
        remAudienceEmpty: "Ahora mismo nadie coincide. La lista se vuelve a comprobar cuando se envía el recordatorio.",
        remAudienceFailed: "No pudimos cargar a quién se envía este recordatorio. Inténtalo de nuevo.",
        remAudienceFixedNone: "No hay nadie en la lista de este recordatorio, así que no le llegará a nadie.",
        remAudienceMeantOne: "Este recordatorio debía enviarse a este invitado. No saldrá hasta que lo edites y lo guardes.",
        remAudienceMeantMany: "Este recordatorio debía enviarse a estos {n} invitados. No saldrá hasta que lo edites y lo guardes.",
        remFailWaOnlyRefusedNote: "WhatsApp no aceptó el mensaje de este recordatorio y sus invitados solo se pueden contactar por WhatsApp, así que no se envió nada. Una causa habitual es una etiqueta de personalización justo al final del mensaje: añade texto después y guárdalo para volver a enviarlo a revisión.",
        acSentOf: "{sent} de {total} enviados",
        acSkippedUntil: "{n} en pausa por WhatsApp hasta {time}; vuelve a intentarlo entonces",
        acSkipped: "{n} omitidos",
        acUnreachable: "{n} sin enviar (datos de contacto)",
        acFailed: "{n} sin entregar",
        acDeferred: "{n} saldrán en la próxima ventana de envío",
        acLineOne: "1 invitado añadido después del envío",
        acLineMany: "{n} invitados añadidos después del envío",
        acLineLast: "último: {n}",
        acLineAwaiting: "{n} esperando la aprobación de WhatsApp",
        acTipAdded: "{when}: {n} añadidos",
        reResendCooldown: "WhatsApp está pausando los mensajes a este número hasta {time}. No se ha cobrado nada. Vuelve a intentarlo después, o envíale tú el enlace personal.",
        reResendCooldownNoTime: "WhatsApp está pausando los mensajes a este número por ahora. No se ha cobrado nada. Vuelve a intentarlo más tarde, o envíale tú el enlace personal.",
        reResendNotSent: "No se pudo enviar el mensaje a esos datos. No se ha cobrado nada. Revisa el número o la dirección e inténtalo de nuevo.",
        // Every guest skipped (Meta 24h cooldown, not on WhatsApp...): see _skipSummary and _mergeSendTimeFailures.
        skipNotSent: "No enviado",
        skipNotSentTip: "No salió nada: se retuvo a todos los invitados de este mensaje. La nota de abajo explica por qué y qué puedes hacer.",
        skippedOne: "1 omitido",
        skippedMany: "{n} omitidos",
        skippedTip: "Retenidos a propósito, no se enviaron. Haz clic para ver quiénes y por qué.",
        skipCoolOne: "WhatsApp rechazó un mensaje reciente a 1 invitado, así que pausamos los mensajes a esa persona hasta {time}.",
        skipCoolMany: "WhatsApp rechazó mensajes recientes a {n} invitados, así que hemos pausado los mensajes a estas personas. Con el primero se podrá volver a intentar después de {time}.",
        skipCoolLiftedOne: "WhatsApp rechazó un mensaje reciente a 1 invitado. Nuestra pausa de mensajes a esa persona ya terminó.",
        skipCoolLiftedMany: "WhatsApp rechazó mensajes recientes a {n} invitados. Nuestra pausa ya terminó para el primero de ellos.",
        skipCoolWhy: "WhatsApp rechaza los mensajes de marketing a quien ha recibido muchos últimamente, y no dice por cuánto tiempo. Tras un rechazo pausamos nuestros mensajes a ese número en lugar de volver a enviar. La pausa abarca todos los eventos de QuikRSVP, así que el mensaje rechazado puede no haber sido tuyo. Un mensaje retenido no se cobra. Un nuevo intento después de la pausa también puede ser rechazado.",
        skipNoWaOne: "1 invitado no está en WhatsApp. Contáctalo por SMS o email, o envíale tú el enlace personal.",
        skipNoWaMany: "{n} invitados no están en WhatsApp. Contáctalos por SMS o email, o envíales tú los enlaces personales.",
        skipUnreachOne: "1 invitado no pudo recibirlo: hay que corregir su teléfono o email. Abre la lista para ver quién y qué corregir.",
        skipUnreachMany: "{n} invitados no pudieron recibirlo: hay que corregir su teléfono o email. Abre la lista para ver quiénes y qué corregir.",
        skipOptOutOne: "1 invitado respondió STOP a nuestros mensajes de WhatsApp, así que no le escribimos por ahí.",
        skipOptOutMany: "{n} invitados respondieron STOP a nuestros mensajes de WhatsApp, así que no les escribimos por ahí.",
        skipUnsubOne: "1 invitado se dio de baja de tus emails. Contáctalo por WhatsApp o SMS.",
        skipUnsubMany: "{n} invitados se dieron de baja de tus emails. Contáctalos por WhatsApp o SMS.",
        skipDoRetryAt: "Vuelve a intentarlo después de {time}, o abre Enlaces personales y envía tú el enlace.",
        skipDoRetryNow: "Vuelve a intentarlo ahora, o abre Enlaces personales y envía tú el enlace.",
        skipDoLink: "Abre Enlaces personales y envía tú el enlace.",
        skipHandledRepliedOne: "No lo enviamos nosotros, pero este invitado ya ha respondido.",
        skipHandledRepliedMany: "No lo enviamos nosotros, pero todos sus invitados ya han respondido.",
        skipHandledLinkOne: "No lo enviamos nosotros, pero este invitado ya ha abierto su enlace personal.",
        skipHandledLinkMany: "No lo enviamos nosotros, pero todos sus invitados ya han abierto su enlace personal o han respondido.",
        skipStillBlockedOne: "Seguimos pausando los mensajes a este invitado hasta {time}. Vuelve a intentarlo después, o envíale tú el enlace personal.",
        skipStillBlockedMany: "Seguimos pausando los mensajes a estos invitados. Con el primero se podrá volver a intentar después de {time}; mientras tanto, envíales tú los enlaces personales.",
        skipRowCool: "WhatsApp rechazó un mensaje reciente a este número (puede que fuera de otro evento), así que pausamos todos los mensajes a ese número hasta {time}. Vuelve a intentarlo después, o envíale tú el enlace personal.",
        skipRowCoolLifted: "WhatsApp rechazó un mensaje reciente a este número (puede que fuera de otro evento). Nuestra pausa ya terminó, así que puedes volver a intentarlo o enviarle tú el enlace personal.",
        skipRowNoWa: "Este número no está en WhatsApp. Contáctalo por SMS o email, o envíale tú el enlace personal.",
        skipRowNoWaNoEmail: "Este número no está en WhatsApp. Contáctalo por SMS, o envíale tú el enlace personal.",
        fbTipFailedEmail: "WhatsApp no pudo entregarlo, así que se envió por email.",
        fbTipFailedSms: "WhatsApp no pudo entregarlo, así que se envió por SMS.",
        fbTipNoWa: "Este número no está en WhatsApp, así que le enviamos un email.",
        fbTipCool: "Los mensajes de WhatsApp a este número están en pausa por ahora, así que le enviamos un email.",
        fbStateOpened: "Ya lo ha abierto.",
        fbStateDelivered: "Se entregó.",
        fbStateOnWay: "Está en camino.",
        skipRowOptOut: "Este invitado respondió STOP a nuestros mensajes de WhatsApp, así que no le escribimos por ahí.",
        skipRowOptOutSms: "Este invitado respondió STOP a nuestros SMS, así que no volvemos a enviarle SMS.",
        skipRowUnsub: "Este invitado se dio de baja de tus emails. Contáctalo por WhatsApp o SMS.",
        skipRowOther: "Omitido: este número no puede recibir mensajes de WhatsApp ahora mismo.",
        skipRowNoDetails: "No enviado: no tiene teléfono ni email. Agrega uno y reenvía, o envíale tú el enlace personal.",
        skipRowBadNumber: "No enviado: este número no se puede marcar. Revisa los dígitos y el código de país, y reenvía.",
        skipRowNoEmail: "No enviado: está configurado para email, pero no tiene dirección. Agrega una, o envíalo por WhatsApp.",
        skipRowNoPhone: "No enviado: está configurado para SMS, pero no tiene teléfono. Agrega uno y reenvía.",
        skipRowShared: "No enviado: tiene el mismo número que {name}, y cada número recibe un solo mensaje. Dale su propio número o email, o agrégalo a la invitación de {name}.",
        skipRowSharedNoName: "No enviado: otro invitado tiene el mismo número, y cada número recibe un solo mensaje. Dale su propio número o email.",
        skipRowNoChannel: "No enviado: no se indicó cómo enviarle. Edita sus datos y reenvía.",
        skipBadge: "Omitido",
        unsubBadge: "Dado de baja",
        skipRetrySent: "Enviado. La tarjeta se actualizará en un momento.",
        skipRetryFailed: "WhatsApp no lo aceptó esta vez. Abre Estado para ver por qué.",
        skipRetryStill: "No salió nada: la pausa de mensajes a estos invitados sigue activa. Vuelve a intentarlo más tarde, o envía tú el enlace personal.",
        skipRetryHeld: "Retenido para una revisión rápida antes de enviarse. Saldrá en cuanto se apruebe.",
        skipRetryPaused: "No enviado: está en pausa. La tarjeta muestra por qué.",
        skipRetryNoCredits: "En pausa: añade créditos para enviarlo.",
        skipRetryRequested: "Reintento solicitado. La tarjeta muestra en qué punto está.",
        skipRetryError: "No se pudo reintentar ahora. Vuelve a probar en un minuto.",
        retryBusy: "Este mensaje aún se está enviando. Cuando termine, comprueba quién falta por recibirlo y vuelve a intentarlo.",
        statusLoadFailed: "No hemos podido cargar la lista de invitados en este momento.",
        statusLoadExpired: "Tu sesión ha caducado. Recarga la página para ver la lista de invitados.",
        statusLoadRetry: "Reintentar",
        statusLoadReload: "Recargar la página",
        statusRefreshFailed: "No se pudo actualizar la lista de invitados. Recarga la página para ver el estado más reciente.",
        retrySentOne: "Mensaje reenviado a 1 invitado.",
        retrySentMany: "Mensaje reenviado a {n} invitados.",
        retryStillFailedOne: "1 invitado sigue sin recibirlo.",
        retryStillFailedMany: "{n} invitados siguen sin recibirlo.",
        retryStaleView: "Un reenvío de este mensaje terminó después de cargar esta lista, así que esta vez no se envió nada. La lista se ha actualizado: comprueba quién falta por recibirlo y vuelve a intentarlo.",
        acSmsConsentWhy: "Los invitados con números de EE. UU. reciben este mensaje por SMS, y otros números +1 o de Brasil pueden recibirlo así si falla WhatsApp.",
        acSmsConsent: "Confirmo que todos los destinatarios han dado su consentimiento para recibir mensajes SMS de invitación a mi evento a través de QuikRSVP.",
        acSmsDisclosure: "Los destinatarios pueden responder STOP para cancelar o HELP para asistencia. Pueden aplicar tarifas de datos y mensajes. La frecuencia varía.",
        waRejectedTitle: "WhatsApp no aceptó este mensaje",
        waOursTitle: "No se pudieron enviar los mensajes de WhatsApp",
        waTimeoutTitle: "WhatsApp no aprobó este mensaje a tiempo",
        waSubmitPendingTitle: "Seguimos enviando tu mensaje a WhatsApp para su aprobación",
        waSubmitPendingBody: "Todavía no hemos podido conectar con WhatsApp. Lo seguiremos intentando automáticamente y te escribiremos si WhatsApp lo rechaza. No tienes que hacer nada.",
        addWaitWaTip: "Podrás añadir contactos cuando tu mensaje haya llegado a WhatsApp para su aprobación. Lo seguimos intentando; vuelve a mirar en unos minutos.",
        remCancelled: "Cancelado",
        remCancelFormDeleted: "No enviado: el evento se eliminó.",
        remCancelDeadline: "No enviado: el plazo para confirmar terminó antes de su envío.",
        remCancelDateMoved: "No enviado: el evento se adelantó y su hora ya había pasado.",
        remOnHold: "En espera",
        remHeldPostponed: "En espera: el evento está aplazado. Se moverá a la nueva fecha cuando la fijes.",
        remCancelEventCancelled: "No enviado: el evento se canceló.",
        remCancelPostponedLate: "No enviado: el evento estuvo aplazado hasta más de un día después de la hora de este recordatorio.",
        remCancelInvitation: "No enviado: su invitación se eliminó o se canceló.",
        remCancelLate: "No enviado: su invitación estuvo detenida hasta más de un día después de la hora de este recordatorio.",
        remCancelNotSent: "No enviado",
        remMovedWithEvent: "se movió con la fecha del evento",
        remCancelDeadlineHint: "Cambia el plazo para confirmar o quita el enlace de confirmación y luego edítalo para enviarlo.",
        remCancelDateMovedHint: "Edítalo para elegir una nueva hora.",
        remSendsSoon: "Se enviará en la próxima hora",
        remDateMovedPast: "El evento se adelantó y la nueva hora de este recordatorio ya pasó, así que no se enviará. Edítalo para elegir una nueva hora.",
        remSendNowWaiting: "Este recordatorio se enviará cuando su invitación haya llegado a todos los invitados.",
        remSendNowNoRecipients: "Ningún invitado coincide con este recordatorio, así que no se envió nada.",
        remSendNowTaken: "Este recordatorio ya se está enviando.",
        remSendNowCancelled: "Este recordatorio se canceló porque su invitación se eliminó o se canceló.",
        remSendNowError: "No pudimos determinar ahora a quién va dirigido este recordatorio. No se envió nada y se volverá a intentar automáticamente.",
        moreActions: "Más acciones", // the card's ... menu; was missing in every pack
        // Card badges and stats, reminder rows, the Status window, Remind All
        // and the card actions' confirms/toasts (code review 2026-10-07: all
        // English on every dashboard). See cCount for the ...One forms.
        badgeSent: "Enviado",
        badgeSending: "Enviando",
        badgeProcessing: "Procesando",
        badgeScheduled: "Programado",
        badgePaused: "En pausa",
        badgeCancelled: "Cancelado",
        badgeDraft: "Borrador",
        badgeExpired: "Caducado",
        badgeUnableToSend: "No se pudo enviar",
        badgeNoCredits: "Créditos insuficientes",
        badgeInReview: "En revisión",
        scheduledFor: "Programado para el {when}",
        sendingAt: "Se envía el {when}",
        expiredTip: "WhatsApp no aprobó este mensaje a tiempo. Usa Editar y reenviar para enviarlo de nuevo.",
        noCreditsTip: "Este mensaje se pausó porque tu cuenta se quedó sin créditos. Compra más créditos y vuelve a enviarlo para entregar los mensajes que faltan.",
        inReviewTip: "Revisamos los mensajes de las cuentas nuevas antes de enviarlos, y alguien está revisando el tuyo ahora. No se ha cobrado nada y tu mensaje y tus contactos están guardados. Se enviará automáticamente en cuanto se apruebe; escríbenos por el chat si lo necesitas antes.",
        toggleRemindersTip: "Mostrar u ocultar los recordatorios de este mensaje",
        showRemindersAria: "Mostrar recordatorios ({n})",
        hideRemindersAria: "Ocultar recordatorios ({n})",
        expandAllReminders: "Expandir todo",
        collapseAllReminders: "Contraer todo",
        expandAllRemindersTip: "Mostrar los recordatorios de todos los mensajes",
        collapseAllRemindersTip: "Ocultar los recordatorios de todos los mensajes",
        remindersOn: "Recordatorios activados",
        statResponded: "{n} respondieron",
        statRespondedOne: "{n} respondió",
        statPending: "{n} pendientes",
        statPendingOne: "{n} pendiente",
        statSent: "{n} enviados",
        statSentOne: "{n} enviado",
        statContacts: "{n} contactos",
        statContactsOne: "{n} contacto",
        statNotDelivered: "{n} no entregados",
        statNotDeliveredOne: "{n} no entregado",
        statRecipients: "{n} destinatarios",
        statRecipientsOne: "{n} destinatario",
        allRepliedTip: "Todos los que lo recibieron ya habían respondido: es a quienes iba dirigido.",
        waCouldNotAccept: "WhatsApp no pudo aceptar tu mensaje. Edítalo y vuelve a enviarlo.",
        untitledMessage: "Mensaje",
        editResendTip: "Corrige el problema y vuelve a enviar los mensajes de WhatsApp",
        timingDayOf: "El mismo día",
        timing1DayBefore: "1 día antes",
        timing3DaysBefore: "3 días antes",
        timing1WeekBefore: "1 semana antes",
        timing2WeeksBefore: "2 semanas antes",
        audNoResponse: "Invitados sin respuesta",
        audAttending: "Invitados que asistirán",
        audMaybe: "Invitados que dijeron quizás",
        audDeclined: "Invitados que no asistirán",
        audAll: "Todos los contactos",
        remTitleAudience: "Recordatorio · {audience}",
        remTitleIndex: "Recordatorio {n}",
        remWasSetFor: "Estaba programado para el {when}",
        remNoCreditsTip: "Este recordatorio no se pudo enviar porque no tienes créditos suficientes. Compra más créditos y reanúdalo para enviarlo.",
        remAddCredits: "Añade créditos para enviar este recordatorio",
        remNoRecipients: "Sin destinatarios",
        remNoMatching: "Ningún destinatario coincide",
        remResubmitTip: "Tu mensaje editado ha vuelto a WhatsApp para su aprobación. Se enviará en cuanto se apruebe.",
        remResubmitting: "Reenviando a aprobación",
        remSendsOnceApproved: "Se envía el {when}, cuando se apruebe",
        remWaitingApproval: "Esperando la aprobación de WhatsApp",
        remAllRepliedTip: "Todos los que recibieron este recordatorio ya habían respondido: es a quienes iba dirigido.",
        remExpiredTip: "WhatsApp no aprobó a tiempo el mensaje de este recordatorio. Edítalo e inténtalo de nuevo.",
        remRejectedTip: "WhatsApp rechazó el contenido del mensaje de este recordatorio. Edita el recordatorio y guárdalo para volver a enviarlo a aprobación.",
        remFailedTip: "Hubo un problema al enviar este recordatorio. Edítalo e inténtalo de nuevo.",
        remRejected: "Mensaje rechazado",
        remCouldntSend: "No se pudo enviar",
        remTimedOut: "Se agotó el plazo de aprobación de WhatsApp",
        remEditToResubmit: "Edita y guarda para volver a enviarlo",
        remSendFailed: "Error de envío",
        remWaitingTip: "Esperando a que WhatsApp (Meta) apruebe el mensaje de este recordatorio: suele tardar unos minutos, a veces algo más. Se enviará automáticamente en cuanto se apruebe.",
        remSendingSoon: "Se enviará pronto",
        remBeingSentTip: "Este recordatorio se está enviando ahora.",
        remDueOnceApproved: "Previsto el {when} · se envía cuando WhatsApp lo apruebe",
        remSendsWhenApproved: "Se envía en cuanto WhatsApp lo apruebe",
        remDue: "Previsto el {when}",
        remSendingNow: "Enviando ahora…",
        remProcessingTip: "Este recordatorio se está enviando ahora. El correo sale de inmediato; si también va por WhatsApp, esa parte se envía en cuanto Meta apruebe el mensaje, normalmente en unos minutos.",
        remSendingMinute: "Enviando ahora; suele tardar uno o dos minutos",
        remSends: "Se envía el {when}",
        remPreviewTip: "Ver el mensaje del recordatorio",
        remEditTip: "Editar el mensaje, el momento o los destinatarios",
        remStatusTip: "Ver el estado de entrega: quién recibió este recordatorio y su respuesta",
        remBuyCreditsTip: "Compra créditos para enviar este recordatorio",
        remResumeCreditsTip: "Reanuda este recordatorio cuando tengas créditos",
        remResumeTip: "Reanudar este recordatorio",
        remPauseTip: "Pausar este recordatorio",
        remDeleteTip: "Eliminar este recordatorio",
        msgNotFound: "No se encontró el mensaje",
        remFromMainMessage: "Añade los recordatorios desde el mensaje principal, no desde otro recordatorio.",
        renamePrompt: "Cambiar el nombre de este mensaje:",
        renameEmpty: "El nombre del mensaje no puede estar vacío",
        renameFailed: "No se pudo cambiar el nombre del mensaje: ",
        cancelConfirm: "¿Seguro que quieres cancelar el mensaje «{name}»?\n\nNo se puede deshacer. Los mensajes que aún no se han enviado no se enviarán.",
        cancelDone: "Mensaje cancelado",
        cancelFailed: "No se pudo cancelar el mensaje: ",
        resumeConfirm: "¿Reanudar el mensaje «{name}»?\n\nSe enviará el {when}.",
        resumeConfirmNoDate: "¿Reanudar el mensaje «{name}»?\n\nSe enviará en la fecha programada originalmente.",
        resumeDone: "Mensaje reanudado",
        resumeFailed: "No se pudo reanudar el mensaje: ",
        pauseConfirm: "¿Pausar el mensaje «{name}»?\n\nNo se enviará a la hora programada. Podrás reanudarlo o cancelarlo más tarde.",
        pauseDone: "Mensaje en pausa",
        pauseFailed: "No se pudo pausar el mensaje: ",
        pauseTooLate: "Este mensaje terminó de enviarse antes de poder pausarlo.",
        autoRetryPauseConfirm: "¿Pausar el reintento automático de «{name}»?",
        autoRetryResumeConfirm: "¿Reanudar el reintento automático de «{name}»?",
        autoRetryNotRetriedOne: "{n} contacto no se volverá a intentar automáticamente.",
        autoRetryNotRetriedMany: "{n} contactos no se volverán a intentar automáticamente.",
        autoRetryRetriedOne: "{n} contacto se volverá a intentar en su próximo intento programado.",
        autoRetryRetriedMany: "{n} contactos se volverán a intentar en su próximo intento programado.",
        autoRetryPaused: "Reintento automático en pausa",
        autoRetryResumed: "Reintento automático reanudado",
        autoRetryPauseFailed: "No se pudo pausar el reintento automático: ",
        autoRetryResumeFailed: "No se pudo reanudar el reintento automático: ",
        sendNowConfirm: "¿Enviar ahora el mensaje «{name}»?\n\nLa fecha programada ya pasó. El mensaje se enviará de inmediato.",
        sendNowDone: "¡Mensaje enviado!",
        sendNowFailed: "No se pudo enviar el mensaje: ",
        deleteDraftConfirm: "¿Seguro que quieres eliminar el borrador «{name}»?\n\nNo se puede deshacer.",
        deleteDraftFailed: "No se pudo eliminar el borrador: ",
        deleteConfirmActive: "¿Eliminar definitivamente «{name}»?\n\nEste mensaje tiene estadísticas de entrega y puede tener recordatorios programados. Se eliminarán ambos.\n\nLas respuestas de los invitados se conservan (se guardan aparte).\n\nNo se puede deshacer.",
        deleteAlsoGuests: "Este mensaje añadió {n} invitados a tu lista que no han respondido y no están en ningún otro mensaje.\n\n¿Quitarlos también de la lista de invitados?\n\nAceptar los quita. Cancelar los mantiene.",
        deleteConfirmUnsent: "¿Eliminar definitivamente «{name}»?\n\nNo se envió nada con este mensaje, así que puedes eliminarlo sin problema.\n\nNo se puede deshacer.",
        deleteFailed: "No se pudo eliminar el mensaje: ",
        statusAllTitle: "Todos los contactos",
        statusAllSubtitle: "De todos los mensajes",
        statusAllMessages: "📊 Todos los mensajes",
        statusAllTags: "Todas las etiquetas", statusNoTag: "Sin etiqueta", statusTagsLine: "Etiquetas",
        statusTitleFallback: "Estado del mensaje",
        statusThisMessageOnly: "Solo se muestran los contactos de este mensaje",
        statusAddedAfterLaunch: "{n} añadidos después del envío",
        statusAddedAfterLaunchOne: "{n} añadido después del envío",
        statusContactCount: "{n} contactos",
        statusContactCountOne: "{n} contacto",
        exportNone: "No hay contactos para exportar",
        exportedOne: "Se exportó {n} contacto",
        exportedMany: "Se exportaron {n} contactos",
        dlvSent: "Enviado",
        dlvDelivered: "Entregado",
        dlvRead: "Leído",
        dlvOpened: "Abierto",
        dlvClicked: "Clic",
        dlvClickedTip: "Su enlace de RSVP (o tu sitio web) se abrió desde uno de tus mensajes. Aparte de una respuesta, es la señal más segura de que lo tiene.",
        dlvReadTip: "Confirmación de lectura del propio WhatsApp (doble check azul), así que lo ha leído.",
        dlvOpenedTip: "Se cargaron las imágenes del email, así que probablemente lo vio. Apple Mail y otras apps cargan las imágenes automáticamente al recibirlo, por eso es una buena señal, no una prueba.",
        dlvDeliveredEmailTip: "Su proveedor de email lo aceptó. No podemos ver en qué carpeta quedó, y muchas apps (Outlook, Hotmail, email del trabajo) nunca avisan de la apertura, así que puede que ya lo haya leído.",
        dlvDeliveredWaTip: "Llegó a su teléfono (doble check de WhatsApp). Aún no hay confirmación de lectura, pero algunos la desactivan, así que puede que ya lo haya leído.",
        dlvDeliveredSmsTip: "Llegó a su teléfono como SMS. Los SMS nunca indican si se leyeron.",
        dlvSentTip: "Se entregó a la red. Aún no hay confirmación de que le haya llegado.",
        dlvBounced: "Rebotado",
        dlvFailed: "Fallido",
        dlvSmsSent: "SMS enviado",
        dlvSmsFailed: "SMS fallido",
        detailNoEmailFallback: "No hay correo electrónico guardado, así que no se pudo enviar por correo. Añade uno con el lápiz para enviarlo.",
        deliveryFailed: "Error de entrega",
        detailEmail: "Correo",
        detailSmsFallback: "SMS de respaldo",
        detailRsvp: "Respuesta",
        noDetail: "No hay detalles disponibles",
        fallbackEmail: "Por correo",
        fallbackSms: "Por SMS",
        aMessageFailed: "falló un mensaje",
        aMessageFailedTip: "Uno de los mensajes a este contacto no se pudo entregar, aunque le llegó otro mensaje.",
        checkNumber: "revisa este número",
        checkNumberTip: "WhatsApp no pudo entregar a este número tal como lo tenemos guardado, y el número no parece completo. Vale la pena corregirlo y volver a enviar antes de suponer que el invitado no usa WhatsApp.",
        rowDetailsTip: "Haz clic para ver todos los detalles de entrega",
        updateResponseTip: "Actualizar respuesta",
        linkColumn: "ENLACE",
        remCellOnAria: "Recordatorios activados: pausar",
        remCellOffAria: "Recordatorios desactivados: reanudar",
        guestFallback: "Invitado",
        addName: "Añadir nombre",
        editNameTip: "Haz clic para editar el nombre",
        nameEditPlaceholder: "Nombre del invitado",
        nameEditSave: "Guardar",
        nameEditCancel: "Cancelar",
        nameSaveFailed: "No se pudo guardar el nombre",
        raLoadingAll: "Cargando todos los contactos...",
        raLoadingPending: "Cargando los contactos pendientes...",
        raLoadingYes: "Cargando a quienes respondieron sí...",
        raLoadingMaybe: "Cargando a quienes respondieron quizás...",
        raLoadingNo: "Cargando a quienes respondieron no...",
        raExcludedAll: "Todos los contactos están excluidos de los recordatorios",
        raExcludedPending: "Todos los invitados pendientes están excluidos de los recordatorios",
        raExcludedYes: "Todos los que respondieron sí están excluidos de los recordatorios",
        raExcludedMaybe: "Todos los que respondieron quizás están excluidos de los recordatorios",
        raExcludedNo: "Todos los que respondieron no están excluidos de los recordatorios",
        raSkippedExcludedOne: "Se omitió {n} invitado que excluiste de los recordatorios",
        raSkippedExcludedMany: "Se omitieron {n} invitados que excluiste de los recordatorios",
        raLoadFailed: "No se pudieron cargar los contactos",
        raNoneSelected: "No seleccionaste destinatarios; se canceló.",
        raNoValidRecipients: "No hay destinatarios válidos a los que enviar.",
        errLabel_131026: "WhatsApp no pudo entregarlo",
        errLabel_131049: "bloqueado por la política de Meta",
        errLabel_131047: "ventana de 24 h vencida",
        errLabel_131048: "bloqueado por spam",
        errLabel_131056: "límite de envío",
        errLabel_131058: "problema con el mensaje",
        errLabel_131061: "mensaje pausado por Meta",
        errLabel_131062: "mensaje desactivado por Meta",
        errLabel_132001: "problema con el mensaje",
        errLabel_130472: "retenido por una prueba de Meta",
        errLabel_130497: "WhatsApp bloqueado en este país",
        errLabel_131063: "no está en WhatsApp",
        errLabel_130429: "límite de envío alcanzado",
        errLabel_marketing_limit_reached: "límite de marketing de Meta",
        errLabel_BREVO_REJECTED: "rechazado por el proveedor de SMS",
        errLabel_unknown: "fallido",
        errTip_131049: "Meta bloqueó este mensaje de marketing (límite por política o interacción); no es una baja del invitado. Usa WhatsApp y se le puede contactar: reenviarlo pasado un día funciona aproximadamente la mitad de las veces.",
        errTip_131026: "Este número no pudo recibir el mensaje: quizá no use WhatsApp o algún dígito esté mal. Reenviarlo rara vez ayuda (llega aproximadamente 1 de cada 20), así que es más fiable contactarlo de otra forma.",
        errTip_131048: "Número de teléfono restringido",
        errTip_131056: "Se superó el límite de envío",
        errTip_131058: "Mensaje no aprobado",
        errTip_131047: "Venció la ventana de 24 horas",
        errTip_131061: "Meta pausó el mensaje de WhatsApp usado aquí, así que no se pudo entregar. Afecta a todos los invitados de este mensaje, no solo a este: hay que volver a crear el mensaje.",
        errTip_131062: "Mensaje desactivado",
        errTip_132001: "WhatsApp rechazó el mensaje (no existía en el idioma en que se envió), así que no se entregó nada. Vuelve a crear el mensaje del recordatorio y reenvíalo.",
        errTip_marketing_limit_reached: "Meta pausó las entregas de marketing a este número; no es una baja del invitado",
        errTip_130472: "Meta retuvo este mensaje como parte de uno de sus propios experimentos de entrega. El número está bien, pero al reenviarlo se aplica la misma retención: contacta con el invitado de otra forma.",
        errTip_130497: "Meta no permite por ahora que nuestra cuenta de WhatsApp escriba a números de este país, así que un reenvío fallará igual. Contacta al invitado por correo o con su enlace personal.",
        errTip_131063: "Este número no está registrado en WhatsApp, así que no puede recibir la invitación. Prueba con otro número o contacta con el invitado de otra forma.",
        errTip_130429: "Alcanzamos nuestro propio límite de envío por ahora; el número está bien. Si lo reenvías en un rato, debería llegar.",
        errTip_BREVO_REJECTED: "El proveedor de SMS rechazó este mensaje después de aceptarlo (normalmente por un problema temporal de saldo de envío por nuestra parte). Vale la pena reintentarlo.",
        errTip_NOT_FOUND: "Contacto no encontrado o no válido",
        errTipUnknown: "Error desconocido",
        // Card status lines, failure reasons and WhatsApp refusals (item M2, 2026-10-07).
        chEmail: "Correo",
        chText: "SMS",
        chWhatsApp: "WhatsApp",
        stSentN: "{n} enviados",
        stAtSendTime: "se envía a la hora programada",
        stGoingOut: "se envía en los próximos minutos",
        stWaWaiting: "esperando a que WhatsApp apruebe el mensaje (suele tardar unos minutos)",
        stWaWaitingLong: "sigue esperando a que WhatsApp apruebe el mensaje. Está tardando más de lo habitual; se enviará solo en cuanto se apruebe",
        stWaSubmitting: "seguimos enviándolo para su aprobación",
        stRetrying: "Todavía no se ha enviado nada. Lo volveremos a intentar en unos minutos.",
        stSendingSoon: "Se envía en los próximos minutos.",
        stWaRefused: "no aceptado. Edítalo y vuelve a enviarlo.",
        stWaRefusedBelow: "no aceptado. Edítalo y vuelve a enviarlo (el motivo está abajo).",
        failFormMissing: "El formulario de este mensaje ya no existe. Vuelve a guardar el formulario y crea un mensaje nuevo.",
        failWaOnly: "WhatsApp no aceptó el mensaje y a todos sus invitados solo se les puede contactar por WhatsApp, así que no se envió nada. Edítalo y vuelve a enviarlo.",
        failWaRefused: "WhatsApp no aceptó el mensaje, así que no se envió nada. Edítalo y vuelve a enviarlo.",
        failNothingSent: "No se pudo enviar nada tras varios intentos. Pulsa Editar y reenviar para volver a intentarlo. Si vuelve a pasar, escríbenos por el chat.",
        failNoMethod: "Los datos de tus invitados no llegaron a nuestro paso de envío. Fue un problema nuestro, no de tu lista de invitados. Pulsa Editar y reenviar para volver a intentarlo y escríbenos por el chat si vuelve a pasar.",
        failSendError: "Algo falló por nuestra parte durante el envío. No fue nada que hicieras tú. Pulsa Editar y reenviar para volver a intentarlo y escríbenos por el chat si vuelve a pasar.",
        failGeneric: "No pudimos enviar este mensaje. Pulsa Editar y reenviar para volver a intentarlo o escríbenos por el chat.",
        err131053Label: "no se pudo cargar la imagen",
        err131053Tip: "WhatsApp no pudo descargar la imagen de la invitación, así que el mensaje no se entregó. Volver a enviarlo suele funcionar.",
        errSmsBouncedLabel: "SMS no entregado",
        errSmsBouncedTip: "La red telefónica no entregó el SMS. Puede que el número esté mal, sea un fijo o esté apagado. Revisa el número, escríbele por correo o copia su enlace personal y envíaselo tú.",
        errSmsFailedLabel: "SMS no enviado",
        errSmsFailedTip: "Nuestro proveedor de SMS no envió este mensaje. Volver a enviarlo suele funcionar.",
        errSmsNonGsmLabel: "no se puede enviar por SMS",
        errSmsNonGsmTip: "Este mensaje no tiene texto en inglés, y los SMS solo pueden llevar ese. Añade una versión en inglés con Editar y reenviar, o contacta con este invitado por correo.",
        errNonUsLabel: "los SMS solo van a números de EE. UU.",
        errNonUsTip: "Solo enviamos SMS a números de EE. UU. Contacta con este invitado por WhatsApp o correo.",
        errEmailHardLabel: "el correo no funciona",
        errEmailHardTip: "Esta dirección de correo no existe o rechaza los mensajes. Revisa cómo está escrita o contacta con el invitado por SMS o WhatsApp.",
        errEmailSoftLabel: "correo no aceptado por ahora",
        errEmailSoftTip: "El buzón del invitado no aceptó el correo esta vez (a menudo porque está lleno o no está disponible un momento). Volver a enviarlo más tarde suele funcionar.",
        errEmailBounceLabel: "correo rebotado",
        errEmailBounceTip: "No se pudo entregar el correo a esta dirección. Revisa cómo está escrita o contacta con el invitado por SMS o WhatsApp.",
        errGenericTip: "No se pudo entregar este mensaje. Contacta con este invitado de otra forma o vuelve a enviarlo más tarde.",
        fbRetryUntilAll: "WhatsApp está reteniendo los mensajes de marketing a estos invitados hasta {time}. Vuelve a intentarlo después.",
        fbRetryUntilSome: "WhatsApp está reteniendo los mensajes de marketing a algunos de ellos hasta {time}. Vuelve a intentarlo después.",
        fbChannelSub: "Este mensaje no les llega por la vía por la que se envió. Recuérdaselo por correo o copia el enlace personal de cada invitado en la lista de abajo.",
        fbChannelNoEmail: "Ninguno de estos invitados tiene correo electrónico. Copia el enlace personal de cada uno en la lista de abajo y envíaselo tú.",
        fbChannelSomeNoEmail: "{n} de estos invitados no tienen correo electrónico, así que el correo no les llegará. Esta lista se cierra al abrir el correo, así que copia antes sus enlaces personales. ¿Continuar con los {m} que sí tienen?",
        ndTipWa: "WhatsApp no entregó el mensaje a {n} de tus invitados. A veces Meta retiene mensajes para evitar el spam, o el número no está en WhatsApp. Abre la lista para ver el motivo y contacta con ellos por correo o SMS.",
        ndTipSms: "{n} de tus SMS no se entregaron, normalmente por un número equivocado o fijo. Abre la lista para ver el motivo y luego revisa el número, escríbeles por correo o copia su enlace personal.",
        ndTipEmail: "{n} de tus correos rebotaron, normalmente por una dirección mal escrita. Abre la lista para ver el motivo y contacta con ellos por SMS o WhatsApp.",
        ndTipMixed: "No entregados: {parts}. Abre la lista para ver el motivo de cada invitado.",
        ndTipGeneric: "{n} de tus invitados no recibieron este mensaje. Abre la lista para ver el motivo.",
        ndTipWaOne: "WhatsApp no entregó el mensaje a uno de tus invitados. A veces Meta retiene mensajes para evitar el spam, o el número no está en WhatsApp. Abre la lista para ver el motivo y contacta con esa persona por correo o SMS.",
        ndTipSmsOne: "Uno de tus SMS no se entregó, normalmente por un número equivocado o fijo. Abre la lista para ver el motivo y luego revisa el número, escríbele por correo o copia su enlace personal.",
        ndTipEmailOne: "Uno de tus correos rebotó, normalmente por una dirección mal escrita. Abre la lista para ver el motivo y contacta con esa persona por SMS o WhatsApp.",
        ndTipGenericOne: "Uno de tus invitados no recibió este mensaje. Abre la lista para ver el motivo.",
        tplErr_ours: "Algo falló por nuestra parte, y no fue nada que hicieras tú. Abre Editar y reenviar y vuelve a enviarlo desde ahí (no hace falta cambiar nada). Si vuelve a pasar, avísanos y lo solucionaremos.",
        tplErr_trailingParam: "Tu mensaje no puede TERMINAR con una etiqueta de personalización como {{name}} o {{table_number}}. WhatsApp no lo permite. Pon una palabra o un punto después de la etiqueta y vuelve a enviarlo.",
        tplErr_waLink: "WhatsApp no permite un botón que enlace de vuelta a WhatsApp. Haz que ese botón lleve a otro sitio y vuelve a enviarlo.",
        tplErr_imageFormat: "Para WhatsApp, la imagen de tu invitación tiene que ser JPG o PNG. Vuelve a subirla en ese formato y reenvíala.",
        tplErr_imageProcess: "WhatsApp no pudo procesar la imagen de tu invitación. Prueba con otra (JPG o PNG, de menos de 5 MB) y vuelve a enviarlo.",
        tplErr_category: "WhatsApp considera tu mensaje de un tipo distinto al que le asignamos. Vuelve a enviarlo y debería pasar.",
        tplErr_layout: "WhatsApp no pudo leer el formato de tu mensaje. Suele ser una {{etiqueta}} sola en su propia línea. Ponla dentro de una frase y vuelve a enviarlo.",
        tplErr_duplicateName: "WhatsApp ya tiene un mensaje guardado con este nombre. Cambia el nombre del mensaje y vuelve a enviarlo.",
        tplErr_rateLimit: "Has llegado por ahora al límite de mensajes nuevos de WhatsApp. Espera un poco y vuelve a enviarlo. No se pierde nada.",
        tplErr_noReason: "WhatsApp no aprobó el texto de este mensaje. Edítalo y vuelve a enviarlo.",
        tplLangsHeadOne: "WhatsApp rechazó la versión en {langs} de tu mensaje.",
        tplLangsHeadMany: "WhatsApp rechazó estas versiones de tu mensaje: {langs}.",
        tplLangsHeadUnknown: "WhatsApp rechazó uno de los idiomas de tu mensaje.",
        tplLangsSaid: "WhatsApp dijo: «{text}».",
        tplLangsEditOne: "Edita esa traducción y vuelve a enviarla.",
        tplLangsEditMany: "Edita esas traducciones y vuelve a enviarlas.",
        langCombined: "dos idiomas combinados",
        failWaRefusedPartial: "WhatsApp no aceptó el mensaje, así que tus invitados de WhatsApp no lo recibieron. Tus invitados por correo y por SMS ya lo tienen, así que no se lo vuelvas a enviar. Cambia el texto y vuelve a enviarlo a tus invitados de WhatsApp.",
        fbPermanentSubEmail: "El número o la dirección de correo no funciona. Corrígelo en la lista de invitados o copia el enlace personal del invitado y envíaselo tú.",
        fbHeldBack: "{n} no se enviaron a propósito",
        fbHeldBackSub: "Los retuvimos: el invitado pidió que paráramos, se dio de baja, no está en WhatsApp o WhatsApp pausó el número. Cada fila de abajo dice cuál. A quien pidió que paráramos nunca le volvemos a escribir.",
        tplLangsHeadCombined: "WhatsApp rechazó la versión combinada en dos idiomas de tu mensaje.",
        remFailFormMissing: "El formulario de este recordatorio ya no existe. Vuelve a guardar el formulario y después edita el recordatorio con el lápiz y guárdalo.",
        remFailWaOnly: "WhatsApp no aceptó el recordatorio y a todos sus invitados solo se les puede contactar por WhatsApp, así que no se envió nada. Edita el recordatorio con el lápiz y guárdalo para volver a enviarlo.",
        remFailWaRefused: "WhatsApp no aceptó el recordatorio, así que no se envió nada. Edita el recordatorio con el lápiz y guárdalo para volver a enviarlo.",
        remFailWaRefusedPartial: "WhatsApp no aceptó el recordatorio, así que tus invitados de WhatsApp no lo recibieron. Tus invitados por correo y por SMS ya lo tienen. Edita el recordatorio con el lápiz y guárdalo para volver a enviarlo.",
        remFailNothingSent: "No se pudo enviar nada tras varios intentos. Edita el recordatorio con el lápiz y guárdalo para volver a intentarlo. Si vuelve a pasar, escríbenos por el chat.",
        remFailNoMethod: "Los datos de tus invitados no llegaron a nuestro paso de envío. Fue un problema nuestro, no de tu lista de invitados. Edita el recordatorio con el lápiz y guárdalo para volver a intentarlo, y escríbenos por el chat si vuelve a pasar.",
        remFailSendError: "Algo falló por nuestra parte durante el envío. No fue nada que hicieras tú. Edita el recordatorio con el lápiz y guárdalo para volver a intentarlo, y escríbenos por el chat si vuelve a pasar.",
        remFailGeneric: "No pudimos enviar este recordatorio. Edítalo con el lápiz y guárdalo para volver a intentarlo, o escríbenos por el chat.",
        // Provider outage pause: see _providerPauseBadgeHtml.
        providerPaused: "Envío detenido",
        providerPausedTip: "Un problema con nuestro proveedor de mensajería detuvo este mensaje antes de que llegara a todos. No se te ha cobrado nada por los invitados que aún no lo han recibido. Nuestro equipo ya está avisado y se lo enviaremos en cuanto se resuelva. No tienes que hacer nada.",
        providerPausedPhrase: "Esperando a nuestro proveedor de mensajería",
        providerResumeConfirm: "¿Quieres volver a intentar enviar este mensaje ahora? Solo lo recibirán los invitados que aún no lo tienen.",
        providerResumed: "Reiniciando. Los invitados que aún no lo han recibido lo tendrán en los próximos minutos.",
        providerResumeFailed: "No pudimos reanudar este mensaje. Inténtalo de nuevo.",
        providerPausedPhraseAuto: "Lo volveremos a intentar automáticamente",
        providerPausedTipAuto: "Un problema temporal con nuestro proveedor de mensajería detuvo este mensaje antes de que llegara a todos. No se te ha cobrado nada por los invitados que aún no lo han recibido. Lo volveremos a intentar automáticamente dentro de un rato y solo lo recibirán ellos. También puedes intentarlo ya con «{resume}».",
        providerResuming: "Reiniciando. Suele tardar unos minutos.",
        providerResumedTeam: "Lo volvemos a intentar ahora. Si nuestro proveedor sigue sin funcionar, se detendrá de nuevo y saldrá en cuanto nuestro equipo lo haya resuelto. No tienes que hacer nada más.",
        sendRestartResumes: "El servidor se reinició mientras se enviaba este mensaje. Quienes ya lo recibieron no lo recibirán dos veces, y el resto lo recibirá automáticamente en unos minutos.",
        sendRestartRetry: "El servidor se reinició mientras se enviaba este mensaje. Quienes ya lo recibieron no lo recibirán dos veces. Vuelve a enviarlo en un minuto para llegar al resto.",
        // Outage-held guests: see _outageHeldNoteHtml.
        heldPhrase: "Puede que {n} invitados ya lo tengan",
        heldPhraseOne: "Puede que 1 invitado ya lo tenga",
        heldWhy: "Mientras nuestro proveedor de mensajería fallaba, su WhatsApp o SMS no obtuvo respuesta, así que puede que les haya llegado de todos modos. Para que nadie lo reciba dos veces, no se lo hemos vuelto a enviar. Todos los demás lo reciben con normalidad.",
        heldMore: "y {n} más",
        heldSend: "Enviárselo también a estos {n}",
        heldSendOne: "Enviárselo también a este invitado",
        heldSendDone: "Listo. Ya se lo hemos enviado.",
        heldSendWithResume: "Listo. Lo recibirán con todos los demás cuando se reanude el envío.",
        heldSendBusy: "Este mensaje se está enviando ahora mismo. Inténtalo de nuevo cuando termine.",
        heldSendPaused: "Reanuda primero este mensaje y luego envíaselo.",
        heldSendFailed: "No hemos podido enviárselo ahora. Inténtalo de nuevo en unos minutos.",
        // Interrupted Add Contacts (PMb).
        errInterruptedLabel: "no enviado (interrumpido)",
        errInterruptedTip: "El envío se detuvo antes de llegar a este invitado porque nuestro servidor se reinició. No se le envió ni se cobró nada; puedes reenviárselo sin problema y se cobra como cualquier envío.",
        acNotSent: "{n} sin enviar: el envío se interrumpió. Reenvíaselo desde Estado",
        addRestartRetry: "Nuestro servidor se estaba reiniciando, así que no se añadió ni se envió nada. Inténtalo de nuevo en un minuto.",
        // Retry, resend explainer, ghost-template recovery, follow-up, CSV export.
        retryWhichMessage: "No sabemos en qué mensaje fallaron. Elige un mensaje en la parte superior de esta ventana y vuelve a intentarlo.",
        retryAMessage: "un mensaje",
        retryScopeOne: "Les reenviaremos «{n}».",
        retryScopeMany: "Reenviaremos cada uno de los {n} mensajes que fallaron, solo a esos invitados.",
        retryConfirmOne: "¿Enviar de nuevo el mismo mensaje a 1 invitado?",
        retryConfirmMany: "¿Enviar de nuevo el mismo mensaje a {n} invitados?",
        retryConfirmNobody: "No se vuelve a contactar a nadie que ya lo recibió.",
        retryConfirmFree: "Reenviar es gratis: el crédito de cada mensaje fallido ya se devolvió.",
        retryResending: "Reenviando…",
        nothingTryLater: "Vuelve a intentarlo en unas horas.",
        nothingTryAfter: "Vuelve a intentarlo después de: {n}.",
        nothingCooledOne: "WhatsApp rechazó este mensaje para 1 invitado en las últimas 24 horas, así que se omitió en lugar de volver a insistir.",
        nothingCooledMany: "WhatsApp rechazó este mensaje para {n} invitados en las últimas 24 horas, así que se omitieron en lugar de volver a insistir.",
        nothingDeadOne: "1 invitado no tiene WhatsApp: un reenvío no puede llegarle. Prueba por correo o SMS.",
        nothingDeadMany: "{n} invitados no tienen WhatsApp: un reenvío no puede llegarles. Prueba por correo o SMS.",
        nothingReachedOne: "1 invitado ya tiene este mensaje (le llegó por otra vía), así que no se volvió a enviar.",
        nothingReachedMany: "{n} invitados ya tienen este mensaje (les llegó por otra vía), así que no se volvió a enviar.",
        nothingNobody: "Esta vez no se pudo llegar a nadie. Vale la pena volver a intentarlo más tarde.",
        ghostConfirm: "¿Reconstruir la plantilla de WhatsApp de este mensaje y reenviarlo a los invitados a los que no llegó?\n\nPuede tardar unos minutos. No se cobran créditos adicionales.",
        ghostStarting: "Iniciando…",
        ghostRecovering: "Recuperando…",
        ghostButton: "Recrear y reenviar",
        ghostStarted: "Reconstruyendo la plantilla de WhatsApp y reenviando; tarda unos minutos. Te avisaremos por correo cuando termine.",
        ghostAlready: "Ya hay una recuperación en curso.",
        ghostNotNeeded: "Este mensaje ya no muestra el problema de la plantilla de WhatsApp, así que no hay nada que reconstruir.",
        ghostStartFailed: "No se pudo iniciar la recuperación. Vuelve a intentarlo en unos minutos.",
        ghostFixed: "Plantilla de WhatsApp corregida.",
        ghostResent: "Reenviado.",
        ghostResentToOne: "Reenviado a 1 invitado.",
        ghostResentTo: "Reenviado a {n} invitados.",
        ghostEscalated: "La plantilla reconstruida sigue sin pasar la revisión de WhatsApp. Ya hemos avisado a nuestro equipo. Contacta con soporte.",
        ghostIssue: "Problema con la plantilla de WhatsApp.",
        ghostIssueBody: "{n} mensajes no se pudieron entregar porque WhatsApp rechazó la plantilla. Un reintento normal no servirá: reconstruye la plantilla y reenvía.",
        followUpLoadedOne: "1 contacto cargado para el seguimiento",
        followUpLoaded: "{n} contactos cargados para el seguimiento",
        csvPhone: "Teléfono",
        csvEmail: "Correo electrónico",
        csvDeliveryStatus: "Estado de entrega",
        csvDeliveryIssue: "Problema de entrega",
        csvRsvp: "Respuesta RSVP",
        csvGuests: "Número de invitados",
        csvPending: "Pendiente",
        csvNotSent: "No enviado",
        csvAttending: "Asistirá",
        csvDeclined: "No asistirá",
        csvMaybe: "Quizás",
        csvResponded: "Respondió",
        csvNoResponse: "Aún sin respuesta",
    },
    fr: {
        remWaOnlyWaitingTip: "Vos invités par e-mail et par SMS ont déjà ce rappel. Vos invités WhatsApp le recevront automatiquement dès que WhatsApp (Meta) aura approuvé le message.",
        remWaOnlyWaiting: "Les invités WhatsApp le recevront après approbation",
        quietHoldSystemTip: "Ce rappel tombait pendant la nuit pour certains de vos invités : il attend donc qu'il fasse jour pour tous. Vous n'avez rien à faire.",
        quietHoldLateTip: "WhatsApp a approuvé votre message pendant la nuit de certains de vos invités. Pour ne pas les réveiller, il partira quand il fera jour pour tous. Vous n'avez rien à faire.",
        remWaLost: "WhatsApp non envoyé",
        remWaLostTip: "WhatsApp n’a pas accepté le message de ce rappel : vos invités WhatsApp ne l’ont donc pas reçu. Vos invités par e-mail et par SMS l’ont déjà. Modifiez le rappel et enregistrez-le pour l’envoyer à vos invités WhatsApp.",
        remWaLostTimeoutTip: "WhatsApp n’a pas approuvé le message de ce rappel dans les 48 heures suivant l’heure d’envoi : vos invités WhatsApp ne l’ont donc pas reçu. Vos invités par e-mail et par SMS l’ont déjà. Modifiez le rappel et enregistrez-le pour le soumettre à nouveau ; inutile de changer quoi que ce soit.",
        remWaLostPhrase: "Modifiez et enregistrez pour l’envoyer aux invités WhatsApp",
        waTimeoutBody: "WhatsApp n’a pas approuvé ce message dans les 48 heures suivant l’heure d’envoi : vos invités WhatsApp ne l’ont donc pas reçu.",
        waOthersHaveIt: "Vos invités par e-mail et par SMS l’ont déjà. « {editResend} » l’envoie à vos invités WhatsApp.",
        waRejectedBadgeTip: "WhatsApp n’a pas accepté ce message. Modifiez le message et soumettez-le à nouveau à WhatsApp.",
        previewTitle: "Aperçu du message",
        previewNoBody: "(aucun texte de message enregistré)",
        previewNoteOne: "Sur WhatsApp, ceci apparaît comme le bouton ci-dessus (cliquez pour tester le lien).",
        previewNoteMany: "Sur WhatsApp et par e-mail, ceux-ci apparaissent comme des boutons (cliquez sur chacun pour tester le lien).",
        previewSmsNote: "Par SMS, le lien arrive sous forme de texte :",
        previewSampleFor: "Exemple affiché pour {name} — les variables comme {{first_name}} sont remplies pour chaque invité.",
        previewClone: "Dupliquer comme nouveau brouillon",
        close: "Fermer",
        addContacts: "Ajouter des contacts",
        preview: "Aperçu", status: "Statut", remind: "Relancer", add: "Ajouter", reminders: "Rappels", remindersNoneTip: "Aucun rappel pour l’instant — envoyez-en un",
        duplicate: "Dupliquer", delete: "Supprimer", edit: "Modifier", editResend: "Modifier et renvoyer",
        personalLinks: "Liens personnels", sharedByHand: "Lien personnel", copyPersonalLink: "Copier le lien personnel", linkCopied: "Lien personnel copié",
        sendOnWhatsApp: "Envoyer sur WhatsApp depuis votre téléphone", waWalkBtn: "Envoyer depuis mon WhatsApp", waWalkIntro: "Notre WhatsApp n’a pas pu joindre ces invités. Envoyez le message à chacun depuis votre propre WhatsApp : ouvrez-le, appuyez sur envoyer, puis revenez pour le suivant.", waWalkCounter: "Invité {i} sur {n}", waWalkOpen: "Ouvrir WhatsApp", waWalkSkip: "Passer", waWalkClose: "Fermer", waWalkDone: "Terminé. Vous avez ouvert WhatsApp pour {k} invités sur {n}.",
        hostSentBadge: "Envoyé depuis votre WhatsApp", hostSentTip: "Vous avez ouvert votre propre WhatsApp pour envoyer ce message. Nous ne voyons pas l’intérieur de WhatsApp : la remise n’est donc pas confirmée.", waWalkCardBtn: "Envoyer depuis mon WhatsApp ({n})", waWalkCardTip: "Notre WhatsApp n’a pas pu joindre ces invités. Envoyez-leur ce message depuis votre propre WhatsApp, un par un.",
        waWalkImageNote: "WhatsApp s’ouvre avec le texte seulement. Pour inclure l’image de votre invitation, joignez-la dans WhatsApp avant d’appuyer sur envoyer.", waWalkImageOpen: "Ouvrir l’image pour l’enregistrer",
        addedSkippedOptedOut: "{n} invité(s) ajouté(s) non contacté(s) : leurs rappels sont désactivés", addedSkippedResponded: "{n} invité(s) ajouté(s) non contacté(s) : ils avaient déjà répondu",
        tagContacts: "Étiqueter tout le monde sur ce message", tagContactsPrompt: "Étiquettes à ajouter à chaque invité de ce message, séparées par des virgules (ex. famille, réception seulement) :", tagContactsDone: "{n} invités étiquetés", tagContactsFailed: "Impossible d'étiqueter les invités",
        continueBtn: "Continuer", pause: "Mettre en pause", cancel: "Annuler", sendNow: "Envoyer maintenant",
        sendTest: "Envoyer un test",
        sendTestTip: "Envoyez-vous une copie de ce message pour voir exactement ce que vos invités recevront",
        sendTestSending: "Envoi de votre test…",
        sendTestDone: "Test envoyé",
        sendTestEmailCopyOnly: "L'e-mail est une copie pour vous — vos invités ne recevront pas d'e-mail.",
        sendTestNoMessage: "Ajoutez un message et choisissez comment l'envoyer, puis vous pourrez vous envoyer un test",
        sendTestFailed: "Impossible d'envoyer le test",
        sendTestCreditReturned: "Le crédit de ce test a été recrédité sur votre solde.",
        sendTestCost: "Votre premier test sur ce message est gratuit. Ensuite, chaque test par SMS ou WhatsApp coûte 1 crédit — les tests par e-mail restent toujours gratuits.",
        sendTestAgain: "Renvoyer un test",
        sendTestAgainTip: "Envoyez-vous une autre copie — utile après avoir modifié le texte",
        sendTestPhoneTitle: "Où envoyer le test ?",
        sendTestPhoneHint: "Votre compte n'a pas de numéro de mobile : saisissez-en un pour recevoir le test. Nous le retiendrons.",
        sendTestPhoneInvalid: "Cela ne ressemble pas à un numéro de mobile. Utilisez le format international, par exemple +14155550123.",
        resume: "Reprendre", buyCredits: "Acheter des crédits", retry: "Réessayer",
        previewTip: "Aperçu du texte du message",
        statusTip: "Voir qui a reçu le message et sa réponse",
        remindTip: "Envoyer un suivi aux invités de ce message",
        addTip: "Ajouter d'autres contacts à ce message",
        addScheduledTip: "Ajoutez d'autres contacts : ils recevront le message à l'heure d'envoi programmée",
        addDisabledTip: "Impossible d'ajouter des contacts : corrigez d'abord ce message via Modifier et renvoyer (le modèle WhatsApp n'est jamais arrivé chez Meta).",
        duplicateTip: "Réutiliser ce message (image, texte, langues, lien) dans un tout nouveau message",
        deleteTip: "Supprimer définitivement ce message de votre historique",
        pauseTip: "Mettre ce message en pause",
        buyCreditsTip: "Rechargez vos crédits de messages, puis reprenez ce message",
        retryTip: "Réessayer d'envoyer (réussit une fois que vous avez assez de crédits)",
        manageRemindersTip: "Gérer les rappels programmés",
        scheduleRemindersTip: "Programmer des rappels",
        resumeAutoRetryTip: "Reprendre la nouvelle tentative auto ({n} en attente)",
        pauseAutoRetryTip: "Suspendre la nouvelle tentative auto ({n} en attente)",
        remindPending: "Invités en attente", remindPendingSub: "N'ont pas encore répondu",
        remindMaybe: "Réponses « peut-être »", remindMaybeSub: "Ont dit peut-être : incitez-les à confirmer",
        remindYes: "Réponses « oui »", remindYesSub: "Rappel du jour J, mise à jour du lieu, etc.",
        remindNo: "Réponses « non »", remindNoSub: "Ne peuvent pas venir — envoyez un lien de diffusion en direct, un remerciement, etc.",
        remindRemovedOne: "L'invité que vous avez retiré ne recevra pas ce rappel.", remindRemovedMany: "Les {n} invités que vous avez retirés ne recevront pas ce rappel.",
        // Response-edit (pencil) window: _openResponseEditModal and what it opens.
        reTitle: "Mettre à jour la réponse de {name}",
        reThisGuest: "cet invité",
        reContact: "contact",
        reManualEntry: "Saisie manuelle :",
        reManualEntryNote: "Créée ici car aucune réponse au formulaire n'existait.",
        reRemove: "Supprimer",
        reCurrentlyPaired: "Associée actuellement :",
        reUnpair: "Dissocier",
        reEditDetails: "Modifier les coordonnées",
        rePhone: "Téléphone",
        reEmail: "E-mail",
        reInvitationHelp: "Indiquez tout le monde, séparés par {sep} (ex. {example}). Les modifications s'appliquent à son lien automatiquement.",
        reChildMarker: "(enfant)",
        reExample: "Michel Martin; Emma (enfant)",
        reMaxGuests: "Invités max.",
        reMembersPh: "ex. Michel Martin; Emma (enfant)",
        // Task #935: the guest-list columns (Add contacts, the contact window).
        acGuestNames: "Nom(s) des invités",
        acGuestNamesHint: "Comment ajouter les noms des invités\n• Séparez les noms par un point-virgule (;). Les virgules fonctionnent aussi.\n• Ajoutez (enfant) après le nom d’un enfant, par ex. Léo Martin (enfant).\n• Exemple : Sophie Martin; Marc Martin; Léo Martin (enfant)\n• Le téléphone et l’e-mail de la ligne sont ceux de la première personne de la liste.",
        acGroupName: "Nom du groupe",
        acGroupNameHint: "À qui l'invitation est adressée, par ex. Famille Martin : sur l'enveloppe et dans l'accueil du formulaire ; les messages saluent les noms des invités. Sans noms d'invités, les invités la retrouvent avec ce nom. Laissez vide pour un invité seul.",
        acCsvColumns: "Colonnes CSV : Nom(s) des invités, Nom du groupe, Téléphone, E-mail · facultatif : Invités max, Étiquettes",
        acSameName: "Attention : plusieurs invités ou groupes portent le même nom. Ajoutez une adresse e-mail ou un numéro de téléphone pour chacun afin qu’ils puissent confirmer leur identité lors de la recherche.",
        acLastFirst: "Une seule personne, nom de famille en premier ? Séparez les personnes différentes par \";\".",
        reGroupName: "Nom du groupe",
        reGroupNameHint: "Le destinataire : l'enveloppe et la salutation. Sans personne dans la liste, les invités la retrouvent par ce nom.",
        reMaxGuestsHint: "Laissez vide pour dimensionner le groupe d'après les noms (ou la limite du formulaire).",
        reGroupNamePh: "ex. Famille Martin",
        reGuestNames: "Nom(s) des invités",
        reTags: "Étiquettes",
        reTagsPh: "ex. famille, réception seulement",
        reTagsHelp: "Les étiquettes décident quelles parties de l'événement le lien de cet invité affiche, lorsqu'une partie est réservée à certaines étiquettes.",
        reLoading: "Chargement…",
        reSave: "Enregistrer",
        rePairHeading: "Associer à une réponse existante",
        rePairHelp: "Si {name} a répondu via le formulaire mais que la réponse ne s'est pas associée automatiquement (lien partagé, autre téléphone…), associez-la ici.",
        reLooking: "Recherche des réponses correspondantes…",
        reManualHeading: "Ou saisir manuellement",
        reManualHelp: "À utiliser uniquement si {name} a répondu en dehors du formulaire (message, appel, en personne).",
        reAttending: "Présent",
        reDeclined: "Absent",
        reMaybe: "Peut-être",
        reResponded: "A répondu",
        rePending: "En attente",
        reSaving: "Enregistrement…",
        reSaved: "Enregistré ✓",
        reCouldNotSave: "Enregistrement impossible",
        reNotOnMessage: "Cet invité ne fait plus partie du message, son invitation ne peut donc pas être modifiée ici.",
        rePartyMany: "Son invitation est pour {n} personnes.",
        rePartyOne: "Son invitation est pour une personne.",
        reLoadFailed: "Impossible de charger l'invitation.",
        reMaxInvalid: "Invités max. doit être un nombre entier de 1 à 50.",
        reBrowseAll: "Voir les {n} réponses non associées",
        reBrowseOne: "Voir la réponse non associée",
        reHideList: "Masquer la liste",
        reCandidatesFailed: "Impossible de charger les correspondances possibles.",
        reManualTag: "Manuel",
        reManualTagTip: "Créée comme saisie manuelle sur un autre contact",
        reNoName: "(sans nom)",
        rePair: "Associer",
        reLikely: "Correspondances probables",
        reNoUnpaired: "Aucune réponse non associée sur ce formulaire. Si l'invité a répondu en dehors du formulaire, utilisez les boutons de \"Ou saisir manuellement\".",
        reNoLikely: "Aucune correspondance probable par nom, téléphone ou e-mail. Parcourez la liste complète si vous savez quelle réponse est la sienne.",
        reSearchPh: "Rechercher par nom, téléphone ou e-mail…",
        rePaired: "Réponse associée à {name}",
        reUnpaired: "{name} dissocié",
        reRecorded: "{status} enregistré pour {name}",
        reManualRemoved: "Saisie manuelle supprimée",
        reFailedPair: "Impossible d'associer la réponse",
        reFailedUnpair: "Impossible de dissocier",
        reFailedRecord: "Impossible d'enregistrer la réponse",
        reFailedRemove: "Suppression impossible",
        reUpdatedNumber: "Numéro mis à jour.",
        reUpdatedEmail: "Adresse e-mail mise à jour.",
        reUpdatedDetails: "Coordonnées mises à jour.",
        reResendAsk: "Renvoyer ce message à {dest} ?",
        reMultiMessage: "Ce contact figure dans plusieurs messages : utilisez {addContacts} sur le message à renvoyer.",
        reResend: "Renvoyer",
        reSending: "Envoi…",
        reSentNumber: "Envoyé à son nouveau numéro ; cela apparaîtra bientôt dans l'historique de ce contact.",
        reQueued: "En attente : il partira tout seul dès que WhatsApp aura approuvé ce message, en général en quelques minutes.",
        reSentEmail: "Envoyé à sa nouvelle adresse e-mail ; cela apparaîtra bientôt dans l'historique de ce contact.",
        reSentBoth: "Envoyé à son nouveau numéro et à sa nouvelle adresse ; cela apparaîtra bientôt dans l'historique de ce contact.",
        reResendFailed: "Renvoi impossible, veuillez réessayer.",
        reminderDupSkipped: "Déjà rappelés par un autre message : {n}",
        reminderDupSkippedBy: "Déjà rappelés par « {name} » : {n}",
        reminderDupSkippedTip: "Ces invités ont reçu le même rappel depuis un autre message de cet événement au cours des 20 dernières heures ; celui-ci ne leur a donc pas été envoyé une seconde fois.",
        reminderDupAll: "Tous les invités de ce rappel avaient déjà été rappelés par un autre message",
        remAttentionOne: "1 rappel nécessite votre attention",
        remAttentionMany: "{n} rappels nécessitent votre attention",
        remFailExpiredNote: "WhatsApp n’a pas approuvé à temps le message de ce rappel. Modifiez-le et enregistrez pour réessayer.",
        remFailRejectedNote: "WhatsApp n’a pas accepté le message de ce rappel. Modifiez le message et enregistrez pour le soumettre à nouveau.",
        remAudienceBtn: "Voir à qui ce rappel sera envoyé",
        remAudienceTitle: "Destinataires de ce rappel",
        msgAudienceTitle: "Destinataires de ce message",
        msgAudienceOne: "Ce message est envoyé à cet invité.",
        msgAudienceMany: "Ce message est envoyé à ces {n} invités.",
        remAudienceNowOne: "S’il partait maintenant, il serait envoyé à 1 invité. La liste est arrêtée au moment de l’envoi, elle peut donc encore changer.",
        remAudienceNowMany: "S’il partait maintenant, il serait envoyé à {n} invités. La liste est arrêtée au moment de l’envoi, elle peut donc encore changer.",
        remAudienceFixedOne: "Ce rappel est envoyé à cet invité.",
        remAudienceFixedMany: "Ce rappel est envoyé à ces {n} invités.",
        remAudienceEmpty: "Personne ne correspond pour l’instant. La liste est vérifiée à nouveau au moment de l’envoi du rappel.",
        remAudienceFailed: "Impossible de charger les destinataires de ce rappel. Veuillez réessayer.",
        remAudienceFixedNone: "La liste de ce rappel est vide : il ne sera envoyé à personne.",
        remAudienceMeantOne: "Ce rappel devait être envoyé à cet invité. Il ne partira pas tant que vous ne l’aurez pas modifié et enregistré.",
        remAudienceMeantMany: "Ce rappel devait être envoyé à ces {n} invités. Il ne partira pas tant que vous ne l’aurez pas modifié et enregistré.",
        remFailWaOnlyRefusedNote: "WhatsApp n’a pas accepté le message de ce rappel, et ses invités ne sont joignables que sur WhatsApp : rien n’a donc été envoyé. Une balise de personnalisation tout à la fin du message en est souvent la cause : ajoutez du texte après, puis enregistrez pour le soumettre à nouveau.",
        acSentOf: "{sent} sur {total} envoyés",
        acSkippedUntil: "{n} mis en pause par WhatsApp jusqu’à {time} ; réessayez à ce moment-là",
        acSkipped: "{n} mis de côté",
        acUnreachable: "{n} non envoyés (coordonnées)",
        acFailed: "{n} non joints",
        acDeferred: "{n} partiront lors de la prochaine fenêtre d’envoi",
        acLineOne: "1 invité ajouté après l’envoi",
        acLineMany: "{n} invités ajoutés après l’envoi",
        acLineLast: "dernier ajout : {n}",
        acLineAwaiting: "{n} en attente de l’approbation de WhatsApp",
        acTipAdded: "{when} : {n} ajoutés",
        reResendCooldown: "WhatsApp suspend les messages vers ce numéro jusqu’à {time}. Rien n’a été facturé. Réessayez ensuite, ou envoyez-lui vous-même son lien personnel.",
        reResendCooldownNoTime: "WhatsApp suspend les messages vers ce numéro pour le moment. Rien n’a été facturé. Réessayez plus tard, ou envoyez-lui vous-même son lien personnel.",
        reResendNotSent: "Le message n’a pas pu être envoyé à ces coordonnées. Rien n’a été facturé. Vérifiez le numéro ou l’adresse et réessayez.",
        // Every guest skipped (Meta 24h cooldown, not on WhatsApp...): see _skipSummary and _mergeSendTimeFailures.
        skipNotSent: "Non envoyé",
        skipNotSentTip: "Rien n’est parti : tous les invités de ce message ont été retenus. La note ci-dessous explique pourquoi et ce que vous pouvez faire.",
        skippedOne: "1 ignoré",
        skippedMany: "{n} ignorés",
        skippedTip: "Retenus volontairement, non envoyés. Cliquez pour voir qui et pourquoi.",
        skipCoolOne: "WhatsApp a refusé un message récent à 1 invité, nous suspendons donc les messages à cette personne jusqu’à {time}.",
        skipCoolMany: "WhatsApp a refusé des messages récents à {n} invités, nous suspendons donc les messages à ces personnes. Le premier pourra être relancé après {time}.",
        skipCoolLiftedOne: "WhatsApp a refusé un message récent à 1 invité. Notre pause des messages à cette personne est terminée.",
        skipCoolLiftedMany: "WhatsApp a refusé des messages récents à {n} invités. Notre pause est terminée pour le premier d’entre eux.",
        skipCoolWhy: "WhatsApp refuse les messages marketing aux personnes qui en ont reçu beaucoup récemment, sans dire pour combien de temps. Après un refus, nous suspendons nos messages à ce numéro au lieu de renvoyer. La pause vaut pour tous les événements sur QuikRSVP : le message refusé n’était donc pas forcément le vôtre. Un message retenu n’est pas facturé. Une nouvelle tentative après la pause peut encore être refusée.",
        skipNoWaOne: "1 invité n’est pas sur WhatsApp. Contactez-le par SMS ou e-mail, ou envoyez-lui vous-même son lien personnel.",
        skipNoWaMany: "{n} invités ne sont pas sur WhatsApp. Contactez-les par SMS ou e-mail, ou envoyez-leur vous-même leurs liens personnels.",
        skipUnreachOne: "1 invité n’a pas pu le recevoir : son numéro ou son e-mail est à corriger. Ouvrez la liste pour voir qui et quoi corriger.",
        skipUnreachMany: "{n} invités n’ont pas pu le recevoir : leur numéro ou leur e-mail est à corriger. Ouvrez la liste pour voir qui et quoi corriger.",
        skipOptOutOne: "1 invité a répondu STOP à nos messages WhatsApp, nous ne lui écrivons donc plus par ce biais.",
        skipOptOutMany: "{n} invités ont répondu STOP à nos messages WhatsApp, nous ne leur écrivons donc plus par ce biais.",
        skipUnsubOne: "1 invité s’est désabonné de vos e-mails. Contactez-le plutôt par WhatsApp ou SMS.",
        skipUnsubMany: "{n} invités se sont désabonnés de vos e-mails. Contactez-les plutôt par WhatsApp ou SMS.",
        skipDoRetryAt: "Réessayez après {time}, ou ouvrez Liens personnels et envoyez le lien vous-même.",
        skipDoRetryNow: "Réessayez maintenant, ou ouvrez Liens personnels et envoyez le lien vous-même.",
        skipDoLink: "Ouvrez Liens personnels et envoyez le lien vous-même.",
        skipHandledRepliedOne: "Non envoyé par nous, mais cet invité a déjà répondu.",
        skipHandledRepliedMany: "Non envoyé par nous, mais tous les invités concernés ont déjà répondu.",
        skipHandledLinkOne: "Non envoyé par nous, mais cet invité a déjà ouvert son lien personnel.",
        skipHandledLinkMany: "Non envoyé par nous, mais tous les invités concernés ont déjà ouvert leur lien personnel ou répondu.",
        skipStillBlockedOne: "Les messages à cet invité restent suspendus jusqu’à {time}. Réessayez ensuite, ou envoyez-lui vous-même son lien personnel.",
        skipStillBlockedMany: "Les messages à ces invités restent suspendus. Le premier pourra être relancé après {time} ; d’ici là, envoyez-leur vous-même leurs liens personnels.",
        skipRowCool: "WhatsApp a refusé un message récent à ce numéro (peut-être pour un autre événement), nous suspendons donc tous les messages à ce numéro jusqu’à {time}. Réessayez ensuite, ou envoyez-lui vous-même son lien personnel.",
        skipRowCoolLifted: "WhatsApp a refusé un message récent à ce numéro (peut-être pour un autre événement). Notre pause est terminée : vous pouvez réessayer, ou lui envoyer vous-même son lien personnel.",
        skipRowNoWa: "Ce numéro n’est pas sur WhatsApp. Contactez cette personne par SMS ou e-mail, ou envoyez-lui vous-même son lien personnel.",
        skipRowNoWaNoEmail: "Ce numéro n’est pas sur WhatsApp. Contactez cette personne par SMS, ou envoyez-lui vous-même son lien personnel.",
        fbTipFailedEmail: "WhatsApp n’a pas pu le remettre, il est donc parti par e-mail.",
        fbTipFailedSms: "WhatsApp n’a pas pu le remettre, il est donc parti par SMS.",
        fbTipNoWa: "Ce numéro n’est pas sur WhatsApp, nous avons donc envoyé un e-mail à la place.",
        fbTipCool: "Les messages WhatsApp vers ce numéro sont en pause pour le moment, nous avons donc envoyé un e-mail à la place.",
        fbStateOpened: "Le message a été ouvert.",
        fbStateDelivered: "Le message a été remis.",
        fbStateOnWay: "Le message est en route.",
        skipRowOptOut: "Cet invité a répondu STOP à nos messages WhatsApp, nous ne lui écrivons donc plus par ce biais.",
        skipRowOptOutSms: "Cet invité a répondu STOP à nos SMS, nous ne lui envoyons donc plus de SMS.",
        skipRowUnsub: "Cet invité s’est désabonné de vos e-mails. Contactez-le plutôt par WhatsApp ou SMS.",
        skipRowOther: "Ignoré : ce numéro ne peut pas recevoir de messages WhatsApp pour le moment.",
        skipRowNoDetails: "Non envoyé : ni numéro ni e-mail. Ajoutez-en un et renvoyez, ou envoyez vous-même le lien personnel.",
        skipRowBadNumber: "Non envoyé : ce numéro ne peut pas être composé. Vérifiez les chiffres et l’indicatif du pays, puis renvoyez.",
        skipRowNoEmail: "Non envoyé : prévu par e-mail, mais sans adresse. Ajoutez-en une, ou envoyez-le par WhatsApp.",
        skipRowNoPhone: "Non envoyé : prévu par SMS, mais sans numéro. Ajoutez-en un et renvoyez.",
        skipRowShared: "Non envoyé : même numéro que {name}, et chaque numéro ne reçoit qu’un message. Donnez-lui son propre numéro ou e-mail, ou ajoutez cette personne à l’invitation de {name}.",
        skipRowSharedNoName: "Non envoyé : un autre invité a le même numéro, et chaque numéro ne reçoit qu’un message. Donnez-lui son propre numéro ou e-mail.",
        skipRowNoChannel: "Non envoyé : aucun moyen d’envoi n’est indiqué pour cet invité. Modifiez ses coordonnées et renvoyez.",
        skipBadge: "Ignoré",
        unsubBadge: "Désabonné",
        skipRetrySent: "Envoyé. La carte se mettra à jour dans un instant.",
        skipRetryFailed: "WhatsApp ne l’a pas accepté cette fois. Ouvrez Statut pour voir pourquoi.",
        skipRetryStill: "Rien n’est parti : la pause des messages à ces invités s’applique toujours. Réessayez plus tard, ou envoyez vous-même le lien personnel.",
        skipRetryHeld: "Retenu pour une vérification rapide avant l’envoi. Il partira dès qu’il sera approuvé.",
        skipRetryPaused: "Non envoyé : il est en pause. La carte indique pourquoi.",
        skipRetryNoCredits: "En pause : ajoutez des crédits pour l’envoyer.",
        skipRetryRequested: "Nouvelle tentative demandée. La carte indique où elle en est.",
        skipRetryError: "Impossible de réessayer pour l’instant. Réessayez dans une minute.",
        retryBusy: "Ce message est encore en cours d'envoi. Une fois l'envoi terminé, vérifiez qui doit encore le recevoir et réessayez.",
        statusLoadFailed: "Impossible de charger la liste des invités pour le moment.",
        statusLoadExpired: "Votre session a expiré. Rechargez la page pour voir la liste des invités.",
        statusLoadRetry: "Réessayer",
        statusLoadReload: "Recharger la page",
        statusRefreshFailed: "Impossible d'actualiser la liste des invités. Rechargez la page pour voir le dernier statut.",
        retrySentOne: "Message renvoyé à 1 invité.",
        retrySentMany: "Message renvoyé à {n} invités.",
        retryStillFailedOne: "1 invité n'a toujours pas pu être joint.",
        retryStillFailedMany: "{n} invités n'ont toujours pas pu être joints.",
        retryStaleView: "Un renvoi de ce message s'est terminé après le chargement de cette liste, donc rien n'a été envoyé cette fois. La liste a été actualisée : vérifiez qui doit encore le recevoir et réessayez.",
        acSmsConsentWhy: "Les invités ayant un numéro américain reçoivent ce message par SMS, et les autres numéros +1 ou brésiliens peuvent le recevoir ainsi si WhatsApp échoue.",
        acSmsConsent: "Je confirme que tous les destinataires ont consenti à recevoir des messages SMS d'invitation à mon événement via QuikRSVP.",
        acSmsDisclosure: "Les destinataires peuvent répondre STOP pour se désabonner ou HELP pour obtenir de l'aide. Des frais de données et de messagerie peuvent s'appliquer. La fréquence varie.",
        waRejectedTitle: "WhatsApp n’a pas accepté ce message",
        waOursTitle: "Les messages WhatsApp n’ont pas pu être envoyés",
        waTimeoutTitle: "WhatsApp n’a pas approuvé ce message à temps",
        waSubmitPendingTitle: "Nous transmettons encore votre message à WhatsApp pour approbation",
        waSubmitPendingBody: "Nous n’avons pas encore pu joindre WhatsApp. Nous réessaierons automatiquement et vous écrirons si WhatsApp le refuse. Vous n’avez rien à faire.",
        addWaitWaTip: "Vous pourrez ajouter des contacts dès que votre message sera arrivé chez WhatsApp pour approbation. Nous réessayons ; revenez dans quelques minutes.",
        remCancelled: "Annulé",
        remCancelFormDeleted: "Non envoyé : l’événement a été supprimé.",
        remCancelDeadline: "Non envoyé : les réponses étaient closes avant sa date d’envoi.",
        remCancelDateMoved: "Non envoyé : l’événement a été avancé et son heure était passée.",
        remOnHold: "En attente",
        remHeldPostponed: "En attente : l’événement est reporté. Il suivra la nouvelle date dès que vous l’aurez fixée.",
        remCancelEventCancelled: "Non envoyé : l’événement a été annulé.",
        remCancelPostponedLate: "Non envoyé : l’événement est resté reporté jusqu’à plus d’un jour après l’heure de ce rappel.",
        remCancelInvitation: "Non envoyé : son invitation a été supprimée ou annulée.",
        remCancelLate: "Non envoyé : son invitation était à l’arrêt jusqu’à plus d’un jour après l’heure de ce rappel.",
        remCancelNotSent: "Non envoyé",
        remMovedWithEvent: "déplacé avec la date de l’événement",
        remCancelDeadlineHint: "Repoussez la date limite de réponse ou retirez le lien de confirmation, puis modifiez-le pour l’envoyer.",
        remCancelDateMovedHint: "Modifiez-le pour choisir une nouvelle heure.",
        remSendsSoon: "Envoi dans l’heure",
        remDateMovedPast: "L’événement a été avancé et la nouvelle heure de ce rappel est passée : il ne sera pas envoyé. Modifiez-le pour choisir une nouvelle heure.",
        remSendNowWaiting: "Ce rappel partira dès que son invitation aura atteint tous les invités.",
        remSendNowNoRecipients: "Aucun invité ne correspond à ce rappel : rien n’a été envoyé.",
        remSendNowTaken: "Ce rappel est déjà en cours d’envoi.",
        remSendNowCancelled: "Ce rappel a été annulé car son invitation a été supprimée ou annulée.",
        remSendNowError: "Nous n’avons pas pu déterminer pour l’instant à qui envoyer ce rappel. Rien n’a été envoyé, et un nouvel essai aura lieu automatiquement.",
        moreActions: "Plus d’actions", // the card's ... menu; was missing in every pack
        // Card badges and stats, reminder rows, the Status window, Remind All
        // and the card actions' confirms/toasts (code review 2026-10-07: all
        // English on every dashboard). See cCount for the ...One forms.
        badgeSent: "Envoyé",
        badgeSending: "Envoi en cours",
        badgeProcessing: "En traitement",
        badgeScheduled: "Programmé",
        badgePaused: "En pause",
        badgeCancelled: "Annulé",
        badgeDraft: "Brouillon",
        badgeExpired: "Expiré",
        badgeUnableToSend: "Envoi impossible",
        badgeNoCredits: "Crédits insuffisants",
        badgeInReview: "En vérification",
        scheduledFor: "Programmé pour le {when}",
        sendingAt: "Envoi le {when}",
        expiredTip: "WhatsApp n'a pas approuvé ce message à temps. Utilisez Modifier et renvoyer pour le soumettre à nouveau.",
        noCreditsTip: "Ce message a été mis en pause car votre compte n’a plus de crédits. Achetez des crédits puis renvoyez-le pour livrer les messages restants.",
        inReviewTip: "Nous vérifions les messages des nouveaux comptes avant leur envoi, et quelqu’un vérifie le vôtre en ce moment. Rien n’a été facturé, et votre message et vos contacts sont enregistrés. Il partira automatiquement une fois approuvé ; écrivez-nous dans le chat si vous en avez besoin plus tôt.",
        toggleRemindersTip: "Afficher ou masquer les rappels de ce message",
        showRemindersAria: "Afficher les rappels ({n})",
        hideRemindersAria: "Masquer les rappels ({n})",
        expandAllReminders: "Tout déplier",
        collapseAllReminders: "Tout replier",
        expandAllRemindersTip: "Afficher les rappels de tous les messages",
        collapseAllRemindersTip: "Masquer les rappels de tous les messages",
        remindersOn: "Rappels activés",
        statResponded: "{n} réponses",
        statRespondedOne: "{n} réponse",
        statPending: "{n} en attente",
        statPendingOne: "{n} en attente",
        statSent: "{n} envoyés",
        statSentOne: "{n} envoyé",
        statContacts: "{n} contacts",
        statContactsOne: "{n} contact",
        statNotDelivered: "{n} non remis",
        statNotDeliveredOne: "{n} non remis",
        statRecipients: "{n} destinataires",
        statRecipientsOne: "{n} destinataire",
        allRepliedTip: "Toutes les personnes à qui il a été envoyé avaient déjà répondu : c’est à elles qu’il était destiné.",
        waCouldNotAccept: "WhatsApp n’a pas pu accepter votre message. Modifiez-le et renvoyez-le.",
        untitledMessage: "Message",
        editResendTip: "Corriger le problème et renvoyer les messages WhatsApp",
        timingDayOf: "Le jour même",
        timing1DayBefore: "1 jour avant",
        timing3DaysBefore: "3 jours avant",
        timing1WeekBefore: "1 semaine avant",
        timing2WeeksBefore: "2 semaines avant",
        audNoResponse: "Invités sans réponse",
        audAttending: "Invités présents",
        audMaybe: "Invités incertains",
        audDeclined: "Invités absents",
        audAll: "Tous les contacts",
        remTitleAudience: "Rappel · {audience}",
        remTitleIndex: "Rappel {n}",
        remWasSetFor: "Était prévu le {when}",
        remNoCreditsTip: "Ce rappel n’a pas pu être envoyé car vous n’avez pas assez de crédits. Achetez des crédits et reprenez-le pour l’envoyer.",
        remAddCredits: "Ajoutez des crédits pour envoyer ce rappel",
        remNoRecipients: "Aucun destinataire",
        remNoMatching: "Aucun destinataire ne correspond",
        remResubmitTip: "Votre message modifié a été renvoyé à WhatsApp pour approbation. Il partira une fois approuvé.",
        remResubmitting: "Nouvelle soumission",
        remSendsOnceApproved: "Envoi le {when}, une fois approuvé",
        remWaitingApproval: "En attente de l’approbation WhatsApp",
        remAllRepliedTip: "Toutes les personnes qui ont reçu ce rappel avaient déjà répondu : c’est à elles qu’il était destiné.",
        remExpiredTip: "WhatsApp n’a pas approuvé à temps le message de ce rappel. Modifiez-le et réessayez.",
        remRejectedTip: "WhatsApp a refusé le contenu du message de ce rappel. Modifiez le rappel et enregistrez-le pour le soumettre à nouveau.",
        remFailedTip: "Un problème est survenu lors de l’envoi de ce rappel. Modifiez-le et réessayez.",
        remRejected: "Message refusé",
        remCouldntSend: "Envoi impossible",
        remTimedOut: "Délai d’approbation WhatsApp dépassé",
        remEditToResubmit: "Modifiez et enregistrez pour le soumettre à nouveau",
        remSendFailed: "Échec de l’envoi",
        remWaitingTip: "En attente de l’approbation du message de ce rappel par WhatsApp (Meta) : généralement quelques minutes, parfois plus. Il partira automatiquement une fois approuvé.",
        remSendingSoon: "Envoi imminent",
        remBeingSentTip: "Ce rappel est en cours d’envoi.",
        remDueOnceApproved: "Prévu le {when} · part dès que WhatsApp l’approuve",
        remSendsWhenApproved: "Part dès que WhatsApp l’approuve",
        remDue: "Prévu le {when}",
        remSendingNow: "Envoi en cours…",
        remProcessingTip: "Ce rappel part maintenant. L’e-mail est envoyé tout de suite ; s’il part aussi par WhatsApp, cette partie est envoyée dès que Meta approuve le message, généralement en quelques minutes.",
        remSendingMinute: "Envoi en cours — cela prend généralement une ou deux minutes",
        remSends: "Envoi le {when}",
        remPreviewTip: "Aperçu du message de rappel",
        remEditTip: "Modifier le message, le moment ou les destinataires",
        remStatusTip: "Voir le statut de remise : qui a reçu ce rappel et sa réponse",
        remBuyCreditsTip: "Acheter des crédits pour envoyer ce rappel",
        remResumeCreditsTip: "Reprendre ce rappel une fois que vous avez des crédits",
        remResumeTip: "Reprendre ce rappel",
        remPauseTip: "Mettre ce rappel en pause",
        remDeleteTip: "Supprimer ce rappel",
        msgNotFound: "Message introuvable",
        remFromMainMessage: "Ajoutez les rappels depuis le message principal, pas depuis un autre rappel.",
        renamePrompt: "Renommer ce message :",
        renameEmpty: "Le nom du message ne peut pas être vide",
        renameFailed: "Impossible de renommer le message : ",
        cancelConfirm: "Voulez-vous vraiment annuler le message « {name} » ?\n\nCette action est irréversible. Les messages qui ne sont pas encore partis ne seront pas envoyés.",
        cancelDone: "Message annulé",
        cancelFailed: "Impossible d’annuler le message : ",
        resumeConfirm: "Reprendre le message « {name} » ?\n\nIl sera envoyé le {when}.",
        resumeConfirmNoDate: "Reprendre le message « {name} » ?\n\nIl sera envoyé à la date prévue à l’origine.",
        resumeDone: "Message repris",
        resumeFailed: "Impossible de reprendre le message : ",
        pauseConfirm: "Mettre le message « {name} » en pause ?\n\nIl ne sera pas envoyé à l’heure prévue. Vous pourrez le reprendre ou l’annuler plus tard.",
        pauseDone: "Message mis en pause",
        pauseFailed: "Impossible de mettre le message en pause : ",
        pauseTooLate: "Ce message a fini d'être envoyé avant d'avoir pu être mis en pause.",
        autoRetryPauseConfirm: "Mettre en pause les nouvelles tentatives automatiques pour « {name} » ?",
        autoRetryResumeConfirm: "Reprendre les nouvelles tentatives automatiques pour « {name} » ?",
        autoRetryNotRetriedOne: "{n} contact ne sera pas relancé automatiquement.",
        autoRetryNotRetriedMany: "{n} contacts ne seront pas relancés automatiquement.",
        autoRetryRetriedOne: "{n} contact sera relancé lors de sa prochaine tentative prévue.",
        autoRetryRetriedMany: "{n} contacts seront relancés lors de leur prochaine tentative prévue.",
        autoRetryPaused: "Nouvelles tentatives automatiques en pause",
        autoRetryResumed: "Nouvelles tentatives automatiques reprises",
        autoRetryPauseFailed: "Impossible de mettre en pause les nouvelles tentatives automatiques : ",
        autoRetryResumeFailed: "Impossible de reprendre les nouvelles tentatives automatiques : ",
        sendNowConfirm: "Envoyer le message « {name} » maintenant ?\n\nLa date prévue est passée. Le message partira immédiatement.",
        sendNowDone: "Message envoyé !",
        sendNowFailed: "Impossible d’envoyer le message : ",
        deleteDraftConfirm: "Voulez-vous vraiment supprimer le brouillon « {name} » ?\n\nCette action est irréversible.",
        deleteDraftFailed: "Impossible de supprimer le brouillon : ",
        deleteConfirmActive: "Supprimer définitivement « {name} » ?\n\nCe message a des statistiques de remise et peut avoir des rappels programmés. Les deux seront supprimés.\n\nLes réponses des invités sont conservées (elles sont enregistrées à part).\n\nCette action est irréversible.",
        deleteAlsoGuests: "Ce message a ajouté {n} invités à votre liste qui n’ont pas répondu et ne sont dans aucun autre message.\n\nLes retirer aussi de la liste d’invités ?\n\nOK les retire. Annuler les garde.",
        deleteConfirmUnsent: "Supprimer définitivement « {name} » ?\n\nRien n’a été envoyé avec ce message, vous pouvez donc le supprimer sans risque.\n\nCette action est irréversible.",
        deleteFailed: "Impossible de supprimer le message : ",
        statusAllTitle: "Tous les contacts",
        statusAllSubtitle: "Sur tous les messages",
        statusAllMessages: "📊 Tous les messages",
        statusAllTags: "Toutes les étiquettes", statusNoTag: "Sans étiquette", statusTagsLine: "Étiquettes",
        statusTitleFallback: "Statut du message",
        statusThisMessageOnly: "Seuls les contacts de ce message sont affichés",
        statusAddedAfterLaunch: "{n} ajoutés après l’envoi",
        statusAddedAfterLaunchOne: "{n} ajouté après l’envoi",
        statusContactCount: "{n} contacts",
        statusContactCountOne: "{n} contact",
        exportNone: "Aucun contact à exporter",
        exportedOne: "{n} contact exporté",
        exportedMany: "{n} contacts exportés",
        dlvSent: "Envoyé",
        dlvDelivered: "Remis",
        dlvRead: "Lu",
        dlvOpened: "Ouvert",
        dlvClicked: "Cliqué",
        dlvClickedTip: "Son lien de réponse (ou votre site) a été ouvert depuis l'un de vos messages. À part une réponse, c'est le signe le plus sûr qu'il l'a reçu.",
        dlvReadTip: "Confirmation de lecture de WhatsApp (coches bleues) : il l'a lu.",
        dlvOpenedTip: "Les images de l'e-mail se sont chargées : il l'a probablement vu. Apple Mail et d'autres applications chargent les images automatiquement à la réception, c'est donc un bon signe, pas une preuve.",
        dlvDeliveredEmailTip: "Son fournisseur de messagerie l'a accepté. Nous ne voyons pas dans quel dossier il est arrivé, et beaucoup d'applications (Outlook, Hotmail, messageries pro) ne signalent jamais l'ouverture : il l'a peut-être déjà lu.",
        dlvDeliveredWaTip: "Il est arrivé sur son téléphone (double coche WhatsApp). Pas encore de confirmation de lecture, mais certains les désactivent : il l'a peut-être déjà lu.",
        dlvDeliveredSmsTip: "Il est arrivé sur son téléphone par SMS. Les SMS n'indiquent jamais s'ils ont été lus.",
        dlvSentTip: "Il a été remis au réseau. Pas encore de confirmation qu'il lui est parvenu.",
        dlvBounced: "Rejeté",
        dlvFailed: "Échec",
        dlvSmsSent: "SMS envoyé",
        dlvSmsFailed: "Échec du SMS",
        detailNoEmailFallback: "Aucune adresse e-mail enregistrée, donc l'envoi par e-mail était impossible. Ajoutez-en une avec le crayon pour l'envoyer.",
        deliveryFailed: "Échec de la remise",
        detailEmail: "E-mail",
        detailSmsFallback: "SMS de secours",
        detailRsvp: "Réponse",
        noDetail: "Aucun détail disponible",
        fallbackEmail: "Par e-mail",
        fallbackSms: "Par SMS",
        aMessageFailed: "un message a échoué",
        aMessageFailedTip: "L’un des messages envoyés à ce contact n’a pas pu être remis, mais un autre message l’a bien atteint.",
        checkNumber: "vérifiez ce numéro",
        checkNumberTip: "WhatsApp n’a pas pu remettre le message à ce numéro tel qu’il est enregistré, et le numéro semble incomplet. Mieux vaut le corriger et renvoyer avant de conclure que l’invité n’est pas sur WhatsApp.",
        rowDetailsTip: "Cliquez pour voir tous les détails de remise",
        updateResponseTip: "Modifier la réponse",
        linkColumn: "LIEN",
        remCellOnAria: "Rappels activés — mettre en pause",
        remCellOffAria: "Rappels désactivés — reprendre",
        guestFallback: "Invité",
        addName: "Ajouter un nom",
        editNameTip: "Cliquez pour modifier le nom",
        nameEditPlaceholder: "Nom de l’invité",
        nameEditSave: "Enregistrer",
        nameEditCancel: "Annuler",
        nameSaveFailed: "Impossible d’enregistrer le nom",
        raLoadingAll: "Chargement de tous les contacts...",
        raLoadingPending: "Chargement des contacts en attente...",
        raLoadingYes: "Chargement des réponses « oui »...",
        raLoadingMaybe: "Chargement des réponses « peut-être »...",
        raLoadingNo: "Chargement des réponses « non »...",
        raExcludedAll: "Tous les contacts sont exclus des rappels",
        raExcludedPending: "Tous les invités en attente sont exclus des rappels",
        raExcludedYes: "Toutes les personnes ayant répondu « oui » sont exclues des rappels",
        raExcludedMaybe: "Toutes les personnes ayant répondu « peut-être » sont exclues des rappels",
        raExcludedNo: "Toutes les personnes ayant répondu « non » sont exclues des rappels",
        raSkippedExcludedOne: "{n} invité ignoré, car vous l’avez exclu des rappels",
        raSkippedExcludedMany: "{n} invités ignorés, car vous les avez exclus des rappels",
        raLoadFailed: "Impossible de charger les contacts",
        raNoneSelected: "Aucun destinataire sélectionné — annulé.",
        raNoValidRecipients: "Aucun destinataire valide à qui envoyer.",
        errLabel_131026: "WhatsApp n’a pas pu le remettre",
        errLabel_131049: "bloqué par la politique de Meta",
        errLabel_131047: "fenêtre de 24 h expirée",
        errLabel_131048: "bloqué pour spam",
        errLabel_131056: "limite d’envoi",
        errLabel_131058: "problème de message",
        errLabel_131061: "message suspendu par Meta",
        errLabel_131062: "message désactivé par Meta",
        errLabel_132001: "problème de message",
        errLabel_130472: "retenu par un test de Meta",
        errLabel_130497: "WhatsApp bloqué dans ce pays",
        errLabel_131063: "pas sur WhatsApp",
        errLabel_130429: "limite d’envoi atteinte",
        errLabel_marketing_limit_reached: "limite marketing de Meta",
        errLabel_BREVO_REJECTED: "refusé par le fournisseur SMS",
        errLabel_unknown: "échec",
        errTip_131049: "Meta a bloqué ce message marketing (limite liée à sa politique ou à l’engagement) ; ce n’est pas un désabonnement de l’invité. Il utilise WhatsApp et reste joignable : un renvoi après environ un jour fonctionne à peu près une fois sur deux.",
        errTip_131026: "Ce numéro n’a pas pu recevoir le message : il n’utilise peut-être pas WhatsApp, ou un chiffre est erroné. Renvoyer aide rarement (environ 1 fois sur 20), il vaut donc mieux le joindre autrement.",
        errTip_131048: "Numéro de téléphone restreint",
        errTip_131056: "Limite d’envoi dépassée",
        errTip_131058: "Message non approuvé",
        errTip_131047: "Fenêtre de 24 heures expirée",
        errTip_131061: "Meta a suspendu le message WhatsApp utilisé ici, il n’a donc pas pu être remis. Cela concerne tous les invités de ce message, pas seulement celui-ci : le message doit être recréé.",
        errTip_131062: "Message désactivé",
        errTip_132001: "WhatsApp a refusé le message (il n’existait pas dans la langue d’envoi), rien n’a donc été remis. Recréez le message du rappel et renvoyez-le.",
        errTip_marketing_limit_reached: "Meta a suspendu la remise des messages marketing à ce numéro ; ce n’est pas un désabonnement de l’invité",
        errTip_130472: "Meta a retenu ce message dans le cadre de l’une de ses propres expériences de remise. Le numéro n’a aucun problème, mais un renvoi subira la même retenue : joignez l’invité autrement.",
        errTip_130497: "Meta n’autorise pas pour l’instant notre compte WhatsApp à écrire aux numéros de ce pays : un renvoi échouera de la même façon. Joignez l’invité par e-mail ou avec son lien personnel.",
        errTip_131063: "Ce numéro n’est pas inscrit sur WhatsApp, il ne peut donc pas recevoir l’invitation. Essayez un autre numéro ou joignez l’invité autrement.",
        errTip_130429: "Nous avons atteint notre propre limite d’envoi pour le moment ; le numéro n’a aucun problème. Un renvoi d’ici peu devrait passer.",
        errTip_BREVO_REJECTED: "Le fournisseur SMS a refusé ce message après l’avoir accepté (généralement un problème temporaire de solde d’envoi de notre côté). Cela vaut la peine de réessayer.",
        errTip_NOT_FOUND: "Contact introuvable ou invalide",
        errTipUnknown: "Erreur inconnue",
        // Card status lines, failure reasons and WhatsApp refusals (item M2, 2026-10-07).
        chEmail: "E-mail",
        chText: "SMS",
        chWhatsApp: "WhatsApp",
        stSentN: "{n} envoyés",
        stAtSendTime: "part à l'heure prévue",
        stGoingOut: "part dans les prochaines minutes",
        stWaWaiting: "en attente de l'approbation du message par WhatsApp (en général quelques minutes)",
        stWaWaitingLong: "toujours en attente de l'approbation du message par WhatsApp. Cela prend plus de temps que d'habitude ; il partira tout seul dès qu'il sera approuvé",
        stWaSubmitting: "envoi pour approbation toujours en cours",
        stRetrying: "Rien n'est encore parti. Nous réessayons dans quelques minutes.",
        stSendingSoon: "Part dans les prochaines minutes.",
        stWaRefused: "non accepté. Modifiez-le et renvoyez-le.",
        stWaRefusedBelow: "non accepté. Modifiez-le et renvoyez-le (la raison est indiquée ci-dessous).",
        failFormMissing: "Le formulaire de ce message n'existe plus. Enregistrez à nouveau le formulaire, puis créez un nouveau message.",
        failWaOnly: "WhatsApp n'a pas accepté le message, et tous ses invités ne sont joignables que par WhatsApp : rien n'a donc été envoyé. Modifiez-le et renvoyez-le.",
        failWaRefused: "WhatsApp n'a pas accepté le message : rien n'a donc été envoyé. Modifiez-le et renvoyez-le.",
        failNothingSent: "Rien n'a pu être envoyé après plusieurs essais. Cliquez sur Modifier et renvoyer pour réessayer. Si cela se reproduit, écrivez-nous dans le chat.",
        failNoMethod: "Les coordonnées de vos invités ne sont pas parvenues à notre étape d'envoi. Le problème venait de chez nous, pas de votre liste d'invités. Cliquez sur Modifier et renvoyer pour réessayer, et écrivez-nous dans le chat si cela se reproduit.",
        failSendError: "Un problème est survenu de notre côté pendant l'envoi. Vous n'y êtes pour rien. Cliquez sur Modifier et renvoyer pour réessayer, et écrivez-nous dans le chat si cela se reproduit.",
        failGeneric: "Nous n'avons pas pu envoyer ce message. Cliquez sur Modifier et renvoyer pour réessayer, ou écrivez-nous dans le chat.",
        err131053Label: "image impossible à charger",
        err131053Tip: "WhatsApp n'a pas pu récupérer l'image de l'invitation, le message n'a donc pas été remis. Le renvoyer fonctionne généralement.",
        errSmsBouncedLabel: "SMS non remis",
        errSmsBouncedTip: "Le réseau téléphonique n'a pas remis le SMS. Le numéro est peut-être erroné, fixe ou éteint. Vérifiez le numéro, écrivez à l'invité par e-mail, ou copiez son lien personnel et envoyez-le vous-même.",
        errSmsFailedLabel: "SMS non envoyé",
        errSmsFailedTip: "Notre fournisseur de SMS n'a pas envoyé ce message. Le renvoyer fonctionne généralement.",
        errSmsNonGsmLabel: "impossible par SMS",
        errSmsNonGsmTip: "Ce message n'a pas de texte en anglais, et un SMS ne peut transporter que celui-ci. Ajoutez une version anglaise avec Modifier et renvoyer, ou contactez cet invité par e-mail.",
        errNonUsLabel: "SMS vers les États-Unis uniquement",
        errNonUsTip: "Nous n'envoyons des SMS qu'aux numéros des États-Unis. Contactez cet invité par WhatsApp ou e-mail.",
        errEmailHardLabel: "adresse e-mail invalide",
        errEmailHardTip: "Cette adresse e-mail n'existe pas ou refuse les messages. Vérifiez l'orthographe, ou contactez l'invité par SMS ou WhatsApp.",
        errEmailSoftLabel: "e-mail refusé pour l'instant",
        errEmailSoftTip: "La boîte de l'invité n'a pas accepté l'e-mail cette fois (souvent parce qu'elle est pleine ou momentanément indisponible). Le renvoyer plus tard fonctionne généralement.",
        errEmailBounceLabel: "e-mail rejeté",
        errEmailBounceTip: "L'e-mail n'a pas pu être remis à cette adresse. Vérifiez l'orthographe, ou contactez l'invité par SMS ou WhatsApp.",
        errGenericTip: "Ce message n'a pas pu être remis. Contactez cet invité autrement, ou renvoyez-le plus tard.",
        fbRetryUntilAll: "WhatsApp retient les messages marketing qui leur sont destinés jusqu'à {time}. Réessayez après.",
        fbRetryUntilSome: "WhatsApp retient les messages marketing destinés à certains d'entre eux jusqu'à {time}. Réessayez après.",
        fbChannelSub: "Ce message ne leur parvient pas par le canal utilisé. Relancez-les par e-mail, ou copiez le lien personnel de chaque invité dans la liste ci-dessous.",
        fbChannelNoEmail: "Aucun de ces invités n'a d'adresse e-mail. Copiez le lien personnel de chacun dans la liste ci-dessous et envoyez-le vous-même.",
        fbChannelSomeNoEmail: "{n} de ces invités n'ont pas d'adresse e-mail : l'e-mail ne leur parviendra pas. Cette liste se ferme à l'ouverture de l'e-mail, copiez donc d'abord leurs liens personnels. Continuer avec les {m} qui en ont une ?",
        ndTipWa: "WhatsApp n'a pas remis le message à {n} de vos invités. Meta retient parfois des messages pour lutter contre le spam, ou le numéro n'est pas sur WhatsApp. Ouvrez la liste pour voir pourquoi, et contactez-les par e-mail ou SMS.",
        ndTipSms: "{n} de vos SMS n'ont pas été remis, en général à cause d'un numéro erroné ou fixe. Ouvrez la liste pour voir pourquoi, puis vérifiez le numéro, écrivez-leur par e-mail ou copiez leur lien personnel.",
        ndTipEmail: "{n} de vos e-mails ont été rejetés, en général à cause d'une adresse mal saisie. Ouvrez la liste pour voir pourquoi, et contactez-les par SMS ou WhatsApp.",
        ndTipMixed: "Non remis : {parts}. Ouvrez la liste pour voir la raison pour chaque invité.",
        ndTipGeneric: "{n} de vos invités n'ont pas reçu ce message. Ouvrez la liste pour voir pourquoi.",
        ndTipWaOne: "WhatsApp n'a pas remis le message à l'un de vos invités. Meta retient parfois des messages pour lutter contre le spam, ou le numéro n'est pas sur WhatsApp. Ouvrez la liste pour voir pourquoi, et contactez cette personne par e-mail ou SMS.",
        ndTipSmsOne: "L'un de vos SMS n'a pas été remis, en général à cause d'un numéro erroné ou fixe. Ouvrez la liste pour voir pourquoi, puis vérifiez le numéro, écrivez-lui par e-mail ou copiez son lien personnel.",
        ndTipEmailOne: "L'un de vos e-mails a été rejeté, en général à cause d'une adresse mal saisie. Ouvrez la liste pour voir pourquoi, et contactez cette personne par SMS ou WhatsApp.",
        ndTipGenericOne: "L'un de vos invités n'a pas reçu ce message. Ouvrez la liste pour voir pourquoi.",
        tplErr_ours: "Un problème est survenu de notre côté, et vous n'y êtes pour rien. Ouvrez Modifier et renvoyer et renvoyez-le depuis là (inutile de changer quoi que ce soit). Si cela se reproduit, prévenez-nous et nous corrigerons.",
        tplErr_trailingParam: "Votre message ne peut pas SE TERMINER par une balise de personnalisation comme {{name}} ou {{table_number}}. WhatsApp ne l'autorise pas. Ajoutez un mot ou un point après la balise et renvoyez-le.",
        tplErr_waLink: "WhatsApp n'autorise pas un bouton qui renvoie vers WhatsApp. Faites pointer ce bouton ailleurs et renvoyez le message.",
        tplErr_imageFormat: "Pour WhatsApp, l'image de votre invitation doit être au format JPG ou PNG. Téléversez-la à nouveau dans ce format et renvoyez le message.",
        tplErr_imageProcess: "WhatsApp n'a pas pu traiter l'image de votre invitation. Essayez-en une autre (JPG ou PNG, moins de 5 Mo) et renvoyez le message.",
        tplErr_category: "WhatsApp considère votre message comme d'un autre type que celui sous lequel nous l'avons classé. Renvoyez-le et il devrait passer.",
        tplErr_layout: "WhatsApp n'a pas pu lire la mise en page de votre message. C'est généralement une {{balise}} seule sur sa ligne. Placez-la dans une phrase et renvoyez le message.",
        tplErr_duplicateName: "WhatsApp a déjà un message enregistré sous ce nom. Renommez le message et renvoyez-le.",
        tplErr_rateLimit: "Vous avez atteint pour l'instant la limite de nouveaux messages de WhatsApp. Patientez un peu et renvoyez-le. Rien n'est perdu.",
        tplErr_noReason: "WhatsApp n'a pas approuvé le texte de ce message. Modifiez-le et renvoyez-le.",
        tplLangsHeadOne: "WhatsApp a refusé la version en {langs} de votre message.",
        tplLangsHeadMany: "WhatsApp a refusé ces versions de votre message : {langs}.",
        tplLangsHeadUnknown: "WhatsApp a refusé l'une des langues de votre message.",
        tplLangsSaid: "WhatsApp a indiqué : « {text} ».",
        tplLangsEditOne: "Modifiez cette traduction et renvoyez-la.",
        tplLangsEditMany: "Modifiez ces traductions et renvoyez-les.",
        langCombined: "deux langues combinées",
        failWaRefusedPartial: "WhatsApp n'a pas accepté le message : vos invités WhatsApp ne l'ont donc pas reçu. Vos invités par e-mail et par SMS l'ont déjà, ne le leur renvoyez pas. Modifiez le texte et renvoyez-le à vos invités WhatsApp.",
        fbPermanentSubEmail: "Le numéro ou l'adresse e-mail ne fonctionne pas. Corrigez-le dans la liste des invités, ou copiez le lien personnel de l'invité et envoyez-le vous-même.",
        fbHeldBack: "{n} non envoyés volontairement",
        fbHeldBackSub: "Nous les avons retenus : l'invité nous a demandé d'arrêter, s'est désabonné, n'est pas sur WhatsApp, ou WhatsApp a mis le numéro en pause. Chaque ligne ci-dessous indique la raison. Nous n'écrivons plus jamais aux invités qui nous ont demandé d'arrêter.",
        tplLangsHeadCombined: "WhatsApp a refusé la version bilingue combinée de votre message.",
        remFailFormMissing: "Le formulaire de ce rappel n'existe plus. Enregistrez à nouveau le formulaire, puis modifiez le rappel avec le crayon et enregistrez-le.",
        remFailWaOnly: "WhatsApp n'a pas accepté le rappel, et tous ses invités ne sont joignables que par WhatsApp : rien n'a donc été envoyé. Modifiez le rappel avec le crayon et enregistrez-le pour le soumettre à nouveau.",
        remFailWaRefused: "WhatsApp n'a pas accepté le rappel : rien n'a donc été envoyé. Modifiez le rappel avec le crayon et enregistrez-le pour le soumettre à nouveau.",
        remFailWaRefusedPartial: "WhatsApp n'a pas accepté le rappel : vos invités WhatsApp ne l'ont donc pas reçu. Vos invités par e-mail et par SMS l'ont déjà. Modifiez le rappel avec le crayon et enregistrez-le pour le soumettre à nouveau.",
        remFailNothingSent: "Rien n'a pu être envoyé après plusieurs essais. Modifiez le rappel avec le crayon et enregistrez-le pour réessayer. Si cela se reproduit, écrivez-nous dans le chat.",
        remFailNoMethod: "Les coordonnées de vos invités ne sont pas parvenues à notre étape d'envoi. Le problème venait de chez nous, pas de votre liste d'invités. Modifiez le rappel avec le crayon et enregistrez-le pour réessayer, et écrivez-nous dans le chat si cela se reproduit.",
        remFailSendError: "Un problème est survenu de notre côté pendant l'envoi. Vous n'y êtes pour rien. Modifiez le rappel avec le crayon et enregistrez-le pour réessayer, et écrivez-nous dans le chat si cela se reproduit.",
        remFailGeneric: "Nous n'avons pas pu envoyer ce rappel. Modifiez-le avec le crayon et enregistrez-le pour réessayer, ou écrivez-nous dans le chat.",
        // Provider outage pause: see _providerPauseBadgeHtml.
        providerPaused: "Envoi interrompu",
        providerPausedTip: "Un problème chez notre fournisseur de messagerie a interrompu ce message avant qu'il n'atteigne tout le monde. Rien ne vous a été facturé pour les invités qui ne l'ont pas encore reçu. Notre équipe a été prévenue et le leur enverra dès que ce sera réglé. Vous n'avez rien à faire.",
        providerPausedPhrase: "En attente de notre fournisseur de messagerie",
        providerResumeConfirm: "Réessayer d'envoyer ce message maintenant ? Seuls les invités qui ne l'ont pas encore reçu le recevront.",
        providerResumed: "Redémarrage en cours. Les invités qui ne l'ont pas encore reçu l'auront d'ici quelques minutes.",
        providerResumeFailed: "Nous n'avons pas pu relancer ce message. Veuillez réessayer.",
        providerPausedPhraseAuto: "Nouvel essai automatique prévu",
        providerPausedTipAuto: "Un problème passager chez notre fournisseur de messagerie a interrompu ce message avant qu'il n'atteigne tout le monde. Rien ne vous a été facturé pour les invités qui ne l'ont pas encore reçu. Nous réessaierons automatiquement d'ici peu, et eux seuls le recevront. Vous pouvez aussi réessayer dès maintenant avec « {resume} ».",
        providerResuming: "Redémarrage en cours. Cela prend généralement quelques minutes.",
        providerResumedTeam: "Nous réessayons maintenant. Si notre fournisseur ne fonctionne toujours pas, l'envoi s'arrêtera de nouveau et partira dès que notre équipe aura réglé le problème. Vous n'avez rien d'autre à faire.",
        sendRestartResumes: "Le serveur a redémarré pendant l’envoi de ce message. Les invités qui l’ont déjà reçu ne le recevront pas deux fois, et les autres le recevront automatiquement d’ici quelques minutes.",
        sendRestartRetry: "Le serveur a redémarré pendant l’envoi de ce message. Les invités qui l’ont déjà reçu ne le recevront pas deux fois. Renvoyez-le dans une minute pour joindre les autres.",
        // Outage-held guests: see _outageHeldNoteHtml.
        heldPhrase: "{n} invités l’ont peut-être déjà reçu",
        heldPhraseOne: "1 invité l’a peut-être déjà reçu",
        heldWhy: "Pendant la panne de notre fournisseur de messagerie, leur WhatsApp ou leur SMS est resté sans réponse : il est donc peut-être arrivé quand même. Pour que personne ne le reçoive deux fois, nous ne le leur avons pas renvoyé. Tous les autres le reçoivent normalement.",
        heldMore: "et {n} autres",
        heldSend: "L’envoyer aussi à ces {n} invités",
        heldSendOne: "L’envoyer aussi à cet invité",
        heldSendDone: "C’est fait. Nous le leur avons envoyé.",
        heldSendWithResume: "C’est fait. Ils le recevront avec tous les autres à la reprise de l’envoi.",
        heldSendBusy: "Ce message est en cours d’envoi. Réessayez quand il sera terminé.",
        heldSendPaused: "Reprenez d’abord ce message, puis envoyez-le-leur.",
        heldSendFailed: "Impossible de le leur envoyer pour le moment. Réessayez dans quelques minutes.",
        // Interrupted Add Contacts (PMb).
        errInterruptedLabel: "non envoyé (interrompu)",
        errInterruptedTip: "L’envoi s’est arrêté avant d’atteindre cet invité, car notre serveur a redémarré. Rien ne lui a été envoyé ni facturé ; vous pouvez le lui renvoyer sans risque, et ce renvoi est facturé comme tout envoi.",
        acNotSent: "{n} non envoyés : l’envoi a été interrompu. Renvoyez-le-leur depuis Statut",
        addRestartRetry: "Notre serveur redémarrait : rien n’a été ajouté ni envoyé. Réessayez dans une minute.",
        // Retry, resend explainer, ghost-template recovery, follow-up, CSV export.
        retryWhichMessage: "Impossible de savoir sur quel message ces envois ont échoué. Choisissez un message en haut de cette fenêtre et réessayez.",
        retryAMessage: "un message",
        retryScopeOne: "Nous leur renverrons « {n} ».",
        retryScopeMany: "Nous renverrons chacun des {n} messages qui ont échoué, à ces invités uniquement.",
        retryConfirmOne: "Renvoyer le même message à 1 invité ?",
        retryConfirmMany: "Renvoyer le même message à {n} invités ?",
        retryConfirmNobody: "Personne l'ayant déjà reçu n'est recontacté.",
        retryConfirmFree: "Le renvoi est gratuit : le crédit de chaque message échoué a déjà été rendu.",
        retryResending: "Renvoi…",
        nothingTryLater: "Réessayez dans quelques heures.",
        nothingTryAfter: "Réessayez après : {n}.",
        nothingCooledOne: "WhatsApp a refusé ce message pour 1 invité au cours des dernières 24 heures : il a donc été laissé de côté plutôt que relancé.",
        nothingCooledMany: "WhatsApp a refusé ce message pour {n} invités au cours des dernières 24 heures : ils ont donc été laissés de côté plutôt que relancés.",
        nothingDeadOne: "1 invité n'est pas sur WhatsApp : un renvoi ne peut pas le joindre. Essayez plutôt l'e-mail ou le SMS.",
        nothingDeadMany: "{n} invités ne sont pas sur WhatsApp : un renvoi ne peut pas les joindre. Essayez plutôt l'e-mail ou le SMS.",
        nothingReachedOne: "1 invité a déjà ce message (il lui est parvenu autrement), il n'a donc pas été renvoyé.",
        nothingReachedMany: "{n} invités ont déjà ce message (il leur est parvenu autrement), il n'a donc pas été renvoyé.",
        nothingNobody: "Personne n'a pu être joint cette fois-ci. Cela vaut la peine de réessayer plus tard.",
        ghostConfirm: "Reconstruire le modèle WhatsApp de ce message et le renvoyer aux invités qui ne l'ont pas reçu ?\n\nCela peut prendre quelques minutes. Aucun crédit supplémentaire n'est facturé.",
        ghostStarting: "Démarrage…",
        ghostRecovering: "Récupération…",
        ghostButton: "Recréer et renvoyer",
        ghostStarted: "Reconstruction du modèle WhatsApp et renvoi en cours : cela prend quelques minutes. Nous vous enverrons un e-mail une fois terminé.",
        ghostAlready: "Une récupération est déjà en cours.",
        ghostNotNeeded: "Ce message ne présente plus le problème de modèle WhatsApp : il n'y a rien à reconstruire.",
        ghostStartFailed: "Impossible de lancer la récupération. Réessayez dans quelques minutes.",
        ghostFixed: "Modèle WhatsApp corrigé.",
        ghostResent: "Renvoyé.",
        ghostResentToOne: "Renvoyé à 1 invité.",
        ghostResentTo: "Renvoyé à {n} invités.",
        ghostEscalated: "Le modèle reconstruit échoue toujours à la vérification de WhatsApp. Notre équipe a été prévenue. Veuillez contacter le support.",
        ghostIssue: "Problème de modèle WhatsApp.",
        ghostIssueBody: "{n} messages n'ont pas pu être remis car WhatsApp a refusé le modèle. Un simple renvoi n'y changera rien : reconstruisez le modèle et renvoyez.",
        followUpLoadedOne: "1 contact chargé pour la relance",
        followUpLoaded: "{n} contacts chargés pour la relance",
        csvPhone: "Téléphone",
        csvEmail: "E-mail",
        csvDeliveryStatus: "Statut de remise",
        csvDeliveryIssue: "Problème de remise",
        csvRsvp: "Réponse RSVP",
        csvGuests: "Nombre d'invités",
        csvPending: "En attente",
        csvNotSent: "Non envoyé",
        csvAttending: "Présent",
        csvDeclined: "Absent",
        csvMaybe: "Peut-être",
        csvResponded: "A répondu",
        csvNoResponse: "Pas encore de réponse",
    },
    de: {
        remWaOnlyWaitingTip: "Ihre Gäste per E-Mail und SMS haben diese Erinnerung bereits. Ihre WhatsApp-Gäste erhalten sie automatisch, sobald WhatsApp (Meta) die Nachricht freigibt.",
        remWaOnlyWaiting: "WhatsApp-Gäste erhalten sie nach der Freigabe",
        quietHoldSystemTip: "Diese Erinnerung wäre für einige Ihrer Gäste mitten in der Nacht gekommen. Sie wartet deshalb, bis es für alle Tag ist. Sie müssen nichts tun.",
        quietHoldLateTip: "WhatsApp hat Ihre Nachricht freigegeben, als es für einige Ihrer Gäste Nacht war. Damit niemand geweckt wird, geht sie raus, sobald es für alle Tag ist. Sie müssen nichts tun.",
        remWaLost: "WhatsApp nicht gesendet",
        remWaLostTip: "WhatsApp hat die Nachricht dieser Erinnerung nicht akzeptiert, daher haben Ihre WhatsApp-Gäste sie nicht erhalten. Ihre Gäste per E-Mail und SMS haben sie bereits. Bearbeiten und speichern Sie die Erinnerung, um sie an Ihre WhatsApp-Gäste zu senden.",
        remWaLostTimeoutTip: "WhatsApp hat die Nachricht dieser Erinnerung nicht innerhalb von 48 Stunden nach dem Sendezeitpunkt freigegeben, daher haben Ihre WhatsApp-Gäste sie nicht erhalten. Ihre Gäste per E-Mail und SMS haben sie bereits. Bearbeiten und speichern Sie die Erinnerung, um sie erneut einzureichen – Sie müssen nichts ändern.",
        remWaLostPhrase: "Bearbeiten und speichern, um sie an WhatsApp-Gäste zu senden",
        waTimeoutBody: "WhatsApp hat diese Nachricht nicht innerhalb von 48 Stunden nach dem Sendezeitpunkt freigegeben, daher haben Ihre WhatsApp-Gäste sie nicht erhalten.",
        waOthersHaveIt: "Ihre Gäste per E-Mail und SMS haben sie bereits. „{editResend}“ sendet sie an Ihre WhatsApp-Gäste.",
        waRejectedBadgeTip: "WhatsApp hat diese Nachricht nicht angenommen. Bearbeiten Sie die Nachricht und reichen Sie sie erneut bei WhatsApp ein.",
        previewTitle: "Nachrichtenvorschau",
        previewNoBody: "(kein Nachrichtentext gespeichert)",
        previewNoteOne: "In WhatsApp erscheint dies als die Schaltfläche oben (zum Testen des Links anklicken).",
        previewNoteMany: "In WhatsApp und per E-Mail erscheinen diese als Schaltflächen (jede anklicken, um den Link zu testen).",
        previewSmsNote: "Per SMS kommt der Link als Text an:",
        previewSampleFor: "Beispiel für {name} — Variablen wie {{first_name}} werden pro Gast eingesetzt.",
        previewClone: "Als neuen Entwurf duplizieren",
        close: "Schließen",
        addContacts: "Kontakte hinzufügen",
        preview: "Vorschau", status: "Status", remind: "Erinnern", add: "Hinzufügen", reminders: "Erinnerungen", remindersNoneTip: "Noch keine Erinnerungen — jetzt eine senden",
        duplicate: "Duplizieren", delete: "Löschen", edit: "Bearbeiten", editResend: "Bearbeiten & erneut senden",
        personalLinks: "Persönliche Links", sharedByHand: "Persönlicher Link", copyPersonalLink: "Persönlichen Link kopieren", linkCopied: "Persönlicher Link kopiert",
        sendOnWhatsApp: "Über WhatsApp vom eigenen Telefon senden", waWalkBtn: "Von meinem WhatsApp senden", waWalkIntro: "Unser WhatsApp konnte diese Gäste nicht erreichen. Senden Sie jedem die Nachricht von Ihrem eigenen WhatsApp: öffnen, auf Senden tippen und für den nächsten zurückkommen.", waWalkCounter: "Gast {i} von {n}", waWalkOpen: "WhatsApp öffnen", waWalkSkip: "Überspringen", waWalkClose: "Schließen", waWalkDone: "Fertig. Sie haben WhatsApp für {k} von {n} Gästen geöffnet.",
        hostSentBadge: "Von Ihrem WhatsApp gesendet", hostSentTip: "Sie haben Ihr eigenes WhatsApp geöffnet, um diese Nachricht zu senden. Wir sehen nicht in WhatsApp hinein, daher ist die Zustellung nicht bestätigt.", waWalkCardBtn: "Von meinem WhatsApp senden ({n})", waWalkCardTip: "Unser WhatsApp konnte diese Gäste nicht erreichen. Senden Sie ihnen diese Nachricht nacheinander von Ihrem eigenen WhatsApp.",
        waWalkImageNote: "WhatsApp öffnet sich nur mit dem Text. Um Ihr Einladungsbild mitzuschicken, hängen Sie es in WhatsApp an, bevor Sie auf Senden tippen.", waWalkImageOpen: "Bild zum Speichern öffnen",
        addedSkippedOptedOut: "{n} der hinzugefügten Gäste übersprungen: Erinnerungen sind ausgeschaltet", addedSkippedResponded: "{n} der hinzugefügten Gäste übersprungen: bereits geantwortet",
        tagContacts: "Alle in dieser Nachricht taggen", tagContactsPrompt: "Tags für alle Gäste dieser Nachricht, durch Kommas getrennt (z. B. Familie, nur Empfang):", tagContactsDone: "{n} Gäste getaggt", tagContactsFailed: "Die Gäste konnten nicht getaggt werden",
        continueBtn: "Fortfahren", pause: "Pausieren", cancel: "Abbrechen", sendNow: "Jetzt senden",
        sendTest: "Test senden",
        sendTestTip: "Senden Sie sich eine Kopie dieser Nachricht, um genau zu sehen, was Ihre Gäste bekommen",
        sendTestSending: "Test wird gesendet…",
        sendTestDone: "Test an Sie gesendet",
        sendTestEmailCopyOnly: "Die E-Mail ist nur eine Kopie für Sie — Ihre Gäste erhalten keine E-Mail.",
        sendTestNoMessage: "Fügen Sie eine Nachricht hinzu und wählen Sie den Versandweg, dann können Sie sich einen Test senden",
        sendTestFailed: "Test konnte nicht gesendet werden",
        sendTestCreditReturned: "Das Guthaben für diesen Test wurde Ihnen wieder gutgeschrieben.",
        sendTestCost: "Der erste Test dieser Nachricht ist kostenlos. Danach kostet jeder Test per SMS oder WhatsApp 1 Guthaben – E-Mail-Tests sind immer kostenlos.",
        sendTestAgain: "Test erneut senden",
        sendTestAgainTip: "Schicken Sie sich noch eine Kopie — nützlich, nachdem Sie den Text geändert haben",
        sendTestPhoneTitle: "Wohin soll der Test gehen?",
        sendTestPhoneHint: "Ihr Konto hat keine Mobilnummer. Geben Sie eine ein, an die der Test gehen soll. Wir merken sie uns.",
        sendTestPhoneInvalid: "Das sieht nicht nach einer Mobilnummer aus. Nutzen Sie die internationale Form, zum Beispiel +14155550123.",
        resume: "Fortsetzen", buyCredits: "Guthaben kaufen", retry: "Erneut versuchen",
        previewTip: "Vorschau des Nachrichtentexts",
        statusTip: "Sehen, wer die Nachricht erhalten hat und wie er geantwortet hat",
        remindTip: "Eine Nachfassnachricht an die Gäste dieser Nachricht senden",
        addTip: "Weitere Kontakte zu dieser Nachricht hinzufügen",
        addScheduledTip: "Weitere Kontakte hinzufügen – sie erhalten die Nachricht zur geplanten Sendezeit",
        addDisabledTip: "Kontakte können nicht hinzugefügt werden – korrigieren Sie zuerst diese Nachricht über Bearbeiten & erneut senden (die WhatsApp-Vorlage hat Meta nie erreicht).",
        duplicateTip: "Diese Nachricht (Bild, Text, Sprachen, Link) in einer brandneuen Nachricht wiederverwenden",
        deleteTip: "Diese Nachricht dauerhaft aus Ihrem Verlauf löschen",
        pauseTip: "Diese Nachricht pausieren",
        buyCreditsTip: "Laden Sie Ihr Nachrichtenguthaben auf und setzen Sie dann diese Nachricht fort",
        retryTip: "Erneut versuchen zu senden (klappt, sobald Sie genug Guthaben haben)",
        manageRemindersTip: "Geplante Erinnerungen verwalten",
        scheduleRemindersTip: "Erinnerungen planen",
        resumeAutoRetryTip: "Automatische Wiederholung fortsetzen ({n} ausstehend)",
        pauseAutoRetryTip: "Automatische Wiederholung pausieren ({n} ausstehend)",
        remindPending: "Ausstehende Gäste", remindPendingSub: "Haben noch nicht geantwortet",
        remindMaybe: "Vielleicht-Antworten", remindMaybeSub: "Sagten vielleicht – zur Bestätigung anstupsen",
        remindYes: "Ja-Antworten", remindYesSub: "Erinnerung am Tag, Veranstaltungsort-Update usw.",
        remindNo: "Absagen", remindNoSub: "Können nicht kommen — z. B. Livestream-Link oder Dankeschön senden",
        remindRemovedOne: "Der entfernte Gast erhält diese Erinnerung nicht.", remindRemovedMany: "Die {n} entfernten Gäste erhalten diese Erinnerung nicht.",
        // Response-edit (pencil) window: _openResponseEditModal and what it opens.
        reTitle: "Antwort von {name} bearbeiten",
        reThisGuest: "diesen Gast",
        reContact: "Kontakt",
        reManualEntry: "Manueller Eintrag:",
        reManualEntryNote: "Hier erstellt, weil keine Formularantwort vorlag.",
        reRemove: "Entfernen",
        reCurrentlyPaired: "Aktuell verknüpft:",
        reUnpair: "Verknüpfung lösen",
        reEditDetails: "Kontaktdaten bearbeiten",
        rePhone: "Telefon",
        reEmail: "E-Mail",
        reInvitationHelp: "Alle eintragen, getrennt mit {sep} (z. B. {example}). Änderungen gelten sofort für den persönlichen Link.",
        reChildMarker: "(Kind)",
        reExample: "Michael Braun; Emma (Kind)",
        reMaxGuests: "Max. Gäste",
        reMembersPh: "z. B. Michael Braun; Emma (Kind)",
        // Task #935: the guest-list columns (Add contacts, the contact window).
        acGuestNames: "Gastname(n)",
        acGuestNamesHint: "So tragen Sie Gastnamen ein\n• Trennen Sie Namen mit einem Semikolon (;). Kommas gehen auch.\n• Schreiben Sie (Kind) hinter den Namen eines Kindes, z. B. Leon Schmidt (Kind).\n• Beispiel: Anna Schmidt; Max Schmidt; Leon Schmidt (Kind)\n• Telefon und E-Mail der Zeile gehören zur ersten genannten Person.",
        acGroupName: "Gruppenname",
        acGroupNameHint: "An wen die Einladung gerichtet ist, z. B. Familie Schmidt: auf dem Umschlag und in der Begrüßung des Formulars; Nachrichten sprechen die Gastnamen an. Ohne Gastnamen finden die Gäste sie über diesen Namen. Für einen Gast, der allein eingeladen ist, leer lassen.",
        acCsvColumns: "CSV-Spalten: Gastname(n), Gruppenname, Telefon, E-Mail · optional: Max. Gäste, Tags",
        acSameName: "Achtung: Mehrere Gäste oder Gruppen haben denselben Namen. Fügen Sie für jeden eine E-Mail-Adresse oder Telefonnummer hinzu, damit sie sich bei der Suche ausweisen können.",
        acLastFirst: "Eine Person, Nachname zuerst? Trennen Sie verschiedene Personen mit \";\".",
        reGroupName: "Gruppenname",
        reGroupNameHint: "An wen sie gerichtet ist: Umschlag und Anrede. Ist niemand aufgeführt, finden die Gäste sie über diesen Namen.",
        reMaxGuestsHint: "Leer lassen, damit sich die Gruppengröße nach den Namen richtet (oder nach dem Limit des Formulars).",
        reGroupNamePh: "z. B. Familie Schmidt",
        reGuestNames: "Gastname(n)",
        reTags: "Tags",
        reTagsPh: "z. B. Familie, nur Empfang",
        reTagsHelp: "Tags bestimmen, welche Teile der Veranstaltung der Link dieses Gastes zeigt, wenn ein Teil auf bestimmte Tags beschränkt ist.",
        reLoading: "Wird geladen…",
        reSave: "Speichern",
        rePairHeading: "Mit einer vorhandenen Antwort verknüpfen",
        rePairHelp: "Wenn {name} über das Formular geantwortet hat, die Antwort aber nicht automatisch zugeordnet wurde (z. B. geteilter Link, andere Telefonnummer), hier verknüpfen.",
        reLooking: "Passende Antworten werden gesucht…",
        reManualHeading: "Oder manuell erfassen",
        reManualHelp: "Nur verwenden, wenn {name} außerhalb des Formulars geantwortet hat (Nachricht, Anruf, persönlich).",
        reAttending: "Kommt",
        reDeclined: "Abgesagt",
        reMaybe: "Vielleicht",
        reResponded: "Geantwortet",
        rePending: "Ausstehend",
        reSaving: "Wird gespeichert…",
        reSaved: "Gespeichert ✓",
        reCouldNotSave: "Speichern fehlgeschlagen",
        reNotOnMessage: "Dieser Gast ist nicht mehr Teil der Nachricht, daher kann die Einladung hier nicht bearbeitet werden.",
        rePartyMany: "Die Einladung gilt für {n} Personen.",
        rePartyOne: "Die Einladung gilt für eine Person.",
        reLoadFailed: "Die Einladung konnte nicht geladen werden.",
        reMaxInvalid: "Max. Gäste muss eine ganze Zahl von 1 bis 50 sein.",
        reBrowseAll: "Alle {n} nicht verknüpften Antworten ansehen",
        reBrowseOne: "Die nicht verknüpfte Antwort ansehen",
        reHideList: "Liste ausblenden",
        reCandidatesFailed: "Mögliche Treffer konnten nicht geladen werden.",
        reManualTag: "Manuell",
        reManualTagTip: "Als manueller Eintrag bei einem anderen Kontakt erstellt",
        reNoName: "(kein Name)",
        rePair: "Verknüpfen",
        reLikely: "Wahrscheinliche Treffer",
        reNoUnpaired: "Keine nicht verknüpften Antworten in diesem Formular. Hat der Gast außerhalb des Formulars geantwortet, die Schaltflächen unter \"Oder manuell erfassen\" verwenden.",
        reNoLikely: "Keine wahrscheinlichen Treffer nach Name, Telefon oder E-Mail. Durchsuchen Sie die vollständige Liste, wenn Sie wissen, welche Antwort es ist.",
        reSearchPh: "Nach Name, Telefon oder E-Mail suchen…",
        rePaired: "Antwort mit {name} verknüpft",
        reUnpaired: "Verknüpfung für {name} gelöst",
        reRecorded: "{status} für {name} erfasst",
        reManualRemoved: "Manueller Eintrag entfernt",
        reFailedPair: "Antwort konnte nicht verknüpft werden",
        reFailedUnpair: "Verknüpfung konnte nicht gelöst werden",
        reFailedRecord: "Antwort konnte nicht erfasst werden",
        reFailedRemove: "Entfernen fehlgeschlagen",
        reUpdatedNumber: "Nummer aktualisiert.",
        reUpdatedEmail: "E-Mail-Adresse aktualisiert.",
        reUpdatedDetails: "Kontaktdaten aktualisiert.",
        reResendAsk: "Diese Nachricht erneut an {dest} senden?",
        reMultiMessage: "Dieser Kontakt kommt in mehreren Nachrichten vor. Verwenden Sie {addContacts} bei der Nachricht, die erneut gesendet werden soll.",
        reResend: "Erneut senden",
        reSending: "Wird gesendet…",
        reSentNumber: "An die neue Nummer gesendet; es erscheint in Kürze im Verlauf dieses Kontakts.",
        reQueued: "Vorgemerkt: Die Nachricht geht von selbst raus, sobald WhatsApp sie freigibt, meist innerhalb weniger Minuten.",
        reSentEmail: "An die neue E-Mail-Adresse gesendet; es erscheint in Kürze im Verlauf dieses Kontakts.",
        reSentBoth: "An die neue Nummer und Adresse gesendet; es erscheint in Kürze im Verlauf dieses Kontakts.",
        reResendFailed: "Erneutes Senden fehlgeschlagen. Bitte noch einmal versuchen.",
        reminderDupSkipped: "Schon durch eine andere Nachricht erinnert: {n}",
        reminderDupSkippedBy: "Schon durch „{name}“ erinnert: {n}",
        reminderDupSkippedTip: "Diese Gäste haben in den letzten 20 Stunden bereits dieselbe Erinnerung aus einer anderen Nachricht zu dieser Veranstaltung erhalten. Damit sie nichts doppelt bekommen, wurden sie hier übersprungen.",
        reminderDupAll: "Alle wurden bereits durch eine andere Nachricht erinnert",
        remAttentionOne: "1 Erinnerung braucht Ihre Aufmerksamkeit",
        remAttentionMany: "{n} Erinnerungen brauchen Ihre Aufmerksamkeit",
        remFailExpiredNote: "WhatsApp hat die Nachricht dieser Erinnerung nicht rechtzeitig genehmigt. Bearbeiten und speichern Sie sie, um es erneut zu versuchen.",
        remFailRejectedNote: "WhatsApp hat die Nachricht dieser Erinnerung nicht akzeptiert. Bearbeiten Sie die Nachricht und speichern Sie, um sie erneut einzureichen.",
        remAudienceBtn: "Ansehen, an wen diese Erinnerung geht",
        remAudienceTitle: "Empfänger dieser Erinnerung",
        msgAudienceTitle: "Empfänger dieser Nachricht",
        msgAudienceOne: "Diese Nachricht geht an diesen Gast.",
        msgAudienceMany: "Diese Nachricht geht an diese {n} Gäste.",
        remAudienceNowOne: "Würde sie jetzt gesendet, ginge sie an 1 Gast. Die Liste wird erst beim Senden festgelegt und kann sich also noch ändern.",
        remAudienceNowMany: "Würde sie jetzt gesendet, ginge sie an {n} Gäste. Die Liste wird erst beim Senden festgelegt und kann sich also noch ändern.",
        remAudienceFixedOne: "Diese Erinnerung geht an diesen Gast.",
        remAudienceFixedMany: "Diese Erinnerung geht an diese {n} Gäste.",
        remAudienceEmpty: "Im Moment passt niemand. Die Liste wird beim Senden der Erinnerung erneut geprüft.",
        remAudienceFailed: "Die Empfänger dieser Erinnerung konnten nicht geladen werden. Bitte versuchen Sie es erneut.",
        remAudienceFixedNone: "Auf der Liste dieser Erinnerung steht niemand, sie erreicht also niemanden.",
        remAudienceMeantOne: "Diese Erinnerung sollte an diesen Gast gehen. Sie wird erst gesendet, wenn Sie sie bearbeiten und speichern.",
        remAudienceMeantMany: "Diese Erinnerung sollte an diese {n} Gäste gehen. Sie wird erst gesendet, wenn Sie sie bearbeiten und speichern.",
        remFailWaOnlyRefusedNote: "WhatsApp hat die Nachricht dieser Erinnerung nicht akzeptiert, und ihre Gäste sind nur über WhatsApp erreichbar, deshalb wurde nichts gesendet. Häufige Ursache ist ein Personalisierungs-Platzhalter ganz am Ende der Nachricht: Fügen Sie danach Text hinzu und speichern Sie, um sie erneut einzureichen.",
        acSentOf: "{sent} von {total} gesendet",
        acSkippedUntil: "{n} von WhatsApp pausiert bis {time}, danach erneut versuchen",
        acSkipped: "{n} übersprungen",
        acUnreachable: "{n} nicht gesendet (Kontaktdaten)",
        acFailed: "{n} nicht erreicht",
        acDeferred: "{n} gehen im nächsten Sendefenster raus",
        acLineOne: "1 Gast nach dem Versand hinzugefügt",
        acLineMany: "{n} Gäste nach dem Versand hinzugefügt",
        acLineLast: "zuletzt {n}",
        acLineAwaiting: "{n} warten auf die WhatsApp-Genehmigung",
        acTipAdded: "{when}: {n} hinzugefügt",
        reResendCooldown: "WhatsApp pausiert Nachrichten an diese Nummer bis {time}. Es wurde nichts berechnet. Versuchen Sie es danach erneut, oder senden Sie den persönlichen Link selbst.",
        reResendCooldownNoTime: "WhatsApp pausiert Nachrichten an diese Nummer vorerst. Es wurde nichts berechnet. Versuchen Sie es später erneut, oder senden Sie den persönlichen Link selbst.",
        reResendNotSent: "Die Nachricht konnte an diese Angaben nicht gesendet werden. Es wurde nichts berechnet. Prüfen Sie die Nummer oder Adresse und versuchen Sie es erneut.",
        // Every guest skipped (Meta 24h cooldown, not on WhatsApp...): see _skipSummary and _mergeSendTimeFailures.
        skipNotSent: "Nicht gesendet",
        skipNotSentTip: "Nichts ist rausgegangen: Alle Gäste dieser Nachricht wurden zurückgehalten. Der Hinweis unten erklärt, warum und was Sie tun können.",
        skippedOne: "1 übersprungen",
        skippedMany: "{n} übersprungen",
        skippedTip: "Bewusst zurückgehalten, nicht gesendet. Klicken, um zu sehen, wer und warum.",
        skipCoolOne: "WhatsApp hat eine kürzliche Nachricht an 1 Gast abgelehnt, deshalb pausieren wir Nachrichten an diese Person bis {time}.",
        skipCoolMany: "WhatsApp hat kürzliche Nachrichten an {n} Gäste abgelehnt, deshalb pausieren wir Nachrichten an diese Personen. Beim ersten von ihnen kann es nach {time} erneut versucht werden.",
        skipCoolLiftedOne: "WhatsApp hat eine kürzliche Nachricht an 1 Gast abgelehnt. Unsere Pause für Nachrichten an diese Person ist vorbei.",
        skipCoolLiftedMany: "WhatsApp hat kürzliche Nachrichten an {n} Gäste abgelehnt. Für den ersten von ihnen ist unsere Pause vorbei.",
        skipCoolWhy: "WhatsApp lehnt Marketingnachrichten an Personen ab, die zuletzt viele erhalten haben, und sagt nicht, wie lange. Nach einer Ablehnung pausieren wir unsere Nachrichten an diese Nummer, statt erneut zu senden. Die Pause gilt für alle Veranstaltungen auf QuikRSVP, die abgelehnte Nachricht muss also nicht Ihre gewesen sein. Eine zurückgehaltene Nachricht wird nicht berechnet. Auch ein neuer Versuch nach der Pause kann abgelehnt werden.",
        skipNoWaOne: "1 Gast ist nicht bei WhatsApp. Erreichen Sie ihn per SMS oder E-Mail, oder senden Sie den persönlichen Link selbst.",
        skipNoWaMany: "{n} Gäste sind nicht bei WhatsApp. Erreichen Sie sie per SMS oder E-Mail, oder senden Sie die persönlichen Links selbst.",
        skipUnreachOne: "1 Gast konnte es nicht erhalten: Telefonnummer oder E-Mail muss korrigiert werden. Öffnen Sie die Liste, um zu sehen, wer und was.",
        skipUnreachMany: "{n} Gäste konnten es nicht erhalten: Telefonnummer oder E-Mail muss korrigiert werden. Öffnen Sie die Liste, um zu sehen, wer und was.",
        skipOptOutOne: "1 Gast hat auf unsere WhatsApp-Nachrichten mit STOP geantwortet, deshalb schreiben wir ihm dort nicht mehr.",
        skipOptOutMany: "{n} Gäste haben auf unsere WhatsApp-Nachrichten mit STOP geantwortet, deshalb schreiben wir ihnen dort nicht mehr.",
        skipUnsubOne: "1 Gast hat sich von Ihren E-Mails abgemeldet. Erreichen Sie ihn stattdessen per WhatsApp oder SMS.",
        skipUnsubMany: "{n} Gäste haben sich von Ihren E-Mails abgemeldet. Erreichen Sie sie stattdessen per WhatsApp oder SMS.",
        skipDoRetryAt: "Versuchen Sie es nach {time} erneut, oder öffnen Sie Persönliche Links und senden Sie den Link selbst.",
        skipDoRetryNow: "Versuchen Sie es jetzt erneut, oder öffnen Sie Persönliche Links und senden Sie den Link selbst.",
        skipDoLink: "Öffnen Sie Persönliche Links und senden Sie den Link selbst.",
        skipHandledRepliedOne: "Nicht von uns gesendet, aber dieser Gast hat bereits geantwortet.",
        skipHandledRepliedMany: "Nicht von uns gesendet, aber alle betroffenen Gäste haben bereits geantwortet.",
        skipHandledLinkOne: "Nicht von uns gesendet, aber dieser Gast hat seinen persönlichen Link bereits geöffnet.",
        skipHandledLinkMany: "Nicht von uns gesendet, aber alle betroffenen Gäste haben ihren persönlichen Link bereits geöffnet oder geantwortet.",
        skipStillBlockedOne: "Nachrichten an diesen Gast pausieren wir noch bis {time}. Versuchen Sie es danach erneut, oder senden Sie den persönlichen Link selbst.",
        skipStillBlockedMany: "Nachrichten an diese Gäste pausieren wir noch. Beim ersten von ihnen kann es nach {time} erneut versucht werden; bis dahin senden Sie die persönlichen Links selbst.",
        skipRowCool: "WhatsApp hat eine kürzliche Nachricht an diese Nummer abgelehnt (vielleicht für eine andere Veranstaltung), deshalb pausieren wir alle Nachrichten an sie bis {time}. Versuchen Sie es danach erneut, oder senden Sie den persönlichen Link selbst.",
        skipRowCoolLifted: "WhatsApp hat eine kürzliche Nachricht an diese Nummer abgelehnt (vielleicht für eine andere Veranstaltung). Unsere Pause ist vorbei: Sie können es erneut versuchen oder den persönlichen Link selbst senden.",
        skipRowNoWa: "Diese Nummer ist nicht bei WhatsApp. Erreichen Sie den Gast per SMS oder E-Mail, oder senden Sie den persönlichen Link selbst.",
        skipRowNoWaNoEmail: "Diese Nummer ist nicht bei WhatsApp. Erreichen Sie den Gast per SMS, oder senden Sie den persönlichen Link selbst.",
        fbTipFailedEmail: "WhatsApp konnte die Nachricht nicht zustellen, deshalb ging sie stattdessen per E-Mail raus.",
        fbTipFailedSms: "WhatsApp konnte die Nachricht nicht zustellen, deshalb ging sie stattdessen per SMS raus.",
        fbTipNoWa: "Diese Nummer ist nicht bei WhatsApp, deshalb haben wir dem Gast stattdessen eine E-Mail geschickt.",
        fbTipCool: "WhatsApp-Nachrichten an diese Nummer sind gerade pausiert, deshalb haben wir dem Gast stattdessen eine E-Mail geschickt.",
        fbStateOpened: "Die Nachricht wurde geöffnet.",
        fbStateDelivered: "Die Nachricht wurde zugestellt.",
        fbStateOnWay: "Die Nachricht ist unterwegs.",
        skipRowOptOut: "Dieser Gast hat auf unsere WhatsApp-Nachrichten mit STOP geantwortet, deshalb schreiben wir ihm dort nicht mehr.",
        skipRowOptOutSms: "Dieser Gast hat auf unsere SMS mit STOP geantwortet, daher schicken wir ihm keine SMS mehr.",
        skipRowUnsub: "Dieser Gast hat sich von Ihren E-Mails abgemeldet. Erreichen Sie ihn stattdessen per WhatsApp oder SMS.",
        skipRowOther: "Übersprungen: Diese Nummer kann gerade keine WhatsApp-Nachrichten empfangen.",
        skipRowNoDetails: "Nicht gesendet: keine Telefonnummer und keine E-Mail. Fügen Sie eine hinzu und senden Sie erneut, oder senden Sie den persönlichen Link selbst.",
        skipRowBadNumber: "Nicht gesendet: Diese Nummer ist nicht wählbar. Prüfen Sie die Ziffern und die Ländervorwahl, dann erneut senden.",
        skipRowNoEmail: "Nicht gesendet: per E-Mail vorgesehen, aber ohne Adresse. Fügen Sie eine hinzu oder senden Sie per WhatsApp.",
        skipRowNoPhone: "Nicht gesendet: per SMS vorgesehen, aber ohne Nummer. Fügen Sie eine hinzu und senden Sie erneut.",
        skipRowShared: "Nicht gesendet: dieselbe Nummer wie {name}, und jede Nummer erhält nur eine Nachricht. Geben Sie dem Gast eine eigene Nummer oder E-Mail, oder nehmen Sie ihn in die Einladung von {name} auf.",
        skipRowSharedNoName: "Nicht gesendet: Ein anderer Gast hat dieselbe Nummer, und jede Nummer erhält nur eine Nachricht. Geben Sie dem Gast eine eigene Nummer oder E-Mail.",
        skipRowNoChannel: "Nicht gesendet: Für diesen Gast ist kein Versandweg angegeben. Bearbeiten Sie seine Daten und senden Sie erneut.",
        skipBadge: "Übersprungen",
        unsubBadge: "Abgemeldet",
        skipRetrySent: "Gesendet. Die Karte aktualisiert sich gleich.",
        skipRetryFailed: "WhatsApp hat sie diesmal nicht angenommen. Öffnen Sie Status, um den Grund zu sehen.",
        skipRetryStill: "Nichts ist rausgegangen: Die Pause für Nachrichten an diese Gäste gilt noch. Versuchen Sie es später erneut, oder senden Sie den persönlichen Link selbst.",
        skipRetryHeld: "Vor dem Versand zur kurzen Prüfung zurückgehalten. Sie wird gesendet, sobald sie freigegeben ist.",
        skipRetryPaused: "Nicht gesendet: Sie ist pausiert. Die Karte zeigt den Grund.",
        skipRetryNoCredits: "Pausiert: Laden Sie Guthaben auf, um sie zu senden.",
        skipRetryRequested: "Erneuter Versuch angefordert. Die Karte zeigt den aktuellen Stand.",
        skipRetryError: "Erneuter Versuch gerade nicht möglich. Bitte in einer Minute noch einmal probieren.",
        retryBusy: "Diese Nachricht wird noch gesendet. Sehen Sie nach Abschluss nach, wer sie noch braucht, und versuchen Sie es dann erneut.",
        statusLoadFailed: "Die Gästeliste konnte gerade nicht geladen werden.",
        statusLoadExpired: "Ihre Sitzung ist abgelaufen. Laden Sie die Seite neu, um die Gästeliste zu sehen.",
        statusLoadRetry: "Erneut versuchen",
        statusLoadReload: "Seite neu laden",
        statusRefreshFailed: "Die Gästeliste konnte nicht aktualisiert werden. Laden Sie die Seite neu, um den aktuellen Stand zu sehen.",
        retrySentOne: "Nachricht erneut an 1 Gast gesendet.",
        retrySentMany: "Nachricht erneut an {n} Gäste gesendet.",
        retryStillFailedOne: "1 Gast konnte weiterhin nicht erreicht werden.",
        retryStillFailedMany: "{n} Gäste konnten weiterhin nicht erreicht werden.",
        retryStaleView: "Ein erneuter Versand dieser Nachricht wurde nach dem Laden dieser Liste abgeschlossen, daher wurde diesmal nichts gesendet. Die Liste wurde aktualisiert: Sehen Sie nach, wer sie noch braucht, und versuchen Sie es erneut.",
        acSmsConsentWhy: "Gäste mit US-Nummern erhalten diese Nachricht per SMS, andere +1-Nummern oder brasilianische Nummern unter Umständen auch, wenn WhatsApp fehlschlägt.",
        acSmsConsent: "Ich bestätige, dass alle Empfänger der Zusendung von SMS-Einladungsnachrichten für meine Veranstaltung über QuikRSVP zugestimmt haben.",
        acSmsDisclosure: "Empfänger können mit STOP abbestellen oder HELP für Hilfe antworten. Nachrichten- und Datengebühren können anfallen. Häufigkeit variiert.",
        waRejectedTitle: "WhatsApp hat diese Nachricht nicht angenommen",
        waOursTitle: "WhatsApp-Nachrichten konnten nicht gesendet werden",
        waTimeoutTitle: "WhatsApp hat diese Nachricht nicht rechtzeitig freigegeben",
        waSubmitPendingTitle: "Ihre Nachricht wird noch zur Freigabe an WhatsApp gesendet",
        waSubmitPendingBody: "Wir konnten WhatsApp noch nicht erreichen. Wir versuchen es automatisch weiter und schreiben Ihnen, falls WhatsApp die Nachricht ablehnt. Sie müssen nichts tun.",
        addWaitWaTip: "Sie können Kontakte hinzufügen, sobald Ihre Nachricht zur Freigabe bei WhatsApp angekommen ist. Wir versuchen es weiter – schauen Sie in ein paar Minuten wieder vorbei.",
        remCancelled: "Abgebrochen",
        remCancelFormDeleted: "Nicht gesendet: Die Veranstaltung wurde gelöscht.",
        remCancelDeadline: "Nicht gesendet: Die Rückmeldefrist war abgelaufen, bevor sie fällig war.",
        remCancelDateMoved: "Nicht gesendet: Die Veranstaltung wurde vorverlegt, und ihr Zeitpunkt war schon vorbei.",
        remOnHold: "Angehalten",
        remHeldPostponed: "Angehalten: Die Veranstaltung ist verschoben. Die Erinnerung zieht mit, sobald Sie das neue Datum festlegen.",
        remCancelEventCancelled: "Nicht gesendet: Die Veranstaltung wurde abgesagt.",
        remCancelPostponedLate: "Nicht gesendet: Die Veranstaltung war bis mehr als einen Tag nach dem Zeitpunkt dieser Erinnerung verschoben.",
        remCancelInvitation: "Nicht gesendet: Die zugehörige Einladung wurde gelöscht oder abgebrochen.",
        remCancelLate: "Nicht gesendet: Die zugehörige Einladung war bis mehr als einen Tag nach dem Zeitpunkt dieser Erinnerung angehalten.",
        remCancelNotSent: "Nicht gesendet",
        remMovedWithEvent: "mit dem Veranstaltungsdatum verschoben",
        remCancelDeadlineHint: "Verschieben Sie die Rückmeldefrist oder entfernen Sie den Zusage-Link und bearbeiten Sie die Erinnerung dann, um sie zu senden.",
        remCancelDateMovedHint: "Bearbeiten Sie sie, um einen neuen Zeitpunkt zu wählen.",
        remSendsSoon: "Wird innerhalb der nächsten Stunde gesendet",
        remDateMovedPast: "Die Veranstaltung wurde vorverlegt und der neue Zeitpunkt dieser Erinnerung ist schon vorbei, daher wird sie nicht gesendet. Bearbeiten Sie sie, um einen neuen Zeitpunkt zu wählen.",
        remSendNowWaiting: "Diese Erinnerung wird gesendet, sobald ihre Einladung alle Gäste erreicht hat.",
        remSendNowNoRecipients: "Keine Gäste passen zu dieser Erinnerung, daher wurde nichts gesendet.",
        remSendNowTaken: "Diese Erinnerung wird bereits gesendet.",
        remSendNowCancelled: "Diese Erinnerung wurde abgebrochen, weil ihre Einladung gelöscht oder abgebrochen wurde.",
        remSendNowError: "Wir konnten gerade nicht ermitteln, an wen diese Erinnerung geht. Es wurde nichts gesendet, und es wird automatisch erneut versucht.",
        moreActions: "Weitere Aktionen", // the card's ... menu; was missing in every pack
        // Card badges and stats, reminder rows, the Status window, Remind All
        // and the card actions' confirms/toasts (code review 2026-10-07: all
        // English on every dashboard). See cCount for the ...One forms.
        badgeSent: "Gesendet",
        badgeSending: "Wird gesendet",
        badgeProcessing: "In Bearbeitung",
        badgeScheduled: "Geplant",
        badgePaused: "Pausiert",
        badgeCancelled: "Abgebrochen",
        badgeDraft: "Entwurf",
        badgeExpired: "Abgelaufen",
        badgeUnableToSend: "Senden nicht möglich",
        badgeNoCredits: "Nicht genug Guthaben",
        badgeInReview: "In Prüfung",
        scheduledFor: "Geplant für {when}",
        sendingAt: "Wird am {when} gesendet",
        expiredTip: "WhatsApp hat diese Nachricht nicht rechtzeitig freigegeben. Mit „Bearbeiten & erneut senden“ reichen Sie sie erneut ein.",
        noCreditsTip: "Diese Nachricht wurde pausiert, weil Ihr Guthaben aufgebraucht ist. Kaufen Sie Guthaben und senden Sie sie erneut, um die restlichen Nachrichten zuzustellen.",
        inReviewTip: "Nachrichten von neuen Konten prüfen wir, bevor sie rausgehen – gerade sieht sich jemand Ihre an. Es wurde nichts berechnet, und Ihre Nachricht und Kontakte sind gespeichert. Sie wird nach der Freigabe automatisch gesendet; schreiben Sie uns im Chat, wenn Sie sie früher brauchen.",
        toggleRemindersTip: "Erinnerungen dieser Nachricht ein- oder ausblenden",
        showRemindersAria: "Erinnerungen anzeigen ({n})",
        hideRemindersAria: "Erinnerungen ausblenden ({n})",
        expandAllReminders: "Alle aufklappen",
        collapseAllReminders: "Alle zuklappen",
        expandAllRemindersTip: "Erinnerungen aller Nachrichten anzeigen",
        collapseAllRemindersTip: "Erinnerungen aller Nachrichten ausblenden",
        remindersOn: "Erinnerungen aktiv",
        statResponded: "{n} geantwortet",
        statRespondedOne: "{n} geantwortet",
        statPending: "{n} ausstehend",
        statPendingOne: "{n} ausstehend",
        statSent: "{n} gesendet",
        statSentOne: "{n} gesendet",
        statContacts: "{n} Kontakte",
        statContactsOne: "{n} Kontakt",
        statNotDelivered: "{n} nicht zugestellt",
        statNotDeliveredOne: "{n} nicht zugestellt",
        statRecipients: "{n} Empfänger",
        statRecipientsOne: "{n} Empfänger",
        allRepliedTip: "Alle, an die sie ging, hatten schon geantwortet – genau an sie war sie gerichtet.",
        waCouldNotAccept: "WhatsApp konnte Ihre Nachricht nicht annehmen. Bearbeiten Sie die Nachricht und senden Sie sie erneut.",
        untitledMessage: "Nachricht",
        editResendTip: "Problem beheben und WhatsApp-Nachrichten erneut senden",
        timingDayOf: "Am Tag selbst",
        timing1DayBefore: "1 Tag vorher",
        timing3DaysBefore: "3 Tage vorher",
        timing1WeekBefore: "1 Woche vorher",
        timing2WeeksBefore: "2 Wochen vorher",
        audNoResponse: "Gäste ohne Antwort",
        audAttending: "Zusagende Gäste",
        audMaybe: "Gäste mit Vielleicht",
        audDeclined: "Absagende Gäste",
        audAll: "Alle Kontakte",
        remTitleAudience: "Erinnerung · {audience}",
        remTitleIndex: "Erinnerung {n}",
        remWasSetFor: "War für {when} geplant",
        remNoCreditsTip: "Diese Erinnerung konnte nicht gesendet werden, weil Ihr Guthaben nicht reicht. Kaufen Sie Guthaben und setzen Sie sie fort, um sie zu senden.",
        remAddCredits: "Laden Sie Guthaben auf, um diese Erinnerung zu senden",
        remNoRecipients: "Keine Empfänger",
        remNoMatching: "Keine passenden Empfänger",
        remResubmitTip: "Ihre bearbeitete Nachricht liegt WhatsApp erneut zur Freigabe vor. Sie wird nach der Freigabe gesendet.",
        remResubmitting: "Erneut eingereicht",
        remSendsOnceApproved: "Wird am {when} gesendet, sobald freigegeben",
        remWaitingApproval: "Wartet auf WhatsApp-Freigabe",
        remAllRepliedTip: "Alle, die diese Erinnerung bekommen haben, hatten schon geantwortet – genau an sie war sie gerichtet.",
        remExpiredTip: "WhatsApp hat die Nachricht dieser Erinnerung nicht rechtzeitig freigegeben. Bearbeiten Sie sie und versuchen Sie es erneut.",
        remRejectedTip: "WhatsApp hat den Inhalt dieser Erinnerung abgelehnt. Bearbeiten und speichern Sie die Erinnerung, um sie erneut einzureichen.",
        remFailedTip: "Beim Senden dieser Erinnerung ist ein Problem aufgetreten. Bearbeiten Sie sie und versuchen Sie es erneut.",
        remRejected: "Nachricht abgelehnt",
        remCouldntSend: "Nicht gesendet",
        remTimedOut: "WhatsApp-Freigabe hat zu lange gedauert",
        remEditToResubmit: "Bearbeiten und speichern, um sie erneut einzureichen",
        remSendFailed: "Senden fehlgeschlagen",
        remWaitingTip: "Warten auf die Freigabe dieser Erinnerung durch WhatsApp (Meta) – meist ein paar Minuten, manchmal länger. Sie wird nach der Freigabe automatisch gesendet.",
        remSendingSoon: "Wird bald gesendet",
        remBeingSentTip: "Diese Erinnerung wird gerade gesendet.",
        remDueOnceApproved: "Fällig am {when} · wird nach WhatsApp-Freigabe gesendet",
        remSendsWhenApproved: "Wird gesendet, sobald WhatsApp sie freigibt",
        remDue: "Fällig am {when}",
        remSendingNow: "Wird gerade gesendet…",
        remProcessingTip: "Diese Erinnerung geht jetzt raus. E-Mails gehen sofort; geht sie auch per WhatsApp, wird dieser Teil gesendet, sobald Meta die Nachricht freigibt – meist innerhalb weniger Minuten.",
        remSendingMinute: "Wird gerade gesendet – das dauert meist ein bis zwei Minuten",
        remSends: "Wird am {when} gesendet",
        remPreviewTip: "Vorschau der Erinnerung",
        remEditTip: "Nachricht, Zeitpunkt oder Empfänger bearbeiten",
        remStatusTip: "Zustellstatus ansehen – wer diese Erinnerung bekommen hat und wie geantwortet wurde",
        remBuyCreditsTip: "Guthaben kaufen, um diese Erinnerung zu senden",
        remResumeCreditsTip: "Diese Erinnerung fortsetzen, sobald Sie Guthaben haben",
        remResumeTip: "Diese Erinnerung fortsetzen",
        remPauseTip: "Diese Erinnerung pausieren",
        remDeleteTip: "Diese Erinnerung löschen",
        msgNotFound: "Nachricht nicht gefunden",
        remFromMainMessage: "Fügen Sie Erinnerungen über die Hauptnachricht hinzu, nicht über eine andere Erinnerung.",
        renamePrompt: "Diese Nachricht umbenennen:",
        renameEmpty: "Der Name der Nachricht darf nicht leer sein",
        renameFailed: "Nachricht konnte nicht umbenannt werden: ",
        cancelConfirm: "Möchten Sie die Nachricht „{name}“ wirklich abbrechen?\n\nDas kann nicht rückgängig gemacht werden. Noch nicht gesendete Nachrichten werden nicht gesendet.",
        cancelDone: "Nachricht abgebrochen",
        cancelFailed: "Nachricht konnte nicht abgebrochen werden: ",
        resumeConfirm: "Nachricht „{name}“ fortsetzen?\n\nSie wird am {when} gesendet.",
        resumeConfirmNoDate: "Nachricht „{name}“ fortsetzen?\n\nSie wird zum ursprünglich geplanten Termin gesendet.",
        resumeDone: "Nachricht fortgesetzt",
        resumeFailed: "Nachricht konnte nicht fortgesetzt werden: ",
        pauseConfirm: "Nachricht „{name}“ pausieren?\n\nSie wird nicht zum geplanten Zeitpunkt gesendet. Sie können sie später fortsetzen oder abbrechen.",
        pauseDone: "Nachricht pausiert",
        pauseFailed: "Nachricht konnte nicht pausiert werden: ",
        pauseTooLate: "Diese Nachricht wurde fertig gesendet, bevor sie pausiert werden konnte.",
        autoRetryPauseConfirm: "Automatische Wiederholung für „{name}“ pausieren?",
        autoRetryResumeConfirm: "Automatische Wiederholung für „{name}“ fortsetzen?",
        autoRetryNotRetriedOne: "Bei {n} Kontakt wird es nicht automatisch erneut versucht.",
        autoRetryNotRetriedMany: "Bei {n} Kontakten wird es nicht automatisch erneut versucht.",
        autoRetryRetriedOne: "Bei {n} Kontakt wird es beim nächsten geplanten Versuch erneut probiert.",
        autoRetryRetriedMany: "Bei {n} Kontakten wird es beim nächsten geplanten Versuch erneut probiert.",
        autoRetryPaused: "Automatische Wiederholung pausiert",
        autoRetryResumed: "Automatische Wiederholung fortgesetzt",
        autoRetryPauseFailed: "Automatische Wiederholung konnte nicht pausiert werden: ",
        autoRetryResumeFailed: "Automatische Wiederholung konnte nicht fortgesetzt werden: ",
        sendNowConfirm: "Nachricht „{name}“ jetzt senden?\n\nDer geplante Termin ist vorbei. Die Nachricht wird sofort gesendet.",
        sendNowDone: "Nachricht gesendet!",
        sendNowFailed: "Nachricht konnte nicht gesendet werden: ",
        deleteDraftConfirm: "Möchten Sie den Entwurf „{name}“ wirklich löschen?\n\nDas kann nicht rückgängig gemacht werden.",
        deleteDraftFailed: "Entwurf konnte nicht gelöscht werden: ",
        deleteConfirmActive: "„{name}“ endgültig löschen?\n\nDiese Nachricht hat Zustellstatistiken und eventuell geplante Erinnerungen. Beides wird entfernt.\n\nDie Antworten der Gäste bleiben erhalten (sie werden getrennt gespeichert).\n\nDas kann nicht rückgängig gemacht werden.",
        deleteAlsoGuests: "Diese Nachricht hat {n} Gäste zu Ihrer Gästeliste hinzugefügt, die nicht geantwortet haben und in keiner anderen Nachricht sind.\n\nAuch von der Gästeliste entfernen?\n\nOK entfernt sie. Abbrechen behält sie.",
        deleteConfirmUnsent: "„{name}“ endgültig löschen?\n\nMit dieser Nachricht wurde nichts gesendet, Sie können sie also bedenkenlos löschen.\n\nDas kann nicht rückgängig gemacht werden.",
        deleteFailed: "Nachricht konnte nicht gelöscht werden: ",
        statusAllTitle: "Alle Kontakte",
        statusAllSubtitle: "Aus allen Nachrichten",
        statusAllMessages: "📊 Alle Nachrichten",
        statusAllTags: "Alle Tags", statusNoTag: "Ohne Tag", statusTagsLine: "Tags",
        statusTitleFallback: "Status der Nachricht",
        statusThisMessageOnly: "Nur Kontakte dieser Nachricht",
        statusAddedAfterLaunch: "{n} nach dem Versand hinzugefügt",
        statusAddedAfterLaunchOne: "{n} nach dem Versand hinzugefügt",
        statusContactCount: "{n} Kontakte",
        statusContactCountOne: "{n} Kontakt",
        exportNone: "Keine Kontakte zum Exportieren",
        exportedOne: "{n} Kontakt exportiert",
        exportedMany: "{n} Kontakte exportiert",
        dlvSent: "Gesendet",
        dlvDelivered: "Zugestellt",
        dlvRead: "Gelesen",
        dlvOpened: "Geöffnet",
        dlvClicked: "Geklickt",
        dlvClickedTip: "Der Antwortlink (oder Ihre Website) wurde aus einer Ihrer Nachrichten geöffnet. Abgesehen von einer Antwort ist das das sicherste Zeichen, dass die Einladung angekommen ist.",
        dlvReadTip: "Lesebestätigung von WhatsApp selbst (blaue Haken), die Nachricht wurde also gelesen.",
        dlvOpenedTip: "Die Bilder der E-Mail wurden geladen, sie wurde also wahrscheinlich angesehen. Apple Mail und einige andere Apps laden Bilder beim Empfang automatisch, daher ist das ein gutes Zeichen, aber kein Beweis.",
        dlvDeliveredEmailTip: "Der E-Mail-Anbieter des Gastes hat sie angenommen. In welchem Ordner sie gelandet ist, sehen wir nicht, und viele Apps (Outlook, Hotmail, Firmen-E-Mail) melden das Öffnen nie, sie kann also schon gelesen sein.",
        dlvDeliveredWaTip: "Sie ist auf dem Telefon angekommen (doppelter WhatsApp-Haken). Noch keine Lesebestätigung, aber manche schalten sie aus, sie kann also schon gelesen sein.",
        dlvDeliveredSmsTip: "Sie ist als SMS auf dem Telefon angekommen. SMS melden nie, ob sie gelesen wurden.",
        dlvSentTip: "Sie wurde an das Netz übergeben. Noch keine Bestätigung, dass sie angekommen ist.",
        dlvBounced: "Unzustellbar",
        dlvFailed: "Fehlgeschlagen",
        dlvSmsSent: "SMS gesendet",
        dlvSmsFailed: "SMS fehlgeschlagen",
        detailNoEmailFallback: "Keine E-Mail-Adresse hinterlegt, daher konnte sie nicht per E-Mail gesendet werden. Fügen Sie eine mit dem Stift hinzu, um sie zu senden.",
        deliveryFailed: "Zustellung fehlgeschlagen",
        detailEmail: "E-Mail",
        detailSmsFallback: "SMS als Ersatz",
        detailRsvp: "Antwort",
        noDetail: "Keine Details verfügbar",
        fallbackEmail: "Per E-Mail",
        fallbackSms: "Per SMS",
        aMessageFailed: "eine Nachricht fehlgeschlagen",
        aMessageFailedTip: "Eine der Nachrichten an diesen Kontakt konnte nicht zugestellt werden, eine andere Nachricht hat ihn aber erreicht.",
        checkNumber: "Nummer prüfen",
        checkNumberTip: "WhatsApp konnte an diese Nummer, so wie sie gespeichert ist, nicht zustellen, und die Nummer sieht unvollständig aus. Korrigieren Sie sie am besten und senden Sie erneut, bevor Sie annehmen, dass der Gast nicht bei WhatsApp ist.",
        rowDetailsTip: "Klicken, um alle Zustelldetails zu sehen",
        updateResponseTip: "Antwort ändern",
        linkColumn: "LINK",
        remCellOnAria: "Erinnerungen aktiv – pausieren",
        remCellOffAria: "Erinnerungen aus – fortsetzen",
        guestFallback: "Gast",
        addName: "Namen hinzufügen",
        editNameTip: "Klicken, um den Namen zu bearbeiten",
        nameEditPlaceholder: "Name des Gastes",
        nameEditSave: "Speichern",
        nameEditCancel: "Abbrechen",
        nameSaveFailed: "Der Name konnte nicht gespeichert werden",
        raLoadingAll: "Alle Kontakte werden geladen...",
        raLoadingPending: "Ausstehende Kontakte werden geladen...",
        raLoadingYes: "Ja-Antworten werden geladen...",
        raLoadingMaybe: "Vielleicht-Antworten werden geladen...",
        raLoadingNo: "Nein-Antworten werden geladen...",
        raExcludedAll: "Alle Kontakte sind von Erinnerungen ausgeschlossen",
        raExcludedPending: "Alle ausstehenden Gäste hier sind von Erinnerungen ausgeschlossen",
        raExcludedYes: "Alle Gäste mit Ja-Antwort hier sind von Erinnerungen ausgeschlossen",
        raExcludedMaybe: "Alle Gäste mit Vielleicht-Antwort hier sind von Erinnerungen ausgeschlossen",
        raExcludedNo: "Alle Gäste mit Nein-Antwort hier sind von Erinnerungen ausgeschlossen",
        raSkippedExcludedOne: "{n} Gast übersprungen, den Sie von Erinnerungen ausgeschlossen haben",
        raSkippedExcludedMany: "{n} Gäste übersprungen, die Sie von Erinnerungen ausgeschlossen haben",
        raLoadFailed: "Kontakte konnten nicht geladen werden",
        raNoneSelected: "Keine Empfänger ausgewählt – abgebrochen.",
        raNoValidRecipients: "Keine gültigen Empfänger zum Senden.",
        errLabel_131026: "WhatsApp konnte nicht zustellen",
        errLabel_131049: "von Meta-Richtlinie blockiert",
        errLabel_131047: "24-Stunden-Fenster abgelaufen",
        errLabel_131048: "als Spam blockiert",
        errLabel_131056: "Sendelimit",
        errLabel_131058: "Problem mit der Nachricht",
        errLabel_131061: "Nachricht von Meta pausiert",
        errLabel_131062: "Nachricht von Meta deaktiviert",
        errLabel_132001: "Problem mit der Nachricht",
        errLabel_130472: "von Meta-Test zurückgehalten",
        errLabel_130497: "WhatsApp in diesem Land gesperrt",
        errLabel_131063: "nicht bei WhatsApp",
        errLabel_130429: "Sendelimit erreicht",
        errLabel_marketing_limit_reached: "Meta-Marketinglimit",
        errLabel_BREVO_REJECTED: "vom SMS-Anbieter abgelehnt",
        errLabel_unknown: "fehlgeschlagen",
        errTip_131049: "Meta hat diese Marketingnachricht blockiert (Richtlinien- oder Interaktionslimit) – keine Abmeldung durch den Gast. Er nutzt WhatsApp und ist erreichbar; ein erneutes Senden nach etwa einem Tag klappt ungefähr in der Hälfte der Fälle.",
        errTip_131026: "Diese Nummer konnte die Nachricht nicht empfangen – vielleicht nutzt der Gast kein WhatsApp, oder Ziffern stimmen nicht. Erneutes Senden hilft selten (etwa 1 von 20 kommt an), ein anderer Weg ist zuverlässiger.",
        errTip_131048: "Telefonnummer eingeschränkt",
        errTip_131056: "Sendelimit überschritten",
        errTip_131058: "Nachricht nicht freigegeben",
        errTip_131047: "24-Stunden-Fenster abgelaufen",
        errTip_131061: "Meta hat die hier verwendete WhatsApp-Nachricht pausiert, daher konnte sie nicht zugestellt werden. Das betrifft alle Gäste dieser Nachricht, nicht nur diesen – die Nachricht muss neu erstellt werden.",
        errTip_131062: "Nachricht deaktiviert",
        errTip_132001: "WhatsApp hat die Nachricht abgelehnt (es gab sie nicht in der Sprache, in der sie gesendet wurde), daher wurde nichts zugestellt. Erstellen Sie die Nachricht der Erinnerung neu und senden Sie sie erneut.",
        errTip_marketing_limit_reached: "Meta hat die Zustellung von Marketingnachrichten an diese Nummer pausiert – keine Abmeldung durch den Gast",
        errTip_130472: "Meta hat diese Nachricht im Rahmen eines eigenen Zustellungsexperiments zurückgehalten. Mit der Nummer ist alles in Ordnung, aber ein erneutes Senden wird genauso zurückgehalten – erreichen Sie den Gast auf anderem Weg.",
        errTip_130497: "Meta lässt unser WhatsApp-Konto derzeit keine Nummern in diesem Land anschreiben, ein erneutes Senden scheitert also genauso. Erreichen Sie den Gast per E-Mail oder über seinen persönlichen Link.",
        errTip_131063: "Diese Nummer ist nicht bei WhatsApp registriert und kann die Einladung daher nicht empfangen. Versuchen Sie eine andere Nummer oder erreichen Sie den Gast auf anderem Weg.",
        errTip_130429: "Wir haben vorübergehend unser eigenes Sendelimit erreicht – mit der Nummer ist alles in Ordnung. Ein erneutes Senden in Kürze sollte klappen.",
        errTip_BREVO_REJECTED: "Der SMS-Anbieter hat diese Nachricht nach der Annahme abgelehnt (meist ein vorübergehendes Guthabenproblem bei uns). Ein erneuter Versuch lohnt sich.",
        errTip_NOT_FOUND: "Kontakt nicht gefunden oder ungültig",
        errTipUnknown: "Unbekannter Fehler",
        // Card status lines, failure reasons and WhatsApp refusals (item M2, 2026-10-07).
        chEmail: "E-Mail",
        chText: "SMS",
        chWhatsApp: "WhatsApp",
        stSentN: "{n} gesendet",
        stAtSendTime: "wird zur geplanten Zeit gesendet",
        stGoingOut: "wird in den nächsten Minuten gesendet",
        stWaWaiting: "wartet darauf, dass WhatsApp die Nachricht freigibt (meist ein paar Minuten)",
        stWaWaitingLong: "wartet noch darauf, dass WhatsApp die Nachricht freigibt. Das dauert diesmal länger als üblich; sie wird nach der Freigabe automatisch gesendet",
        stWaSubmitting: "wird noch zur Freigabe gesendet",
        stRetrying: "Es wurde noch nichts gesendet. Wir versuchen es in ein paar Minuten erneut.",
        stSendingSoon: "Wird in den nächsten Minuten gesendet.",
        stWaRefused: "nicht angenommen. Bearbeiten und erneut senden.",
        stWaRefusedBelow: "nicht angenommen. Bearbeiten und erneut senden (den Grund finden Sie unten).",
        failFormMissing: "Das Formular dieser Nachricht existiert nicht mehr. Speichern Sie das Formular erneut und erstellen Sie dann eine neue Nachricht.",
        failWaOnly: "WhatsApp hat die Nachricht nicht angenommen, und alle Gäste darauf sind nur über WhatsApp erreichbar. Deshalb wurde nichts gesendet. Bearbeiten und erneut senden.",
        failWaRefused: "WhatsApp hat die Nachricht nicht angenommen, deshalb wurde nichts gesendet. Bearbeiten und erneut senden.",
        failNothingSent: "Nach mehreren Versuchen konnte nichts gesendet werden. Klicken Sie auf „Bearbeiten & erneut senden“, um es noch einmal zu versuchen. Wenn es wieder passiert, schreiben Sie uns im Chat.",
        failNoMethod: "Die Daten Ihrer Gäste sind nicht bei unserem Versandschritt angekommen. Das war ein Fehler auf unserer Seite, nicht in Ihrer Gästeliste. Klicken Sie auf „Bearbeiten & erneut senden“, um es noch einmal zu versuchen, und schreiben Sie uns im Chat, wenn es wieder passiert.",
        failSendError: "Beim Senden ist auf unserer Seite etwas schiefgelaufen. Es lag nicht an Ihnen. Klicken Sie auf „Bearbeiten & erneut senden“, um es noch einmal zu versuchen, und schreiben Sie uns im Chat, wenn es wieder passiert.",
        failGeneric: "Wir konnten diese Nachricht nicht senden. Klicken Sie auf „Bearbeiten & erneut senden“, um es noch einmal zu versuchen, oder schreiben Sie uns im Chat.",
        err131053Label: "Bild konnte nicht geladen werden",
        err131053Tip: "WhatsApp konnte das Einladungsbild nicht abrufen, deshalb wurde die Nachricht nicht zugestellt. Erneutes Senden funktioniert meistens.",
        errSmsBouncedLabel: "SMS nicht zugestellt",
        errSmsBouncedTip: "Das Mobilfunknetz hat die SMS nicht zugestellt. Die Nummer ist vielleicht falsch, ein Festnetzanschluss oder ausgeschaltet. Prüfen Sie die Nummer, schreiben Sie dem Gast eine E-Mail oder kopieren Sie seinen persönlichen Link und senden Sie ihn selbst.",
        errSmsFailedLabel: "SMS nicht gesendet",
        errSmsFailedTip: "Unser SMS-Anbieter hat diese Nachricht nicht gesendet. Erneutes Senden funktioniert meistens.",
        errSmsNonGsmLabel: "nicht per SMS möglich",
        errSmsNonGsmTip: "Diese Nachricht hat keinen englischen Text, und eine SMS kann nur diesen transportieren. Fügen Sie mit „Bearbeiten & erneut senden“ eine englische Fassung hinzu oder erreichen Sie diesen Gast per E-Mail.",
        errNonUsLabel: "SMS nur an US-Nummern",
        errNonUsTip: "Wir senden SMS nur an US-Nummern. Erreichen Sie diesen Gast per WhatsApp oder E-Mail.",
        errEmailHardLabel: "E-Mail-Adresse funktioniert nicht",
        errEmailHardTip: "Diese E-Mail-Adresse existiert nicht oder nimmt keine Nachrichten an. Prüfen Sie die Schreibweise oder erreichen Sie den Gast per SMS oder WhatsApp.",
        errEmailSoftLabel: "E-Mail vorerst nicht angenommen",
        errEmailSoftTip: "Das Postfach des Gastes hat die E-Mail diesmal nicht angenommen (oft, weil es voll oder kurz nicht erreichbar ist). Später erneut senden funktioniert meistens.",
        errEmailBounceLabel: "E-Mail zurückgewiesen",
        errEmailBounceTip: "Die E-Mail konnte an diese Adresse nicht zugestellt werden. Prüfen Sie die Schreibweise oder erreichen Sie den Gast per SMS oder WhatsApp.",
        errGenericTip: "Diese Nachricht konnte nicht zugestellt werden. Erreichen Sie diesen Gast auf anderem Weg oder senden Sie sie später erneut.",
        fbRetryUntilAll: "WhatsApp hält Marketingnachrichten an diese Gäste bis {time} zurück. Versuchen Sie es danach erneut.",
        fbRetryUntilSome: "WhatsApp hält Marketingnachrichten an einige dieser Gäste bis {time} zurück. Versuchen Sie es danach erneut.",
        fbChannelSub: "Auf dem gewählten Weg kommt diese Nachricht bei ihnen nicht an. Erinnern Sie sie per E-Mail oder kopieren Sie den persönlichen Link jedes Gastes aus der Liste unten.",
        fbChannelNoEmail: "Keiner dieser Gäste hat eine E-Mail-Adresse. Kopieren Sie den persönlichen Link jedes Gastes aus der Liste unten und senden Sie ihn selbst.",
        fbChannelSomeNoEmail: "{n} dieser Gäste haben keine E-Mail-Adresse, die E-Mail erreicht sie also nicht. Diese Liste schließt sich, wenn die E-Mail geöffnet wird. Kopieren Sie deshalb zuerst ihre persönlichen Links. Mit den {m} Gästen mit E-Mail-Adresse fortfahren?",
        ndTipWa: "WhatsApp hat die Nachricht an {n} Ihrer Gäste nicht zugestellt. Meta hält manchmal Nachrichten zum Schutz vor Spam zurück, oder die Nummer ist nicht bei WhatsApp. Öffnen Sie die Liste, um den Grund zu sehen, und erreichen Sie sie per E-Mail oder SMS.",
        ndTipSms: "{n} Ihrer SMS wurden nicht zugestellt, meist wegen einer falschen Nummer oder eines Festnetzanschlusses. Öffnen Sie die Liste, um den Grund zu sehen, und prüfen Sie dann die Nummer, schreiben Sie eine E-Mail oder kopieren Sie den persönlichen Link.",
        ndTipEmail: "{n} Ihrer E-Mails wurden zurückgewiesen, meist wegen einer falsch geschriebenen Adresse. Öffnen Sie die Liste, um den Grund zu sehen, und erreichen Sie die Gäste per SMS oder WhatsApp.",
        ndTipMixed: "Nicht zugestellt: {parts}. Öffnen Sie die Liste, um den Grund für jeden Gast zu sehen.",
        ndTipGeneric: "{n} Ihrer Gäste haben diese Nachricht nicht erhalten. Öffnen Sie die Liste, um den Grund zu sehen.",
        ndTipWaOne: "WhatsApp hat die Nachricht an einen Ihrer Gäste nicht zugestellt. Meta hält manchmal Nachrichten zum Schutz vor Spam zurück, oder die Nummer ist nicht bei WhatsApp. Öffnen Sie die Liste, um den Grund zu sehen, und erreichen Sie den Gast per E-Mail oder SMS.",
        ndTipSmsOne: "Eine Ihrer SMS wurde nicht zugestellt, meist wegen einer falschen Nummer oder eines Festnetzanschlusses. Öffnen Sie die Liste, um den Grund zu sehen, und prüfen Sie dann die Nummer, schreiben Sie eine E-Mail oder kopieren Sie den persönlichen Link.",
        ndTipEmailOne: "Eine Ihrer E-Mails wurde zurückgewiesen, meist wegen einer falsch geschriebenen Adresse. Öffnen Sie die Liste, um den Grund zu sehen, und erreichen Sie den Gast per SMS oder WhatsApp.",
        ndTipGenericOne: "Einer Ihrer Gäste hat diese Nachricht nicht erhalten. Öffnen Sie die Liste, um den Grund zu sehen.",
        tplErr_ours: "Auf unserer Seite ist etwas schiefgelaufen, und es lag nicht an Ihnen. Öffnen Sie „Bearbeiten & erneut senden“ und senden Sie sie von dort erneut (ohne Änderungen). Wenn es noch einmal passiert, sagen Sie uns Bescheid, und wir beheben es.",
        tplErr_trailingParam: "Ihre Nachricht darf nicht mit einem Personalisierungs-Tag wie {{name}} oder {{table_number}} ENDEN. WhatsApp erlaubt das nicht. Setzen Sie ein Wort oder einen Punkt hinter den Tag und senden Sie sie erneut.",
        tplErr_waLink: "WhatsApp erlaubt keinen Button, der zurück zu WhatsApp führt. Lassen Sie den Button woandershin verweisen und senden Sie die Nachricht erneut.",
        tplErr_imageFormat: "Für WhatsApp muss Ihr Einladungsbild ein JPG oder PNG sein. Laden Sie es in diesem Format erneut hoch und senden Sie die Nachricht erneut.",
        tplErr_imageProcess: "WhatsApp konnte Ihr Einladungsbild nicht verarbeiten. Versuchen Sie ein anderes (JPG oder PNG, unter 5 MB) und senden Sie die Nachricht erneut.",
        tplErr_category: "WhatsApp stuft Ihre Nachricht als eine andere Art von Nachricht ein, als wir sie eingeordnet haben. Senden Sie sie erneut, dann sollte sie durchgehen.",
        tplErr_layout: "WhatsApp konnte den Aufbau Ihrer Nachricht nicht lesen. Meist steht ein {{Tag}} allein in einer eigenen Zeile. Setzen Sie ihn in einen Satz und senden Sie die Nachricht erneut.",
        tplErr_duplicateName: "WhatsApp hat bereits eine Nachricht unter diesem Namen gespeichert. Benennen Sie die Nachricht um und senden Sie sie erneut.",
        tplErr_rateLimit: "Sie haben das Limit von WhatsApp für neue Nachrichten vorerst erreicht. Warten Sie kurz und senden Sie sie erneut. Es geht nichts verloren.",
        tplErr_noReason: "WhatsApp hat den Wortlaut dieser Nachricht nicht freigegeben. Bearbeiten Sie sie und reichen Sie sie erneut ein.",
        tplLangsHeadOne: "WhatsApp hat die Fassung Ihrer Nachricht auf {langs} abgelehnt.",
        tplLangsHeadMany: "WhatsApp hat diese Fassungen Ihrer Nachricht abgelehnt: {langs}.",
        tplLangsHeadUnknown: "WhatsApp hat eine der Sprachen Ihrer Nachricht abgelehnt.",
        tplLangsSaid: "WhatsApp schrieb: „{text}“.",
        tplLangsEditOne: "Bearbeiten Sie diese Übersetzung und reichen Sie sie erneut ein.",
        tplLangsEditMany: "Bearbeiten Sie diese Übersetzungen und reichen Sie sie erneut ein.",
        langCombined: "zweisprachig kombiniert",
        failWaRefusedPartial: "WhatsApp hat die Nachricht nicht angenommen, deshalb haben Ihre WhatsApp-Gäste sie nicht erhalten. Ihre Gäste per E-Mail und SMS haben sie bereits, senden Sie sie ihnen also nicht noch einmal. Ändern Sie den Text und senden Sie die Nachricht erneut an Ihre WhatsApp-Gäste.",
        fbPermanentSubEmail: "Die Nummer oder E-Mail-Adresse funktioniert nicht. Korrigieren Sie sie in der Gästeliste oder kopieren Sie den persönlichen Link des Gastes und senden Sie ihn selbst.",
        fbHeldBack: "{n} absichtlich nicht gesendet",
        fbHeldBackSub: "Diese haben wir zurückgehalten: Der Gast hat uns gebeten aufzuhören, sich abgemeldet, ist nicht bei WhatsApp, oder WhatsApp hat die Nummer pausiert. Jede Zeile unten sagt, was zutrifft. Gäste, die uns gebeten haben aufzuhören, bekommen nie wieder eine Nachricht.",
        tplLangsHeadCombined: "WhatsApp hat die kombinierte zweisprachige Fassung Ihrer Nachricht abgelehnt.",
        remFailFormMissing: "Das Formular dieser Erinnerung existiert nicht mehr. Speichern Sie das Formular erneut, bearbeiten Sie dann die Erinnerung mit dem Stift und speichern Sie sie.",
        remFailWaOnly: "WhatsApp hat die Erinnerung nicht angenommen, und alle Gäste darauf sind nur über WhatsApp erreichbar. Deshalb wurde nichts gesendet. Bearbeiten Sie die Erinnerung mit dem Stift und speichern Sie sie, um sie erneut einzureichen.",
        remFailWaRefused: "WhatsApp hat die Erinnerung nicht angenommen, deshalb wurde nichts gesendet. Bearbeiten Sie die Erinnerung mit dem Stift und speichern Sie sie, um sie erneut einzureichen.",
        remFailWaRefusedPartial: "WhatsApp hat die Erinnerung nicht angenommen, deshalb haben Ihre WhatsApp-Gäste sie nicht erhalten. Ihre Gäste per E-Mail und SMS haben sie bereits. Bearbeiten Sie die Erinnerung mit dem Stift und speichern Sie sie, um sie erneut einzureichen.",
        remFailNothingSent: "Nach mehreren Versuchen konnte nichts gesendet werden. Bearbeiten Sie die Erinnerung mit dem Stift und speichern Sie sie, um es noch einmal zu versuchen. Wenn es wieder passiert, schreiben Sie uns im Chat.",
        remFailNoMethod: "Die Daten Ihrer Gäste sind nicht bei unserem Versandschritt angekommen. Das war ein Fehler auf unserer Seite, nicht in Ihrer Gästeliste. Bearbeiten Sie die Erinnerung mit dem Stift und speichern Sie sie, um es noch einmal zu versuchen, und schreiben Sie uns im Chat, wenn es wieder passiert.",
        remFailSendError: "Beim Senden ist auf unserer Seite etwas schiefgelaufen. Es lag nicht an Ihnen. Bearbeiten Sie die Erinnerung mit dem Stift und speichern Sie sie, um es noch einmal zu versuchen, und schreiben Sie uns im Chat, wenn es wieder passiert.",
        remFailGeneric: "Wir konnten diese Erinnerung nicht senden. Bearbeiten Sie sie mit dem Stift und speichern Sie sie, um es noch einmal zu versuchen, oder schreiben Sie uns im Chat.",
        // Provider outage pause: see _providerPauseBadgeHtml.
        providerPaused: "Versand angehalten",
        providerPausedTip: "Ein Problem bei unserem Nachrichtenanbieter hat diese Nachricht angehalten, bevor alle sie erhalten haben. Für Gäste, die sie noch nicht erhalten haben, wurde Ihnen nichts berechnet. Unser Team ist informiert und sendet sie ihnen, sobald das Problem behoben ist. Sie müssen nichts tun.",
        providerPausedPhrase: "Wartet auf unseren Nachrichtenanbieter",
        providerResumeConfirm: "Diese Nachricht jetzt erneut senden? Nur Gäste, die sie noch nicht erhalten haben, bekommen sie.",
        providerResumed: "Wird neu gestartet. Gäste, die sie noch nicht erhalten haben, bekommen sie in den nächsten Minuten.",
        providerResumeFailed: "Die Nachricht konnte nicht fortgesetzt werden. Bitte versuchen Sie es erneut.",
        providerPausedPhraseAuto: "Wird automatisch erneut versucht",
        providerPausedTipAuto: "Ein vorübergehendes Problem bei unserem Nachrichtenanbieter hat diese Nachricht angehalten, bevor alle sie erhalten haben. Für Gäste, die sie noch nicht erhalten haben, wurde Ihnen nichts berechnet. Wir versuchen es in Kürze automatisch erneut, und nur diese Gäste erhalten sie. Sie können es auch jetzt mit „{resume}“ erneut versuchen.",
        providerResuming: "Wird neu gestartet. Das dauert meist ein paar Minuten.",
        providerResumedTeam: "Wir versuchen es jetzt erneut. Funktioniert unser Anbieter noch nicht, hält der Versand wieder an und geht raus, sobald unser Team das Problem behoben hat. Sie müssen sonst nichts tun.",
        sendRestartResumes: "Der Server wurde während des Versands dieser Nachricht neu gestartet. Gäste, die sie schon haben, bekommen sie nicht doppelt, und die übrigen erhalten sie in wenigen Minuten automatisch.",
        sendRestartRetry: "Der Server wurde während des Versands dieser Nachricht neu gestartet. Gäste, die sie schon haben, bekommen sie nicht doppelt. Senden Sie sie in einer Minute erneut, um die übrigen zu erreichen.",
        // Outage-held guests: see _outageHeldNoteHtml.
        heldPhrase: "{n} Gäste haben sie vielleicht schon",
        heldPhraseOne: "1 Gast hat sie vielleicht schon",
        heldWhy: "Während unser Nachrichtenanbieter gestört war, kam auf ihre WhatsApp-Nachricht oder SMS keine Antwort, sie ist also vielleicht trotzdem angekommen. Damit niemand sie doppelt erhält, haben wir sie ihnen nicht noch einmal gesendet. Alle anderen erhalten sie wie gewohnt.",
        heldMore: "und {n} weitere",
        heldSend: "Auch an diese {n} senden",
        heldSendOne: "Auch an diesen Gast senden",
        heldSendDone: "Erledigt. Wir haben sie ihnen gesendet.",
        heldSendWithResume: "Erledigt. Sie erhalten sie zusammen mit allen anderen, sobald der Versand weiterläuft.",
        heldSendBusy: "Diese Nachricht wird gerade gesendet. Versuchen Sie es erneut, wenn der Versand abgeschlossen ist.",
        heldSendPaused: "Setzen Sie diese Nachricht zuerst fort und senden Sie sie ihnen dann.",
        heldSendFailed: "Wir konnten sie ihnen gerade nicht senden. Bitte versuchen Sie es in ein paar Minuten erneut.",
        // Interrupted Add Contacts (PMb).
        errInterruptedLabel: "nicht gesendet (unterbrochen)",
        errInterruptedTip: "Der Versand wurde gestoppt, bevor dieser Gast erreicht war, weil unser Server neu gestartet wurde. Es wurde nichts gesendet und nichts berechnet; Sie können problemlos erneut senden, und das wird wie jeder Versand berechnet.",
        acNotSent: "{n} nicht gesendet: Der Versand wurde unterbrochen. Senden Sie sie ihnen über Status erneut",
        addRestartRetry: "Unser Server wurde gerade neu gestartet, daher wurde nichts hinzugefügt oder gesendet. Bitte versuchen Sie es in einer Minute erneut.",
        // Retry, resend explainer, ghost-template recovery, follow-up, CSV export.
        retryWhichMessage: "Wir können nicht erkennen, bei welcher Nachricht das fehlgeschlagen ist. Wählen Sie oben in diesem Fenster eine Nachricht aus und versuchen Sie es erneut.",
        retryAMessage: "eine Nachricht",
        retryScopeOne: "Wir senden ihnen „{n}“ erneut.",
        retryScopeMany: "Wir senden jede der {n} fehlgeschlagenen Nachrichten erneut, nur an diese Gäste.",
        retryConfirmOne: "Dieselbe Nachricht erneut an 1 Gast senden?",
        retryConfirmMany: "Dieselbe Nachricht erneut an {n} Gäste senden?",
        retryConfirmNobody: "Wer sie bereits erhalten hat, wird nicht erneut kontaktiert.",
        retryConfirmFree: "Das erneute Senden ist kostenlos: Das Guthaben für jede fehlgeschlagene Nachricht wurde bereits erstattet.",
        retryResending: "Wird erneut gesendet…",
        nothingTryLater: "Versuchen Sie es in ein paar Stunden erneut.",
        nothingTryAfter: "Versuchen Sie es nach {n} erneut.",
        nothingCooledOne: "WhatsApp hat diese Nachricht für 1 Gast in den letzten 24 Stunden abgelehnt, daher wurde dieser Gast ausgelassen, statt es erneut zu versuchen.",
        nothingCooledMany: "WhatsApp hat diese Nachricht für {n} Gäste in den letzten 24 Stunden abgelehnt, daher wurden sie ausgelassen, statt es erneut zu versuchen.",
        nothingDeadOne: "1 Gast ist nicht bei WhatsApp – ein erneutes Senden erreicht diesen Gast nicht. Versuchen Sie es per E-Mail oder SMS.",
        nothingDeadMany: "{n} Gäste sind nicht bei WhatsApp – ein erneutes Senden erreicht sie nicht. Versuchen Sie es per E-Mail oder SMS.",
        nothingReachedOne: "1 Gast hat diese Nachricht bereits (sie hat ihn auf anderem Weg erreicht), daher wurde sie nicht erneut gesendet.",
        nothingReachedMany: "{n} Gäste haben diese Nachricht bereits (sie hat sie auf anderem Weg erreicht), daher wurde sie nicht erneut gesendet.",
        nothingNobody: "Diesmal konnte niemand erreicht werden. Ein späterer Versuch lohnt sich.",
        ghostConfirm: "Die WhatsApp-Vorlage dieser Nachricht neu erstellen und sie erneut an die nicht erreichten Gäste senden?\n\nDas kann einige Minuten dauern. Es wird kein zusätzliches Guthaben berechnet.",
        ghostStarting: "Wird gestartet…",
        ghostRecovering: "Wird wiederhergestellt…",
        ghostButton: "Neu erstellen & erneut senden",
        ghostStarted: "Die WhatsApp-Vorlage wird neu erstellt und erneut gesendet – das dauert einige Minuten. Wir schicken Ihnen eine E-Mail, wenn es fertig ist.",
        ghostAlready: "Eine Wiederherstellung läuft bereits.",
        ghostNotNeeded: "Diese Nachricht zeigt das WhatsApp-Vorlagenproblem nicht mehr, es gibt also nichts neu zu erstellen.",
        ghostStartFailed: "Die Wiederherstellung konnte nicht gestartet werden. Bitte versuchen Sie es in ein paar Minuten erneut.",
        ghostFixed: "WhatsApp-Vorlage repariert.",
        ghostResent: "Erneut gesendet.",
        ghostResentToOne: "Erneut an 1 Gast gesendet.",
        ghostResentTo: "Erneut an {n} Gäste gesendet.",
        ghostEscalated: "Die neu erstellte Vorlage besteht die Prüfung von WhatsApp weiterhin nicht. Unser Team wurde benachrichtigt. Bitte wenden Sie sich an den Support.",
        ghostIssue: "Problem mit der WhatsApp-Vorlage.",
        ghostIssueBody: "{n} Nachrichten konnten nicht zugestellt werden, weil WhatsApp die Vorlage abgelehnt hat. Ein normaler neuer Versuch hilft nicht – erstellen Sie die Vorlage neu und senden Sie erneut.",
        followUpLoadedOne: "1 Kontakt für die Folgenachricht geladen",
        followUpLoaded: "{n} Kontakte für die Folgenachricht geladen",
        csvPhone: "Telefon",
        csvEmail: "E-Mail",
        csvDeliveryStatus: "Zustellstatus",
        csvDeliveryIssue: "Zustellproblem",
        csvRsvp: "Antwort",
        csvGuests: "Anzahl Gäste",
        csvPending: "Ausstehend",
        csvNotSent: "Nicht gesendet",
        csvAttending: "Zusage",
        csvDeclined: "Absage",
        csvMaybe: "Vielleicht",
        csvResponded: "Geantwortet",
        csvNoResponse: "Noch keine Antwort",
    },
    pt: {
        remWaOnlyWaitingTip: "Os seus convidados por e-mail e por SMS já têm este lembrete. Os convidados do WhatsApp recebem-no automaticamente assim que o WhatsApp (Meta) aprovar a mensagem.",
        remWaOnlyWaiting: "Os convidados do WhatsApp recebem-no após a aprovação",
        quietHoldSystemTip: "Este lembrete calhava durante a noite para alguns dos seus convidados, por isso espera até ser de dia para todos. Não precisa de fazer nada.",
        quietHoldLateTip: "O WhatsApp aprovou a sua mensagem quando era noite para alguns dos seus convidados. Para não os acordar, sai quando for de dia para todos. Não precisa de fazer nada.",
        remWaLost: "WhatsApp não enviado",
        remWaLostTip: "O WhatsApp não aceitou a mensagem deste lembrete, por isso os seus convidados do WhatsApp não a receberam. Os seus convidados por e-mail e por SMS já a têm. Edite o lembrete e guarde-o para o enviar aos convidados do WhatsApp.",
        remWaLostTimeoutTip: "O WhatsApp não aprovou a mensagem deste lembrete nas 48 horas seguintes à hora de envio, por isso os seus convidados do WhatsApp não a receberam. Os seus convidados por e-mail e por SMS já a têm. Edite o lembrete e guarde-o para o voltar a submeter; não precisa de alterar nada.",
        remWaLostPhrase: "Edite e guarde para enviar aos convidados do WhatsApp",
        waTimeoutBody: "O WhatsApp não aprovou esta mensagem nas 48 horas seguintes à hora de envio, por isso os seus convidados do WhatsApp não a receberam.",
        waOthersHaveIt: "Os seus convidados por e-mail e por SMS já a têm. «{editResend}» envia-a aos convidados do WhatsApp.",
        waRejectedBadgeTip: "O WhatsApp não aceitou esta mensagem. Edite a mensagem e volte a submetê-la ao WhatsApp.",
        previewTitle: "Pré-visualização da mensagem",
        previewNoBody: "(nenhum texto de mensagem guardado)",
        previewNoteOne: "No WhatsApp isto aparece como o botão acima (clique para testar o link).",
        previewNoteMany: "No WhatsApp e no e-mail estes aparecem como botões (clique em cada um para testar o link).",
        previewSmsNote: "Por SMS o link chega como texto:",
        previewSampleFor: "Exemplo mostrado para {name} — variáveis como {{first_name}} são preenchidas para cada convidado.",
        previewClone: "Duplicar como novo rascunho",
        close: "Fechar",
        addContacts: "Adicionar contactos",
        preview: "Pré-visualizar", status: "Estado", remind: "Lembrar", add: "Adicionar", reminders: "Lembretes", remindersNoneTip: "Ainda sem lembretes — envie um agora",
        duplicate: "Duplicar", delete: "Eliminar", edit: "Editar", editResend: "Editar e reenviar",
        personalLinks: "Links pessoais", sharedByHand: "Link pessoal", copyPersonalLink: "Copiar link pessoal", linkCopied: "Link pessoal copiado",
        sendOnWhatsApp: "Enviar por WhatsApp a partir do seu telefone", waWalkBtn: "Enviar do meu WhatsApp", waWalkIntro: "O nosso WhatsApp não conseguiu chegar a estes convidados. Envie a mensagem a cada um a partir do seu próprio WhatsApp: abra, toque em enviar e volte para o seguinte.", waWalkCounter: "Convidado {i} de {n}", waWalkOpen: "Abrir o WhatsApp", waWalkSkip: "Saltar", waWalkClose: "Fechar", waWalkDone: "Concluído. Abriu o WhatsApp para {k} de {n} convidados.",
        hostSentBadge: "Enviado do seu WhatsApp", hostSentTip: "Abriu o seu próprio WhatsApp para enviar esta mensagem. Não conseguimos ver dentro do WhatsApp, por isso a entrega não está confirmada.", waWalkCardBtn: "Enviar do meu WhatsApp ({n})", waWalkCardTip: "O nosso WhatsApp não conseguiu chegar a estes convidados. Envie-lhes esta mensagem a partir do seu próprio WhatsApp, um de cada vez.",
        waWalkImageNote: "O WhatsApp abre só com o texto. Para incluir a imagem do seu convite, anexe-a no WhatsApp antes de tocar em enviar.", waWalkImageOpen: "Abrir a imagem para a guardar",
        addedSkippedOptedOut: "{n} convidado(s) adicionado(s) não incluído(s): têm os lembretes desativados", addedSkippedResponded: "{n} convidado(s) adicionado(s) não incluído(s): já tinham respondido",
        tagContacts: "Etiquetar todos nesta mensagem", tagContactsPrompt: "Etiquetas para adicionar a todos os convidados desta mensagem, separadas por vírgulas (ex.: família, só recepção):", tagContactsDone: "{n} convidados etiquetados", tagContactsFailed: "Não foi possível etiquetar os convidados",
        continueBtn: "Continuar", pause: "Pausar", cancel: "Cancelar", sendNow: "Enviar agora",
        sendTest: "Enviar teste",
        sendTestTip: "Envie a si mesmo uma cópia desta mensagem para ver exatamente o que os convidados vão receber",
        sendTestSending: "A enviar o teste…",
        sendTestDone: "Teste enviado",
        sendTestEmailCopyOnly: "O e-mail é uma cópia só para você — seus convidados não receberão e-mail.",
        sendTestNoMessage: "Adicione uma mensagem e escolha como enviá-la; depois poderá enviar um teste a si mesmo",
        sendTestFailed: "Não foi possível enviar o teste",
        sendTestCreditReturned: "O crédito deste teste foi devolvido ao seu saldo.",
        sendTestCost: "O primeiro teste desta mensagem é gratuito. Depois, cada teste por SMS ou WhatsApp usa 1 crédito — testes por e-mail são sempre gratuitos.",
        sendTestAgain: "Enviar outro teste",
        sendTestAgainTip: "Envie a si mesmo outra cópia — útil depois de alterar o texto",
        sendTestPhoneTitle: "Para onde enviamos o teste?",
        sendTestPhoneHint: "A sua conta não tem número de telemóvel; escreva um para receber o teste. Vamos guardá-lo.",
        sendTestPhoneInvalid: "Isso não parece um número de telemóvel. Use o formato internacional, por exemplo +14155550123.",
        resume: "Retomar", buyCredits: "Comprar créditos", retry: "Tentar novamente",
        previewTip: "Pré-visualizar o texto da mensagem",
        statusTip: "Ver quem recebeu a mensagem e a resposta",
        remindTip: "Enviar um lembrete aos convidados desta mensagem",
        addTip: "Adicionar mais contactos a esta mensagem",
        addScheduledTip: "Adicione mais contactos — eles receberão a mensagem na hora de envio agendada",
        addDisabledTip: "Não é possível adicionar contactos — corrija primeiro esta mensagem em Editar e reenviar (o modelo do WhatsApp nunca chegou à Meta).",
        duplicateTip: "Reutilizar esta mensagem (imagem, texto, idiomas, link) numa mensagem nova",
        deleteTip: "Excluir permanentemente esta mensagem do seu histórico",
        pauseTip: "Pausar esta mensagem",
        buyCreditsTip: "Recarregue os seus créditos de mensagens e depois retome esta mensagem",
        retryTip: "Tentar enviar novamente (funciona quando tiver créditos suficientes)",
        manageRemindersTip: "Gerir lembretes agendados",
        scheduleRemindersTip: "Agendar lembretes",
        resumeAutoRetryTip: "Retomar nova tentativa automática ({n} pendentes)",
        pauseAutoRetryTip: "Pausar nova tentativa automática ({n} pendentes)",
        remindPending: "Convidados pendentes", remindPendingSub: "Ainda não responderam",
        remindMaybe: "Responderam talvez", remindMaybeSub: "Disseram talvez — incentive-os a confirmar",
        remindYes: "Responderam sim", remindYesSub: "Lembrete do dia, atualização do local, etc.",
        remindNo: "Responderam não", remindNoSub: "Não podem comparecer — envie um link de transmissão ao vivo, um agradecimento, etc.",
        remindRemovedOne: "O convidado que removeu não vai receber este lembrete.", remindRemovedMany: "Os {n} convidados que removeu não vão receber este lembrete.",
        // Response-edit (pencil) window: _openResponseEditModal and what it opens.
        reTitle: "Atualizar a resposta de {name}",
        reThisGuest: "este convidado",
        reContact: "contato",
        reManualEntry: "Registro manual:",
        reManualEntryNote: "Criado aqui porque não havia resposta no formulário.",
        reRemove: "Remover",
        reCurrentlyPaired: "Vinculada agora:",
        reUnpair: "Desvincular",
        reEditDetails: "Editar dados do contato",
        rePhone: "Telefone",
        reEmail: "E-mail",
        reInvitationHelp: "Escreva todos, separados por {sep} (ex.: {example}). As alterações chegam ao link do convidado automaticamente.",
        reChildMarker: "(criança)",
        reExample: "Carlos Silva; Bia (criança)",
        reMaxGuests: "Máx. de convidados",
        reMembersPh: "ex.: Carlos Silva; Bia (criança)",
        // Task #935: the guest-list columns (Add contacts, the contact window).
        acGuestNames: "Nome(s) dos convidados",
        acGuestNamesHint: "Como adicionar nomes de convidados\n• Separe os nomes com ponto e vírgula (;). Vírgulas também funcionam.\n• Acrescente (criança) após o nome de uma criança, p. ex. Pedro Silva (criança).\n• Exemplo: Ana Silva; João Silva; Pedro Silva (criança)\n• O telefone e o e-mail da linha são da primeira pessoa da lista.",
        acGroupName: "Nome do grupo",
        acGroupNameHint: "A quem o convite é dirigido, p. ex. Família Silva: no envelope e nas boas-vindas do formulário; as mensagens saúdam os nomes dos convidados. Sem nomes de convidados, eles o encontram por este nome. Deixe em branco para um convidado que vem sozinho.",
        acCsvColumns: "Colunas do CSV: Nome(s) dos convidados, Nome do grupo, Telefone, E-mail · opcionais: Máx. convidados, Etiquetas",
        acSameName: "Atenção: tem vários convidados ou grupos com o mesmo nome. Adicione um e-mail ou um telefone para cada um, para que possam confirmar a identidade ao procurar o convite.",
        acLastFirst: "Uma pessoa, com o sobrenome primeiro? Separe pessoas diferentes com \";\".",
        reGroupName: "Nome do grupo",
        reGroupNameHint: "A quem o convite é dirigido: o envelope e a saudação. Sem ninguém na lista, os convidados o encontram por este nome.",
        reMaxGuestsHint: "Deixe em branco para contar o grupo pelos nomes (ou pelo limite do formulário).",
        reGroupNamePh: "ex.: Família Silva",
        reGuestNames: "Nome(s) dos convidados",
        reTags: "Etiquetas",
        reTagsPh: "ex.: família, só recepção",
        reTagsHelp: "As etiquetas decidem quais partes do evento o link deste convidado mostra, quando uma parte é limitada a certas etiquetas.",
        reLoading: "Carregando…",
        reSave: "Salvar",
        rePairHeading: "Vincular a uma resposta existente",
        rePairHelp: "Se {name} respondeu pelo formulário mas a resposta não foi vinculada automaticamente (por exemplo, link compartilhado ou outro telefone), vincule aqui.",
        reLooking: "Procurando respostas correspondentes…",
        reManualHeading: "Ou registrar manualmente",
        reManualHelp: "Use só se {name} respondeu fora do formulário (mensagem, ligação, pessoalmente).",
        reAttending: "Vai",
        reDeclined: "Não vai",
        reMaybe: "Talvez",
        reResponded: "Respondeu",
        rePending: "Pendente",
        reSaving: "Salvando…",
        reSaved: "Salvo ✓",
        reCouldNotSave: "Não foi possível salvar",
        reNotOnMessage: "Este convidado não está mais na mensagem, então o convite dele não pode ser editado aqui.",
        rePartyMany: "O convite é para {n} pessoas.",
        rePartyOne: "O convite é para uma pessoa.",
        reLoadFailed: "Não foi possível carregar o convite.",
        reMaxInvalid: "Máx. de convidados deve ser um número inteiro de 1 a 50.",
        reBrowseAll: "Ver as {n} respostas sem vínculo",
        reBrowseOne: "Ver a resposta sem vínculo",
        reHideList: "Ocultar a lista",
        reCandidatesFailed: "Não foi possível carregar as possíveis correspondências.",
        reManualTag: "Manual",
        reManualTagTip: "Criado como registro manual em outro contato",
        reNoName: "(sem nome)",
        rePair: "Vincular",
        reLikely: "Correspondências prováveis",
        reNoUnpaired: "Não há respostas sem vínculo neste formulário. Se o convidado respondeu fora do formulário, use os botões de \"Ou registrar manualmente\".",
        reNoLikely: "Nenhuma correspondência provável por nome, telefone ou e-mail. Veja a lista completa se souber qual é a resposta dele.",
        reSearchPh: "Buscar por nome, telefone ou e-mail…",
        rePaired: "Resposta vinculada a {name}",
        reUnpaired: "{name} desvinculado",
        reRecorded: "{status} registrado para {name}",
        reManualRemoved: "Registro manual removido",
        reFailedPair: "Não foi possível vincular a resposta",
        reFailedUnpair: "Não foi possível desvincular",
        reFailedRecord: "Não foi possível registrar a resposta",
        reFailedRemove: "Não foi possível remover",
        reUpdatedNumber: "Número atualizado.",
        reUpdatedEmail: "E-mail atualizado.",
        reUpdatedDetails: "Dados do contato atualizados.",
        reResendAsk: "Enviar esta mensagem de novo para {dest}?",
        reMultiMessage: "Este contato aparece em mais de uma mensagem, então use {addContacts} na mensagem que você quer reenviar.",
        reResend: "Reenviar",
        reSending: "Enviando…",
        reSentNumber: "Enviado para o novo número; vai aparecer no histórico deste contato em instantes.",
        reQueued: "Na fila: será enviado sozinho assim que o WhatsApp aprovar esta mensagem, normalmente em poucos minutos.",
        reSentEmail: "Enviado para o novo e-mail; vai aparecer no histórico deste contato em instantes.",
        reSentBoth: "Enviado para o novo número e e-mail; vai aparecer no histórico deste contato em instantes.",
        reResendFailed: "Não foi possível reenviar. Tente de novo.",
        reminderDupSkipped: "Já receberam um lembrete de outra mensagem: {n}",
        reminderDupSkippedBy: "Já receberam o lembrete de «{name}»: {n}",
        reminderDupSkippedTip: "Estes convidados já receberam o mesmo lembrete de outra mensagem deste evento nas últimas 20 horas, por isso este não lhes foi enviado, para não o repetir.",
        reminderDupAll: "Todos já tinham recebido um lembrete de outra mensagem",
        remAttentionOne: "1 lembrete precisa da sua atenção",
        remAttentionMany: "{n} lembretes precisam da sua atenção",
        remFailExpiredNote: "O WhatsApp não aprovou a tempo a mensagem deste lembrete. Edite-o e guarde para tentar novamente.",
        remFailRejectedNote: "O WhatsApp não aceitou a mensagem deste lembrete. Edite a mensagem e guarde para a submeter novamente.",
        remAudienceBtn: "Ver para quem vai este lembrete",
        remAudienceTitle: "Para quem vai este lembrete",
        msgAudienceTitle: "Para quem vai esta mensagem",
        msgAudienceOne: "Esta mensagem vai para este convidado.",
        msgAudienceMany: "Esta mensagem vai para estes {n} convidados.",
        remAudienceNowOne: "Se fosse enviado agora, chegaria a 1 convidado. A lista só fica definida no momento do envio, por isso ainda pode mudar.",
        remAudienceNowMany: "Se fosse enviado agora, chegaria a {n} convidados. A lista só fica definida no momento do envio, por isso ainda pode mudar.",
        remAudienceFixedOne: "Este lembrete vai para este convidado.",
        remAudienceFixedMany: "Este lembrete vai para estes {n} convidados.",
        remAudienceEmpty: "Neste momento ninguém corresponde. A lista é verificada novamente quando o lembrete for enviado.",
        remAudienceFailed: "Não foi possível carregar os destinatários deste lembrete. Tente novamente.",
        remAudienceFixedNone: "Não há ninguém na lista deste lembrete, por isso não chegará a ninguém.",
        remAudienceMeantOne: "Este lembrete devia ir para este convidado. Só será enviado depois de o editar e guardar.",
        remAudienceMeantMany: "Este lembrete devia ir para estes {n} convidados. Só será enviado depois de o editar e guardar.",
        remFailWaOnlyRefusedNote: "O WhatsApp não aceitou a mensagem deste lembrete e os seus convidados só podem ser contactados por WhatsApp, por isso nada foi enviado. Uma causa comum é uma etiqueta de personalização mesmo no fim da mensagem: acrescente texto depois dela e guarde para a submeter novamente.",
        acSentOf: "{sent} de {total} enviados",
        acSkippedUntil: "{n} em pausa pelo WhatsApp até {time}; tente novamente nessa altura",
        acSkipped: "{n} ignorados",
        acUnreachable: "{n} não enviados (contactos)",
        acFailed: "{n} não alcançados",
        acDeferred: "{n} seguem na próxima janela de envio",
        acLineOne: "1 convidado adicionado após o envio",
        acLineMany: "{n} convidados adicionados após o envio",
        acLineLast: "último: {n}",
        acLineAwaiting: "{n} a aguardar a aprovação do WhatsApp",
        acTipAdded: "{when}: {n} adicionados",
        reResendCooldown: "O WhatsApp está a pausar as mensagens para este número até {time}. Não foi cobrado nada. Tente novamente depois disso, ou envie diretamente o link pessoal.",
        reResendCooldownNoTime: "O WhatsApp está a pausar as mensagens para este número por agora. Não foi cobrado nada. Tente novamente mais tarde, ou envie diretamente o link pessoal.",
        reResendNotSent: "Não foi possível enviar a mensagem para esses dados. Não foi cobrado nada. Verifique o número ou o endereço e tente novamente.",
        // Every guest skipped (Meta 24h cooldown, not on WhatsApp...): see _skipSummary and _mergeSendTimeFailures.
        skipNotSent: "Não enviada",
        skipNotSentTip: "Nada foi enviado: todos os convidados desta mensagem ficaram retidos. A nota abaixo explica porquê e o que pode fazer.",
        skippedOne: "1 retido",
        skippedMany: "{n} retidos",
        skippedTip: "Retidos de propósito, não enviados. Clique para ver quem e porquê.",
        skipCoolOne: "O WhatsApp recusou uma mensagem recente a 1 convidado, por isso estamos a pausar as mensagens para essa pessoa até {time}.",
        skipCoolMany: "O WhatsApp recusou mensagens recentes a {n} convidados, por isso estamos a pausar as mensagens para essas pessoas. Com o primeiro deles, pode voltar a tentar após {time}.",
        skipCoolLiftedOne: "O WhatsApp recusou uma mensagem recente a 1 convidado. A nossa pausa nas mensagens para essa pessoa já terminou.",
        skipCoolLiftedMany: "O WhatsApp recusou mensagens recentes a {n} convidados. A nossa pausa já terminou para o primeiro deles.",
        skipCoolWhy: "O WhatsApp recusa mensagens de marketing a quem recebeu muitas recentemente, e não diz durante quanto tempo. Depois de uma recusa, pausamos as nossas mensagens para esse número em vez de voltar a enviar. A pausa abrange todos os eventos no QuikRSVP, por isso a mensagem recusada pode não ter sido sua. Uma mensagem retida não é cobrada. Uma nova tentativa depois da pausa ainda pode ser recusada.",
        skipNoWaOne: "1 convidado não está no WhatsApp. Contacte-o por SMS ou e-mail, ou envie-lhe diretamente o link pessoal.",
        skipNoWaMany: "{n} convidados não estão no WhatsApp. Contacte-os por SMS ou e-mail, ou envie-lhes diretamente os links pessoais.",
        skipUnreachOne: "1 convidado não a pôde receber: o telefone ou o e-mail tem de ser corrigido. Abra a lista para ver quem e o quê.",
        skipUnreachMany: "{n} convidados não a puderam receber: o telefone ou o e-mail tem de ser corrigido. Abra a lista para ver quem e o quê.",
        skipOptOutOne: "1 convidado respondeu STOP às nossas mensagens de WhatsApp, por isso não lhe escrevemos por lá.",
        skipOptOutMany: "{n} convidados responderam STOP às nossas mensagens de WhatsApp, por isso não lhes escrevemos por lá.",
        skipUnsubOne: "1 convidado cancelou a subscrição dos seus e-mails. Contacte-o antes por WhatsApp ou SMS.",
        skipUnsubMany: "{n} convidados cancelaram a subscrição dos seus e-mails. Contacte-os antes por WhatsApp ou SMS.",
        skipDoRetryAt: "Tente novamente após {time}, ou abra Links pessoais e envie o link diretamente.",
        skipDoRetryNow: "Tente novamente agora, ou abra Links pessoais e envie o link diretamente.",
        skipDoLink: "Abra Links pessoais e envie o link diretamente.",
        skipHandledRepliedOne: "Não foi enviada por nós, mas este convidado já respondeu.",
        skipHandledRepliedMany: "Não foi enviada por nós, mas todos os convidados a que se destinava já responderam.",
        skipHandledLinkOne: "Não foi enviada por nós, mas este convidado já abriu o seu link pessoal.",
        skipHandledLinkMany: "Não foi enviada por nós, mas todos os convidados a que se destinava já abriram o seu link pessoal ou responderam.",
        skipStillBlockedOne: "Ainda estamos a pausar as mensagens para este convidado até {time}. Tente novamente depois disso, ou envie diretamente o link pessoal.",
        skipStillBlockedMany: "Ainda estamos a pausar as mensagens para estes convidados. Com o primeiro deles, pode voltar a tentar após {time}; até lá, envie diretamente os links pessoais.",
        skipRowCool: "O WhatsApp recusou uma mensagem recente para este número (talvez de outro evento), por isso estamos a pausar todas as mensagens para ele até {time}. Tente novamente depois disso, ou envie diretamente o link pessoal.",
        skipRowCoolLifted: "O WhatsApp recusou uma mensagem recente para este número (talvez de outro evento). A nossa pausa já terminou: pode tentar novamente, ou enviar diretamente o link pessoal.",
        skipRowNoWa: "Este número não está no WhatsApp. Contacte o convidado por SMS ou e-mail, ou envie diretamente o link pessoal.",
        skipRowNoWaNoEmail: "Este número não está no WhatsApp. Contacte o convidado por SMS, ou envie diretamente o link pessoal.",
        fbTipFailedEmail: "O WhatsApp não conseguiu entregar a mensagem, por isso foi enviada por e-mail.",
        fbTipFailedSms: "O WhatsApp não conseguiu entregar a mensagem, por isso foi enviada por SMS.",
        fbTipNoWa: "Este número não está no WhatsApp, por isso enviámos um e-mail ao convidado.",
        fbTipCool: "As mensagens de WhatsApp para este número estão em pausa de momento, por isso enviámos um e-mail ao convidado.",
        fbStateOpened: "A mensagem foi aberta.",
        fbStateDelivered: "A mensagem foi entregue.",
        fbStateOnWay: "A mensagem está a caminho.",
        skipRowOptOut: "Este convidado respondeu STOP às nossas mensagens de WhatsApp, por isso não lhe escrevemos por lá.",
        skipRowOptOutSms: "Este convidado respondeu STOP às nossas mensagens de texto, por isso não lhe enviamos mais SMS.",
        skipRowUnsub: "Este convidado cancelou a subscrição dos seus e-mails. Contacte-o antes por WhatsApp ou SMS.",
        skipRowOther: "Retido: este número não pode receber mensagens de WhatsApp de momento.",
        skipRowNoDetails: "Não enviado: sem telefone nem e-mail. Adicione um e reenvie, ou envie diretamente o link pessoal.",
        skipRowBadNumber: "Não enviado: este número não pode ser marcado. Verifique os dígitos e o indicativo do país e reenvie.",
        skipRowNoEmail: "Não enviado: definido para e-mail, mas sem endereço. Adicione um, ou envie pelo WhatsApp.",
        skipRowNoPhone: "Não enviado: definido para SMS, mas sem número. Adicione um e reenvie.",
        skipRowShared: "Não enviado: o mesmo número que {name}, e cada número recebe uma só mensagem. Dê-lhe um número ou e-mail próprio, ou junte-o ao convite de {name}.",
        skipRowSharedNoName: "Não enviado: outro convidado tem o mesmo número, e cada número recebe uma só mensagem. Dê-lhe um número ou e-mail próprio.",
        skipRowNoChannel: "Não enviado: não foi indicada forma de envio para este convidado. Edite os dados e reenvie.",
        skipBadge: "Retido",
        unsubBadge: "Subscrição cancelada",
        skipRetrySent: "Enviada. O cartão será atualizado dentro de instantes.",
        skipRetryFailed: "O WhatsApp não a aceitou desta vez. Abra Estado para ver porquê.",
        skipRetryStill: "Nada foi enviado: a pausa nas mensagens para estes convidados ainda se aplica. Tente novamente mais tarde, ou envie diretamente o link pessoal.",
        skipRetryHeld: "Retida para uma revisão rápida antes do envio. Será enviada assim que for aprovada.",
        skipRetryPaused: "Não enviada: está em pausa. O cartão mostra porquê.",
        skipRetryNoCredits: "Em pausa: adicione créditos para a enviar.",
        skipRetryRequested: "Nova tentativa pedida. O cartão mostra em que ponto está.",
        skipRetryError: "Não foi possível tentar novamente agora. Tente outra vez dentro de um minuto.",
        retryBusy: "Esta mensagem ainda está a ser enviada. Quando terminar, veja quem ainda precisa de a receber e tente de novo.",
        statusLoadFailed: "Não foi possível carregar a lista de convidados neste momento.",
        statusLoadExpired: "A sua sessão expirou. Recarregue a página para ver a lista de convidados.",
        statusLoadRetry: "Tentar de novo",
        statusLoadReload: "Recarregar a página",
        statusRefreshFailed: "Não foi possível atualizar a lista de convidados. Recarregue a página para ver o estado mais recente.",
        retrySentOne: "Mensagem reenviada a 1 convidado.",
        retrySentMany: "Mensagem reenviada a {n} convidados.",
        retryStillFailedOne: "1 convidado continua sem a receber.",
        retryStillFailedMany: "{n} convidados continuam sem a receber.",
        retryStaleView: "Um reenvio desta mensagem terminou depois de esta lista ser carregada, por isso desta vez nada foi enviado. A lista foi atualizada: veja quem ainda precisa de a receber e tente de novo.",
        acSmsConsentWhy: "Os convidados com números dos EUA recebem esta mensagem por SMS, e outros números +1 ou do Brasil podem recebê-la assim se o WhatsApp falhar.",
        acSmsConsent: "Confirmo que todos os destinatários consentiram em receber mensagens SMS de convite para o meu evento através da QuikRSVP.",
        acSmsDisclosure: "Os destinatários podem responder STOP para cancelar ou HELP para obter ajuda. Podem aplicar-se tarifas de mensagens e dados. A frequência varia.",
        waRejectedTitle: "O WhatsApp não aceitou esta mensagem",
        waOursTitle: "Não foi possível enviar as mensagens do WhatsApp",
        waTimeoutTitle: "O WhatsApp não aprovou esta mensagem a tempo",
        waSubmitPendingTitle: "Ainda estamos a enviar a sua mensagem ao WhatsApp para aprovação",
        waSubmitPendingBody: "Ainda não conseguimos contactar o WhatsApp. Vamos continuar a tentar automaticamente e enviamos-lhe um e-mail se o WhatsApp a recusar. Não precisa de fazer nada.",
        addWaitWaTip: "Poderá adicionar contactos assim que a sua mensagem chegar ao WhatsApp para aprovação. Continuamos a tentar; volte a ver dentro de alguns minutos.",
        remCancelled: "Cancelado",
        remCancelFormDeleted: "Não enviado: o evento foi eliminado.",
        remCancelDeadline: "Não enviado: as respostas fecharam antes da data de envio.",
        remCancelDateMoved: "Não enviado: o evento foi antecipado e a hora já tinha passado.",
        remOnHold: "Em espera",
        remHeldPostponed: "Em espera: o evento está adiado. Passa para a nova data quando a definir.",
        remCancelEventCancelled: "Não enviado: o evento foi cancelado.",
        remCancelPostponedLate: "Não enviado: o evento ficou adiado até mais de um dia depois da hora deste lembrete.",
        remCancelInvitation: "Não enviado: o respetivo convite foi eliminado ou cancelado.",
        remCancelLate: "Não enviado: o respetivo convite esteve parado até mais de um dia depois da hora deste lembrete.",
        remCancelNotSent: "Não enviado",
        remMovedWithEvent: "acompanhou a nova data do evento",
        remCancelDeadlineHint: "Altere o prazo de resposta ou retire o link de confirmação e depois edite o lembrete para o enviar.",
        remCancelDateMovedHint: "Edite-o para escolher uma nova hora.",
        remSendsSoon: "Será enviado dentro de uma hora",
        remDateMovedPast: "O evento foi antecipado e a nova hora deste lembrete já passou, por isso não será enviado. Edite-o para escolher uma nova hora.",
        remSendNowWaiting: "Este lembrete será enviado assim que o convite chegar a todos os convidados.",
        remSendNowNoRecipients: "Nenhum convidado corresponde a este lembrete, por isso nada foi enviado.",
        remSendNowTaken: "Este lembrete já está sendo enviado.",
        remSendNowCancelled: "Este lembrete foi cancelado porque o convite foi excluído ou cancelado.",
        remSendNowError: "Não conseguimos identificar agora para quem vai este lembrete. Nada foi enviado, e uma nova tentativa será feita automaticamente.",
        moreActions: "Mais ações", // the card's ... menu; was missing in every pack
        // Card badges and stats, reminder rows, the Status window, Remind All
        // and the card actions' confirms/toasts (code review 2026-10-07: all
        // English on every dashboard). See cCount for the ...One forms.
        badgeSent: "Enviado",
        badgeSending: "A enviar",
        badgeProcessing: "A processar",
        badgeScheduled: "Agendado",
        badgePaused: "Em pausa",
        badgeCancelled: "Cancelado",
        badgeDraft: "Rascunho",
        badgeExpired: "Expirado",
        badgeUnableToSend: "Não foi possível enviar",
        badgeNoCredits: "Créditos insuficientes",
        badgeInReview: "Em revisão",
        scheduledFor: "Agendada para {when}",
        sendingAt: "Envio: {when}",
        expiredTip: "O WhatsApp não aprovou esta mensagem a tempo. Use Editar e reenviar para enviá-la novamente.",
        noCreditsTip: "Esta mensagem foi colocada em pausa porque a sua conta ficou sem créditos. Compre mais créditos e volte a enviá-la para entregar as mensagens em falta.",
        inReviewTip: "Revemos as mensagens de contas novas antes de serem enviadas, e alguém está a rever a sua neste momento. Nada foi cobrado, e a sua mensagem e os seus contactos estão guardados. Será enviada automaticamente assim que for aprovada; fale connosco no chat se precisar dela mais cedo.",
        toggleRemindersTip: "Mostrar ou ocultar os lembretes desta mensagem",
        showRemindersAria: "Mostrar lembretes ({n})",
        hideRemindersAria: "Ocultar lembretes ({n})",
        expandAllReminders: "Expandir tudo",
        collapseAllReminders: "Recolher tudo",
        expandAllRemindersTip: "Mostrar os lembretes de todas as mensagens",
        collapseAllRemindersTip: "Ocultar os lembretes de todas as mensagens",
        remindersOn: "Lembretes ativos",
        statResponded: "{n} responderam",
        statRespondedOne: "{n} respondeu",
        statPending: "{n} pendentes",
        statPendingOne: "{n} pendente",
        statSent: "{n} enviadas",
        statSentOne: "{n} enviada",
        statContacts: "{n} contatos",
        statContactsOne: "{n} contato",
        statNotDelivered: "{n} não entregues",
        statNotDeliveredOne: "{n} não entregue",
        statRecipients: "{n} destinatários",
        statRecipientsOne: "{n} destinatário",
        allRepliedTip: "Todas as pessoas que a receberam já tinham respondido — era a elas que se destinava.",
        waCouldNotAccept: "O WhatsApp não conseguiu aceitar a sua mensagem. Edite a mensagem e volte a enviá-la.",
        untitledMessage: "Mensagem",
        editResendTip: "Corrigir o problema e voltar a enviar as mensagens de WhatsApp",
        timingDayOf: "No próprio dia",
        timing1DayBefore: "1 dia antes",
        timing3DaysBefore: "3 dias antes",
        timing1WeekBefore: "1 semana antes",
        timing2WeeksBefore: "2 semanas antes",
        audNoResponse: "Convidados sem resposta",
        audAttending: "Convidados que vão",
        audMaybe: "Convidados que talvez vão",
        audDeclined: "Convidados que não vão",
        audAll: "Todos os contactos",
        remTitleAudience: "Lembrete · {audience}",
        remTitleIndex: "Lembrete {n}",
        remWasSetFor: "Estava agendado para {when}",
        remNoCreditsTip: "Este lembrete não foi enviado porque não tem créditos suficientes. Compre mais créditos e retome-o para o enviar.",
        remAddCredits: "Adicione créditos para enviar este lembrete",
        remNoRecipients: "Sem destinatários",
        remNoMatching: "Nenhum destinatário corresponde",
        remResubmitTip: "A sua mensagem editada voltou ao WhatsApp para aprovação. Será enviada assim que for aprovada.",
        remResubmitting: "Novamente em aprovação",
        remSendsOnceApproved: "Envio a {when}, após aprovação",
        remWaitingApproval: "A aguardar aprovação do WhatsApp",
        remAllRepliedTip: "Todas as pessoas que receberam este lembrete já tinham respondido — era a elas que se destinava.",
        remExpiredTip: "O WhatsApp não aprovou a tempo a mensagem deste lembrete. Edite-o e tente novamente.",
        remRejectedTip: "O WhatsApp rejeitou o conteúdo da mensagem deste lembrete. Edite o lembrete e guarde-o para o voltar a submeter.",
        remFailedTip: "Houve um problema ao enviar este lembrete. Edite-o e tente novamente.",
        remRejected: "Mensagem rejeitada",
        remCouldntSend: "Não foi enviado",
        remTimedOut: "Prazo de aprovação do WhatsApp esgotado",
        remEditToResubmit: "Edite e guarde para voltar a submeter",
        remSendFailed: "Falha no envio",
        remWaitingTip: "A aguardar que o WhatsApp (Meta) aprove a mensagem deste lembrete — normalmente alguns minutos, por vezes mais. Será enviado automaticamente assim que for aprovado.",
        remSendingSoon: "Envio em breve",
        remBeingSentTip: "Este lembrete está a ser enviado agora.",
        remDueOnceApproved: "Previsto para {when} · é enviado quando o WhatsApp aprovar",
        remSendsWhenApproved: "É enviado assim que o WhatsApp aprovar",
        remDue: "Previsto para {when}",
        remSendingNow: "A enviar agora…",
        remProcessingTip: "Este lembrete está a ser enviado agora. O e-mail sai de imediato; se também for por WhatsApp, essa parte é enviada assim que a Meta aprovar a mensagem — normalmente em poucos minutos.",
        remSendingMinute: "A enviar agora — costuma demorar um ou dois minutos",
        remSends: "Envio a {when}",
        remPreviewTip: "Pré-visualizar a mensagem do lembrete",
        remEditTip: "Editar a mensagem, o momento ou os destinatários",
        remStatusTip: "Ver o estado de entrega — quem recebeu este lembrete e a resposta",
        remBuyCreditsTip: "Comprar créditos para enviar este lembrete",
        remResumeCreditsTip: "Retomar este lembrete quando tiver créditos",
        remResumeTip: "Retomar este lembrete",
        remPauseTip: "Pausar este lembrete",
        remDeleteTip: "Eliminar este lembrete",
        msgNotFound: "Mensagem não encontrada",
        remFromMainMessage: "Adicione lembretes a partir da mensagem principal, não de outro lembrete.",
        renamePrompt: "Mudar o nome desta mensagem:",
        renameEmpty: "O nome da mensagem não pode ficar vazio",
        renameFailed: "Não foi possível mudar o nome da mensagem: ",
        cancelConfirm: "Tem a certeza de que quer cancelar a mensagem «{name}»?\n\nEsta ação não pode ser anulada. As mensagens que ainda não foram enviadas não serão enviadas.",
        cancelDone: "Mensagem cancelada",
        cancelFailed: "Não foi possível cancelar a mensagem: ",
        resumeConfirm: "Retomar a mensagem «{name}»?\n\nSerá enviada a {when}.",
        resumeConfirmNoDate: "Retomar a mensagem «{name}»?\n\nSerá enviada na data agendada originalmente.",
        resumeDone: "Mensagem retomada",
        resumeFailed: "Não foi possível retomar a mensagem: ",
        pauseConfirm: "Pausar a mensagem «{name}»?\n\nNão será enviada à hora agendada. Pode retomá-la ou cancelá-la mais tarde.",
        pauseDone: "Mensagem em pausa",
        pauseFailed: "Não foi possível pausar a mensagem: ",
        pauseTooLate: "Esta mensagem terminou de ser enviada antes que pudesse ser pausada.",
        autoRetryPauseConfirm: "Pausar as novas tentativas automáticas de «{name}»?",
        autoRetryResumeConfirm: "Retomar as novas tentativas automáticas de «{name}»?",
        autoRetryNotRetriedOne: "{n} contacto não voltará a ser tentado automaticamente.",
        autoRetryNotRetriedMany: "{n} contactos não voltarão a ser tentados automaticamente.",
        autoRetryRetriedOne: "{n} contacto voltará a ser tentado na próxima tentativa agendada.",
        autoRetryRetriedMany: "{n} contactos voltarão a ser tentados na próxima tentativa agendada.",
        autoRetryPaused: "Novas tentativas automáticas em pausa",
        autoRetryResumed: "Novas tentativas automáticas retomadas",
        autoRetryPauseFailed: "Não foi possível pausar as novas tentativas automáticas: ",
        autoRetryResumeFailed: "Não foi possível retomar as novas tentativas automáticas: ",
        sendNowConfirm: "Enviar já a mensagem «{name}»?\n\nA data agendada já passou. A mensagem será enviada de imediato.",
        sendNowDone: "Mensagem enviada!",
        sendNowFailed: "Não foi possível enviar a mensagem: ",
        deleteDraftConfirm: "Tem a certeza de que quer eliminar o rascunho «{name}»?\n\nEsta ação não pode ser anulada.",
        deleteDraftFailed: "Não foi possível eliminar o rascunho: ",
        deleteConfirmActive: "Eliminar definitivamente «{name}»?\n\nEsta mensagem tem estatísticas de entrega e pode ter lembretes agendados. Ambos serão removidos.\n\nAs respostas dos convidados são mantidas (estão guardadas à parte).\n\nEsta ação não pode ser anulada.",
        deleteAlsoGuests: "Esta mensagem adicionou {n} convidados à sua lista que não responderam e não estão em nenhuma outra mensagem.\n\nRemovê-los também da lista de convidados?\n\nOK remove. Cancelar mantém.",
        deleteConfirmUnsent: "Eliminar definitivamente «{name}»?\n\nNada foi enviado com esta mensagem, por isso pode eliminá-la sem problema.\n\nEsta ação não pode ser anulada.",
        deleteFailed: "Não foi possível eliminar a mensagem: ",
        statusAllTitle: "Todos os contactos",
        statusAllSubtitle: "De todas as mensagens",
        statusAllMessages: "📊 Todas as mensagens",
        statusAllTags: "Todas as etiquetas", statusNoTag: "Sem etiqueta", statusTagsLine: "Etiquetas",
        statusTitleFallback: "Estado da mensagem",
        statusThisMessageOnly: "Apenas os contactos desta mensagem",
        statusAddedAfterLaunch: "{n} adicionados após o envio",
        statusAddedAfterLaunchOne: "{n} adicionado após o envio",
        statusContactCount: "{n} contactos",
        statusContactCountOne: "{n} contacto",
        exportNone: "Não há contactos para exportar",
        exportedOne: "{n} contacto exportado",
        exportedMany: "{n} contactos exportados",
        dlvSent: "Enviada",
        dlvDelivered: "Entregue",
        dlvRead: "Lida",
        dlvOpened: "Aberta",
        dlvClicked: "Clicada",
        dlvClickedTip: "O link de resposta (ou o seu site) foi aberto a partir de uma das suas mensagens. Além de uma resposta, é o sinal mais seguro de que o convidado o recebeu.",
        dlvReadTip: "Confirmação de leitura do próprio WhatsApp (vistos azuis), por isso foi lida.",
        dlvOpenedTip: "As imagens do e-mail carregaram, por isso provavelmente foi visto. O Apple Mail e outras apps carregam as imagens automaticamente quando o e-mail chega, por isso é um bom sinal, não uma prova.",
        dlvDeliveredEmailTip: "O fornecedor de e-mail do convidado aceitou a mensagem. Não conseguimos ver em que pasta ficou, e muitas apps (Outlook, Hotmail, e-mail do trabalho) nunca indicam a abertura, por isso pode já ter sido lido.",
        dlvDeliveredWaTip: "Chegou ao telemóvel (dois vistos do WhatsApp). Ainda sem confirmação de leitura, mas alguns desativam-na, por isso pode já ter sido lida.",
        dlvDeliveredSmsTip: "Chegou ao telemóvel por SMS. Os SMS nunca indicam se foram lidos.",
        dlvSentTip: "Foi entregue à rede. Ainda sem confirmação de que chegou.",
        dlvBounced: "Devolvida",
        dlvFailed: "Falhou",
        dlvSmsSent: "SMS enviado",
        dlvSmsFailed: "SMS falhou",
        detailNoEmailFallback: "Nenhum e-mail salvo, então não foi possível enviar por e-mail. Adicione um com o lápis para enviar.",
        deliveryFailed: "Falha na entrega",
        detailEmail: "E-mail",
        detailSmsFallback: "SMS alternativo",
        detailRsvp: "Resposta",
        noDetail: "Sem detalhes disponíveis",
        fallbackEmail: "Por e-mail",
        fallbackSms: "Por SMS",
        aMessageFailed: "uma mensagem falhou",
        aMessageFailedTip: "Uma das mensagens para este contacto não foi entregue, mas outra mensagem chegou até ele.",
        checkNumber: "verifique este número",
        checkNumberTip: "O WhatsApp não conseguiu entregar a este número tal como está guardado, e o número parece incompleto. Vale a pena corrigi-lo e voltar a enviar antes de concluir que o convidado não usa o WhatsApp.",
        rowDetailsTip: "Clique para ver todos os detalhes de entrega",
        updateResponseTip: "Atualizar resposta",
        linkColumn: "LINK",
        remCellOnAria: "Lembretes ativos — pausar",
        remCellOffAria: "Lembretes desativados — retomar",
        guestFallback: "Convidado",
        addName: "Adicionar nome",
        editNameTip: "Clique para editar o nome",
        nameEditPlaceholder: "Nome do convidado",
        nameEditSave: "Guardar",
        nameEditCancel: "Cancelar",
        nameSaveFailed: "Não foi possível guardar o nome",
        raLoadingAll: "A carregar todos os contactos...",
        raLoadingPending: "A carregar os contactos pendentes...",
        raLoadingYes: "A carregar quem respondeu sim...",
        raLoadingMaybe: "A carregar quem respondeu talvez...",
        raLoadingNo: "A carregar quem respondeu não...",
        raExcludedAll: "Todos os contactos estão excluídos dos lembretes",
        raExcludedPending: "Todos os convidados pendentes estão excluídos dos lembretes",
        raExcludedYes: "Todos os que responderam sim estão excluídos dos lembretes",
        raExcludedMaybe: "Todos os que responderam talvez estão excluídos dos lembretes",
        raExcludedNo: "Todos os que responderam não estão excluídos dos lembretes",
        raSkippedExcludedOne: "{n} convidado que excluiu dos lembretes foi ignorado",
        raSkippedExcludedMany: "{n} convidados que excluiu dos lembretes foram ignorados",
        raLoadFailed: "Não foi possível carregar os contactos",
        raNoneSelected: "Nenhum destinatário selecionado — cancelado.",
        raNoValidRecipients: "Não há destinatários válidos para enviar.",
        errLabel_131026: "o WhatsApp não conseguiu entregar",
        errLabel_131049: "bloqueado pela política da Meta",
        errLabel_131047: "janela de 24 h expirada",
        errLabel_131048: "bloqueado por spam",
        errLabel_131056: "limite de envio",
        errLabel_131058: "problema na mensagem",
        errLabel_131061: "mensagem suspensa pela Meta",
        errLabel_131062: "mensagem desativada pela Meta",
        errLabel_132001: "problema na mensagem",
        errLabel_130472: "retido por um teste da Meta",
        errLabel_130497: "WhatsApp bloqueado neste país",
        errLabel_131063: "não está no WhatsApp",
        errLabel_130429: "limite de envio atingido",
        errLabel_marketing_limit_reached: "limite de marketing da Meta",
        errLabel_BREVO_REJECTED: "rejeitado pelo fornecedor de SMS",
        errLabel_unknown: "falhou",
        errTip_131049: "A Meta bloqueou esta mensagem de marketing (limite de política ou de interação) — não é um cancelamento por parte do convidado. Usa o WhatsApp e pode ser contactado; voltar a enviar ao fim de cerca de um dia resulta em aproximadamente metade dos casos.",
        errTip_131026: "Este número não conseguiu receber a mensagem — talvez não use o WhatsApp, ou algum dígito esteja errado. Voltar a enviar raramente ajuda (chega cerca de 1 em cada 20), por isso é mais fiável contactá-lo de outra forma.",
        errTip_131048: "Número de telefone restringido",
        errTip_131056: "Limite de envio ultrapassado",
        errTip_131058: "Mensagem não aprovada",
        errTip_131047: "Janela de 24 horas expirada",
        errTip_131061: "A Meta suspendeu a mensagem de WhatsApp usada aqui, por isso não foi entregue. Isto afeta todos os convidados desta mensagem, não só este — é preciso criar a mensagem de novo.",
        errTip_131062: "Mensagem desativada",
        errTip_132001: "O WhatsApp rejeitou a mensagem (não existia no idioma em que foi enviada), por isso nada foi entregue. Crie de novo a mensagem do lembrete e volte a enviá-la.",
        errTip_marketing_limit_reached: "A Meta suspendeu a entrega de marketing a este número — não é um cancelamento por parte do convidado",
        errTip_130472: "A Meta reteve esta mensagem no âmbito de uma das suas próprias experiências de entrega. O número não tem nenhum problema, mas voltar a enviar esbarra na mesma retenção — contacte o convidado de outra forma.",
        errTip_130497: "De momento, a Meta não deixa a nossa conta de WhatsApp enviar mensagens para números deste país, por isso um reenvio falha da mesma forma. Contacte o convidado por e-mail ou com o link pessoal.",
        errTip_131063: "Este número não está registado no WhatsApp, por isso não pode receber o convite. Experimente outro número ou contacte o convidado de outra forma.",
        errTip_130429: "Atingimos o nosso próprio limite de envio de momento — o número não tem nenhum problema. Voltar a enviar daqui a pouco deverá funcionar.",
        errTip_BREVO_REJECTED: "O fornecedor de SMS rejeitou esta mensagem depois de a aceitar (normalmente um problema temporário de saldo de envio do nosso lado). Vale a pena tentar novamente.",
        errTip_NOT_FOUND: "Contacto não encontrado ou inválido",
        errTipUnknown: "Erro desconhecido",
        // Card status lines, failure reasons and WhatsApp refusals (item M2, 2026-10-07).
        chEmail: "E-mail",
        chText: "SMS",
        chWhatsApp: "WhatsApp",
        stSentN: "{n} enviados",
        stAtSendTime: "é enviado à hora agendada",
        stGoingOut: "é enviado nos próximos minutos",
        stWaWaiting: "à espera de que o WhatsApp aprove a mensagem (normalmente alguns minutos)",
        stWaWaitingLong: "ainda à espera de que o WhatsApp aprove a mensagem. Está a demorar mais do que o habitual; é enviada automaticamente assim que for aprovada",
        stWaSubmitting: "ainda a enviar para aprovação",
        stRetrying: "Ainda não foi enviado nada. Vamos tentar novamente dentro de alguns minutos.",
        stSendingSoon: "É enviado nos próximos minutos.",
        stWaRefused: "não aceite. Edite e volte a enviar.",
        stWaRefusedBelow: "não aceite. Edite e volte a enviar (o motivo está abaixo).",
        failFormMissing: "O formulário desta mensagem já não existe. Guarde novamente o formulário e depois crie uma mensagem nova.",
        failWaOnly: "O WhatsApp não aceitou a mensagem e todos os convidados só podem ser contactados pelo WhatsApp, por isso não foi enviado nada. Edite e volte a enviar.",
        failWaRefused: "O WhatsApp não aceitou a mensagem, por isso não foi enviado nada. Edite e volte a enviar.",
        failNothingSent: "Não foi possível enviar nada após várias tentativas. Carregue em «Editar e reenviar» para tentar novamente. Se voltar a acontecer, fale connosco no chat.",
        failNoMethod: "Os dados dos seus convidados não chegaram à nossa etapa de envio. O problema foi do nosso lado, não da sua lista de convidados. Carregue em «Editar e reenviar» para tentar novamente e fale connosco no chat se voltar a acontecer.",
        failSendError: "Algo correu mal do nosso lado durante o envio. Não foi nada que tenha feito. Carregue em «Editar e reenviar» para tentar novamente e fale connosco no chat se voltar a acontecer.",
        failGeneric: "Não conseguimos enviar esta mensagem. Carregue em «Editar e reenviar» para tentar novamente ou fale connosco no chat.",
        err131053Label: "não foi possível carregar a imagem",
        err131053Tip: "O WhatsApp não conseguiu obter a imagem do convite, por isso a mensagem não foi entregue. Voltar a enviar costuma resultar.",
        errSmsBouncedLabel: "SMS não entregue",
        errSmsBouncedTip: "A rede telefónica não entregou o SMS. O número pode estar errado, ser fixo ou estar desligado. Verifique o número, envie um e-mail ao convidado ou copie a ligação pessoal dele e envie-a você.",
        errSmsFailedLabel: "SMS não enviado",
        errSmsFailedTip: "O nosso fornecedor de SMS não enviou esta mensagem. Voltar a enviar costuma resultar.",
        errSmsNonGsmLabel: "não é possível por SMS",
        errSmsNonGsmTip: "Esta mensagem não tem texto em inglês, e os SMS só conseguem levar esse. Acrescente uma versão em inglês com «Editar e reenviar» ou contacte este convidado por e-mail.",
        errNonUsLabel: "SMS só para números dos EUA",
        errNonUsTip: "Só enviamos SMS para números dos EUA. Contacte este convidado por WhatsApp ou e-mail.",
        errEmailHardLabel: "o endereço de e-mail não funciona",
        errEmailHardTip: "Este endereço de e-mail não existe ou recusa mensagens. Verifique como está escrito ou contacte o convidado por SMS ou WhatsApp.",
        errEmailSoftLabel: "e-mail não aceite por agora",
        errEmailSoftTip: "A caixa de correio do convidado não aceitou o e-mail desta vez (muitas vezes por estar cheia ou temporariamente indisponível). Voltar a enviar mais tarde costuma resultar.",
        errEmailBounceLabel: "e-mail devolvido",
        errEmailBounceTip: "Não foi possível entregar o e-mail neste endereço. Verifique como está escrito ou contacte o convidado por SMS ou WhatsApp.",
        errGenericTip: "Não foi possível entregar esta mensagem. Contacte este convidado de outra forma ou volte a enviar mais tarde.",
        fbRetryUntilAll: "O WhatsApp está a reter as mensagens de marketing para estes convidados até {time}. Tente novamente depois disso.",
        fbRetryUntilSome: "O WhatsApp está a reter as mensagens de marketing para alguns destes convidados até {time}. Tente novamente depois disso.",
        fbChannelSub: "Esta mensagem não lhes chega pela via por onde foi enviada. Lembre-os por e-mail ou copie a ligação pessoal de cada convidado na lista abaixo.",
        fbChannelNoEmail: "Nenhum destes convidados tem endereço de e-mail. Copie a ligação pessoal de cada um na lista abaixo e envie-a você mesmo.",
        fbChannelSomeNoEmail: "{n} destes convidados não têm endereço de e-mail, por isso o e-mail não lhes chega. Esta lista fecha quando o e-mail abrir, por isso copie primeiro as ligações pessoais deles. Continuar com os {m} que têm e-mail?",
        ndTipWa: "O WhatsApp não entregou a mensagem a {n} dos seus convidados. Por vezes a Meta retém mensagens para evitar spam, ou o número não está no WhatsApp. Abra a lista para ver o motivo e contacte-os por e-mail ou SMS.",
        ndTipSms: "{n} dos seus SMS não foram entregues, normalmente por um número errado ou fixo. Abra a lista para ver o motivo e depois verifique o número, envie um e-mail ou copie a ligação pessoal.",
        ndTipEmail: "{n} dos seus e-mails foram devolvidos, normalmente por um endereço mal escrito. Abra a lista para ver o motivo e contacte-os por SMS ou WhatsApp.",
        ndTipMixed: "Não entregues: {parts}. Abra a lista para ver o motivo de cada convidado.",
        ndTipGeneric: "{n} dos seus convidados não receberam esta mensagem. Abra a lista para ver o motivo.",
        ndTipWaOne: "O WhatsApp não entregou a mensagem a um dos seus convidados. Por vezes a Meta retém mensagens para evitar spam, ou o número não está no WhatsApp. Abra a lista para ver o motivo e contacte-o por e-mail ou SMS.",
        ndTipSmsOne: "Um dos seus SMS não foi entregue, normalmente por um número errado ou fixo. Abra a lista para ver o motivo e depois verifique o número, envie um e-mail ou copie a ligação pessoal.",
        ndTipEmailOne: "Um dos seus e-mails foi devolvido, normalmente por um endereço mal escrito. Abra a lista para ver o motivo e contacte o convidado por SMS ou WhatsApp.",
        ndTipGenericOne: "Um dos seus convidados não recebeu esta mensagem. Abra a lista para ver o motivo.",
        tplErr_ours: "Algo correu mal do nosso lado, e não foi nada que tenha feito. Abra «Editar e reenviar» e envie novamente a partir daí (não precisa de alterar nada). Se acontecer outra vez, avise-nos e resolvemos.",
        tplErr_trailingParam: "A sua mensagem não pode TERMINAR com uma etiqueta de personalização como {{name}} ou {{table_number}}. O WhatsApp não o permite. Coloque uma palavra ou um ponto final depois da etiqueta e volte a enviar.",
        tplErr_waLink: "O WhatsApp não permite um botão com ligação de volta para o WhatsApp. Aponte esse botão para outro sítio e volte a enviar.",
        tplErr_imageFormat: "Para o WhatsApp, a imagem do seu convite tem de ser JPG ou PNG. Carregue-a novamente nesse formato e volte a enviar.",
        tplErr_imageProcess: "O WhatsApp não conseguiu processar a imagem do seu convite. Experimente outra (JPG ou PNG, com menos de 5 MB) e volte a enviar.",
        tplErr_category: "O WhatsApp considera a sua mensagem de um tipo diferente daquele em que a registámos. Volte a enviar e deverá passar.",
        tplErr_layout: "O WhatsApp não conseguiu ler o formato da sua mensagem. Normalmente é uma {{etiqueta}} sozinha numa linha. Coloque-a dentro de uma frase e volte a enviar.",
        tplErr_duplicateName: "O WhatsApp já tem uma mensagem guardada com este nome. Mude o nome da mensagem e volte a enviar.",
        tplErr_rateLimit: "Atingiu por agora o limite de mensagens novas do WhatsApp. Aguarde um pouco e volte a enviar. Não se perde nada.",
        tplErr_noReason: "O WhatsApp não aprovou o texto desta mensagem. Edite-o e volte a submeter.",
        tplLangsHeadOne: "O WhatsApp recusou a versão em {langs} da sua mensagem.",
        tplLangsHeadMany: "O WhatsApp recusou estas versões da sua mensagem: {langs}.",
        tplLangsHeadUnknown: "O WhatsApp recusou um dos idiomas da sua mensagem.",
        tplLangsSaid: "O WhatsApp indicou: «{text}».",
        tplLangsEditOne: "Edite essa tradução e volte a submetê-la.",
        tplLangsEditMany: "Edite essas traduções e volte a submetê-las.",
        langCombined: "dois idiomas combinados",
        failWaRefusedPartial: "O WhatsApp não aceitou a mensagem, por isso os seus convidados do WhatsApp não a receberam. Os seus convidados por e-mail e por SMS já a têm, por isso não lha volte a enviar. Altere o texto e volte a enviá-la aos convidados do WhatsApp.",
        fbPermanentSubEmail: "O número ou o endereço de e-mail não funciona. Corrija-o na lista de convidados ou copie a ligação pessoal do convidado e envie-a você.",
        fbHeldBack: "{n} não enviados de propósito",
        fbHeldBackSub: "Retivemos estes: o convidado pediu para pararmos, cancelou a inscrição, não está no WhatsApp, ou o WhatsApp pausou o número. Cada linha abaixo diz qual. Nunca voltamos a enviar mensagens a quem pediu para pararmos.",
        tplLangsHeadCombined: "O WhatsApp recusou a versão combinada em dois idiomas da sua mensagem.",
        remFailFormMissing: "O formulário deste lembrete já não existe. Guarde novamente o formulário e depois edite o lembrete com o lápis e guarde-o.",
        remFailWaOnly: "O WhatsApp não aceitou o lembrete e todos os convidados só podem ser contactados pelo WhatsApp, por isso não foi enviado nada. Edite o lembrete com o lápis e guarde-o para o submeter novamente.",
        remFailWaRefused: "O WhatsApp não aceitou o lembrete, por isso não foi enviado nada. Edite o lembrete com o lápis e guarde-o para o submeter novamente.",
        remFailWaRefusedPartial: "O WhatsApp não aceitou o lembrete, por isso os seus convidados do WhatsApp não o receberam. Os seus convidados por e-mail e por SMS já o têm. Edite o lembrete com o lápis e guarde-o para o submeter novamente.",
        remFailNothingSent: "Não foi possível enviar nada após várias tentativas. Edite o lembrete com o lápis e guarde-o para tentar novamente. Se voltar a acontecer, fale connosco no chat.",
        remFailNoMethod: "Os dados dos seus convidados não chegaram à nossa etapa de envio. O problema foi do nosso lado, não da sua lista de convidados. Edite o lembrete com o lápis e guarde-o para tentar novamente, e fale connosco no chat se voltar a acontecer.",
        remFailSendError: "Algo correu mal do nosso lado durante o envio. Não foi nada que tenha feito. Edite o lembrete com o lápis e guarde-o para tentar novamente, e fale connosco no chat se voltar a acontecer.",
        remFailGeneric: "Não conseguimos enviar este lembrete. Edite-o com o lápis e guarde-o para tentar novamente, ou fale connosco no chat.",
        // Provider outage pause: see _providerPauseBadgeHtml.
        providerPaused: "Envio interrompido",
        providerPausedTip: "Um problema no nosso fornecedor de mensagens interrompeu esta mensagem antes de chegar a todos. Não lhe foi cobrado nada pelos convidados que ainda não a receberam. A nossa equipa já foi avisada e vai enviá-la assim que o problema estiver resolvido. Não precisa de fazer nada.",
        providerPausedPhrase: "À espera do nosso fornecedor de mensagens",
        providerResumeConfirm: "Tentar enviar esta mensagem novamente agora? Só a recebem os convidados que ainda não a receberam.",
        providerResumed: "A reiniciar. Os convidados que ainda não a receberam vão recebê-la nos próximos minutos.",
        providerResumeFailed: "Não foi possível retomar esta mensagem. Tente novamente.",
        providerPausedPhraseAuto: "Vamos tentar novamente de forma automática",
        providerPausedTipAuto: "Um problema temporário no nosso fornecedor de mensagens interrompeu esta mensagem antes de chegar a todos. Não lhe foi cobrado nada pelos convidados que ainda não a receberam. Vamos tentar novamente de forma automática daqui a pouco, e só eles a vão receber. Também pode tentar já com «{resume}».",
        providerResuming: "A reiniciar. Costuma demorar alguns minutos.",
        providerResumedTeam: "Vamos tentar novamente agora. Se o nosso fornecedor continuar sem funcionar, o envio volta a parar e segue assim que a nossa equipa resolver o problema. Não precisa de fazer mais nada.",
        sendRestartResumes: "O servidor reiniciou enquanto esta mensagem estava a ser enviada. Os convidados que já a receberam não a recebem duas vezes, e os restantes vão recebê-la automaticamente dentro de alguns minutos.",
        sendRestartRetry: "O servidor reiniciou enquanto esta mensagem estava a ser enviada. Os convidados que já a receberam não a recebem duas vezes. Volte a enviá-la daqui a um minuto para chegar aos restantes.",
        // Outage-held guests: see _outageHeldNoteHtml.
        heldPhrase: "Talvez {n} convidados já a tenham recebido",
        heldPhraseOne: "Talvez 1 convidado já a tenha recebido",
        heldWhy: "Enquanto o nosso fornecedor de mensagens esteve em baixo, o WhatsApp ou o SMS destes convidados ficou sem resposta, por isso pode ter chegado na mesma. Para que ninguém a receba duas vezes, não voltámos a enviá-la. Todos os outros recebem-na normalmente.",
        heldMore: "e mais {n}",
        heldSend: "Enviar também a estes {n}",
        heldSendOne: "Enviar também a este convidado",
        heldSendDone: "Feito. A mensagem foi-lhes enviada.",
        heldSendWithResume: "Feito. Vão recebê-la com todos os outros quando o envio for retomado.",
        heldSendBusy: "Esta mensagem está a ser enviada neste momento. Tente novamente quando terminar.",
        heldSendPaused: "Retome primeiro esta mensagem e só depois a envie a estes convidados.",
        heldSendFailed: "Não foi possível enviá-la agora. Tente novamente daqui a alguns minutos.",
        // Interrupted Add Contacts (PMb).
        errInterruptedLabel: "não enviado (interrompido)",
        errInterruptedTip: "O envio parou antes de chegar a este convidado porque o nosso servidor reiniciou. Não lhe foi enviado nem cobrado nada; pode voltar a enviar sem problema, e o reenvio é cobrado como qualquer envio.",
        acNotSent: "{n} não enviados: o envio foi interrompido. Volte a enviar a partir de Estado",
        addRestartRetry: "O nosso servidor estava a reiniciar, por isso nada foi adicionado nem enviado. Tente novamente daqui a um minuto.",
        // Retry, resend explainer, ghost-template recovery, follow-up, CSV export.
        retryWhichMessage: "Não conseguimos saber em que mensagem estas falharam. Escolha uma mensagem no topo desta janela e tente de novo.",
        retryAMessage: "uma mensagem",
        retryScopeOne: "Vamos reenviar «{n}» a estes convidados.",
        retryScopeMany: "Vamos reenviar cada uma das {n} mensagens que falharam, apenas a esses convidados.",
        retryConfirmOne: "Enviar novamente a mesma mensagem a 1 convidado?",
        retryConfirmMany: "Enviar novamente a mesma mensagem a {n} convidados?",
        retryConfirmNobody: "Ninguém que já a recebeu é contactado de novo.",
        retryConfirmFree: "Reenviar é gratuito: o crédito de cada mensagem falhada já foi devolvido.",
        retryResending: "A reenviar…",
        nothingTryLater: "Tente de novo daqui a algumas horas.",
        nothingTryAfter: "Tente de novo depois de: {n}.",
        nothingCooledOne: "O WhatsApp recusou esta mensagem para 1 convidado nas últimas 24 horas, por isso ficou de fora em vez de insistirmos.",
        nothingCooledMany: "O WhatsApp recusou esta mensagem para {n} convidados nas últimas 24 horas, por isso ficaram de fora em vez de insistirmos.",
        nothingDeadOne: "1 convidado não está no WhatsApp: um reenvio não o consegue alcançar. Experimente por e-mail ou SMS.",
        nothingDeadMany: "{n} convidados não estão no WhatsApp: um reenvio não os consegue alcançar. Experimente por e-mail ou SMS.",
        nothingReachedOne: "1 convidado já tem esta mensagem (chegou-lhe por outra via), por isso não foi reenviada.",
        nothingReachedMany: "{n} convidados já têm esta mensagem (chegou-lhes por outra via), por isso não foi reenviada.",
        nothingNobody: "Desta vez não foi possível chegar a ninguém. Vale a pena tentar de novo mais tarde.",
        ghostConfirm: "Reconstruir o modelo de WhatsApp desta mensagem e reenviá-la aos convidados que não a receberam?\n\nPode demorar alguns minutos. Não são cobrados créditos extra.",
        ghostStarting: "A iniciar…",
        ghostRecovering: "A recuperar…",
        ghostButton: "Recriar e reenviar",
        ghostStarted: "A reconstruir o modelo de WhatsApp e a reenviar; demora alguns minutos. Enviamos-lhe um e-mail quando terminar.",
        ghostAlready: "Já está em curso uma recuperação.",
        ghostNotNeeded: "Esta mensagem já não mostra o problema do modelo de WhatsApp, por isso não há nada para reconstruir.",
        ghostStartFailed: "Não foi possível iniciar a recuperação. Tente de novo daqui a alguns minutos.",
        ghostFixed: "Modelo de WhatsApp corrigido.",
        ghostResent: "Reenviada.",
        ghostResentToOne: "Reenviada a 1 convidado.",
        ghostResentTo: "Reenviada a {n} convidados.",
        ghostEscalated: "O modelo reconstruído continua a falhar a verificação do WhatsApp. A nossa equipa já foi avisada. Contacte o suporte.",
        ghostIssue: "Problema com o modelo de WhatsApp.",
        ghostIssueBody: "{n} mensagens não foram entregues porque o WhatsApp recusou o modelo. Uma nova tentativa normal não resolve: reconstrua o modelo e reenvie.",
        followUpLoadedOne: "1 contacto carregado para o seguimento",
        followUpLoaded: "{n} contactos carregados para o seguimento",
        csvPhone: "Telefone",
        csvEmail: "E-mail",
        csvDeliveryStatus: "Estado da entrega",
        csvDeliveryIssue: "Problema de entrega",
        csvRsvp: "Resposta RSVP",
        csvGuests: "Número de convidados",
        csvPending: "Pendente",
        csvNotSent: "Não enviada",
        csvAttending: "Vai",
        csvDeclined: "Não vai",
        csvMaybe: "Talvez",
        csvResponded: "Respondeu",
        csvNoResponse: "Ainda sem resposta",
    },
    he: {
        remWaOnlyWaitingTip: "האורחים שלכם באימייל וב-SMS כבר קיבלו את התזכורת. האורחים בוואטסאפ יקבלו אותה אוטומטית ברגע שוואטסאפ (Meta) תאשר את ההודעה.",
        remWaOnlyWaiting: "האורחים בוואטסאפ יקבלו אותה לאחר האישור",
        quietHoldSystemTip: "התזכורת הייתה אמורה לצאת בשעות הלילה אצל חלק מהאורחים, ולכן היא ממתינה עד שיהיה יום אצל כולם. אין צורך לעשות דבר.",
        quietHoldLateTip: "וואטסאפ אישרה את ההודעה בשעות הלילה אצל חלק מהאורחים. כדי לא להעיר אותם, היא תישלח כשיהיה יום אצל כולם. אין צורך לעשות דבר.",
        remWaLost: "לא נשלח בוואטסאפ",
        remWaLostTip: "וואטסאפ לא אישרה את ההודעה של התזכורת, ולכן האורחים בוואטסאפ לא קיבלו אותה. האורחים שלכם באימייל וב-SMS כבר קיבלו אותה. ערכו את התזכורת ושמרו כדי לשלוח אותה לאורחים בוואטסאפ.",
        remWaLostTimeoutTip: "וואטסאפ לא אישרה את ההודעה של התזכורת תוך 48 שעות ממועד השליחה, ולכן האורחים בוואטסאפ לא קיבלו אותה. האורחים שלכם באימייל וב-SMS כבר קיבלו אותה. ערכו את התזכורת ושמרו כדי לשלוח אותה שוב לאישור — אין צורך לשנות דבר.",
        remWaLostPhrase: "ערכו ושמרו כדי לשלוח לאורחים בוואטסאפ",
        waTimeoutBody: "וואטסאפ לא אישרה את ההודעה תוך 48 שעות ממועד השליחה, ולכן האורחים בוואטסאפ לא קיבלו אותה.",
        waOthersHaveIt: "האורחים שלכם באימייל וב-SMS כבר קיבלו אותה. דרך {editResend} היא תישלח לאורחים בוואטסאפ.",
        waRejectedBadgeTip: "וואטסאפ דחתה את ההודעה. ערכו את ההודעה ושלחו אותה שוב לאישור של וואטסאפ.",
        previewTitle: "תצוגה מקדימה של ההודעה",
        previewNoBody: "(לא נשמר טקסט להודעה)",
        previewNoteOne: "בוואטסאפ זה מופיע ככפתור שלמעלה (לחצו כדי לבדוק את הקישור).",
        previewNoteMany: "בוואטסאפ ובאימייל אלה מופיעים ככפתורים (לחצו על כל אחד כדי לבדוק את הקישור).",
        previewSmsNote: "ב-SMS הקישור מגיע כטקסט:",
        previewSampleFor: "דוגמה עבור {name} — משתנים כמו {{first_name}} מתמלאים עבור כל אורח.",
        previewClone: "שכפול כטיוטה חדשה",
        close: "סגירה",
        addContacts: "הוספת אנשי קשר",
        preview: "תצוגה מקדימה", status: "סטטוס", remind: "תזכורת", add: "הוספה", reminders: "תזכורות", remindersNoneTip: "אין תזכורות עדיין — שלחו אחת עכשיו",
        duplicate: "שכפול", delete: "מחיקה", edit: "עריכה", editResend: "עריכה ושליחה מחדש",
        personalLinks: "קישורים אישיים", sharedByHand: "קישור אישי", copyPersonalLink: "העתקת קישור אישי", linkCopied: "הקישור האישי הועתק",
        sendOnWhatsApp: "שליחה בוואטסאפ מהטלפון שלך", waWalkBtn: "שליחה מהוואטסאפ שלי", waWalkIntro: "הוואטסאפ שלנו לא הצליח להגיע לאורחים האלה. אפשר לשלוח לכל אחד את ההודעה מהוואטסאפ שלך: פותחים, לוחצים על שליחה וחוזרים לאורח הבא.", waWalkCounter: "אורח {i} מתוך {n}", waWalkOpen: "פתיחת וואטסאפ", waWalkSkip: "דילוג", waWalkClose: "סגירה", waWalkDone: "סיימת. פתחת וואטסאפ עבור {k} מתוך {n} אורחים.",
        hostSentBadge: "נשלח מהוואטסאפ שלך", hostSentTip: "פתחת את הוואטסאפ שלך כדי לשלוח את ההודעה הזו. אין לנו גישה לתוך וואטסאפ, ולכן המסירה לא מאומתת.", waWalkCardBtn: "שליחה מהוואטסאפ שלי ({n})", waWalkCardTip: "הוואטסאפ שלנו לא הצליח להגיע לאורחים האלה. אפשר לשלוח להם את ההודעה מהוואטסאפ שלך, אחד אחרי השני.",
        waWalkImageNote: "וואטסאפ נפתח עם הטקסט בלבד. כדי לצרף את תמונת ההזמנה, מצרפים אותה בוואטסאפ לפני שלוחצים על שליחה.", waWalkImageOpen: "פתיחת התמונה כדי לשמור אותה",
        addedSkippedOptedOut: "{n} מהאורחים שנוספו לא קיבלו: התזכורות כבויות עבורם", addedSkippedResponded: "{n} מהאורחים שנוספו לא קיבלו: הם כבר השיבו",
        tagContacts: "תיוג כל המוזמנים בהודעה הזו", tagContactsPrompt: "תגיות להוספה לכל האורחים בהודעה הזו, מופרדות בפסיקים (לדוגמה: משפחה, קבלת פנים בלבד):", tagContactsDone: "{n} אורחים תויגו", tagContactsFailed: "לא ניתן היה לתייג את האורחים",
        continueBtn: "המשך", pause: "השהיה", cancel: "ביטול", sendNow: "שליחה עכשיו",
        sendTest: "שליחת בדיקה",
        sendTestTip: "שלחו לעצמכם עותק אחד של ההודעה כדי לראות בדיוק מה האורחים יקבלו",
        sendTestSending: "שולח בדיקה…",
        sendTestDone: "הבדיקה נשלחה אליכם",
        sendTestEmailCopyOnly: "האימייל הוא עותק בשבילכם בלבד — האורחים לא יקבלו אימייל.",
        sendTestNoMessage: "הוסיפו הודעה ובחרו איך לשלוח אותה, ואז תוכלו לשלוח לעצמכם בדיקה",
        sendTestFailed: "לא הצלחנו לשלוח את הבדיקה",
        sendTestCreditReturned: "הקרדיט על הבדיקה הזו הוחזר ליתרה שלכם.",
        sendTestCost: "הבדיקה הראשונה בהודעה הזו בחינם. אחרי זה כל בדיקה ב-SMS או בוואטסאפ עולה קרדיט אחד — בדיקות באימייל תמיד בחינם.",
        sendTestAgain: "שליחת בדיקה נוספת",
        sendTestAgainTip: "שלחו לעצמכם עותק נוסף — שימושי אחרי שינוי הנוסח",
        sendTestPhoneTitle: "לאן לשלוח את הבדיקה?",
        sendTestPhoneHint: "בחשבון שלכם אין מספר נייד, אז הזינו מספר שאליו תישלח הבדיקה. אנחנו נזכור אותו.",
        sendTestPhoneInvalid: "זה לא נראה כמו מספר נייד. השתמשו בפורמט הבינלאומי, למשל ‎+14155550123.",
        resume: "חידוש", buyCredits: "רכישת קרדיטים", retry: "ניסיון חוזר",
        previewTip: "תצוגה מקדימה של טקסט ההודעה",
        statusTip: "ראו מי קיבל את ההודעה ומה הייתה תגובתו",
        remindTip: "שליחת מעקב לאורחים בהודעה הזו",
        addTip: "הוספת אנשי קשר נוספים להודעה הזו",
        addScheduledTip: "הוסיפו אנשי קשר נוספים — הם יקבלו את ההודעה בזמן השליחה המתוזמן",
        addDisabledTip: "לא ניתן להוסיף אנשי קשר — תקנו תחילה את ההודעה דרך עריכה ושליחה מחדש (תבנית ה-WhatsApp מעולם לא הגיעה ל-Meta).",
        duplicateTip: "שימוש חוזר בהודעה הזו (תמונה, טקסט, שפות, קישור) בהודעה חדשה לגמרי",
        deleteTip: "מחיקה לצמיתות של ההודעה הזו מההיסטוריה שלכם",
        pauseTip: "השהיית ההודעה הזו",
        buyCreditsTip: "טענו את קרדיט ההודעות שלכם ואז חדשו את ההודעה",
        retryTip: "נסו לשלוח שוב (יצליח כשיהיו לכם מספיק קרדיטים)",
        manageRemindersTip: "ניהול תזכורות מתוזמנות",
        scheduleRemindersTip: "תזמון תזכורות",
        resumeAutoRetryTip: "חידוש ניסיון אוטומטי ({n} ממתינים)",
        pauseAutoRetryTip: "השהיית ניסיון אוטומטי ({n} ממתינים)",
        remindPending: "אורחים שטרם השיבו", remindPendingSub: "עדיין לא הגיבו",
        remindMaybe: "השיבו אולי", remindMaybeSub: "אמרו אולי — עודדו אותם לאשר",
        remindYes: "השיבו כן", remindYesSub: "תזכורת ליום האירוע, עדכון מקום וכו׳",
        remindNo: "השיבו לא", remindNoSub: "לא יכולים להגיע — שלחו קישור לשידור חי, תודה וכו׳",
        remindRemovedOne: "האורח שהסרתם לא יקבל את התזכורת הזו.", remindRemovedMany: "האורחים שהסרתם ({n}) לא יקבלו את התזכורת הזו.",
        // Response-edit (pencil) window: _openResponseEditModal and what it opens.
        reTitle: "עדכון התשובה של {name}",
        reThisGuest: "האורח הזה",
        reContact: "איש קשר",
        reManualEntry: "רישום ידני:",
        reManualEntryNote: "נוצר כאן כי לא הייתה תשובה בטופס.",
        reRemove: "הסרה",
        reCurrentlyPaired: "מקושר כעת:",
        reUnpair: "ביטול קישור",
        reEditDetails: "עריכת פרטי איש הקשר",
        rePhone: "טלפון",
        reEmail: "אימייל",
        reInvitationHelp: "כתבו את כולם, מופרדים ב־{sep} (למשל {example}). שינויים מתעדכנים בקישור האישי אוטומטית.",
        reChildMarker: "(ילד)",
        reExample: "משה כהן; נועה (ילד)",
        reMaxGuests: "מקס׳ אורחים",
        reMembersPh: "לדוגמה: משה כהן; נועה (ילד)",
        // Task #935: the guest-list columns (Add contacts, the contact window).
        acGuestNames: "שמות האורחים",
        acGuestNamesHint: "איך מוסיפים שמות אורחים\n• מפרידים בין שמות בנקודה-פסיק (;). אפשר גם בפסיק.\n• מוסיפים (ילד) אחרי שם של ילד, למשל: יואב כהן (ילד).\n• דוגמה: שרה כהן; מיכאל כהן; יואב כהן (ילד)\n• הטלפון והאימייל בשורה שייכים לאורח הראשון ברשימה.",
        acGroupName: "שם הקבוצה",
        acGroupNameHint: "למי ההזמנה ממוענת, למשל משפחת כהן: על המעטפה ובברכת הטופס; ההודעות פונות לשמות האורחים. בלי שמות אורחים, האורחים מוצאים אותה לפי השם הזה. לאורח שמוזמן לבד השאירו ריק.",
        acCsvColumns: "עמודות CSV: שמות האורחים, שם הקבוצה, טלפון, אימייל · אופציונלי: מקס' אורחים, תגיות",
        acSameName: "שימו לב: יש כמה אורחים או קבוצות עם אותו שם. הוסיפו כתובת אימייל או מספר טלפון לכל אחד, כדי שיוכלו לאמת את זהותם בחיפוש ההזמנה.",
        acLastFirst: "אדם אחד, שם המשפחה קודם? הפרידו בין אנשים שונים ב-\";\".",
        reGroupName: "שם הקבוצה",
        reGroupNameHint: "למי ההזמנה ממוענת: המעטפה והפנייה. כשאיש לא רשום, האורחים מוצאים אותה לפי השם הזה.",
        reMaxGuestsHint: "השאירו ריק כדי שגודל הקבוצה ייקבע לפי השמות (או לפי מגבלת הטופס).",
        reGroupNamePh: "למשל משפחת כהן",
        reGuestNames: "שמות האורחים",
        reTags: "תגיות",
        reTagsPh: "לדוגמה: משפחה, קבלת פנים בלבד",
        reTagsHelp: "התגיות קובעות אילו חלקים של האירוע הקישור של האורח מציג, כשחלק מוגבל לתגיות מסוימות.",
        reLoading: "טוען…",
        reSave: "שמירה",
        rePairHeading: "קישור לתשובה קיימת",
        rePairHelp: "אם {name} ענה דרך הטופס אבל התשובה לא קושרה אוטומטית (למשל קישור משותף או טלפון אחר), אפשר לקשר אותה כאן.",
        reLooking: "מחפש תשובות תואמות…",
        reManualHeading: "או רישום ידני",
        reManualHelp: "רק אם {name} ענה מחוץ לטופס (הודעה, שיחה, פנים אל פנים).",
        reAttending: "מגיע",
        reDeclined: "לא מגיע",
        reMaybe: "אולי",
        reResponded: "ענה",
        rePending: "ממתין",
        reSaving: "שומר…",
        reSaved: "נשמר ✓",
        reCouldNotSave: "השמירה נכשלה",
        reNotOnMessage: "האורח הזה כבר לא ברשימת ההודעה, ולכן אי אפשר לערוך כאן את ההזמנה שלו.",
        rePartyMany: "ההזמנה היא ל־{n} אנשים.",
        rePartyOne: "ההזמנה היא לאדם אחד.",
        reLoadFailed: "לא ניתן לטעון את ההזמנה.",
        reMaxInvalid: "מקס׳ אורחים חייב להיות מספר שלם בין 1 ל־50.",
        reBrowseAll: "הצגת כל {n} התשובות הלא מקושרות",
        reBrowseOne: "הצגת התשובה הלא מקושרת",
        reHideList: "הסתרת הרשימה",
        reCandidatesFailed: "לא ניתן לטעון התאמות אפשריות.",
        reManualTag: "ידני",
        reManualTagTip: "נוצר כרישום ידני אצל איש קשר אחר",
        reNoName: "(ללא שם)",
        rePair: "קישור",
        reLikely: "התאמות סבירות",
        reNoUnpaired: "אין בטופס הזה תשובות לא מקושרות. אם האורח ענה מחוץ לטופס, השתמשו בכפתורים של \"או רישום ידני\".",
        reNoLikely: "אין התאמות סבירות לפי שם, טלפון או אימייל. אפשר לעבור על הרשימה המלאה אם ידוע איזו תשובה שלו.",
        reSearchPh: "חיפוש לפי שם, טלפון או אימייל…",
        rePaired: "התשובה קושרה ל־{name}",
        reUnpaired: "הקישור של {name} בוטל",
        reRecorded: "נרשם \"{status}\" עבור {name}",
        reManualRemoved: "הרישום הידני הוסר",
        reFailedPair: "לא ניתן לקשר את התשובה",
        reFailedUnpair: "לא ניתן לבטל את הקישור",
        reFailedRecord: "לא ניתן לרשום את התשובה",
        reFailedRemove: "ההסרה נכשלה",
        reUpdatedNumber: "המספר עודכן.",
        reUpdatedEmail: "כתובת האימייל עודכנה.",
        reUpdatedDetails: "פרטי איש הקשר עודכנו.",
        reResendAsk: "לשלוח את ההודעה הזו שוב אל {dest}?",
        reMultiMessage: "איש הקשר הזה מופיע ביותר מהודעה אחת, לכן השתמשו ב־{addContacts} בהודעה שרוצים לשלוח שוב.",
        reResend: "שליחה חוזרת",
        reSending: "שולח…",
        reSentNumber: "נשלח למספר החדש; זה יופיע בהיסטוריה של איש הקשר בקרוב.",
        reQueued: "בתור: ההודעה תישלח מעצמה ברגע ש-WhatsApp יאשר אותה, בדרך כלל תוך כמה דקות.",
        reSentEmail: "נשלח לאימייל החדש; זה יופיע בהיסטוריה של איש הקשר בקרוב.",
        reSentBoth: "נשלח למספר ולאימייל החדשים; זה יופיע בהיסטוריה של איש הקשר בקרוב.",
        reResendFailed: "השליחה החוזרת נכשלה. נסו שוב.",
        reminderDupSkipped: "כבר קיבלו תזכורת מהודעה אחרת: {n}",
        reminderDupSkippedBy: "כבר קיבלו את התזכורת מההודעה “{name}”: {n}",
        reminderDupSkippedTip: "האורחים האלה כבר קיבלו את אותה תזכורת מהודעה אחרת של האירוע ב-20 השעות האחרונות, ולכן התזכורת הזו לא נשלחה אליהם פעם נוספת.",
        reminderDupAll: "כולם כבר קיבלו תזכורת מהודעה אחרת",
        remAttentionOne: "תזכורת אחת דורשת את תשומת לבכם",
        remAttentionMany: "{n} תזכורות דורשות את תשומת לבכם",
        remFailExpiredNote: "וואטסאפ לא אישר את הודעת התזכורת בזמן. ערכו ושמרו כדי לנסות שוב.",
        remFailRejectedNote: "וואטסאפ לא קיבל את הודעת התזכורת. ערכו את ההודעה ושמרו כדי לשלוח אותה שוב לאישור.",
        remAudienceBtn: "למי תישלח התזכורת",
        remAudienceTitle: "למי נשלחת התזכורת",
        msgAudienceTitle: "למי נשלחת ההודעה",
        msgAudienceOne: "ההודעה נשלחת לאורח הזה.",
        msgAudienceMany: "ההודעה נשלחת ל־{n} האורחים האלה.",
        remAudienceNowOne: "אם הייתה נשלחת עכשיו, היא הייתה מגיעה לאורח אחד. הרשימה נסגרת ברגע השליחה, ולכן היא עוד עשויה להשתנות.",
        remAudienceNowMany: "אם הייתה נשלחת עכשיו, היא הייתה מגיעה ל־{n} אורחים. הרשימה נסגרת ברגע השליחה, ולכן היא עוד עשויה להשתנות.",
        remAudienceFixedOne: "התזכורת נשלחת לאורח הזה.",
        remAudienceFixedMany: "התזכורת נשלחת ל־{n} האורחים האלה.",
        remAudienceEmpty: "כרגע אף אחד לא מתאים. הרשימה תיבדק שוב כשהתזכורת תישלח.",
        remAudienceFailed: "לא הצלחנו לטעון למי נשלחת התזכורת. נסו שוב.",
        remAudienceFixedNone: "אין אף אחד ברשימה של התזכורת הזו, ולכן היא לא תגיע לאף אחד.",
        remAudienceMeantOne: "התזכורת הייתה אמורה להישלח לאורח הזה. היא לא תישלח עד שתערכו ותשמרו אותה.",
        remAudienceMeantMany: "התזכורת הייתה אמורה להישלח ל־{n} האורחים האלה. היא לא תישלח עד שתערכו ותשמרו אותה.",
        remFailWaOnlyRefusedNote: "וואטסאפ לא קיבל את הודעת התזכורת, ואת האורחים שלה אפשר להשיג רק בוואטסאפ, ולכן לא נשלח דבר. סיבה נפוצה היא תגית התאמה אישית בסוף ההודעה: הוסיפו טקסט אחריה ושמרו כדי לשלוח אותה שוב לאישור.",
        acSentOf: "{sent} מתוך {total} נשלחו",
        acSkippedUntil: "{n} בהשהיה של וואטסאפ עד {time}, נסו שוב אז",
        acSkipped: "{n} דולגו",
        acUnreachable: "{n} לא נשלחו (פרטי קשר)",
        acFailed: "{n} לא נמסרו",
        acDeferred: "{n} יישלחו בחלון השליחה הבא",
        acLineOne: "אורח אחד נוסף אחרי השליחה",
        acLineMany: "{n} אורחים נוספו אחרי השליחה",
        acLineLast: "אחרון: {n}",
        acLineAwaiting: "{n} ממתינים לאישור וואטסאפ",
        acTipAdded: "{when}: נוספו {n}",
        reResendCooldown: "וואטסאפ משהה הודעות למספר הזה עד {time}. לא חויבתם. נסו שוב אחרי כן, או שלחו את הקישור האישי בעצמכם.",
        reResendCooldownNoTime: "וואטסאפ משהה כרגע הודעות למספר הזה. לא חויבתם. נסו שוב מאוחר יותר, או שלחו את הקישור האישי בעצמכם.",
        reResendNotSent: "לא ניתן היה לשלוח את ההודעה לפרטים האלה. לא חויבתם. בדקו את המספר או הכתובת ונסו שוב.",
        // Every guest skipped (Meta 24h cooldown, not on WhatsApp...): see _skipSummary and _mergeSendTimeFailures.
        skipNotSent: "לא נשלחה",
        skipNotSentTip: "שום דבר לא יצא: כל האורחים בהודעה הזו עוכבו. ההערה למטה מסבירה למה ומה אפשר לעשות.",
        skippedOne: "1 עוכב",
        skippedMany: "{n} עוכבו",
        skippedTip: "עוכבו בכוונה ולא נשלחו. לחצו כדי לראות מי ולמה.",
        skipCoolOne: "וואטסאפ דחה הודעה שנשלחה לאחרונה לאורח אחד, ולכן אנחנו משהים את ההודעות אליו עד {time}.",
        skipCoolMany: "וואטסאפ דחה הודעות שנשלחו לאחרונה ל-{n} אורחים, ולכן אנחנו משהים את ההודעות אליהם. את הראשון מביניהם אפשר לנסות שוב אחרי {time}.",
        skipCoolLiftedOne: "וואטסאפ דחה הודעה שנשלחה לאחרונה לאורח אחד. ההשהיה שלנו להודעות אליו כבר הסתיימה.",
        skipCoolLiftedMany: "וואטסאפ דחה הודעות שנשלחו לאחרונה ל-{n} אורחים. ההשהיה שלנו כבר הסתיימה עבור הראשון מביניהם.",
        skipCoolWhy: "וואטסאפ דוחה הודעות שיווקיות לאנשים שקיבלו הרבה הודעות לאחרונה, ולא אומר לכמה זמן. אחרי דחייה אנחנו משהים את ההודעות שלנו למספר הזה במקום לשלוח שוב. ההשהיה חלה על כל האירועים ב-QuikRSVP, כך שייתכן שההודעה שנדחתה בכלל לא הייתה שלכם. הודעה שעוכבה לא מחויבת. גם ניסיון חוזר אחרי ההשהיה עלול להידחות.",
        skipNoWaOne: "אורח אחד לא נמצא בוואטסאפ. אפשר לפנות אליו ב-SMS או באימייל, או לשלוח לו את הקישור האישי בעצמכם.",
        skipNoWaMany: "{n} אורחים לא נמצאים בוואטסאפ. אפשר לפנות אליהם ב-SMS או באימייל, או לשלוח להם את הקישורים האישיים בעצמכם.",
        skipUnreachOne: "לאורח אחד לא ניתן היה לשלוח: צריך לתקן את הטלפון או האימייל. אפשר לפתוח את הרשימה ולראות מי ומה לתקן.",
        skipUnreachMany: "ל-{n} אורחים לא ניתן היה לשלוח: צריך לתקן את הטלפון או האימייל. אפשר לפתוח את הרשימה ולראות מי ומה לתקן.",
        skipOptOutOne: "אורח אחד השיב STOP להודעות הוואטסאפ שלנו, ולכן אנחנו לא שולחים לו הודעות שם.",
        skipOptOutMany: "{n} אורחים השיבו STOP להודעות הוואטסאפ שלנו, ולכן אנחנו לא שולחים להם הודעות שם.",
        skipUnsubOne: "אורח אחד הסיר את עצמו מהאימיילים שלכם. אפשר לפנות אליו בוואטסאפ או ב-SMS במקום.",
        skipUnsubMany: "{n} אורחים הסירו את עצמם מהאימיילים שלכם. אפשר לפנות אליהם בוואטסאפ או ב-SMS במקום.",
        skipDoRetryAt: "נסו שוב אחרי {time}, או פתחו את קישורים אישיים ושלחו את הקישור בעצמכם.",
        skipDoRetryNow: "נסו שוב עכשיו, או פתחו את קישורים אישיים ושלחו את הקישור בעצמכם.",
        skipDoLink: "פתחו את קישורים אישיים ושלחו את הקישור בעצמכם.",
        skipHandledRepliedOne: "לא נשלחה על ידינו, אבל האורח הזה כבר השיב.",
        skipHandledRepliedMany: "לא נשלחה על ידינו, אבל כל האורחים שהיא נועדה להם כבר השיבו.",
        skipHandledLinkOne: "לא נשלחה על ידינו, אבל האורח הזה כבר פתח את הקישור האישי שלו.",
        skipHandledLinkMany: "לא נשלחה על ידינו, אבל כל האורחים שהיא נועדה להם כבר פתחו את הקישור האישי שלהם או השיבו.",
        skipStillBlockedOne: "אנחנו עדיין משהים הודעות לאורח הזה עד {time}. נסו שוב אחרי כן, או שלחו את הקישור האישי בעצמכם.",
        skipStillBlockedMany: "אנחנו עדיין משהים הודעות לאורחים האלה. את הראשון מביניהם אפשר לנסות שוב אחרי {time}; עד אז, שלחו את הקישורים האישיים בעצמכם.",
        skipRowCool: "וואטסאפ דחה הודעה שנשלחה לאחרונה למספר הזה (ייתכן שמאירוע אחר), ולכן אנחנו משהים את כל ההודעות אליו עד {time}. נסו שוב אחרי כן, או שלחו את הקישור האישי בעצמכם.",
        skipRowCoolLifted: "וואטסאפ דחה הודעה שנשלחה לאחרונה למספר הזה (ייתכן שמאירוע אחר). ההשהיה שלנו הסתיימה, כך שאפשר לנסות שוב או לשלוח את הקישור האישי בעצמכם.",
        skipRowNoWa: "המספר הזה לא נמצא בוואטסאפ. אפשר לפנות לאורח ב-SMS או באימייל, או לשלוח את הקישור האישי בעצמכם.",
        skipRowNoWaNoEmail: "המספר הזה לא נמצא בוואטסאפ. אפשר לפנות לאורח ב-SMS, או לשלוח את הקישור האישי בעצמכם.",
        fbTipFailedEmail: "וואטסאפ לא הצליח למסור את ההודעה, ולכן היא נשלחה באימייל במקום.",
        fbTipFailedSms: "וואטסאפ לא הצליח למסור את ההודעה, ולכן היא נשלחה ב-SMS במקום.",
        fbTipNoWa: "המספר הזה לא נמצא בוואטסאפ, ולכן שלחנו לאורח אימייל במקום.",
        fbTipCool: "ההודעות בוואטסאפ למספר הזה מושהות כרגע, ולכן שלחנו לאורח אימייל במקום.",
        fbStateOpened: "ההודעה נפתחה.",
        fbStateDelivered: "ההודעה נמסרה.",
        fbStateOnWay: "ההודעה בדרך.",
        skipRowOptOut: "האורח הזה השיב STOP להודעות הוואטסאפ שלנו, ולכן אנחנו לא שולחים לו הודעות שם.",
        skipRowOptOutSms: "האורח השיב STOP להודעות ה-SMS שלנו, ולכן לא נשלח לו עוד SMS.",
        skipRowUnsub: "האורח הזה הסיר את עצמו מהאימיילים שלכם. אפשר לפנות אליו בוואטסאפ או ב-SMS במקום.",
        skipRowOther: "עוכב: המספר הזה לא יכול לקבל הודעות וואטסאפ כרגע.",
        skipRowNoDetails: "לא נשלח: אין מספר טלפון ואין אימייל. אפשר להוסיף אחד ולשלוח שוב, או לשלוח את הקישור האישי בעצמכם.",
        skipRowBadNumber: "לא נשלח: אי אפשר לחייג למספר הזה. כדאי לבדוק את הספרות ואת קידומת המדינה ולשלוח שוב.",
        skipRowNoEmail: "לא נשלח: מוגדר לשליחה באימייל, אבל אין כתובת. אפשר להוסיף כתובת או לשלוח בוואטסאפ.",
        skipRowNoPhone: "לא נשלח: מוגדר לשליחה ב-SMS, אבל אין מספר. אפשר להוסיף מספר ולשלוח שוב.",
        skipRowShared: "לא נשלח: אותו מספר כמו של {name}, ולכל מספר נשלחת הודעה אחת. אפשר לתת מספר או אימייל נפרדים, או לצרף להזמנה של {name}.",
        skipRowSharedNoName: "לא נשלח: לאורח אחר יש אותו מספר, ולכל מספר נשלחת הודעה אחת. אפשר לתת מספר או אימייל נפרדים.",
        skipRowNoChannel: "לא נשלח: לא הוגדרה דרך שליחה לאורח הזה. אפשר לערוך את הפרטים ולשלוח שוב.",
        skipBadge: "עוכב",
        unsubBadge: "הוסר מהדיוור",
        skipRetrySent: "נשלחה. הכרטיס יתעדכן בעוד רגע.",
        skipRetryFailed: "וואטסאפ לא קיבל אותה הפעם. פתחו את סטטוס כדי לראות למה.",
        skipRetryStill: "שום דבר לא נשלח: ההשהיה של ההודעות לאורחים האלה עדיין בתוקף. נסו שוב מאוחר יותר, או שלחו את הקישור האישי בעצמכם.",
        skipRetryHeld: "ההודעה עוכבה לבדיקה קצרה לפני השליחה. היא תישלח ברגע שתאושר.",
        skipRetryPaused: "לא נשלחה: ההודעה מושהית. הכרטיס מראה למה.",
        skipRetryNoCredits: "מושהית: הוסיפו קרדיטים כדי לשלוח אותה.",
        skipRetryRequested: "ביקשנו לנסות שוב. הכרטיס מראה איפה זה עומד.",
        skipRetryError: "לא ניתן לנסות שוב כרגע. נסו שוב בעוד דקה.",
        retryBusy: "ההודעה הזו עדיין בשליחה. כשהשליחה תסתיים, בדקו מי עדיין צריך לקבל אותה ונסו שוב.",
        statusLoadFailed: "לא הצלחנו לטעון את רשימת האורחים כרגע.",
        statusLoadExpired: "תוקף ההתחברות פג. רעננו את הדף כדי לראות את רשימת האורחים.",
        statusLoadRetry: "נסו שוב",
        statusLoadReload: "רענון הדף",
        statusRefreshFailed: "לא הצלחנו לרענן את רשימת האורחים. רעננו את הדף כדי לראות את המצב העדכני.",
        retrySentOne: "ההודעה נשלחה שוב לאורח אחד.",
        retrySentMany: "ההודעה נשלחה שוב ל-{n} אורחים.",
        retryStillFailedOne: "לאורח אחד עדיין לא הצלחנו להגיע.",
        retryStillFailedMany: "ל-{n} אורחים עדיין לא הצלחנו להגיע.",
        retryStaleView: "שליחה חוזרת של ההודעה הזו הסתיימה אחרי שהרשימה נטענה, ולכן הפעם לא נשלח דבר. הרשימה רועננה: בדקו מי עדיין צריך לקבל אותה ונסו שוב.",
        acSmsConsentWhy: "אורחים עם מספרים אמריקאיים מקבלים את ההודעה הזו ב-SMS, ומספרים אחרים עם קידומת המדינה 1 (כמו קנדה) או מספרים מברזיל עשויים לקבל אותה כך אם WhatsApp נכשל.",
        acSmsConsent: "אני מאשר שכל הנמענים הסכימו לקבל הודעות SMS של הזמנה לאירוע שלי דרך QuikRSVP.",
        acSmsDisclosure: "הנמענים יכולים להשיב STOP לביטול או HELP לעזרה. עלולים לחול חיובי הודעות ונתונים. תדירות משתנה.",
        waRejectedTitle: "וואטסאפ דחתה את ההודעה",
        waOursTitle: "לא ניתן היה לשלוח את הודעות הוואטסאפ",
        waTimeoutTitle: "וואטסאפ לא אישרה את ההודעה בזמן",
        waSubmitPendingTitle: "אנחנו עדיין שולחים את ההודעה לוואטסאפ לאישור",
        waSubmitPendingBody: "עדיין לא הצלחנו להגיע לוואטסאפ. ננסה שוב באופן אוטומטי ונשלח לכם אימייל אם וואטסאפ תדחה אותה. אין צורך לעשות דבר.",
        addWaitWaTip: "תוכלו להוסיף אנשי קשר אחרי שההודעה תגיע לוואטסאפ לאישור. אנחנו עדיין מנסים, בדקו שוב בעוד כמה דקות.",
        remCancelled: "בוטלה",
        remCancelFormDeleted: "לא נשלחה: האירוע נמחק.",
        remCancelDeadline: "לא נשלחה: מועד אישורי ההגעה הסתיים לפני מועד השליחה.",
        remCancelDateMoved: "לא נשלחה: האירוע הוקדם והמועד שלה כבר עבר.",
        remOnHold: "בהמתנה",
        remHeldPostponed: "בהמתנה: האירוע נדחה. התזכורת תעבור לתאריך החדש ברגע שתקבעו אותו.",
        remCancelEventCancelled: "לא נשלחה: האירוע בוטל.",
        remCancelPostponedLate: "לא נשלחה: האירוע נדחה עד יותר מיום אחרי מועד התזכורת.",
        remCancelInvitation: "לא נשלחה: ההזמנה שלה נמחקה או בוטלה.",
        remCancelLate: "לא נשלחה: ההזמנה שלה הייתה עצורה עד יותר מיום אחרי מועד התזכורת.",
        remCancelNotSent: "לא נשלחה",
        remMovedWithEvent: "הוזזה יחד עם תאריך האירוע",
        remCancelDeadlineHint: "הזיזו את מועד אישורי ההגעה או הסירו את הקישור לאישור הגעה, ואז ערכו את התזכורת כדי לשלוח אותה.",
        remCancelDateMovedHint: "ערכו אותה כדי לבחור מועד חדש.",
        remSendsSoon: "תישלח בשעה הקרובה",
        remDateMovedPast: "האירוע הוקדם והמועד החדש של התזכורת כבר עבר, ולכן היא לא תישלח. ערכו אותה כדי לבחור מועד חדש.",
        remSendNowWaiting: "התזכורת תישלח ברגע שההזמנה שלה תגיע לכל האורחים.",
        remSendNowNoRecipients: "אף אורח לא מתאים לתזכורת הזו, ולכן לא נשלח דבר.",
        remSendNowTaken: "התזכורת הזו כבר בשליחה.",
        remSendNowCancelled: "התזכורת בוטלה כי ההזמנה שלה נמחקה או בוטלה.",
        remSendNowError: "לא הצלחנו לקבוע כרגע למי התזכורת הזו נשלחת. לא נשלח דבר, וננסה שוב באופן אוטומטי.",
        moreActions: "פעולות נוספות", // the card's ... menu; was missing in every pack
        // Card badges and stats, reminder rows, the Status window, Remind All
        // and the card actions' confirms/toasts (code review 2026-10-07: all
        // English on every dashboard). See cCount for the ...One forms.
        badgeSent: "נשלחה",
        badgeSending: "בשליחה",
        badgeProcessing: "בטיפול",
        badgeScheduled: "מתוזמנת",
        badgePaused: "מושהית",
        badgeCancelled: "בוטלה",
        badgeDraft: "טיוטה",
        badgeExpired: "פג תוקף",
        badgeUnableToSend: "לא ניתן לשלוח",
        badgeNoCredits: "אין מספיק קרדיטים",
        badgeInReview: "בבדיקה",
        scheduledFor: "מתוזמנת ל־{when}",
        sendingAt: "תישלח ב־{when}",
        expiredTip: "וואטסאפ לא אישר את ההודעה בזמן. אפשר לשלוח אותה שוב לאישור דרך עריכה ושליחה מחדש.",
        noCreditsTip: "ההודעה הזו הושהתה כי נגמרו הקרדיטים בחשבון. רכשו קרדיטים ושלחו שוב כדי למסור את ההודעות שנותרו.",
        inReviewTip: "אנחנו בודקים הודעות מחשבונות חדשים לפני שהן יוצאות, ומישהו בודק את שלכם עכשיו. שום דבר לא חויב, וההודעה ואנשי הקשר שלכם שמורים. היא תישלח אוטומטית אחרי האישור; כתבו לנו בצ׳אט אם אתם צריכים אותה מוקדם יותר.",
        toggleRemindersTip: "הצגה או הסתרה של התזכורות של ההודעה הזו",
        showRemindersAria: "הצגת תזכורות ({n})",
        hideRemindersAria: "הסתרת תזכורות ({n})",
        expandAllReminders: "הרחבת הכול",
        collapseAllReminders: "כיווץ הכול",
        expandAllRemindersTip: "הצגת התזכורות של כל ההודעות",
        collapseAllRemindersTip: "הסתרת התזכורות של כל ההודעות",
        remindersOn: "תזכורות פעילות",
        statResponded: "{n} השיבו",
        statRespondedOne: "{n} השיב",
        statPending: "{n} ממתינים",
        statPendingOne: "{n} ממתין",
        statSent: "{n} נשלחו",
        statSentOne: "{n} נשלחה",
        statContacts: "{n} אנשי קשר",
        statContactsOne: "איש קשר אחד",
        statNotDelivered: "{n} לא נמסרו",
        statNotDeliveredOne: "{n} לא נמסרה",
        statRecipients: "{n} נמענים",
        statRecipientsOne: "נמען אחד",
        allRepliedTip: "כל מי שקיבלו אותה כבר השיבו — אליהם היא נועדה.",
        waCouldNotAccept: "וואטסאפ לא קיבל את ההודעה שלכם. ערכו את ההודעה ושלחו שוב.",
        untitledMessage: "הודעה",
        editResendTip: "תקנו את הבעיה ושלחו שוב את הודעות הוואטסאפ",
        timingDayOf: "ביום האירוע",
        timing1DayBefore: "יום לפני",
        timing3DaysBefore: "3 ימים לפני",
        timing1WeekBefore: "שבוע לפני",
        timing2WeeksBefore: "שבועיים לפני",
        audNoResponse: "אורחים שטרם השיבו",
        audAttending: "אורחים שמגיעים",
        audMaybe: "אורחים שאולי מגיעים",
        audDeclined: "אורחים שלא מגיעים",
        audAll: "כל אנשי הקשר",
        remTitleAudience: "תזכורת · {audience}",
        remTitleIndex: "תזכורת {n}",
        remWasSetFor: "הייתה מתוזמנת ל־{when}",
        remNoCreditsTip: "התזכורת הזו לא נשלחה כי אין לכם מספיק קרדיטים. רכשו קרדיטים וחדשו אותה כדי לשלוח.",
        remAddCredits: "הוסיפו קרדיטים כדי לשלוח את התזכורת",
        remNoRecipients: "אין נמענים",
        remNoMatching: "אין נמענים מתאימים",
        remResubmitTip: "ההודעה הערוכה שלכם חזרה לאישור בוואטסאפ. היא תישלח אחרי האישור.",
        remResubmitting: "נשלחה שוב לאישור",
        remSendsOnceApproved: "תישלח ב־{when}, אחרי האישור",
        remWaitingApproval: "ממתינה לאישור וואטסאפ",
        remAllRepliedTip: "כל מי שקיבלו את התזכורת כבר השיבו — אליהם היא נועדה.",
        remExpiredTip: "וואטסאפ לא אישר בזמן את ההודעה של התזכורת. ערכו אותה ונסו שוב.",
        remRejectedTip: "וואטסאפ דחה את תוכן ההודעה של התזכורת. ערכו את התזכורת ושמרו כדי לשלוח אותה שוב לאישור.",
        remFailedTip: "הייתה בעיה בשליחת התזכורת. ערכו אותה ונסו שוב.",
        remRejected: "ההודעה נדחתה",
        remCouldntSend: "לא נשלחה",
        remTimedOut: "תם הזמן לאישור וואטסאפ",
        remEditToResubmit: "ערכו ושמרו כדי לשלוח שוב לאישור",
        remSendFailed: "השליחה נכשלה",
        remWaitingTip: "ממתינים שוואטסאפ (Meta) יאשר את ההודעה של התזכורת — בדרך כלל כמה דקות, לפעמים יותר. היא תישלח אוטומטית אחרי האישור.",
        remSendingSoon: "תישלח בקרוב",
        remBeingSentTip: "התזכורת נשלחת עכשיו.",
        remDueOnceApproved: "מתוכננת ל־{when} · תישלח אחרי אישור וואטסאפ",
        remSendsWhenApproved: "תישלח מיד כשוואטסאפ יאשר",
        remDue: "מתוכננת ל־{when}",
        remSendingNow: "נשלחת עכשיו…",
        remProcessingTip: "התזכורת יוצאת עכשיו. האימייל נשלח מיד; אם היא נשלחת גם בוואטסאפ, החלק הזה יישלח ברגע ש־Meta תאשר את ההודעה — בדרך כלל תוך כמה דקות.",
        remSendingMinute: "נשלחת עכשיו — זה לוקח בדרך כלל דקה או שתיים",
        remSends: "תישלח ב־{when}",
        remPreviewTip: "תצוגה מקדימה של הודעת התזכורת",
        remEditTip: "עריכת ההודעה, המועד או הנמענים",
        remStatusTip: "סטטוס מסירה — מי קיבל את התזכורת ומה השיב",
        remBuyCreditsTip: "רכישת קרדיטים לשליחת התזכורת",
        remResumeCreditsTip: "חידוש התזכורת כשיהיו לכם קרדיטים",
        remResumeTip: "חידוש התזכורת",
        remPauseTip: "השהיית התזכורת",
        remDeleteTip: "מחיקת התזכורת",
        msgNotFound: "ההודעה לא נמצאה",
        remFromMainMessage: "הוסיפו תזכורות מההודעה הראשית, לא מתזכורת אחרת.",
        renamePrompt: "שינוי שם ההודעה:",
        renameEmpty: "שם ההודעה לא יכול להיות ריק",
        renameFailed: "לא הצלחנו לשנות את שם ההודעה: ",
        cancelConfirm: "לבטל את ההודעה \"{name}\"?\n\nאי אפשר לבטל את הפעולה. הודעות שעוד לא נשלחו לא יישלחו.",
        cancelDone: "ההודעה בוטלה",
        cancelFailed: "לא הצלחנו לבטל את ההודעה: ",
        resumeConfirm: "לחדש את ההודעה \"{name}\"?\n\nהיא תישלח ב־{when}.",
        resumeConfirmNoDate: "לחדש את ההודעה \"{name}\"?\n\nהיא תישלח במועד שתוזמן במקור.",
        resumeDone: "ההודעה חודשה",
        resumeFailed: "לא הצלחנו לחדש את ההודעה: ",
        pauseConfirm: "להשהות את ההודעה \"{name}\"?\n\nהיא לא תישלח במועד המתוזמן. אפשר לחדש או לבטל אותה בהמשך.",
        pauseDone: "ההודעה הושהתה",
        pauseFailed: "לא הצלחנו להשהות את ההודעה: ",
        pauseTooLate: "ההודעה סיימה להישלח לפני שהספקנו להשהות אותה.",
        autoRetryPauseConfirm: "להשהות את הניסיונות החוזרים האוטומטיים של \"{name}\"?",
        autoRetryResumeConfirm: "לחדש את הניסיונות החוזרים האוטומטיים של \"{name}\"?",
        autoRetryNotRetriedOne: "לא יהיה ניסיון חוזר אוטומטי לאיש קשר אחד.",
        autoRetryNotRetriedMany: "לא יהיה ניסיון חוזר אוטומטי ל־{n} אנשי קשר.",
        autoRetryRetriedOne: "יהיה ניסיון חוזר לאיש קשר אחד בניסיון המתוזמן הבא.",
        autoRetryRetriedMany: "יהיה ניסיון חוזר ל־{n} אנשי קשר בניסיון המתוזמן הבא שלהם.",
        autoRetryPaused: "הניסיונות החוזרים האוטומטיים הושהו",
        autoRetryResumed: "הניסיונות החוזרים האוטומטיים חודשו",
        autoRetryPauseFailed: "לא הצלחנו להשהות את הניסיונות החוזרים האוטומטיים: ",
        autoRetryResumeFailed: "לא הצלחנו לחדש את הניסיונות החוזרים האוטומטיים: ",
        sendNowConfirm: "לשלוח עכשיו את ההודעה \"{name}\"?\n\nהמועד המתוזמן כבר עבר. ההודעה תישלח מיד.",
        sendNowDone: "ההודעה נשלחה!",
        sendNowFailed: "לא הצלחנו לשלוח את ההודעה: ",
        deleteDraftConfirm: "למחוק את הטיוטה \"{name}\"?\n\nאי אפשר לבטל את הפעולה.",
        deleteDraftFailed: "לא הצלחנו למחוק את הטיוטה: ",
        deleteConfirmActive: "למחוק לצמיתות את \"{name}\"?\n\nלהודעה הזו יש נתוני מסירה ואולי גם תזכורות מתוזמנות. שניהם יימחקו.\n\nתשובות האורחים נשמרות (הן נשמרות בנפרד).\n\nאי אפשר לבטל את הפעולה.",
        deleteAlsoGuests: "ההודעה הזו הוסיפה לרשימת האורחים {n} אורחים שלא ענו ואינם באף הודעה אחרת.\n\nלהסיר אותם גם מרשימת האורחים?\n\nאישור מסיר אותם. ביטול משאיר אותם.",
        deleteConfirmUnsent: "למחוק לצמיתות את \"{name}\"?\n\nשום דבר לא נשלח בהודעה הזו, כך שאפשר למחוק אותה בבטחה.\n\nאי אפשר לבטל את הפעולה.",
        deleteFailed: "לא הצלחנו למחוק את ההודעה: ",
        statusAllTitle: "כל אנשי הקשר",
        statusAllSubtitle: "מכל ההודעות",
        statusAllMessages: "📊 כל ההודעות",
        statusAllTags: "כל התגיות", statusNoTag: "ללא תגית", statusTagsLine: "תגיות",
        statusTitleFallback: "סטטוס ההודעה",
        statusThisMessageOnly: "מוצגים רק אנשי הקשר של ההודעה הזו",
        statusAddedAfterLaunch: "{n} נוספו אחרי השליחה",
        statusAddedAfterLaunchOne: "אחד נוסף אחרי השליחה",
        statusContactCount: "{n} אנשי קשר",
        statusContactCountOne: "איש קשר אחד",
        exportNone: "אין אנשי קשר לייצוא",
        exportedOne: "יוצא איש קשר אחד",
        exportedMany: "יוצאו {n} אנשי קשר",
        dlvSent: "נשלחה",
        dlvDelivered: "נמסרה",
        dlvRead: "נקראה",
        dlvOpened: "נפתחה",
        dlvClicked: "נלחץ קישור",
        dlvClickedTip: "קישור אישור ההגעה (או האתר שלכם) נפתח מאחת ההודעות שלכם. מלבד תשובה, זה הסימן הבטוח ביותר שההזמנה אצלו.",
        dlvReadTip: "אישור קריאה של וואטסאפ עצמו (וי כחול), כך שההודעה נקראה.",
        dlvOpenedTip: "התמונות במייל נטענו, כך שסביר שהאורח ראה אותו. Apple Mail ואפליקציות נוספות טוענות תמונות אוטומטית כשהמייל מגיע, ולכן זה סימן טוב ולא הוכחה.",
        dlvDeliveredEmailTip: "ספק המייל של האורח קיבל את ההודעה. איננו רואים לאיזו תיקייה היא הגיעה, והרבה אפליקציות (Outlook, Hotmail, מייל עבודה) אף פעם לא מדווחות על פתיחה, כך שייתכן שכבר נקראה.",
        dlvDeliveredWaTip: "ההודעה הגיעה לטלפון (וי כפול בוואטסאפ). עדיין אין אישור קריאה, אבל יש מי שמכבים אישורי קריאה, כך שייתכן שכבר נקראה.",
        dlvDeliveredSmsTip: "ההודעה הגיעה לטלפון כ-SMS. הודעות SMS אף פעם לא מדווחות אם נקראו.",
        dlvSentTip: "ההודעה נמסרה לרשת. עדיין אין אישור שהגיעה.",
        dlvBounced: "הוחזרה",
        dlvFailed: "נכשלה",
        dlvSmsSent: "SMS נשלח",
        dlvSmsFailed: "SMS נכשל",
        detailNoEmailFallback: "אין כתובת אימייל שמורה, ולכן לא ניתן היה לשלוח באימייל במקום. הוסיפו אחת בעיפרון כדי לשלוח.",
        deliveryFailed: "המסירה נכשלה",
        detailEmail: "אימייל",
        detailSmsFallback: "SMS חלופי",
        detailRsvp: "תשובה",
        noDetail: "אין פרטים זמינים",
        fallbackEmail: "נשלחה באימייל",
        fallbackSms: "נשלחה ב־SMS",
        aMessageFailed: "הודעה נכשלה",
        aMessageFailedTip: "אחת ההודעות לאיש הקשר הזה לא נמסרה, אבל הודעה אחרת כן הגיעה אליו.",
        checkNumber: "בדקו את המספר",
        checkNumberTip: "וואטסאפ לא הצליח למסור למספר כפי שהוא שמור אצלנו, והמספר נראה חסר. כדאי לתקן אותו ולשלוח שוב לפני שמניחים שהאורח לא בוואטסאפ.",
        rowDetailsTip: "לחצו כדי לראות את כל פרטי המסירה",
        updateResponseTip: "עדכון תשובה",
        linkColumn: "קישור",
        remCellOnAria: "תזכורות פעילות — השהיה",
        remCellOffAria: "תזכורות כבויות — חידוש",
        guestFallback: "אורח",
        addName: "הוספת שם",
        editNameTip: "לחצו כדי לערוך את השם",
        nameEditPlaceholder: "שם האורח",
        nameEditSave: "שמירה",
        nameEditCancel: "ביטול",
        nameSaveFailed: "לא הצלחנו לשמור את השם",
        raLoadingAll: "טוענים את כל אנשי הקשר...",
        raLoadingPending: "טוענים את מי שטרם השיבו...",
        raLoadingYes: "טוענים את מי שהשיבו כן...",
        raLoadingMaybe: "טוענים את מי שהשיבו אולי...",
        raLoadingNo: "טוענים את מי שהשיבו לא...",
        raExcludedAll: "כל אנשי הקשר הוחרגו מתזכורות",
        raExcludedPending: "כל מי שטרם השיבו כאן הוחרגו מתזכורות",
        raExcludedYes: "כל מי שהשיבו כן כאן הוחרגו מתזכורות",
        raExcludedMaybe: "כל מי שהשיבו אולי כאן הוחרגו מתזכורות",
        raExcludedNo: "כל מי שהשיבו לא כאן הוחרגו מתזכורות",
        raSkippedExcludedOne: "דילגנו על אורח אחד שהחרגתם מתזכורות",
        raSkippedExcludedMany: "דילגנו על {n} אורחים שהחרגתם מתזכורות",
        raLoadFailed: "לא הצלחנו לטעון את אנשי הקשר",
        raNoneSelected: "לא נבחרו נמענים — בוטל.",
        raNoValidRecipients: "אין נמענים תקינים לשליחה.",
        errLabel_131026: "וואטסאפ לא הצליח למסור",
        errLabel_131049: "נחסם במדיניות של Meta",
        errLabel_131047: "חלון 24 השעות הסתיים",
        errLabel_131048: "נחסם כספאם",
        errLabel_131056: "מגבלת קצב",
        errLabel_131058: "בעיה בהודעה",
        errLabel_131061: "ההודעה הושהתה ע״י Meta",
        errLabel_131062: "ההודעה הושבתה ע״י Meta",
        errLabel_132001: "בעיה בהודעה",
        errLabel_130472: "עוכב בניסוי של Meta",
        errLabel_130497: "וואטסאפ חסום במדינה הזו",
        errLabel_131063: "לא בוואטסאפ",
        errLabel_130429: "הגענו למגבלת השליחה",
        errLabel_marketing_limit_reached: "מגבלת שיווק של Meta",
        errLabel_BREVO_REJECTED: "ספק ה־SMS דחה",
        errLabel_unknown: "נכשלה",
        errTip_131049: "Meta חסמה את הודעת השיווק הזו (מגבלת מדיניות או מעורבות) — זו לא הסרה מרצון של האורח. הוא משתמש בוואטסאפ ואפשר להשיג אותו; שליחה חוזרת אחרי בערך יום מצליחה בערך בחצי מהמקרים.",
        errTip_131026: "המספר הזה לא הצליח לקבל את ההודעה — אולי האורח לא משתמש בוואטסאפ, או שיש טעות בספרות. שליחה חוזרת כמעט לא עוזרת (בערך 1 מכל 20 מגיעה), ולכן עדיף להגיע אליו בדרך אחרת.",
        errTip_131048: "המספר מוגבל",
        errTip_131056: "חריגה ממגבלת הקצב",
        errTip_131058: "ההודעה לא אושרה",
        errTip_131047: "חלון 24 השעות הסתיים",
        errTip_131061: "Meta השהתה את הודעת הוואטסאפ שבה השתמשנו כאן, ולכן היא לא נמסרה. זה משפיע על כל האורחים בהודעה הזו, לא רק על האורח הזה — צריך ליצור את ההודעה מחדש.",
        errTip_131062: "ההודעה הושבתה",
        errTip_132001: "וואטסאפ דחה את ההודעה (היא לא הייתה קיימת בשפה שבה נשלחה), ולכן שום דבר לא נמסר. צרו מחדש את הודעת התזכורת ושלחו שוב.",
        errTip_marketing_limit_reached: "Meta עצרה מסירת הודעות שיווק למספר הזה — זו לא הסרה מרצון של האורח",
        errTip_130472: "Meta עיכבה את ההודעה הזו במסגרת אחד מניסויי המסירה שלה. אין בעיה עם המספר, אבל שליחה חוזרת תיתקל באותו עיכוב — הגיעו לאורח בדרך אחרת.",
        errTip_130497: "Meta לא מאפשרת כרגע לחשבון הוואטסאפ שלנו לשלוח הודעות למספרים במדינה הזו, ולכן שליחה חוזרת תיכשל באותו אופן. הגיעו לאורח במייל או בקישור האישי שלו.",
        errTip_131063: "המספר הזה לא רשום בוואטסאפ, ולכן הוא לא יכול לקבל את ההזמנה. נסו מספר אחר או הגיעו לאורח בדרך אחרת.",
        errTip_130429: "הגענו זמנית למגבלת השליחה שלנו — אין בעיה עם המספר. שליחה חוזרת בעוד זמן קצר אמורה לעבור.",
        errTip_BREVO_REJECTED: "ספק ה־SMS דחה את ההודעה אחרי שקיבל אותה (בדרך כלל בעיה זמנית ביתרת השליחה אצלנו). כדאי לנסות שוב.",
        errTip_NOT_FOUND: "איש הקשר לא נמצא או לא תקין",
        errTipUnknown: "שגיאה לא ידועה",
        // Card status lines, failure reasons and WhatsApp refusals (item M2, 2026-10-07).
        chEmail: "אימייל",
        chText: "SMS",
        chWhatsApp: "וואטסאפ",
        stSentN: "{n} נשלחו",
        stAtSendTime: "יישלח במועד שנקבע",
        stGoingOut: "יישלח בדקות הקרובות",
        stWaWaiting: "ממתין לאישור ההודעה בוואטסאפ (בדרך כלל כמה דקות)",
        stWaWaitingLong: "עדיין ממתין לאישור ההודעה בוואטסאפ. זה לוקח יותר זמן מהרגיל; ההודעה תישלח מעצמה מיד כשתאושר",
        stWaSubmitting: "עדיין שולחים לאישור",
        stRetrying: "עדיין לא נשלח דבר. ננסה שוב בעוד כמה דקות.",
        stSendingSoon: "יישלח בדקות הקרובות.",
        stWaRefused: "לא התקבלה. ערכו ושלחו שוב.",
        stWaRefusedBelow: "לא התקבלה. ערכו ושלחו שוב (הסיבה מופיעה למטה).",
        failFormMissing: "הטופס של ההודעה הזו כבר לא קיים. שמרו את הטופס שוב ואז צרו הודעה חדשה.",
        failWaOnly: "וואטסאפ לא קיבלה את ההודעה, ואת כל האורחים בה אפשר להשיג רק בוואטסאפ, ולכן לא נשלח דבר. ערכו ושלחו שוב.",
        failWaRefused: "וואטסאפ לא קיבלה את ההודעה, ולכן לא נשלח דבר. ערכו ושלחו שוב.",
        failNothingSent: "לא ניתן היה לשלוח דבר אחרי כמה ניסיונות. לחצו על \"עריכה ושליחה מחדש\" כדי לנסות שוב. אם זה קורה שוב, כתבו לנו בצ'אט.",
        failNoMethod: "פרטי האורחים שלכם לא הגיעו לשלב השליחה שלנו. זו הייתה תקלה אצלנו, לא ברשימת האורחים שלכם. לחצו על \"עריכה ושליחה מחדש\" כדי לנסות שוב, וכתבו לנו בצ'אט אם זה קורה שוב.",
        failSendError: "משהו השתבש אצלנו בזמן השליחה. זה לא קרה בגללכם. לחצו על \"עריכה ושליחה מחדש\" כדי לנסות שוב, וכתבו לנו בצ'אט אם זה קורה שוב.",
        failGeneric: "לא הצלחנו לשלוח את ההודעה. לחצו על \"עריכה ושליחה מחדש\" כדי לנסות שוב, או כתבו לנו בצ'אט.",
        err131053Label: "לא ניתן היה לטעון את התמונה",
        err131053Tip: "וואטסאפ לא הצליחה להוריד את תמונת ההזמנה, ולכן ההודעה לא נמסרה. שליחה חוזרת בדרך כלל מצליחה.",
        errSmsBouncedLabel: "ה-SMS לא נמסר",
        errSmsBouncedTip: "רשת הטלפון לא מסרה את ה-SMS. ייתכן שהמספר שגוי, קווי או כבוי. בדקו את המספר, שלחו לאורח אימייל, או העתיקו את הקישור האישי שלו ושלחו אותו בעצמכם.",
        errSmsFailedLabel: "ה-SMS לא נשלח",
        errSmsFailedTip: "ספק ה-SMS שלנו לא שלח את ההודעה. שליחה חוזרת בדרך כלל מצליחה.",
        errSmsNonGsmLabel: "לא ניתן לשלוח ב-SMS",
        errSmsNonGsmTip: "בהודעה הזו אין נוסח באנגלית, ו-SMS יכול לשאת רק אותו. הוסיפו גרסה באנגלית דרך “עריכה ושליחה מחדש”, או פנו לאורח הזה באימייל.",
        errNonUsLabel: "SMS נשלח רק למספרים בארה״ב",
        errNonUsTip: "אנחנו שולחים SMS רק למספרים בארה״ב. פנו לאורח הזה בוואטסאפ או באימייל.",
        errEmailHardLabel: "כתובת האימייל לא תקינה",
        errEmailHardTip: "כתובת האימייל הזו לא קיימת או דוחה הודעות. בדקו את האיות, או פנו לאורח ב-SMS או בוואטסאפ.",
        errEmailSoftLabel: "האימייל לא התקבל כרגע",
        errEmailSoftTip: "תיבת הדואר של האורח לא קיבלה את האימייל הפעם (לרוב כי היא מלאה או לא זמינה לרגע). שליחה חוזרת מאוחר יותר בדרך כלל מצליחה.",
        errEmailBounceLabel: "האימייל הוחזר",
        errEmailBounceTip: "לא ניתן היה למסור את האימייל לכתובת הזו. בדקו את האיות, או פנו לאורח ב-SMS או בוואטסאפ.",
        errGenericTip: "לא ניתן היה למסור את ההודעה. פנו לאורח הזה בדרך אחרת, או שלחו שוב מאוחר יותר.",
        fbRetryUntilAll: "וואטסאפ מעכבת הודעות שיווקיות אליהם עד {time}. נסו שוב אחרי כן.",
        fbRetryUntilSome: "וואטסאפ מעכבת הודעות שיווקיות לחלק מהם עד {time}. נסו שוב אחרי כן.",
        fbChannelSub: "ההודעה לא מגיעה אליהם בדרך שבה נשלחה. הזכירו להם באימייל, או העתיקו את הקישור האישי של כל אורח מהרשימה למטה.",
        fbChannelNoEmail: "לאף אחד מהאורחים האלה אין כתובת אימייל. העתיקו את הקישור האישי של כל אחד מהרשימה למטה ושלחו אותו בעצמכם.",
        fbChannelSomeNoEmail: "ל-{n} מהאורחים האלה אין כתובת אימייל, ולכן האימייל לא יגיע אליהם. הרשימה הזו נסגרת כשהאימייל נפתח, אז העתיקו קודם את הקישורים האישיים שלהם. להמשיך עם {m} האורחים שיש להם אימייל?",
        ndTipWa: "וואטסאפ לא מסרה את ההודעה ל-{n} מהאורחים שלכם. לפעמים מטא מעכבת הודעות כדי להגן מפני ספאם, או שהמספר לא בוואטסאפ. פתחו את הרשימה כדי לראות למה, ופנו אליהם באימייל או ב-SMS.",
        ndTipSms: "{n} מהודעות ה-SMS שלכם לא נמסרו, בדרך כלל בגלל מספר שגוי או קווי. פתחו את הרשימה כדי לראות למה, ואז בדקו את המספר, שלחו אימייל או העתיקו את הקישור האישי.",
        ndTipEmail: "{n} מהאימיילים שלכם הוחזרו, בדרך כלל בגלל כתובת עם שגיאת כתיב. פתחו את הרשימה כדי לראות למה, ופנו אליהם ב-SMS או בוואטסאפ.",
        ndTipMixed: "לא נמסרו: {parts}. פתחו את הרשימה כדי לראות את הסיבה לכל אורח.",
        ndTipGeneric: "{n} מהאורחים שלכם לא קיבלו את ההודעה. פתחו את הרשימה כדי לראות למה.",
        ndTipWaOne: "וואטסאפ לא מסרה את ההודעה לאחד מהאורחים שלכם. לפעמים מטא מעכבת הודעות כדי להגן מפני ספאם, או שהמספר לא בוואטסאפ. פתחו את הרשימה כדי לראות למה, ופנו אליו באימייל או ב-SMS.",
        ndTipSmsOne: "אחת מהודעות ה-SMS שלכם לא נמסרה, בדרך כלל בגלל מספר שגוי או קווי. פתחו את הרשימה כדי לראות למה, ואז בדקו את המספר, שלחו אימייל או העתיקו את הקישור האישי.",
        ndTipEmailOne: "אחד מהאימיילים שלכם הוחזר, בדרך כלל בגלל כתובת עם שגיאת כתיב. פתחו את הרשימה כדי לראות למה, ופנו אל האורח ב-SMS או בוואטסאפ.",
        ndTipGenericOne: "אחד מהאורחים שלכם לא קיבל את ההודעה. פתחו את הרשימה כדי לראות למה.",
        tplErr_ours: "משהו השתבש אצלנו, וזה לא קרה בגללכם. פתחו את \"עריכה ושליחה מחדש\" ושלחו שוב משם (אין צורך לשנות דבר). אם זה קורה פעם נוספת, ספרו לנו ונתקן.",
        tplErr_trailingParam: "ההודעה לא יכולה להסתיים בתגית התאמה אישית כמו {{name}} או {{table_number}}. וואטסאפ לא מאפשרת זאת. הוסיפו מילה או נקודה אחרי התגית ושלחו שוב.",
        tplErr_waLink: "וואטסאפ לא מאפשרת כפתור שמקשר חזרה לוואטסאפ. כוונו את הכפתור למקום אחר ושלחו שוב.",
        tplErr_imageFormat: "בשביל וואטסאפ, תמונת ההזמנה צריכה להיות JPG או PNG. העלו אותה שוב בפורמט הזה ושלחו שוב.",
        tplErr_imageProcess: "וואטסאפ לא הצליחה לעבד את תמונת ההזמנה. נסו תמונה אחרת (JPG או PNG, עד 5MB) ושלחו שוב.",
        tplErr_category: "וואטסאפ מסווגת את ההודעה כסוג אחר מזה שתייקנו אותה תחתיו. שלחו שוב וזה אמור לעבור.",
        tplErr_layout: "וואטסאפ לא הצליחה לקרוא את מבנה ההודעה. בדרך כלל זו {{תגית}} שעומדת לבד בשורה משלה. שלבו אותה בתוך משפט ושלחו שוב.",
        tplErr_duplicateName: "בוואטסאפ כבר שמורה הודעה בשם הזה. שנו את שם ההודעה ושלחו שוב.",
        tplErr_rateLimit: "הגעתם כרגע למגבלה של וואטסאפ על הודעות חדשות. המתינו מעט ושלחו שוב. שום דבר לא אבד.",
        tplErr_noReason: "וואטסאפ לא אישרה את נוסח ההודעה. ערכו אותו ושלחו שוב לאישור.",
        tplLangsHeadOne: "וואטסאפ דחתה את הגרסה ב{langs} של ההודעה.",
        tplLangsHeadMany: "וואטסאפ דחתה את הגרסאות האלה של ההודעה: {langs}.",
        tplLangsHeadUnknown: "וואטסאפ דחתה את אחת השפות של ההודעה.",
        tplLangsSaid: "וואטסאפ כתבה: \"{text}\".",
        tplLangsEditOne: "ערכו את התרגום הזה ושלחו שוב לאישור.",
        tplLangsEditMany: "ערכו את התרגומים האלה ושלחו שוב לאישור.",
        langCombined: "שתי שפות משולבות",
        failWaRefusedPartial: "וואטסאפ לא קיבלה את ההודעה, ולכן האורחים בוואטסאפ לא קיבלו אותה. האורחים שלכם באימייל וב-SMS כבר קיבלו אותה, אז אל תשלחו להם שוב. שנו את הנוסח ושלחו שוב לאורחים בוואטסאפ.",
        fbPermanentSubEmail: "המספר או כתובת האימייל לא עובדים. תקנו אותם ברשימת האורחים, או העתיקו את הקישור האישי של האורח ושלחו אותו בעצמכם.",
        fbHeldBack: "{n} לא נשלחו בכוונה",
        fbHeldBackSub: "עיכבנו אותם: האורח ביקש שנפסיק, הסיר את עצמו מהרשימה, אינו ב-WhatsApp, או ש-WhatsApp השהה את המספר. כל שורה למטה מציינת את הסיבה. לאורחים שביקשו שנפסיק לא נשלח הודעות לעולם.",
        tplLangsHeadCombined: "וואטסאפ דחתה את הגרסה הדו-לשונית המשולבת של ההודעה.",
        remFailFormMissing: "הטופס של התזכורת הזו כבר לא קיים. שמרו את הטופס שוב, ואז ערכו את התזכורת בעיפרון ושמרו אותה.",
        remFailWaOnly: "וואטסאפ לא קיבלה את התזכורת, ואת כל האורחים בה אפשר להשיג רק בוואטסאפ, ולכן לא נשלח דבר. ערכו את התזכורת בעיפרון ושמרו כדי להגיש אותה מחדש.",
        remFailWaRefused: "וואטסאפ לא קיבלה את התזכורת, ולכן לא נשלח דבר. ערכו את התזכורת בעיפרון ושמרו כדי להגיש אותה מחדש.",
        remFailWaRefusedPartial: "וואטסאפ לא קיבלה את התזכורת, ולכן האורחים בוואטסאפ לא קיבלו אותה. האורחים שלכם באימייל וב-SMS כבר קיבלו אותה. ערכו את התזכורת בעיפרון ושמרו כדי להגיש אותה מחדש.",
        remFailNothingSent: "לא ניתן היה לשלוח דבר אחרי כמה ניסיונות. ערכו את התזכורת בעיפרון ושמרו כדי לנסות שוב. אם זה קורה שוב, כתבו לנו בצ'אט.",
        remFailNoMethod: "פרטי האורחים שלכם לא הגיעו לשלב השליחה שלנו. זו הייתה תקלה אצלנו, לא ברשימת האורחים שלכם. ערכו את התזכורת בעיפרון ושמרו כדי לנסות שוב, וכתבו לנו בצ'אט אם זה קורה שוב.",
        remFailSendError: "משהו השתבש אצלנו בזמן השליחה. זה לא קרה בגללכם. ערכו את התזכורת בעיפרון ושמרו כדי לנסות שוב, וכתבו לנו בצ'אט אם זה קורה שוב.",
        remFailGeneric: "לא הצלחנו לשלוח את התזכורת. ערכו אותה בעיפרון ושמרו כדי לנסות שוב, או כתבו לנו בצ'אט.",
        // Provider outage pause: see _providerPauseBadgeHtml.
        providerPaused: "השליחה נעצרה",
        providerPausedTip: "תקלה אצל ספק ההודעות שלנו עצרה את ההודעה הזו לפני שכולם קיבלו אותה. לא חויבתם על אורחים שעדיין לא קיבלו אותה. הצוות שלנו קיבל התראה, וההודעה תישלח אליהם ברגע שהתקלה תיפתר. אין צורך לעשות דבר.",
        providerPausedPhrase: "ממתינים לספק ההודעות שלנו",
        providerResumeConfirm: "לנסות לשלוח את ההודעה שוב עכשיו? רק אורחים שעדיין לא קיבלו אותה יקבלו אותה.",
        providerResumed: "מתחילים מחדש. אורחים שעדיין לא קיבלו את ההודעה יקבלו אותה בדקות הקרובות.",
        providerResumeFailed: "לא הצלחנו לחדש את שליחת ההודעה. נסו שוב.",
        providerPausedPhraseAuto: "ננסה שוב באופן אוטומטי",
        providerPausedTipAuto: "תקלה זמנית אצל ספק ההודעות שלנו עצרה את ההודעה הזו לפני שכולם קיבלו אותה. לא חויבתם על אורחים שעדיין לא קיבלו אותה. ננסה שוב באופן אוטומטי בעוד זמן קצר, ורק הם יקבלו אותה. אפשר גם לנסות כבר עכשיו בלחיצה על \"{resume}\".",
        providerResuming: "מתחילים מחדש. זה לוקח בדרך כלל כמה דקות.",
        providerResumedTeam: "ננסה שוב עכשיו. אם הספק שלנו עדיין לא עובד, השליחה תיעצר שוב ותצא ברגע שהצוות שלנו יפתור את התקלה. אין צורך לעשות שום דבר נוסף.",
        sendRestartResumes: "השרת הופעל מחדש בזמן שליחת ההודעה. אורחים שכבר קיבלו אותה לא יקבלו אותה פעמיים, והשאר יקבלו אותה אוטומטית בעוד כמה דקות.",
        sendRestartRetry: "השרת הופעל מחדש בזמן שליחת ההודעה. אורחים שכבר קיבלו אותה לא יקבלו אותה פעמיים. שלחו אותה שוב בעוד דקה כדי להגיע לשאר.",
        // Outage-held guests: see _outageHeldNoteHtml.
        heldPhrase: "ייתכן ש-{n} אורחים כבר קיבלו אותה",
        heldPhraseOne: "ייתכן שאורח אחד כבר קיבל אותה",
        heldWhy: "בזמן התקלה אצל ספק ההודעות שלנו, הוואטסאפ או ה-SMS שלהם לא קיבלו תשובה, כך שייתכן שההודעה הגיעה בכל זאת. כדי שאף אחד לא יקבל אותה פעמיים, לא שלחנו להם אותה שוב. כל השאר מקבלים אותה כרגיל.",
        heldMore: "ועוד {n}",
        heldSend: "לשלוח גם ל-{n} האלה",
        heldSendOne: "לשלוח גם לאורח הזה",
        heldSendDone: "בוצע. שלחנו להם את ההודעה.",
        heldSendWithResume: "בוצע. הם יקבלו אותה יחד עם כל השאר כשהשליחה תתחדש.",
        heldSendBusy: "ההודעה הזו נשלחת עכשיו. נסו שוב כשהשליחה תסתיים.",
        heldSendPaused: "חדשו קודם את שליחת ההודעה, ואז שלחו להם.",
        heldSendFailed: "לא הצלחנו לשלוח להם כרגע. נסו שוב בעוד כמה דקות.",
        // Interrupted Add Contacts (PMb).
        errInterruptedLabel: "לא נשלח (השליחה נקטעה)",
        errInterruptedTip: "השליחה נעצרה לפני שהגיעה לאורח הזה, כי השרת שלנו הופעל מחדש. לא נשלח לו דבר ולא חויבתם; אפשר לשלוח שוב בלי חשש, והשליחה החוזרת מחויבת כמו כל שליחה.",
        acNotSent: "{n} לא נשלחו: השליחה נקטעה. שלחו להם שוב מתוך סטטוס",
        addRestartRetry: "השרת שלנו הופעל מחדש, ולכן שום דבר לא נוסף ולא נשלח. נסו שוב בעוד דקה.",
        // Retry, resend explainer, ghost-template recovery, follow-up, CSV export.
        retryWhichMessage: "לא ניתן לדעת באיזו הודעה השליחה נכשלה. יש לבחור הודעה בראש החלון ולנסות שוב.",
        retryAMessage: "הודעה",
        retryScopeOne: "ההודעה \"{n}\" תישלח אליהם שוב.",
        retryScopeMany: "כל אחת מ-{n} ההודעות שנכשלו תישלח שוב, רק לאורחים האלה.",
        retryConfirmOne: "לשלוח שוב את אותה הודעה לאורח אחד?",
        retryConfirmMany: "לשלוח שוב את אותה הודעה ל-{n} אורחים?",
        retryConfirmNobody: "אורחים שכבר קיבלו אותה לא יקבלו אותה שוב.",
        retryConfirmFree: "השליחה החוזרת חינמית: הקרדיט על כל הודעה שנכשלה כבר הוחזר.",
        retryResending: "בשליחה חוזרת…",
        nothingTryLater: "אפשר לנסות שוב בעוד כמה שעות.",
        nothingTryAfter: "אפשר לנסות שוב אחרי {n}.",
        nothingCooledOne: "וואטסאפ דחתה את ההודעה הזו לאורח אחד ב-24 השעות האחרונות, ולכן הוא דולג במקום ניסיון נוסף.",
        nothingCooledMany: "וואטסאפ דחתה את ההודעה הזו ל-{n} אורחים ב-24 השעות האחרונות, ולכן הם דולגו במקום ניסיון נוסף.",
        nothingDeadOne: "לאורח אחד אין וואטסאפ — שליחה חוזרת לא תגיע אליו. אפשר לנסות במייל או ב-SMS.",
        nothingDeadMany: "ל-{n} אורחים אין וואטסאפ — שליחה חוזרת לא תגיע אליהם. אפשר לנסות במייל או ב-SMS.",
        nothingReachedOne: "אורח אחד כבר קיבל את ההודעה הזו בדרך אחרת, ולכן היא לא נשלחה שוב.",
        nothingReachedMany: "{n} אורחים כבר קיבלו את ההודעה הזו בדרך אחרת, ולכן היא לא נשלחה שוב.",
        nothingNobody: "הפעם לא הצלחנו להגיע לאף אחד. כדאי לנסות שוב מאוחר יותר.",
        ghostConfirm: "לבנות מחדש את תבנית הוואטסאפ של ההודעה ולשלוח שוב לאורחים שלא קיבלו אותה?\n\nזה עשוי לקחת כמה דקות. לא יחויב קרדיט נוסף.",
        ghostStarting: "בהפעלה…",
        ghostRecovering: "בשחזור…",
        ghostButton: "יצירה מחדש ושליחה חוזרת",
        ghostStarted: "תבנית הוואטסאפ נבנית מחדש וההודעה נשלחת שוב — זה לוקח כמה דקות. נשלח מייל בסיום.",
        ghostAlready: "שחזור כבר מתבצע.",
        ghostNotNeeded: "בעיית תבנית הוואטסאפ כבר לא מופיעה בהודעה הזו, כך שאין מה לבנות מחדש.",
        ghostStartFailed: "לא ניתן היה להתחיל את השחזור. אפשר לנסות שוב בעוד כמה דקות.",
        ghostFixed: "תבנית הוואטסאפ תוקנה.",
        ghostResent: "נשלחה שוב.",
        ghostResentToOne: "נשלחה שוב לאורח אחד.",
        ghostResentTo: "נשלחה שוב ל-{n} אורחים.",
        ghostEscalated: "התבנית שנבנתה מחדש עדיין לא עוברת את הבדיקה של וואטסאפ. הצוות שלנו קיבל הודעה. יש לפנות לתמיכה.",
        ghostIssue: "בעיה בתבנית הוואטסאפ.",
        ghostIssueBody: "{n} הודעות לא נמסרו כי וואטסאפ דחתה את התבנית. ניסיון חוזר רגיל לא יעזור — יש לבנות מחדש את התבנית ולשלוח שוב.",
        followUpLoadedOne: "נטען איש קשר אחד להודעת המשך",
        followUpLoaded: "נטענו {n} אנשי קשר להודעת המשך",
        csvPhone: "טלפון",
        csvEmail: "אימייל",
        csvDeliveryStatus: "סטטוס מסירה",
        csvDeliveryIssue: "בעיית מסירה",
        csvRsvp: "תשובת אישור הגעה",
        csvGuests: "מספר אורחים",
        csvPending: "בהמתנה",
        csvNotSent: "לא נשלחה",
        csvAttending: "אישור הגעה",
        csvDeclined: "סירוב",
        csvMaybe: "אולי",
        csvResponded: "התקבלה תשובה",
        csvNoResponse: "טרם התקבלה תשובה",
    },
    ar: {
        remWaOnlyWaitingTip: "ضيوفك عبر البريد الإلكتروني والرسائل النصية تلقّوا هذا التذكير بالفعل. سيتلقّاه ضيوف واتساب تلقائياً فور موافقة واتساب (Meta) على الرسالة.",
        remWaOnlyWaiting: "سيتلقّاه ضيوف واتساب بعد الموافقة",
        quietHoldSystemTip: "كان موعد هذا التذكير في ساعات الليل لدى بعض ضيوفك، لذلك ينتظر حتى يحلّ النهار لديهم جميعًا. لا داعي لفعل أي شيء.",
        quietHoldLateTip: "وافق واتساب على رسالتك في ساعات الليل لدى بعض ضيوفك. حتى لا نوقظهم، ستُرسل عندما يحلّ النهار لديهم جميعًا. لا داعي لفعل أي شيء.",
        remWaLost: "لم يُرسل عبر واتساب",
        remWaLostTip: "لم يقبل واتساب رسالة هذا التذكير، لذلك لم يتلقّها ضيوف واتساب. ضيوفك عبر البريد الإلكتروني والرسائل النصية تلقّوها بالفعل. عدّل التذكير واحفظه لإرساله إلى ضيوف واتساب.",
        remWaLostTimeoutTip: "لم يوافق واتساب على رسالة هذا التذكير خلال 48 ساعة من موعد إرسالها، لذلك لم يتلقّها ضيوف واتساب. ضيوفك عبر البريد الإلكتروني والرسائل النصية تلقّوها بالفعل. عدّل التذكير واحفظه لإعادة إرساله للمراجعة، ولا حاجة إلى تغيير أي شيء.",
        remWaLostPhrase: "عدّل واحفظ لإرساله إلى ضيوف واتساب",
        waTimeoutBody: "لم يوافق واتساب على هذه الرسالة خلال 48 ساعة من موعد إرسالها، لذلك لم يتلقّها ضيوف واتساب.",
        waOthersHaveIt: "ضيوفك عبر البريد الإلكتروني والرسائل النصية تلقّوها بالفعل. «{editResend}» يرسلها إلى ضيوف واتساب.",
        waRejectedBadgeTip: "لم يقبل واتساب هذه الرسالة. عدّل الرسالة وأعد إرسالها إلى واتساب للموافقة عليها.",
        previewTitle: "معاينة الرسالة",
        previewNoBody: "(لم يُحفظ نص للرسالة)",
        previewNoteOne: "في واتساب يظهر هذا كالزر أعلاه (انقر لاختبار الرابط).",
        previewNoteMany: "في واتساب والبريد الإلكتروني تظهر هذه كأزرار (انقر على كل منها لاختبار الرابط).",
        previewSmsNote: "عبر الرسائل النصية يصل الرابط كنص:",
        previewSampleFor: "مثال معروض لـ {name} — المتغيرات مثل {{first_name}} تُملأ لكل ضيف.",
        previewClone: "نسخ الرسالة كمسودة جديدة",
        close: "إغلاق",
        addContacts: "إضافة جهات اتصال",
        preview: "معاينة", status: "الحالة", remind: "تذكير", add: "إضافة", reminders: "تذكيرات", remindersNoneTip: "لا توجد تذكيرات بعد — أرسل واحدًا الآن",
        duplicate: "تكرار", delete: "حذف", edit: "تعديل", editResend: "تعديل وإعادة الإرسال",
        personalLinks: "روابط شخصية", sharedByHand: "رابط شخصي", copyPersonalLink: "نسخ الرابط الشخصي", linkCopied: "تم نسخ الرابط الشخصي",
        sendOnWhatsApp: "الإرسال عبر واتساب من هاتفك", waWalkBtn: "الإرسال من واتساب الخاص بي", waWalkIntro: "تعذّر على واتساب الخاص بنا الوصول إلى هؤلاء الضيوف. أرسل الرسالة لكل ضيف من واتساب الخاص بك: افتحها، واضغط إرسال، ثم عُد للضيف التالي.", waWalkCounter: "الضيف {i} من {n}", waWalkOpen: "فتح واتساب", waWalkSkip: "تخطٍّ", waWalkClose: "إغلاق", waWalkDone: "تم. فتحت واتساب لـ {k} من {n} ضيوف.",
        hostSentBadge: "أُرسلت من واتساب الخاص بك", hostSentTip: "فتحت واتساب الخاص بك لإرسال هذه الرسالة. لا يمكننا رؤية ما يجري داخل واتساب، لذا لم يتأكد التسليم.", waWalkCardBtn: "الإرسال من واتساب الخاص بي ({n})", waWalkCardTip: "تعذّر على واتساب الخاص بنا الوصول إلى هؤلاء الضيوف. أرسل لهم هذه الرسالة من واتساب الخاص بك، واحدًا تلو الآخر.",
        waWalkImageNote: "يفتح واتساب بالنص فقط. لإضافة صورة دعوتك، أرفقها في واتساب قبل الضغط على إرسال.", waWalkImageOpen: "افتح الصورة لحفظها",
        addedSkippedOptedOut: "لم يُرسل إلى {n} من الضيوف المضافين: التذكيرات متوقفة لهم", addedSkippedResponded: "لم يُرسل إلى {n} من الضيوف المضافين: سبق أن ردّوا",
        tagContacts: "وسم الجميع في هذه الرسالة", tagContactsPrompt: "وسوم تُضاف إلى كل ضيف في هذه الرسالة، مفصولة بفواصل (مثال: العائلة، الاستقبال فقط):", tagContactsDone: "تم وسم {n} ضيوف", tagContactsFailed: "تعذر وسم الضيوف",
        continueBtn: "متابعة", pause: "إيقاف مؤقت", cancel: "إلغاء", sendNow: "إرسال الآن",
        sendTest: "إرسال اختبار",
        sendTestTip: "أرسل لنفسك نسخة واحدة من هذه الرسالة لترى تمامًا ما سيصل إلى ضيوفك",
        sendTestSending: "جارٍ إرسال الاختبار…",
        sendTestDone: "تم إرسال الاختبار إليك",
        sendTestEmailCopyOnly: "البريد الإلكتروني نسخة لك فقط — لن يتلقى ضيوفك بريدًا إلكترونيًا.",
        sendTestNoMessage: "أضف رسالة واختر طريقة إرسالها، ثم يمكنك إرسال اختبار إلى نفسك",
        sendTestFailed: "تعذّر إرسال الاختبار",
        sendTestCreditReturned: "أعدنا رصيد هذا الاختبار إلى حسابك.",
        sendTestCost: "أول اختبار لهذه الرسالة مجاني. بعد ذلك يكلّف كل اختبار عبر SMS أو واتساب رصيداً واحداً — واختبارات البريد مجانية دائماً.",
        sendTestAgain: "إرسال اختبار آخر",
        sendTestAgainTip: "أرسل لنفسك نسخة أخرى — مفيد بعد تغيير النص",
        sendTestPhoneTitle: "إلى أين نرسل الاختبار؟",
        sendTestPhoneHint: "لا يوجد رقم جوال في حسابك، فاكتب رقمًا لإرسال الاختبار إليه. سنحفظه لك.",
        sendTestPhoneInvalid: "هذا لا يبدو رقم جوال. استخدم الصيغة الدولية، مثل ‎+14155550123.",
        resume: "استئناف", buyCredits: "شراء رصيد", retry: "إعادة المحاولة",
        previewTip: "معاينة نص الرسالة",
        statusTip: "معرفة من استلم الرسالة وردّه",
        remindTip: "إرسال متابعة إلى ضيوف هذه الرسالة",
        addTip: "إضافة المزيد من جهات الاتصال إلى هذه الرسالة",
        addScheduledTip: "أضف المزيد من جهات الاتصال — سيستلمون الرسالة في وقت الإرسال المجدول",
        addDisabledTip: "تعذّر إضافة جهات اتصال — أصلح هذه الرسالة أولاً عبر تعديل وإعادة الإرسال (لم يصل قالب واتساب إلى Meta أبداً).",
        duplicateTip: "إعادة استخدام هذه الرسالة (صورة، نص، لغات، رابط) في رسالة جديدة تماماً",
        deleteTip: "حذف هذه الرسالة نهائياً من سجلك",
        pauseTip: "إيقاف هذه الرسالة مؤقتاً",
        buyCreditsTip: "اشحن رصيد رسائلك ثم استأنف هذه الرسالة",
        retryTip: "حاول الإرسال مرة أخرى (ينجح عندما يتوفر لديك رصيد كافٍ)",
        manageRemindersTip: "إدارة التذكيرات المجدولة",
        scheduleRemindersTip: "جدولة التذكيرات",
        resumeAutoRetryTip: "استئناف إعادة المحاولة التلقائية ({n} قيد الانتظار)",
        pauseAutoRetryTip: "إيقاف إعادة المحاولة التلقائية ({n} قيد الانتظار)",
        remindPending: "الضيوف المعلّقون", remindPendingSub: "لم يردّوا بعد",
        remindMaybe: "من ردّوا بربما", remindMaybeSub: "قالوا ربما — شجّعهم على التأكيد",
        remindYes: "من ردّوا بنعم", remindYesSub: "تذكير يوم الحدث، تحديث المكان، إلخ.",
        remindNo: "من ردّوا بالاعتذار", remindNoSub: "لا يستطيعون الحضور — أرسل رابط بث مباشر أو رسالة شكر، إلخ.",
        remindRemovedOne: "الضيف الذي أزلته لن يتلقى هذا التذكير.", remindRemovedMany: "الضيوف الذين أزلتهم ({n}) لن يتلقوا هذا التذكير.",
        // Response-edit (pencil) window: _openResponseEditModal and what it opens.
        reTitle: "تحديث رد {name}",
        reThisGuest: "هذا الضيف",
        reContact: "جهة الاتصال",
        reManualEntry: "إدخال يدوي:",
        reManualEntryNote: "أُنشئ هنا لعدم وجود رد في النموذج.",
        reRemove: "إزالة",
        reCurrentlyPaired: "مرتبط حاليًا:",
        reUnpair: "إلغاء الربط",
        reEditDetails: "تعديل بيانات جهة الاتصال",
        rePhone: "الهاتف",
        reEmail: "البريد الإلكتروني",
        reInvitationHelp: "اكتب الجميع مفصولين بـ {sep} (مثل {example}). تصل التعديلات إلى رابطه تلقائيًا.",
        reChildMarker: "(طفل)",
        reExample: "محمد علي; سارة (طفل)",
        reMaxGuests: "الحد الأقصى للضيوف",
        reMembersPh: "مثال: محمد علي; سارة (طفل)",
        // Task #935: the guest-list columns (Add contacts, the contact window).
        acGuestNames: "أسماء الضيوف",
        acGuestNamesHint: "كيفية إضافة أسماء الضيوف\n• افصل بين الأسماء بفاصلة منقوطة (;). الفاصلة تعمل أيضًا.\n• أضف (طفل) بعد اسم الطفل، مثل: يوسف حداد (طفل).\n• مثال: سارة حداد; محمد حداد; يوسف حداد (طفل)\n• الهاتف والبريد في الصف للضيف الأول في القائمة.",
        acGroupName: "اسم المجموعة",
        acGroupNameHint: "لمن الدعوة موجّهة، مثل عائلة حداد: على الظرف وفي ترحيب النموذج؛ أما الرسائل فتحيّي أسماء الضيوف. دون أسماء ضيوف، يعثر الضيوف عليها بهذا الاسم. اتركه فارغًا لضيف مدعو وحده.",
        acCsvColumns: "أعمدة CSV: أسماء الضيوف، اسم المجموعة، الهاتف، البريد الإلكتروني · اختياري: الحد الأقصى للضيوف، الوسوم",
        acSameName: "تنبيه: لديك عدة ضيوف أو مجموعات بالاسم نفسه. أضف بريدًا إلكترونيًا أو رقم هاتف لكل منهم ليتمكنوا من التحقق من هويتهم عند البحث عن الدعوة.",
        acLastFirst: "شخص واحد واسم العائلة أولًا؟ افصل بين الأشخاص المختلفين بـ \";\".",
        reGroupName: "اسم المجموعة",
        reGroupNameHint: "لمن الدعوة موجّهة: الظرف والتحية. عند عدم إدراج أحد، يعثر الضيوف عليها بهذا الاسم.",
        reMaxGuestsHint: "اتركه فارغًا ليُحسب حجم المجموعة من الأسماء (أو من حد النموذج).",
        reGroupNamePh: "مثل عائلة حداد",
        reGuestNames: "أسماء الضيوف",
        reTags: "الوسوم",
        reTagsPh: "مثال: العائلة، الاستقبال فقط",
        reTagsHelp: "تحدد الوسوم أجزاء الحدث التي يعرضها رابط هذا الضيف، عندما يكون جزء مقصورًا على وسوم معينة.",
        reLoading: "جارٍ التحميل…",
        reSave: "حفظ",
        rePairHeading: "الربط برد موجود",
        rePairHelp: "إذا ردّ {name} عبر النموذج ولم يُربط الرد تلقائيًا (مثل رابط مشترك أو رقم هاتف آخر)، اربطه من هنا.",
        reLooking: "جارٍ البحث عن ردود مطابقة…",
        reManualHeading: "أو التسجيل يدويًا",
        reManualHelp: "استخدم هذا فقط إذا ردّ {name} خارج النموذج (رسالة، مكالمة، شخصيًا).",
        reAttending: "سيحضر",
        reDeclined: "لن يحضر",
        reMaybe: "ربما",
        reResponded: "ردّ",
        rePending: "بانتظار الرد",
        reSaving: "جارٍ الحفظ…",
        reSaved: "تم الحفظ ✓",
        reCouldNotSave: "تعذّر الحفظ",
        reNotOnMessage: "لم يعد هذا الضيف ضمن الرسالة، لذا لا يمكن تعديل دعوته هنا.",
        rePartyMany: "الدعوة لـ {n} أشخاص.",
        rePartyOne: "الدعوة لشخص واحد.",
        reLoadFailed: "تعذّر تحميل الدعوة.",
        reMaxInvalid: "يجب أن يكون الحد الأقصى للضيوف عددًا صحيحًا من 1 إلى 50.",
        reBrowseAll: "عرض كل الردود غير المرتبطة ({n})",
        reBrowseOne: "عرض الرد غير المرتبط",
        reHideList: "إخفاء القائمة",
        reCandidatesFailed: "تعذّر تحميل التطابقات المحتملة.",
        reManualTag: "يدوي",
        reManualTagTip: "أُنشئ كإدخال يدوي لجهة اتصال أخرى",
        reNoName: "(بدون اسم)",
        rePair: "ربط",
        reLikely: "تطابقات محتملة",
        reNoUnpaired: "لا توجد ردود غير مرتبطة في هذا النموذج. إذا ردّ الضيف خارج النموذج، فاستخدم أزرار \"أو التسجيل يدويًا\".",
        reNoLikely: "لا توجد تطابقات محتملة بالاسم أو الهاتف أو البريد الإلكتروني. تصفّح القائمة الكاملة إذا كنت تعرف رده.",
        reSearchPh: "ابحث بالاسم أو الهاتف أو البريد الإلكتروني…",
        rePaired: "تم ربط الرد بـ {name}",
        reUnpaired: "تم إلغاء ربط {name}",
        reRecorded: "تم تسجيل \"{status}\" لـ {name}",
        reManualRemoved: "تمت إزالة الإدخال اليدوي",
        reFailedPair: "تعذّر ربط الرد",
        reFailedUnpair: "تعذّر إلغاء الربط",
        reFailedRecord: "تعذّر تسجيل الرد",
        reFailedRemove: "تعذّرت الإزالة",
        reUpdatedNumber: "تم تحديث الرقم.",
        reUpdatedEmail: "تم تحديث البريد الإلكتروني.",
        reUpdatedDetails: "تم تحديث بيانات جهة الاتصال.",
        reResendAsk: "إرسال هذه الرسالة مرة أخرى إلى {dest}؟",
        reMultiMessage: "تظهر جهة الاتصال هذه في أكثر من رسالة، لذا استخدم {addContacts} في الرسالة التي تريد إعادة إرسالها.",
        reResend: "إعادة الإرسال",
        reSending: "جارٍ الإرسال…",
        reSentNumber: "أُرسلت إلى الرقم الجديد، وستظهر في سجل جهة الاتصال قريبًا.",
        reQueued: "في الانتظار: ستُرسل تلقائيًا فور موافقة واتساب على هذه الرسالة، عادةً خلال دقائق.",
        reSentEmail: "أُرسلت إلى البريد الإلكتروني الجديد، وستظهر في سجل جهة الاتصال قريبًا.",
        reSentBoth: "أُرسلت إلى الرقم والبريد الإلكتروني الجديدين، وستظهر في سجل جهة الاتصال قريبًا.",
        reResendFailed: "تعذّرت إعادة الإرسال. حاول مرة أخرى.",
        reminderDupSkipped: "تلقّوا تذكيرًا من رسالة أخرى بالفعل: {n}",
        reminderDupSkippedBy: "تلقّوا التذكير بالفعل من «{name}»: {n}",
        reminderDupSkippedTip: "تلقّى هؤلاء الضيوف التذكير نفسه من رسالة أخرى لهذه المناسبة خلال آخر 20 ساعة، لذلك لم يُرسل إليهم هذا التذكير مرة ثانية.",
        reminderDupAll: "تلقّى الجميع تذكيرًا من رسالة أخرى بالفعل",
        remAttentionOne: "تذكير واحد يحتاج إلى انتباهك",
        remAttentionMany: "تذكيرات تحتاج إلى انتباهك: {n}",
        remFailExpiredNote: "لم يوافق واتساب على رسالة هذا التذكير في الوقت المناسب. عدّله واحفظه للمحاولة مرة أخرى.",
        remFailRejectedNote: "لم يقبل واتساب رسالة هذا التذكير. عدّل الرسالة واحفظها لإعادة إرسالها للمراجعة.",
        remAudienceBtn: "عرض من سيصله هذا التذكير",
        remAudienceTitle: "من يصله هذا التذكير",
        msgAudienceTitle: "من تصله هذه الرسالة",
        msgAudienceOne: "تُرسل هذه الرسالة إلى هذا الضيف.",
        msgAudienceMany: "تُرسل هذه الرسالة إلى هؤلاء الضيوف الـ{n}.",
        remAudienceNowOne: "لو أُرسل الآن لوصل إلى ضيف واحد. تُحدَّد القائمة نهائيًا عند الإرسال، لذا قد تتغير.",
        remAudienceNowMany: "لو أُرسل الآن لوصل إلى {n} من الضيوف. تُحدَّد القائمة نهائيًا عند الإرسال، لذا قد تتغير.",
        remAudienceFixedOne: "يُرسل هذا التذكير إلى هذا الضيف.",
        remAudienceFixedMany: "يُرسل هذا التذكير إلى هؤلاء الضيوف ({n}).",
        remAudienceEmpty: "لا يوجد أحد مطابق حاليًا. ستُفحص القائمة مجددًا عند إرسال التذكير.",
        remAudienceFailed: "تعذّر تحميل من يصله هذا التذكير. حاول مرة أخرى.",
        remAudienceFixedNone: "قائمة هذا التذكير فارغة، لذا لن يصل إلى أحد.",
        remAudienceMeantOne: "كان يُفترض أن يُرسل هذا التذكير إلى هذا الضيف. لن يُرسل حتى تعدّله وتحفظه.",
        remAudienceMeantMany: "كان يُفترض أن يُرسل هذا التذكير إلى هؤلاء الضيوف ({n}). لن يُرسل حتى تعدّله وتحفظه.",
        remFailWaOnlyRefusedNote: "لم يقبل واتساب رسالة هذا التذكير، ولا يمكن الوصول إلى ضيوفه إلا عبر واتساب، لذا لم يُرسل شيء. من الأسباب الشائعة وجود وسم تخصيص في نهاية الرسالة تمامًا: أضف نصًا بعده ثم احفظ لإعادة إرسالها للمراجعة.",
        acSentOf: "أُرسل {sent} من {total}",
        acSkippedUntil: "أوقف واتساب مؤقتًا الإرسال إلى {n} حتى {time}، حاول مرة أخرى حينها",
        acSkipped: "تم تخطي {n}",
        acUnreachable: "لم يُرسَل {n} (بيانات الاتصال)",
        acFailed: "لم يصل إلى {n}",
        acDeferred: "سيُرسل إلى {n} في نافذة الإرسال التالية",
        acLineOne: "أُضيف ضيف واحد بعد الإرسال",
        acLineMany: "ضيوف أُضيفوا بعد الإرسال: {n}",
        acLineLast: "آخرها {n}",
        acLineAwaiting: "{n} بانتظار موافقة واتساب",
        acTipAdded: "{when}: أُضيف {n}",
        reResendCooldown: "يوقف واتساب مؤقتًا الرسائل إلى هذا الرقم حتى {time}. لم يُخصم أي رصيد. حاول مرة أخرى بعد ذلك، أو أرسل الرابط الشخصي بنفسك.",
        reResendCooldownNoTime: "يوقف واتساب مؤقتًا الرسائل إلى هذا الرقم حاليًا. لم يُخصم أي رصيد. حاول لاحقًا، أو أرسل الرابط الشخصي بنفسك.",
        reResendNotSent: "تعذّر إرسال الرسالة إلى هذه البيانات. لم يُخصم أي رصيد. تحقّق من الرقم أو العنوان وحاول مرة أخرى.",
        // Every guest skipped (Meta 24h cooldown, not on WhatsApp...): see _skipSummary and _mergeSendTimeFailures.
        skipNotSent: "لم تُرسَل",
        skipNotSentTip: "لم يُرسَل شيء: أُوقف الإرسال إلى جميع ضيوف هذه الرسالة. توضح الملاحظة أدناه السبب وما يمكنك فعله.",
        skippedOne: "تم تخطي 1",
        skippedMany: "تم تخطي {n}",
        skippedTip: "أُوقف الإرسال إليهم عمداً ولم يُرسَل. انقر لمعرفة من ولماذا.",
        skipCoolOne: "رفض واتساب رسالة حديثة إلى ضيف واحد، لذلك نوقف الرسائل إليه مؤقتاً حتى {time}.",
        skipCoolMany: "رفض واتساب رسائل حديثة إلى {n} من الضيوف، لذلك نوقف الرسائل إليهم مؤقتاً. يمكن إعادة المحاولة مع أولهم بعد {time}.",
        skipCoolLiftedOne: "رفض واتساب رسالة حديثة إلى ضيف واحد. انتهى إيقافنا المؤقت للرسائل إليه.",
        skipCoolLiftedMany: "رفض واتساب رسائل حديثة إلى {n} من الضيوف. انتهى إيقافنا المؤقت بالنسبة لأولهم.",
        skipCoolWhy: "يرفض واتساب الرسائل التسويقية إلى من تلقّى الكثير منها مؤخراً، ولا يحدد المدة. بعد الرفض نوقف مؤقتاً رسائلنا إلى ذلك الرقم بدلاً من الإرسال مجدداً. ويشمل الإيقاف كل المناسبات على QuikRSVP، لذا قد لا تكون الرسالة المرفوضة رسالتك. لا تُحتسب عليك الرسالة الموقوفة. وقد تُرفض المحاولة الجديدة بعد الإيقاف أيضاً.",
        skipNoWaOne: "ضيف واحد ليس على واتساب. تواصل معه عبر الرسائل النصية أو البريد الإلكتروني، أو أرسل له الرابط الشخصي بنفسك.",
        skipNoWaMany: "{n} من الضيوف ليسوا على واتساب. تواصل معهم عبر الرسائل النصية أو البريد الإلكتروني، أو أرسل لهم الروابط الشخصية بنفسك.",
        skipUnreachOne: "تعذّر الإرسال إلى ضيف واحد: يجب تصحيح رقم الهاتف أو البريد الإلكتروني. افتح القائمة لمعرفة من وما الذي يجب تصحيحه.",
        skipUnreachMany: "تعذّر الإرسال إلى {n} من الضيوف: يجب تصحيح رقم الهاتف أو البريد الإلكتروني. افتح القائمة لمعرفة من وما الذي يجب تصحيحه.",
        skipOptOutOne: "ردّ ضيف واحد بـ STOP على رسائل واتساب منا، لذلك لا نراسله هناك.",
        skipOptOutMany: "ردّ {n} من الضيوف بـ STOP على رسائل واتساب منا، لذلك لا نراسلهم هناك.",
        skipUnsubOne: "ألغى ضيف واحد اشتراكه في رسائلك الإلكترونية. تواصل معه عبر واتساب أو الرسائل النصية بدلاً من ذلك.",
        skipUnsubMany: "ألغى {n} من الضيوف اشتراكهم في رسائلك الإلكترونية. تواصل معهم عبر واتساب أو الرسائل النصية بدلاً من ذلك.",
        skipDoRetryAt: "حاول مرة أخرى بعد {time}، أو افتح روابط شخصية وأرسل الرابط بنفسك.",
        skipDoRetryNow: "حاول مرة أخرى الآن، أو افتح روابط شخصية وأرسل الرابط بنفسك.",
        skipDoLink: "افتح روابط شخصية وأرسل الرابط بنفسك.",
        skipHandledRepliedOne: "لم نرسلها نحن، لكن هذا الضيف قد ردّ بالفعل.",
        skipHandledRepliedMany: "لم نرسلها نحن، لكن جميع الضيوف المعنيين قد ردّوا بالفعل.",
        skipHandledLinkOne: "لم نرسلها نحن، لكن هذا الضيف قد فتح رابطه الشخصي بالفعل.",
        skipHandledLinkMany: "لم نرسلها نحن، لكن جميع الضيوف المعنيين قد فتحوا روابطهم الشخصية أو ردّوا بالفعل.",
        skipStillBlockedOne: "ما زلنا نوقف الرسائل إلى هذا الضيف مؤقتاً حتى {time}. حاول مرة أخرى بعد ذلك، أو أرسل الرابط الشخصي بنفسك.",
        skipStillBlockedMany: "ما زلنا نوقف الرسائل إلى هؤلاء الضيوف مؤقتاً. يمكن إعادة المحاولة مع أولهم بعد {time}؛ وحتى ذلك الحين، أرسل الروابط الشخصية بنفسك.",
        skipRowCool: "رفض واتساب رسالة حديثة إلى هذا الرقم (ربما من مناسبة أخرى)، لذلك نوقف كل الرسائل إليه مؤقتاً حتى {time}. حاول مرة أخرى بعد ذلك، أو أرسل الرابط الشخصي بنفسك.",
        skipRowCoolLifted: "رفض واتساب رسالة حديثة إلى هذا الرقم (ربما من مناسبة أخرى). انتهى إيقافنا المؤقت، لذا يمكنك المحاولة مرة أخرى أو إرسال الرابط الشخصي بنفسك.",
        skipRowNoWa: "هذا الرقم ليس على واتساب. تواصل مع الضيف عبر الرسائل النصية أو البريد الإلكتروني، أو أرسل الرابط الشخصي بنفسك.",
        skipRowNoWaNoEmail: "هذا الرقم ليس على واتساب. تواصل مع الضيف عبر الرسائل النصية، أو أرسل الرابط الشخصي بنفسك.",
        fbTipFailedEmail: "تعذّر على واتساب توصيل الرسالة، لذا أُرسلت بالبريد الإلكتروني بدلاً من ذلك.",
        fbTipFailedSms: "تعذّر على واتساب توصيل الرسالة، لذا أُرسلت برسالة نصية بدلاً من ذلك.",
        fbTipNoWa: "هذا الرقم ليس على واتساب، لذا أرسلنا إلى الضيف بريداً إلكترونياً بدلاً من ذلك.",
        fbTipCool: "رسائل واتساب إلى هذا الرقم متوقفة مؤقتاً الآن، لذا أرسلنا إلى الضيف بريداً إلكترونياً بدلاً من ذلك.",
        fbStateOpened: "تم فتح الرسالة.",
        fbStateDelivered: "تم تسليم الرسالة.",
        fbStateOnWay: "الرسالة في الطريق.",
        skipRowOptOut: "ردّ هذا الضيف بـ STOP على رسائل واتساب منا، لذلك لا نراسله هناك.",
        skipRowOptOutSms: "ردّ هذا الضيف بـ STOP على رسائلنا النصية، لذلك لن نرسل له رسائل نصية مرة أخرى.",
        skipRowUnsub: "ألغى هذا الضيف اشتراكه في رسائلك الإلكترونية. تواصل معه عبر واتساب أو الرسائل النصية بدلاً من ذلك.",
        skipRowOther: "تم التخطي: لا يمكن لهذا الرقم تلقي رسائل واتساب حالياً.",
        skipRowNoDetails: "لم يُرسَل: لا يوجد رقم هاتف ولا بريد إلكتروني. أضف أحدهما وأعد الإرسال، أو أرسل الرابط الشخصي بنفسك.",
        skipRowBadNumber: "لم يُرسَل: لا يمكن الاتصال بهذا الرقم. تحقّق من الأرقام ورمز الدولة ثم أعد الإرسال.",
        skipRowNoEmail: "لم يُرسَل: مُعدّ للإرسال بالبريد الإلكتروني ولكن لا يوجد عنوان. أضف عنواناً أو أرسل عبر واتساب.",
        skipRowNoPhone: "لم يُرسَل: مُعدّ للإرسال برسالة نصية ولكن لا يوجد رقم. أضف رقماً وأعد الإرسال.",
        skipRowShared: "لم يُرسَل: نفس رقم {name}، ويتلقى كل رقم رسالة واحدة فقط. أعطِ الضيف رقماً أو بريداً خاصاً به، أو أضفه إلى دعوة {name}.",
        skipRowSharedNoName: "لم يُرسَل: لدى ضيف آخر نفس الرقم، ويتلقى كل رقم رسالة واحدة فقط. أعطِ الضيف رقماً أو بريداً خاصاً به.",
        skipRowNoChannel: "لم يُرسَل: لم تُحدَّد طريقة إرسال لهذا الضيف. عدّل بياناته وأعد الإرسال.",
        skipBadge: "تم التخطي",
        unsubBadge: "ألغى الاشتراك",
        skipRetrySent: "تم الإرسال. ستتحدّث البطاقة بعد لحظات.",
        skipRetryFailed: "لم يقبلها واتساب هذه المرة. افتح الحالة لمعرفة السبب.",
        skipRetryStill: "لم يُرسَل شيء: ما زال الإيقاف المؤقت للرسائل إلى هؤلاء الضيوف سارياً. حاول مرة أخرى لاحقاً، أو أرسل الرابط الشخصي بنفسك.",
        skipRetryHeld: "أُوقفت لمراجعة سريعة قبل الإرسال. ستُرسَل فور الموافقة عليها.",
        skipRetryPaused: "لم تُرسَل: إنها متوقفة مؤقتاً. توضح البطاقة السبب.",
        skipRetryNoCredits: "متوقفة مؤقتاً: أضف رصيداً لإرسالها.",
        skipRetryRequested: "طُلبت إعادة المحاولة. توضح البطاقة ما وصلت إليه.",
        skipRetryError: "تعذّرت إعادة المحاولة الآن. حاول مرة أخرى بعد دقيقة.",
        retryBusy: "لا تزال هذه الرسالة قيد الإرسال. عند انتهاء الإرسال، تحقّق ممن لا يزال بحاجة إليها ثم حاول مرة أخرى.",
        statusLoadFailed: "تعذّر تحميل قائمة الضيوف الآن.",
        statusLoadExpired: "انتهت صلاحية جلستك. أعد تحميل الصفحة لعرض قائمة الضيوف.",
        statusLoadRetry: "حاول مرة أخرى",
        statusLoadReload: "إعادة تحميل الصفحة",
        statusRefreshFailed: "تعذّر تحديث قائمة الضيوف. أعد تحميل الصفحة لعرض أحدث حالة.",
        retrySentOne: "أُعيد إرسال الرسالة إلى ضيف واحد.",
        retrySentMany: "أُعيد إرسال الرسالة إلى {n} من الضيوف.",
        retryStillFailedOne: "تعذّر الوصول إلى ضيف واحد حتى الآن.",
        retryStillFailedMany: "تعذّر الوصول إلى {n} من الضيوف حتى الآن.",
        retryStaleView: "انتهت إعادة إرسال لهذه الرسالة بعد تحميل هذه القائمة، لذا لم يُرسَل شيء هذه المرة. تم تحديث القائمة: تحقّق ممن لا يزال بحاجة إليها ثم حاول مرة أخرى.",
        acSmsConsentWhy: "يتلقى الضيوف ذوو الأرقام الأمريكية هذه الرسالة عبر SMS، وقد تتلقاها بهذه الطريقة الأرقام الأخرى ذات رمز الدولة 1 (مثل كندا) أو الأرقام البرازيلية إذا تعذّر إرسالها عبر واتساب.",
        acSmsConsent: "أؤكد أن جميع المستلمين وافقوا على تلقي رسائل SMS لدعوة الحدث عبر QuikRSVP.",
        acSmsDisclosure: "يمكن للمستلمين الرد بـ STOP لإلغاء الاشتراك أو HELP للمساعدة. قد تُطبق رسوم الرسائل والبيانات. التكرار يختلف.",
        waRejectedTitle: "لم يقبل واتساب هذه الرسالة",
        waOursTitle: "تعذّر إرسال رسائل واتساب",
        waTimeoutTitle: "لم يوافق واتساب على هذه الرسالة في الوقت المحدد",
        waSubmitPendingTitle: "ما زلنا نرسل رسالتك إلى واتساب للموافقة عليها",
        waSubmitPendingBody: "لم نتمكن من الوصول إلى واتساب بعد. سنواصل المحاولة تلقائيًا وسنراسلك عبر البريد الإلكتروني إذا رفضها واتساب. لا داعي لفعل أي شيء.",
        addWaitWaTip: "يمكنك إضافة جهات اتصال بعد وصول رسالتك إلى واتساب للموافقة عليها. ما زلنا نحاول، فتحقق مرة أخرى بعد بضع دقائق.",
        remCancelled: "أُلغي",
        remCancelFormDeleted: "لم يُرسَل: حُذفت المناسبة.",
        remCancelDeadline: "لم يُرسَل: انتهى موعد تأكيد الحضور قبل موعد الإرسال.",
        remCancelDateMoved: "لم يُرسَل: قُدِّم موعد المناسبة وكان وقته قد فات.",
        remOnHold: "قيد الانتظار",
        remHeldPostponed: "قيد الانتظار: المناسبة مؤجّلة. سينتقل التذكير إلى الموعد الجديد فور تحديده.",
        remCancelEventCancelled: "لم يُرسَل: أُلغيت المناسبة.",
        remCancelPostponedLate: "لم يُرسَل: ظلّت المناسبة مؤجّلة حتى أكثر من يوم بعد موعد هذا التذكير.",
        remCancelInvitation: "لم يُرسَل: حُذفت دعوته أو أُلغيت.",
        remCancelLate: "لم يُرسَل: كانت دعوته متوقفة حتى أكثر من يوم بعد موعد هذا التذكير.",
        remCancelNotSent: "لم يُرسَل",
        remMovedWithEvent: "نُقل مع موعد المناسبة",
        remCancelDeadlineHint: "غيّر موعد تأكيد الحضور أو أزل رابط تأكيد الحضور، ثم عدّل التذكير لإرساله.",
        remCancelDateMovedHint: "عدّله لاختيار وقت جديد.",
        remSendsSoon: "سيُرسَل خلال الساعة القادمة",
        remDateMovedPast: "قُدِّم موعد المناسبة وفات الوقت الجديد لهذا التذكير، لذلك لن يُرسَل. عدّله لاختيار وقت جديد.",
        remSendNowWaiting: "سيُرسَل هذا التذكير بعد وصول دعوته إلى جميع الضيوف.",
        remSendNowNoRecipients: "لا يطابق أي ضيف هذا التذكير، لذلك لم يُرسَل شيء.",
        remSendNowTaken: "هذا التذكير قيد الإرسال بالفعل.",
        remSendNowCancelled: "أُلغي هذا التذكير لأن دعوته حُذفت أو أُلغيت.",
        remSendNowError: "لم نتمكن الآن من تحديد من يجب أن يتلقى هذا التذكير. لم يُرسَل شيء، وستُعاد المحاولة تلقائيًا.",
        moreActions: "مزيد من الإجراءات", // the card's ... menu; was missing in every pack
        // Card badges and stats, reminder rows, the Status window, Remind All
        // and the card actions' confirms/toasts (code review 2026-10-07: all
        // English on every dashboard). See cCount for the ...One forms.
        badgeSent: "تم الإرسال",
        badgeSending: "جارٍ الإرسال",
        badgeProcessing: "قيد المعالجة",
        badgeScheduled: "مجدول",
        badgePaused: "متوقف مؤقتًا",
        badgeCancelled: "ملغى",
        badgeDraft: "مسودة",
        badgeExpired: "منتهي الصلاحية",
        badgeUnableToSend: "تعذّر الإرسال",
        badgeNoCredits: "رصيد غير كافٍ",
        badgeInReview: "قيد المراجعة",
        scheduledFor: "مجدولة في {when}",
        sendingAt: "يُرسل في {when}",
        expiredTip: "لم يوافق واتساب على هذه الرسالة في الوقت المحدد. استخدم تعديل وإعادة الإرسال لإرسالها مرة أخرى.",
        noCreditsTip: "توقفت هذه الرسالة مؤقتًا لأن رصيد حسابك نفد. اشترِ رصيدًا وأعد الإرسال لتسليم الرسائل المتبقية.",
        inReviewTip: "نراجع رسائل الحسابات الجديدة قبل إرسالها، وهناك من يراجع رسالتك الآن. لم يُخصم أي شيء، ورسالتك وجهات اتصالك محفوظة. ستُرسل تلقائيًا بعد الموافقة؛ راسلنا في الدردشة إن كنت تحتاجها أسرع.",
        toggleRemindersTip: "إظهار تذكيرات هذه الرسالة أو إخفاؤها",
        showRemindersAria: "إظهار التذكيرات ({n})",
        hideRemindersAria: "إخفاء التذكيرات ({n})",
        expandAllReminders: "توسيع الكل",
        collapseAllReminders: "طي الكل",
        expandAllRemindersTip: "إظهار تذكيرات جميع الرسائل",
        collapseAllRemindersTip: "إخفاء تذكيرات جميع الرسائل",
        remindersOn: "التذكيرات مفعّلة",
        statResponded: "ردّ {n}",
        statRespondedOne: "ردّ {n}",
        statPending: "بانتظار الرد: {n}",
        statPendingOne: "بانتظار الرد: {n}",
        statSent: "أُرسل إلى {n}",
        statSentOne: "أُرسل إلى {n}",
        statContacts: "جهات الاتصال: {n}",
        statContactsOne: "جهة اتصال واحدة",
        statNotDelivered: "لم يُسلَّم إلى {n}",
        statNotDeliveredOne: "لم يُسلَّم إلى {n}",
        statRecipients: "المستلمون: {n}",
        statRecipientsOne: "مستلم واحد",
        allRepliedTip: "كل من أُرسلت إليهم كانوا قد ردّوا بالفعل — وهم من كانت موجّهة إليهم.",
        waCouldNotAccept: "تعذّر على واتساب قبول رسالتك. عدّل الرسالة وأعد إرسالها.",
        untitledMessage: "رسالة",
        editResendTip: "أصلح المشكلة وأعد إرسال رسائل واتساب",
        timingDayOf: "يوم الحدث",
        timing1DayBefore: "قبل يوم",
        timing3DaysBefore: "قبل 3 أيام",
        timing1WeekBefore: "قبل أسبوع",
        timing2WeeksBefore: "قبل أسبوعين",
        audNoResponse: "الضيوف الذين لم يردّوا",
        audAttending: "الضيوف الحاضرون",
        audMaybe: "الضيوف المتردّدون",
        audDeclined: "الضيوف المعتذرون",
        audAll: "جميع جهات الاتصال",
        remTitleAudience: "تذكير · {audience}",
        remTitleIndex: "تذكير {n}",
        remWasSetFor: "كان مجدولًا في {when}",
        remNoCreditsTip: "تعذّر إرسال هذا التذكير لأن رصيدك غير كافٍ. اشترِ رصيدًا واستأنفه لإرساله.",
        remAddCredits: "أضف رصيدًا لإرسال هذا التذكير",
        remNoRecipients: "لا يوجد مستلمون",
        remNoMatching: "لا يوجد مستلمون مطابقون",
        remResubmitTip: "عادت رسالتك المعدّلة إلى واتساب للموافقة. ستُرسل بعد الموافقة.",
        remResubmitting: "أُعيد إرساله للموافقة",
        remSendsOnceApproved: "يُرسل في {when} بعد الموافقة",
        remWaitingApproval: "بانتظار موافقة واتساب",
        remAllRepliedTip: "كل من وصلهم هذا التذكير كانوا قد ردّوا بالفعل — وهم من كان موجّهًا إليهم.",
        remExpiredTip: "لم يوافق واتساب على رسالة هذا التذكير في الوقت المحدد. عدّله وحاول مرة أخرى.",
        remRejectedTip: "رفض واتساب محتوى رسالة هذا التذكير. عدّل التذكير واحفظه لإعادة إرساله للموافقة.",
        remFailedTip: "حدثت مشكلة أثناء إرسال هذا التذكير. عدّله وحاول مرة أخرى.",
        remRejected: "رُفضت الرسالة",
        remCouldntSend: "تعذّر الإرسال",
        remTimedOut: "انتهت مهلة موافقة واتساب",
        remEditToResubmit: "عدّل واحفظ لإعادة الإرسال للموافقة",
        remSendFailed: "فشل الإرسال",
        remWaitingTip: "بانتظار موافقة واتساب (Meta) على رسالة هذا التذكير — عادةً بضع دقائق، وأحيانًا أطول. سيُرسل تلقائيًا بعد الموافقة.",
        remSendingSoon: "يُرسل قريبًا",
        remBeingSentTip: "يجري إرسال هذا التذكير الآن.",
        remDueOnceApproved: "موعده {when} · يُرسل بعد موافقة واتساب",
        remSendsWhenApproved: "يُرسل فور موافقة واتساب",
        remDue: "موعده {when}",
        remSendingNow: "جارٍ الإرسال الآن…",
        remProcessingTip: "يُرسل هذا التذكير الآن. البريد الإلكتروني يُرسل فورًا؛ وإن كان يُرسل أيضًا عبر واتساب، فهذا الجزء يُرسل فور موافقة Meta على الرسالة — عادةً خلال بضع دقائق.",
        remSendingMinute: "جارٍ الإرسال الآن — يستغرق ذلك عادةً دقيقة أو دقيقتين",
        remSends: "يُرسل في {when}",
        remPreviewTip: "معاينة رسالة التذكير",
        remEditTip: "تعديل الرسالة أو التوقيت أو المستلمين",
        remStatusTip: "حالة التسليم — من وصله هذا التذكير وردّه",
        remBuyCreditsTip: "شراء رصيد لإرسال هذا التذكير",
        remResumeCreditsTip: "استئناف هذا التذكير عند توفر الرصيد",
        remResumeTip: "استئناف هذا التذكير",
        remPauseTip: "إيقاف هذا التذكير مؤقتًا",
        remDeleteTip: "حذف هذا التذكير",
        msgNotFound: "لم يتم العثور على الرسالة",
        remFromMainMessage: "أضف التذكيرات من الرسالة الرئيسية، لا من تذكير آخر.",
        renamePrompt: "إعادة تسمية هذه الرسالة:",
        renameEmpty: "لا يمكن أن يكون اسم الرسالة فارغًا",
        renameFailed: "تعذّرت إعادة تسمية الرسالة: ",
        cancelConfirm: "هل تريد بالتأكيد إلغاء الرسالة \"{name}\"؟\n\nلا يمكن التراجع عن ذلك. الرسائل التي لم تُرسل بعد لن تُرسل.",
        cancelDone: "أُلغيت الرسالة",
        cancelFailed: "تعذّر إلغاء الرسالة: ",
        resumeConfirm: "استئناف الرسالة \"{name}\"؟\n\nستُرسل في {when}.",
        resumeConfirmNoDate: "استئناف الرسالة \"{name}\"؟\n\nستُرسل في الموعد المجدول أصلًا.",
        resumeDone: "استُؤنفت الرسالة",
        resumeFailed: "تعذّر استئناف الرسالة: ",
        pauseConfirm: "إيقاف الرسالة \"{name}\" مؤقتًا؟\n\nلن تُرسل في الموعد المجدول. يمكنك استئنافها أو إلغاؤها لاحقًا.",
        pauseDone: "أُوقفت الرسالة مؤقتًا",
        pauseFailed: "تعذّر إيقاف الرسالة مؤقتًا: ",
        pauseTooLate: "اكتمل إرسال هذه الرسالة قبل أن يتسنى إيقافها مؤقتًا.",
        autoRetryPauseConfirm: "إيقاف إعادة المحاولة التلقائية لـ \"{name}\" مؤقتًا؟",
        autoRetryResumeConfirm: "استئناف إعادة المحاولة التلقائية لـ \"{name}\"؟",
        autoRetryNotRetriedOne: "لن تُعاد المحاولة تلقائيًا لجهة اتصال واحدة.",
        autoRetryNotRetriedMany: "لن تُعاد المحاولة تلقائيًا لـ {n} من جهات الاتصال.",
        autoRetryRetriedOne: "ستُعاد المحاولة لجهة اتصال واحدة في المحاولة المجدولة التالية.",
        autoRetryRetriedMany: "ستُعاد المحاولة لـ {n} من جهات الاتصال في محاولتهم المجدولة التالية.",
        autoRetryPaused: "أُوقفت إعادة المحاولة التلقائية مؤقتًا",
        autoRetryResumed: "استُؤنفت إعادة المحاولة التلقائية",
        autoRetryPauseFailed: "تعذّر إيقاف إعادة المحاولة التلقائية مؤقتًا: ",
        autoRetryResumeFailed: "تعذّر استئناف إعادة المحاولة التلقائية: ",
        sendNowConfirm: "إرسال الرسالة \"{name}\" الآن؟\n\nفات الموعد المجدول. ستُرسل الرسالة فورًا.",
        sendNowDone: "أُرسلت الرسالة!",
        sendNowFailed: "تعذّر إرسال الرسالة: ",
        deleteDraftConfirm: "هل تريد بالتأكيد حذف المسودة \"{name}\"؟\n\nلا يمكن التراجع عن ذلك.",
        deleteDraftFailed: "تعذّر حذف المسودة: ",
        deleteConfirmActive: "حذف \"{name}\" نهائيًا؟\n\nلهذه الرسالة إحصاءات تسليم وقد تكون لها تذكيرات مجدولة. سيُحذف كلاهما.\n\nتبقى ردود الضيوف محفوظة (فهي محفوظة بشكل منفصل).\n\nلا يمكن التراجع عن ذلك.",
        deleteAlsoGuests: "أضافت هذه الرسالة {n} ضيوف إلى قائمتك لم يردّوا وليسوا في أي رسالة أخرى.\n\nهل تريد إزالتهم من قائمة الضيوف أيضًا؟\n\nموافق يزيلهم. إلغاء يبقيهم.",
        deleteConfirmUnsent: "حذف \"{name}\" نهائيًا؟\n\nلم يُرسل أي شيء بهذه الرسالة، لذا يمكن حذفها بأمان.\n\nلا يمكن التراجع عن ذلك.",
        deleteFailed: "تعذّر حذف الرسالة: ",
        statusAllTitle: "جميع جهات الاتصال",
        statusAllSubtitle: "من جميع الرسائل",
        statusAllMessages: "📊 جميع الرسائل",
        statusAllTags: "كل الوسوم", statusNoTag: "بلا وسم", statusTagsLine: "الوسوم",
        statusTitleFallback: "حالة الرسالة",
        statusThisMessageOnly: "تُعرض جهات اتصال هذه الرسالة فقط",
        statusAddedAfterLaunch: "أُضيف بعد الإرسال: {n}",
        statusAddedAfterLaunchOne: "أُضيف واحد بعد الإرسال",
        statusContactCount: "جهات الاتصال: {n}",
        statusContactCountOne: "جهة اتصال واحدة",
        exportNone: "لا توجد جهات اتصال لتصديرها",
        exportedOne: "صُدّرت جهة اتصال واحدة",
        exportedMany: "صُدّرت جهات الاتصال: {n}",
        dlvSent: "أُرسلت",
        dlvDelivered: "سُلّمت",
        dlvRead: "قُرئت",
        dlvOpened: "فُتحت",
        dlvClicked: "نُقر الرابط",
        dlvClickedTip: "تم فتح رابط تأكيد الحضور (أو موقعك) من إحدى رسائلك. بعد الرد، هذه أوضح علامة على أن الدعوة وصلته.",
        dlvReadTip: "إشعار القراءة من واتساب نفسه (علامتا صح زرقاوان)، أي أن الرسالة قُرئت.",
        dlvOpenedTip: "تم تحميل صور البريد الإلكتروني، لذا من المرجح أن الضيف رآه. يحمّل Apple Mail وبعض التطبيقات الأخرى الصور تلقائيًا عند وصول الرسالة، لذلك هذه علامة جيدة وليست دليلًا.",
        dlvDeliveredEmailTip: "قَبِل مزوّد البريد لدى الضيف الرسالة. لا يمكننا رؤية المجلد الذي وصلت إليه، وكثير من التطبيقات (Outlook وHotmail وبريد العمل) لا تُبلغ أبدًا عن الفتح، لذا ربما قُرئت بالفعل.",
        dlvDeliveredWaTip: "وصلت الرسالة إلى الهاتف (علامتا صح في واتساب). لا يوجد إشعار قراءة بعد، لكن بعض الضيوف يوقفون إشعارات القراءة، لذا ربما قُرئت بالفعل.",
        dlvDeliveredSmsTip: "وصلت الرسالة إلى الهاتف كرسالة SMS. رسائل SMS لا تُبلغ أبدًا إن كانت قد قُرئت.",
        dlvSentTip: "تم تسليم الرسالة إلى الشبكة. لا يوجد تأكيد بعد بأنها وصلت.",
        dlvBounced: "ارتدّت",
        dlvFailed: "فشلت",
        dlvSmsSent: "أُرسلت SMS",
        dlvSmsFailed: "فشلت SMS",
        detailNoEmailFallback: "لا يوجد بريد إلكتروني محفوظ، لذا تعذّر الإرسال بالبريد الإلكتروني بدلاً من ذلك. أضف واحدًا بالقلم لإرساله.",
        deliveryFailed: "فشل التسليم",
        detailEmail: "البريد الإلكتروني",
        detailSmsFallback: "SMS بديلة",
        detailRsvp: "الرد",
        noDetail: "لا توجد تفاصيل متاحة",
        fallbackEmail: "أُرسلت بالبريد",
        fallbackSms: "أُرسلت عبر SMS",
        aMessageFailed: "فشلت رسالة",
        aMessageFailedTip: "تعذّر تسليم إحدى الرسائل إلى جهة الاتصال هذه، لكن رسالة أخرى وصلت إليها.",
        checkNumber: "تحقّق من هذا الرقم",
        checkNumberTip: "تعذّر على واتساب التسليم إلى هذا الرقم كما هو محفوظ لدينا، ويبدو الرقم غير مكتمل. من الأفضل تصحيحه وإعادة الإرسال قبل افتراض أن الضيف ليس على واتساب.",
        rowDetailsTip: "انقر لرؤية كل تفاصيل التسليم",
        updateResponseTip: "تحديث الرد",
        linkColumn: "الرابط",
        remCellOnAria: "التذكيرات مفعّلة — إيقاف مؤقت",
        remCellOffAria: "التذكيرات متوقفة — استئناف",
        guestFallback: "ضيف",
        addName: "إضافة اسم",
        editNameTip: "انقر لتعديل الاسم",
        nameEditPlaceholder: "اسم الضيف",
        nameEditSave: "حفظ",
        nameEditCancel: "إلغاء",
        nameSaveFailed: "تعذّر حفظ الاسم",
        raLoadingAll: "جارٍ تحميل جميع جهات الاتصال...",
        raLoadingPending: "جارٍ تحميل من لم يردّوا...",
        raLoadingYes: "جارٍ تحميل من ردّوا بنعم...",
        raLoadingMaybe: "جارٍ تحميل من ردّوا بربما...",
        raLoadingNo: "جارٍ تحميل من ردّوا بلا...",
        raExcludedAll: "جميع جهات الاتصال مستبعدة من التذكيرات",
        raExcludedPending: "جميع من لم يردّوا هنا مستبعدون من التذكيرات",
        raExcludedYes: "جميع من ردّوا بنعم هنا مستبعدون من التذكيرات",
        raExcludedMaybe: "جميع من ردّوا بربما هنا مستبعدون من التذكيرات",
        raExcludedNo: "جميع من ردّوا بلا هنا مستبعدون من التذكيرات",
        raSkippedExcludedOne: "تم تخطي ضيف واحد استبعدته من التذكيرات",
        raSkippedExcludedMany: "تم تخطي {n} من الضيوف استبعدتهم من التذكيرات",
        raLoadFailed: "تعذّر تحميل جهات الاتصال",
        raNoneSelected: "لم يتم اختيار مستلمين — أُلغي.",
        raNoValidRecipients: "لا يوجد مستلمون صالحون للإرسال إليهم.",
        errLabel_131026: "تعذّر التسليم عبر واتساب",
        errLabel_131049: "حظرته سياسة Meta",
        errLabel_131047: "انتهت نافذة 24 ساعة",
        errLabel_131048: "حُظر كرسائل مزعجة",
        errLabel_131056: "تجاوز حد الإرسال",
        errLabel_131058: "مشكلة في الرسالة",
        errLabel_131061: "أوقفت Meta الرسالة",
        errLabel_131062: "عطّلت Meta الرسالة",
        errLabel_132001: "مشكلة في الرسالة",
        errLabel_130472: "احتجزه اختبار من Meta",
        errLabel_130497: "واتساب محظور في هذا البلد",
        errLabel_131063: "ليس على واتساب",
        errLabel_130429: "بلغنا حد الإرسال",
        errLabel_marketing_limit_reached: "حد التسويق لدى Meta",
        errLabel_BREVO_REJECTED: "رفضه مزوّد SMS",
        errLabel_unknown: "فشل",
        errTip_131049: "حظرت Meta رسالة التسويق هذه (حد السياسة أو التفاعل) — وهذا ليس إلغاء اشتراك من الضيف. فهو يستخدم واتساب ويمكن الوصول إليه؛ وإعادة الإرسال بعد نحو يوم تنجح في قرابة نصف الحالات.",
        errTip_131026: "تعذّر على هذا الرقم استلام الرسالة — ربما لا يستخدم الضيف واتساب، أو في الأرقام خطأ. نادرًا ما تفيد إعادة الإرسال (تصل نحو 1 من كل 20)، لذا من الأضمن الوصول إليه بطريقة أخرى.",
        errTip_131048: "رقم الهاتف مقيّد",
        errTip_131056: "تم تجاوز حد الإرسال",
        errTip_131058: "الرسالة غير معتمدة",
        errTip_131047: "انتهت نافذة الـ24 ساعة",
        errTip_131061: "أوقفت Meta رسالة واتساب المستخدمة هنا مؤقتًا، لذا تعذّر تسليمها. يؤثر ذلك على جميع ضيوف هذه الرسالة لا على هذا الضيف وحده — يجب إنشاء الرسالة من جديد.",
        errTip_131062: "الرسالة معطّلة",
        errTip_132001: "رفض واتساب الرسالة (لم تكن موجودة باللغة التي أُرسلت بها)، لذا لم يُسلَّم شيء. أنشئ رسالة التذكير من جديد وأعد إرسالها.",
        errTip_marketing_limit_reached: "أوقفت Meta تسليم رسائل التسويق إلى هذا الرقم مؤقتًا — وهذا ليس إلغاء اشتراك من الضيف",
        errTip_130472: "احتجزت Meta هذه الرسالة ضمن إحدى تجارب التسليم الخاصة بها. لا مشكلة في الرقم، لكن إعادة الإرسال ستواجه الاحتجاز نفسه — تواصل مع الضيف بطريقة أخرى.",
        errTip_130497: "لا تسمح Meta حاليًا لحساب واتساب الخاص بنا بمراسلة الأرقام في هذا البلد، لذا ستفشل إعادة الإرسال بالطريقة نفسها. تواصل مع الضيف بالبريد الإلكتروني أو برابطه الشخصي.",
        errTip_131063: "هذا الرقم غير مسجّل على واتساب، لذا لا يمكنه استلام الدعوة. جرّب رقمًا آخر أو تواصل مع الضيف بطريقة أخرى.",
        errTip_130429: "بلغنا حد الإرسال الخاص بنا مؤقتًا — لا مشكلة في هذا الرقم. يُفترض أن تنجح إعادة الإرسال بعد قليل.",
        errTip_BREVO_REJECTED: "رفض مزوّد SMS هذه الرسالة بعد قبولها (عادةً بسبب مشكلة مؤقتة في رصيد الإرسال لدينا). تستحق المحاولة مرة أخرى.",
        errTip_NOT_FOUND: "جهة الاتصال غير موجودة أو غير صالحة",
        errTipUnknown: "خطأ غير معروف",
        // Card status lines, failure reasons and WhatsApp refusals (item M2, 2026-10-07).
        chEmail: "البريد الإلكتروني",
        chText: "الرسائل النصية",
        chWhatsApp: "واتساب",
        stSentN: "أُرسلت {n}",
        stAtSendTime: "تُرسل في الموعد المحدد",
        stGoingOut: "تُرسل خلال الدقائق القليلة القادمة",
        stWaWaiting: "بانتظار موافقة واتساب على الرسالة (عادةً بضع دقائق)",
        stWaWaitingLong: "ما زالت بانتظار موافقة واتساب على الرسالة. يستغرق الأمر هذه المرة وقتًا أطول من المعتاد؛ وستُرسل تلقائيًا فور الموافقة عليها",
        stWaSubmitting: "ما زلنا نرسلها للموافقة عليها",
        stRetrying: "لم يُرسل شيء بعد. سنحاول مرة أخرى خلال بضع دقائق.",
        stSendingSoon: "تُرسل خلال الدقائق القليلة القادمة.",
        stWaRefused: "لم تُقبل. عدّلها وأعد إرسالها.",
        stWaRefusedBelow: "لم تُقبل. عدّلها وأعد إرسالها (السبب موضّح أدناه).",
        failFormMissing: "نموذج هذه الرسالة لم يعد موجودًا. احفظ النموذج مرة أخرى، ثم أنشئ رسالة جديدة.",
        failWaOnly: "لم يقبل واتساب الرسالة، ولا يمكن الوصول إلى جميع ضيوفها إلا عبر واتساب، لذلك لم يُرسل شيء. عدّلها وأعد إرسالها.",
        failWaRefused: "لم يقبل واتساب الرسالة، لذلك لم يُرسل شيء. عدّلها وأعد إرسالها.",
        failNothingSent: "تعذّر إرسال أي شيء بعد عدة محاولات. اضغط «تعديل وإعادة الإرسال» للمحاولة مرة أخرى. وإذا تكرر ذلك، راسلنا عبر الدردشة.",
        failNoMethod: "لم تصل بيانات ضيوفك إلى مرحلة الإرسال لدينا. كانت المشكلة من جهتنا، وليست في قائمة ضيوفك. اضغط «تعديل وإعادة الإرسال» للمحاولة مرة أخرى، وراسلنا عبر الدردشة إذا تكرر ذلك.",
        failSendError: "حدث خطأ من جهتنا أثناء الإرسال، ولم يكن بسبب أي شيء فعلته. اضغط «تعديل وإعادة الإرسال» للمحاولة مرة أخرى، وراسلنا عبر الدردشة إذا تكرر ذلك.",
        failGeneric: "تعذّر علينا إرسال هذه الرسالة. اضغط «تعديل وإعادة الإرسال» للمحاولة مرة أخرى، أو راسلنا عبر الدردشة.",
        err131053Label: "تعذّر تحميل الصورة",
        err131053Tip: "لم يتمكن واتساب من جلب صورة الدعوة، لذلك لم تُسلَّم الرسالة. إعادة الإرسال تنجح عادةً.",
        errSmsBouncedLabel: "لم تُسلَّم الرسالة النصية",
        errSmsBouncedTip: "لم تسلّم شبكة الهاتف الرسالة النصية. قد يكون الرقم خاطئًا أو أرضيًا أو مغلقًا. تحقق من الرقم، أو راسل الضيف عبر البريد الإلكتروني، أو انسخ رابطه الشخصي وأرسله بنفسك.",
        errSmsFailedLabel: "لم تُرسل الرسالة النصية",
        errSmsFailedTip: "لم يرسل مزوّد الرسائل النصية لدينا هذه الرسالة. إعادة الإرسال تنجح عادةً.",
        errSmsNonGsmLabel: "لا يمكن إرسالها كرسالة نصية",
        errSmsNonGsmTip: "لا تحتوي هذه الرسالة على نص بالإنجليزية، والرسائل النصية لا تحمل إلا هذا النص. أضف نسخة بالإنجليزية عبر «تعديل وإعادة الإرسال»، أو تواصل مع هذا الضيف عبر البريد الإلكتروني.",
        errNonUsLabel: "الرسائل النصية لأرقام أمريكية فقط",
        errNonUsTip: "نرسل الرسائل النصية إلى الأرقام الأمريكية فقط. تواصل مع هذا الضيف عبر واتساب أو البريد الإلكتروني.",
        errEmailHardLabel: "عنوان البريد لا يعمل",
        errEmailHardTip: "عنوان البريد الإلكتروني هذا غير موجود أو يرفض الرسائل. تحقق من كتابته، أو تواصل مع الضيف عبر رسالة نصية أو واتساب.",
        errEmailSoftLabel: "لم يُقبل البريد حاليًا",
        errEmailSoftTip: "لم يقبل صندوق بريد الضيف الرسالة هذه المرة (غالبًا لأنه ممتلئ أو غير متاح مؤقتًا). إعادة الإرسال لاحقًا تنجح عادةً.",
        errEmailBounceLabel: "ارتدّ البريد",
        errEmailBounceTip: "تعذّر تسليم البريد الإلكتروني إلى هذا العنوان. تحقق من كتابته، أو تواصل مع الضيف عبر رسالة نصية أو واتساب.",
        errGenericTip: "تعذّر تسليم هذه الرسالة. تواصل مع هذا الضيف بطريقة أخرى، أو أعد الإرسال لاحقًا.",
        fbRetryUntilAll: "يحجب واتساب الرسائل التسويقية عنهم حتى {time}. أعد المحاولة بعد ذلك.",
        fbRetryUntilSome: "يحجب واتساب الرسائل التسويقية عن بعضهم حتى {time}. أعد المحاولة بعد ذلك.",
        fbChannelSub: "لا تصل هذه الرسالة إليهم بالطريقة التي أُرسلت بها. ذكّرهم عبر البريد الإلكتروني، أو انسخ الرابط الشخصي لكل ضيف من القائمة أدناه.",
        fbChannelNoEmail: "لا يملك أي من هؤلاء الضيوف بريدًا إلكترونيًا. انسخ الرابط الشخصي لكل منهم من القائمة أدناه وأرسله بنفسك.",
        fbChannelSomeNoEmail: "{n} من هؤلاء الضيوف ليس لديهم بريد إلكتروني، لذلك لن تصلهم الرسالة. تُغلق هذه القائمة عند فتح البريد الإلكتروني، فانسخ روابطهم الشخصية أولًا. هل تريد المتابعة مع الضيوف الذين لديهم بريد إلكتروني وعددهم {m}؟",
        ndTipWa: "لم يسلّم واتساب الرسالة إلى {n} من ضيوفك. أحيانًا تحجب ميتا الرسائل للحماية من الرسائل المزعجة، أو أن الرقم ليس على واتساب. افتح القائمة لمعرفة السبب، وتواصل معهم عبر البريد الإلكتروني أو رسالة نصية.",
        ndTipSms: "لم تُسلَّم {n} من رسائلك النصية، عادةً بسبب رقم خاطئ أو أرضي. افتح القائمة لمعرفة السبب، ثم تحقق من الرقم أو راسلهم عبر البريد الإلكتروني أو انسخ روابطهم الشخصية.",
        ndTipEmail: "ارتدّت {n} من رسائل بريدك الإلكتروني، عادةً بسبب خطأ في كتابة العنوان. افتح القائمة لمعرفة السبب، وتواصل معهم عبر رسالة نصية أو واتساب.",
        ndTipMixed: "لم تُسلَّم: {parts}. افتح القائمة لمعرفة السبب لكل ضيف.",
        ndTipGeneric: "لم يتلقَّ {n} من ضيوفك هذه الرسالة. افتح القائمة لمعرفة السبب.",
        ndTipWaOne: "لم يسلّم واتساب الرسالة إلى أحد ضيوفك. أحيانًا تحجب ميتا الرسائل للحماية من الرسائل المزعجة، أو أن الرقم ليس على واتساب. افتح القائمة لمعرفة السبب، وتواصل معه عبر البريد الإلكتروني أو رسالة نصية.",
        ndTipSmsOne: "لم تُسلَّم إحدى رسائلك النصية، عادةً بسبب رقم خاطئ أو أرضي. افتح القائمة لمعرفة السبب، ثم تحقق من الرقم أو راسله عبر البريد الإلكتروني أو انسخ رابطه الشخصي.",
        ndTipEmailOne: "ارتدّت إحدى رسائل بريدك الإلكتروني، عادةً بسبب خطأ في كتابة العنوان. افتح القائمة لمعرفة السبب، وتواصل مع الضيف عبر رسالة نصية أو واتساب.",
        ndTipGenericOne: "لم يتلقَّ أحد ضيوفك هذه الرسالة. افتح القائمة لمعرفة السبب.",
        tplErr_ours: "حدث خطأ من جهتنا، ولم يكن بسبب أي شيء فعلته. افتح «تعديل وإعادة الإرسال» وأرسلها مرة أخرى من هناك (لا حاجة لتغيير أي شيء). وإذا تكرر ذلك، أخبرنا وسنصلحه.",
        tplErr_trailingParam: "لا يمكن أن تنتهي رسالتك بوسم تخصيص مثل {{name}} أو {{table_number}}، فواتساب لا يسمح بذلك. أضف كلمة أو نقطة بعد الوسم وأعد الإرسال.",
        tplErr_waLink: "لا يسمح واتساب بزر يعيد إلى واتساب. اجعل الزر يشير إلى مكان آخر وأعد الإرسال.",
        tplErr_imageFormat: "يجب أن تكون صورة دعوتك بصيغة JPG أو PNG لواتساب. ارفعها مجددًا بهذه الصيغة وأعد الإرسال.",
        tplErr_imageProcess: "لم يتمكن واتساب من معالجة صورة دعوتك. جرّب صورة أخرى (JPG أو PNG، أقل من 5 ميغابايت) وأعد الإرسال.",
        tplErr_category: "يصنّف واتساب رسالتك كنوع مختلف عن النوع الذي سجلناها تحته. أعد إرسالها ويُفترض أن تُقبل.",
        tplErr_layout: "لم يتمكن واتساب من قراءة تنسيق رسالتك. غالبًا يكون السبب {{وسمًا}} وحده في سطر مستقل. ضعه داخل جملة وأعد الإرسال.",
        tplErr_duplicateName: "لدى واتساب رسالة محفوظة بهذا الاسم بالفعل. غيّر اسم الرسالة وأعد الإرسال.",
        tplErr_rateLimit: "بلغت حاليًا حد واتساب للرسائل الجديدة. انتظر قليلًا وأعد الإرسال، فلن يضيع شيء.",
        tplErr_noReason: "لم يوافق واتساب على صياغة هذه الرسالة. عدّلها وأعد تقديمها.",
        tplLangsHeadOne: "رفض واتساب النسخة {langs} من رسالتك.",
        tplLangsHeadMany: "رفض واتساب هذه النسخ من رسالتك: {langs}.",
        tplLangsHeadUnknown: "رفض واتساب إحدى لغات رسالتك.",
        tplLangsSaid: "قال واتساب: «{text}».",
        tplLangsEditOne: "عدّل هذه الترجمة وأعد تقديمها.",
        tplLangsEditMany: "عدّل هذه الترجمات وأعد تقديمها.",
        langCombined: "ثنائية اللغة",
        failWaRefusedPartial: "لم يقبل واتساب الرسالة، لذلك لم يتلقّها ضيوف واتساب. ضيوفك عبر البريد الإلكتروني والرسائل النصية تلقّوها بالفعل، فلا ترسلها إليهم مرة أخرى. عدّل النص وأعد إرسالها إلى ضيوف واتساب.",
        fbPermanentSubEmail: "الرقم أو عنوان البريد الإلكتروني لا يعمل. صحّحه في قائمة الضيوف، أو انسخ الرابط الشخصي للضيف وأرسله بنفسك.",
        fbHeldBack: "{n} لم تُرسل عن قصد",
        fbHeldBackSub: "أوقفنا هذه الرسائل: طلب الضيف التوقف، أو ألغى اشتراكه، أو ليس على WhatsApp، أو أوقف WhatsApp الرقم مؤقتًا. يوضح كل صف أدناه السبب. لا نرسل أبدًا رسائل مرة أخرى إلى من طلب منا التوقف.",
        tplLangsHeadCombined: "رفض واتساب النسخة المدمجة ثنائية اللغة من رسالتك.",
        remFailFormMissing: "نموذج هذا التذكير لم يعد موجودًا. احفظ النموذج مرة أخرى، ثم عدّل التذكير بالقلم واحفظه.",
        remFailWaOnly: "لم يقبل واتساب التذكير، ولا يمكن الوصول إلى جميع ضيوفه إلا عبر واتساب، لذلك لم يُرسل شيء. عدّل التذكير بالقلم واحفظه لإعادة تقديمه.",
        remFailWaRefused: "لم يقبل واتساب التذكير، لذلك لم يُرسل شيء. عدّل التذكير بالقلم واحفظه لإعادة تقديمه.",
        remFailWaRefusedPartial: "لم يقبل واتساب التذكير، لذلك لم يتلقّه ضيوف واتساب. ضيوفك عبر البريد الإلكتروني والرسائل النصية تلقّوه بالفعل. عدّل التذكير بالقلم واحفظه لإعادة تقديمه.",
        remFailNothingSent: "تعذّر إرسال أي شيء بعد عدة محاولات. عدّل التذكير بالقلم واحفظه للمحاولة مرة أخرى. وإذا تكرر ذلك، راسلنا عبر الدردشة.",
        remFailNoMethod: "لم تصل بيانات ضيوفك إلى مرحلة الإرسال لدينا. كانت المشكلة من جهتنا، وليست في قائمة ضيوفك. عدّل التذكير بالقلم واحفظه للمحاولة مرة أخرى، وراسلنا عبر الدردشة إذا تكرر ذلك.",
        remFailSendError: "حدث خطأ من جهتنا أثناء الإرسال، ولم يكن بسبب أي شيء فعلته. عدّل التذكير بالقلم واحفظه للمحاولة مرة أخرى، وراسلنا عبر الدردشة إذا تكرر ذلك.",
        remFailGeneric: "تعذّر علينا إرسال هذا التذكير. عدّله بالقلم واحفظه للمحاولة مرة أخرى، أو راسلنا عبر الدردشة.",
        // Provider outage pause: see _providerPauseBadgeHtml.
        providerPaused: "توقف الإرسال",
        providerPausedTip: "أوقفت مشكلة لدى مزوّد الرسائل لدينا هذه الرسالة قبل أن تصل إلى الجميع. لم يُخصم من رصيدك أي شيء مقابل الضيوف الذين لم يستلموها بعد. تم تنبيه فريقنا، وستُرسَل إليهم فور حل المشكلة. لا تحتاج إلى فعل أي شيء.",
        providerPausedPhrase: "بانتظار مزوّد الرسائل لدينا",
        providerResumeConfirm: "هل تريد محاولة إرسال هذه الرسالة مرة أخرى الآن؟ سيتلقاها فقط الضيوف الذين لم يستلموها بعد.",
        providerResumed: "جارٍ إعادة التشغيل. سيتلقاها الضيوف الذين لم يستلموها بعد خلال الدقائق القادمة.",
        providerResumeFailed: "تعذّر استئناف هذه الرسالة. يُرجى المحاولة مرة أخرى.",
        providerPausedPhraseAuto: "سنعيد المحاولة تلقائيًا",
        providerPausedTipAuto: "أوقفت مشكلة مؤقتة لدى مزوّد الرسائل لدينا هذه الرسالة قبل أن تصل إلى الجميع. لم يُخصم من رصيدك أي شيء مقابل الضيوف الذين لم يستلموها بعد. سنعيد المحاولة تلقائيًا بعد قليل، ولن يتلقاها سوى هؤلاء الضيوف. يمكنك أيضًا المحاولة الآن عبر «{resume}».",
        providerResuming: "جارٍ إعادة التشغيل. يستغرق ذلك عادةً بضع دقائق.",
        providerResumedTeam: "سنعيد المحاولة الآن. إذا كان مزوّدنا لا يزال لا يعمل، فسيتوقف الإرسال مجددًا ويُستكمل فور أن يحل فريقنا المشكلة. لا تحتاج إلى فعل أي شيء آخر.",
        sendRestartResumes: "أُعيد تشغيل الخادم أثناء إرسال هذه الرسالة. لن يتلقاها مرتين من وصلته بالفعل، وسيتلقاها الباقون تلقائيًا خلال بضع دقائق.",
        sendRestartRetry: "أُعيد تشغيل الخادم أثناء إرسال هذه الرسالة. لن يتلقاها مرتين من وصلته بالفعل. أعد إرسالها بعد دقيقة للوصول إلى الباقين.",
        // Outage-held guests: see _outageHeldNoteHtml.
        heldPhrase: "ربما وصلت الرسالة إلى {n} من الضيوف بالفعل",
        heldPhraseOne: "ربما وصلت الرسالة إلى ضيف واحد بالفعل",
        heldWhy: "أثناء تعطّل مزوّد الرسائل لدينا، لم تتلقَّ رسالة واتساب أو SMS الخاصة بهم أي رد، لذا ربما وصلت رغم ذلك. وحتى لا يتلقاها أحد مرتين، لم نُعِد إرسالها إليهم. أما الباقون فيتلقونها كالمعتاد.",
        heldMore: "و{n} آخرون",
        heldSend: "أرسلها إلى هؤلاء الـ{n} أيضًا",
        heldSendOne: "أرسلها إلى هذا الضيف أيضًا",
        heldSendDone: "تم. أرسلناها إليهم.",
        heldSendWithResume: "تم. سيتلقونها مع الجميع عند استئناف الإرسال.",
        heldSendBusy: "يجري إرسال هذه الرسالة الآن. حاول مرة أخرى عند انتهائه.",
        heldSendPaused: "استأنف هذه الرسالة أولًا، ثم أرسلها إليهم.",
        heldSendFailed: "تعذّر إرسالها إليهم الآن. يُرجى المحاولة مرة أخرى بعد بضع دقائق.",
        // Interrupted Add Contacts (PMb).
        errInterruptedLabel: "لم تُرسل (انقطع الإرسال)",
        errInterruptedTip: "توقف الإرسال قبل الوصول إلى هذا الضيف لأن خادمنا أُعيد تشغيله. لم يُرسل إليه شيء ولم يُخصم منك أي رصيد، لذا يمكنك إعادة الإرسال بأمان، ويُحتسب الإرسال من جديد مثل أي إرسال.",
        acNotSent: "لم تُرسل إلى {n}: انقطع الإرسال. أعد الإرسال إليهم من الحالة",
        addRestartRetry: "كان خادمنا يُعاد تشغيله، لذا لم تتم إضافة أو إرسال أي شيء. يُرجى المحاولة مرة أخرى بعد دقيقة.",
        // Retry, resend explainer, ghost-template recovery, follow-up, CSV export.
        retryWhichMessage: "لا يمكننا معرفة الرسالة التي فشلت فيها هذه. اختر رسالة من أعلى هذه النافذة وحاول مرة أخرى.",
        retryAMessage: "رسالة",
        retryScopeOne: "سنعيد إرسال «{n}» إليهم.",
        retryScopeMany: "سنعيد إرسال كل رسالة من الرسائل الـ {n} التي فشلت، إلى هؤلاء الضيوف فقط.",
        retryConfirmOne: "إرسال الرسالة نفسها مرة أخرى إلى ضيف واحد؟",
        retryConfirmMany: "إرسال الرسالة نفسها مرة أخرى إلى {n} من الضيوف؟",
        retryConfirmNobody: "لن نتواصل مجددًا مع أي شخص استلمها بالفعل.",
        retryConfirmFree: "إعادة الإرسال مجانية: أُعيد بالفعل رصيد كل رسالة فشلت.",
        retryResending: "جارٍ إعادة الإرسال…",
        nothingTryLater: "حاول مرة أخرى بعد بضع ساعات.",
        nothingTryAfter: "حاول مرة أخرى بعد {n}.",
        nothingCooledOne: "رفض واتساب هذه الرسالة لضيف واحد خلال آخر 24 ساعة، لذلك تم تخطيه بدلًا من تكرار المحاولة.",
        nothingCooledMany: "رفض واتساب هذه الرسالة لـ {n} من الضيوف خلال آخر 24 ساعة، لذلك تم تخطيهم بدلًا من تكرار المحاولة.",
        nothingDeadOne: "ضيف واحد ليس على واتساب — لن تصل إليه إعادة الإرسال. جرّب البريد الإلكتروني أو الرسائل النصية.",
        nothingDeadMany: "{n} من الضيوف ليسوا على واتساب — لن تصل إليهم إعادة الإرسال. جرّب البريد الإلكتروني أو الرسائل النصية.",
        nothingReachedOne: "ضيف واحد استلم هذه الرسالة بالفعل بطريقة أخرى، لذا لم تُرسل مرة أخرى.",
        nothingReachedMany: "{n} من الضيوف استلموا هذه الرسالة بالفعل بطريقة أخرى، لذا لم تُرسل مرة أخرى.",
        nothingNobody: "تعذّر الوصول إلى أي أحد هذه المرة. تستحق المحاولة مرة أخرى لاحقًا.",
        ghostConfirm: "إعادة بناء قالب واتساب لهذه الرسالة وإعادة إرسالها إلى الضيوف الذين لم تصلهم؟\n\nقد يستغرق ذلك بضع دقائق. لن يُخصم أي رصيد إضافي.",
        ghostStarting: "جارٍ البدء…",
        ghostRecovering: "جارٍ الاستعادة…",
        ghostButton: "إعادة الإنشاء والإرسال",
        ghostStarted: "جارٍ إعادة بناء قالب واتساب وإعادة الإرسال — يستغرق ذلك بضع دقائق. سنراسلك بالبريد الإلكتروني عند الانتهاء.",
        ghostAlready: "هناك عملية استعادة جارية بالفعل.",
        ghostNotNeeded: "لم تعد هذه الرسالة تُظهر مشكلة قالب واتساب، فلا يوجد ما يلزم إعادة بنائه.",
        ghostStartFailed: "تعذّر بدء الاستعادة. يُرجى المحاولة مرة أخرى بعد بضع دقائق.",
        ghostFixed: "تم إصلاح قالب واتساب.",
        ghostResent: "أُعيد الإرسال.",
        ghostResentToOne: "أُعيد الإرسال إلى ضيف واحد.",
        ghostResentTo: "أُعيد الإرسال إلى {n} من الضيوف.",
        ghostEscalated: "لا يزال القالب المُعاد بناؤه لا يجتاز فحص واتساب. تم إبلاغ فريقنا. يُرجى التواصل مع الدعم.",
        ghostIssue: "مشكلة في قالب واتساب.",
        ghostIssueBody: "تعذّر تسليم {n} من الرسائل لأن واتساب رفض القالب. لن تفيد إعادة المحاولة العادية — أعد بناء القالب ثم أعد الإرسال.",
        followUpLoadedOne: "تم تحميل جهة اتصال واحدة للمتابعة",
        followUpLoaded: "تم تحميل {n} من جهات الاتصال للمتابعة",
        csvPhone: "الهاتف",
        csvEmail: "البريد الإلكتروني",
        csvDeliveryStatus: "حالة التسليم",
        csvDeliveryIssue: "مشكلة التسليم",
        csvRsvp: "الرد على الدعوة",
        csvGuests: "عدد الضيوف",
        csvPending: "قيد الانتظار",
        csvNotSent: "لم تُرسل",
        csvAttending: "سيحضر",
        csvDeclined: "اعتذر",
        csvMaybe: "ربما",
        csvResponded: "ردّ",
        csvNoResponse: "لا رد بعد",
    }
};

// Create global instance
// Guests a send could not try for a reason in their own details (server/utils/campaign-skips UNREACHABLE).
DashboardCampaigns.UNREACHABLE_REASONS = new Set(['no_contact_details', 'invalid_phone', 'no_email', 'no_phone', 'shared_number', 'no_channel', 'sms_country']);
window.dashboardCampaigns = new DashboardCampaigns();

// Helper function to get the current form ID from multiple sources
function getCurrentFormId() {
    // 1. Try form-select dropdown
    const formSelect = document.getElementById('form-select');
    let formId = formSelect?.value;

    if (formId && formId !== 'all') {
        return formId;
    }

    // 2. Try URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    formId = urlParams.get('formId');
    if (formId) {
        return formId;
    }

    // 3. Try global currentEventId (set by admin.js when loading single form)
    if (typeof currentEventId !== 'undefined' && currentEventId) {
        return currentEventId;
    }

    // 4. Try window.currentFormId (set by some components)
    if (window.currentFormId) {
        return window.currentFormId;
    }

    // 5. Try to get from analytics if available
    if (window.userAnalytics?.formId) {
        return window.userAnalytics.formId;
    }

    return null;
}

// Listen for tab activation and form changes
/**
 * Task #836: where a dashboard deep link can land. `tabs` are the tab
 * buttons (#<name>-tab); `open` names a button that is pressed once its tab
 * is showing - the same button the host would press, so every plan gate and
 * dialog behind it still applies. An unknown name just opens the tab.
 */
// ═══════════════════════════════════════════════════════════════════════════
// EMBEDDED IN THE WEBSITE BUILDER (task #865)
// The wedding website builder shows the Messaging wizard and the Personal
// links window over itself by loading this page in a transparent full-window
// frame: ?embed=site-builder&open=new-message|personal-links. Both windows
// need this page (the wizard writes to Firestore from here and uses dozens of
// its globals), so it is the dashboard that is embedded, not the windows.
// In this mode the page is invisible: until the window is up nothing but
// modals shows; once it is, everything on the page at that moment stays
// hidden, and whatever opens after it (other windows, toasts, the tag list)
// shows. The builder is told "ready" when the window is up and "close" when
// no window is left open (assets/js/wedding-builder.js, openInviteWindow).
// ═══════════════════════════════════════════════════════════════════════════
(function () {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (params.get('embed') !== 'site-builder' || window.parent === window) return;
    const post = (type) => {
        try { window.parent.postMessage({ source: 'quikrsvp-embed', type }, window.location.origin); } catch (e) { /* parent gone */ }
    };
    // Paying (credits, a plan) leaves for Stripe, which refuses to load in a
    // frame: wallet-manager.js / payment-manager.js send the whole tab instead.
    window.QR_EMBED_TOP = window.top;
    const root = document.documentElement;
    root.classList.add('qr-embed', 'qr-embed-wait');
    const style = document.createElement('style');
    style.textContent = `
        html.qr-embed, html.qr-embed body { background: transparent none !important; overflow: hidden !important; }
        html.qr-embed-wait body > *:not(.modal):not(.modal-backdrop) { visibility: hidden !important; }
        html.qr-embed [data-qr-embed-hidden], html.qr-embed #qr-help-widget-root,
        html.qr-embed #notification-opt-in, html.qr-embed #pwa-update-toast { visibility: hidden !important; }
        html.qr-embed .modal { visibility: visible !important; }`;
    (document.head || root).appendChild(style);

    const WINDOWS = ['campaignsCreateModal', 'personalLinksModal'];
    let ready = false;
    document.addEventListener('shown.bs.modal', (e) => {
        if (ready || !WINDOWS.includes(e.target.id)) return;
        ready = true;
        for (const el of document.body.children) {
            if (!el.matches('.modal, .modal-backdrop, .toast-notification')) el.setAttribute('data-qr-embed-hidden', '');
        }
        root.classList.remove('qr-embed-wait');
        post('ready');
    });
    // A window may close to open another (e.g. a confirmation); only when
    // none is open a moment later is the host done.
    document.addEventListener('hidden.bs.modal', () => {
        if (!ready) return;
        setTimeout(() => { if (!document.querySelector('.modal.show')) post('close'); }, 350);
    });
})();

const DASHBOARD_DEEP_LINKS = {
    tabs: ['responses', 'campaigns', 'seating', 'checkin', 'registry', 'album'],
    open: {
        'add-guest': { tab: 'responses', button: 'add-guest-btn' },
        'share-dashboard': { tab: 'responses', button: 'header-share-dashboard-btn' },
        'notifications': { tab: 'responses', button: 'header-response-notif-btn' },
        'new-message': { tab: 'campaigns', run: () => window.dashboardCampaigns?.openCreateCampaignModal?.() },
        'contacts': { tab: 'campaigns', button: 'campaignsAllContactsBtn' },
        // Contact Details already on Pending: every messaged guest who has
        // not answered, each row with its own WhatsApp icon for a hand-send
        // from the host's phone. The chat had sent a host asking for exactly
        // that to Remind pending (a bulk send) and to Personal links (new
        // links only) instead (chat 6582f145, 2026-10-27).
        'pending-guests': { tab: 'campaigns', run: () => window.dashboardCampaigns?.showContactStatusModal?.(null, 'pending') },
        'credits': { tab: 'campaigns', button: 'campaignsBuyCreditsBtn' },
        'add-table': { tab: 'seating', button: 'seatingAddTableBtn' },
        'auto-assign': { tab: 'seating', button: 'seatingAutoAssignBtn' },
        'master-qr': { tab: 'checkin', button: 'checkinMasterQrBtn' },
        'remind-pending': { tab: 'campaigns', run: () => window.dashboardCampaigns?.remindAllPending?.() },
        // The v2 dashboard's "Send a reminder" next step (event and home row):
        // the guests who have not answered, or the invitation wizard when
        // nobody has been messaged yet.
        'remind': { tab: 'campaigns', run: () => window.dashboardCampaigns?.remindPendingOrInvite?.() },
        // The wedding builder's "Personal link for each guest" (task #865): the
        // Personal links window for this event, as on the RSVP builder's Share tab.
        // The builder opens this (and new-message) in embed mode, below.
        'personal-links': { tab: 'campaigns', run: () => window.PersonalLinks?.open?.({ formId: getCurrentFormId() }) },
        // ...from the wedding website builder, the links open the website (task #874).
        'personal-links-site': { tab: 'campaigns', run: () => window.PersonalLinks?.open?.({ formId: getCurrentFormId(), target: 'website' }) }
    }
};

/**
 * Show a dashboard tab, then (optionally) press one of its buttons. Also what
 * the support chat calls when its link is for the dashboard already open, so
 * the host is not sent to a second copy of the page. Returns false if the tab
 * is not on this dashboard (e.g. no registry on this event).
 */
window.dashboardDeepLink = function (tab, open) {
    const action = open && Object.prototype.hasOwnProperty.call(DASHBOARD_DEEP_LINKS.open, open) ? DASHBOARD_DEEP_LINKS.open[open] : null;
    const name = (action && action.tab) || tab;
    const tabBtn = document.getElementById(name + '-tab');
    const navItem = tabBtn && (tabBtn.closest('li') || tabBtn);
    if (!tabBtn || (navItem && navItem.style.display === 'none')) return false;
    tabBtn.click();
    if (action) {
        // Which door a window came through, for Clarity: wizard_step says how
        // far a wizard got, not that a next-step line or a link opened it.
        if (typeof window.clarity === 'function') {
            try { window.clarity('event', 'deeplink_' + open.replace(/-/g, '_')); } catch (e) { /* analytics never breaks the page */ }
        }
        // The tab's content loads after it is shown; press the button once it
        // is visible and enabled, for up to ~8 seconds.
        let tries = 0;
        const press = () => {
            if (action.run) { action.run(); return; }
            const btn = document.getElementById(action.button);
            if (btn && btn.offsetParent !== null && !btn.disabled) { btn.click(); return; }
            if (tries++ < 40) setTimeout(press, 200);
        };
        setTimeout(press, action.run ? 600 : 300);
    }
    // Pressed once: a reload stays on the tab without reopening the dialog.
    try {
        const u = new URL(window.location.href);
        if (u.searchParams.has('open') || u.searchParams.has('wizard')) {
            u.searchParams.delete('open');
            u.searchParams.delete('wizard');
            history.replaceState(history.state, '', u.pathname + u.search + u.hash);
        }
    } catch (e) { /* leave the address as it is */ }
    return true;
};

document.addEventListener('DOMContentLoaded', () => {
    // Initialize when campaigns tab is shown
    document.getElementById('campaigns-tab')?.addEventListener('shown.bs.tab', () => {
        const formId = getCurrentFormId();
        if (formId) {
            window.dashboardCampaigns.init(formId);
        } else {
            window.dashboardCampaigns.showNoFormError();
        }
    });

    // Reinitialize when form selection changes
    document.getElementById('form-select')?.addEventListener('change', (e) => {
        // Only reinit if campaigns tab is active
        if (document.getElementById('campaigns-tab')?.classList.contains('active')) {
            window.dashboardCampaigns.init(e.target.value);
        }
    });

    // Deep links into the dashboard: ?tab=<tab>[&open=<action>] (task #836).
    // ?tab=campaigns&wizard=new was the only one; the support chat now links
    // hosts straight to any tab, and to the button they were asking about,
    // instead of describing the route. Names are in DASHBOARD_DEEP_LINKS
    // below - keep them in step with the bot's list in
    // server/knowledge/chatbot-knowledge-base.js ("DASHBOARD DEEP LINKS").
    const urlParams = new URLSearchParams(window.location.search);
    const wantOpen = urlParams.get('open') || (urlParams.get('wizard') === 'new' ? 'new-message' : '');
    const wantTab = urlParams.get('tab') || DASHBOARD_DEEP_LINKS.open[wantOpen]?.tab || null;
    // Every event page now carries ?tab=responses (dashboard.html head, for
    // Clarity). Responses is where the page opens anyway, so without an
    // ?open= there is nothing to do - and pressing the tab late would pull a
    // host who already moved to another tab back to Responses.
    const bareResponses = wantTab === 'responses' && !wantOpen;
    if (wantTab && !bareResponses && DASHBOARD_DEEP_LINKS.tabs.includes(wantTab)) {
        const formIdFromUrl = urlParams.get('formId');

        // Wait for dashboard to fully load
        const waitForDashboard = () => {
            const formSelect = document.getElementById('form-select');
            const tabBtn = document.getElementById(wantTab + '-tab');
            const dashboardContainer = document.getElementById('dashboard-container');

            // Check if dashboard is loaded (visible) and the tab exists
            const dashboardVisible = dashboardContainer && dashboardContainer.style.display !== 'none';
            const hasFormId = formIdFromUrl || (formSelect?.value && formSelect.value !== 'all');

            if (tabBtn && dashboardVisible && hasFormId) {
                // If formId is in URL and form selector exists with options, try to select it
                if (formIdFromUrl && formSelect && formSelect.options.length > 0) {
                    const optionExists = Array.from(formSelect.options).some(opt => opt.value === formIdFromUrl);
                    if (optionExists && formSelect.value !== formIdFromUrl) {
                        formSelect.value = formIdFromUrl;
                        formSelect.dispatchEvent(new Event('change'));
                    }
                }
                // Small delay to let form selection complete, then switch tab
                // and press the asked-for button once it is on screen.
                setTimeout(() => window.dashboardDeepLink(wantTab, wantOpen), 100);
            } else {
                // Dashboard not ready yet, try again
                setTimeout(waitForDashboard, 200);
            }
        };

        // Start waiting after a short initial delay
        setTimeout(waitForDashboard, 300);
    }
});

// ── Instant hint tooltips (task #528) ────────────────────────────────────────
// Same snippet as bulk-sharing-wizard.js (see the note there): upgrade any
// .js-hint-tip icon to a real Bootstrap tooltip on first hover/focus/tap, so
// header hints appear instantly instead of relying on the native title delay.
// Globally guarded — whichever file loads first installs the listeners.
(function () {
    if (window.__qrHintTipDelegation) return;
    window.__qrHintTipDelegation = true;
    const upgrade = (target) => {
        const el = target && target.closest && target.closest('.js-hint-tip');
        if (!el || el.__hintTip || !(window.bootstrap && window.bootstrap.Tooltip)) return;
        el.__hintTip = true;
        if (!el.getAttribute('title')) return; // nothing to show
        // customClass + the CSS below, rather than html:true — a hint written
        // over several lines (the Party-members format guide, task #581) has to
        // KEEP those lines, and turning HTML on for every hint would open the
        // door for any title carrying host-entered text. `white-space: pre-line`
        // renders the newlines while the text stays escaped.
        if (!document.getElementById('qrHintTipStyles')) {
            const st = document.createElement('style');
            st.id = 'qrHintTipStyles';
            // The hint has to float above the dashboard's own layers, not just
            // Bootstrap's. Bootstrap gives .tooltip 1080, which loses to the
            // sticky dark table header these icons sit IN — the hint rendered
            // behind it and was cut off at the header's top edge (task #602).
            // 10050 clears every z-index used on this dashboard (highest: 10000).
            // …and it has to be SEEN once it is on top. These icons sit in a
            // dark header, and a dark tooltip resting against it read as one
            // shape (task #602 round 2). Extra clearance below, a hairline rim
            // and a shadow give it an edge of its own.
            // touch-action: a second tap closes a hint, and two quick taps
            // must not zoom the page instead.
            st.textContent = '.tooltip.qr-hint-tip{z-index:10050;}.js-hint-tip{touch-action:manipulation;}'
                + '.qr-hint-tip .tooltip-inner{white-space:pre-line;text-align:start;max-width:320px;'
                + 'border:1px solid rgba(255,255,255,0.28);box-shadow:0 6px 18px rgba(0,0,0,0.35);}';
            document.head.appendChild(st);
        }
        const tip = new window.bootstrap.Tooltip(el, {
            trigger: 'hover focus', container: 'body', customClass: 'qr-hint-tip',
            // Lift it clear of the header rather than letting it rest on it.
            offset: [0, 12]
        });
        tip.show();
        // Bootstrap moved the title into the tooltip; an empty one keeps an
        // ancestor's (a Contact Details row's "click for details") from
        // showing on top of it.
        el.setAttribute('title', '');
    };
    // A phone has no "mouse leaves", so a shown hint stayed up (delivery
    // badges, chat 4d5262e0): a second tap on it, or a tap anywhere else,
    // closes it. Touch and pen only; a mouse keeps hover-to-show.
    const tappedWhileShown = new WeakSet();
    const tipOf = (el) => window.bootstrap && window.bootstrap.Tooltip && window.bootstrap.Tooltip.getInstance(el);
    document.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        const el = e.target && e.target.closest && e.target.closest('.js-hint-tip');
        // Both ways: a scroll that began on a shown hint ends with no click,
        // and a stale entry would swallow that hint's next tap.
        if (el && el.__hintTip) {
            if (el.getAttribute('aria-describedby')) tappedWhileShown.add(el);
            else tappedWhileShown.delete(el);
        }
        document.querySelectorAll('.js-hint-tip[aria-describedby]').forEach((open) => {
            if (open !== el && open.__hintTip) { const t = tipOf(open); if (t) t.hide(); }
        });
    }, true);
    document.addEventListener('mouseover', (e) => upgrade(e.target), true);
    document.addEventListener('focusin', (e) => upgrade(e.target), true);
    document.addEventListener('click', (e) => {
        const el = e.target && e.target.closest && e.target.closest('.js-hint-tip');
        if (el && tappedWhileShown.has(el)) {
            tappedWhileShown.delete(el);
            const t = tipOf(el);
            if (t) t.hide();
            if (el.blur) el.blur();
            return;
        }
        upgrade(e.target);
    }, true);
})();
